// src/context/SmartShikkaContext.ts
import { createContext, useContext } from "react";
var SmartShikkaContext = createContext({
  config: null,
  isReady: false
});
function useSmartShikkaContext() {
  return useContext(SmartShikkaContext);
}

// src/hooks/useSmartShikka.ts
function useSmartShikka() {
  return useSmartShikkaContext();
}

// src/hooks/useFormBridge.ts
import { useState, useCallback } from "react";

// src/logging/redaction.ts
import { buildSafeErrorLog, redactInlineSecrets } from "@smart-shikka/core/audit";
var piiPatterns = [
  { label: "phone_detected", pattern: /(\+91[-\s]?)?\b\d{5}[-\s]?\d{5}\b/g, replacement: "[PHONE]" },
  {
    label: "email_detected",
    pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    replacement: "[EMAIL]"
  },
  {
    label: "gstin_detected",
    pattern: /\b\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]\b/g,
    replacement: "[GSTIN]"
  },
  {
    label: "aadhaar_detected",
    pattern: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
    replacement: "[AADHAAR]"
  }
];
var sensitiveKeyPattern = /(prompt|transcript|audio|payload|secret|token|api[_-]?key|password|authorization|bearer|private[_-]?key)/i;
var unique = (values) => Array.from(new Set(values));
var redactText = (value) => {
  const redactions = [];
  let redacted = redactInlineSecrets(value);
  if (redacted !== value) {
    redactions.push("secret_detected");
  }
  for (const rule of piiPatterns) {
    redacted = redacted.replace(rule.pattern, () => {
      redactions.push(rule.label);
      return rule.replacement;
    });
  }
  return { value: redacted, redactions: unique(redactions) };
};
var redactUnknown = (value) => {
  if (typeof value === "string") return redactText(value);
  if (typeof value === "number" || typeof value === "boolean" || value === null || value === void 0) {
    return { value, redactions: [] };
  }
  if (Array.isArray(value)) {
    const redactions = [];
    const nextValue = value.map((item) => {
      const result = redactUnknown(item);
      redactions.push(...result.redactions);
      return result.value;
    });
    return { value: nextValue, redactions: unique(redactions) };
  }
  if (typeof value === "object") {
    return redactRecord(value);
  }
  return { value: String(value), redactions: [] };
};
var redactRecord = (value = {}) => {
  const redactions = [];
  const safeRecord = {};
  for (const [key, item] of Object.entries(value)) {
    if (sensitiveKeyPattern.test(key)) {
      safeRecord[key] = "[REDACTED]";
      redactions.push(`${key.toLowerCase()}_redacted`);
      continue;
    }
    const result = redactUnknown(item);
    safeRecord[key] = result.value;
    redactions.push(...result.redactions);
  }
  return { value: safeRecord, redactions: unique(redactions) };
};
var defaultRedactor = {
  redactText,
  redactRecord
};
function safeError(error, input) {
  return buildSafeErrorLog({
    errorCode: input?.errorCode ?? "ERR_INTERNAL",
    category: input?.category ?? "internal",
    operation: input?.operation ?? "react.safeError",
    error,
    requestId: input?.requestId,
    retryable: input?.retryable ?? false
  });
}

// src/logging/logger.ts
var LogLevel = /* @__PURE__ */ ((LogLevel3) => {
  LogLevel3[LogLevel3["DEBUG"] = 0] = "DEBUG";
  LogLevel3[LogLevel3["INFO"] = 1] = "INFO";
  LogLevel3[LogLevel3["WARN"] = 2] = "WARN";
  LogLevel3[LogLevel3["ERROR"] = 3] = "ERROR";
  LogLevel3[LogLevel3["FATAL"] = 4] = "FATAL";
  return LogLevel3;
})(LogLevel || {});
var InMemoryLogSink = class {
  constructor() {
    this.logs = [];
  }
  write(entry) {
    this.logs.push(entry);
  }
  read() {
    return [...this.logs];
  }
  clear() {
    this.logs = [];
  }
};
var ConsoleLogSink = class {
  write(entry) {
    const formatted = `[${entry.timestamp.toISOString()}] [${entry.levelName}] ${entry.message}`;
    if (entry.level === 0 /* DEBUG */) console.debug(formatted);
    else if (entry.level === 1 /* INFO */) console.info(formatted);
    else if (entry.level === 2 /* WARN */) console.warn(formatted);
    else console.error(formatted);
  }
};
var levelNames = ["DEBUG", "INFO", "WARN", "ERROR", "FATAL"];
var Logger = class _Logger {
  constructor(config) {
    this.memorySink = config?.sink instanceof InMemoryLogSink ? config.sink : new InMemoryLogSink();
    this.config = {
      minLevel: 1 /* INFO */,
      enableConsole: false,
      redactor: defaultRedactor,
      sink: config?.sink ?? this.memorySink,
      ...config
    };
  }
  static getInstance() {
    if (!_Logger.instance) {
      _Logger.instance = new _Logger();
    }
    return _Logger.instance;
  }
  configure(config) {
    this.config = { ...this.config, ...config };
  }
  shouldLog(level) {
    return level >= this.config.minLevel;
  }
  log(level, message, context) {
    if (!this.shouldLog(level)) return;
    const safeMessage = this.config.redactor.redactText(message);
    const safeContext = this.config.redactor.redactRecord(context);
    const entry = {
      timestamp: /* @__PURE__ */ new Date(),
      level,
      levelName: levelNames[level],
      message: safeMessage.value,
      context: safeContext.value,
      redactions: Array.from(/* @__PURE__ */ new Set([...safeMessage.redactions, ...safeContext.redactions])),
      source: "SmartShikkaReact"
    };
    this.config.sink.write(entry);
    if (this.config.enableConsole) {
      new ConsoleLogSink().write(entry);
    }
  }
  debug(message, context) {
    this.log(0 /* DEBUG */, message, context);
  }
  info(message, context) {
    this.log(1 /* INFO */, message, context);
  }
  warn(message, context) {
    this.log(2 /* WARN */, message, context);
  }
  error(message, context) {
    this.log(3 /* ERROR */, message, context);
  }
  fatal(message, context) {
    this.log(4 /* FATAL */, message, context);
  }
  getLogs(level) {
    const logs = this.config.sink.read?.() ?? this.memorySink.read();
    if (level === void 0) return logs;
    return logs.filter((log) => log.level >= level);
  }
  clearLogs() {
    this.config.sink.clear?.();
    this.memorySink.clear();
  }
};
var logger = Logger.getInstance();

// src/hooks/useFormBridge.ts
function useFormBridge(bridge) {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const validate = useCallback(
    (fieldId, _value) => {
      const isSafe = bridge.safeFields.some((f) => f.fieldId === fieldId);
      if (!isSafe) {
        setErrors((prev) => ({
          ...prev,
          [fieldId]: "\u092F\u0939 field Smart Shikka \u0926\u094D\u0935\u093E\u0930\u093E draft \u0915\u0930\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964"
        }));
        return false;
      }
      setErrors((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
      return true;
    },
    [bridge.safeFields]
  );
  const submit = useCallback(
    async (data) => {
      if (bridge.status === "guide_only" || bridge.status === "planned" || bridge.status === "none") {
        return;
      }
      setIsSubmitting(true);
      setErrors({});
      setLastResult(null);
      try {
        const beforeSnapshot = bridge.getSnapshot();
        const safeData = {};
        const intendedFieldIds = [];
        for (const [key, value] of Object.entries(data)) {
          if (bridge.safeFields.some((f) => f.fieldId === key)) {
            safeData[key] = value;
            intendedFieldIds.push(key);
          }
        }
        if (intendedFieldIds.length === 0) {
          setIsSubmitting(false);
          return;
        }
        await bridge.setFields(safeData);
        const afterSnapshot = bridge.getSnapshot();
        const result = bridge.verifyAfterApply(intendedFieldIds, beforeSnapshot, afterSnapshot);
        setLastResult(result);
        if (!result.success) {
          setErrors((prev) => ({
            ...prev,
            _form: result.message_hi
          }));
        }
      } catch (err) {
        const log = safeError(err, {
          errorCode: "ERR_FORM_SUBMIT",
          operation: "useFormBridge.submit",
          retryable: false
        });
        logger.error(log.safeMessage, {
          errorCode: log.errorCode,
          retryable: log.retryable,
          requestId: log.requestId
        });
        setErrors((prev) => ({
          ...prev,
          _form: "\u090F\u0915 \u0905\u0928\u092A\u0947\u0915\u094D\u0937\u093F\u0924 \u0924\u094D\u0930\u0941\u091F\u093F \u0939\u0941\u0908\u0964"
        }));
      } finally {
        setIsSubmitting(false);
      }
    },
    [bridge]
  );
  return { errors, isSubmitting, lastResult, validate, submit };
}

// src/hooks/usePageCapability.ts
import { useMemo } from "react";
function usePageCapability(_route) {
  const { config } = useSmartShikkaContext();
  return useMemo(() => {
    if (!config) return void 0;
    return void 0;
  }, [config]);
}

// src/hooks/useProjectRuntime.ts
import { useCallback as useCallback2, useEffect, useMemo as useMemo2, useRef, useState as useState2 } from "react";

// src/feedback/safeErrorCopy.ts
var TABLE = {
  release_metadata_unavailable: {
    titleEn: "Installer release details are not published yet.",
    titleHi: "\u0907\u0902\u0938\u094D\u091F\u0949\u0932\u0930 \u0930\u093F\u0932\u0940\u091C\u093C \u0935\u093F\u0935\u0930\u0923 \u0905\u092D\u0940 \u092A\u094D\u0930\u0915\u093E\u0936\u093F\u0924 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
    actionEn: "Check the release notes or ask the owner.",
    actionHi: "Release notes \u0926\u0947\u0916\u0947\u0902 \u092F\u093E owner \u0938\u0947 \u092A\u0942\u091B\u0947\u0902\u0964"
  },
  provider_key_missing: {
    titleEn: "Provider key is not set.",
    titleHi: "\u092A\u094D\u0930\u094B\u0935\u093E\u0907\u0921\u0930 key \u0938\u0947\u091F \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
    actionEn: "Add it in Settings \u2192 Provider (owner only).",
    actionHi: "Settings \u2192 Provider \u092E\u0947\u0902 \u091C\u094B\u0921\u093C\u0947\u0902 (\u0915\u0947\u0935\u0932 owner)\u0964"
  },
  provider_auth_failed: {
    titleEn: "Provider rejected the key.",
    titleHi: "\u092A\u094D\u0930\u094B\u0935\u093E\u0907\u0921\u0930 \u0928\u0947 key \u0905\u0938\u094D\u0935\u0940\u0915\u093E\u0930 \u0915\u0940\u0964",
    actionEn: "Re-check the key in Provider Settings.",
    actionHi: "Provider Settings \u092E\u0947\u0902 key \u0926\u094B\u092C\u093E\u0930\u093E \u091C\u093E\u0901\u091A\u0947\u0902\u0964"
  },
  provider_unreachable: {
    titleEn: "Provider could not be reached.",
    titleHi: "\u092A\u094D\u0930\u094B\u0935\u093E\u0907\u0921\u0930 \u0924\u0915 \u092A\u0939\u0941\u0901\u091A \u0928\u0939\u0940\u0902 \u0939\u0941\u0908\u0964",
    actionEn: "Check the network and retry.",
    actionHi: "\u0928\u0947\u091F\u0935\u0930\u094D\u0915 \u091C\u093E\u0901\u091A\u0947\u0902 \u0914\u0930 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902\u0964"
  },
  auth_invalid: {
    titleEn: "Sign-in failed.",
    titleHi: "\u0938\u093E\u0907\u0928-\u0907\u0928 \u0928\u0939\u0940\u0902 \u0939\u0941\u0906\u0964",
    actionEn: "Re-check email/password or ask the administrator.",
    actionHi: "Email/\u092A\u093E\u0938\u0935\u0930\u094D\u0921 \u0926\u094B\u092C\u093E\u0930\u093E \u091C\u093E\u0901\u091A\u0947\u0902 \u092F\u093E administrator \u0938\u0947 \u092A\u0942\u091B\u0947\u0902\u0964"
  },
  auth_forbidden: {
    titleEn: "This account is not allowed to do that.",
    titleHi: "\u092F\u0939 \u0916\u093E\u0924\u093E \u0907\u0938 \u0915\u093E\u092E \u0915\u0940 \u0905\u0928\u0941\u092E\u0924\u093F \u0928\u0939\u0940\u0902 \u0930\u0916\u0924\u093E\u0964",
    actionEn: "Ask the owner for access.",
    actionHi: "Owner \u0938\u0947 access \u092E\u093E\u0901\u0917\u0947\u0902\u0964"
  },
  rag_source_selection_denied: {
    titleEn: "That knowledge source is not available for this chat.",
    titleHi: "\u0907\u0938 chat \u0915\u0947 \u0932\u093F\u090F \u0935\u0939 knowledge source \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
    actionEn: "Use an allowed source or ask the owner.",
    actionHi: "\u0905\u0928\u0941\u092E\u0924 source \u091A\u0941\u0928\u0947\u0902 \u092F\u093E owner \u0938\u0947 \u092A\u0942\u091B\u0947\u0902\u0964"
  },
  durability_backend_missing: {
    titleEn: "A required durable backend is missing.",
    titleHi: "\u090F\u0915 \u091C\u093C\u0930\u0942\u0930\u0940 durable backend \u092E\u094C\u091C\u0942\u0926 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
    actionEn: "The capability is disabled until the owner configures persistence.",
    actionHi: "Owner persistence configure \u0915\u0930\u0928\u0947 \u0924\u0915 \u0938\u0941\u0935\u093F\u0927\u093E \u092C\u0902\u0926 \u0930\u0939\u0924\u0940 \u0939\u0948\u0964"
  },
  product_truth_unavailable: {
    titleEn: "Capability status could not be loaded.",
    titleHi: "\u0938\u0941\u0935\u093F\u0927\u093E \u0938\u094D\u0925\u093F\u0924\u093F \u0932\u094B\u0921 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0940\u0964",
    actionEn: "Refresh; if it stays, the status is unverified.",
    actionHi: "Refresh \u0915\u0930\u0947\u0902; \u092C\u0928\u093E \u0930\u0939\u0947 \u0924\u094B \u0938\u094D\u0925\u093F\u0924\u093F unverified \u0939\u0948\u0964"
  },
  network_error: {
    titleEn: "Network request failed.",
    titleHi: "\u0928\u0947\u091F\u0935\u0930\u094D\u0915 request \u0905\u0938\u092B\u0932 \u0930\u0939\u093E\u0964",
    actionEn: "Check connectivity and retry.",
    actionHi: "Connectivity \u091C\u093E\u0901\u091A\u0947\u0902 \u0914\u0930 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902\u0964"
  }
};
var FALLBACK = {
  titleEn: "Something could not be completed.",
  titleHi: "\u0915\u0941\u091B \u092A\u0942\u0930\u093E \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u093E\u0964",
  actionEn: "Retry once; if it persists, tell the owner the error code.",
  actionHi: "\u090F\u0915 \u092C\u093E\u0930 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902; \u092C\u0928\u093E \u0930\u0939\u0947 \u0924\u094B owner \u0915\u094B error code \u092C\u0924\u093E\u090F\u0901\u0964"
};
var EXTRA_CODES = {
  generic_error: FALLBACK,
  session_invalid: {
    titleEn: "The session is no longer valid.",
    titleHi: "\u0938\u0947\u0936\u0928 \u0905\u092C \u092E\u093E\u0928\u094D\u092F \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
    actionEn: "Sign in again.",
    actionHi: "\u0926\u094B\u092C\u093E\u0930\u093E \u0938\u093E\u0907\u0928-\u0907\u0928 \u0915\u0930\u0947\u0902\u0964"
  },
  browser_artifact_list_failed: {
    titleEn: "Browser artifacts could not be loaded.",
    titleHi: "Browser artifacts \u0932\u094B\u0921 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0947\u0964",
    actionEn: "Refresh once; check the Desktop/browser connection.",
    actionHi: "\u090F\u0915 \u092C\u093E\u0930 refresh \u0915\u0930\u0947\u0902; Desktop/browser connection \u091C\u093E\u0901\u091A\u0947\u0902\u0964"
  },
  browser_artifact_upload_failed: {
    titleEn: "The browser artifact could not be saved.",
    titleHi: "Browser artifact \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u093E\u0964",
    actionEn: "Retry once; check the connection.",
    actionHi: "\u090F\u0915 \u092C\u093E\u0930 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902; connection \u091C\u093E\u0901\u091A\u0947\u0902\u0964"
  },
  attachment_mime_unsupported: {
    titleEn: "This file type is not supported as an attachment.",
    titleHi: "\u092F\u0939 \u092B\u093C\u093E\u0907\u0932 \u092A\u094D\u0930\u0915\u093E\u0930 attachment \u092E\u0947\u0902 \u0938\u092E\u0930\u094D\u0925\u093F\u0924 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
    actionEn: "Attach a supported image file instead.",
    actionHi: "\u0938\u092E\u0930\u094D\u0925\u093F\u0924 image \u092B\u093C\u093E\u0907\u0932 attach \u0915\u0930\u0947\u0902\u0964"
  },
  chat_send_failed: {
    titleEn: "The message could not be sent.",
    titleHi: "\u0938\u0902\u0926\u0947\u0936 \u0928\u0939\u0940\u0902 \u092D\u0947\u091C\u093E \u091C\u093E \u0938\u0915\u093E\u0964",
    actionEn: "Retry once; check the connection.",
    actionHi: "\u090F\u0915 \u092C\u093E\u0930 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902; connection \u091C\u093E\u0901\u091A\u0947\u0902\u0964"
  },
  runtime_unavailable: {
    titleEn: "The runtime is unavailable.",
    titleHi: "Runtime \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
    actionEn: "Retry shortly; if it persists ask the owner.",
    actionHi: "\u0925\u094B\u0921\u093C\u0940 \u0926\u0947\u0930 \u092C\u093E\u0926 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902; \u092C\u0928\u093E \u0930\u0939\u0947 \u0924\u094B owner \u0938\u0947 \u092A\u0942\u091B\u0947\u0902\u0964"
  },
  settings_load_failed: {
    titleEn: "Settings could not be loaded.",
    titleHi: "\u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 \u092A\u0922\u093C\u0940 \u0928\u0939\u0940\u0902 \u091C\u093E \u0938\u0915\u0940\u0902\u0964",
    actionEn: "Refresh the page.",
    actionHi: "\u092A\u0947\u091C refresh \u0915\u0930\u0947\u0902\u0964"
  },
  settings_save_failed: {
    titleEn: "The setting could not be saved.",
    titleHi: "\u0938\u0947\u091F\u093F\u0902\u0917 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0940\u0964",
    actionEn: "Re-check the value and retry.",
    actionHi: "\u092E\u093E\u0928 \u091C\u093E\u0901\u091A\u0915\u0930 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902\u0964"
  },
  settings_delete_failed: {
    titleEn: "The setting could not be removed.",
    titleHi: "\u0938\u0947\u091F\u093F\u0902\u0917 \u0939\u091F\u093E\u0908 \u0928\u0939\u0940\u0902 \u091C\u093E \u0938\u0915\u0940\u0964",
    actionEn: "Retry once.",
    actionHi: "\u090F\u0915 \u092C\u093E\u0930 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902\u0964"
  },
  routing_save_failed: {
    titleEn: "Routing settings could not be saved.",
    titleHi: "\u0930\u0942\u091F\u093F\u0902\u0917 \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0940\u0902\u0964",
    actionEn: "Re-check values and retry.",
    actionHi: "\u092E\u093E\u0928 \u091C\u093E\u0901\u091A\u0915\u0930 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902\u0964"
  },
  project_list_failed: {
    titleEn: "The project list could not be loaded.",
    titleHi: "\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u0938\u0942\u091A\u0940 \u0932\u094B\u0921 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0940\u0964",
    actionEn: "Refresh; check the connection.",
    actionHi: "Refresh \u0915\u0930\u0947\u0902; connection \u091C\u093E\u0901\u091A\u0947\u0902\u0964"
  },
  approval_action_failed: {
    titleEn: "The approval action did not complete.",
    titleHi: "Approval \u0915\u093E\u0930\u094D\u092F \u092A\u0942\u0930\u093E \u0928\u0939\u0940\u0902 \u0939\u0941\u0906\u0964",
    actionEn: "Retry once; check the connection.",
    actionHi: "\u090F\u0915 \u092C\u093E\u0930 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902; connection \u091C\u093E\u0901\u091A\u0947\u0902\u0964"
  },
  broker_request_failed: {
    titleEn: "The desktop/browser broker request failed.",
    titleHi: "Desktop/browser broker request \u0905\u0938\u092B\u0932 \u0930\u0939\u093E\u0964",
    actionEn: "Check the Desktop pairing and retry.",
    actionHi: "Desktop pairing \u091C\u093E\u0901\u091A\u0947\u0902 \u0914\u0930 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902\u0964"
  },
  update_check_failed: {
    titleEn: "The update check failed.",
    titleHi: "Update \u091C\u093E\u0901\u091A \u0905\u0938\u092B\u0932 \u0930\u0939\u0940\u0964",
    actionEn: "Retry later.",
    actionHi: "\u092C\u093E\u0926 \u092E\u0947\u0902 \u092B\u093F\u0930 \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902\u0964"
  },
  invalid_selection: {
    titleEn: "The selection is not valid.",
    titleHi: "\u091A\u092F\u0928 \u092E\u093E\u0928\u094D\u092F \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
    actionEn: "Pick a listed value.",
    actionHi: "\u0938\u0942\u091A\u0940 \u0915\u093E \u092E\u093E\u0928 \u091A\u0941\u0928\u0947\u0902\u0964"
  }
};
Object.assign(TABLE, EXTRA_CODES);
function safeErrorCopy(code) {
  const key = typeof code === "string" ? code.toLowerCase() : code;
  return (key ? TABLE[key] : void 0) ?? FALLBACK;
}
var SAFE_ERROR_CODES = Object.keys(TABLE);
var SAFE_CODE_RE = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;
function safeErrorCode(raw) {
  if (typeof raw !== "string") return "generic_error";
  const s = raw.trim();
  return SAFE_CODE_RE.test(s) ? s : "generic_error";
}
function errorToCode(err) {
  if (err && typeof err === "object" && "code" in err) {
    const code = err.code;
    if (typeof code === "string" && SAFE_CODE_RE.test(code)) return code;
  }
  if (err instanceof Error) {
    const m = err.message.toLowerCase();
    if (m.includes("fetch") || m.includes("network") || m.includes("timeout")) {
      return "network_error";
    }
    return "generic_error";
  }
  return safeErrorCode(err);
}
function safeErrorText(code, locale) {
  const c = safeErrorCopy(code);
  const hi = locale === "hi";
  return { title: hi ? c.titleHi : c.titleEn, action: hi ? c.actionHi : c.actionEn };
}

// src/hooks/useProjectRuntime.ts
import {
  createCwjProjectRuntimeStore
} from "@smart-shikka/core/cwj-project-runtime";
function useProjectRuntime(options) {
  const { service, ownerId, tenantId, defaultSkillId } = options;
  const localStore = useMemo2(
    () => ownerId ? createCwjProjectRuntimeStore({
      ...defaultSkillId !== void 0 ? { defaultSkillId } : {},
      storageKeyPrefix: "smart_shikka_project_runtime_web"
    }) : void 0,
    [defaultSkillId, ownerId]
  );
  const [allowedProjects, setAllowedProjects] = useState2([]);
  const [currentContext, setCurrentContext] = useState2(void 0);
  const [loading, setLoading] = useState2(false);
  const [error, setError] = useState2(null);
  const abortRef = useRef(null);
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, [service, ownerId, tenantId]);
  const getSignal = useCallback2(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    return controller.signal;
  }, []);
  const load = useCallback2(async () => {
    if (service) {
      setLoading(true);
      setError(null);
      try {
        const signal = getSignal();
        const projects = await service.listAllowedProjects(signal);
        if (signal.aborted) return;
        setAllowedProjects(projects);
      } catch (e) {
        setError(errorToCode(e));
      } finally {
        setLoading(false);
      }
      return;
    }
    if (localStore && ownerId) {
      setAllowedProjects(localStore.listAllowedProjects(ownerId, tenantId));
      setCurrentContext(localStore.getCurrentContext(ownerId, tenantId));
    }
  }, [service, localStore, ownerId, tenantId, getSignal]);
  useEffect(() => {
    void load();
    return () => {
      abortRef.current?.abort();
    };
  }, [load]);
  const selectProject = useCallback2(
    async (projectId, skillId) => {
      setError(null);
      if (service) {
        const signal = getSignal();
        const ctx2 = await service.selectProject(projectId, skillId, signal);
        setCurrentContext(ctx2);
        return ctx2;
      }
      if (!localStore || !ownerId) {
        throw new Error("Project runtime service not configured.");
      }
      const ctx = localStore.selectProject({ projectId, ownerId, tenantId, skillId });
      setCurrentContext(ctx);
      return ctx;
    },
    [service, localStore, ownerId, tenantId, getSignal]
  );
  const invalidate = useCallback2(async () => {
    if (service) {
      const signal = getSignal();
      await service.invalidate(signal);
      setCurrentContext(void 0);
      return;
    }
    if (localStore && ownerId) {
      localStore.invalidate(ownerId, tenantId);
      setCurrentContext(void 0);
    }
  }, [service, localStore, ownerId, tenantId, getSignal]);
  return {
    store: localStore,
    allowedProjects,
    currentContext,
    selectProject,
    invalidate,
    loading,
    error
  };
}

// src/components/SmartShikkaProvider.tsx
import { useState as useState3 } from "react";
import { jsx } from "react/jsx-runtime";
function SmartShikkaProvider({
  config,
  children
}) {
  const [isReady] = useState3(true);
  return /* @__PURE__ */ jsx(SmartShikkaContext.Provider, { value: { config, isReady }, children });
}

// src/components/SmartShikkaDock.tsx
import { useState as useState4, useRef as useRef2, useEffect as useEffect2 } from "react";
import { getBridgeStatusLabel_hi, UNIVERSAL_BLOCKED_ACTIONS } from "@smart-shikka/core";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function SmartShikkaDock({
  appName,
  currentRoute,
  currentPageTitle,
  role,
  bridgeStatus = "none",
  messages = [],
  onSendMessage,
  onApplyDraft,
  disabled = false,
  devMode = false
}) {
  const [isExpanded, setIsExpanded] = useState4(true);
  const [showHelp, setShowHelp] = useState4(false);
  const [inputText, setInputText] = useState4("");
  const messageEndRef = useRef2(null);
  useEffect2(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isExpanded]);
  const handleSend = () => {
    if (disabled || !inputText.trim()) return;
    if (onSendMessage) {
      onSendMessage(inputText.trim());
    }
    setInputText("");
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };
  const getStatusColorClass = (status) => {
    switch (status) {
      case "active":
        return "shikha-status-active";
      case "partial_active":
        return "shikha-status-partial";
      case "guide_only":
        return "shikha-status-guide";
      case "planned":
      case "none":
      default:
        return "shikha-status-none";
    }
  };
  const getStatusDisplayName = (status) => {
    switch (status) {
      case "active":
        return "\u0938\u0915\u094D\u0930\u093F\u092F (Active)";
      case "partial_active":
        return "\u0906\u0902\u0936\u093F\u0915 (Partial)";
      case "guide_only":
        return "\u0915\u0947\u0935\u0932 \u0917\u093E\u0907\u0921 (Guide Only)";
      case "planned":
        return "\u0928\u093F\u092F\u094B\u091C\u093F\u0924 (Planned)";
      case "none":
      default:
        return "\u0905\u0915\u094D\u0937\u092E (Disabled)";
    }
  };
  if (!isExpanded) {
    return /* @__PURE__ */ jsxs(
      "button",
      {
        className: "shikha-fab",
        onClick: () => setIsExpanded(true),
        "aria-label": "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E \u0938\u0939\u093E\u092F\u0915 \u0916\u094B\u0932\u0947\u0902",
        title: "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E \u0938\u0939\u093E\u092F\u0915",
        children: [
          /* @__PURE__ */ jsx2("span", { className: "shikha-fab-icon", children: "\u{1F469}\u200D\u{1F4BC}" }),
          /* @__PURE__ */ jsx2("span", { className: "shikha-fab-text", children: "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E" })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: `shikha-dock-container ${devMode ? "shikha-dev-mode" : ""}`, id: "smart-shikka-dock", children: [
    /* @__PURE__ */ jsxs("header", { className: "shikha-header", children: [
      /* @__PURE__ */ jsxs("div", { className: "shikha-header-info", children: [
        /* @__PURE__ */ jsx2("div", { className: "shikha-header-avatar", children: "\u{1F469}\u200D\u{1F4BC}" }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx2("h2", { className: "shikha-header-title", children: "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E" }),
          /* @__PURE__ */ jsx2("span", { className: "shikha-header-subtitle", children: appName })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "shikha-header-actions", children: [
        /* @__PURE__ */ jsx2(
          "button",
          {
            className: "shikha-btn-icon",
            onClick: () => setShowHelp(!showHelp),
            "aria-label": "\u092E\u0926\u0926 \u0914\u0930 \u0938\u0941\u0930\u0915\u094D\u0937\u093E \u0928\u093F\u092F\u092E",
            title: "\u0938\u0941\u0930\u0915\u094D\u0937\u093E \u0928\u093F\u092F\u092E",
            children: "\u2753"
          }
        ),
        /* @__PURE__ */ jsx2(
          "button",
          {
            className: "shikha-btn-icon",
            onClick: () => setIsExpanded(false),
            "aria-label": "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E \u092C\u0902\u0926 \u0915\u0930\u0947\u0902",
            title: "\u091B\u094B\u091F\u093E \u0915\u0930\u0947\u0902",
            children: "\u2796"
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxs("section", { className: "shikha-info-bar", children: [
      /* @__PURE__ */ jsxs("div", { className: "shikha-chips-row", children: [
        /* @__PURE__ */ jsxs(
          "span",
          {
            className: `shikha-chip ${getStatusColorClass(bridgeStatus)}`,
            title: getBridgeStatusLabel_hi(bridgeStatus),
            children: [
              "\u{1F310} ",
              getStatusDisplayName(bridgeStatus)
            ]
          }
        ),
        currentPageTitle && /* @__PURE__ */ jsxs("span", { className: "shikha-chip shikha-chip-page", title: `\u0935\u0930\u094D\u0924\u092E\u093E\u0928 \u092A\u0947\u091C: ${currentPageTitle} (${currentRoute})`, children: [
          "\u{1F4C4} ",
          currentPageTitle
        ] }),
        role && /* @__PURE__ */ jsxs("span", { className: "shikha-chip shikha-chip-role", title: `\u0906\u092A\u0915\u0940 \u092D\u0942\u092E\u093F\u0915\u093E: ${role}`, children: [
          "\u{1F464} ",
          role
        ] }),
        devMode && /* @__PURE__ */ jsx2("span", { className: "shikha-chip shikha-chip-dev", title: "\u0921\u0947\u0935\u0932\u092A\u0930 \u092E\u094B\u0921 \u0938\u0915\u094D\u0930\u093F\u092F \u0939\u0948", children: "\u{1F6E0}\uFE0F DEV MODE" })
      ] }),
      /* @__PURE__ */ jsx2("div", { className: "shikha-status-desc", children: getBridgeStatusLabel_hi(bridgeStatus) })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "shikha-safety-notice", children: [
      /* @__PURE__ */ jsx2("span", { className: "shikha-safety-icon", children: "\u26A0\uFE0F" }),
      /* @__PURE__ */ jsxs("p", { className: "shikha-safety-text", children: [
        /* @__PURE__ */ jsx2("strong", { children: "\u0938\u0941\u0930\u0915\u094D\u0937\u093E \u0928\u093F\u0930\u094D\u0926\u0947\u0936:" }),
        " \u092E\u0948\u0902 \u0915\u0947\u0935\u0932 \u0921\u094D\u0930\u093E\u092B\u094D\u091F (draft) \u092D\u0930 \u0938\u0915\u0924\u0940 \u0939\u0942\u0901\u0964 \u0921\u0947\u091F\u093E \u0915\u094B \u0938\u0939\u0947\u091C\u0928\u093E (Save), \u092A\u094B\u0938\u094D\u091F \u0915\u0930\u0928\u093E (Post) \u092F\u093E \u092D\u0941\u0917\u0924\u093E\u0928 (Payment) \u0915\u0930\u0928\u093E \u0915\u093E\u0928\u0942\u0928\u0940 \u0930\u0942\u092A \u0938\u0947 \u0906\u092A\u0915\u094B \u0938\u094D\u0935\u092F\u0902 \u0939\u0940 \u0915\u0930\u0928\u093E \u0939\u094B\u0917\u093E\u0964"
      ] })
    ] }),
    /* @__PURE__ */ jsxs("main", { className: "shikha-body", children: [
      showHelp && /* @__PURE__ */ jsxs("div", { className: "shikha-help-panel", id: "shikha-help-panel", children: [
        /* @__PURE__ */ jsxs("div", { className: "shikha-help-header", children: [
          /* @__PURE__ */ jsx2("h3", { children: "\u{1F6E1}\uFE0F \u0938\u0941\u0930\u0915\u094D\u0937\u093E \u090F\u0935\u0902 \u0935\u0930\u094D\u091C\u093F\u0924 \u0915\u094D\u0930\u093F\u092F\u093E\u090F\u0902 (Blocked Actions)" }),
          /* @__PURE__ */ jsx2(
            "button",
            {
              className: "shikha-btn-close-help",
              onClick: () => setShowHelp(false),
              "aria-label": "\u0939\u0947\u0932\u094D\u092A \u092C\u0902\u0926 \u0915\u0930\u0947\u0902",
              children: "\u2715"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "shikha-help-content", children: [
          /* @__PURE__ */ jsxs("p", { className: "shikha-help-intro", children: [
            "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E \u0928\u093F\u092E\u094D\u0928\u0932\u093F\u0916\u093F\u0924 \u0915\u094D\u0930\u093F\u092F\u093E\u090F\u0902 \u0915\u092D\u0940 \u092D\u0940 ",
            /* @__PURE__ */ jsx2("strong", { children: "\u0938\u094D\u0935\u091A\u093E\u0932\u093F\u0924 \u0930\u0942\u092A \u0938\u0947 (automatically)" }),
            " \u0928\u0939\u0940\u0902 \u0915\u0930\u0947\u0917\u0940:"
          ] }),
          /* @__PURE__ */ jsx2("ul", { className: "shikha-blocked-list", children: UNIVERSAL_BLOCKED_ACTIONS.map((action) => /* @__PURE__ */ jsxs("li", { className: "shikha-blocked-item", children: [
            /* @__PURE__ */ jsx2("strong", { className: "shikha-blocked-label", children: action.label_hi }),
            /* @__PURE__ */ jsx2("span", { className: "shikha-blocked-reason", children: action.reason_hi })
          ] }, action.id)) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "shikha-messages-area", children: [
        messages.length === 0 ? /* @__PURE__ */ jsxs("div", { className: "shikha-empty-messages", children: [
          /* @__PURE__ */ jsx2("div", { className: "shikha-empty-avatar", children: "\u{1F469}\u200D\u{1F4BC}" }),
          /* @__PURE__ */ jsx2("h4", { children: "\u0928\u092E\u0938\u094D\u0924\u0947! \u092E\u0948\u0902 \u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E \u0939\u0942\u0901\u0964" }),
          /* @__PURE__ */ jsx2("p", { children: "\u092E\u0948\u0902 \u0906\u092A\u0915\u0940 \u0907\u0938 \u092A\u0947\u091C \u092A\u0930 \u092B\u0949\u0930\u094D\u092E \u0921\u094D\u0930\u093E\u092B\u094D\u091F \u092D\u0930\u0928\u0947 \u092F\u093E \u0917\u093E\u0907\u0921 \u0915\u0930\u0928\u0947 \u092E\u0947\u0902 \u092E\u0926\u0926 \u0915\u0930 \u0938\u0915\u0924\u0940 \u0939\u0942\u0901\u0964 \u0906\u092A \u092E\u0941\u091D\u0938\u0947 \u0915\u094B\u0908 \u092D\u0940 \u092A\u094D\u0930\u0936\u094D\u0928 \u092A\u0942\u091B \u0938\u0915\u0924\u0947 \u0939\u0948\u0902\u0964" })
        ] }) : messages.map((msg) => /* @__PURE__ */ jsx2(
          "div",
          {
            className: `shikha-message-wrapper ${msg.sender === "user" ? "shikha-msg-user" : "shikha-msg-assistant"}`,
            children: /* @__PURE__ */ jsxs("div", { className: "shikha-message-bubble", children: [
              /* @__PURE__ */ jsx2("p", { className: "shikha-message-text", children: msg.text }),
              msg.timestamp && /* @__PURE__ */ jsx2("span", { className: "shikha-message-time", children: msg.timestamp })
            ] })
          },
          msg.id
        )),
        /* @__PURE__ */ jsx2("div", { ref: messageEndRef })
      ] })
    ] }),
    onApplyDraft && bridgeStatus !== "none" && bridgeStatus !== "planned" && /* @__PURE__ */ jsx2("div", { className: "shikha-draft-action-bar", children: /* @__PURE__ */ jsx2(
      "button",
      {
        className: "shikha-btn-apply-draft",
        onClick: onApplyDraft,
        disabled,
        children: "\u2728 \u092B\u0949\u0930\u094D\u092E \u0921\u094D\u0930\u093E\u092B\u094D\u091F \u0932\u093E\u0917\u0942 \u0915\u0930\u0947\u0902 (Apply Draft)"
      }
    ) }),
    /* @__PURE__ */ jsxs("footer", { className: "shikha-footer", children: [
      /* @__PURE__ */ jsx2(
        "input",
        {
          type: "text",
          className: "shikha-input",
          placeholder: disabled ? "\u0907\u0928\u092A\u0941\u091F \u0905\u0915\u094D\u0937\u092E \u0939\u0948..." : "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E \u0938\u0947 \u0938\u0935\u093E\u0932 \u092A\u0942\u091B\u0947\u0902...",
          value: inputText,
          onChange: (e) => setInputText(e.target.value),
          onKeyDown: handleKeyDown,
          disabled,
          "aria-label": "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E \u0907\u0928\u092A\u0941\u091F \u092B\u093C\u0940\u0932\u094D\u0921"
        }
      ),
      /* @__PURE__ */ jsx2(
        "button",
        {
          className: "shikha-btn-send",
          onClick: handleSend,
          disabled: disabled || !inputText.trim(),
          "aria-label": "\u0938\u0902\u0926\u0947\u0936 \u092D\u0947\u091C\u0947\u0902",
          title: "\u0938\u0947\u0902\u0921 \u0915\u0930\u0947\u0902",
          children: "\u2708\uFE0F"
        }
      )
    ] })
  ] });
}

// src/settings/SettingsStatusBadge.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function SettingsStatusBadge({ status }) {
  const normalized = (status || "unknown").toLowerCase();
  let text2 = "\u0905\u091C\u094D\u091E\u093E\u0924 (Unknown)";
  let bg = "rgba(108, 117, 125, 0.15)";
  let color = "#6c757d";
  switch (normalized) {
    case "available":
    case "ready":
      text2 = "\u0909\u092A\u0932\u092C\u094D\u0927 (Available)";
      bg = "rgba(40, 167, 69, 0.15)";
      color = "#28a745";
      break;
    case "planned":
      text2 = "\u0928\u093F\u092F\u094B\u091C\u093F\u0924 (Planned)";
      bg = "rgba(0, 123, 255, 0.15)";
      color = "#007bff";
      break;
    case "disabled":
      text2 = "\u0905\u0915\u094D\u0930\u093F\u092F (Disabled)";
      bg = "rgba(108, 117, 125, 0.12)";
      color = "#868e96";
      break;
    case "blocked":
      text2 = "\u0905\u0935\u0930\u0941\u0926\u094D\u0927 (Blocked)";
      bg = "rgba(220, 53, 69, 0.15)";
      color = "#dc3545";
      break;
    case "missing_configuration":
    case "missing_credentials":
      text2 = "\u0905\u092A\u0942\u0930\u094D\u0923 \u0935\u093F\u0928\u094D\u092F\u093E\u0938 (Pending Config)";
      bg = "rgba(255, 193, 7, 0.15)";
      color = "#b58100";
      break;
    case "unsupported":
      text2 = "\u0905\u0938\u092E\u0930\u094D\u0925\u093F\u0924 (Unsupported)";
      bg = "rgba(23, 162, 184, 0.15)";
      color = "#17a2b8";
      break;
  }
  return /* @__PURE__ */ jsxs2(
    "span",
    {
      className: "settings-status-badge",
      style: {
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor: bg,
        color,
        border: `1px solid ${color}30`,
        fontFamily: "inherit"
      },
      children: [
        /* @__PURE__ */ jsx3(
          "span",
          {
            style: {
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: color,
              marginRight: "6px"
            }
          }
        ),
        text2
      ]
    }
  );
}

// src/settings/SettingsEmptyState.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
function SettingsEmptyState({ title_hi, message_hi }) {
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className: "settings-empty-state",
      style: {
        padding: "40px 20px",
        textAlign: "center",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        border: "1px dashed #dee2e6",
        margin: "20px 0"
      },
      children: [
        /* @__PURE__ */ jsx4(
          "div",
          {
            style: {
              fontSize: "36px",
              color: "#adb5bd",
              marginBottom: "12px"
            },
            children: "\u{1F4ED}"
          }
        ),
        /* @__PURE__ */ jsx4(
          "h4",
          {
            style: {
              margin: "0 0 8px 0",
              color: "#495057",
              fontWeight: 600,
              fontFamily: "inherit"
            },
            children: title_hi
          }
        ),
        /* @__PURE__ */ jsx4(
          "p",
          {
            style: {
              margin: 0,
              color: "#6c757d",
              fontSize: "14px",
              fontFamily: "inherit"
            },
            children: message_hi
          }
        )
      ]
    }
  );
}

// src/settings/SettingsTabs.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
function SettingsTabs({ activeTab, onTabChange }) {
  const tabs = [
    { id: "control", label_hi: "\u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923 (Control Plane)" },
    { id: "providers", label_hi: "\u092E\u0949\u0921\u0932 \u092A\u094D\u0930\u0926\u093E\u0924\u093E (Model Providers)" },
    { id: "knowledge", label_hi: "\u091C\u094D\u091E\u093E\u0928 \u0938\u094D\u0930\u094B\u0924 (Knowledge Sources)" },
    { id: "tools", label_hi: "\u0909\u092A\u0915\u0930\u0923 (Tools)" },
    { id: "integrations", label_hi: "\u090F\u0915\u0940\u0915\u0930\u0923 (Integrations)" },
    { id: "apps", label_hi: "\u0905\u0928\u0941\u092A\u094D\u0930\u092F\u094B\u0917 (Apps)" },
    { id: "skills", label_hi: "\u0915\u094C\u0936\u0932 (Skills)" },
    { id: "agents", label_hi: "\u090F\u091C\u0947\u0902\u091F (Agent Presets)" },
    { id: "safety", label_hi: "\u0938\u0941\u0930\u0915\u094D\u0937\u093E (Safety)" }
  ];
  return /* @__PURE__ */ jsx5(
    "div",
    {
      className: "settings-tabs-container",
      style: {
        display: "flex",
        borderBottom: "2px solid #dee2e6",
        marginBottom: "24px",
        overflowX: "auto",
        scrollbarWidth: "none"
      },
      children: tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return /* @__PURE__ */ jsx5(
          "button",
          {
            onClick: () => onTabChange(tab.id),
            style: {
              padding: "12px 18px",
              border: "none",
              background: "none",
              color: isActive ? "#007bff" : "#495057",
              borderBottom: isActive ? "3px solid #007bff" : "3px solid transparent",
              fontWeight: isActive ? 600 : 500,
              fontSize: "14px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.2s ease",
              marginRight: "8px",
              fontFamily: "inherit"
            },
            children: tab.label_hi
          },
          tab.id
        );
      })
    }
  );
}

// src/settings/ModelProviderSettings.tsx
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
function ModelProviderSettings({
  providers = [],
  onSetupProvider,
  readOnly = true
}) {
  if (!providers || providers.length === 0) {
    return /* @__PURE__ */ jsx6(
      SettingsEmptyState,
      {
        title_hi: "\u0915\u094B\u0908 \u092E\u0949\u0921\u0932 \u092A\u094D\u0930\u0926\u093E\u0924\u093E \u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0928\u0939\u0940\u0902 \u0939\u0948",
        message_hi: "\u0915\u0943\u092A\u092F\u093E \u090F\u0906\u0908 \u0938\u0939\u093E\u092F\u0915\u094B\u0902 \u0915\u093E \u0938\u0902\u091A\u093E\u0932\u0928 \u0915\u0930\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u0905\u092A\u0928\u0947 \u092E\u0949\u0921\u0932 \u092A\u094D\u0930\u0926\u093E\u0924\u093E\u0913\u0902 \u0915\u0940 \u0938\u0942\u091A\u0940 \u092A\u094D\u0930\u0926\u093E\u0928 \u0915\u0930\u0947\u0902\u0964"
      }
    );
  }
  return /* @__PURE__ */ jsx6("div", { className: "model-provider-settings-panel", style: { fontFamily: "inherit" }, children: /* @__PURE__ */ jsx6(
    "div",
    {
      style: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        gap: "16px"
      },
      children: providers.map((p) => {
        const providerId = p.providerId || "unknown";
        return /* @__PURE__ */ jsxs4(
          "div",
          {
            className: "provider-card",
            style: {
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: "#ffffff",
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "between"
            },
            children: [
              /* @__PURE__ */ jsxs4("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }, children: [
                /* @__PURE__ */ jsxs4("div", { children: [
                  /* @__PURE__ */ jsx6("h4", { style: { margin: "0 0 4px 0", color: "#343a40", fontWeight: 600 }, children: p.label || providerId }),
                  /* @__PURE__ */ jsxs4("span", { style: { fontSize: "12px", color: "#6c757d", textTransform: "uppercase" }, children: [
                    "\u092A\u094D\u0930\u0915\u093E\u0930: ",
                    p.providerType || "LLM"
                  ] })
                ] }),
                /* @__PURE__ */ jsx6(SettingsStatusBadge, { status: p.status || (p.enabled ? "available" : "disabled") })
              ] }),
              /* @__PURE__ */ jsxs4("div", { style: { fontSize: "13px", color: "#495057", marginBottom: "16px", flexGrow: 1 }, children: [
                /* @__PURE__ */ jsxs4("div", { style: { marginBottom: "6px" }, children: [
                  /* @__PURE__ */ jsx6("strong", { children: "\u0921\u093F\u092B\u093C\u0949\u0932\u094D\u091F \u092E\u0949\u0921\u0932:" }),
                  " ",
                  p.defaultModel || p.models?.[0] || "\u0915\u094B\u0908 \u0928\u0939\u0940\u0902"
                ] }),
                /* @__PURE__ */ jsxs4("div", { style: { marginBottom: "6px" }, children: [
                  /* @__PURE__ */ jsx6("strong", { children: "\u0938\u0915\u094D\u0930\u093F\u092F \u092E\u0949\u0921\u0932:" }),
                  " ",
                  p.models?.join(", ") || "\u0915\u094B\u0908 \u0928\u0939\u0940\u0902"
                ] }),
                /* @__PURE__ */ jsxs4("div", { style: { marginBottom: "6px" }, children: [
                  /* @__PURE__ */ jsx6("strong", { children: "\u0915\u094D\u0930\u0947\u0921\u0947\u0902\u0936\u093F\u092F\u0932 \u092E\u094B\u0921:" }),
                  " ",
                  /* @__PURE__ */ jsx6("code", { style: { backgroundColor: "#f1f3f5", padding: "2px 6px", borderRadius: "4px" }, children: p.credentialMode || "not_configured" })
                ] })
              ] }),
              p.errors && p.errors.length > 0 && /* @__PURE__ */ jsxs4("div", { style: { backgroundColor: "#fff5f5", border: "1px solid #ffe3e3", borderRadius: "4px", padding: "8px", marginBottom: "12px", fontSize: "12px", color: "#e03131" }, children: [
                /* @__PURE__ */ jsx6("strong", { children: "\u0924\u094D\u0930\u0941\u091F\u093F\u092F\u093E\u0902:" }),
                /* @__PURE__ */ jsx6("ul", { style: { margin: "4px 0 0 0", paddingLeft: "16px" }, children: p.errors.map((err, idx) => /* @__PURE__ */ jsx6("li", { children: err }, idx)) })
              ] }),
              p.warnings && p.warnings.length > 0 && /* @__PURE__ */ jsxs4("div", { style: { backgroundColor: "#fff9db", border: "1px solid #fff3bf", borderRadius: "4px", padding: "8px", marginBottom: "12px", fontSize: "12px", color: "#f08c00" }, children: [
                /* @__PURE__ */ jsx6("strong", { children: "\u091A\u0947\u0924\u093E\u0935\u0928\u093F\u092F\u093E\u0902:" }),
                /* @__PURE__ */ jsx6("ul", { style: { margin: "4px 0 0 0", paddingLeft: "16px" }, children: p.warnings.map((warn, idx) => /* @__PURE__ */ jsx6("li", { children: warn }, idx)) })
              ] }),
              /* @__PURE__ */ jsx6(
                "button",
                {
                  disabled: readOnly,
                  onClick: () => onSetupProvider && onSetupProvider(providerId),
                  style: {
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #dee2e6",
                    backgroundColor: readOnly ? "#f8f9fa" : "#ffffff",
                    color: readOnly ? "#adb5bd" : "#007bff",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: readOnly ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease"
                  },
                  children: readOnly ? "\u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u092E\u094B\u0921 (Read-Only)" : "\u092A\u094D\u0930\u0926\u093E\u0924\u093E \u0938\u0947\u091F\u0905\u092A \u0915\u0930\u0947\u0902 (Setup)"
                }
              )
            ]
          },
          providerId
        );
      })
    }
  ) });
}

// src/settings/KnowledgeSourceSettings.tsx
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
function KnowledgeSourceSettings({
  knowledgeSources = [],
  onConfigureSource,
  readOnly = true
}) {
  if (!knowledgeSources || knowledgeSources.length === 0) {
    return /* @__PURE__ */ jsx7(
      SettingsEmptyState,
      {
        title_hi: "\u0915\u094B\u0908 \u091C\u094D\u091E\u093E\u0928 \u0938\u094D\u0930\u094B\u0924 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948",
        message_hi: "\u0915\u0943\u092A\u092F\u093E \u090F\u0906\u0908 \u0938\u0939\u093E\u092F\u0915\u094B\u0902 \u0915\u0947 \u0938\u0902\u0926\u0930\u094D\u092D \u0938\u0902\u091A\u092F \u0915\u0947 \u0932\u093F\u090F \u091C\u094D\u091E\u093E\u0928 \u0938\u094D\u0930\u094B\u0924\u094B\u0902 \u0915\u0940 \u0938\u0942\u091A\u0940 \u092A\u094D\u0930\u0926\u093E\u0928 \u0915\u0930\u0947\u0902\u0964"
      }
    );
  }
  return /* @__PURE__ */ jsx7("div", { className: "knowledge-source-settings-panel", style: { fontFamily: "inherit" }, children: /* @__PURE__ */ jsx7(
    "div",
    {
      style: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: "16px"
      },
      children: knowledgeSources.map((s) => {
        const sourceId = s.sourceId || "unknown";
        return /* @__PURE__ */ jsxs5(
          "div",
          {
            className: "knowledge-card",
            style: {
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: "#ffffff",
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "between"
            },
            children: [
              /* @__PURE__ */ jsxs5("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }, children: [
                /* @__PURE__ */ jsxs5("div", { children: [
                  /* @__PURE__ */ jsx7("h4", { style: { margin: "0 0 4px 0", color: "#343a40", fontWeight: 600 }, children: s.label || sourceId }),
                  /* @__PURE__ */ jsxs5("span", { style: { fontSize: "12px", color: "#6c757d", textTransform: "uppercase" }, children: [
                    "\u092A\u094D\u0930\u0915\u093E\u0930: ",
                    s.sourceType || "Manual"
                  ] })
                ] }),
                /* @__PURE__ */ jsx7(SettingsStatusBadge, { status: s.status || (s.enabled ? "ready" : "disabled") })
              ] }),
              /* @__PURE__ */ jsxs5("div", { style: { fontSize: "13px", color: "#495057", marginBottom: "16px", flexGrow: 1 }, children: [
                /* @__PURE__ */ jsxs5("div", { style: { marginBottom: "6px" }, children: [
                  /* @__PURE__ */ jsx7("strong", { children: "\u0938\u093F\u0902\u0915 \u092E\u094B\u0921 (Sync Mode):" }),
                  " ",
                  /* @__PURE__ */ jsx7("code", { style: { backgroundColor: "#e9ecef", padding: "2px 6px", borderRadius: "4px" }, children: s.syncMode || "manual" })
                ] }),
                s.allowedFileTypes && s.allowedFileTypes.length > 0 && /* @__PURE__ */ jsxs5("div", { style: { marginBottom: "6px" }, children: [
                  /* @__PURE__ */ jsx7("strong", { children: "\u0938\u094D\u0935\u0940\u0915\u0943\u0924 \u092B\u093C\u093E\u0907\u0932 \u092A\u094D\u0930\u0915\u093E\u0930:" }),
                  " ",
                  s.allowedFileTypes.join(", ")
                ] }),
                s.maxFileMb !== void 0 && /* @__PURE__ */ jsxs5("div", { style: { marginBottom: "6px" }, children: [
                  /* @__PURE__ */ jsx7("strong", { children: "\u0905\u0927\u093F\u0915\u0924\u092E \u0906\u0915\u093E\u0930 \u0938\u0940\u092E\u093E:" }),
                  " ",
                  `${s.maxFileMb} MB`
                ] })
              ] }),
              s.ingestionSteps && s.ingestionSteps.length > 0 && /* @__PURE__ */ jsxs5("div", { style: { backgroundColor: "#f8f9fa", borderRadius: "6px", padding: "10px", marginBottom: "12px", fontSize: "12px" }, children: [
                /* @__PURE__ */ jsx7("strong", { style: { color: "#495057", display: "block", marginBottom: "6px" }, children: "\u0905\u0902\u0924\u0930\u094D\u0917\u094D\u0930\u0939\u0923 \u091A\u0930\u0923 (Ingestion Steps):" }),
                /* @__PURE__ */ jsx7("ul", { style: { margin: 0, paddingLeft: "16px", color: "#495057" }, children: s.ingestionSteps.map((step, idx) => /* @__PURE__ */ jsxs5("li", { style: { marginBottom: "4px" }, children: [
                  /* @__PURE__ */ jsx7("span", { style: { color: step.implemented ? "#28a745" : "#ffc107", marginRight: "4px" }, children: step.implemented ? "\u2713" : "\u25CB" }),
                  step.title_hi || step.stepId
                ] }, idx)) })
              ] }),
              s.errors && s.errors.length > 0 && /* @__PURE__ */ jsxs5("div", { style: { backgroundColor: "#fff5f5", border: "1px solid #ffe3e3", borderRadius: "4px", padding: "8px", marginBottom: "12px", fontSize: "12px", color: "#e03131" }, children: [
                /* @__PURE__ */ jsx7("strong", { children: "\u0924\u094D\u0930\u0941\u091F\u093F\u092F\u093E\u0902:" }),
                /* @__PURE__ */ jsx7("ul", { style: { margin: "4px 0 0 0", paddingLeft: "16px" }, children: s.errors.map((err, idx) => /* @__PURE__ */ jsx7("li", { children: err }, idx)) })
              ] }),
              /* @__PURE__ */ jsx7(
                "button",
                {
                  disabled: readOnly,
                  onClick: () => onConfigureSource && onConfigureSource(sourceId),
                  style: {
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #dee2e6",
                    backgroundColor: readOnly ? "#f8f9fa" : "#ffffff",
                    color: readOnly ? "#adb5bd" : "#007bff",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: readOnly ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease"
                  },
                  children: readOnly ? "\u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u092E\u094B\u0921 (Read-Only)" : "\u0938\u094D\u0930\u094B\u0924 \u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0915\u0930\u0947\u0902 (Configure)"
                }
              )
            ]
          },
          sourceId
        );
      })
    }
  ) });
}

// src/settings/ToolMarketplace.tsx
import { useState as useState5 } from "react";
import { createToolDryRun } from "@smart-shikka/core";
import { jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
function ToolMarketplace({
  tools = [],
  onTriggerDryRun,
  readOnly = true
}) {
  const [dryRunOutputs, setDryRunOutputs] = useState5({});
  if (!tools || tools.length === 0) {
    return /* @__PURE__ */ jsx8(
      SettingsEmptyState,
      {
        title_hi: "\u0915\u094B\u0908 \u090F\u0906\u0908 \u0909\u092A\u0915\u0930\u0923 (Tools) \u0938\u0915\u094D\u0930\u093F\u092F \u0928\u0939\u0940\u0902 \u0939\u0948",
        message_hi: "\u0915\u0943\u092A\u092F\u093E \u090F\u0906\u0908 \u0938\u0939\u093E\u092F\u0915\u094B\u0902 \u0915\u0947 \u0932\u093F\u090F \u0938\u0941\u0930\u0915\u094D\u0937\u093E \u0938\u0940\u092E\u093E\u0913\u0902 \u0915\u0947 \u0905\u0902\u0924\u0930\u094D\u0917\u0924 \u0909\u092A\u0915\u0930\u0923\u094B\u0902 \u0915\u0940 \u0938\u0942\u091A\u0940 \u092A\u094D\u0930\u0926\u093E\u0928 \u0915\u0930\u0947\u0902\u0964"
      }
    );
  }
  const handleSimulateDryRun = (t5, actionId) => {
    const key = `${t5.toolId || t5.integrationId}-${actionId}`;
    if (onTriggerDryRun) {
      onTriggerDryRun(t5.toolId || t5.integrationId, actionId);
    }
    const result = createToolDryRun(t5, actionId);
    setDryRunOutputs((prev) => ({
      ...prev,
      [key]: result.preview_hi || "\u0915\u094B\u0908 \u092A\u0942\u0930\u094D\u0935\u093E\u0935\u0932\u094B\u0915\u0928 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964"
    }));
  };
  return /* @__PURE__ */ jsx8("div", { className: "tool-marketplace-panel", style: { fontFamily: "inherit" }, children: /* @__PURE__ */ jsx8(
    "div",
    {
      style: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
        gap: "16px"
      },
      children: tools.map((t5) => {
        const toolId = t5.toolId || t5.integrationId || "unknown";
        return /* @__PURE__ */ jsxs6(
          "div",
          {
            className: "tool-card",
            style: {
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: "#ffffff",
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "between"
            },
            children: [
              /* @__PURE__ */ jsxs6("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }, children: [
                /* @__PURE__ */ jsxs6("div", { children: [
                  /* @__PURE__ */ jsx8("h4", { style: { margin: "0 0 4px 0", color: "#343a40", fontWeight: 600 }, children: t5.label || toolId }),
                  /* @__PURE__ */ jsxs6("span", { style: { fontSize: "12px", color: "#6c757d", textTransform: "uppercase" }, children: [
                    "\u0936\u094D\u0930\u0947\u0923\u0940: ",
                    t5.toolType || t5.integrationType || "Utility"
                  ] })
                ] }),
                /* @__PURE__ */ jsx8(SettingsStatusBadge, { status: t5.status || (t5.enabled ? "available" : "disabled") })
              ] }),
              /* @__PURE__ */ jsxs6("div", { style: { fontSize: "13px", color: "#495057", marginBottom: "16px", flexGrow: 1 }, children: [
                /* @__PURE__ */ jsxs6("div", { style: { marginBottom: "6px" }, children: [
                  /* @__PURE__ */ jsx8("strong", { children: "\u0928\u093F\u0937\u094D\u092A\u093E\u0926\u0928 \u092E\u094B\u0921 (Execution Mode):" }),
                  " ",
                  /* @__PURE__ */ jsx8("code", { style: { backgroundColor: "#e9ecef", padding: "2px 6px", borderRadius: "4px" }, children: t5.executionMode || "dry_run" })
                ] }),
                t5.safetyNote && /* @__PURE__ */ jsxs6("div", { style: { marginBottom: "8px", fontSize: "12px", color: "#495057", fontStyle: "italic" }, children: [
                  "\u2139 ",
                  t5.safetyNote
                ] }),
                /* @__PURE__ */ jsxs6("div", { style: { marginTop: "12px" }, children: [
                  /* @__PURE__ */ jsx8("strong", { style: { display: "block", marginBottom: "4px" }, children: "\u0938\u094D\u0935\u0940\u0915\u0943\u0924 \u0915\u094D\u0930\u093F\u092F\u093E\u090F\u0902 (Allowed Actions):" }),
                  t5.allowedActions && t5.allowedActions.length > 0 ? /* @__PURE__ */ jsx8("div", { style: { display: "flex", flexWrap: "wrap", gap: "4px" }, children: t5.allowedActions.map((act) => /* @__PURE__ */ jsx8("span", { style: { backgroundColor: "#e8f4fd", color: "#1971c2", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 500 }, children: act }, act)) }) : /* @__PURE__ */ jsx8("span", { style: { color: "#adb5bd", fontSize: "12px" }, children: "\u0915\u094B\u0908 \u0938\u094D\u0935\u0940\u0915\u0943\u0924 \u0915\u094D\u0930\u093F\u092F\u093E \u0928\u0939\u0940\u0902" })
                ] }),
                /* @__PURE__ */ jsxs6("div", { style: { marginTop: "12px" }, children: [
                  /* @__PURE__ */ jsx8("strong", { style: { display: "block", marginBottom: "4px" }, children: "\u0905\u0935\u0930\u0941\u0926\u094D\u0927 \u0938\u0902\u0935\u0947\u0926\u0928\u0936\u0940\u0932 \u0915\u094D\u0930\u093F\u092F\u093E\u090F\u0902 (Blocked Actions):" }),
                  t5.blockedActions && t5.blockedActions.length > 0 ? /* @__PURE__ */ jsx8("div", { style: { display: "flex", flexWrap: "wrap", gap: "4px" }, children: t5.blockedActions.map((act) => /* @__PURE__ */ jsx8("span", { style: { backgroundColor: "#fff5f5", color: "#c92a2a", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 500 }, children: act }, act)) }) : /* @__PURE__ */ jsx8("span", { style: { color: "#adb5bd", fontSize: "12px" }, children: "\u0915\u094B\u0908 \u0905\u0935\u0930\u0941\u0926\u094D\u0927 \u0915\u094D\u0930\u093F\u092F\u093E \u0928\u0939\u0940\u0902" })
                ] })
              ] }),
              t5.enabled && t5.allowedActions && t5.allowedActions.length > 0 && /* @__PURE__ */ jsxs6("div", { style: { marginTop: "14px", borderTop: "1px solid #eee", paddingTop: "14px" }, children: [
                /* @__PURE__ */ jsx8("span", { style: { fontSize: "12px", fontWeight: 600, color: "#495057", display: "block", marginBottom: "6px" }, children: "\u0938\u093F\u092E\u094D\u092F\u0941\u0932\u0947\u091F\u0947\u0921 \u0921\u094D\u0930\u093E\u0908-\u0930\u0928 \u0938\u0941\u0930\u0915\u094D\u0937\u093E \u091C\u093E\u0902\u091A (Dry Run Sandbox):" }),
                /* @__PURE__ */ jsxs6("div", { style: { display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "10px" }, children: [
                  t5.allowedActions.map((act) => /* @__PURE__ */ jsxs6(
                    "button",
                    {
                      onClick: () => handleSimulateDryRun(t5, act),
                      disabled: readOnly,
                      style: {
                        padding: "4px 8px",
                        borderRadius: "4px",
                        border: "1px solid #dee2e6",
                        backgroundColor: "#f8f9fa",
                        fontSize: "11px",
                        cursor: readOnly ? "not-allowed" : "pointer",
                        opacity: readOnly ? 0.6 : 1
                      },
                      children: [
                        "\u26A1 ",
                        act
                      ]
                    },
                    act
                  )),
                  t5.blockedActions && t5.blockedActions.map((act) => /* @__PURE__ */ jsxs6(
                    "button",
                    {
                      onClick: () => handleSimulateDryRun(t5, act),
                      disabled: readOnly,
                      style: {
                        padding: "4px 8px",
                        borderRadius: "4px",
                        border: "1px solid #ffc9c9",
                        backgroundColor: "#fff5f5",
                        color: "#c92a2a",
                        fontSize: "11px",
                        cursor: readOnly ? "not-allowed" : "pointer",
                        opacity: readOnly ? 0.6 : 1
                      },
                      children: [
                        "\u{1F512} ",
                        act
                      ]
                    },
                    act
                  ))
                ] }),
                Object.keys(dryRunOutputs).map((k) => {
                  const hasAction = t5.allowedActions.some((act) => k.endsWith(`-${act}`)) || t5.blockedActions && t5.blockedActions.some((act) => k.endsWith(`-${act}`));
                  if (!hasAction || !dryRunOutputs[k]) return null;
                  const isBlocked = k.split("-").pop() && (t5.blockedActions?.includes(k.split("-").pop()) || createToolDryRun(t5, k.split("-").pop()).blocked);
                  return /* @__PURE__ */ jsx8(
                    "div",
                    {
                      style: {
                        backgroundColor: isBlocked ? "#fff5f5" : "#f1f3f5",
                        borderLeft: isBlocked ? "4px solid #c92a2a" : "4px solid #495057",
                        padding: "8px 10px",
                        borderRadius: "0 4px 4px 0",
                        fontSize: "11px",
                        color: isBlocked ? "#c92a2a" : "#212529",
                        marginTop: "8px",
                        wordBreak: "break-word"
                      },
                      children: dryRunOutputs[k]
                    },
                    k
                  );
                })
              ] })
            ]
          },
          toolId
        );
      })
    }
  ) });
}

// src/settings/IntegrationSettings.tsx
import { jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
function IntegrationSettings({
  integrations = [],
  readOnly = true
}) {
  if (!integrations || integrations.length === 0) {
    return /* @__PURE__ */ jsx9(
      SettingsEmptyState,
      {
        title_hi: "\u0915\u094B\u0908 \u0938\u0915\u094D\u0930\u093F\u092F \u090F\u0915\u0940\u0915\u0930\u0923 (Integrations) \u0928\u0939\u0940\u0902 \u0939\u0948",
        message_hi: "\u0915\u0943\u092A\u092F\u093E \u090F\u0906\u0908 \u0938\u0939\u093E\u092F\u0915\u094B\u0902 \u0915\u0947 \u0915\u093E\u0930\u094D\u092F\u092A\u094D\u0930\u0935\u093E\u0939 \u092E\u0947\u0902 \u0938\u0939\u092F\u094B\u0917 \u0926\u0947\u0928\u0947 \u0935\u093E\u0932\u0947 \u090F\u0915\u0940\u0915\u0930\u0923\u094B\u0902 \u0915\u0940 \u0938\u0942\u091A\u0940 \u092A\u094D\u0930\u0926\u093E\u0928 \u0915\u0930\u0947\u0902\u0964"
      }
    );
  }
  return /* @__PURE__ */ jsx9("div", { className: "integration-settings-panel", style: { fontFamily: "inherit" }, children: /* @__PURE__ */ jsx9(
    "div",
    {
      style: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: "16px"
      },
      children: integrations.map((i) => {
        const integrationId = i.integrationId || i.toolId || "unknown";
        return /* @__PURE__ */ jsxs7(
          "div",
          {
            className: "integration-card",
            style: {
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: "#ffffff",
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "between"
            },
            children: [
              /* @__PURE__ */ jsxs7("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }, children: [
                /* @__PURE__ */ jsxs7("div", { children: [
                  /* @__PURE__ */ jsx9("h4", { style: { margin: "0 0 4px 0", color: "#343a40", fontWeight: 600 }, children: i.label || integrationId }),
                  /* @__PURE__ */ jsxs7("span", { style: { fontSize: "12px", color: "#6c757d", textTransform: "uppercase" }, children: [
                    "\u092A\u094D\u0930\u0915\u093E\u0930: ",
                    i.integrationType || "API Integration"
                  ] })
                ] }),
                /* @__PURE__ */ jsx9(SettingsStatusBadge, { status: i.status || (i.enabled ? "available" : "disabled") })
              ] }),
              /* @__PURE__ */ jsxs7("div", { style: { fontSize: "13px", color: "#495057", marginBottom: "16px", flexGrow: 1 }, children: [
                /* @__PURE__ */ jsxs7("div", { style: { marginBottom: "6px" }, children: [
                  /* @__PURE__ */ jsx9("strong", { children: "\u0915\u094D\u0930\u0947\u0921\u0947\u0902\u0936\u093F\u092F\u0932 \u0938\u094D\u0925\u093F\u0924\u093F:" }),
                  " ",
                  /* @__PURE__ */ jsx9("code", { style: { backgroundColor: "#f1f3f5", padding: "2px 6px", borderRadius: "4px" }, children: i.credentialMode || "not_configured" })
                ] }),
                /* @__PURE__ */ jsxs7("div", { style: { marginTop: "12px" }, children: [
                  /* @__PURE__ */ jsx9("strong", { style: { display: "block", marginBottom: "4px" }, children: "\u0938\u094D\u0935\u0940\u0915\u0943\u0924 \u0915\u094D\u0930\u093F\u092F\u093E\u090F\u0902 (Allowed Actions):" }),
                  i.allowedActions && i.allowedActions.length > 0 ? /* @__PURE__ */ jsx9("div", { style: { display: "flex", flexWrap: "wrap", gap: "4px" }, children: i.allowedActions.map((act) => /* @__PURE__ */ jsx9("span", { style: { backgroundColor: "#e2f9e1", color: "#2b8a3e", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 500 }, children: act }, act)) }) : /* @__PURE__ */ jsx9("span", { style: { color: "#adb5bd", fontSize: "12px" }, children: "\u0915\u094B\u0908 \u0938\u094D\u0935\u0940\u0915\u0943\u0924 \u0915\u094D\u0930\u093F\u092F\u093E \u0928\u0939\u0940\u0902" })
                ] }),
                /* @__PURE__ */ jsxs7("div", { style: { marginTop: "12px" }, children: [
                  /* @__PURE__ */ jsx9("strong", { style: { display: "block", marginBottom: "4px" }, children: "\u0905\u0935\u0930\u0941\u0926\u094D\u0927 \u0938\u0902\u0935\u0947\u0926\u0928\u0936\u0940\u0932 \u0915\u094D\u0930\u093F\u092F\u093E\u090F\u0902 (Blocked Actions):" }),
                  i.blockedActions && i.blockedActions.length > 0 ? /* @__PURE__ */ jsx9("div", { style: { display: "flex", flexWrap: "wrap", gap: "4px" }, children: i.blockedActions.map((act) => /* @__PURE__ */ jsx9("span", { style: { backgroundColor: "#fff5f5", color: "#c92a2a", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 500 }, children: act }, act)) }) : /* @__PURE__ */ jsx9("span", { style: { color: "#adb5bd", fontSize: "12px" }, children: "\u0915\u094B\u0908 \u0905\u0935\u0930\u0941\u0926\u094D\u0927 \u0915\u094D\u0930\u093F\u092F\u093E \u0928\u0939\u0940\u0902" })
                ] })
              ] }),
              /* @__PURE__ */ jsx9(
                "button",
                {
                  disabled: readOnly,
                  style: {
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #dee2e6",
                    backgroundColor: "#f8f9fa",
                    color: "#adb5bd",
                    fontWeight: 600,
                    fontSize: "13px",
                    cursor: "not-allowed",
                    transition: "all 0.2s ease"
                  },
                  children: "\u{1F512} \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u090F\u0915\u0940\u0915\u0930\u0923 \u092E\u094B\u0921 (OAuth / Real Send Restricted)"
                }
              )
            ]
          },
          integrationId
        );
      })
    }
  ) });
}

// src/settings/AppRegistryPanel.tsx
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
function AppRegistryPanel({ appAdapter, appSettings }) {
  const appId = appAdapter?.appId || appSettings?.appId || "unknown_app";
  const appName = appAdapter?.appName || appSettings?.appName || "Unnamed Application";
  const modules = appAdapter?.modules || appSettings?.modules || [];
  const roles = appAdapter?.roles || appSettings?.roles || [];
  const knowledgeNamespace = appAdapter?.knowledgeNamespace || appSettings?.knowledgeNamespace || "not_configured";
  const providersCount = appSettings?.providers?.length || appAdapter?.appSettings?.providers?.length || 0;
  const knowledgeCount = appSettings?.knowledgeSources?.length || appAdapter?.appSettings?.knowledgeSources?.length || 0;
  const integrationsCount = appSettings?.integrations?.length || appAdapter?.appSettings?.integrations?.length || 0;
  return /* @__PURE__ */ jsxs8(
    "div",
    {
      className: "app-registry-panel",
      style: {
        fontFamily: "inherit",
        backgroundColor: "#ffffff",
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
      },
      children: [
        /* @__PURE__ */ jsxs8("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", borderBottom: "1px solid #eee", paddingBottom: "14px" }, children: [
          /* @__PURE__ */ jsxs8("div", { children: [
            /* @__PURE__ */ jsx10("h3", { style: { margin: "0 0 4px 0", color: "#212529", fontWeight: 600 }, children: appName }),
            /* @__PURE__ */ jsxs8("span", { style: { fontSize: "12px", color: "#6c757d" }, children: [
              "\u090F\u092A\u094D\u0932\u093F\u0915\u0947\u0936\u0928 \u092A\u0939\u091A\u093E\u0928\u0915\u0930\u094D\u0924\u093E (App ID): ",
              /* @__PURE__ */ jsx10("code", { children: appId })
            ] })
          ] }),
          /* @__PURE__ */ jsx10(SettingsStatusBadge, { status: "available" })
        ] }),
        /* @__PURE__ */ jsxs8(
          "div",
          {
            style: {
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "24px"
            },
            children: [
              /* @__PURE__ */ jsxs8("div", { style: { backgroundColor: "#f8f9fa", padding: "14px", borderRadius: "6px", textAlign: "center" }, children: [
                /* @__PURE__ */ jsx10("span", { style: { fontSize: "24px", display: "block", marginBottom: "4px" }, children: "\u{1F916}" }),
                /* @__PURE__ */ jsx10("span", { style: { fontSize: "13px", color: "#6c757d", display: "block" }, children: "\u092A\u094D\u0930\u0926\u093E\u0924\u093E (Providers)" }),
                /* @__PURE__ */ jsx10("strong", { style: { fontSize: "20px", color: "#212529" }, children: providersCount })
              ] }),
              /* @__PURE__ */ jsxs8("div", { style: { backgroundColor: "#f8f9fa", padding: "14px", borderRadius: "6px", textAlign: "center" }, children: [
                /* @__PURE__ */ jsx10("span", { style: { fontSize: "24px", display: "block", marginBottom: "4px" }, children: "\u{1F4DA}" }),
                /* @__PURE__ */ jsx10("span", { style: { fontSize: "13px", color: "#6c757d", display: "block" }, children: "\u091C\u094D\u091E\u093E\u0928 \u0938\u094D\u0930\u094B\u0924 (Knowledge)" }),
                /* @__PURE__ */ jsx10("strong", { style: { fontSize: "20px", color: "#212529" }, children: knowledgeCount })
              ] }),
              /* @__PURE__ */ jsxs8("div", { style: { backgroundColor: "#f8f9fa", padding: "14px", borderRadius: "6px", textAlign: "center" }, children: [
                /* @__PURE__ */ jsx10("span", { style: { fontSize: "24px", display: "block", marginBottom: "4px" }, children: "\u{1F6E0}" }),
                /* @__PURE__ */ jsx10("span", { style: { fontSize: "13px", color: "#6c757d", display: "block" }, children: "\u090F\u0915\u0940\u0915\u0930\u0923 (Integrations)" }),
                /* @__PURE__ */ jsx10("strong", { style: { fontSize: "20px", color: "#212529" }, children: integrationsCount })
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsxs8("div", { style: { fontSize: "14px", color: "#495057" }, children: [
          /* @__PURE__ */ jsxs8("div", { style: { marginBottom: "12px", display: "flex", alignItems: "start" }, children: [
            /* @__PURE__ */ jsx10("strong", { style: { width: "160px", flexShrink: 0 }, children: "\u091C\u094D\u091E\u093E\u0928 \u0928\u0947\u092E\u0938\u094D\u092A\u0947\u0938:" }),
            /* @__PURE__ */ jsx10("code", { children: knowledgeNamespace })
          ] }),
          /* @__PURE__ */ jsxs8("div", { style: { marginBottom: "12px", display: "flex", alignItems: "start" }, children: [
            /* @__PURE__ */ jsx10("strong", { style: { width: "160px", flexShrink: 0 }, children: "\u0938\u0915\u094D\u0930\u093F\u092F \u092E\u0949\u0921\u094D\u092F\u0942\u0932 (Modules):" }),
            /* @__PURE__ */ jsx10("div", { style: { display: "flex", flexWrap: "wrap", gap: "4px" }, children: modules.map((m) => /* @__PURE__ */ jsx10("span", { style: { backgroundColor: "#e9ecef", color: "#495057", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }, children: m }, m)) })
          ] }),
          /* @__PURE__ */ jsxs8("div", { style: { marginBottom: "12px", display: "flex", alignItems: "start" }, children: [
            /* @__PURE__ */ jsx10("strong", { style: { width: "160px", flexShrink: 0 }, children: "\u0938\u0915\u094D\u0930\u093F\u092F \u092D\u0942\u092E\u093F\u0915\u093E\u090F\u0902 (Roles):" }),
            /* @__PURE__ */ jsx10("div", { style: { display: "flex", flexWrap: "wrap", gap: "4px" }, children: roles.map((r) => /* @__PURE__ */ jsx10("span", { style: { backgroundColor: "#e9ecef", color: "#495057", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }, children: r }, r)) })
          ] })
        ] })
      ]
    }
  );
}

// src/settings/SettingsPanel.tsx
import { useState as useState11 } from "react";

// src/settings/McpToolsPanel.tsx
import { useCallback as useCallback3, useEffect as useEffect3, useState as useState6 } from "react";
import { jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
var STR = {
  title: { en: "MCP tools (permission gate)", hi: "MCP \u0909\u092A\u0915\u0930\u0923 (permission gate)" },
  sub: {
    en: "Canonical gate contract \u2014 every family is blocked by default; owner approval + operator confirmation + redaction are required. No tool executes from this surface.",
    hi: "Canonical gate contract \u2014 \u0939\u0930 family default \u0930\u0942\u092A \u0938\u0947 blocked \u0939\u0948; owner approval + operator confirmation + redaction \u0905\u0928\u093F\u0935\u093E\u0930\u094D\u092F \u0939\u0948\u0902\u0964 \u0907\u0938 surface \u0938\u0947 \u0915\u094B\u0908 tool execute \u0928\u0939\u0940\u0902 \u0939\u094B\u0924\u093E\u0964"
  },
  loading: { en: "Loading tool gate contract\u2026", hi: "Tool gate contract \u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026" },
  families: { en: "Tool families", hi: "Tool families" },
  contract: { en: "Gate contract", hi: "Gate contract" },
  ownerApproval: { en: "owner approval", hi: "owner approval" },
  operatorConfirm: { en: "operator confirmation", hi: "operator confirmation" },
  redaction: { en: "redaction", hi: "redaction" },
  noExec: { en: "no real execution", hi: "\u0915\u094B\u0908 real execution \u0928\u0939\u0940\u0902" },
  empty: { en: "No tool families registered.", hi: "\u0915\u094B\u0908 tool family registered \u0928\u0939\u0940\u0902\u0964" }
};
function tr(key, locale) {
  const entry = STR[key];
  return typeof entry === "string" ? entry : entry[locale];
}
function McpToolsPanel({
  token,
  locale = "en",
  baseUrl = "",
  fetchImpl
}) {
  const doFetch = fetchImpl ?? fetch;
  const [families, setFamilies] = useState6([]);
  const [contract, setContract] = useState6([]);
  const [loading, setLoading] = useState6(true);
  const [error, setError] = useState6(null);
  const getRows = useCallback3(
    async (path) => {
      const res = await doFetch(`${baseUrl}${path}`, {
        headers: { Authorization: `Bearer ${token ?? ""}` }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(typeof json.error === "string" ? json.error : `http_error_${res.status}`);
        err.name = "api_error";
        throw err;
      }
      return Array.isArray(json.rows) ? json.rows : [];
    },
    [doFetch, baseUrl, token]
  );
  useEffect3(() => {
    let cancelled = false;
    if (!token) {
      setError("auth_required");
      setLoading(false);
      return;
    }
    setLoading(true);
    void Promise.all([
      getRows("/api/mcp-tools-permission/tool-family-catalog"),
      getRows("/api/mcp-tools-permission/permission-gate-contract")
    ]).then(([fam, con]) => {
      if (cancelled) return;
      setFamilies(fam);
      setContract(con);
      setError(null);
    }).catch((e) => {
      if (!cancelled) setError(errorToCode(e));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [getRows, token]);
  return /* @__PURE__ */ jsxs9("div", { style: { display: "grid", gap: 14 }, children: [
    /* @__PURE__ */ jsxs9("div", { children: [
      /* @__PURE__ */ jsx11("h4", { style: { margin: "0 0 4px", fontSize: 15 }, children: tr("title", locale) }),
      /* @__PURE__ */ jsx11("p", { style: { margin: 0, fontSize: 12, color: "#6c757d" }, children: tr("sub", locale) })
    ] }),
    loading ? /* @__PURE__ */ jsx11("p", { style: { fontSize: 13 }, children: tr("loading", locale) }) : null,
    error ? /* @__PURE__ */ jsx11("p", { role: "alert", style: { fontSize: 12, color: "#c92a2a" }, children: safeErrorCode(error) }) : null,
    !loading && !error && families.length === 0 ? /* @__PURE__ */ jsx11("p", { style: { fontSize: 13, color: "#6c757d" }, children: tr("empty", locale) }) : null,
    families.length > 0 ? /* @__PURE__ */ jsxs9("div", { children: [
      /* @__PURE__ */ jsx11("h5", { style: { margin: "0 0 6px", fontSize: 13 }, children: tr("families", locale) }),
      /* @__PURE__ */ jsxs9("table", { style: { width: "100%", borderCollapse: "collapse", fontSize: 12 }, children: [
        /* @__PURE__ */ jsx11("thead", { children: /* @__PURE__ */ jsxs9("tr", { style: { textAlign: "left", borderBottom: "1px solid #dee2e6" }, children: [
          /* @__PURE__ */ jsx11("th", { style: { padding: "4px 6px" }, children: "family" }),
          /* @__PURE__ */ jsx11("th", { style: { padding: "4px 6px" }, children: "gate" }),
          /* @__PURE__ */ jsx11("th", { style: { padding: "4px 6px" }, children: "requirements" })
        ] }) }),
        /* @__PURE__ */ jsx11("tbody", { children: families.map((f) => /* @__PURE__ */ jsxs9("tr", { style: { borderBottom: "1px solid #f1f3f5" }, children: [
          /* @__PURE__ */ jsx11("td", { style: { padding: "6px" }, children: /* @__PURE__ */ jsx11("code", { children: f.toolFamilyPreview }) }),
          /* @__PURE__ */ jsx11("td", { style: { padding: "6px" }, children: /* @__PURE__ */ jsx11("code", { style: { color: "#c92a2a" }, children: f.defaultGateStatusPreview }) }),
          /* @__PURE__ */ jsx11("td", { style: { padding: "6px", color: "#6c757d" }, children: [
            f.ownerApprovalRequired ? tr("ownerApproval", locale) : null,
            f.operatorConfirmationRequired ? tr("operatorConfirm", locale) : null,
            f.redactionPolicyRequired ? tr("redaction", locale) : null,
            tr("noExec", locale)
          ].filter(Boolean).join(" \xB7 ") })
        ] }, f.familyIdPreview)) })
      ] })
    ] }) : null,
    contract.length > 0 ? /* @__PURE__ */ jsxs9("div", { children: [
      /* @__PURE__ */ jsx11("h5", { style: { margin: "0 0 6px", fontSize: 13 }, children: tr("contract", locale) }),
      /* @__PURE__ */ jsx11("ul", { style: { margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 6 }, children: contract.map((c) => /* @__PURE__ */ jsxs9(
        "li",
        {
          style: { border: "1px solid #dee2e6", borderRadius: 6, padding: "8px 10px", fontSize: 12 },
          children: [
            /* @__PURE__ */ jsx11("code", { children: c.gateContractIdPreview }),
            /* @__PURE__ */ jsxs9("span", { style: { color: "#6c757d", marginLeft: 8 }, children: [
              c.gateStatusPreview,
              " \u2014 ",
              tr("noExec", locale)
            ] })
          ]
        },
        c.gateContractIdPreview
      )) })
    ] }) : null
  ] });
}

// src/settings/SkillsSettings.tsx
import { useEffect as useEffect4, useMemo as useMemo3, useState as useState7 } from "react";

// src/components/base/theme.ts
var baseUiTokens = {
  colors: {
    surface: "#f8f7f5",
    surfaceStrong: "#ffffff",
    border: "#dadce4",
    borderStrong: "#b8bbc4",
    text: "#111215",
    textMuted: "#4f5056",
    primary: "#c2410c",
    secondary: "#f5f2ed",
    danger: "#b91c1c",
    success: "#047857",
    warning: "#f59e0b",
    info: "#0059c6",
    focus: "#c2410c"
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px"
  },
  radius: {
    sm: "6px",
    md: "8px",
    lg: "12px",
    pill: "9999px"
  },
  font: {
    family: "'Segoe UI', system-ui, sans-serif",
    weightMedium: 500,
    weightSemibold: 600
  }
};

// src/workspace/shellTheme.ts
var tokens = baseUiTokens;
var warmNeutralTokens = {
  surface: "#f8f7f5",
  surfaceStrong: "#ffffff",
  text: "#111215",
  textMuted: "#4f5056",
  border: "#dadce4",
  borderStrong: "#b8bbc4",
  warmAccent: "#c2410c",
  info: "#0059c6"
};
function themeColors(theme2) {
  if (theme2 === "dark") {
    return {
      surface: "#1f2023",
      surfaceStrong: "#111215",
      text: "#f4f4f5",
      textMuted: "#9ca3af",
      border: "#2e3035",
      borderStrong: "#4b4d53",
      warmAccent: "#f59e0b",
      info: "#60a5fa"
    };
  }
  return {
    surface: warmNeutralTokens.surface,
    surfaceStrong: warmNeutralTokens.surfaceStrong,
    text: warmNeutralTokens.text,
    textMuted: warmNeutralTokens.textMuted,
    border: warmNeutralTokens.border,
    borderStrong: warmNeutralTokens.borderStrong,
    warmAccent: "#c2410c",
    info: warmNeutralTokens.info
  };
}

// src/settings/SkillsSettings.tsx
import { Fragment, jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
var t = baseUiTokens;
var w = warmNeutralTokens;
var theme = {
  surface: w.surface,
  surfaceStrong: w.surfaceStrong,
  text: w.text,
  textMuted: w.textMuted,
  border: w.border,
  borderStrong: w.borderStrong,
  primary: w.warmAccent,
  primaryText: w.surfaceStrong,
  info: w.info,
  successBg: "#047857",
  successText: "#ecfdf5",
  warningBg: "#92400e",
  warningText: "#fffbeb",
  dangerBg: "#b91c1c",
  dangerText: "#fef2f2",
  neutralBg: "#f1f5f9",
  neutralText: "#334155",
  warningSurface: "#fffbeb",
  warningBorder: "#f59e0b",
  dangerSurface: "#fef2f2",
  dangerBorder: "#fca5a5"
};
var FRIENDLY_META = {
  "smart-shikka-design-pack": { displayName: "Frontend Design", publisherLine: "Anthropic", defaultEnabled: true },
  "smart-shikka-engineering-pack": { displayName: "Superpowers Engineering", publisherLine: "Prime Radiant / Superpowers", defaultEnabled: true },
  "smart-shikka-project-pack": { displayName: "GSD Project Planning", publisherLine: "Open GSD", defaultEnabled: false },
  "smart-shikka-leadership-pack": { displayName: "gstack CEO Review", publisherLine: "Garry Tan / gstack", defaultEnabled: true }
};
var ID_ORDER = [
  "smart-shikka-design-pack",
  "smart-shikka-engineering-pack",
  "smart-shikka-project-pack",
  "smart-shikka-leadership-pack"
];
function friendly(skill) {
  const meta = FRIENDLY_META[skill.extensionId] ?? { displayName: skill.name, publisherLine: skill.publisher ?? "", defaultEnabled: false };
  const statusLabel2 = skill.status === "enabled" || skill.enabled ? "Enabled" : "Disabled";
  return { ...meta, statusLabel: statusLabel2 };
}
function riskVariant(risk) {
  if (risk === "low") return "success";
  if (risk === "medium") return "warning";
  if (risk === "high" || risk === "critical") return "danger";
  return "neutral";
}
function highlightColor(variant) {
  if (variant === "danger") return theme.dangerBg;
  if (variant === "warning") return theme.warningBg;
  if (variant === "success") return theme.successBg;
  return theme.text;
}
function SkillsSettings({ service, readOnly = true, initialSkills }) {
  const [skills, setSkills] = useState7(initialSkills ?? []);
  const [loading, setLoading] = useState7(false);
  const [error, setError] = useState7(null);
  const [view, setView] = useState7("list");
  const [selected, setSelected] = useState7(null);
  const [actionBusy, setActionBusy] = useState7(false);
  const [updateCheck, setUpdateCheck] = useState7(null);
  const sortedSkills = useMemo3(() => {
    const map = new Map(skills.map((s) => [s.extensionId, s]));
    const ordered = [];
    for (const id of ID_ORDER) {
      const s = map.get(id);
      if (s) {
        ordered.push(s);
        map.delete(id);
      }
    }
    ordered.push(...map.values());
    return ordered;
  }, [skills]);
  async function loadSkills() {
    if (!service) return;
    setLoading(true);
    setError(null);
    try {
      const list = await service.listSkills();
      setSkills(list);
    } catch (err) {
      setError(errorToCode(err));
    } finally {
      setLoading(false);
    }
  }
  useEffect4(() => {
    if (initialSkills) return;
    loadSkills();
  }, [service, initialSkills]);
  async function handleEnable(skill) {
    if (!service || readOnly || actionBusy) return;
    setActionBusy(true);
    setError(null);
    try {
      await service.enableSkill(skill.extensionId, void 0);
      await loadSkills();
    } catch (err) {
      setError(errorToCode(err));
    } finally {
      setActionBusy(false);
    }
  }
  async function handleDisable(skill) {
    if (!service || readOnly || actionBusy) return;
    setActionBusy(true);
    setError(null);
    try {
      await service.disableSkill(skill.extensionId);
      await loadSkills();
    } catch (err) {
      setError(errorToCode(err));
    } finally {
      setActionBusy(false);
    }
  }
  function openDetail(skill) {
    setSelected(skill);
    setUpdateCheck(null);
    setView("detail");
  }
  function openProvenance(skill) {
    setSelected(skill);
    setView("provenance");
  }
  function openUpdate(skill) {
    setSelected(skill);
    setUpdateCheck(null);
    setView("update");
  }
  async function handleCheckUpdate(skill) {
    if (!service || actionBusy) return;
    setActionBusy(true);
    setError(null);
    setUpdateCheck(null);
    try {
      const result = await service.checkSkillUpdate(skill.extensionId);
      setUpdateCheck(result);
    } catch (err) {
      setError(errorToCode(err));
    } finally {
      setActionBusy(false);
    }
  }
  async function handleUpdate(skill) {
    if (!service || actionBusy) return;
    setActionBusy(true);
    setError(null);
    try {
      const updated = await service.updateSkill(skill.extensionId);
      const next = [...skills];
      const idx = next.findIndex((s) => s.extensionId === updated.extensionId);
      if (idx >= 0) next[idx] = updated;
      setSkills(next);
      setSelected(updated);
      setUpdateCheck(null);
      setView("detail");
    } catch (err) {
      setError(errorToCode(err));
    } finally {
      setActionBusy(false);
    }
  }
  if (!service && !initialSkills) {
    return /* @__PURE__ */ jsx12("div", { style: {
      padding: t.spacing.xl,
      color: theme.textMuted,
      backgroundColor: theme.surfaceStrong,
      borderRadius: t.radius.md,
      border: `1px solid ${theme.border}`,
      fontFamily: t.font.family
    }, children: "\u0915\u094C\u0936\u0932 \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0902\u0964 \u0915\u0943\u092A\u092F\u093E WorkspaceAgentSkillsService \u0915\u0928\u0947\u0915\u094D\u091F \u0915\u0930\u0947\u0902\u0964" });
  }
  if (view === "detail" && selected) {
    return renderDetail(selected, () => setView("list"), () => openProvenance(selected));
  }
  if (view === "provenance" && selected) {
    return renderProvenance(selected, () => setView("detail"));
  }
  if (view === "update" && selected) {
    return renderUpdate(selected, () => setView("detail"));
  }
  function renderDetail(skill, onBack, onProvenance) {
    const info = friendly(skill);
    return /* @__PURE__ */ jsxs10("div", { style: cardStyle, children: [
      /* @__PURE__ */ jsx12("button", { onClick: onBack, style: { ...secondaryButtonStyle, marginBottom: t.spacing.md }, children: "\u2190 \u0935\u093E\u092A\u0938 \u0938\u0942\u091A\u0940" }),
      /* @__PURE__ */ jsx12("h3", { style: { margin: `0 0 ${t.spacing.sm}`, color: theme.text, fontFamily: t.font.family, fontSize: "18px" }, children: info.displayName }),
      /* @__PURE__ */ jsx12("p", { style: { margin: `0 0 ${t.spacing.lg}`, color: theme.textMuted, fontSize: "14px", fontFamily: t.font.family }, children: skill.description }),
      /* @__PURE__ */ jsxs10("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: t.spacing.md, marginBottom: t.spacing.lg }, children: [
        /* @__PURE__ */ jsx12(Field, { label: "Publisher", value: info.publisherLine }),
        /* @__PURE__ */ jsx12(Field, { label: "Status", value: info.statusLabel }),
        /* @__PURE__ */ jsx12(Field, { label: "Risk", value: skill.risk ?? "unknown", highlight: riskVariant(skill.risk) }),
        /* @__PURE__ */ jsx12(Field, { label: "Default mode", value: skill.defaultMode ?? "propose_only" }),
        /* @__PURE__ */ jsx12(Field, { label: "Version", value: skill.version ?? "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "Pinned SHA", value: skill.provenance?.sourceCommitSha ? String(skill.provenance.sourceCommitSha).slice(0, 16) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "License", value: skill.provenance?.sourceLicense ? String(skill.provenance.sourceLicense) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "Source", value: skill.source ?? "-" })
      ] }),
      /* @__PURE__ */ jsxs10("div", { style: { display: "flex", gap: t.spacing.md, flexWrap: "wrap", marginBottom: t.spacing.lg }, children: [
        !skill.enabled ? /* @__PURE__ */ jsx12("button", { disabled: readOnly || actionBusy, onClick: () => handleEnable(skill), style: primaryButtonStyle, children: "\u0938\u0915\u094D\u0937\u092E \u0915\u0930\u0947\u0902 (Enable)" }) : /* @__PURE__ */ jsx12("button", { disabled: readOnly || actionBusy, onClick: () => handleDisable(skill), style: secondaryButtonStyle, children: "\u0905\u0915\u094D\u0937\u092E \u0915\u0930\u0947\u0902 (Disable)" }),
        /* @__PURE__ */ jsx12("button", { onClick: onProvenance, style: secondaryButtonStyle, children: "\u0938\u094D\u0930\u094B\u0924 / provenance \u0926\u0947\u0916\u0947\u0902" }),
        /* @__PURE__ */ jsx12("button", { onClick: () => openUpdate(skill), style: secondaryButtonStyle, children: "\u0905\u092A\u0921\u0947\u091F \u091C\u093E\u0901\u091A\u0947\u0902" })
      ] }),
      skill.contentPreview && /* @__PURE__ */ jsxs10("div", { style: { backgroundColor: theme.surface, border: `1px solid ${theme.border}`, borderRadius: t.radius.sm, padding: t.spacing.lg }, children: [
        /* @__PURE__ */ jsx12("strong", { style: { color: theme.textMuted, fontFamily: t.font.family, fontSize: "13px" }, children: "SKILL.md \u092A\u0942\u0930\u094D\u0935\u093E\u0935\u0932\u094B\u0915\u0928:" }),
        /* @__PURE__ */ jsx12("pre", { style: { whiteSpace: "pre-wrap", fontSize: "13px", color: theme.text, maxHeight: "320px", overflow: "auto", fontFamily: t.font.family, margin: t.spacing.sm }, children: skill.contentPreview })
      ] })
    ] });
  }
  function renderProvenance(skill, onBack) {
    const info = friendly(skill);
    const provenance = skill.provenance ?? {};
    const conflict = skill.conflictMetadata ?? {};
    return /* @__PURE__ */ jsxs10("div", { style: cardStyle, children: [
      /* @__PURE__ */ jsx12("button", { onClick: onBack, style: { ...secondaryButtonStyle, marginBottom: t.spacing.md }, children: "\u2190 \u0935\u093E\u092A\u0938 \u0935\u093F\u0935\u0930\u0923" }),
      /* @__PURE__ */ jsxs10("h3", { style: { margin: `0 0 ${t.spacing.md}`, color: theme.text, fontFamily: t.font.family, fontSize: "18px" }, children: [
        info.displayName,
        " \u2014 \u0938\u094D\u0930\u094B\u0924 \u0914\u0930 \u0932\u093E\u0907\u0938\u0947\u0902\u0938"
      ] }),
      /* @__PURE__ */ jsxs10("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: t.spacing.md, marginBottom: t.spacing.lg }, children: [
        /* @__PURE__ */ jsx12(Field, { label: "Source provider", value: provenance.sourceProvider ? String(provenance.sourceProvider) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "Source repository", value: provenance.sourceRepository ? String(provenance.sourceRepository) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "Pinned commit", value: provenance.sourceCommitSha ? String(provenance.sourceCommitSha) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "Source path", value: provenance.sourcePath ? String(provenance.sourcePath) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "License", value: provenance.sourceLicense ? String(provenance.sourceLicense) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "Upstream name", value: provenance.upstreamName ? String(provenance.upstreamName) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "Upstream version", value: provenance.upstreamVersion ? String(provenance.upstreamVersion) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "Imported at", value: provenance.importedAt ? String(provenance.importedAt) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "Reviewed at", value: provenance.reviewedAt ? String(provenance.reviewedAt) : "-" }),
        /* @__PURE__ */ jsx12(Field, { label: "Attribution", value: provenance.attribution ? String(provenance.attribution) : "-" })
      ] }),
      /* @__PURE__ */ jsxs10("div", { style: { backgroundColor: theme.warningSurface, border: `1px solid ${theme.warningBorder}`, borderRadius: t.radius.sm, padding: t.spacing.lg, marginBottom: t.spacing.lg }, children: [
        /* @__PURE__ */ jsx12("strong", { style: { color: theme.warningBg, fontFamily: t.font.family, fontSize: "14px" }, children: "\u0938\u0932\u093E\u0939\u0915\u093E\u0930 \u0928\u094B\u091F (Advisory note):" }),
        /* @__PURE__ */ jsx12("p", { style: { margin: `${t.spacing.sm} 0 0`, fontSize: "13px", color: theme.warningBg, fontFamily: t.font.family }, children: conflict.advisoryOnly === true ? "\u092F\u0939 skill \u0915\u0947\u0935\u0932 \u0938\u0932\u093E\u0939\u0915\u093E\u0930/\u092A\u0942\u0930\u094D\u0935\u093E\u0935\u0932\u094B\u0915\u0928 \u092E\u094B\u0921 \u092E\u0947\u0902 \u091A\u0932\u0924\u0940 \u0939\u0948\u0964 \u092F\u0939 \u0938\u094D\u0935\u091A\u093E\u0932\u093F\u0924 \u0928\u093F\u0937\u094D\u092A\u093E\u0926\u0928 \u0928\u0939\u0940\u0902 \u0915\u0930\u0924\u0940\u0964" : "Execution mode controlled by server policy." }),
        Array.isArray(conflict.preferredFor) && /* @__PURE__ */ jsxs10("p", { style: { margin: `${t.spacing.sm} 0 0`, fontSize: "13px", color: theme.warningBg, fontFamily: t.font.family }, children: [
          /* @__PURE__ */ jsx12("strong", { children: "Preferred surfaces:" }),
          " ",
          conflict.preferredFor.join(", ")
        ] })
      ] }),
      skill.contentPreview && /* @__PURE__ */ jsxs10("div", { style: { backgroundColor: theme.surface, border: `1px solid ${theme.border}`, borderRadius: t.radius.sm, padding: t.spacing.lg }, children: [
        /* @__PURE__ */ jsx12("strong", { style: { color: theme.textMuted, fontFamily: t.font.family, fontSize: "13px" }, children: "SKILL.md \u0905\u0902\u0936:" }),
        /* @__PURE__ */ jsx12("pre", { style: { whiteSpace: "pre-wrap", fontSize: "13px", color: theme.text, maxHeight: "220px", overflow: "auto", fontFamily: t.font.family, margin: t.spacing.sm }, children: skill.contentPreview })
      ] })
    ] });
  }
  function renderUpdate(skill, onBack) {
    const info = friendly(skill);
    const canUpdate = updateCheck?.updateAvailable && updateCheck?.allowed && !updateCheck?.licenseChanged && !updateCheck?.riskChanged && !updateCheck?.scopeChanged;
    return /* @__PURE__ */ jsxs10("div", { style: cardStyle, children: [
      /* @__PURE__ */ jsx12("button", { onClick: onBack, style: { ...secondaryButtonStyle, marginBottom: t.spacing.md }, children: "\u2190 \u0935\u093E\u092A\u0938 \u0935\u093F\u0935\u0930\u0923" }),
      /* @__PURE__ */ jsxs10("h3", { style: { margin: `0 0 ${t.spacing.md}`, color: theme.text, fontFamily: t.font.family, fontSize: "18px" }, children: [
        info.displayName,
        " \u2014 \u0905\u092A\u0921\u0947\u091F \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923"
      ] }),
      /* @__PURE__ */ jsx12("p", { style: { margin: `0 0 ${t.spacing.lg}`, color: theme.textMuted, fontSize: "14px", fontFamily: t.font.family }, children: "\u0938\u0930\u094D\u0935\u0930 installed provenance \u0938\u0947 approved upstream \u0915\u094B resolve \u0915\u0930\u0924\u093E \u0939\u0948 \u0914\u0930 immutable candidate \u0938\u0947 \u0924\u0941\u0932\u0928\u093E \u0915\u0930\u0924\u093E \u0939\u0948\u0964 \u0915\u094B\u0908 manual SHA, version \u092F\u093E license input \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" }),
      /* @__PURE__ */ jsxs10("div", { style: { display: "flex", gap: t.spacing.md, marginBottom: t.spacing.lg, flexWrap: "wrap" }, children: [
        /* @__PURE__ */ jsx12("button", { disabled: actionBusy, onClick: () => handleCheckUpdate(skill), style: primaryButtonStyle, children: "\u0905\u092A\u0921\u0947\u091F \u091C\u093E\u0901\u091A\u0947\u0902 (Check for update)" }),
        canUpdate && /* @__PURE__ */ jsx12("button", { disabled: actionBusy || readOnly, onClick: () => handleUpdate(skill), style: primaryButtonStyle, children: "\u0905\u092A\u0921\u0947\u091F \u0915\u0930\u0947\u0902 (Update)" })
      ] }),
      updateCheck && /* @__PURE__ */ jsx12("div", { style: {
        backgroundColor: updateCheck.allowed ? theme.successBg : theme.dangerBg,
        border: `1px solid ${updateCheck.allowed ? theme.successBg : theme.dangerBg}`,
        borderRadius: t.radius.sm,
        padding: t.spacing.lg
      }, children: /* @__PURE__ */ jsxs10("p", { style: { margin: 0, color: updateCheck.allowed ? theme.successText : theme.dangerText, fontFamily: t.font.family, fontSize: "14px" }, children: [
        /* @__PURE__ */ jsxs10("strong", { children: [
          updateCheck.updateAvailable ? "\u0905\u092A\u0921\u0947\u091F \u0909\u092A\u0932\u092C\u094D\u0927" : "\u0905\u092A-\u091F\u0942-\u0921\u0947\u091F",
          ":"
        ] }),
        " ",
        updateCheck.updateAvailable ? `${updateCheck.currentCommitSha.slice(0, 16)} \u2192 ${updateCheck.candidateCommitSha.slice(0, 16)}` : updateCheck.currentCommitSha.slice(0, 16) || "-",
        /* @__PURE__ */ jsx12("br", {}),
        updateCheck.newVersion && /* @__PURE__ */ jsxs10(Fragment, { children: [
          /* @__PURE__ */ jsx12("strong", { children: "Version:" }),
          " ",
          updateCheck.newVersion,
          /* @__PURE__ */ jsx12("br", {})
        ] }),
        /* @__PURE__ */ jsx12("strong", { children: "\u0938\u094D\u0925\u093F\u0924\u093F:" }),
        " ",
        updateCheck.allowed ? "\u0905\u0928\u0941\u092E\u0924" : "\u0905\u0938\u094D\u0935\u0940\u0915\u0943\u0924",
        /* @__PURE__ */ jsx12("br", {}),
        updateCheck.licenseChanged && /* @__PURE__ */ jsxs10(Fragment, { children: [
          "\u26A0 License \u092C\u0926\u0932 \u0917\u092F\u093E; owner re-approval \u0906\u0935\u0936\u094D\u092F\u0915\u0964",
          /* @__PURE__ */ jsx12("br", {})
        ] }),
        updateCheck.riskChanged && /* @__PURE__ */ jsxs10(Fragment, { children: [
          "\u26A0 Risk \u092C\u0922\u093C \u0917\u092F\u093E; owner re-approval \u0906\u0935\u0936\u094D\u092F\u0915\u0964",
          /* @__PURE__ */ jsx12("br", {})
        ] }),
        updateCheck.scopeChanged && /* @__PURE__ */ jsxs10(Fragment, { children: [
          "\u26A0 Scope \u092C\u0926\u0932 \u0917\u092F\u093E; owner re-approval \u0906\u0935\u0936\u094D\u092F\u0915\u0964",
          /* @__PURE__ */ jsx12("br", {})
        ] }),
        updateCheck.reason && /* @__PURE__ */ jsxs10(Fragment, { children: [
          /* @__PURE__ */ jsx12("strong", { children: "\u0915\u093E\u0930\u0923:" }),
          " ",
          safeErrorCode(updateCheck.reason)
        ] }),
        updateCheck.changeSummary && /* @__PURE__ */ jsxs10(Fragment, { children: [
          /* @__PURE__ */ jsx12("br", {}),
          /* @__PURE__ */ jsx12("strong", { children: "Summary:" }),
          " ",
          safeErrorCode(updateCheck.changeSummary)
        ] })
      ] }) })
    ] });
  }
  return /* @__PURE__ */ jsxs10("div", { style: cardStyle, children: [
    /* @__PURE__ */ jsx12("h3", { style: { margin: `0 0 ${t.spacing.sm}`, color: theme.text, fontFamily: t.font.family, fontSize: "18px" }, children: "\u0915\u094C\u0936\u0932 \u092A\u0948\u0915 (Skills)" }),
    /* @__PURE__ */ jsx12("p", { style: { margin: `0 0 ${t.spacing.lg}`, color: theme.textMuted, fontSize: "14px", fontFamily: t.font.family }, children: "\u091A\u092F\u0928\u093F\u0924 \u092C\u093E\u0939\u094D\u092F \u0915\u094C\u0936\u0932 \u092A\u0948\u0915 \u091C\u093F\u0928\u0915\u093E provenance, license \u0914\u0930 pinned \u0938\u0902\u0938\u094D\u0915\u0930\u0923 \u0938\u0930\u094D\u0935\u0930 \u0926\u094D\u0935\u093E\u0930\u093E \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u093F\u0924 \u0939\u0948\u0964" }),
    error && /* @__PURE__ */ jsxs10("div", { role: "alert", style: { backgroundColor: theme.dangerSurface, border: `1px solid ${theme.dangerBorder}`, color: theme.dangerBg, padding: t.spacing.md, borderRadius: t.radius.sm, marginBottom: t.spacing.lg, fontFamily: t.font.family }, children: [
      safeErrorText(error, "hi").title,
      " ",
      /* @__PURE__ */ jsx12("code", { children: error })
    ] }),
    loading && /* @__PURE__ */ jsx12("p", { style: { color: theme.textMuted, fontFamily: t.font.family }, children: "\u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026" }),
    /* @__PURE__ */ jsx12("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: t.spacing.lg }, children: sortedSkills.map((skill) => {
      const info = friendly(skill);
      return /* @__PURE__ */ jsxs10(
        "div",
        {
          style: {
            backgroundColor: skill.enabled ? theme.surfaceStrong : theme.surface,
            border: `1px solid ${skill.enabled ? theme.successBg : theme.border}`,
            borderRadius: t.radius.md,
            padding: t.spacing.lg,
            fontFamily: t.font.family
          },
          children: [
            /* @__PURE__ */ jsxs10("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: t.spacing.sm }, children: [
              /* @__PURE__ */ jsx12("h4", { style: { margin: 0, color: theme.text, fontSize: "16px", fontFamily: t.font.family }, children: info.displayName }),
              /* @__PURE__ */ jsx12(StatusTag, { enabled: skill.enabled || skill.status === "enabled", label: info.statusLabel })
            ] }),
            /* @__PURE__ */ jsx12("p", { style: { margin: `0 0 ${t.spacing.md}`, fontSize: "13px", color: theme.textMuted, fontFamily: t.font.family }, children: info.publisherLine }),
            /* @__PURE__ */ jsxs10("div", { style: { display: "flex", gap: t.spacing.sm, flexWrap: "wrap", marginBottom: t.spacing.md }, children: [
              /* @__PURE__ */ jsx12(Tag, { label: skill.risk ?? "unknown", variant: riskVariant(skill.risk) }),
              skill.approvalRequired && /* @__PURE__ */ jsx12(Tag, { label: "approval", variant: "neutral" }),
              skill.requiredScopes && skill.requiredScopes.length > 0 && /* @__PURE__ */ jsx12(Tag, { label: skill.requiredScopes.join(", "), variant: "neutral" })
            ] }),
            /* @__PURE__ */ jsxs10("div", { style: { display: "flex", gap: t.spacing.sm, flexWrap: "wrap" }, children: [
              !skill.enabled ? /* @__PURE__ */ jsx12("button", { disabled: readOnly || actionBusy, onClick: () => handleEnable(skill), style: smallPrimaryButtonStyle, children: "\u0938\u0915\u094D\u0937\u092E \u0915\u0930\u0947\u0902" }) : /* @__PURE__ */ jsx12("button", { disabled: readOnly || actionBusy, onClick: () => handleDisable(skill), style: smallSecondaryButtonStyle, children: "\u0905\u0915\u094D\u0937\u092E \u0915\u0930\u0947\u0902" }),
              /* @__PURE__ */ jsx12("button", { onClick: () => openDetail(skill), style: smallSecondaryButtonStyle, children: "\u0935\u093F\u0935\u0930\u0923" }),
              /* @__PURE__ */ jsx12("button", { onClick: () => openProvenance(skill), style: smallSecondaryButtonStyle, children: "\u0938\u094D\u0930\u094B\u0924" }),
              /* @__PURE__ */ jsx12("button", { onClick: () => openUpdate(skill), style: smallSecondaryButtonStyle, children: "\u0905\u092A\u0921\u0947\u091F" })
            ] })
          ]
        },
        skill.extensionId
      );
    }) })
  ] });
}
function Field({ label, value, highlight }) {
  return /* @__PURE__ */ jsxs10("div", { style: { backgroundColor: theme.surface, border: `1px solid ${theme.border}`, borderRadius: t.radius.sm, padding: t.spacing.md }, children: [
    /* @__PURE__ */ jsx12("div", { style: { fontSize: "11px", color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: t.font.family, marginBottom: t.spacing.xs }, children: label }),
    /* @__PURE__ */ jsx12("div", { style: { fontSize: "13px", color: highlight ? highlightColor(highlight) : theme.text, wordBreak: "break-all", fontFamily: t.font.family }, children: value })
  ] });
}
function Tag({ label, variant = "neutral" }) {
  const color = highlightColor(variant);
  const bg = variant === "neutral" ? theme.neutralBg : color;
  const text2 = variant === "neutral" ? theme.neutralText : w.surfaceStrong;
  return /* @__PURE__ */ jsx12("span", { style: { fontSize: "11px", padding: `${t.spacing.xs} ${t.spacing.sm}`, borderRadius: t.radius.pill, backgroundColor: bg, color: text2, fontFamily: t.font.family, fontWeight: t.font.weightMedium }, children: label });
}
function StatusTag({ label, enabled }) {
  return /* @__PURE__ */ jsx12("span", { style: { fontSize: "12px", padding: `${t.spacing.xs} ${t.spacing.sm}`, borderRadius: t.radius.pill, backgroundColor: enabled ? theme.successBg : theme.neutralBg, color: enabled ? theme.successText : theme.neutralText, fontFamily: t.font.family, fontWeight: t.font.weightMedium }, children: label });
}
var cardStyle = {
  backgroundColor: theme.surfaceStrong,
  border: `1px solid ${theme.border}`,
  borderRadius: t.radius.md,
  padding: t.spacing.xl,
  fontFamily: t.font.family
};
var primaryButtonStyle = {
  padding: `${t.spacing.sm} ${t.spacing.md}`,
  backgroundColor: theme.primary,
  color: theme.primaryText,
  border: "none",
  borderRadius: t.radius.sm,
  cursor: "pointer",
  fontSize: "13px",
  fontFamily: t.font.family
};
var secondaryButtonStyle = {
  padding: `${t.spacing.sm} ${t.spacing.md}`,
  backgroundColor: theme.surfaceStrong,
  color: theme.primary,
  border: `1px solid ${theme.borderStrong}`,
  borderRadius: t.radius.sm,
  cursor: "pointer",
  fontSize: "13px",
  fontFamily: t.font.family
};
var smallPrimaryButtonStyle = {
  padding: `${t.spacing.xs} ${t.spacing.sm}`,
  backgroundColor: theme.primary,
  color: theme.primaryText,
  border: "none",
  borderRadius: t.radius.sm,
  cursor: "pointer",
  fontSize: "12px",
  fontFamily: t.font.family
};
var smallSecondaryButtonStyle = {
  padding: `${t.spacing.xs} ${t.spacing.sm}`,
  backgroundColor: theme.surfaceStrong,
  color: theme.primary,
  border: `1px solid ${theme.borderStrong}`,
  borderRadius: t.radius.sm,
  cursor: "pointer",
  fontSize: "12px",
  fontFamily: t.font.family
};

// src/settings/AgentPromptSettings.tsx
import { useEffect as useEffect5, useMemo as useMemo5, useState as useState8 } from "react";
import {
  listAgentPromptPatterns,
  getAgentPromptPreset,
  agentPromptActiveSurfaces,
  isAgentPromptRuntimeActive,
  AGENT_PERSONALITIES,
  AGENT_AUTONOMY_CHOICES,
  AGENT_PROGRESS_MODES,
  AGENT_EVIDENCE_MODES,
  AGENT_MODEL_PROFILE_IDS,
  AGENT_PROMPT_SELECTION_NAMESPACE,
  AGENT_PROMPT_SELECTION_SETTING_KEY,
  validateAgentPromptSelection
} from "@smart-shikka/core";

// src/settings/canonicalSettingsService.ts
var SETTINGS_NAMESPACES = [
  "ceo",
  "providers",
  "voice",
  "browser",
  "desktop",
  "extension",
  "skills_agents",
  "integrations",
  "security_privacy",
  "public_market",
  "updates"
];
var COOKIE_SESSION_TOKEN = "__smart_shikka_cookie_session__";
var CSRF_COOKIE_NAME = "smart_shikka_csrf";
var CSRF_HEADER_NAME = "x-smart-shikka-csrf";
function readCookie(name) {
  if (typeof document === "undefined") return void 0;
  for (const part of document.cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return decodeURIComponent(rest.join("="));
  }
  return void 0;
}
function createCanonicalSettingsService(options) {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = options.baseUrl ?? "";
  const cookieSession = options.token === COOKIE_SESSION_TOKEN;
  const headers2 = (mutating = false) => {
    const next = { "Content-Type": "application/json" };
    if (!cookieSession) next.Authorization = `Bearer ${options.token}`;
    if (cookieSession && mutating) {
      const csrf = readCookie(CSRF_COOKIE_NAME);
      if (csrf) next[CSRF_HEADER_NAME] = csrf;
    }
    return next;
  };
  const credentials = cookieSession ? "include" : "same-origin";
  async function parse(response) {
    const body = await response.json();
    if (!response.ok || body.success !== true || body.ok !== true) {
      throw new Error(
        typeof body.error === "string" ? body.error : "Settings control plane request failed."
      );
    }
    return body;
  }
  return {
    async list(namespace, signal) {
      const query = namespace ? `?namespace=${encodeURIComponent(namespace)}` : "";
      const response = await fetchImpl(`${baseUrl}/api/settings/control-plane${query}`, {
        method: "GET",
        headers: headers2(),
        credentials,
        ...signal ? { signal } : {}
      });
      const body = await parse(response);
      return Array.isArray(body.settings) ? body.settings : [];
    },
    async save(input, signal) {
      const response = await fetchImpl(`${baseUrl}/api/settings/control-plane`, {
        method: "PATCH",
        headers: headers2(true),
        credentials,
        body: JSON.stringify(input),
        ...signal ? { signal } : {}
      });
      const body = await parse(response);
      return body.setting;
    },
    async delete(namespace, settingKey, signal) {
      const response = await fetchImpl(`${baseUrl}/api/settings/control-plane`, {
        method: "PATCH",
        headers: headers2(true),
        credentials,
        body: JSON.stringify({ operation: "delete", namespace, settingKey }),
        ...signal ? { signal } : {}
      });
      const body = await parse(response);
      return body.deleted === true;
    }
  };
}

// src/components/base/brandColors.ts
import { useMemo as useMemo4 } from "react";
var STORAGE_KEY = "smart-shikka-brand-theme";
var brandColors = {
  bg: "hsl(var(--brand-background))",
  fg: "hsl(var(--brand-foreground))",
  muted: "hsl(var(--brand-muted-foreground))",
  primary: "hsl(var(--brand-primary))",
  primaryFg: "hsl(var(--brand-primary-foreground))",
  card: "hsl(var(--brand-card))",
  panel: "hsl(var(--brand-sidebar))",
  accent: "hsl(var(--brand-accent))",
  accentFg: "hsl(var(--brand-accent-foreground))",
  border: "var(--brand-border)",
  success: "hsl(var(--brand-success))",
  warning: "hsl(var(--brand-warning))",
  destructive: "hsl(var(--brand-destructive))",
  successBg: "color-mix(in srgb, hsl(var(--brand-success)) 14%, transparent)",
  warningBg: "color-mix(in srgb, hsl(var(--brand-warning)) 14%, transparent)",
  successFg: "hsl(var(--brand-success))",
  chip: "hsl(var(--brand-accent))",
  assistantBg: "hsl(var(--brand-sidebar))",
  inputBg: "hsl(var(--brand-card))",
  btn: "hsl(var(--brand-primary-foreground))",
  radius: "var(--brand-radius, 0.625rem)",
  fontSans: "var(--brand-font-sans)"
};
function useBrandColors() {
  return useMemo4(() => brandColors, []);
}
function getInitialBrandMode() {
  if (typeof window === "undefined") return "system";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  return "system";
}
function resolveBrandMode(mode) {
  if (mode !== "system") return mode;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function applyBrandMode(mode) {
  if (typeof document === "undefined") return;
  const resolved = resolveBrandMode(mode);
  document.documentElement.setAttribute("data-brand", "smart-shikka");
  document.documentElement.setAttribute("data-theme", resolved);
  document.documentElement.classList.remove("dark", "light");
  document.documentElement.classList.add(resolved);
  window.localStorage.setItem(STORAGE_KEY, mode);
  applyBrandAccent();
}
function useBrandTheme() {
  return {
    colors: brandColors,
    applyBrandMode,
    resolveBrandMode,
    storageKey: STORAGE_KEY
  };
}
var BRAND_ACCENTS = [
  "saffron",
  "indigo",
  "emerald",
  "rose",
  "violet",
  "sky"
];
var ACCENT_STORAGE_KEY = "smart-shikka-brand-accent";
var ACCENT_PRESETS = {
  saffron: {
    light: { accent: "30 40% 90%", accentForeground: "28 54% 49%", primary: "28 54% 49%", primaryForeground: "40 25% 97%" },
    dark: { accent: "28 35% 18%", accentForeground: "28 54% 65%", primary: "28 54% 49%", primaryForeground: "40 25% 97%" }
  },
  indigo: {
    light: { accent: "232 45% 92%", accentForeground: "239 57% 55%", primary: "239 57% 55%", primaryForeground: "0 0% 100%" },
    dark: { accent: "239 32% 20%", accentForeground: "234 62% 72%", primary: "239 62% 62%", primaryForeground: "0 0% 100%" }
  },
  emerald: {
    light: { accent: "152 40% 90%", accentForeground: "160 84% 30%", primary: "160 84% 32%", primaryForeground: "0 0% 100%" },
    dark: { accent: "160 30% 17%", accentForeground: "158 60% 55%", primary: "158 64% 40%", primaryForeground: "0 0% 100%" }
  },
  rose: {
    light: { accent: "350 55% 92%", accentForeground: "346 65% 48%", primary: "346 65% 50%", primaryForeground: "0 0% 100%" },
    dark: { accent: "346 32% 20%", accentForeground: "348 70% 68%", primary: "346 68% 56%", primaryForeground: "0 0% 100%" }
  },
  violet: {
    light: { accent: "270 45% 92%", accentForeground: "268 55% 55%", primary: "268 55% 58%", primaryForeground: "0 0% 100%" },
    dark: { accent: "268 32% 21%", accentForeground: "270 62% 74%", primary: "268 60% 66%", primaryForeground: "0 0% 100%" }
  },
  sky: {
    light: { accent: "200 55% 90%", accentForeground: "202 70% 40%", primary: "202 72% 44%", primaryForeground: "0 0% 100%" },
    dark: { accent: "202 32% 20%", accentForeground: "200 65% 66%", primary: "200 70% 52%", primaryForeground: "0 0% 100%" }
  }
};
var BRAND_ACCENT_SWATCH = {
  saffron: ACCENT_PRESETS.saffron.light.primary,
  indigo: ACCENT_PRESETS.indigo.light.primary,
  emerald: ACCENT_PRESETS.emerald.light.primary,
  rose: ACCENT_PRESETS.rose.light.primary,
  violet: ACCENT_PRESETS.violet.light.primary,
  sky: ACCENT_PRESETS.sky.light.primary
};
function getStoredBrandAccent() {
  if (typeof window === "undefined") return "saffron";
  const stored = window.localStorage.getItem(ACCENT_STORAGE_KEY);
  return BRAND_ACCENTS.includes(stored) ? stored : "saffron";
}
function applyBrandAccent(accent) {
  if (typeof document === "undefined") return;
  const choice = accent ?? getStoredBrandAccent();
  const mode = document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
  const palette = ACCENT_PRESETS[choice][mode];
  const root = document.documentElement.style;
  root.setProperty("--brand-accent", palette.accent);
  root.setProperty("--brand-accent-foreground", palette.accentForeground);
  root.setProperty("--brand-primary", palette.primary);
  root.setProperty("--brand-primary-foreground", palette.primaryForeground);
  window.localStorage.setItem(ACCENT_STORAGE_KEY, choice);
}
function resetBrandAccent() {
  if (typeof document === "undefined") return;
  const root = document.documentElement.style;
  root.removeProperty("--brand-accent");
  root.removeProperty("--brand-accent-foreground");
  root.removeProperty("--brand-primary");
  root.removeProperty("--brand-primary-foreground");
  window.localStorage.removeItem(ACCENT_STORAGE_KEY);
}

// src/settings/AgentPromptSettings.tsx
import { jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
var MODEL_PROFILE_OPTIONS = [
  { id: AGENT_MODEL_PROFILE_IDS.fast, label: "Fast", intent: "fast" },
  { id: AGENT_MODEL_PROFILE_IDS.reasoning, label: "Reasoning", intent: "reasoning" }
];
var PROGRESS_LABEL_HI = {
  off: "\u092C\u0902\u0926",
  minimal: "\u0928\u094D\u092F\u0942\u0928\u0924\u092E",
  normal: "\u0938\u093E\u092E\u093E\u0928\u094D\u092F",
  detailed: "\u0935\u093F\u0938\u094D\u0924\u0943\u0924"
};
var EVIDENCE_LABEL_HI = {
  none: "\u0915\u094B\u0908 \u0928\u0939\u0940\u0902",
  summary: "\u0938\u093E\u0930\u093E\u0902\u0936",
  required_for_mutation: "\u092E\u094D\u092F\u0942\u091F\u0947\u0936\u0928 \u092A\u0930 \u0906\u0935\u0936\u094D\u092F\u0915",
  always: "\u0939\u092E\u0947\u0936\u093E"
};
function defaultsForPreset(presetId) {
  const preset = getAgentPromptPreset(presetId);
  return {
    presetId,
    personalityId: preset?.defaultPersonality ?? "professional",
    autonomyMode: preset?.defaultExecutionMode ?? "read_only",
    modelProfileId: AGENT_MODEL_PROFILE_IDS[preset?.defaultModelProfile ?? "fast"],
    progressMode: preset?.defaultProgressMode ?? "normal",
    evidenceMode: preset?.defaultEvidenceMode ?? "summary"
  };
}
function toSelectionState(value) {
  const base = defaultsForPreset(value?.presetId ?? "jarvis-ceo");
  return {
    presetId: base.presetId,
    personalityId: value?.personalityId ?? base.personalityId,
    autonomyMode: value?.autonomyMode ?? base.autonomyMode,
    modelProfileId: value?.modelProfileId ?? base.modelProfileId,
    progressMode: value?.progressMode ?? base.progressMode,
    evidenceMode: value?.evidenceMode ?? base.evidenceMode
  };
}
var sectionStyle = { marginBottom: "18px" };
var labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: 600,
  marginBottom: "6px"
};
var selectStyle = {
  width: "100%",
  maxWidth: "360px",
  padding: "8px 10px",
  borderRadius: "6px",
  fontSize: "14px"
};
function AgentPromptSettings({
  service,
  token,
  baseUrl,
  fetchImpl,
  readOnly = false
}) {
  const C = useBrandColors();
  const fieldStyle = {
    ...selectStyle,
    border: `1px solid ${C.border}`,
    backgroundColor: C.inputBg,
    color: C.fg
  };
  const fieldLabelStyle = { ...labelStyle, color: C.fg };
  const presets = useMemo5(() => listAgentPromptPatterns(), []);
  const [selection, setSelection] = useState8(toSelectionState(void 0));
  const [status, setStatus] = useState8("");
  const [loading, setLoading] = useState8(true);
  const [storedInvalid, setStoredInvalid] = useState8(false);
  const resolvedService = useMemo5(() => {
    if (service) return service;
    if (token) {
      return createCanonicalSettingsService({
        token,
        ...baseUrl ? { baseUrl } : {},
        ...fetchImpl ? { fetchImpl } : {}
      });
    }
    return void 0;
  }, [baseUrl, fetchImpl, service, token]);
  useEffect5(() => {
    let cancelled = false;
    if (!resolvedService) {
      setLoading(false);
      return;
    }
    resolvedService.list(AGENT_PROMPT_SELECTION_NAMESPACE).then((settings) => {
      if (cancelled) return;
      const record = settings.find((s) => s.settingKey === AGENT_PROMPT_SELECTION_SETTING_KEY);
      const parsed = validateAgentPromptSelection(record?.settingValue);
      if (parsed.valid && parsed.normalized) {
        setSelection(toSelectionState(parsed.normalized));
        setStoredInvalid(false);
      } else if (record === void 0) {
        setStoredInvalid(false);
      } else {
        setStoredInvalid(true);
        setStatus("\u0938\u0939\u0947\u091C\u093E \u0917\u092F\u093E \u090F\u091C\u0947\u0902\u091F \u091A\u092F\u0928 \u0905\u092E\u093E\u0928\u094D\u092F \u092F\u093E \u092A\u0941\u0930\u093E\u0928\u093E \u0939\u0948 \u2014 \u0921\u093F\u092B\u093C\u0949\u0932\u094D\u091F \u0926\u093F\u0916\u093E\u090F \u0917\u090F \u0939\u0948\u0902\u0964 \u0938\u0939\u0947\u091C\u0947\u0902 \u0926\u092C\u093E\u0915\u0930 \u0920\u0940\u0915 \u0915\u0930\u0947\u0902\u0964");
      }
    }).catch(() => {
      if (!cancelled) setStatus("\u0938\u0947\u091F\u093F\u0902\u0917 \u0932\u094B\u0921 \u0928\u0939\u0940\u0902 \u0939\u094B \u092A\u093E\u0908\u0964");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [resolvedService]);
  const update = (patch) => {
    setSelection((prev) => ({ ...prev, ...patch }));
  };
  const save = async () => {
    if (!resolvedService) {
      setStatus("\u0938\u0947\u091F\u093F\u0902\u0917 \u0938\u0947\u0935\u093E \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964");
      return;
    }
    const value = { ...selection };
    const preset2 = presets.find((p) => p.id === selection.presetId);
    if (preset2) value.presetVersion = preset2.version;
    const check = validateAgentPromptSelection(value);
    if (!check.valid) {
      setStatus(`\u0905\u092E\u093E\u0928\u094D\u092F \u091A\u092F\u0928: ${check.errors.join(", ")}`);
      return;
    }
    try {
      await resolvedService.save({
        namespace: AGENT_PROMPT_SELECTION_NAMESPACE,
        settingKey: AGENT_PROMPT_SELECTION_SETTING_KEY,
        settingValue: { agentPromptSelection: check.normalized }
      });
      setStoredInvalid(false);
      setStatus("\u0938\u0939\u0947\u091C\u093E \u0917\u092F\u093E\u0964");
    } catch (error) {
      setStatus(`\u0938\u0939\u0947\u091C\u0928\u0947 \u092E\u0947\u0902 \u0924\u094D\u0930\u0941\u091F\u093F: ${safeErrorText(errorToCode(error), "hi").title}`);
    }
  };
  const preset = presets.find((p) => p.id === selection.presetId);
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      "data-testid": "agent-prompt-settings",
      className: "agent-prompt-settings",
      style: {
        backgroundColor: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: "8px",
        padding: "20px",
        color: C.fg,
        maxWidth: "720px",
        width: "100%",
        boxSizing: "border-box"
      },
      children: [
        /* @__PURE__ */ jsx13("h3", { style: { margin: "0 0 4px 0", fontWeight: 700 }, children: "\u090F\u091C\u0947\u0902\u091F \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 (Agent Settings)" }),
        /* @__PURE__ */ jsx13("p", { style: { margin: "0 0 16px 0", fontSize: "13px", color: C.muted }, children: "\u090F\u091C\u0947\u0902\u091F \u0915\u0940 \u092D\u0942\u092E\u093F\u0915\u093E, \u0935\u094D\u092F\u0915\u094D\u0924\u093F\u0924\u094D\u0935, \u0938\u094D\u0935\u093E\u092F\u0924\u094D\u0924\u0924\u093E \u0914\u0930 \u092E\u0949\u0921\u0932 \u092A\u094D\u0930\u094B\u092B\u093C\u093E\u0907\u0932 \u091A\u0941\u0928\u0947\u0902\u0964 \u092A\u094D\u0930\u0949\u092E\u094D\u092A\u094D\u091F \u091F\u0947\u0915\u094D\u0938\u094D\u091F \u0915\u0947\u0935\u0932 \u0935\u094D\u092F\u0935\u0939\u093E\u0930 \u092C\u0926\u0932\u0924\u093E \u0939\u0948, \u0905\u0928\u0941\u092E\u0924\u093F\u092F\u093E\u0901 \u0928\u0939\u0940\u0902 \u2014 \u0938\u092D\u0940 \u0905\u0928\u0941\u092E\u0924\u093F\u092F\u093E\u0901 SkillPlatform \u0928\u0940\u0924\u093F \u0938\u0947 \u0939\u0940 \u0906\u0924\u0940 \u0939\u0948\u0902\u0964" }),
        /* @__PURE__ */ jsx13(
          "div",
          {
            role: "note",
            style: {
              fontSize: "12px",
              backgroundColor: C.warningBg,
              border: `1px solid ${C.warning}`,
              color: C.warning,
              padding: "8px 10px",
              borderRadius: "6px",
              marginBottom: "16px"
            },
            children: "Prompt text changes behavior, not permissions. \u092A\u094D\u0930\u0949\u092E\u094D\u092A\u094D\u091F \u091F\u0947\u0915\u094D\u0938\u094D\u091F \u0915\u0947\u0935\u0932 \u0935\u094D\u092F\u0935\u0939\u093E\u0930 \u092C\u0926\u0932\u0924\u093E \u0939\u0948, \u0905\u0928\u0941\u092E\u0924\u093F\u092F\u093E\u0901 \u0928\u0939\u0940\u0902\u0964"
          }
        ),
        /* @__PURE__ */ jsxs11("div", { style: sectionStyle, children: [
          /* @__PURE__ */ jsx13("label", { htmlFor: "agent-preset", style: fieldLabelStyle, children: "\u090F\u091C\u0947\u0902\u091F \u092D\u0942\u092E\u093F\u0915\u093E (Agent Role)" }),
          /* @__PURE__ */ jsx13(
            "select",
            {
              id: "agent-preset",
              style: fieldStyle,
              disabled: readOnly || loading,
              value: selection.presetId,
              onChange: (e) => {
                setSelection(defaultsForPreset(e.target.value));
                setStatus("\u0928\u0908 \u092A\u094D\u0930\u0940\u0938\u0947\u091F \u0921\u093F\u092B\u093C\u0949\u0932\u094D\u091F \u0932\u093E\u0917\u0942 \u0915\u093F\u090F \u0917\u090F \u2014 \u0938\u0939\u0947\u091C\u0947\u0902 \u0926\u092C\u093E\u0928\u0947 \u092A\u0930 \u0939\u0940 \u0938\u0939\u0947\u091C\u093E \u091C\u093E\u090F\u0917\u093E\u0964");
              },
              children: presets.map((p) => /* @__PURE__ */ jsxs11("option", { value: p.id, children: [
                p.titleHi,
                " \u2014 ",
                p.title
              ] }, p.id))
            }
          ),
          preset && /* @__PURE__ */ jsx13("p", { style: { fontSize: "12px", color: C.muted, margin: "6px 0 0 0" }, children: preset.description }),
          preset && /* @__PURE__ */ jsx13("p", { style: { fontSize: "12px", color: C.muted, margin: "6px 0 0 0" }, children: isAgentPromptRuntimeActive(preset) ? `\u0938\u0915\u094D\u0930\u093F\u092F \u0938\u0924\u0939\u0947\u0902 (active surfaces): ${agentPromptActiveSurfaces(preset).map((s) => `${s.titleHi} / ${s.title}`).join(", ")}` : "\u0915\u0947\u0935\u0932 \u092A\u0942\u0930\u094D\u0935\u093E\u0935\u0932\u094B\u0915\u0928 \u2014 \u0907\u0938 \u092A\u094D\u0930\u0940\u0938\u0947\u091F \u0915\u0940 \u0915\u094B\u0908 \u0938\u0915\u094D\u0930\u093F\u092F runtime \u0938\u0924\u0939 \u0928\u0939\u0940\u0902 \u0939\u0948 (Preview only \u2014 no active runtime surface)." }),
          storedInvalid && /* @__PURE__ */ jsx13(
            "p",
            {
              role: "alert",
              "data-testid": "agent-prompt-selection-invalid",
              style: { fontSize: "12px", color: C.warning, margin: "6px 0 0 0" },
              children: "\u0938\u0939\u0947\u091C\u093E \u0917\u092F\u093E \u091A\u092F\u0928 registry \u0938\u0947 \u092E\u0947\u0932 \u0928\u0939\u0940\u0902 \u0916\u093E\u0924\u093E \u2014 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0921\u093F\u092B\u093C\u0949\u0932\u094D\u091F \u092A\u0930 \u091A\u0932 \u0930\u0939\u093E \u0939\u0948\u0964"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs11("div", { style: sectionStyle, children: [
          /* @__PURE__ */ jsx13("label", { htmlFor: "agent-personality", style: fieldLabelStyle, children: "\u0935\u094D\u092F\u0915\u094D\u0924\u093F\u0924\u094D\u0935 (Personality)" }),
          /* @__PURE__ */ jsx13(
            "select",
            {
              id: "agent-personality",
              style: fieldStyle,
              disabled: readOnly || loading,
              value: selection.personalityId,
              onChange: (e) => update({ personalityId: e.target.value }),
              children: AGENT_PERSONALITIES.map((p) => /* @__PURE__ */ jsxs11("option", { value: p.id, children: [
                p.titleHi,
                " \u2014 ",
                p.title
              ] }, p.id))
            }
          )
        ] }),
        /* @__PURE__ */ jsxs11("div", { style: sectionStyle, children: [
          /* @__PURE__ */ jsx13("label", { htmlFor: "agent-autonomy", style: fieldLabelStyle, children: "\u0938\u094D\u0935\u093E\u092F\u0924\u094D\u0924\u0924\u093E (Autonomy)" }),
          /* @__PURE__ */ jsx13(
            "select",
            {
              id: "agent-autonomy",
              style: fieldStyle,
              disabled: readOnly || loading,
              value: selection.autonomyMode,
              onChange: (e) => update({ autonomyMode: e.target.value }),
              children: AGENT_AUTONOMY_CHOICES.map((c) => /* @__PURE__ */ jsxs11("option", { value: c.id, children: [
                c.labelHi,
                " \u2014 ",
                c.label
              ] }, c.id))
            }
          )
        ] }),
        /* @__PURE__ */ jsxs11("div", { style: sectionStyle, children: [
          /* @__PURE__ */ jsx13("label", { htmlFor: "agent-model-profile", style: fieldLabelStyle, children: "\u092E\u0949\u0921\u0932 \u092A\u094D\u0930\u094B\u092B\u093C\u093E\u0907\u0932 (Model Profile)" }),
          /* @__PURE__ */ jsx13(
            "select",
            {
              id: "agent-model-profile",
              style: fieldStyle,
              disabled: readOnly || loading,
              value: selection.modelProfileId,
              onChange: (e) => update({ modelProfileId: e.target.value }),
              children: MODEL_PROFILE_OPTIONS.map((m) => /* @__PURE__ */ jsxs11("option", { value: m.id, children: [
                m.label,
                " (",
                m.id,
                ")"
              ] }, m.id))
            }
          )
        ] }),
        /* @__PURE__ */ jsxs11("div", { style: sectionStyle, children: [
          /* @__PURE__ */ jsx13("label", { htmlFor: "agent-progress", style: fieldLabelStyle, children: "\u092A\u094D\u0930\u0917\u0924\u093F \u0905\u092A\u0921\u0947\u091F (Progress Updates)" }),
          /* @__PURE__ */ jsx13(
            "select",
            {
              id: "agent-progress",
              style: fieldStyle,
              disabled: readOnly || loading,
              value: selection.progressMode,
              onChange: (e) => update({ progressMode: e.target.value }),
              children: AGENT_PROGRESS_MODES.map((m) => /* @__PURE__ */ jsxs11("option", { value: m, children: [
                PROGRESS_LABEL_HI[m] ?? m,
                " \u2014 ",
                m
              ] }, m))
            }
          )
        ] }),
        /* @__PURE__ */ jsxs11("div", { style: sectionStyle, children: [
          /* @__PURE__ */ jsx13("label", { htmlFor: "agent-evidence", style: fieldLabelStyle, children: "\u092A\u094D\u0930\u092E\u093E\u0923 \u0906\u0935\u0936\u094D\u092F\u0915 (Evidence Required)" }),
          /* @__PURE__ */ jsx13(
            "select",
            {
              id: "agent-evidence",
              style: fieldStyle,
              disabled: readOnly || loading,
              value: selection.evidenceMode,
              onChange: (e) => update({ evidenceMode: e.target.value }),
              children: AGENT_EVIDENCE_MODES.map((m) => /* @__PURE__ */ jsxs11("option", { value: m, children: [
                EVIDENCE_LABEL_HI[m] ?? m,
                " \u2014 ",
                m
              ] }, m))
            }
          )
        ] }),
        !readOnly && /* @__PURE__ */ jsx13(
          "button",
          {
            type: "button",
            onClick: () => void save(),
            disabled: loading,
            style: {
              padding: "8px 18px",
              backgroundColor: C.primary,
              color: C.primaryFg,
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer"
            },
            children: "\u0938\u0939\u0947\u091C\u0947\u0902"
          }
        ),
        status !== "" && /* @__PURE__ */ jsx13(
          "p",
          {
            role: "status",
            "aria-live": "polite",
            style: { fontSize: "13px", color: C.muted, marginTop: "10px" },
            children: status
          }
        )
      ]
    }
  );
}

// src/settings/CanonicalSettingsControlPlane.tsx
import React7, { useCallback as useCallback4, useEffect as useEffect6, useMemo as useMemo6, useState as useState9 } from "react";
import { jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
var codingProfiles = [
  "coding.fast",
  "coding.reasoning",
  "coding.review",
  "coding.fix",
  "coding.local"
];
var defaultUiBudget = {
  maxCostPerTask: 0,
  maxInputTokens: 0,
  maxOutputTokens: 0,
  maxToolCalls: 0,
  maxWallTimeMs: 0,
  maxIterations: 0,
  maxStrongModelCalls: 0,
  maxReviewerCalls: 0,
  maxCiRepairAttempts: 0,
  hardStop: true
};
function routingProjection(setting) {
  const raw = setting?.settingValue ?? {};
  const value = raw.coding && typeof raw.coding === "object" && !Array.isArray(raw.coding) ? raw.coding : raw;
  const privacy = value.privacyPolicy && typeof value.privacyPolicy === "object" && !Array.isArray(value.privacyPolicy) ? value.privacyPolicy : {};
  const profileTruth = value.profileTruth && typeof value.profileTruth === "object" && !Array.isArray(value.profileTruth) ? value.profileTruth : {};
  const routes = Array.isArray(value.routes) ? value.routes.filter(
    (route) => Boolean(
      route && typeof route === "object" && !Array.isArray(route) && typeof route.providerId === "string" && typeof route.modelId === "string"
    )
  ) : [];
  return {
    adaptiveEnabled: value.adaptiveEnabled !== false,
    defaultMode: value.defaultMode === "fast" || value.defaultMode === "normal" || value.defaultMode === "ultra" || value.defaultMode === "manual" ? value.defaultMode : "adaptive",
    profileTruth: {
      fast: codingProfiles.includes(profileTruth.fast) ? profileTruth.fast : "coding.fast",
      normal: codingProfiles.includes(profileTruth.normal) ? profileTruth.normal : "coding.reasoning",
      ultra: codingProfiles.includes(profileTruth.ultra) ? profileTruth.ultra : "coding.review"
    },
    routes,
    providerHealth: value.providerHealth && typeof value.providerHealth === "object" && !Array.isArray(value.providerHealth) ? value.providerHealth : {},
    fallbackOrder: Array.isArray(value.fallbackOrder) ? value.fallbackOrder.filter((entry) => typeof entry === "string") : [],
    modeBudgets: value.modeBudgets && typeof value.modeBudgets === "object" && !Array.isArray(value.modeBudgets) ? value.modeBudgets : {},
    privacyPolicy: {
      cloudAllowed: privacy.cloudAllowed !== false,
      sensitiveCodeAllowed: privacy.sensitiveCodeAllowed === true,
      customerDataAllowed: privacy.customerDataAllowed === true,
      localOnly: privacy.localOnly === true,
      maxDataClassification: privacy.maxDataClassification === "public" || privacy.maxDataClassification === "confidential" || privacy.maxDataClassification === "restricted" ? privacy.maxDataClassification : "internal"
    }
  };
}
function ModelRoutingSettingsPanel({
  setting,
  readOnly,
  locale,
  onSave
}) {
  const text2 = (en, hi) => locale === "hi" ? hi : en;
  const [value, setValue] = useState9(() => routingProjection(setting));
  const [newProvider, setNewProvider] = useState9("");
  const [newModel, setNewModel] = useState9("");
  useEffect6(() => setValue(routingProjection(setting)), [setting]);
  const update = (patch) => setValue((current) => ({ ...current, ...patch }));
  const move = (index, offset) => {
    const next = [...value.routes];
    const destination = index + offset;
    if (destination < 0 || destination >= next.length) return;
    [next[index], next[destination]] = [next[destination], next[index]];
    update({ routes: next });
  };
  const addCandidate = () => {
    if (!newProvider.trim() || !newModel.trim()) return;
    update({
      routes: [
        ...value.routes,
        {
          profileId: "coding.fast",
          providerId: newProvider.trim(),
          modelId: newModel.trim(),
          tier: "cheap",
          enabled: true,
          ownerApproved: false,
          contextWindow: 0,
          capabilities: {},
          estimatedInputCostPer1kTokens: 0,
          estimatedOutputCostPer1kTokens: 0,
          latencyPreference: 0,
          allowsSensitiveCode: false,
          allowsCustomerData: false,
          maxDataClassification: "internal"
        }
      ]
    });
    setNewProvider("");
    setNewModel("");
  };
  return /* @__PURE__ */ jsxs12("section", { "aria-labelledby": "model-routing-heading", style: { display: "grid", gap: 12, border: "1px solid #dee2e6", borderRadius: 8, padding: 12 }, children: [
    /* @__PURE__ */ jsxs12("div", { children: [
      /* @__PURE__ */ jsx14("h4", { id: "model-routing-heading", style: { margin: 0 }, children: text2("Model routing", "\u092E\u0949\u0921\u0932 \u0930\u0942\u091F\u093F\u0902\u0917") }),
      /* @__PURE__ */ jsx14("small", { children: text2("Provider health is server-owned. Secret values are never returned.", "\u092A\u094D\u0930\u0926\u093E\u0924\u093E \u0938\u094D\u0935\u093E\u0938\u094D\u0925\u094D\u092F \u0938\u0930\u094D\u0935\u0930-\u0928\u093F\u092F\u0902\u0924\u094D\u0930\u093F\u0924 \u0939\u0948\u0964 \u0917\u094B\u092A\u0928\u0940\u092F \u092E\u093E\u0928 \u0915\u092D\u0940 \u0935\u093E\u092A\u0938 \u0928\u0939\u0940\u0902 \u0906\u0924\u0947\u0964") })
    ] }),
    /* @__PURE__ */ jsxs12("label", { children: [
      /* @__PURE__ */ jsx14("input", { type: "checkbox", checked: value.adaptiveEnabled, disabled: readOnly, onChange: (event) => update({ adaptiveEnabled: event.target.checked }) }),
      " ",
      text2("Adaptive routing enabled", "Adaptive \u0930\u0942\u091F\u093F\u0902\u0917 \u091A\u093E\u0932\u0942")
    ] }),
    /* @__PURE__ */ jsxs12("label", { children: [
      text2("Default logical mode", "\u0921\u093F\u092B\u093C\u0949\u0932\u094D\u091F \u0924\u093E\u0930\u094D\u0915\u093F\u0915 \u092E\u094B\u0921"),
      " ",
      /* @__PURE__ */ jsx14("select", { value: value.defaultMode, disabled: readOnly, onChange: (event) => update({ defaultMode: event.target.value }), children: ["adaptive", "fast", "normal", "ultra", "manual"].map((mode) => /* @__PURE__ */ jsx14("option", { children: mode }, mode)) })
    ] }),
    /* @__PURE__ */ jsx14("div", { style: { display: "grid", gridTemplateColumns: "repeat(3, minmax(150px, 1fr))", gap: 8 }, children: ["fast", "normal", "ultra"].map((mode) => /* @__PURE__ */ jsxs12("label", { children: [
      mode,
      " profile",
      " ",
      /* @__PURE__ */ jsx14(
        "select",
        {
          value: value.profileTruth[mode],
          disabled: readOnly,
          onChange: (event) => update({ profileTruth: { ...value.profileTruth, [mode]: event.target.value } }),
          children: codingProfiles.map((profile) => /* @__PURE__ */ jsx14("option", { children: profile }, profile))
        }
      )
    ] }, mode)) }),
    /* @__PURE__ */ jsx14("strong", { children: text2("Ordered provider/model candidates", "\u0915\u094D\u0930\u092E\u092C\u0926\u094D\u0927 \u092A\u094D\u0930\u0926\u093E\u0924\u093E/\u092E\u0949\u0921\u0932 \u0909\u092E\u094D\u092E\u0940\u0926\u0935\u093E\u0930") }),
    value.routes.map((route, index) => /* @__PURE__ */ jsxs12("div", { style: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }, children: [
      /* @__PURE__ */ jsx14("input", { type: "checkbox", checked: route.enabled, disabled: readOnly, onChange: (event) => {
        const routes = value.routes.map((candidate, candidateIndex) => candidateIndex === index ? { ...candidate, enabled: event.target.checked } : candidate);
        update({ routes });
      } }),
      /* @__PURE__ */ jsx14("code", { children: route.profileId }),
      /* @__PURE__ */ jsxs12("span", { children: [
        route.providerId,
        " / ",
        route.modelId
      ] }),
      /* @__PURE__ */ jsxs12("span", { children: [
        text2("Health", "\u0938\u094D\u0935\u093E\u0938\u094D\u0925\u094D\u092F"),
        ": ",
        value.providerHealth[route.profileId] ?? value.providerHealth[route.providerId] ?? "unknown"
      ] }),
      /* @__PURE__ */ jsx14("button", { type: "button", disabled: readOnly || index === 0, onClick: () => move(index, -1), children: "\u2191" }),
      /* @__PURE__ */ jsx14("button", { type: "button", disabled: readOnly || index === value.routes.length - 1, onClick: () => move(index, 1), children: "\u2193" })
    ] }, `${route.providerId}:${route.modelId}:${index}`)),
    !readOnly ? /* @__PURE__ */ jsxs12("div", { style: { display: "flex", gap: 8 }, children: [
      /* @__PURE__ */ jsx14("input", { "aria-label": "provider id", value: newProvider, onChange: (event) => setNewProvider(event.target.value), placeholder: "providerId" }),
      /* @__PURE__ */ jsx14("input", { "aria-label": "model id", value: newModel, onChange: (event) => setNewModel(event.target.value), placeholder: "modelId" }),
      /* @__PURE__ */ jsx14("button", { type: "button", onClick: addCandidate, children: text2("Add candidate", "\u0909\u092E\u094D\u092E\u0940\u0926\u0935\u093E\u0930 \u091C\u094B\u0921\u093C\u0947\u0902") })
    ] }) : null,
    /* @__PURE__ */ jsxs12("label", { children: [
      text2("Fallback order", "\u092B\u0949\u0932\u092C\u0948\u0915 \u0915\u094D\u0930\u092E"),
      " ",
      /* @__PURE__ */ jsx14(
        "input",
        {
          value: value.fallbackOrder.join(", "),
          disabled: readOnly,
          onChange: (event) => update({ fallbackOrder: event.target.value.split(",").map((entry) => entry.trim()).filter(Boolean) })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs12("fieldset", { children: [
      /* @__PURE__ */ jsx14("legend", { children: text2("Budget guard", "\u092C\u091C\u091F \u0938\u0941\u0930\u0915\u094D\u0937\u093E") }),
      ["fast", "normal", "ultra"].map((mode) => {
        const budget = { ...defaultUiBudget, ...value.modeBudgets[mode] };
        return /* @__PURE__ */ jsxs12("div", { style: { display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }, children: [
          /* @__PURE__ */ jsx14("strong", { style: { width: 60 }, children: mode }),
          /* @__PURE__ */ jsxs12("label", { children: [
            text2("Max cost/task", "\u0905\u0927\u093F\u0915\u0924\u092E \u0932\u093E\u0917\u0924/task"),
            " ",
            /* @__PURE__ */ jsx14(
              "input",
              {
                type: "number",
                min: 0,
                step: "0.01",
                value: Number(budget.maxCostPerTask),
                disabled: readOnly,
                onChange: (event) => update({
                  modeBudgets: {
                    ...value.modeBudgets,
                    [mode]: { ...budget, maxCostPerTask: Number(event.target.value) }
                  }
                })
              }
            )
          ] }),
          /* @__PURE__ */ jsxs12("label", { children: [
            /* @__PURE__ */ jsx14(
              "input",
              {
                type: "checkbox",
                checked: budget.hardStop !== false,
                disabled: readOnly,
                onChange: (event) => update({
                  modeBudgets: {
                    ...value.modeBudgets,
                    [mode]: { ...budget, hardStop: event.target.checked }
                  }
                })
              }
            ),
            " ",
            text2("Hard stop", "Hard stop")
          ] })
        ] }, mode);
      })
    ] }),
    /* @__PURE__ */ jsxs12("fieldset", { children: [
      /* @__PURE__ */ jsx14("legend", { children: text2("Privacy policy", "\u0917\u094B\u092A\u0928\u0940\u092F\u0924\u093E \u0928\u0940\u0924\u093F") }),
      [
        ["cloudAllowed", text2("Cloud allowed", "Cloud \u0905\u0928\u0941\u092E\u0924")],
        ["sensitiveCodeAllowed", text2("Sensitive code allowed", "\u0938\u0902\u0935\u0947\u0926\u0928\u0936\u0940\u0932 code \u0905\u0928\u0941\u092E\u0924")],
        ["customerDataAllowed", text2("Customer data allowed", "\u0917\u094D\u0930\u093E\u0939\u0915 \u0921\u0947\u091F\u093E \u0905\u0928\u0941\u092E\u0924")],
        ["localOnly", text2("Local only", "\u0915\u0947\u0935\u0932 local")]
      ].map(([key, label]) => /* @__PURE__ */ jsxs12("label", { style: { marginRight: 12 }, children: [
        /* @__PURE__ */ jsx14(
          "input",
          {
            type: "checkbox",
            checked: value.privacyPolicy[key],
            disabled: readOnly,
            onChange: (event) => update({ privacyPolicy: { ...value.privacyPolicy, [key]: event.target.checked } })
          }
        ),
        " ",
        label
      ] }, key))
    ] }),
    /* @__PURE__ */ jsxs12("div", { children: [
      text2("Secret status", "\u0917\u094B\u092A\u0928\u0940\u092F \u0938\u094D\u0925\u093F\u0924\u093F"),
      ": ",
      /* @__PURE__ */ jsx14("strong", { children: setting?.credentialStatus ?? "missing" })
    ] }),
    /* @__PURE__ */ jsx14(
      "button",
      {
        type: "button",
        disabled: readOnly,
        onClick: () => {
          const { providerHealth: _providerHealth, ...writableValue } = value;
          void onSave(setting?.settingKey ?? "coding_router", { coding: writableValue });
        },
        children: text2("Save routing settings", "\u0930\u0942\u091F\u093F\u0902\u0917 \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0915\u0930\u0947\u0902")
      }
    )
  ] });
}
function extensionProjection(setting) {
  const value = setting?.settingValue ?? {};
  const controlMode = value.controlMode === "preview_only" || value.controlMode === "server_controlled" ? value.controlMode : "unavailable";
  return {
    enabled: value.enabled === true,
    controlMode,
    actorId: typeof value.actorId === "string" ? value.actorId : "\u0905\u0938\u0902\u092C\u0926\u094D\u0927",
    ownerId: typeof value.ownerId === "string" ? value.ownerId : "\u0905\u0938\u0902\u092C\u0926\u094D\u0927",
    tenantId: typeof value.tenantId === "string" ? value.tenantId : "\u0905\u0938\u0902\u092C\u0926\u094D\u0927",
    pairingRequired: value.pairingRequired !== false,
    pairingState: typeof value.pairingState === "string" ? value.pairingState : "not_paired",
    extensionVersion: typeof value.extensionVersion === "string" ? value.extensionVersion : "\u0905\u091C\u094D\u091E\u093E\u0924",
    minimumAllowedVersion: typeof value.minimumAllowedVersion === "string" ? value.minimumAllowedVersion : "\u0905\u091C\u094D\u091E\u093E\u0924",
    allowedExtensionIds: Array.isArray(value.allowedExtensionIds) ? value.allowedExtensionIds.filter(
      (entry) => typeof entry === "string"
    ) : [],
    allowedOrigins: Array.isArray(value.allowedOrigins) ? value.allowedOrigins.filter(
      (entry) => typeof entry === "string"
    ) : [],
    trusted: value.trusted === true,
    projectBinding: typeof value.projectBinding === "string" ? value.projectBinding : "\u0905\u0938\u0902\u092C\u0926\u094D\u0927",
    allowedReadPermissions: Array.isArray(value.allowedReadPermissions) ? value.allowedReadPermissions.filter(
      (permission) => typeof permission === "string"
    ) : [],
    allowedControlPermissions: Array.isArray(value.allowedControlPermissions) ? value.allowedControlPermissions.filter(
      (permission) => typeof permission === "string"
    ) : [],
    canonicalBrowserMutationAvailable: value.canonicalBrowserMutationAvailable === true,
    executionIdentityRequired: value.executionIdentityRequired !== false,
    emergencyStop: value.emergencyStop === true,
    approvalRequired: value.approvalRequired !== false,
    stagingEnabled: value.stagingEnabled === true,
    productionEnabled: value.productionEnabled === true,
    configured: value.configured === true,
    health: typeof value.health === "string" ? value.health : "disabled",
    sourceExecutionSupported: value.sourceExecutionSupported === true,
    runtimeVerified: value.runtimeVerified === true,
    liveProven: value.liveProven === true,
    realExecutionSupported: value.realExecutionSupported === true && value.configured === true && value.runtimeVerified === true && value.liveProven === true && value.canonicalBrowserMutationAvailable === true && value.emergencyStop !== true,
    factoryProofPending: value.factoryProofPending !== false
  };
}
function browserControlModeLabel(mode) {
  if (mode === "preview_only") return "\u0915\u0947\u0935\u0932 \u092A\u0942\u0930\u094D\u0935\u093E\u0935\u0932\u094B\u0915\u0928";
  if (mode === "server_controlled") return "\u0938\u0930\u094D\u0935\u0930 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u093F\u0924";
  return "\u0905\u0928\u0941\u092A\u0932\u092C\u094D\u0927";
}
function pairingStateLabel(state) {
  const labels = {
    runtime_unavailable: "\u0930\u0928\u091F\u093E\u0907\u092E \u0905\u0928\u0941\u092A\u0932\u092C\u094D\u0927",
    not_paired: "\u092F\u0941\u0917\u094D\u092E\u093F\u0924 \u0928\u0939\u0940\u0902",
    pairing_pending: "\u092F\u0941\u0917\u094D\u092E\u0928 \u0932\u0902\u092C\u093F\u0924",
    connected: "\u091C\u0941\u0921\u093C\u093E \u0939\u0941\u0906",
    revoked: "\u0928\u093F\u0930\u0938\u094D\u0924",
    expired: "\u0938\u092E\u093E\u092A\u094D\u0924"
  };
  return labels[state] ?? state;
}
function healthLabel(health) {
  const labels = {
    disabled: "\u0928\u093F\u0937\u094D\u0915\u094D\u0930\u093F\u092F",
    configured_unverified: "\u0935\u093F\u0928\u094D\u092F\u0938\u094D\u0924, \u0905\u0938\u0924\u094D\u092F\u093E\u092A\u093F\u0924",
    available: "\u0909\u092A\u0932\u092C\u094D\u0927",
    unavailable: "\u0905\u0928\u0941\u092A\u0932\u092C\u094D\u0927"
  };
  return labels[health] ?? health;
}
function BrowserExtensionSettingsPanel({
  setting,
  locale
}) {
  const extension = extensionProjection(setting);
  const text2 = (en, hi) => locale === "hi" ? hi : en;
  const rows = [
    [text2("Policy", "\u0928\u0940\u0924\u093F"), extension.enabled ? text2("On", "\u091A\u093E\u0932\u0942") : text2("Off", "\u092C\u0902\u0926")],
    [text2("Control mode", "\u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923 \u092A\u094D\u0930\u0915\u093E\u0930"), browserControlModeLabel(extension.controlMode)],
    [text2("Actor identity", "\u0905\u092D\u093F\u0928\u0947\u0924\u093E \u092A\u0939\u091A\u093E\u0928"), extension.actorId],
    [text2("Owner identity", "\u0938\u094D\u0935\u093E\u092E\u0940 \u092A\u0939\u091A\u093E\u0928"), extension.ownerId],
    [text2("Tenant identity", "\u0915\u093F\u0930\u093E\u092F\u0947\u0926\u093E\u0930 \u092A\u0939\u091A\u093E\u0928"), extension.tenantId],
    [text2("Pairing required", "\u092F\u0941\u0917\u094D\u092E\u0928 \u0906\u0935\u0936\u094D\u092F\u0915"), extension.pairingRequired ? text2("Yes", "\u0939\u093E\u0901") : text2("No", "\u0928\u0939\u0940\u0902")],
    [text2("Pairing/session", "\u092F\u0941\u0917\u094D\u092E\u0928/\u0938\u0924\u094D\u0930"), pairingStateLabel(extension.pairingState)],
    [text2("Extension version", "\u090F\u0915\u094D\u0938\u091F\u0947\u0902\u0936\u0928 \u0938\u0902\u0938\u094D\u0915\u0930\u0923"), extension.extensionVersion],
    [text2("Minimum version", "\u0928\u094D\u092F\u0942\u0928\u0924\u092E \u0938\u0902\u0938\u094D\u0915\u0930\u0923"), extension.minimumAllowedVersion],
    [
      "\u0905\u0928\u0941\u092E\u0924 \u090F\u0915\u094D\u0938\u091F\u0947\u0902\u0936\u0928 \u092A\u0939\u091A\u093E\u0928",
      extension.allowedExtensionIds.join(", ") || "\u0915\u094B\u0908 \u0928\u0939\u0940\u0902"
    ],
    ["\u0905\u0928\u0941\u092E\u0924 \u0909\u0926\u094D\u0917\u092E", extension.allowedOrigins.join(", ") || "\u0915\u094B\u0908 \u0928\u0939\u0940\u0902"],
    ["\u0935\u093F\u0936\u094D\u0935\u0938\u0928\u0940\u092F", extension.trusted ? "\u0939\u093E\u0901" : "\u0928\u0939\u0940\u0902"],
    ["\u092A\u0930\u093F\u092F\u094B\u091C\u0928\u093E \u092C\u0902\u0927\u0928", extension.projectBinding],
    [
      "\u0905\u0928\u0941\u092E\u0924 \u092A\u0920\u0928 \u0905\u0928\u0941\u092E\u0924\u093F\u092F\u093E\u0901",
      extension.allowedReadPermissions.join(", ") || "\u0915\u094B\u0908 \u0928\u0939\u0940\u0902"
    ],
    [
      "\u0905\u0928\u0941\u092E\u0924 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923 \u0905\u0928\u0941\u092E\u0924\u093F\u092F\u093E\u0901",
      extension.allowedControlPermissions.join(", ") || "\u0915\u094B\u0908 \u0928\u0939\u0940\u0902"
    ],
    [
      "\u092E\u093E\u0928\u0915 \u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930 \u092A\u0930\u093F\u0935\u0930\u094D\u0924\u0928",
      extension.canonicalBrowserMutationAvailable ? "\u0909\u092A\u0932\u092C\u094D\u0927" : "\u0905\u0928\u0941\u092A\u0932\u092C\u094D\u0927"
    ],
    [
      "\u0938\u094D\u0925\u093E\u092F\u0940 \u0928\u093F\u0937\u094D\u092A\u093E\u0926\u0928 \u092A\u0939\u091A\u093E\u0928",
      extension.executionIdentityRequired ? "\u0906\u0935\u0936\u094D\u092F\u0915" : "\u0905\u0928\u0941\u092A\u0932\u092C\u094D\u0927"
    ],
    ["\u0905\u0928\u0941\u092E\u094B\u0926\u0928 \u0906\u0935\u0936\u094D\u092F\u0915", extension.approvalRequired ? "\u0939\u093E\u0901" : "\u0928\u0939\u0940\u0902"],
    ["\u0938\u094D\u091F\u0947\u091C\u093F\u0902\u0917 \u092E\u0947\u0902 \u091A\u093E\u0932\u0942", extension.stagingEnabled ? "\u0939\u093E\u0901" : "\u0928\u0939\u0940\u0902"],
    ["\u092A\u094D\u0930\u094B\u0921\u0915\u094D\u0936\u0928 \u092E\u0947\u0902 \u091A\u093E\u0932\u0942", extension.productionEnabled ? "\u0939\u093E\u0901" : "\u0928\u0939\u0940\u0902"],
    ["\u0935\u093F\u0928\u094D\u092F\u093E\u0938 \u092A\u0942\u0930\u094D\u0923", extension.configured ? "\u0939\u093E\u0901" : "\u0928\u0939\u0940\u0902"],
    ["\u0906\u092A\u093E\u0924\u0915\u093E\u0932\u0940\u0928 \u0930\u094B\u0915", extension.emergencyStop ? "\u0938\u0915\u094D\u0930\u093F\u092F" : "\u0928\u093F\u0937\u094D\u0915\u094D\u0930\u093F\u092F"],
    ["\u0938\u094D\u0935\u093E\u0938\u094D\u0925\u094D\u092F", healthLabel(extension.health)],
    [
      "\u0938\u094D\u0930\u094B\u0924 \u0928\u093F\u0937\u094D\u092A\u093E\u0926\u0928 \u0938\u092E\u0930\u094D\u0925\u0928",
      extension.sourceExecutionSupported ? "\u0939\u093E\u0901" : "\u0928\u0939\u0940\u0902"
    ],
    ["\u0930\u0928\u091F\u093E\u0907\u092E \u0938\u0924\u094D\u092F\u093E\u092A\u093F\u0924", extension.runtimeVerified ? "\u0939\u093E\u0901" : "\u0928\u0939\u0940\u0902"],
    ["\u092A\u094D\u0930\u0924\u094D\u092F\u0915\u094D\u0937 \u092A\u094D\u0930\u092E\u093E\u0923\u093F\u0924", extension.liveProven ? "\u0939\u093E\u0901" : "\u0928\u0939\u0940\u0902"],
    [
      "\u0935\u093E\u0938\u094D\u0924\u0935\u093F\u0915 \u0928\u093F\u0937\u094D\u092A\u093E\u0926\u0928 \u0938\u092E\u0930\u094D\u0925\u093F\u0924",
      extension.realExecutionSupported ? "\u0939\u093E\u0901" : "\u0928\u0939\u0940\u0902"
    ]
  ];
  return /* @__PURE__ */ jsxs12(
    "section",
    {
      "aria-labelledby": "browser-extension-settings-heading",
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        padding: "12px"
      },
      children: [
        /* @__PURE__ */ jsx14("h4", { id: "browser-extension-settings-heading", style: { margin: "0 0 10px" }, children: text2("Browser extension", "\u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930 \u090F\u0915\u094D\u0938\u091F\u0947\u0902\u0936\u0928") }),
        /* @__PURE__ */ jsx14(
          "dl",
          {
            style: {
              display: "grid",
              gridTemplateColumns: "minmax(160px, 1fr) 2fr",
              gap: "8px 12px",
              margin: 0,
              fontSize: "13px"
            },
            children: rows.map(([label, value]) => /* @__PURE__ */ jsxs12(React7.Fragment, { children: [
              /* @__PURE__ */ jsx14("dt", { style: { fontWeight: 600 }, children: label }),
              /* @__PURE__ */ jsx14("dd", { style: { margin: 0 }, children: value })
            ] }, label))
          }
        ),
        extension.factoryProofPending || !extension.liveProven ? /* @__PURE__ */ jsx14("p", { style: { margin: "10px 0 0", color: "#6c757d", fontSize: "12px" }, children: text2("Factory proof is pending; configuration or transport success is not treated as live proof.", "\u092B\u093C\u0948\u0915\u094D\u091F\u0930\u0940 \u092A\u094D\u0930\u092E\u093E\u0923 \u0932\u0902\u092C\u093F\u0924 \u0939\u0948; \u0915\u0947\u0935\u0932 \u0935\u093F\u0928\u094D\u092F\u093E\u0938 \u092F\u093E \u092A\u0930\u093F\u0935\u0939\u0928 \u0938\u092B\u0932\u0924\u093E \u0915\u094B \u092A\u094D\u0930\u0924\u094D\u092F\u0915\u094D\u0937 \u092A\u094D\u0930\u092E\u093E\u0923 \u0928\u0939\u0940\u0902 \u092E\u093E\u0928\u093E \u0917\u092F\u093E \u0939\u0948\u0964") }) : null
      ]
    }
  );
}
function CanonicalSettingsControlPlane({
  token,
  readOnly = true,
  locale = "hi",
  baseUrl,
  fetchImpl
}) {
  const C = useBrandColors();
  const text2 = (en, hi) => locale === "hi" ? hi : en;
  const service = useMemo6(
    () => token ? createCanonicalSettingsService({
      token,
      ...baseUrl ? { baseUrl } : {},
      ...fetchImpl ? { fetchImpl } : {}
    }) : void 0,
    [baseUrl, fetchImpl, token]
  );
  const [namespace, setNamespace] = useState9("ceo");
  const [settings, setSettings] = useState9([]);
  const [settingKey, setSettingKey] = useState9("runtime");
  const [settingValue, setSettingValue] = useState9('{\n  "enabled": false\n}');
  const [secretRef, setSecretRef] = useState9("");
  const [status, setStatus] = useState9("");
  const extensionRuntimeSetting = useMemo6(
    () => settings.find((setting) => setting.settingKey === "runtime"),
    [settings]
  );
  const providerRoutingSetting = useMemo6(
    () => settings.find(
      (setting) => setting.settingKey === "coding_router" || setting.settingValue.coding && typeof setting.settingValue.coding === "object"
    ) ?? settings[0],
    [settings]
  );
  const refresh = useCallback4(async (signal) => {
    if (!service) {
      setSettings([]);
      setStatus(text2("An authenticated session is not available.", "\u092A\u094D\u0930\u092E\u093E\u0923\u093F\u0924 \u0938\u0924\u094D\u0930 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964"));
      return;
    }
    try {
      setSettings(await service.list(namespace, signal));
      setStatus("");
    } catch (error) {
      if (signal?.aborted) return;
      setStatus(`${text2("Settings could not be loaded.", "\u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 \u092A\u0922\u093C\u0940 \u0928\u0939\u0940\u0902 \u091C\u093E \u0938\u0915\u0940\u0902\u0964")} (${safeErrorText(errorToCode(error), locale).title})`);
    }
  }, [namespace, service]);
  useEffect6(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);
  const save = async () => {
    if (!service || readOnly) return;
    try {
      const parsed = JSON.parse(settingValue);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setStatus(text2("The setting value must be a JSON object.", "\u0938\u0947\u091F\u093F\u0902\u0917 \u092E\u093E\u0928 \u090F\u0915 JSON \u0935\u0938\u094D\u0924\u0941 \u0939\u094B\u0928\u093E \u091A\u093E\u0939\u093F\u090F\u0964"));
        return;
      }
      await service.save({
        namespace,
        settingKey,
        settingValue: parsed,
        ...secretRef.trim() ? { secretRef: secretRef.trim() } : {}
      });
      setStatus(text2("Setting saved.", "\u0938\u0947\u091F\u093F\u0902\u0917 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0939\u094B \u0917\u0908\u0964"));
      await refresh();
    } catch (error) {
      setStatus(`${text2("Setting could not be saved.", "\u0938\u0947\u091F\u093F\u0902\u0917 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0940\u0964")} (${safeErrorText(errorToCode(error), locale).title})`);
    }
  };
  const remove = async () => {
    if (!service || readOnly) return;
    try {
      const deleted = await service.delete(namespace, settingKey);
      setStatus(deleted ? text2("Setting deleted.", "\u0938\u0947\u091F\u093F\u0902\u0917 \u0939\u091F\u093E\u0908 \u0917\u0908\u0964") : text2("Setting did not exist.", "\u0938\u0947\u091F\u093F\u0902\u0917 \u092E\u094C\u091C\u0942\u0926 \u0928\u0939\u0940\u0902 \u0925\u0940\u0964"));
      await refresh();
    } catch (error) {
      setStatus(`${text2("Setting could not be deleted.", "\u0938\u0947\u091F\u093F\u0902\u0917 \u0939\u091F\u093E\u0908 \u0928\u0939\u0940\u0902 \u091C\u093E \u0938\u0915\u0940\u0964")} (${safeErrorText(errorToCode(error), locale).title})`);
    }
  };
  const saveStructured = async (key, value) => {
    if (!service || readOnly) return;
    try {
      await service.save({
        namespace: "providers",
        settingKey: key,
        settingValue: value
      });
      setStatus(text2("Routing settings saved.", "\u0930\u0942\u091F\u093F\u0902\u0917 \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0939\u094B \u0917\u0908\u0902\u0964"));
      await refresh();
    } catch (error) {
      setStatus(`${text2("Routing settings could not be saved.", "\u0930\u0942\u091F\u093F\u0902\u0917 \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0940\u0902\u0964")} (${safeErrorText(errorToCode(error), locale).title})`);
    }
  };
  return /* @__PURE__ */ jsxs12("section", { style: { display: "grid", gap: "16px" }, children: [
    /* @__PURE__ */ jsxs12("div", { children: [
      /* @__PURE__ */ jsx14("h3", { style: { margin: "0 0 6px" }, children: text2("Canonical settings control plane", "\u092E\u093E\u0928\u0915 \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923 \u0915\u0947\u0902\u0926\u094D\u0930") }),
      /* @__PURE__ */ jsx14("p", { style: { margin: 0, color: C.muted, fontSize: "14px" }, children: text2("Runtime settings are server-validated by namespace and scope; credentials remain server-only.", "\u0938\u092D\u0940 \u0930\u0928\u091F\u093E\u0907\u092E \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 \u0928\u093E\u092E\u0938\u094D\u0925\u093E\u0928 \u0914\u0930 \u0926\u093E\u092F\u0930\u0947 \u0915\u0947 \u0905\u0928\u0941\u0938\u093E\u0930 \u0938\u0930\u094D\u0935\u0930 \u092A\u0930 \u0938\u0924\u094D\u092F\u093E\u092A\u093F\u0924 \u0914\u0930 \u0938\u0902\u0935\u0947\u0926\u0928\u0936\u0940\u0932 \u091C\u093E\u0928\u0915\u093E\u0930\u0940 \u0938\u0947 \u092E\u0941\u0915\u094D\u0924 \u0939\u094B\u0924\u0940 \u0939\u0948\u0902\u0964") })
    ] }),
    /* @__PURE__ */ jsxs12("label", { style: { display: "grid", gap: "6px", fontSize: "13px", fontWeight: 600 }, children: [
      text2("Namespace", "\u0928\u093E\u092E\u0938\u094D\u0925\u093E\u0928"),
      /* @__PURE__ */ jsx14("select", { value: namespace, onChange: (event) => setNamespace(event.target.value), style: { background: C.inputBg, color: C.fg, border: `1px solid ${C.border}` }, children: SETTINGS_NAMESPACES.map((value) => /* @__PURE__ */ jsx14("option", { value, children: value }, value)) })
    ] }),
    namespace === "extension" ? /* @__PURE__ */ jsx14(BrowserExtensionSettingsPanel, { setting: extensionRuntimeSetting, locale }) : null,
    namespace === "providers" ? /* @__PURE__ */ jsx14(
      ModelRoutingSettingsPanel,
      {
        setting: providerRoutingSetting,
        readOnly,
        locale,
        onSave: saveStructured
      }
    ) : null,
    /* @__PURE__ */ jsx14("div", { style: { display: "grid", gap: "8px" }, children: settings.length === 0 ? /* @__PURE__ */ jsx14("div", { style: { color: C.muted, fontSize: "13px" }, children: text2("No safe setting exists in this namespace.", "\u0907\u0938 \u0928\u093E\u092E\u0938\u094D\u0925\u093E\u0928 \u092E\u0947\u0902 \u0915\u094B\u0908 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0938\u0947\u091F\u093F\u0902\u0917 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964") }) : settings.map((setting) => /* @__PURE__ */ jsxs12(
      "button",
      {
        type: "button",
        onClick: () => {
          setSettingKey(setting.settingKey);
          setSettingValue(JSON.stringify(setting.settingValue, null, 2));
          setSecretRef("");
        },
        style: { textAlign: "left", padding: "10px", border: `1px solid ${C.border}`, borderRadius: "6px", background: C.inputBg, color: C.fg },
        children: [
          /* @__PURE__ */ jsx14("strong", { children: setting.settingKey }),
          /* @__PURE__ */ jsx14("span", { style: { marginLeft: "8px", color: C.muted, fontSize: "12px" }, children: setting.serverOnlyCredentials ? text2("Server credential configured", "\u0938\u0930\u094D\u0935\u0930 \u092A\u094D\u0930\u092E\u093E\u0923-\u092A\u0924\u094D\u0930 \u0935\u093F\u0928\u094D\u092F\u0938\u094D\u0924") : text2("No secret reference", "\u0915\u094B\u0908 \u0917\u094B\u092A\u0928\u0940\u092F \u0938\u0902\u0926\u0930\u094D\u092D \u0928\u0939\u0940\u0902") })
        ]
      },
      setting.id
    )) }),
    /* @__PURE__ */ jsxs12("label", { style: { display: "grid", gap: "6px", fontSize: "13px", fontWeight: 600 }, children: [
      text2("Setting key", "\u0938\u0947\u091F\u093F\u0902\u0917 \u0915\u0941\u0902\u091C\u0940"),
      /* @__PURE__ */ jsx14("input", { value: settingKey, onChange: (event) => setSettingKey(event.target.value), disabled: readOnly })
    ] }),
    /* @__PURE__ */ jsxs12("label", { style: { display: "grid", gap: "6px", fontSize: "13px", fontWeight: 600 }, children: [
      text2("JSON value", "JSON \u092E\u093E\u0928"),
      /* @__PURE__ */ jsx14("textarea", { rows: 8, value: settingValue, onChange: (event) => setSettingValue(event.target.value), disabled: readOnly })
    ] }),
    /* @__PURE__ */ jsxs12("label", { style: { display: "grid", gap: "6px", fontSize: "13px", fontWeight: 600 }, children: [
      text2("Secure secret reference", "\u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0917\u094B\u092A\u0928\u0940\u092F \u0938\u0902\u0926\u0930\u094D\u092D"),
      /* @__PURE__ */ jsx14(
        "input",
        {
          value: secretRef,
          onChange: (event) => setSecretRef(event.target.value),
          placeholder: "secret://provider/credential",
          disabled: readOnly
        }
      )
    ] }),
    /* @__PURE__ */ jsxs12("div", { style: { display: "flex", gap: "8px" }, children: [
      /* @__PURE__ */ jsx14("button", { type: "button", onClick: () => void save(), disabled: readOnly || !service, children: text2("Save", "\u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0915\u0930\u0947\u0902") }),
      /* @__PURE__ */ jsx14("button", { type: "button", onClick: () => void remove(), disabled: readOnly || !service, children: text2("Delete", "\u0939\u091F\u093E\u090F\u0901") })
    ] }),
    status ? /* @__PURE__ */ jsx14("div", { role: "status", style: { color: C.muted, fontSize: "13px" }, children: status }) : null
  ] });
}

// src/settings/SecretBoxPanel.tsx
import { useCallback as useCallback5, useEffect as useEffect7, useRef as useRef3, useState as useState10 } from "react";
import { Fragment as Fragment2, jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
var KIND_OPTIONS = [
  { id: "llm_provider", label_hi: "LLM \u092A\u094D\u0930\u0926\u093E\u0924\u093E \u0915\u0941\u0902\u091C\u0940" },
  { id: "github_connector", label_hi: "GitHub \u0915\u0928\u0947\u0915\u094D\u091F\u0930" },
  { id: "supabase_connector", label_hi: "Supabase \u0915\u0928\u0947\u0915\u094D\u091F\u0930" },
  { id: "email_smtp", label_hi: "\u0908\u092E\u0947\u0932/SMTP" },
  { id: "whatsapp_provider", label_hi: "WhatsApp" },
  { id: "payment_provider", label_hi: "\u092D\u0941\u0917\u0924\u093E\u0928" },
  { id: "webhook_hmac", label_hi: "Webhook/HMAC" },
  { id: "deploy_automation", label_hi: "\u0921\u093F\u092A\u094D\u0932\u0949\u092F/\u0911\u091F\u094B\u092E\u0947\u0936\u0928" },
  { id: "project_env", label_hi: "\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F env" },
  { id: "custom", label_hi: "\u0905\u0928\u094D\u092F" }
];
var COPY = {
  title: "\u0938\u0940\u0915\u094D\u0930\u0947\u091F \u092C\u0949\u0915\u094D\u0938",
  blurb: "\u0938\u0940\u0915\u094D\u0930\u0947\u091F \u0938\u0947\u0935 \u0939\u094B\u0928\u0947 \u0915\u0947 \u092C\u093E\u0926 \u0926\u094B\u092C\u093E\u0930\u093E \u0926\u093F\u0916\u093E\u0908 \u0928\u0939\u0940\u0902 \u0926\u0947\u0917\u093E; Smart Shikka \u0915\u0947\u0935\u0932 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 reference \u0915\u093E \u0909\u092A\u092F\u094B\u0917 \u0915\u0930\u0947\u0917\u093E\u0964",
  empty: "\u0915\u094B\u0908 \u0938\u0940\u0915\u094D\u0930\u0947\u091F \u0938\u0939\u0947\u091C\u093E \u0928\u0939\u0940\u0902 \u0917\u092F\u093E \u0939\u0948",
  add: "\u0928\u092F\u093E \u0938\u0940\u0915\u094D\u0930\u0947\u091F \u091C\u094B\u0921\u093C\u0947\u0902",
  label: "\u0928\u093E\u092E",
  kind: "\u092A\u094D\u0930\u0915\u093E\u0930",
  provider: "\u092A\u094D\u0930\u0926\u093E\u0924\u093E (\u0935\u0948\u0915\u0932\u094D\u092A\u093F\u0915)",
  value: "\u0938\u0940\u0915\u094D\u0930\u0947\u091F \u092E\u093E\u0928",
  save: "\u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0938\u0939\u0947\u091C\u0947\u0902",
  rotate: "\u0928\u092F\u093E \u092E\u093E\u0928 \u092C\u0926\u0932\u0947\u0902",
  delete: "\u0939\u091F\u093E\u090F\u0901",
  test: "\u091C\u093E\u0901\u091A\u0947\u0902",
  cancel: "\u0930\u0926\u094D\u0926 \u0915\u0930\u0947\u0902",
  confirmDelete: "\u092A\u0915\u094D\u0915\u093E \u0939\u091F\u093E\u090F\u0901",
  unavailable: "\u0938\u0940\u0915\u094D\u0930\u0947\u091F \u092C\u0949\u0915\u094D\u0938 \u0905\u092D\u0940 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948 \u2014 \u0938\u0947\u0935\u093E \u0915\u0940 \u0938\u094D\u0925\u093F\u0924\u093F \u091C\u093E\u0901\u091A\u0947\u0902\u0964",
  saved: "\u0938\u0940\u0915\u094D\u0930\u0947\u091F \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0939\u0941\u0906 \u2014 \u092F\u0939 \u0905\u092C \u0926\u094B\u092C\u093E\u0930\u093E \u0928\u0939\u0940\u0902 \u0926\u093F\u0916\u0947\u0917\u093E\u0964",
  rotated: "\u0938\u0940\u0915\u094D\u0930\u0947\u091F \u092C\u0926\u0932\u093E \u0917\u092F\u093E \u2014 \u0928\u092F\u093E \u092E\u093E\u0928 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924, \u092A\u0941\u0930\u093E\u0928\u093E \u0926\u093F\u0916\u093E\u0908 \u0928\u0939\u0940\u0902 \u0926\u0947\u0917\u093E\u0964",
  deleted: "\u0938\u0940\u0915\u094D\u0930\u0947\u091F \u0939\u091F\u093E\u092F\u093E \u0917\u092F\u093E\u0964",
  testOk: "\u091C\u093E\u0901\u091A \u0938\u092B\u0932",
  testFail: "\u091C\u093E\u0901\u091A \u0905\u0938\u092B\u0932"
};
function SecretBoxPanel(props) {
  const { token, baseUrl = "", scope, projectId } = props;
  const doFetch = props.fetchImpl ?? fetch;
  const [secrets, setSecrets] = useState10([]);
  const [loading, setLoading] = useState10(true);
  const [unavailable, setUnavailable] = useState10(false);
  const [error, setError] = useState10(null);
  const [notice, setNotice] = useState10(null);
  const [busyRef, setBusyRef] = useState10(null);
  const [adding, setAdding] = useState10(false);
  const [label, setLabel] = useState10("");
  const [kind, setKind] = useState10("llm_provider");
  const [provider, setProvider] = useState10("");
  const [value, setValue] = useState10("");
  const valueRef = useRef3(null);
  const [rotatingRef, setRotatingRef] = useState10(null);
  const [rotateValue, setRotateValue] = useState10("");
  const [deleteRef, setDeleteRef] = useState10(null);
  const call = useCallback5(
    async (method, path, body) => {
      const res = await doFetch(`${baseUrl}${path}`, {
        method,
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token ?? ""}`,
          ...body !== void 0 ? { "Content-Type": "application/json" } : {}
        },
        body: body === void 0 ? null : JSON.stringify(body)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(typeof json.error === "string" ? json.error : `http_error_${res.status}`);
        err.name = typeof json.errorCode === "string" ? json.errorCode : "api_error";
        err.status = res.status;
        throw err;
      }
      return json;
    },
    [doFetch, baseUrl, token]
  );
  const load = useCallback5(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (scope) qs.set("scope", scope);
      if (projectId) qs.set("projectId", projectId);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const json = await call("GET", `/api/secrets${suffix}`);
      setSecrets(json.secrets ?? []);
      setUnavailable(false);
    } catch (err) {
      const status = err.status;
      if (status === 503) {
        setUnavailable(true);
        setSecrets([]);
      } else {
        setError(err instanceof Error ? err.message : "load_failed");
      }
    } finally {
      setLoading(false);
    }
  }, [call, scope, projectId]);
  useEffect7(() => {
    void load();
  }, [load]);
  const clearSensitiveInputs = useCallback5(() => {
    setValue("");
    setRotateValue("");
    if (valueRef.current) valueRef.current.value = "";
  }, []);
  const createSecret = useCallback5(async () => {
    if (!label.trim() || !value) return;
    setBusyRef("__create__");
    setError(null);
    try {
      await call("POST", "/api/secrets", {
        scope: scope ?? "account",
        ...scope === "project" && projectId ? { projectId } : {},
        ...provider.trim() ? { provider: provider.trim() } : {},
        kind,
        label: label.trim(),
        value
      });
      setNotice(COPY.saved);
      setAdding(false);
      setLabel("");
      setProvider("");
      clearSensitiveInputs();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "save_failed");
    } finally {
      setBusyRef(null);
    }
  }, [call, label, value, scope, projectId, provider, kind, clearSensitiveInputs, load]);
  const rotateSecret = useCallback5(async (secretRef) => {
    if (!rotateValue) return;
    setBusyRef(secretRef);
    setError(null);
    try {
      await call("POST", `/api/secrets/${encodeURIComponent(secretRef)}/rotate`, { value: rotateValue });
      setNotice(COPY.rotated);
      setRotatingRef(null);
      clearSensitiveInputs();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "rotate_failed");
    } finally {
      setBusyRef(null);
    }
  }, [call, rotateValue, clearSensitiveInputs, load]);
  const deleteSecret = useCallback5(async (secretRef) => {
    setBusyRef(secretRef);
    setError(null);
    try {
      await call("POST", `/api/secrets/${encodeURIComponent(secretRef)}/delete`);
      setNotice(COPY.deleted);
      setDeleteRef(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "delete_failed");
    } finally {
      setBusyRef(null);
    }
  }, [call, load]);
  const testSecret = useCallback5(async (secretRef) => {
    setBusyRef(secretRef);
    setError(null);
    setNotice(null);
    try {
      const json = await call("POST", `/api/secrets/${encodeURIComponent(secretRef)}/test`);
      setNotice(json.ok === true ? COPY.testOk : `${COPY.testFail}: ${typeof json.detail === "string" ? json.detail : "failed"}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "test_failed");
    } finally {
      setBusyRef(null);
    }
  }, [call]);
  if (unavailable) {
    return /* @__PURE__ */ jsxs13("section", { className: "secret-box-panel", "data-testid": "secret-box-panel", children: [
      /* @__PURE__ */ jsx15("h3", { children: COPY.title }),
      /* @__PURE__ */ jsx15(SettingsEmptyState, { title_hi: COPY.title, message_hi: COPY.unavailable })
    ] });
  }
  return /* @__PURE__ */ jsxs13("section", { className: "secret-box-panel", "data-testid": "secret-box-panel", children: [
    /* @__PURE__ */ jsx15("h3", { children: COPY.title }),
    /* @__PURE__ */ jsx15("p", { className: "secret-box-blurb", children: COPY.blurb }),
    notice !== null && /* @__PURE__ */ jsx15("p", { role: "status", style: { color: "#2b8a3e", fontSize: "13px" }, children: notice }),
    error !== null && /* @__PURE__ */ jsx15("p", { role: "alert", style: { color: "#c92a2a", fontSize: "13px" }, children: error }),
    loading ? /* @__PURE__ */ jsx15("p", { children: "\u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026" }) : secrets.length === 0 ? /* @__PURE__ */ jsx15(SettingsEmptyState, { title_hi: COPY.title, message_hi: COPY.empty }) : /* @__PURE__ */ jsx15("ul", { className: "secret-box-list", children: secrets.map((s) => /* @__PURE__ */ jsxs13("li", { className: "secret-box-item", children: [
      /* @__PURE__ */ jsxs13("div", { className: "secret-box-meta", children: [
        /* @__PURE__ */ jsx15("strong", { children: s.label }),
        /* @__PURE__ */ jsx15("span", { className: "secret-box-kind", children: KIND_OPTIONS.find((k) => k.id === s.kind)?.label_hi ?? s.kind }),
        s.provider && /* @__PURE__ */ jsx15("span", { className: "secret-box-provider", children: s.provider }),
        /* @__PURE__ */ jsx15("span", { className: "secret-box-status", children: s.status }),
        s.lastUsedAt && /* @__PURE__ */ jsxs13("span", { className: "secret-box-used", children: [
          "\u0905\u0902\u0924\u093F\u092E \u0909\u092A\u092F\u094B\u0917: ",
          new Date(s.lastUsedAt).toLocaleString("hi-IN")
        ] })
      ] }),
      /* @__PURE__ */ jsx15("div", { className: "secret-box-actions", children: rotatingRef === s.secretRef ? /* @__PURE__ */ jsxs13(Fragment2, { children: [
        /* @__PURE__ */ jsx15(
          "input",
          {
            type: "password",
            autoComplete: "off",
            "data-testid": "secret-rotate-input",
            placeholder: COPY.value,
            value: rotateValue,
            onChange: (e) => setRotateValue(e.target.value)
          }
        ),
        /* @__PURE__ */ jsx15("button", { type: "button", disabled: busyRef === s.secretRef, onClick: () => void rotateSecret(s.secretRef), children: COPY.rotate }),
        /* @__PURE__ */ jsx15("button", { type: "button", onClick: () => {
          setRotatingRef(null);
          clearSensitiveInputs();
        }, children: COPY.cancel })
      ] }) : deleteRef === s.secretRef ? /* @__PURE__ */ jsxs13(Fragment2, { children: [
        /* @__PURE__ */ jsx15("span", { children: "\u092A\u0915\u094D\u0915\u093E \u0939\u091F\u093E\u0928\u093E \u0939\u0948?" }),
        /* @__PURE__ */ jsx15("button", { type: "button", disabled: busyRef === s.secretRef, onClick: () => void deleteSecret(s.secretRef), children: COPY.confirmDelete }),
        /* @__PURE__ */ jsx15("button", { type: "button", onClick: () => setDeleteRef(null), children: COPY.cancel })
      ] }) : /* @__PURE__ */ jsxs13(Fragment2, { children: [
        /* @__PURE__ */ jsx15("button", { type: "button", onClick: () => setRotatingRef(s.secretRef), children: COPY.rotate }),
        /* @__PURE__ */ jsx15("button", { type: "button", disabled: busyRef === s.secretRef, onClick: () => void testSecret(s.secretRef), children: COPY.test }),
        /* @__PURE__ */ jsx15("button", { type: "button", onClick: () => setDeleteRef(s.secretRef), children: COPY.delete })
      ] }) })
    ] }, s.secretRef)) }),
    adding ? /* @__PURE__ */ jsxs13("div", { className: "secret-box-form", children: [
      /* @__PURE__ */ jsx15(
        "input",
        {
          type: "text",
          placeholder: COPY.label,
          value: label,
          onChange: (e) => setLabel(e.target.value),
          maxLength: 120
        }
      ),
      /* @__PURE__ */ jsx15("select", { value: kind, onChange: (e) => setKind(e.target.value), "aria-label": COPY.kind, children: KIND_OPTIONS.map((k) => /* @__PURE__ */ jsx15("option", { value: k.id, children: k.label_hi }, k.id)) }),
      /* @__PURE__ */ jsx15(
        "input",
        {
          type: "text",
          placeholder: COPY.provider,
          value: provider,
          onChange: (e) => setProvider(e.target.value),
          maxLength: 80
        }
      ),
      /* @__PURE__ */ jsx15(
        "input",
        {
          ref: valueRef,
          type: "password",
          autoComplete: "off",
          "data-testid": "secret-value-input",
          placeholder: COPY.value,
          value,
          onChange: (e) => setValue(e.target.value)
        }
      ),
      /* @__PURE__ */ jsxs13("div", { children: [
        /* @__PURE__ */ jsx15("button", { type: "button", disabled: busyRef === "__create__" || !label.trim() || !value, onClick: () => void createSecret(), children: COPY.save }),
        /* @__PURE__ */ jsx15("button", { type: "button", onClick: () => {
          setAdding(false);
          clearSensitiveInputs();
        }, children: COPY.cancel })
      ] })
    ] }) : /* @__PURE__ */ jsx15("button", { type: "button", onClick: () => setAdding(true), children: COPY.add })
  ] });
}

// src/settings/SettingsPanel.tsx
import { jsx as jsx16, jsxs as jsxs14 } from "react/jsx-runtime";
function SettingsPanel({
  appAdapter,
  appSettings,
  providers = [],
  knowledgeSources = [],
  tools = [],
  integrations = [],
  agentSkillsService,
  activeTab: controlledActiveTab,
  onTabChange,
  readOnly = true,
  token,
  integrationsPanelSlot,
  appsPanelSlot,
  toolsPanelSlot,
  locale = "hi"
}) {
  const [localActiveTab, setLocalActiveTab] = useState11("providers");
  const activeTab = controlledActiveTab || localActiveTab;
  const handleTabChange = (tab) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setLocalActiveTab(tab);
    }
  };
  const resolvedProviders = providers.length > 0 ? providers : appSettings?.providers || appAdapter?.appSettings?.providers || [];
  const resolvedKnowledge = knowledgeSources.length > 0 ? knowledgeSources : appSettings?.knowledgeSources || appAdapter?.appSettings?.knowledgeSources || [];
  const resolvedTools = tools.length > 0 ? tools : appSettings?.integrations || appAdapter?.appSettings?.integrations || [];
  const resolvedIntegrations = integrations.length > 0 ? integrations : appSettings?.integrations || appAdapter?.appSettings?.integrations || [];
  return /* @__PURE__ */ jsxs14(
    "div",
    {
      className: "smart-shikka-settings-panel",
      style: {
        padding: "20px",
        backgroundColor: "#f8f9fa",
        borderRadius: "12px",
        border: "1px solid #dee2e6",
        boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
        maxWidth: "1200px",
        margin: "0 auto",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      },
      children: [
        /* @__PURE__ */ jsxs14("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }, children: [
          /* @__PURE__ */ jsxs14("div", { children: [
            /* @__PURE__ */ jsx16("h2", { style: { margin: "0 0 4px 0", color: "#212529", fontWeight: 700 }, children: "\u2699\uFE0F \u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938 (AI settings)" }),
            /* @__PURE__ */ jsx16("p", { style: { margin: 0, color: "#6c757d", fontSize: "14px" }, children: "\u090F\u0906\u0908 \u092E\u0949\u0921\u0932 \u092A\u094D\u0930\u0926\u093E\u0924\u093E, \u091C\u094D\u091E\u093E\u0928 \u0938\u094D\u0930\u094B\u0924, \u0914\u0930 \u0909\u092A\u0915\u0930\u0923\u094B\u0902 \u0915\u0940 \u0938\u0941\u0930\u0915\u094D\u0937\u093E \u0938\u0940\u092E\u093E\u0913\u0902 \u0915\u093E \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923 \u0936\u0947\u0932\u0964" })
          ] }),
          /* @__PURE__ */ jsx16(
            "span",
            {
              style: {
                fontSize: "12px",
                backgroundColor: "#e8f4fd",
                color: "#1971c2",
                padding: "6px 12px",
                borderRadius: "6px",
                fontWeight: 600
              },
              children: "\u{1F512} \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u092E\u0947\u091F\u093E\u0921\u0947\u091F\u093E \u092E\u094B\u0921 (Read-Only)"
            }
          )
        ] }),
        /* @__PURE__ */ jsx16(SettingsTabs, { activeTab, onTabChange: handleTabChange }),
        /* @__PURE__ */ jsxs14("div", { className: "settings-tab-content", style: { marginTop: "16px" }, children: [
          activeTab === "control" && /* @__PURE__ */ jsx16(CanonicalSettingsControlPlane, { token, readOnly }),
          activeTab === "providers" && /* @__PURE__ */ jsx16(ModelProviderSettings, { providers: resolvedProviders, readOnly }),
          activeTab === "knowledge" && /* @__PURE__ */ jsx16(KnowledgeSourceSettings, { knowledgeSources: resolvedKnowledge, readOnly }),
          activeTab === "tools" && (toolsPanelSlot ? toolsPanelSlot : token ? /* @__PURE__ */ jsx16(McpToolsPanel, { token, locale }) : /* @__PURE__ */ jsx16(ToolMarketplace, { tools: resolvedTools, readOnly })),
          activeTab === "integrations" && (integrationsPanelSlot ?? /* @__PURE__ */ jsx16(IntegrationSettings, { integrations: resolvedIntegrations, readOnly })),
          activeTab === "apps" && (appsPanelSlot ?? (appAdapter || appSettings ? /* @__PURE__ */ jsx16(AppRegistryPanel, { appAdapter, appSettings }) : /* @__PURE__ */ jsx16(
            SettingsEmptyState,
            {
              title_hi: "\u0915\u094B\u0908 \u0910\u092A \u0930\u091C\u093F\u0938\u094D\u091F\u094D\u0930\u0940 \u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0928\u0939\u0940\u0902 \u0939\u0948",
              message_hi: "\u0907\u0938 surface \u092A\u0930 \u0915\u094B\u0908 app adapter \u091C\u0941\u0921\u093C\u093E \u0928\u0939\u0940\u0902 \u0939\u0948 \u2014 Apps \u0928\u0947\u0935\u093F\u0917\u0947\u0936\u0928 \u0938\u0947 federation \u0915\u093E live status \u0926\u0947\u0916\u0947\u0902\u0964"
            }
          ))),
          activeTab === "skills" && /* @__PURE__ */ jsx16(SkillsSettings, { service: agentSkillsService, readOnly }),
          activeTab === "agents" && /* @__PURE__ */ jsx16(AgentPromptSettings, { token, readOnly }),
          activeTab === "safety" && /* @__PURE__ */ jsxs14(
            "div",
            {
              style: {
                backgroundColor: "#ffffff",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                padding: "20px",
                color: "#343a40"
              },
              children: [
                /* @__PURE__ */ jsx16("h3", { style: { margin: "0 0 16px 0", color: "#c92a2a", fontWeight: 700 }, children: "\u{1F6E1}\uFE0F \u0938\u0941\u0930\u0915\u094D\u0937\u093E \u0938\u0940\u092E\u093E \u0928\u093F\u092F\u092E (Safety Guard Rules)" }),
                /* @__PURE__ */ jsx16("p", { style: { fontSize: "14px", lineHeight: 1.6, color: "#495057", marginBottom: "20px" }, children: "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0916\u093E \u0915\u094B\u0930 \u0915\u093E \u0938\u0941\u0930\u0915\u094D\u0937\u093E \u0915\u0935\u091A \u0938\u0902\u0935\u0947\u0926\u0928\u0936\u0940\u0932 \u0915\u094D\u0930\u0947\u0921\u0947\u0902\u0936\u093F\u092F\u0932\u094D\u0938 \u0915\u0947 \u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930 \u0932\u0940\u0915 \u0914\u0930 \u0905\u0928\u0927\u093F\u0915\u0943\u0924 \u0915\u094D\u0930\u093F\u092F\u093E\u0913\u0902 \u0915\u094B \u0938\u094D\u0935\u0924\u0903 \u0930\u094B\u0915\u0924\u093E \u0939\u0948:" }),
                /* @__PURE__ */ jsxs14("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px" }, children: [
                  /* @__PURE__ */ jsxs14("div", { style: { border: "1px solid #ffe3e3", backgroundColor: "#fff5f5", padding: "14px", borderRadius: "6px" }, children: [
                    /* @__PURE__ */ jsx16("strong", { style: { color: "#c92a2a", display: "block", marginBottom: "6px" }, children: "\u{1F512} \u0938\u0902\u0935\u0947\u0926\u0928\u0936\u0940\u0932 \u0915\u094D\u0930\u0947\u0921\u0947\u0902\u0936\u093F\u092F\u0932 \u092B\u093C\u093F\u0932\u094D\u091F\u0930" }),
                    /* @__PURE__ */ jsxs14("span", { style: { fontSize: "13px", color: "#495057" }, children: [
                      "\u0935\u093F\u0928\u094D\u092F\u093E\u0938 \u092E\u0947\u0902 ",
                      /* @__PURE__ */ jsx16("code", { children: "apiKey" }),
                      ", ",
                      /* @__PURE__ */ jsx16("code", { children: "token" }),
                      ", ",
                      /* @__PURE__ */ jsx16("code", { children: "secret" }),
                      ", ",
                      /* @__PURE__ */ jsx16("code", { children: "password" }),
                      ", ",
                      /* @__PURE__ */ jsx16("code", { children: "privateKey" }),
                      " \u091C\u0948\u0938\u0940 \u091A\u093E\u092C\u093F\u092F\u094B\u0902 \u0915\u093E \u092A\u093E\u092F\u093E \u091C\u093E\u0928\u093E \u0938\u0916\u094D\u0924 \u092E\u0928\u093E \u0939\u0948\u0964"
                    ] })
                  ] }),
                  /* @__PURE__ */ jsxs14("div", { style: { border: "1px solid #ffe3e3", backgroundColor: "#fff5f5", padding: "14px", borderRadius: "6px" }, children: [
                    /* @__PURE__ */ jsx16("strong", { style: { color: "#c92a2a", display: "block", marginBottom: "6px" }, children: "\u{1F6AB} \u0905\u0935\u0930\u0941\u0926\u094D\u0927 \u0938\u0902\u0935\u0947\u0926\u0928\u0936\u0940\u0932 \u0915\u094D\u0930\u093F\u092F\u093E\u090F\u0902" }),
                    /* @__PURE__ */ jsx16("span", { style: { fontSize: "13px", color: "#495057" }, children: "\u0935\u093F\u0924\u094D\u0924\u0940\u092F \u0938\u0902\u0935\u093F\u0924\u0930\u0923 (salary transfers), \u0911\u091F\u094B-\u0938\u092C\u092E\u093F\u091F, \u0938\u0940\u0927\u0947 \u092A\u0947\u092E\u0947\u0902\u091F \u0917\u0947\u091F\u0935\u0947 \u0928\u093F\u0937\u094D\u092A\u093E\u0926\u0928, \u0938\u0941\u092A\u092C\u0947\u0938 \u0930\u094B\u0932 \u092F\u093E \u091A\u093E\u092C\u093F\u092F\u094B\u0902 \u0915\u0947 \u092E\u094D\u092F\u0942\u091F\u0947\u0936\u0928 \u0915\u094B \u0930\u0928\u091F\u093E\u0907\u092E \u092A\u0930 \u092A\u0942\u0930\u094D\u0923\u0924\u0903 \u092C\u094D\u0932\u0949\u0915 \u0915\u093F\u092F\u093E \u0917\u092F\u093E \u0939\u0948\u0964" })
                  ] }),
                  /* @__PURE__ */ jsxs14("div", { style: { border: "1px solid #e9ecef", backgroundColor: "#f8f9fa", padding: "14px", borderRadius: "6px" }, children: [
                    /* @__PURE__ */ jsx16("strong", { style: { color: "#495057", display: "block", marginBottom: "6px" }, children: "\u26A0\uFE0F \u092E\u0948\u0928\u094D\u092F\u0941\u0905\u0932 \u092A\u0941\u0937\u094D\u091F\u093F \u0915\u0940 \u0906\u0935\u0936\u094D\u092F\u0915\u0924\u093E" }),
                    /* @__PURE__ */ jsx16("span", { style: { fontSize: "13px", color: "#495057" }, children: "\u0909\u091A\u094D\u091A-\u091C\u094B\u0916\u093F\u092E \u0935\u093E\u0932\u0940 \u0915\u094D\u0930\u093F\u092F\u093E\u090F\u0902 (\u091C\u0948\u0938\u0947 \u0907\u0928\u0935\u0949\u0907\u0938 \u0921\u094D\u0930\u093E\u092B\u094D\u091F, \u092A\u0940\u0921\u0940\u090F\u092B \u090F\u0915\u094D\u0938\u092A\u094B\u0930\u094D\u091F, \u0914\u0930 \u0930\u093F\u092A\u094B\u0930\u094D\u091F \u091C\u0947\u0928\u0930\u0947\u0936\u0928) \u0928\u093F\u0937\u094D\u092A\u093E\u0926\u0928 \u0938\u0947 \u092A\u0939\u0932\u0947 \u0909\u092A\u092F\u094B\u0917\u0915\u0930\u094D\u0924\u093E \u0915\u0940 \u0938\u094D\u092A\u0937\u094D\u091F \u0938\u0939\u092E\u0924\u093F \u092E\u093E\u0902\u0917\u0924\u0940 \u0939\u0948\u0902\u0964" })
                  ] })
                ] }),
                token ? /* @__PURE__ */ jsx16("div", { style: { marginTop: "20px", border: "1px solid #dee2e6", borderRadius: "6px", padding: "14px" }, children: /* @__PURE__ */ jsx16(SecretBoxPanel, { token, locale }) }) : null
              ]
            }
          )
        ] })
      ]
    }
  );
}

// src/settings/CapabilityStateBadge.tsx
import { buildCapabilityStateView } from "@smart-shikka/core";
import { jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
var TONE_COLOR = {
  positive: "#16a34a",
  neutral: "#64748b",
  warning: "#ca8a04",
  blocked: "#dc2626"
};
function CapabilityStateBadge(props) {
  const view = buildCapabilityStateView(props.status);
  const hi = props.locale === "hi";
  const label = hi ? view.labelHi : view.labelEn;
  const explanation = hi ? view.explanationHi : view.explanationEn;
  const nextAction = hi ? view.nextActionHi : view.nextActionEn;
  return /* @__PURE__ */ jsxs15(
    "span",
    {
      className: `capability-state-badge tone-${view.tone}`,
      role: "status",
      "aria-label": `${label}. ${explanation} ${nextAction}`,
      title: explanation,
      style: { display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 },
      children: [
        /* @__PURE__ */ jsxs15("span", { style: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11 }, children: [
          /* @__PURE__ */ jsx17("span", { "aria-hidden": "true", style: { color: TONE_COLOR[view.tone] }, children: view.marker }),
          /* @__PURE__ */ jsx17("strong", { children: label })
        ] }),
        props.verbose ? /* @__PURE__ */ jsxs15("small", { style: { color: "#64748b", fontSize: 10, textAlign: "right" }, children: [
          explanation,
          " ",
          nextAction
        ] }) : null
      ]
    }
  );
}

// src/settings/GlobalRulesPanel.tsx
import { useCallback as useCallback6, useEffect as useEffect8, useState as useState12 } from "react";
import { jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
var STR2 = {
  title: { en: "Global Rules", hi: "\u0917\u094D\u0932\u094B\u092C\u0932 \u0928\u093F\u092F\u092E" },
  sub: {
    en: "Owner-scoped rules included in every project chat context.",
    hi: "\u0939\u0930 \u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F chat context \u092E\u0947\u0902 \u0936\u093E\u092E\u093F\u0932 \u0930\u0939\u0928\u0947 \u0935\u093E\u0932\u0947 owner-level \u0928\u093F\u092F\u092E\u0964"
  },
  loading: { en: "Loading rules\u2026", hi: "\u0928\u093F\u092F\u092E \u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u0947 \u0939\u0948\u0902\u2026" },
  empty: { en: "No global rules yet.", hi: "\u0905\u092D\u0940 \u0915\u094B\u0908 \u0917\u094D\u0932\u094B\u092C\u0932 \u0928\u093F\u092F\u092E \u0928\u0939\u0940\u0902\u0964" },
  placeholder: {
    en: "Write a rule \u2014 it stays in Shikka context for every project",
    hi: "\u0928\u093F\u092F\u092E \u0932\u093F\u0916\u0947\u0902 \u2014 \u092F\u0939 \u0939\u0930 \u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u092E\u0947\u0902 Shikka \u0915\u0947 context \u092E\u0947\u0902 \u0930\u0939\u0947\u0917\u093E"
  },
  add: { en: "Add rule", hi: "\u0928\u093F\u092F\u092E \u091C\u094B\u0921\u093C\u0947\u0902" },
  del: { en: "Delete", hi: "\u0939\u091F\u093E\u090F\u0901" },
  pinned: { en: "pinned", hi: "\u092A\u093F\u0928 \u0915\u093F\u092F\u093E \u0917\u092F\u093E" },
  denied: { en: "Adding rules requires a privileged role.", hi: "\u0928\u093F\u092F\u092E \u091C\u094B\u0921\u093C\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F privileged role \u091A\u093E\u0939\u093F\u090F\u0964" }
};
function tr2(key, locale) {
  const entry = STR2[key];
  return typeof entry === "string" ? entry : entry[locale];
}
function GlobalRulesPanel({
  token,
  locale = "en",
  baseUrl = "",
  fetchImpl,
  readOnly = true
}) {
  const doFetch = fetchImpl ?? fetch;
  const [rules, setRules] = useState12([]);
  const [loading, setLoading] = useState12(true);
  const [error, setError] = useState12(null);
  const [draft, setDraft] = useState12("");
  const [busy, setBusy] = useState12(false);
  const call = useCallback6(
    async (method, path, body) => {
      const res = await doFetch(`${baseUrl}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${token ?? ""}`,
          ...body !== void 0 ? { "Content-Type": "application/json" } : {}
        },
        body: body === void 0 ? null : JSON.stringify(body)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(typeof json.error === "string" ? json.error : `http_error_${res.status}`);
        err.name = "api_error";
        throw err;
      }
      return json;
    },
    [doFetch, baseUrl, token]
  );
  const load = useCallback6(async () => {
    if (!token) {
      setError("auth_required");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await call("GET", "/api/rules");
      setRules(Array.isArray(res.rules) ? res.rules : []);
      setError(null);
    } catch (e) {
      setError(errorToCode(e));
    } finally {
      setLoading(false);
    }
  }, [call, token]);
  useEffect8(() => {
    void load();
  }, [load]);
  const add = async () => {
    const content = draft.trim();
    if (!content || busy) return;
    setBusy(true);
    try {
      await call("POST", "/api/rules", { content });
      setDraft("");
      await load();
    } catch (e) {
      setError(errorToCode(e));
    } finally {
      setBusy(false);
    }
  };
  const del = async (memoryId) => {
    if (busy) return;
    setBusy(true);
    try {
      await call("POST", `/api/rules/${encodeURIComponent(memoryId)}/delete`);
      await load();
    } catch (e) {
      setError(errorToCode(e));
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs16("div", { style: { display: "grid", gap: 12 }, children: [
    /* @__PURE__ */ jsxs16("div", { children: [
      /* @__PURE__ */ jsx18("h4", { style: { margin: "0 0 4px", fontSize: 15 }, children: tr2("title", locale) }),
      /* @__PURE__ */ jsx18("p", { style: { margin: 0, fontSize: 12, color: "#6c757d" }, children: tr2("sub", locale) })
    ] }),
    loading ? /* @__PURE__ */ jsx18("p", { style: { fontSize: 13 }, children: tr2("loading", locale) }) : null,
    error ? /* @__PURE__ */ jsx18("p", { role: "alert", style: { fontSize: 12, color: "#c92a2a" }, children: safeErrorCode(error) }) : null,
    !loading && rules.length === 0 ? /* @__PURE__ */ jsx18("p", { style: { fontSize: 13, color: "#6c757d" }, children: tr2("empty", locale) }) : null,
    /* @__PURE__ */ jsx18("ul", { style: { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }, children: rules.map((r) => /* @__PURE__ */ jsxs16(
      "li",
      {
        style: {
          border: "1px solid #dee2e6",
          borderRadius: 8,
          padding: "10px 12px",
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start"
        },
        children: [
          /* @__PURE__ */ jsxs16("div", { style: { fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }, children: [
            r.content,
            r.pinned ? /* @__PURE__ */ jsxs16("span", { style: { marginLeft: 8, fontSize: 11, color: "#6c757d" }, children: [
              "(",
              tr2("pinned", locale),
              ")"
            ] }) : null
          ] }),
          !readOnly ? /* @__PURE__ */ jsx18("button", { type: "button", disabled: busy, onClick: () => void del(r.memoryId), style: { fontSize: 12 }, children: tr2("del", locale) }) : null
        ]
      },
      r.memoryId
    )) }),
    !readOnly ? /* @__PURE__ */ jsxs16("div", { style: { display: "grid", gap: 8 }, children: [
      /* @__PURE__ */ jsx18(
        "textarea",
        {
          value: draft,
          onChange: (e) => setDraft(e.target.value),
          placeholder: tr2("placeholder", locale),
          rows: 2,
          style: { width: "100%", font: "inherit", fontSize: 13, padding: 8, borderRadius: 8, border: "1px solid #dee2e6" }
        }
      ),
      /* @__PURE__ */ jsx18("button", { type: "button", disabled: busy || !draft.trim(), onClick: () => void add(), style: { justifySelf: "start" }, children: tr2("add", locale) })
    ] }) : /* @__PURE__ */ jsx18("p", { style: { fontSize: 11, color: "#6c757d", margin: 0 }, children: tr2("denied", locale) })
  ] });
}

// src/settings/ByokProvidersPanel.tsx
import { useCallback as useCallback7, useEffect as useEffect9, useState as useState13 } from "react";
import { jsx as jsx19, jsxs as jsxs17 } from "react/jsx-runtime";
var PROVIDER_OPTIONS = [
  { id: "openai_compatible", label: "OpenAI-compatible (custom base URL)" },
  { id: "openai", label: "OpenAI" },
  { id: "deepseek", label: "DeepSeek" },
  { id: "anthropic", label: "Anthropic" },
  { id: "gemini", label: "Gemini" },
  { id: "local_model", label: "Local model" },
  { id: "kimi", label: "Kimi" },
  { id: "sakana", label: "Sakana" },
  { id: "gateway", label: "Gateway" }
];
var STR3 = {
  title: { en: "Your LLM providers (BYOK)", hi: "\u0906\u092A\u0915\u0947 LLM \u092A\u094D\u0930\u0926\u093E\u0924\u093E (BYOK)" },
  off: {
    en: "User BYOK is not enabled by the super-admin for your account.",
    hi: "\u0906\u092A\u0915\u0947 \u0916\u093E\u0924\u0947 \u0915\u0947 \u0932\u093F\u090F super-admin \u0926\u094D\u0935\u093E\u0930\u093E User BYOK enabled \u0928\u0939\u0940\u0902 \u0939\u0948\u0964"
  },
  offDetail: {
    en: "Platform provider replies keep working; bringing your own key requires the super-admin toggle.",
    hi: "\u092A\u094D\u0932\u0947\u091F\u092B\u093C\u0949\u0930\u094D\u092E \u092A\u094D\u0930\u0926\u093E\u0924\u093E \u0909\u0924\u094D\u0924\u0930 \u091A\u0932\u0924\u0947 \u0930\u0939\u0947\u0902\u0917\u0947; \u0905\u092A\u0928\u0940 key \u0932\u093E\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F super-admin toggle \u091A\u093E\u0939\u093F\u090F\u0964"
  },
  provider: { en: "Provider", hi: "\u092A\u094D\u0930\u0926\u093E\u0924\u093E" },
  labelName: { en: "Provider name (optional)", hi: "\u092A\u094D\u0930\u0926\u093E\u0924\u093E \u0928\u093E\u092E (\u0935\u0948\u0915\u0932\u094D\u092A\u093F\u0915)" },
  baseUrl: { en: "Base URL (optional)", hi: "Base URL (\u0935\u0948\u0915\u0932\u094D\u092A\u093F\u0915)" },
  model: { en: "Model (optional)", hi: "\u092E\u0949\u0921\u0932 (\u0935\u0948\u0915\u0932\u094D\u092A\u093F\u0915)" },
  apiKey: { en: "API key", hi: "API key" },
  headers: { en: "Extra headers JSON (optional, max 8)", hi: "\u0905\u0924\u093F\u0930\u093F\u0915\u094D\u0924 headers JSON (\u0935\u0948\u0915\u0932\u094D\u092A\u093F\u0915, \u0905\u0927\u093F\u0915\u0924\u092E 8)" },
  save: { en: "Save key", hi: "Key \u0938\u0939\u0947\u091C\u0947\u0902" },
  test: { en: "Test connection", hi: "Connection \u091C\u093E\u0901\u091A\u0947\u0902" },
  del: { en: "Delete", hi: "\u0939\u091F\u093E\u090F\u0901" },
  saving: { en: "Saving\u2026", hi: "\u0938\u0939\u0947\u091C\u093E \u091C\u093E \u0930\u0939\u093E \u0939\u0948\u2026" },
  testing: { en: "Testing\u2026", hi: "\u091C\u093E\u0901\u091A \u0939\u094B \u0930\u0939\u0940 \u0939\u0948\u2026" },
  enabledBadge: { en: "enabled", hi: "\u0938\u0915\u094D\u0930\u093F\u092F" },
  disabledBadge: { en: "not enabled \u2014 run Test connection", hi: "\u0938\u0915\u094D\u0930\u093F\u092F \u0928\u0939\u0940\u0902 \u2014 Test connection \u091A\u0932\u093E\u090F\u0901" },
  empty: { en: "No provider keys saved yet.", hi: "\u0905\u092D\u0940 \u0915\u094B\u0908 provider key \u0938\u0939\u0947\u091C\u0940 \u0928\u0939\u0940\u0902 \u0917\u0908\u0964" },
  testBeforeEnable: {
    en: "A key becomes active only after a successful Test connection.",
    hi: "Key \u0938\u092B\u0932 Test connection \u0915\u0947 \u092C\u093E\u0926 \u0939\u0940 \u0938\u0915\u094D\u0930\u093F\u092F \u0939\u094B\u0924\u0940 \u0939\u0948\u0964"
  },
  maskedNote: {
    en: "Keys are encrypted server-side and never returned \u2014 only masked status is shown.",
    hi: "Keys server \u092A\u0930 encrypted \u0930\u0939\u0924\u0940 \u0939\u0948\u0902 \u0914\u0930 \u0915\u092D\u0940 \u0935\u093E\u092A\u0938 \u0928\u0939\u0940\u0902 \u0906\u0924\u0940\u0902 \u2014 \u0915\u0947\u0935\u0932 masked status \u0926\u093F\u0916\u0924\u093E \u0939\u0948\u0964"
  }
};
function tr3(key, locale) {
  return STR3[key][locale];
}
function ByokProvidersPanel({
  token,
  locale = "en",
  baseUrl = "",
  fetchImpl
}) {
  const doFetch = fetchImpl ?? fetch;
  const [policy, setPolicy] = useState13();
  const [privileged, setPrivileged] = useState13(false);
  const [keys, setKeys] = useState13([]);
  const [loading, setLoading] = useState13(true);
  const [error, setError] = useState13(null);
  const [busy, setBusy] = useState13(null);
  const [notice, setNotice] = useState13(null);
  const [providerId, setProviderId] = useState13("openai_compatible");
  const [label, setLabel] = useState13("");
  const [baseUrlField, setBaseUrlField] = useState13("");
  const [model, setModel] = useState13("");
  const [apiKey, setApiKey] = useState13("");
  const [headersJson, setHeadersJson] = useState13("");
  const call = useCallback7(
    async (method, path, body) => {
      const res = await doFetch(`${baseUrl}${path}`, {
        method,
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token ?? ""}`,
          ...body !== void 0 ? { "Content-Type": "application/json" } : {}
        },
        body: body === void 0 ? null : JSON.stringify(body)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(typeof json.error === "string" ? json.error : `http_error_${res.status}`);
        err.name = typeof json.errorCode === "string" ? json.errorCode : "api_error";
        throw err;
      }
      return json;
    },
    [doFetch, baseUrl, token]
  );
  const load = useCallback7(async () => {
    setLoading(true);
    setError(null);
    try {
      const policyJson = await call("GET", "/api/chat/provider-keys/policy");
      const byok = policyJson.byok ?? {};
      setPolicy({ enabled: byok.enabled === true, visible: byok.visible === true });
      setPrivileged(policyJson.privileged === true);
      const listJson = await call("GET", "/api/chat/provider-keys");
      setKeys(listJson.keys ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "load_failed");
    } finally {
      setLoading(false);
    }
  }, [call]);
  useEffect9(() => {
    void load();
  }, [load]);
  const onSave = async () => {
    setBusy("save");
    setError(null);
    setNotice(null);
    try {
      let extraHeaders;
      if (headersJson.trim()) {
        const parsed = JSON.parse(headersJson);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          throw new Error("headers_json_invalid");
        }
        extraHeaders = parsed;
      }
      await call("POST", "/api/chat/provider-keys", {
        providerId,
        key: apiKey,
        ...label.trim() ? { label: label.trim() } : {},
        ...baseUrlField.trim() ? { baseUrl: baseUrlField.trim() } : {},
        ...model.trim() ? { model: model.trim() } : {},
        ...extraHeaders ? { extraHeaders } : {}
      });
      setApiKey("");
      setHeadersJson("");
      setNotice(locale === "hi" ? "Key \u0938\u0939\u0947\u091C\u0940 \u0917\u0908 \u2014 \u0938\u0915\u094D\u0930\u093F\u092F \u0915\u0930\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F Test connection \u091A\u0932\u093E\u090F\u0901\u0964" : "Key saved \u2014 run Test connection to enable it.");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "save_failed");
    } finally {
      setBusy(null);
    }
  };
  const onTest = async (pid) => {
    setBusy("test");
    setError(null);
    setNotice(null);
    try {
      const json = await call("POST", "/api/chat/provider-keys/test", { providerId: pid });
      const ok = json.status === "available" || json.status === "healthy";
      setNotice(
        ok ? locale === "hi" ? "Connection \u0938\u092B\u0932 \u2014 key \u0938\u0915\u094D\u0930\u093F\u092F \u0939\u0948\u0964" : "Connection succeeded \u2014 the key is enabled." : locale === "hi" ? `Connection \u0905\u0938\u092B\u0932 (${String(json.status ?? "unknown")}) \u2014 key \u0938\u0915\u094D\u0930\u093F\u092F \u0928\u0939\u0940\u0902 \u0939\u0941\u0908\u0964` : `Connection failed (${String(json.status ?? "unknown")}) \u2014 the key stays disabled.`
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "test_failed");
    } finally {
      setBusy(null);
    }
  };
  const onDelete = async (keyRefId) => {
    setBusy("save");
    setError(null);
    try {
      await call("POST", `/api/chat/provider-keys/${encodeURIComponent(keyRefId)}/delete`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "delete_failed");
    } finally {
      setBusy(null);
    }
  };
  if (loading) {
    return /* @__PURE__ */ jsx19("div", { style: { padding: 12, color: "#6c757d" }, children: "\u2026" });
  }
  const surfaceVisible = policy?.visible === true || policy?.enabled === true || privileged;
  return /* @__PURE__ */ jsxs17("section", { "aria-label": tr3("title", locale), style: { display: "grid", gap: 12 }, children: [
    /* @__PURE__ */ jsx19("h3", { style: { margin: 0, fontSize: 16, fontWeight: 600 }, children: tr3("title", locale) }),
    /* @__PURE__ */ jsx19("p", { style: { margin: 0, fontSize: 12, color: "#6c757d" }, children: tr3("maskedNote", locale) }),
    policy !== void 0 && !policy.enabled && /* @__PURE__ */ jsxs17("div", { style: { border: "1px solid #dee2e6", borderRadius: 8, padding: 12, background: "#f8f9fa" }, children: [
      /* @__PURE__ */ jsx19("strong", { children: tr3("off", locale) }),
      /* @__PURE__ */ jsx19("div", { style: { fontSize: 12, color: "#6c757d", marginTop: 4 }, children: tr3("offDetail", locale) })
    ] }),
    surfaceVisible ? /* @__PURE__ */ jsxs17("div", { style: { border: "1px solid #dee2e6", borderRadius: 8, padding: 12, display: "grid", gap: 8 }, children: [
      /* @__PURE__ */ jsxs17("label", { style: { display: "grid", gap: 4, fontSize: 13 }, children: [
        tr3("provider", locale),
        /* @__PURE__ */ jsx19("select", { value: providerId, onChange: (e) => setProviderId(e.target.value), children: PROVIDER_OPTIONS.map((o) => /* @__PURE__ */ jsx19("option", { value: o.id, children: o.label }, o.id)) })
      ] }),
      /* @__PURE__ */ jsxs17("label", { style: { display: "grid", gap: 4, fontSize: 13 }, children: [
        tr3("labelName", locale),
        /* @__PURE__ */ jsx19("input", { value: label, onChange: (e) => setLabel(e.target.value), maxLength: 120 })
      ] }),
      /* @__PURE__ */ jsxs17("label", { style: { display: "grid", gap: 4, fontSize: 13 }, children: [
        tr3("baseUrl", locale),
        /* @__PURE__ */ jsx19("input", { value: baseUrlField, onChange: (e) => setBaseUrlField(e.target.value), placeholder: "https://llm.example.com/v1" })
      ] }),
      /* @__PURE__ */ jsxs17("label", { style: { display: "grid", gap: 4, fontSize: 13 }, children: [
        tr3("model", locale),
        /* @__PURE__ */ jsx19("input", { value: model, onChange: (e) => setModel(e.target.value), placeholder: "gpt-4o / llama-3-70b \u2026" })
      ] }),
      /* @__PURE__ */ jsxs17("label", { style: { display: "grid", gap: 4, fontSize: 13 }, children: [
        tr3("apiKey", locale),
        /* @__PURE__ */ jsx19("input", { value: apiKey, onChange: (e) => setApiKey(e.target.value), type: "password", autoComplete: "off" })
      ] }),
      /* @__PURE__ */ jsxs17("label", { style: { display: "grid", gap: 4, fontSize: 13 }, children: [
        tr3("headers", locale),
        /* @__PURE__ */ jsx19("input", { value: headersJson, onChange: (e) => setHeadersJson(e.target.value), placeholder: '{"X-Tenant":"t1"}' })
      ] }),
      /* @__PURE__ */ jsxs17("div", { style: { display: "flex", gap: 8, alignItems: "center" }, children: [
        /* @__PURE__ */ jsx19("button", { type: "button", disabled: busy !== null || !apiKey.trim(), onClick: () => void onSave(), children: busy === "save" ? tr3("saving", locale) : tr3("save", locale) }),
        /* @__PURE__ */ jsx19("span", { style: { fontSize: 12, color: "#6c757d" }, children: tr3("testBeforeEnable", locale) })
      ] })
    ] }) : /* @__PURE__ */ jsx19(
      SettingsEmptyState,
      {
        title_hi: tr3("off", "hi"),
        message_hi: tr3("offDetail", "hi")
      }
    ),
    keys.length === 0 ? /* @__PURE__ */ jsx19("p", { style: { margin: 0, fontSize: 13, color: "#6c757d" }, children: tr3("empty", locale) }) : /* @__PURE__ */ jsx19("div", { style: { display: "grid", gap: 8 }, children: keys.map((k) => /* @__PURE__ */ jsxs17("div", { style: { border: "1px solid #dee2e6", borderRadius: 8, padding: 12, display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }, children: [
      /* @__PURE__ */ jsxs17("div", { children: [
        /* @__PURE__ */ jsx19("div", { style: { fontWeight: 600 }, children: k.label ?? k.providerId }),
        /* @__PURE__ */ jsxs17("div", { style: { fontSize: 12, color: "#6c757d" }, children: [
          k.providerId,
          k.modelId ? ` \xB7 ${k.modelId}` : "",
          k.baseUrl ? ` \xB7 ${k.baseUrl}` : "",
          k.extraHeaderCount ? ` \xB7 +${k.extraHeaderCount} headers` : ""
        ] }),
        /* @__PURE__ */ jsxs17("div", { style: { fontSize: 12, color: "#6c757d" }, children: [
          k.maskedKey,
          k.lastTestStatus ? ` \xB7 test: ${k.lastTestStatus}` : ""
        ] })
      ] }),
      /* @__PURE__ */ jsxs17("div", { style: { display: "flex", gap: 8, alignItems: "center" }, children: [
        /* @__PURE__ */ jsx19(SettingsStatusBadge, { status: k.enabled ? "available" : "disabled" }),
        /* @__PURE__ */ jsx19("button", { type: "button", disabled: busy !== null, onClick: () => void onTest(k.providerId), children: busy === "test" ? tr3("testing", locale) : tr3("test", locale) }),
        /* @__PURE__ */ jsx19("button", { type: "button", disabled: busy !== null, onClick: () => void onDelete(k.keyRefId), children: tr3("del", locale) })
      ] })
    ] }, k.keyRefId)) }),
    notice ? /* @__PURE__ */ jsx19("p", { role: "status", style: { margin: 0, fontSize: 13, color: "#1b7f3b" }, children: notice }) : null,
    error ? /* @__PURE__ */ jsx19("p", { role: "alert", style: { margin: 0, fontSize: 13, color: "#b42318" }, children: error }) : null
  ] });
}

// src/settings/ChatHistoryPanel.tsx
import { useCallback as useCallback8, useEffect as useEffect10, useState as useState14 } from "react";
import { jsx as jsx20, jsxs as jsxs18 } from "react/jsx-runtime";
var PAGE_SIZE = 50;
var STR4 = {
  title: { en: "Chat history", hi: "\u091A\u0948\u091F \u0907\u0924\u093F\u0939\u093E\u0938" },
  search: { en: "Search chats\u2026", hi: "\u091A\u0948\u091F \u0916\u094B\u091C\u0947\u0902\u2026" },
  all: { en: "Active", hi: "\u0938\u0915\u094D\u0930\u093F\u092F" },
  archivedView: { en: "Archived", hi: "\u0938\u0902\u0917\u094D\u0930\u0939\u093F\u0924" },
  open: { en: "Open", hi: "\u0916\u094B\u0932\u0947\u0902" },
  rename: { en: "Rename", hi: "\u0928\u093E\u092E \u092C\u0926\u0932\u0947\u0902" },
  archive: { en: "Archive", hi: "\u0938\u0902\u0917\u094D\u0930\u0939\u093F\u0924 \u0915\u0930\u0947\u0902" },
  unarchive: { en: "Unarchive", hi: "\u092C\u093E\u0939\u0930 \u0928\u093F\u0915\u093E\u0932\u0947\u0902" },
  del: { en: "Delete", hi: "\u0939\u091F\u093E\u090F\u0901" },
  deleteConfirm: {
    en: "Delete this chat permanently? This cannot be undone.",
    hi: "\u092F\u0939 \u091A\u0948\u091F \u0938\u094D\u0925\u093E\u092F\u0940 \u0930\u0942\u092A \u0938\u0947 \u0939\u091F\u093E\u090F\u0901? \u0907\u0938\u0947 \u0935\u093E\u092A\u0938 \u0928\u0939\u0940\u0902 \u0932\u093E\u092F\u093E \u091C\u093E \u0938\u0915\u0924\u093E\u0964"
  },
  renamePrompt: { en: "New chat name:", hi: "\u0928\u092F\u093E \u091A\u0948\u091F \u0928\u093E\u092E:" },
  loadMore: { en: "Load more", hi: "\u0914\u0930 \u0926\u093F\u0916\u093E\u090F\u0901" },
  empty: { en: "No chats yet.", hi: "\u0905\u092D\u0940 \u0915\u094B\u0908 \u091A\u0948\u091F \u0928\u0939\u0940\u0902\u0964" },
  emptyArchived: { en: "No archived chats.", hi: "\u0915\u094B\u0908 \u0938\u0902\u0917\u094D\u0930\u0939\u093F\u0924 \u091A\u0948\u091F \u0928\u0939\u0940\u0902\u0964" },
  emptySearch: { en: "No chats match the search.", hi: "\u0916\u094B\u091C \u0938\u0947 \u0915\u094B\u0908 \u091A\u0948\u091F \u0928\u0939\u0940\u0902 \u092E\u093F\u0932\u0940\u0964" },
  project: { en: "project", hi: "\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F" }
};
function tr4(key, locale) {
  return STR4[key][locale];
}
function ChatHistoryPanel({
  token,
  locale = "en",
  baseUrl = "",
  fetchImpl,
  onOpenConversation
}) {
  const doFetch = fetchImpl ?? fetch;
  const [rows, setRows] = useState14([]);
  const [loading, setLoading] = useState14(true);
  const [error, setError] = useState14(null);
  const [view, setView] = useState14("active");
  const [query, setQuery] = useState14("");
  const [offset, setOffset] = useState14(0);
  const [hasMore, setHasMore] = useState14(false);
  const [busyId, setBusyId] = useState14(null);
  const call = useCallback8(
    async (method, path, body) => {
      const res = await doFetch(`${baseUrl}${path}`, {
        method,
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token ?? ""}`,
          ...body !== void 0 ? { "Content-Type": "application/json" } : {}
        },
        body: body === void 0 ? null : JSON.stringify(body)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(typeof json.error === "string" ? json.error : `http_error_${res.status}`);
        err.name = typeof json.errorCode === "string" ? json.errorCode : "api_error";
        throw err;
      }
      return json;
    },
    [doFetch, baseUrl, token]
  );
  const mapRow = (raw) => {
    const r = raw;
    const id = r.conversationId ?? r.id;
    if (typeof id !== "string" || !id) return null;
    return {
      conversationId: id,
      title: typeof r.title === "string" && r.title ? r.title : locale === "hi" ? "\u092C\u093F\u0928\u093E \u0928\u093E\u092E \u0915\u0940 \u091A\u0948\u091F" : "Untitled chat",
      status: typeof r.status === "string" ? r.status : "active",
      ...typeof r.updatedAt === "number" ? { updatedAt: r.updatedAt } : {},
      ...typeof r.archivedAt === "number" ? { archivedAt: r.archivedAt } : {},
      ...typeof r.projectId === "string" && r.projectId ? { projectId: r.projectId } : {}
    };
  };
  const load = useCallback8(
    async (resetOffset, append) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set("status", view);
        params.set("limit", String(PAGE_SIZE));
        if (resetOffset > 0) params.set("offset", String(resetOffset));
        if (query.trim()) params.set("q", query.trim());
        const json = await call("GET", `/api/chat/conversations?${params.toString()}`);
        const next = (json.conversations ?? []).map(mapRow).filter((r) => r !== null);
        setRows((prev) => append ? [...prev, ...next.filter((n) => !prev.some((p) => p.conversationId === n.conversationId))] : next);
        setHasMore(next.length === PAGE_SIZE);
        setOffset(resetOffset + next.length);
      } catch (e) {
        setError(e instanceof Error ? e.message : "load_failed");
      } finally {
        setLoading(false);
      }
    },
    [call, view, query]
  );
  useEffect10(() => {
    void load(0, false);
  }, [load]);
  const patch = async (row2, patchBody) => {
    setBusyId(row2.conversationId);
    setError(null);
    try {
      await call("POST", `/api/chat/conversations/${encodeURIComponent(row2.conversationId)}/update`, patchBody);
      if (patchBody.status === "archived" || patchBody.status === "active") {
        setRows((prev) => prev.filter((r) => r.conversationId !== row2.conversationId));
      } else {
        setRows((prev) => prev.map((r) => r.conversationId === row2.conversationId ? { ...r, ...patchBody } : r));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "update_failed");
    } finally {
      setBusyId(null);
    }
  };
  const onRename = async (row2) => {
    const next = window.prompt(tr4("renamePrompt", locale), row2.title);
    if (next === null || next.trim() === "" || next.trim() === row2.title) return;
    await patch(row2, { title: next.trim().slice(0, 200) });
  };
  const onDelete = async (row2) => {
    if (!window.confirm(tr4("deleteConfirm", locale))) return;
    setBusyId(row2.conversationId);
    setError(null);
    try {
      await call("POST", `/api/chat/conversations/${encodeURIComponent(row2.conversationId)}/delete`);
      setRows((prev) => prev.filter((r) => r.conversationId !== row2.conversationId));
    } catch (e) {
      setError(e instanceof Error ? e.message : "delete_failed");
    } finally {
      setBusyId(null);
    }
  };
  return /* @__PURE__ */ jsxs18("section", { "aria-label": tr4("title", locale), style: { display: "grid", gap: 12 }, children: [
    /* @__PURE__ */ jsx20("h3", { style: { margin: 0, fontSize: 16, fontWeight: 600 }, children: tr4("title", locale) }),
    /* @__PURE__ */ jsxs18("div", { style: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }, children: [
      /* @__PURE__ */ jsx20(
        "input",
        {
          type: "search",
          "aria-label": tr4("search", locale),
          placeholder: tr4("search", locale),
          value: query,
          onChange: (e) => setQuery(e.target.value),
          style: { flex: 1, minWidth: 160, padding: "6px 10px", borderRadius: 6, border: "1px solid #dee2e6" }
        }
      ),
      /* @__PURE__ */ jsx20("div", { role: "tablist", "aria-label": "view", style: { display: "flex", gap: 4 }, children: ["active", "archived"].map((v) => /* @__PURE__ */ jsx20(
        "button",
        {
          type: "button",
          role: "tab",
          "aria-selected": view === v,
          onClick: () => {
            setView(v);
            setOffset(0);
          },
          style: {
            padding: "5px 10px",
            borderRadius: 6,
            border: "1px solid #dee2e6",
            background: view === v ? "#e7f0ff" : "transparent",
            fontSize: 12,
            cursor: "pointer"
          },
          children: v === "active" ? tr4("all", locale) : tr4("archivedView", locale)
        },
        v
      )) })
    ] }),
    loading && rows.length === 0 ? /* @__PURE__ */ jsx20("p", { style: { margin: 0, fontSize: 13, color: "#6c757d" }, children: "\u2026" }) : rows.length === 0 ? /* @__PURE__ */ jsx20(
      SettingsEmptyState,
      {
        title_hi: view === "archived" ? STR4.emptyArchived.hi : query.trim() ? STR4.emptySearch.hi : STR4.empty.hi,
        message_hi: ""
      }
    ) : /* @__PURE__ */ jsxs18("div", { style: { display: "grid", gap: 6 }, children: [
      rows.map((row2) => /* @__PURE__ */ jsxs18(
        "div",
        {
          style: {
            display: "flex",
            alignItems: "center",
            gap: 8,
            border: "1px solid #dee2e6",
            borderRadius: 8,
            padding: "8px 10px",
            flexWrap: "wrap"
          },
          children: [
            /* @__PURE__ */ jsxs18("div", { style: { flex: 1, minWidth: 140 }, children: [
              /* @__PURE__ */ jsx20("div", { style: { fontSize: 13, fontWeight: 500, wordBreak: "break-word" }, children: row2.title }),
              /* @__PURE__ */ jsxs18("div", { style: { fontSize: 11, color: "#6c757d" }, children: [
                row2.updatedAt ? new Date(row2.updatedAt).toLocaleString(locale === "hi" ? "hi-IN" : "en-IN", { dateStyle: "short", timeStyle: "short" }) : "",
                row2.projectId ? ` \xB7 ${tr4("project", locale)}: ${row2.projectId}` : ""
              ] })
            ] }),
            onOpenConversation ? /* @__PURE__ */ jsx20("button", { type: "button", disabled: busyId === row2.conversationId, onClick: () => onOpenConversation(row2.conversationId), style: { fontSize: 12 }, children: tr4("open", locale) }) : null,
            /* @__PURE__ */ jsx20("button", { type: "button", disabled: busyId === row2.conversationId, onClick: () => void onRename(row2), style: { fontSize: 12 }, children: tr4("rename", locale) }),
            view === "active" ? /* @__PURE__ */ jsx20("button", { type: "button", disabled: busyId === row2.conversationId, onClick: () => void patch(row2, { status: "archived" }), style: { fontSize: 12 }, children: tr4("archive", locale) }) : /* @__PURE__ */ jsx20("button", { type: "button", disabled: busyId === row2.conversationId, onClick: () => void patch(row2, { status: "active" }), style: { fontSize: 12 }, children: tr4("unarchive", locale) }),
            /* @__PURE__ */ jsx20("button", { type: "button", disabled: busyId === row2.conversationId, onClick: () => void onDelete(row2), style: { fontSize: 12, color: "#b42318" }, children: tr4("del", locale) })
          ]
        },
        row2.conversationId
      )),
      hasMore ? /* @__PURE__ */ jsx20("button", { type: "button", disabled: loading, onClick: () => void load(offset, true), style: { justifySelf: "center", fontSize: 13 }, children: tr4("loadMore", locale) }) : null
    ] }),
    error ? /* @__PURE__ */ jsx20("p", { role: "alert", style: { margin: 0, fontSize: 13, color: "#b42318" }, children: error }) : null
  ] });
}

// src/settings/KnowledgeLibraryPanel.tsx
import { useCallback as useCallback9, useEffect as useEffect11, useState as useState15 } from "react";
import { jsx as jsx21, jsxs as jsxs19 } from "react/jsx-runtime";
var STR5 = {
  title: { en: "Knowledge", hi: "\u0928\u0949\u0932\u0947\u091C" },
  sub: {
    en: "Your project Knowledge Library \u2014 entries as the durable store reports them.",
    hi: "\u0906\u092A\u0915\u0947 \u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u0915\u0940 Knowledge Library \u2014 durable store \u091C\u0948\u0938\u0947 \u0930\u093F\u092A\u094B\u0930\u094D\u091F \u0915\u0930\u0924\u093E \u0939\u0948 \u0935\u0948\u0938\u0940 entries\u0964"
  },
  empty: { en: "No knowledge entries yet.", hi: "\u0905\u092D\u0940 \u0915\u094B\u0908 knowledge entry \u0928\u0939\u0940\u0902\u0964" },
  noProject: { en: "No project is available for this account.", hi: "\u0907\u0938 \u0916\u093E\u0924\u0947 \u0915\u0947 \u0932\u093F\u090F \u0915\u094B\u0908 \u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" },
  chunks: { en: "chunks", hi: "chunks" }
};
function tr5(key, locale) {
  return STR5[key][locale];
}
function KnowledgeLibraryPanel({
  token,
  locale = "en",
  baseUrl = "",
  fetchImpl
}) {
  const doFetch = fetchImpl ?? fetch;
  const [rows, setRows] = useState15([]);
  const [loading, setLoading] = useState15(true);
  const [error, setError] = useState15(null);
  const [noProject, setNoProject] = useState15(false);
  const load = useCallback9(async () => {
    setLoading(true);
    setError(null);
    setNoProject(false);
    try {
      const pres = await doFetch(`${baseUrl}/api/projects`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token ?? ""}` }
      });
      const pjson = await pres.json().catch(() => ({}));
      if (!pres.ok) throw new Error(typeof pjson.error === "string" ? pjson.error : `http_error_${pres.status}`);
      const projects = Array.isArray(pjson.projects) ? pjson.projects : [];
      const project = projects.find((p) => typeof p.projectId === "string");
      if (!project) {
        setNoProject(true);
        setRows([]);
        return;
      }
      const projectId = String(project.projectId);
      const lres = await doFetch(`${baseUrl}/api/projects/${encodeURIComponent(projectId)}/knowledge/library`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token ?? ""}` }
      });
      const ljson = await lres.json().catch(() => ({}));
      if (!lres.ok) throw new Error(typeof ljson.error === "string" ? ljson.error : `http_error_${lres.status}`);
      const entries = Array.isArray(ljson.entries) ? ljson.entries : [];
      setRows(
        entries.map((e) => ({
          entryId: String(e.entryId ?? e.id ?? ""),
          ...typeof e.title === "string" ? { title: e.title } : {},
          ...typeof e.fileName === "string" ? { fileName: e.fileName } : {},
          ...typeof e.state === "string" ? { state: e.state } : {},
          ...typeof e.chunkCount === "number" ? { chunkCount: e.chunkCount } : {},
          ...typeof e.updatedAt === "number" ? { updatedAt: e.updatedAt } : {}
        })).filter((e) => e.entryId !== "")
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "load_failed");
    } finally {
      setLoading(false);
    }
  }, [doFetch, baseUrl, token]);
  useEffect11(() => {
    void load();
  }, [load]);
  return /* @__PURE__ */ jsxs19("section", { "aria-label": tr5("title", locale), style: { display: "grid", gap: 12 }, children: [
    /* @__PURE__ */ jsxs19("header", { children: [
      /* @__PURE__ */ jsx21("h3", { style: { margin: 0, fontSize: 16, fontWeight: 600 }, children: tr5("title", locale) }),
      /* @__PURE__ */ jsx21("p", { style: { margin: "4px 0 0", fontSize: 12, color: "#6c757d" }, children: tr5("sub", locale) })
    ] }),
    loading ? /* @__PURE__ */ jsx21("p", { style: { margin: 0, fontSize: 13, color: "#6c757d" }, children: "\u2026" }) : noProject ? /* @__PURE__ */ jsx21(SettingsEmptyState, { title_hi: STR5.noProject.hi, message_hi: "" }) : rows.length === 0 ? /* @__PURE__ */ jsx21(SettingsEmptyState, { title_hi: STR5.empty.hi, message_hi: "" }) : /* @__PURE__ */ jsx21("div", { style: { display: "grid", gap: 6 }, children: rows.map((e) => /* @__PURE__ */ jsxs19("div", { style: { border: "1px solid #dee2e6", borderRadius: 8, padding: "8px 10px" }, children: [
      /* @__PURE__ */ jsx21("div", { style: { fontSize: 13, fontWeight: 500, wordBreak: "break-word" }, children: e.title ?? e.fileName ?? e.entryId }),
      /* @__PURE__ */ jsxs19("div", { style: { fontSize: 11, color: "#6c757d" }, children: [
        e.state ?? "",
        typeof e.chunkCount === "number" ? ` \xB7 ${e.chunkCount} ${tr5("chunks", locale)}` : "",
        e.updatedAt ? ` \xB7 ${new Date(e.updatedAt).toLocaleString(locale === "hi" ? "hi-IN" : "en-IN", { dateStyle: "short", timeStyle: "short" })}` : ""
      ] })
    ] }, e.entryId)) }),
    error ? /* @__PURE__ */ jsx21("p", { role: "alert", style: { margin: 0, fontSize: 13, color: "#b42318" }, children: error }) : null
  ] });
}

// src/settings/MemoryPanel.tsx
import { useCallback as useCallback10, useEffect as useEffect12, useState as useState16 } from "react";
import { jsx as jsx22, jsxs as jsxs20 } from "react/jsx-runtime";
var STR6 = {
  title: { en: "Memory", hi: "\u0938\u094D\u092E\u0943\u0924\u093F" },
  intro: {
    en: "Canonical memory state for this deployment. Memory keeps long-term context only when the server-side persistence path is configured.",
    hi: "\u0907\u0938 \u0921\u093F\u092A\u094D\u0932\u0949\u092F\u092E\u0947\u0902\u091F \u0915\u0940 canonical \u0938\u094D\u092E\u0943\u0924\u093F \u0938\u094D\u0925\u093F\u0924\u093F\u0964 \u0938\u094D\u092E\u0943\u0924\u093F \u0924\u092D\u0940 \u0932\u0902\u092C\u093E \u0938\u0902\u0926\u0930\u094D\u092D \u0930\u0916\u0924\u0940 \u0939\u0948 \u091C\u092C server-side persistence path \u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917 \u0939\u094B\u0964"
  },
  mode: { en: "Memory mode", hi: "\u0938\u094D\u092E\u0943\u0924\u093F \u092E\u094B\u0921" },
  modeDisabled: { en: "Disabled", hi: "\u092C\u0902\u0926" },
  modeMetadata: { en: "Metadata preview only", hi: "\u0915\u0947\u0935\u0932 \u092E\u0947\u091F\u093E\u0921\u0947\u091F\u093E \u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942" },
  modeDryRun: { en: "Dry-run (no writes)", hi: "\u0921\u094D\u0930\u093E\u092F-\u0930\u0928 (\u0915\u094B\u0908 write \u0928\u0939\u0940\u0902)" },
  modeApproval: { en: "Approval required", hi: "\u0905\u0928\u0941\u092E\u0924\u093F \u0906\u0935\u0936\u094D\u092F\u0915" },
  storage: { en: "Persistent storage", hi: "\u0938\u094D\u0925\u093E\u092F\u0940 storage" },
  adapter: { en: "Storage adapter", hi: "Storage adapter" },
  vector: { en: "Vector store adapter", hi: "Vector store adapter" },
  rawContent: { en: "Raw content storage", hi: "raw \u0938\u093E\u092E\u0917\u094D\u0930\u0940 storage" },
  embeddings: { en: "Embedding generation", hi: "Embedding \u0928\u093F\u0930\u094D\u092E\u093E\u0923" },
  ingestion: { en: "Knowledge ingestion", hi: "Knowledge ingestion" },
  crossApp: { en: "Cross-app sharing", hi: "\u0910\u092A-\u092A\u093E\u0930 \u0938\u093E\u091D\u093E\u0915\u0930\u0923" },
  on: { en: "On", hi: "\u091A\u093E\u0932\u0942" },
  off: { en: "Off", hi: "\u092C\u0902\u0926" },
  configured: { en: "Configured", hi: "\u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930\u094D\u0921" },
  notConfigured: { en: "Not configured", hi: "\u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917 \u0928\u0939\u0940\u0902" },
  unavailable: {
    en: "Memory persistence is not configured on this deployment \u2014 no raw conversations, embeddings, or long-term memory are stored.",
    hi: "\u0907\u0938 \u0921\u093F\u092A\u094D\u0932\u0949\u092F\u092E\u0947\u0902\u091F \u092A\u0930 \u0938\u094D\u092E\u0943\u0924\u093F persistence \u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917 \u0928\u0939\u0940\u0902 \u0939\u0948 \u2014 \u0915\u094B\u0908 raw \u091A\u0948\u091F, embeddings \u092F\u093E \u0932\u0902\u092C\u0940 \u0938\u094D\u092E\u0943\u0924\u093F \u0938\u094D\u091F\u094B\u0930 \u0928\u0939\u0940\u0902 \u0939\u094B\u0924\u0940\u0964"
  },
  loadFailed: { en: "Memory status could not be loaded.", hi: "\u0938\u094D\u092E\u0943\u0924\u093F \u0938\u094D\u0925\u093F\u0924\u093F \u0932\u094B\u0921 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0940\u0964" },
  loading: { en: "Loading\u2026", hi: "\u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026" },
  retry: { en: "Retry", hi: "\u092A\u0941\u0928\u0903 \u092A\u094D\u0930\u092F\u093E\u0938" }
};
function t2(key, locale) {
  return STR6[key][locale];
}
function MemoryPanel({ token, locale = "en", baseUrl = "", fetchImpl }) {
  const [state, setState] = useState16("loading");
  const [payload, setPayload] = useState16(void 0);
  const load = useCallback10(async () => {
    setState("loading");
    try {
      const init = { credentials: "include" };
      if (token) init.headers = { Authorization: `Bearer ${token}` };
      const r = await (fetchImpl ?? fetch)(`${baseUrl}/api/memory/settings`, init);
      if (!r.ok) {
        setState("error");
        return;
      }
      const json = await r.json();
      setPayload(json.memory ?? json);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [baseUrl, fetchImpl, token]);
  useEffect12(() => {
    void load();
  }, [load]);
  if (state === "loading") {
    return /* @__PURE__ */ jsx22("p", { style: { margin: 0, fontSize: 13, color: "#6c757d" }, children: t2("loading", locale) });
  }
  if (state === "error" || !payload) {
    return /* @__PURE__ */ jsxs20("div", { style: { display: "grid", gap: 10, justifyItems: "start" }, children: [
      /* @__PURE__ */ jsx22("p", { role: "alert", style: { margin: 0, fontSize: 13, color: "#b42318" }, children: t2("loadFailed", locale) }),
      /* @__PURE__ */ jsx22(
        "button",
        {
          type: "button",
          onClick: () => void load(),
          style: { border: "1px solid #dee2e6", borderRadius: 8, padding: "6px 12px", background: "#fff", cursor: "pointer", fontSize: 12.5 },
          children: t2("retry", locale)
        }
      )
    ] });
  }
  const modeLabel = payload.memoryMode === "disabled" ? t2("modeDisabled", locale) : payload.memoryMode === "dry_run" ? t2("modeDryRun", locale) : payload.memoryMode === "approval_required" ? t2("modeApproval", locale) : t2("modeMetadata", locale);
  const rows = [
    { label: t2("storage", locale), value: payload.persistentMemoryEnabled ? t2("on", locale) : t2("off", locale), truthy: payload.persistentMemoryEnabled === true },
    { label: t2("adapter", locale), value: payload.persistenceAdapterConfigured ? t2("configured", locale) : t2("notConfigured", locale), truthy: payload.persistenceAdapterConfigured === true },
    { label: t2("vector", locale), value: payload.vectorStoreAdapterConfigured ? t2("configured", locale) : t2("notConfigured", locale), truthy: payload.vectorStoreAdapterConfigured === true },
    { label: t2("rawContent", locale), value: payload.rawConversationStorageEnabled ? t2("on", locale) : t2("off", locale), truthy: payload.rawConversationStorageEnabled === true },
    { label: t2("embeddings", locale), value: payload.embeddingGenerationEnabled ? t2("on", locale) : t2("off", locale), truthy: payload.embeddingGenerationEnabled === true },
    { label: t2("ingestion", locale), value: payload.knowledgeIngestionEnabled ? t2("on", locale) : t2("off", locale), truthy: payload.knowledgeIngestionEnabled === true },
    { label: t2("crossApp", locale), value: payload.crossAppMemorySharingEnabled ? t2("on", locale) : t2("off", locale), truthy: payload.crossAppMemorySharingEnabled === true }
  ];
  return /* @__PURE__ */ jsxs20("div", { style: { display: "grid", gap: 14 }, children: [
    /* @__PURE__ */ jsxs20("header", { children: [
      /* @__PURE__ */ jsx22("h3", { style: { margin: "0 0 5px", fontSize: 17 }, children: t2("title", locale) }),
      /* @__PURE__ */ jsx22("p", { style: { margin: 0, color: "var(--ss-muted, #6b7280)", fontSize: 12.5 }, children: t2("intro", locale) })
    ] }),
    payload.persistentMemoryEnabled !== true ? /* @__PURE__ */ jsx22(
      "div",
      {
        style: {
          border: "1px solid var(--ss-border, #e5e7eb)",
          borderRadius: 10,
          padding: "10px 12px",
          fontSize: 12.5,
          color: "var(--ss-muted, #6b7280)",
          background: "var(--ss-panel, #fafafa)"
        },
        children: t2("unavailable", locale)
      }
    ) : null,
    /* @__PURE__ */ jsxs20("div", { style: { display: "grid", gap: 2, fontSize: 13 }, children: [
      /* @__PURE__ */ jsxs20("div", { style: { display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--ss-border, #e5e7eb)" }, children: [
        /* @__PURE__ */ jsx22("span", { children: t2("mode", locale) }),
        /* @__PURE__ */ jsx22("strong", { children: modeLabel })
      ] }),
      rows.map((row2) => /* @__PURE__ */ jsxs20(
        "div",
        {
          style: { display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--ss-border, #e5e7eb)" },
          children: [
            /* @__PURE__ */ jsx22("span", { children: row2.label }),
            /* @__PURE__ */ jsx22("strong", { style: { color: row2.truthy ? "var(--ss-success, #16a34a)" : "var(--ss-muted, #6b7280)" }, children: row2.value })
          ]
        },
        row2.label
      ))
    ] })
  ] });
}

// src/settings/VoiceSettingsPanel.tsx
import { useCallback as useCallback11, useEffect as useEffect13, useState as useState17 } from "react";
import { jsx as jsx23, jsxs as jsxs21 } from "react/jsx-runtime";
var VOICE_PREFERENCE_SETTING_KEY = "preferred_voice_id";
var STR7 = {
  title: { en: "Voice", hi: "\u0935\u0949\u0907\u0938" },
  intro: {
    en: "Voice catalog and spoken-reply preference for this deployment. The catalog and mode come from the server voice policy.",
    hi: "\u0907\u0938 \u0921\u093F\u092A\u094D\u0932\u0949\u092F\u092E\u0947\u0902\u091F \u0915\u0940 \u0935\u0949\u0907\u0938 \u0938\u0942\u091A\u0940 \u0914\u0930 \u092C\u094B\u0932\u0947-\u0909\u0924\u094D\u0924\u0930 \u0915\u0940 \u092A\u0938\u0902\u0926\u0964 \u0938\u0942\u091A\u0940 \u0914\u0930 \u092E\u094B\u0921 server \u0915\u0940 voice policy \u0938\u0947 \u0906\u0924\u0947 \u0939\u0948\u0902\u0964"
  },
  catalog: { en: "Available voices", hi: "\u0909\u092A\u0932\u092C\u094D\u0927 \u0935\u0949\u0907\u0938" },
  preferred: { en: "Preferred voice for spoken replies", hi: "\u092C\u094B\u0932\u0947 \u091C\u093E\u0928\u0947 \u0935\u093E\u0932\u0947 \u0909\u0924\u094D\u0924\u0930\u094B\u0902 \u0915\u0940 \u092A\u0938\u0902\u0926\u0940\u0926\u093E \u0935\u0949\u0907\u0938" },
  systemDefault: { en: "System default", hi: "\u0938\u093F\u0938\u094D\u091F\u092E \u0921\u093F\u092B\u093C\u0949\u0932\u094D\u091F" },
  selected: { en: "Selected", hi: "\u091A\u0941\u0928\u0940 \u0917\u0908" },
  save: { en: "Save preference", hi: "\u092A\u0938\u0902\u0926 \u0938\u0939\u0947\u091C\u0947\u0902" },
  saving: { en: "Saving\u2026", hi: "\u0938\u0939\u0947\u091C\u093E \u091C\u093E \u0930\u0939\u093E \u0939\u0948\u2026" },
  saved: { en: "Preference saved.", hi: "\u092A\u0938\u0902\u0926 \u0938\u0939\u0947\u091C\u0940 \u0917\u0908\u0964" },
  saveFailed: { en: "Preference could not be saved.", hi: "\u092A\u0938\u0902\u0926 \u0938\u0939\u0947\u091C\u0940 \u0928\u0939\u0940\u0902 \u091C\u093E \u0938\u0915\u0940\u0964" },
  mode: { en: "Voice mode", hi: "\u0935\u0949\u0907\u0938 \u092E\u094B\u0921" },
  pushToTalk: { en: "Push-to-talk", hi: "\u092A\u0941\u0936-\u091F\u0942-\u091F\u0949\u0915" },
  alwaysListening: { en: "Always listening", hi: "\u0939\u092E\u0947\u0936\u093E \u0938\u0941\u0928\u0928\u093E" },
  on: { en: "On", hi: "\u091A\u093E\u0932\u0942" },
  off: { en: "Off", hi: "\u092C\u0902\u0926" },
  note: {
    en: "Voice input is push-to-talk only \u2014 nothing is recorded until you start it. Spoken replies use your preferred voice where voice output is enabled.",
    hi: "\u0935\u0949\u0907\u0938 \u0907\u0928\u092A\u0941\u091F \u0915\u0947\u0935\u0932 \u092A\u0941\u0936-\u091F\u0942-\u091F\u0949\u0915 \u0939\u0948 \u2014 \u091C\u092C \u0924\u0915 \u0906\u092A \u0936\u0941\u0930\u0942 \u0928 \u0915\u0930\u0947\u0902 \u0915\u0941\u091B \u0930\u093F\u0915\u0949\u0930\u094D\u0921 \u0928\u0939\u0940\u0902 \u0939\u094B\u0924\u093E\u0964 \u092C\u094B\u0932\u0947 \u0909\u0924\u094D\u0924\u0930 \u091C\u0939\u093E\u0901 voice output \u091A\u093E\u0932\u0942 \u0939\u0948 \u0935\u0939\u093E\u0901 \u0906\u092A\u0915\u0940 \u092A\u0938\u0902\u0926\u0940\u0926\u093E \u0935\u0949\u0907\u0938 \u0915\u093E \u0909\u092A\u092F\u094B\u0917 \u0915\u0930\u0924\u0947 \u0939\u0948\u0902\u0964"
  },
  empty: {
    en: "No voices are published by this server yet.",
    hi: "\u0907\u0938 server \u0928\u0947 \u0905\u092D\u0940 \u0915\u094B\u0908 \u0935\u0949\u0907\u0938 \u092A\u094D\u0930\u0915\u093E\u0936\u093F\u0924 \u0928\u0939\u0940\u0902 \u0915\u0940 \u0939\u0948\u0964"
  },
  loadFailed: {
    en: "Voice catalog could not be loaded.",
    hi: "\u0935\u0949\u0907\u0938 \u0938\u0942\u091A\u0940 \u0932\u094B\u0921 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0940\u0964"
  },
  loading: { en: "Loading\u2026", hi: "\u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026" },
  retry: { en: "Retry", hi: "\u092A\u0941\u0928\u0903 \u092A\u094D\u0930\u092F\u093E\u0938" }
};
function t3(key, locale) {
  return STR7[key][locale];
}
function VoiceSettingsPanel({
  token,
  locale = "en",
  baseUrl = "",
  fetchImpl,
  settingsService,
  initialPayload,
  initialPreferredVoiceId
}) {
  const [state, setState] = useState17(
    initialPayload ? "ready" : "loading"
  );
  const [payload, setPayload] = useState17(initialPayload);
  const [preferredVoiceId, setPreferredVoiceId] = useState17(
    initialPreferredVoiceId ?? void 0
  );
  const [selected, setSelected] = useState17(initialPreferredVoiceId ?? "");
  const [saveState, setSaveState] = useState17("idle");
  const settings = useCallback11(() => {
    if (settingsService) return settingsService;
    try {
      return createCanonicalSettingsService({
        token: token ?? COOKIE_SESSION_TOKEN,
        baseUrl,
        ...fetchImpl !== void 0 ? { fetchImpl } : {}
      });
    } catch {
      return void 0;
    }
  }, [baseUrl, fetchImpl, settingsService, token]);
  const load = useCallback11(async () => {
    if (initialPayload) {
      setState("ready");
      return;
    }
    setState("loading");
    try {
      const init = { credentials: "include" };
      if (token) init.headers = { Authorization: `Bearer ${token}` };
      const r = await (fetchImpl ?? fetch)(`${baseUrl}/api/voice/voices`, init);
      if (!r.ok) {
        setState("error");
        return;
      }
      const json = await r.json();
      setPayload({
        voices: Array.isArray(json.voices) ? json.voices : [],
        voiceMode: json.voiceMode,
        alwaysListening: json.alwaysListening === true
      });
      const svc = settings();
      if (svc) {
        const rows = await svc.list("voice").catch(() => []);
        const pref = rows.find((row2) => row2.settingKey === VOICE_PREFERENCE_SETTING_KEY);
        const value = pref?.settingValue?.voiceId;
        if (typeof value === "string" && value.length > 0) {
          setPreferredVoiceId(value);
          setSelected(value);
        }
      }
      setState("ready");
    } catch {
      setState("error");
    }
  }, [baseUrl, fetchImpl, initialPayload, settings, token]);
  useEffect13(() => {
    void load();
  }, [load]);
  const save = useCallback11(async () => {
    const svc = settings();
    if (!svc) {
      setSaveState("error");
      return;
    }
    setSaveState("saving");
    try {
      if (selected === "") {
        await svc.delete("voice", VOICE_PREFERENCE_SETTING_KEY);
        setPreferredVoiceId(void 0);
      } else {
        await svc.save({
          namespace: "voice",
          settingKey: VOICE_PREFERENCE_SETTING_KEY,
          settingValue: { voiceId: selected }
        });
        setPreferredVoiceId(selected);
      }
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }, [selected, settings]);
  if (state === "loading") {
    return /* @__PURE__ */ jsx23("p", { style: { margin: 0, fontSize: 13, color: "#6c757d" }, children: t3("loading", locale) });
  }
  if (state === "error" || !payload) {
    return /* @__PURE__ */ jsxs21("div", { style: { display: "grid", gap: 10, justifyItems: "start" }, children: [
      /* @__PURE__ */ jsx23("p", { role: "alert", style: { margin: 0, fontSize: 13, color: "#b42318" }, children: t3("loadFailed", locale) }),
      /* @__PURE__ */ jsx23(
        "button",
        {
          type: "button",
          onClick: () => void load(),
          style: { border: "1px solid #dee2e6", borderRadius: 8, padding: "6px 12px", background: "#fff", cursor: "pointer", fontSize: 12.5 },
          children: t3("retry", locale)
        }
      )
    ] });
  }
  const dirty = selected !== (preferredVoiceId ?? "");
  return /* @__PURE__ */ jsxs21("div", { style: { display: "grid", gap: 14 }, children: [
    /* @__PURE__ */ jsxs21("header", { children: [
      /* @__PURE__ */ jsx23("h3", { style: { margin: "0 0 5px", fontSize: 17 }, children: t3("title", locale) }),
      /* @__PURE__ */ jsx23("p", { style: { margin: 0, color: "var(--ss-muted, #6b7280)", fontSize: 12.5 }, children: t3("intro", locale) })
    ] }),
    /* @__PURE__ */ jsxs21("div", { style: { display: "grid", gap: 2, fontSize: 13 }, children: [
      /* @__PURE__ */ jsxs21("div", { style: { display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--ss-border, #e5e7eb)" }, children: [
        /* @__PURE__ */ jsx23("span", { children: t3("mode", locale) }),
        /* @__PURE__ */ jsx23("strong", { children: payload.voiceMode === "push_to_talk" ? t3("pushToTalk", locale) : payload.voiceMode ?? "\u2014" })
      ] }),
      /* @__PURE__ */ jsxs21("div", { style: { display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--ss-border, #e5e7eb)" }, children: [
        /* @__PURE__ */ jsx23("span", { children: t3("alwaysListening", locale) }),
        /* @__PURE__ */ jsx23("strong", { style: { color: payload.alwaysListening === true ? "var(--ss-warning, #b45309)" : "var(--ss-success, #16a34a)" }, children: payload.alwaysListening === true ? t3("on", locale) : t3("off", locale) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs21("section", { "aria-label": t3("catalog", locale), style: { display: "grid", gap: 8 }, children: [
      /* @__PURE__ */ jsx23("strong", { style: { fontSize: 13 }, children: t3("catalog", locale) }),
      payload.voices.length === 0 ? /* @__PURE__ */ jsx23("p", { style: { margin: 0, fontSize: 12.5, color: "var(--ss-muted, #6b7280)" }, children: t3("empty", locale) }) : /* @__PURE__ */ jsxs21("div", { role: "radiogroup", "aria-label": t3("preferred", locale), style: { display: "grid", gap: 6 }, children: [
        /* @__PURE__ */ jsxs21(
          "label",
          {
            style: {
              display: "flex",
              alignItems: "center",
              gap: 10,
              border: `1px solid ${selected === "" ? "var(--ss-accent, #0f766e)" : "var(--ss-border, #e5e7eb)"}`,
              borderRadius: 10,
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: 13
            },
            children: [
              /* @__PURE__ */ jsx23(
                "input",
                {
                  type: "radio",
                  name: "ss-voice-preference",
                  checked: selected === "",
                  onChange: () => {
                    setSelected("");
                    setSaveState("idle");
                  }
                }
              ),
              /* @__PURE__ */ jsx23("span", { style: { flex: 1 }, children: t3("systemDefault", locale) }),
              preferredVoiceId === void 0 ? /* @__PURE__ */ jsx23("span", { style: { fontSize: 11, color: "var(--ss-success, #16a34a)" }, children: t3("selected", locale) }) : null
            ]
          }
        ),
        payload.voices.map((voice) => /* @__PURE__ */ jsxs21(
          "label",
          {
            style: {
              display: "flex",
              alignItems: "center",
              gap: 10,
              border: `1px solid ${selected === voice.id ? "var(--ss-accent, #0f766e)" : "var(--ss-border, #e5e7eb)"}`,
              borderRadius: 10,
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: 13
            },
            children: [
              /* @__PURE__ */ jsx23(
                "input",
                {
                  type: "radio",
                  name: "ss-voice-preference",
                  checked: selected === voice.id,
                  onChange: () => {
                    setSelected(voice.id);
                    setSaveState("idle");
                  }
                }
              ),
              /* @__PURE__ */ jsxs21("span", { style: { flex: 1 }, children: [
                voice.name,
                /* @__PURE__ */ jsxs21("span", { style: { marginLeft: 8, fontSize: 11, color: "var(--ss-muted, #6b7280)" }, children: [
                  voice.language,
                  " \xB7 ",
                  voice.id
                ] })
              ] }),
              preferredVoiceId === voice.id ? /* @__PURE__ */ jsx23("span", { style: { fontSize: 11, color: "var(--ss-success, #16a34a)" }, children: t3("selected", locale) }) : null
            ]
          },
          voice.id
        ))
      ] })
    ] }),
    payload.voices.length > 0 ? /* @__PURE__ */ jsxs21("div", { style: { display: "flex", alignItems: "center", gap: 10 }, children: [
      /* @__PURE__ */ jsx23(
        "button",
        {
          type: "button",
          disabled: !dirty || saveState === "saving",
          onClick: () => void save(),
          style: {
            border: "none",
            borderRadius: 8,
            padding: "7px 14px",
            background: dirty ? "var(--ss-accent, #0f766e)" : "var(--ss-border, #e5e7eb)",
            color: dirty ? "#fff" : "var(--ss-muted, #6b7280)",
            cursor: dirty ? "pointer" : "default",
            fontSize: 12.5
          },
          children: saveState === "saving" ? t3("saving", locale) : t3("save", locale)
        }
      ),
      saveState === "saved" ? /* @__PURE__ */ jsx23("span", { style: { fontSize: 12, color: "var(--ss-success, #16a34a)" }, children: t3("saved", locale) }) : null,
      saveState === "error" ? /* @__PURE__ */ jsx23("span", { role: "alert", style: { fontSize: 12, color: "#b42318" }, children: t3("saveFailed", locale) }) : null
    ] }) : null,
    /* @__PURE__ */ jsx23("p", { style: { margin: 0, fontSize: 12, color: "var(--ss-muted, #6b7280)", lineHeight: 1.55 }, children: t3("note", locale) })
  ] });
}

// src/settings/ConnectionsPanel.tsx
import { useCallback as useCallback12, useEffect as useEffect14, useState as useState18 } from "react";
import { jsx as jsx24, jsxs as jsxs22 } from "react/jsx-runtime";
var STR8 = {
  title: { en: "Connections", hi: "\u0915\u0928\u0947\u0915\u094D\u0936\u0928" },
  intro: {
    en: "App connections and connectors known to this server. Status and capabilities come from the server \u2014 nothing is connected silently.",
    hi: "\u0907\u0938 server \u0915\u094B \u091C\u094D\u091E\u093E\u0924 \u0910\u092A \u0915\u0928\u0947\u0915\u094D\u0936\u0928 \u0914\u0930 \u0915\u0928\u0947\u0915\u094D\u091F\u0930\u0964 \u0938\u094D\u0925\u093F\u0924\u093F \u0914\u0930 \u0915\u094D\u0937\u092E\u0924\u093E\u090F\u0901 server \u0938\u0947 \u0906\u0924\u0940 \u0939\u0948\u0902 \u2014 \u091A\u0941\u092A\u091A\u093E\u092A \u0915\u0941\u091B \u092D\u0940 \u0915\u0928\u0947\u0915\u094D\u091F \u0928\u0939\u0940\u0902 \u0939\u094B\u0924\u093E\u0964"
  },
  appsSection: { en: "App connections", hi: "\u0910\u092A \u0915\u0928\u0947\u0915\u094D\u0936\u0928" },
  connectorsSection: { en: "Connectors", hi: "\u0915\u0928\u0947\u0915\u094D\u091F\u0930" },
  mode: { en: "Mode", hi: "\u092E\u094B\u0921" },
  serverOnly: { en: "Credentials stay on the server", hi: "\u0915\u094D\u0930\u0947\u0921\u0947\u0902\u0936\u093F\u092F\u0932 \u0915\u0947\u0935\u0932 server \u092A\u0930 \u0930\u0939\u0924\u0947 \u0939\u0948\u0902" },
  allowed: { en: "Allowed", hi: "\u0905\u0928\u0941\u092E\u0924" },
  blocked: { en: "Blocked", hi: "\u0905\u0935\u0930\u0941\u0926\u094D\u0927" },
  status: {
    not_configured: { en: "Not configured", hi: "\u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0928\u0939\u0940\u0902" },
    config_not_connected: { en: "Not connected", hi: "\u0915\u0928\u0947\u0915\u094D\u091F \u0928\u0939\u0940\u0902" },
    connected: { en: "Connected", hi: "\u0915\u0928\u0947\u0915\u094D\u091F\u0947\u0921" }
  },
  previewOnly: { en: "Preview only \u2014 no live connection yet", hi: "\u0915\u0947\u0935\u0932 \u092A\u0942\u0930\u094D\u0935\u093E\u0935\u0932\u094B\u0915\u0928 \u2014 \u0905\u092D\u0940 \u0915\u094B\u0908 live \u0915\u0928\u0947\u0915\u094D\u0936\u0928 \u0928\u0939\u0940\u0902" },
  emptyApps: { en: "No app connections are published by this server.", hi: "\u0907\u0938 server \u0928\u0947 \u0915\u094B\u0908 \u0910\u092A \u0915\u0928\u0947\u0915\u094D\u0936\u0928 \u092A\u094D\u0930\u0915\u093E\u0936\u093F\u0924 \u0928\u0939\u0940\u0902 \u0915\u093F\u092F\u093E \u0939\u0948\u0964" },
  emptyConnectors: { en: "No connectors are published by this server.", hi: "\u0907\u0938 server \u0928\u0947 \u0915\u094B\u0908 \u0915\u0928\u0947\u0915\u094D\u091F\u0930 \u092A\u094D\u0930\u0915\u093E\u0936\u093F\u0924 \u0928\u0939\u0940\u0902 \u0915\u093F\u092F\u093E \u0939\u0948\u0964" },
  loadFailed: { en: "Connections could not be loaded.", hi: "\u0915\u0928\u0947\u0915\u094D\u0936\u0928 \u0932\u094B\u0921 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0947\u0964" },
  loading: { en: "Loading\u2026", hi: "\u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026" },
  retry: { en: "Retry", hi: "\u092A\u0941\u0928\u0903 \u092A\u094D\u0930\u092F\u093E\u0938" }
};
function statusLabel(status, locale) {
  const known = STR8.status[status];
  return known ? known[locale] : status;
}
function ConnectionsPanel({
  token,
  locale = "en",
  baseUrl = "",
  fetchImpl,
  initialApps,
  initialConnectors
}) {
  const [state, setState] = useState18(
    initialApps !== void 0 || initialConnectors !== void 0 ? "ready" : "loading"
  );
  const [apps, setApps] = useState18(initialApps);
  const [connectors, setConnectors] = useState18(initialConnectors);
  const t5 = useCallback12(
    (key) => STR8[key][locale],
    [locale]
  );
  const load = useCallback12(async () => {
    if (initialApps !== void 0 || initialConnectors !== void 0) {
      setState("ready");
      return;
    }
    setState("loading");
    try {
      const init = { credentials: "include" };
      if (token) init.headers = { Authorization: `Bearer ${token}` };
      const call = fetchImpl ?? fetch;
      const [appsRes, connectorsRes] = await Promise.all([
        call(`${baseUrl}/api/apps/connections`, init),
        call(`${baseUrl}/api/settings-studio/connectors`, init)
      ]);
      if (!appsRes.ok || !connectorsRes.ok) {
        setState("error");
        return;
      }
      const appsJson = await appsRes.json();
      const connectorsJson = await connectorsRes.json();
      setApps(Array.isArray(appsJson.connections) ? appsJson.connections : []);
      setConnectors(Array.isArray(connectorsJson.connectors) ? connectorsJson.connectors : []);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [baseUrl, fetchImpl, initialApps, initialConnectors, token]);
  useEffect14(() => {
    void load();
  }, [load]);
  if (state === "loading") {
    return /* @__PURE__ */ jsx24("p", { style: { margin: 0, fontSize: 13, color: "#6c757d" }, children: t5("loading") });
  }
  if (state === "error") {
    return /* @__PURE__ */ jsxs22("div", { style: { display: "grid", gap: 10, justifyItems: "start" }, children: [
      /* @__PURE__ */ jsx24("p", { role: "alert", style: { margin: 0, fontSize: 13, color: "#b42318" }, children: t5("loadFailed") }),
      /* @__PURE__ */ jsx24(
        "button",
        {
          type: "button",
          onClick: () => void load(),
          style: { border: "1px solid #dee2e6", borderRadius: 8, padding: "6px 12px", background: "#fff", cursor: "pointer", fontSize: 12.5 },
          children: t5("retry")
        }
      )
    ] });
  }
  const chip = {
    display: "inline-block",
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 999,
    border: "1px solid var(--ss-border, #e5e7eb)",
    marginRight: 4,
    marginBottom: 3
  };
  return /* @__PURE__ */ jsxs22("div", { style: { display: "grid", gap: 16 }, children: [
    /* @__PURE__ */ jsxs22("header", { children: [
      /* @__PURE__ */ jsx24("h3", { style: { margin: "0 0 5px", fontSize: 17 }, children: t5("title") }),
      /* @__PURE__ */ jsx24("p", { style: { margin: 0, color: "var(--ss-muted, #6b7280)", fontSize: 12.5 }, children: t5("intro") })
    ] }),
    /* @__PURE__ */ jsxs22("section", { "aria-label": t5("appsSection"), style: { display: "grid", gap: 8 }, children: [
      /* @__PURE__ */ jsx24("strong", { style: { fontSize: 13 }, children: t5("appsSection") }),
      (apps ?? []).length === 0 ? /* @__PURE__ */ jsx24("p", { style: { margin: 0, fontSize: 12.5, color: "var(--ss-muted, #6b7280)" }, children: t5("emptyApps") }) : /* @__PURE__ */ jsx24("div", { style: { display: "grid", gap: 8 }, children: (apps ?? []).map((app) => /* @__PURE__ */ jsxs22(
        "div",
        {
          style: { border: "1px solid var(--ss-border, #e5e7eb)", borderRadius: 10, padding: "10px 12px", fontSize: 13 },
          children: [
            /* @__PURE__ */ jsxs22("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }, children: [
              /* @__PURE__ */ jsx24("strong", { children: app.appDisplayName }),
              /* @__PURE__ */ jsxs22("span", { style: { fontSize: 11.5, color: "var(--ss-muted, #6b7280)" }, children: [
                statusLabel(app.connectionStatus, locale),
                " \xB7 ",
                app.connectionMode
              ] })
            ] }),
            app.allowedCapabilities.length > 0 ? /* @__PURE__ */ jsxs22("div", { style: { marginTop: 7 }, children: [
              /* @__PURE__ */ jsxs22("span", { style: { fontSize: 11, color: "var(--ss-muted, #6b7280)" }, children: [
                t5("allowed"),
                ": "
              ] }),
              app.allowedCapabilities.map((cap) => /* @__PURE__ */ jsx24("span", { style: { ...chip, color: "var(--ss-success, #16a34a)" }, children: cap }, cap))
            ] }) : null,
            app.blockedCapabilities.length > 0 ? /* @__PURE__ */ jsxs22("div", { style: { marginTop: 2 }, children: [
              /* @__PURE__ */ jsxs22("span", { style: { fontSize: 11, color: "var(--ss-muted, #6b7280)" }, children: [
                t5("blocked"),
                ": "
              ] }),
              app.blockedCapabilities.map((cap) => /* @__PURE__ */ jsx24("span", { style: { ...chip, color: "var(--ss-warning, #b45309)" }, children: cap }, cap))
            ] }) : null,
            app.serverOnlyCredentials ? /* @__PURE__ */ jsx24("p", { style: { margin: "6px 0 0", fontSize: 11, color: "var(--ss-muted, #6b7280)" }, children: t5("serverOnly") }) : null
          ]
        },
        app.appId
      )) })
    ] }),
    /* @__PURE__ */ jsxs22("section", { "aria-label": t5("connectorsSection"), style: { display: "grid", gap: 8 }, children: [
      /* @__PURE__ */ jsx24("strong", { style: { fontSize: 13 }, children: t5("connectorsSection") }),
      (connectors ?? []).length === 0 ? /* @__PURE__ */ jsx24("p", { style: { margin: 0, fontSize: 12.5, color: "var(--ss-muted, #6b7280)" }, children: t5("emptyConnectors") }) : /* @__PURE__ */ jsx24("div", { style: { display: "grid", gap: 8 }, children: (connectors ?? []).map((connector) => /* @__PURE__ */ jsxs22(
        "div",
        {
          style: { border: "1px solid var(--ss-border, #e5e7eb)", borderRadius: 10, padding: "10px 12px", fontSize: 13 },
          children: [
            /* @__PURE__ */ jsxs22("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }, children: [
              /* @__PURE__ */ jsx24("strong", { children: connector.displayName }),
              /* @__PURE__ */ jsxs22("span", { style: { fontSize: 11.5, color: "var(--ss-muted, #6b7280)" }, children: [
                statusLabel(connector.status, locale),
                " \xB7 ",
                connector.category
              ] })
            ] }),
            connector.safetyHindi ? /* @__PURE__ */ jsx24("p", { style: { margin: "6px 0 0", fontSize: 11.5, color: "var(--ss-muted, #6b7280)" }, children: connector.safetyHindi }) : /* @__PURE__ */ jsx24("p", { style: { margin: "6px 0 0", fontSize: 11.5, color: "var(--ss-muted, #6b7280)" }, children: t5("previewOnly") })
          ]
        },
        connector.connectorId
      )) })
    ] })
  ] });
}

// src/desktop/ConnectionModeSelector.tsx
import { jsx as jsx25, jsxs as jsxs23 } from "react/jsx-runtime";
var modes = [
  { mode: "local_demo", label: "Local Demo", description: "Preview local desktop-only behavior without runtime execution." },
  { mode: "remote_https", label: "Remote HTTPS", description: "Show metadata for a future remote backend connection." },
  { mode: "vps_ssh_tunnel", label: "VPS SSH Tunnel", description: "Preview a planned tunnel route without opening SSH." },
  { mode: "disabled", label: "Disabled", description: "Desktop bridge is visible but inactive." }
];
function ConnectionModeSelector({
  selectedMode,
  readOnly = true,
  onConnectionModeChange
}) {
  return /* @__PURE__ */ jsxs23("div", { style: { border: "1px solid #dfe7ef", borderRadius: "8px", padding: "12px", backgroundColor: "#f8fafc" }, children: [
    /* @__PURE__ */ jsx25("h3", { style: { margin: "0 0 10px", fontSize: "15px", color: "#1f2933" }, children: "Connection Mode" }),
    /* @__PURE__ */ jsx25("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "8px" }, children: modes.map((item) => {
      const active = item.mode === selectedMode;
      return /* @__PURE__ */ jsxs23(
        "button",
        {
          type: "button",
          disabled: readOnly,
          "aria-pressed": active,
          onClick: () => onConnectionModeChange?.(item.mode),
          style: {
            textAlign: "left",
            border: active ? "1px solid #1d4ed8" : "1px solid #c8d3e0",
            borderRadius: "6px",
            padding: "10px",
            backgroundColor: active ? "#eff6ff" : "#ffffff",
            color: "#1f2933",
            cursor: readOnly ? "not-allowed" : "pointer",
            opacity: readOnly ? 0.72 : 1
          },
          children: [
            /* @__PURE__ */ jsx25("span", { style: { display: "block", fontWeight: 700, fontSize: "13px" }, children: item.label }),
            /* @__PURE__ */ jsx25("span", { style: { display: "block", color: "#5f6b7a", fontSize: "12px", marginTop: "4px" }, children: item.description })
          ]
        },
        item.mode
      );
    }) })
  ] });
}

// src/desktop/VpsConnectionStatus.tsx
import { jsx as jsx26, jsxs as jsxs24 } from "react/jsx-runtime";
function renderList(title, items, tone) {
  const color = tone === "safe" ? "#0f766e" : "#b42318";
  const bg = tone === "safe" ? "#ecfdf5" : "#fff1f2";
  return /* @__PURE__ */ jsxs24("div", { style: { marginTop: "8px" }, children: [
    /* @__PURE__ */ jsx26("strong", { style: { display: "block", fontSize: "12px", color: "#3d4b5c", marginBottom: "4px" }, children: title }),
    /* @__PURE__ */ jsx26("div", { style: { display: "flex", gap: "5px", flexWrap: "wrap" }, children: (items && items.length > 0 ? items : ["none"]).map((item) => /* @__PURE__ */ jsx26("span", { style: { border: `1px solid ${color}33`, backgroundColor: bg, color, borderRadius: "5px", padding: "3px 7px", fontSize: "11px" }, children: item }, item)) })
  ] });
}
function VpsConnectionStatus({ mode, tunnel }) {
  const active = mode === "vps_ssh_tunnel";
  const hostAlias = tunnel?.hostAlias || "not configured";
  return /* @__PURE__ */ jsxs24("article", { style: { border: "1px solid #dfe7ef", borderRadius: "8px", padding: "12px", backgroundColor: "#ffffff" }, children: [
    /* @__PURE__ */ jsx26("h3", { style: { margin: "0 0 8px", fontSize: "15px", color: "#1f2933" }, children: "VPS SSH Tunnel Preview" }),
    /* @__PURE__ */ jsxs24("dl", { style: { margin: 0, display: "grid", gap: "6px", fontSize: "13px" }, children: [
      /* @__PURE__ */ jsxs24("div", { children: [
        /* @__PURE__ */ jsx26("dt", { style: { fontWeight: 700, display: "inline" }, children: "Mode: " }),
        /* @__PURE__ */ jsx26("dd", { style: { margin: 0, display: "inline" }, children: tunnel?.mode || mode })
      ] }),
      /* @__PURE__ */ jsxs24("div", { children: [
        /* @__PURE__ */ jsx26("dt", { style: { fontWeight: 700, display: "inline" }, children: "Status: " }),
        /* @__PURE__ */ jsx26("dd", { style: { margin: 0, display: "inline" }, children: active ? tunnel?.status || "planned" : "inactive" })
      ] }),
      /* @__PURE__ */ jsxs24("div", { children: [
        /* @__PURE__ */ jsx26("dt", { style: { fontWeight: 700, display: "inline" }, children: "Host alias: " }),
        /* @__PURE__ */ jsx26("dd", { style: { margin: 0, display: "inline" }, children: hostAlias })
      ] }),
      tunnel?.localPort !== void 0 && /* @__PURE__ */ jsxs24("div", { children: [
        /* @__PURE__ */ jsx26("dt", { style: { fontWeight: 700, display: "inline" }, children: "Local port: " }),
        /* @__PURE__ */ jsx26("dd", { style: { margin: 0, display: "inline" }, children: tunnel.localPort })
      ] }),
      tunnel?.remotePort !== void 0 && /* @__PURE__ */ jsxs24("div", { children: [
        /* @__PURE__ */ jsx26("dt", { style: { fontWeight: 700, display: "inline" }, children: "Remote port: " }),
        /* @__PURE__ */ jsx26("dd", { style: { margin: 0, display: "inline" }, children: tunnel.remotePort })
      ] })
    ] }),
    renderList("Allowed admin actions", tunnel?.allowedAdminActions, "safe"),
    renderList("Blocked admin actions", tunnel?.blockedAdminActions, "blocked"),
    /* @__PURE__ */ jsx26("p", { style: { margin: "10px 0 0", color: "#6b7280", fontSize: "12px" }, children: "Preview-only: no SSH session, credential read, service command, or network tunnel is opened." })
  ] });
}

// src/desktop/RemoteBackendStatus.tsx
import { jsx as jsx27, jsxs as jsxs25 } from "react/jsx-runtime";
function renderMessages(label, items, color) {
  if (!items || items.length === 0) return null;
  return /* @__PURE__ */ jsxs25("div", { style: { marginTop: "8px" }, children: [
    /* @__PURE__ */ jsx27("strong", { style: { display: "block", fontSize: "12px", color }, children: label }),
    /* @__PURE__ */ jsx27("ul", { style: { margin: "4px 0 0 18px", padding: 0, color, fontSize: "12px" }, children: items.map((item) => /* @__PURE__ */ jsx27("li", { children: item }, item)) })
  ] });
}
function RemoteBackendStatus({ mode, backend }) {
  const active = mode === "remote_https";
  return /* @__PURE__ */ jsxs25("article", { style: { border: "1px solid #dfe7ef", borderRadius: "8px", padding: "12px", backgroundColor: "#ffffff" }, children: [
    /* @__PURE__ */ jsx27("h3", { style: { margin: "0 0 8px", fontSize: "15px", color: "#1f2933" }, children: "Remote Backend Status" }),
    /* @__PURE__ */ jsxs25("dl", { style: { margin: 0, display: "grid", gap: "6px", fontSize: "13px" }, children: [
      /* @__PURE__ */ jsxs25("div", { children: [
        /* @__PURE__ */ jsx27("dt", { style: { fontWeight: 700, display: "inline" }, children: "Label: " }),
        /* @__PURE__ */ jsx27("dd", { style: { margin: 0, display: "inline" }, children: backend?.label || "Remote backend" })
      ] }),
      /* @__PURE__ */ jsxs25("div", { children: [
        /* @__PURE__ */ jsx27("dt", { style: { fontWeight: 700, display: "inline" }, children: "Mode: " }),
        /* @__PURE__ */ jsx27("dd", { style: { margin: 0, display: "inline" }, children: mode })
      ] }),
      /* @__PURE__ */ jsxs25("div", { children: [
        /* @__PURE__ */ jsx27("dt", { style: { fontWeight: 700, display: "inline" }, children: "Status: " }),
        /* @__PURE__ */ jsx27("dd", { style: { margin: 0, display: "inline" }, children: active ? backend?.status || "planned" : "inactive" })
      ] }),
      backend?.baseUrl && /* @__PURE__ */ jsxs25("div", { children: [
        /* @__PURE__ */ jsx27("dt", { style: { fontWeight: 700, display: "inline" }, children: "Base URL: " }),
        /* @__PURE__ */ jsx27("dd", { style: { margin: 0, display: "inline" }, children: backend.baseUrl })
      ] }),
      backend?.apiBasePath && /* @__PURE__ */ jsxs25("div", { children: [
        /* @__PURE__ */ jsx27("dt", { style: { fontWeight: 700, display: "inline" }, children: "API base path: " }),
        /* @__PURE__ */ jsx27("dd", { style: { margin: 0, display: "inline" }, children: backend.apiBasePath })
      ] })
    ] }),
    renderMessages("Warnings", backend?.warnings, "#92400e"),
    renderMessages("Errors", backend?.errors, "#b42318"),
    /* @__PURE__ */ jsx27("p", { style: { margin: "10px 0 0", color: "#6b7280", fontSize: "12px" }, children: "Preview-only: no fetch, ping, health check, or remote connection is executed." })
  ] });
}

// src/desktop/DesktopCapabilityList.tsx
import { jsx as jsx28, jsxs as jsxs26 } from "react/jsx-runtime";
var DEFAULT_DESKTOP_CAPABILITIES = [
  "open_settings",
  "open_runtime_session",
  "show_logs",
  "show_diagnostics",
  "backup_preview",
  "restore_preview",
  "vps_status_preview",
  "remote_backend_status_preview",
  "copy_debug_summary"
];
var BLOCKED_DESKTOP_ADMIN_ACTIONS = [
  "shell_execute",
  "secret_read",
  "database_direct_write",
  "service_restart",
  "restore_execute"
];
function DesktopCapabilityList({ capabilities }) {
  const resolvedCapabilities = capabilities && capabilities.length > 0 ? capabilities : DEFAULT_DESKTOP_CAPABILITIES;
  return /* @__PURE__ */ jsxs26("article", { style: { border: "1px solid #dfe7ef", borderRadius: "8px", padding: "12px", backgroundColor: "#ffffff" }, children: [
    /* @__PURE__ */ jsx28("h3", { style: { margin: "0 0 8px", fontSize: "15px", color: "#1f2933" }, children: "Desktop Capabilities" }),
    /* @__PURE__ */ jsx28("div", { style: { display: "flex", flexWrap: "wrap", gap: "6px" }, children: resolvedCapabilities.map((capability) => /* @__PURE__ */ jsx28("span", { style: { backgroundColor: "#eef6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: "5px", padding: "4px 8px", fontSize: "12px" }, children: capability }, capability)) }),
    /* @__PURE__ */ jsx28("h4", { style: { margin: "12px 0 6px", fontSize: "13px", color: "#7f1d1d" }, children: "Blocked dangerous admin actions" }),
    /* @__PURE__ */ jsx28("div", { style: { display: "flex", flexWrap: "wrap", gap: "6px" }, children: BLOCKED_DESKTOP_ADMIN_ACTIONS.map((action) => /* @__PURE__ */ jsx28("span", { style: { backgroundColor: "#fff1f2", color: "#b42318", border: "1px solid #fecdd3", borderRadius: "5px", padding: "4px 8px", fontSize: "12px" }, children: `${action} blocked` }, action)) })
  ] });
}

// src/desktop/DesktopDiagnosticsPanel.tsx
import { jsx as jsx29, jsxs as jsxs27 } from "react/jsx-runtime";
function DesktopDiagnosticsPanel({
  appId,
  appName,
  connectionStatus,
  capabilityCount,
  warningCount,
  errorCount,
  readOnly = true,
  onPreviewDiagnostics,
  onPreviewBackup
}) {
  return /* @__PURE__ */ jsxs27("article", { style: { border: "1px solid #dfe7ef", borderRadius: "8px", padding: "12px", backgroundColor: "#ffffff" }, children: [
    /* @__PURE__ */ jsx29("h3", { style: { margin: "0 0 8px", fontSize: "15px", color: "#1f2933" }, children: "Diagnostics Preview" }),
    /* @__PURE__ */ jsxs27("dl", { style: { margin: 0, display: "grid", gridTemplateColumns: "minmax(120px, auto) 1fr", gap: "6px 10px", fontSize: "13px" }, children: [
      /* @__PURE__ */ jsx29("dt", { style: { fontWeight: 700 }, children: "App ID" }),
      /* @__PURE__ */ jsx29("dd", { style: { margin: 0 }, children: appId }),
      /* @__PURE__ */ jsx29("dt", { style: { fontWeight: 700 }, children: "App name" }),
      /* @__PURE__ */ jsx29("dd", { style: { margin: 0 }, children: appName }),
      /* @__PURE__ */ jsx29("dt", { style: { fontWeight: 700 }, children: "Connection status" }),
      /* @__PURE__ */ jsx29("dd", { style: { margin: 0 }, children: connectionStatus }),
      /* @__PURE__ */ jsx29("dt", { style: { fontWeight: 700 }, children: "Capability count" }),
      /* @__PURE__ */ jsx29("dd", { style: { margin: 0 }, children: capabilityCount }),
      /* @__PURE__ */ jsx29("dt", { style: { fontWeight: 700 }, children: "Warning count" }),
      /* @__PURE__ */ jsx29("dd", { style: { margin: 0 }, children: warningCount }),
      /* @__PURE__ */ jsx29("dt", { style: { fontWeight: 700 }, children: "Error count" }),
      /* @__PURE__ */ jsx29("dd", { style: { margin: 0 }, children: errorCount })
    ] }),
    /* @__PURE__ */ jsx29("p", { style: { margin: "10px 0", color: "#6b7280", fontSize: "12px" }, children: "Preview-only diagnostics: no logs fetched and no file read." }),
    /* @__PURE__ */ jsxs27("div", { style: { display: "flex", gap: "8px", flexWrap: "wrap" }, children: [
      /* @__PURE__ */ jsx29(
        "button",
        {
          type: "button",
          disabled: readOnly,
          onClick: () => onPreviewDiagnostics?.(),
          style: { border: "1px solid #c8d3e0", borderRadius: "6px", padding: "6px 10px", backgroundColor: "#f8fafc", cursor: readOnly ? "not-allowed" : "pointer" },
          children: "Preview diagnostics"
        }
      ),
      /* @__PURE__ */ jsx29(
        "button",
        {
          type: "button",
          disabled: readOnly,
          onClick: () => onPreviewBackup?.(),
          style: { border: "1px solid #c8d3e0", borderRadius: "6px", padding: "6px 10px", backgroundColor: "#f8fafc", cursor: readOnly ? "not-allowed" : "pointer" },
          children: "Preview backup"
        }
      )
    ] })
  ] });
}

// src/desktop/DesktopBridgeEmptyState.tsx
import { jsx as jsx30, jsxs as jsxs28 } from "react/jsx-runtime";
function DesktopBridgeEmptyState() {
  return /* @__PURE__ */ jsxs28(
    "section",
    {
      className: "smart-shikka-desktop-empty-state",
      style: {
        border: "1px dashed #c8d3e0",
        borderRadius: "8px",
        padding: "18px",
        backgroundColor: "#f8fafc",
        color: "#3d4b5c",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      },
      children: [
        /* @__PURE__ */ jsx30("h2", { style: { margin: "0 0 6px", color: "#1f2933", fontSize: "18px" }, children: "Desktop bridge preview not configured" }),
        /* @__PURE__ */ jsx30("p", { style: { margin: 0, fontSize: "13px" }, children: "Provide an app adapter or bridge plan to preview Smart Shikka Desktop connection metadata." })
      ]
    }
  );
}

// src/desktop/DesktopConnectionPanel.tsx
import { jsx as jsx31, jsxs as jsxs29 } from "react/jsx-runtime";
var panelStyle = {
  border: "1px solid #d7dde5",
  borderRadius: "8px",
  backgroundColor: "#ffffff",
  color: "#1f2933",
  padding: "18px",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
};
var sectionGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "14px",
  alignItems: "start"
};
function statusText(status) {
  return status || "unknown";
}
function DesktopConnectionPanel({
  appAdapter,
  bridgePlan,
  connectionMode,
  readOnly = true,
  onConnectionModeChange,
  onPreviewDiagnostics,
  onPreviewBackup
}) {
  const resolvedMode = connectionMode || bridgePlan?.connectionMode || "local_demo";
  const appId = bridgePlan?.appId || appAdapter?.appId || "smart-shikka-desktop";
  const appName = bridgePlan?.appName || appAdapter?.appName || "Smart Shikka Desktop";
  const status = statusText(bridgePlan?.connectionStatus);
  const warnings = bridgePlan?.warnings || [];
  const errors = bridgePlan?.errors || [];
  if (!bridgePlan && !appAdapter) {
    return /* @__PURE__ */ jsx31(DesktopBridgeEmptyState, {});
  }
  return /* @__PURE__ */ jsxs29("section", { className: "smart-shikka-desktop-connection-panel", style: panelStyle, children: [
    /* @__PURE__ */ jsxs29("header", { style: { display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "16px" }, children: [
      /* @__PURE__ */ jsxs29("div", { children: [
        /* @__PURE__ */ jsx31("h2", { style: { margin: "0 0 4px", fontSize: "20px", color: "#111827" }, children: "Desktop Connection" }),
        /* @__PURE__ */ jsxs29("p", { style: { margin: 0, color: "#5f6b7a", fontSize: "13px" }, children: [
          appName,
          " (",
          appId,
          ") preview-only connection shell"
        ] })
      ] }),
      /* @__PURE__ */ jsx31(
        "span",
        {
          style: {
            alignSelf: "start",
            border: "1px solid #c8d3e0",
            borderRadius: "999px",
            padding: "4px 10px",
            fontSize: "12px",
            color: "#334e68",
            backgroundColor: "#f3f7fb"
          },
          children: status
        }
      )
    ] }),
    /* @__PURE__ */ jsx31(
      ConnectionModeSelector,
      {
        selectedMode: resolvedMode,
        readOnly,
        onConnectionModeChange
      }
    ),
    /* @__PURE__ */ jsxs29("div", { style: { ...sectionGridStyle, marginTop: "14px" }, children: [
      /* @__PURE__ */ jsx31(RemoteBackendStatus, { mode: resolvedMode, backend: bridgePlan?.remoteTarget }),
      /* @__PURE__ */ jsx31(VpsConnectionStatus, { mode: resolvedMode, tunnel: bridgePlan?.vpsPlan })
    ] }),
    /* @__PURE__ */ jsxs29("div", { style: { ...sectionGridStyle, marginTop: "14px" }, children: [
      /* @__PURE__ */ jsx31(DesktopCapabilityList, { capabilities: bridgePlan?.capabilities }),
      /* @__PURE__ */ jsx31(
        DesktopDiagnosticsPanel,
        {
          appId,
          appName,
          connectionStatus: status,
          capabilityCount: (bridgePlan?.capabilities || []).length,
          warningCount: warnings.length,
          errorCount: errors.length,
          onPreviewDiagnostics,
          onPreviewBackup,
          readOnly
        }
      )
    ] }),
    (warnings.length > 0 || errors.length > 0) && /* @__PURE__ */ jsxs29("div", { style: { marginTop: "14px", display: "grid", gap: "8px" }, children: [
      warnings.map((warning) => /* @__PURE__ */ jsxs29("div", { style: { border: "1px solid #ffe8a3", backgroundColor: "#fff9db", borderRadius: "6px", padding: "8px", fontSize: "13px" }, children: [
        "Warning: ",
        warning
      ] }, warning)),
      errors.map((error) => /* @__PURE__ */ jsxs29("div", { style: { border: "1px solid #ffc9c9", backgroundColor: "#fff5f5", borderRadius: "6px", padding: "8px", fontSize: "13px" }, children: [
        "Error: ",
        error
      ] }, error))
    ] })
  ] });
}

// src/hooks/useSmartShikkaSettings.ts
import { useMemo as useMemo7 } from "react";
import {
  createProviderRegistry,
  createKnowledgeSourceRegistry,
  createToolRegistry,
  getProviderRuntimeSummary
} from "@smart-shikka/core";
function useSmartShikkaSettings({ appAdapter, appSettings }) {
  const resolvedSettings = useMemo7(() => {
    return appSettings || appAdapter?.appSettings || {};
  }, [appSettings, appAdapter]);
  const providerRegistry = useMemo7(() => {
    return createProviderRegistry(resolvedSettings.providers || []);
  }, [resolvedSettings.providers]);
  const knowledgeRegistry = useMemo7(() => {
    return createKnowledgeSourceRegistry(resolvedSettings.knowledgeSources || []);
  }, [resolvedSettings.knowledgeSources]);
  const toolRegistry = useMemo7(() => {
    return createToolRegistry(resolvedSettings.integrations || []);
  }, [resolvedSettings.integrations]);
  const providerSummary = useMemo7(() => {
    if (!resolvedSettings.providers) return void 0;
    return getProviderRuntimeSummary(resolvedSettings);
  }, [resolvedSettings]);
  return {
    providers: providerRegistry.listProviders(),
    knowledgeSources: knowledgeRegistry.listKnowledgeSources(),
    tools: toolRegistry.listTools(),
    integrations: toolRegistry.listTools(),
    providerSummary,
    appAdapter,
    appSettings: resolvedSettings,
    validateAll: () => {
      const pVal = providerRegistry.validateProviderRegistry();
      const kVal = knowledgeRegistry.validateKnowledgeSourceRegistry();
      const tVal = toolRegistry.validateToolRegistry();
      return {
        valid: pVal.valid && kVal.ok && tVal.valid,
        errors: [...pVal.errors, ...kVal.errors, ...tVal.errors],
        warnings: [...pVal.warnings, ...kVal.warnings, ...tVal.warnings]
      };
    }
  };
}

// src/agentops/AgentOpsInventoryPanel.tsx
import { jsx as jsx32, jsxs as jsxs30 } from "react/jsx-runtime";
var cardStyle2 = {
  backgroundColor: "#1e293b",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "16px",
  color: "#f8fafc"
};
var metricStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: "6px",
  padding: "12px",
  textAlign: "center"
};
var numStyle = {
  fontSize: "24px",
  fontWeight: 700,
  color: "#38bdf8"
};
function AgentOpsInventoryPanel({ inventory }) {
  const appsCount = inventory?.apps?.length ?? 0;
  const servicesCount = inventory?.services?.length ?? 0;
  const vpsCount = inventory?.vpsServers?.length ?? 0;
  const agentsCount = inventory?.agents?.length ?? 0;
  const warnings = inventory?.warnings ?? [];
  return /* @__PURE__ */ jsxs30("section", { style: cardStyle2, "aria-label": "Inventory Overview", children: [
    /* @__PURE__ */ jsx32("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: "Inventory Overview" }),
    /* @__PURE__ */ jsxs30("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "10px" }, children: [
      /* @__PURE__ */ jsxs30("div", { style: metricStyle, children: [
        /* @__PURE__ */ jsx32("span", { style: numStyle, children: appsCount }),
        /* @__PURE__ */ jsx32("span", { style: { fontSize: "12px", color: "#94a3b8", marginTop: "4px" }, children: "Apps" })
      ] }),
      /* @__PURE__ */ jsxs30("div", { style: metricStyle, children: [
        /* @__PURE__ */ jsx32("span", { style: numStyle, children: servicesCount }),
        /* @__PURE__ */ jsx32("span", { style: { fontSize: "12px", color: "#94a3b8", marginTop: "4px" }, children: "Services" })
      ] }),
      /* @__PURE__ */ jsxs30("div", { style: metricStyle, children: [
        /* @__PURE__ */ jsx32("span", { style: numStyle, children: vpsCount }),
        /* @__PURE__ */ jsx32("span", { style: { fontSize: "12px", color: "#94a3b8", marginTop: "4px" }, children: "VPS Servers" })
      ] }),
      /* @__PURE__ */ jsxs30("div", { style: metricStyle, children: [
        /* @__PURE__ */ jsx32("span", { style: numStyle, children: agentsCount }),
        /* @__PURE__ */ jsx32("span", { style: { fontSize: "12px", color: "#94a3b8", marginTop: "4px" }, children: "Agents" })
      ] })
    ] }),
    warnings.length > 0 && /* @__PURE__ */ jsxs30("div", { style: { marginTop: "12px", padding: "10px", backgroundColor: "#451a03", border: "1px solid #7c2d12", borderRadius: "6px", color: "#fed7aa", fontSize: "12px" }, children: [
      /* @__PURE__ */ jsx32("strong", { children: "Warnings:" }),
      /* @__PURE__ */ jsx32("ul", { style: { margin: "4px 0 0", paddingLeft: "16px" }, children: warnings.map((w2, index) => /* @__PURE__ */ jsx32("li", { children: w2 }, index)) })
    ] })
  ] });
}

// src/agentops/AgentOpsStatusBadge.tsx
import { jsx as jsx33 } from "react/jsx-runtime";
function AgentOpsStatusBadge({ status }) {
  let backgroundColor = "#334155";
  let color = "#cbd5e1";
  const s = status.toLowerCase();
  if (s === "blocked" || s === "critical" || s === "rejected" || s === "dangerous_blocked") {
    backgroundColor = "#7f1d1d";
    color = "#fecaca";
  } else if (s === "approval_required" || s === "high" || s === "waiting_for_approval") {
    backgroundColor = "#7c2d12";
    color = "#ffedd5";
  } else if (s === "medium" || s === "dry_run") {
    backgroundColor = "#713f12";
    color = "#fef9c3";
  } else if (s === "read_only" || s === "safe_preview" || s === "completed_preview") {
    backgroundColor = "#064e3b";
    color = "#d1fae5";
  } else if (s === "metadata_only" || s === "low" || s === "queued") {
    backgroundColor = "#1e3a8a";
    color = "#dbeafe";
  } else if (s === "approved_for_preview") {
    backgroundColor = "#14532d";
    color = "#bbf7d0";
  }
  return /* @__PURE__ */ jsx33(
    "span",
    {
      style: {
        display: "inline-block",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "11px",
        fontWeight: 600,
        backgroundColor,
        color,
        textTransform: "uppercase",
        letterSpacing: "0.05em"
      },
      children: status.replace(/_/g, " ")
    }
  );
}

// src/agentops/AgentOpsAgentsPanel.tsx
import { jsx as jsx34, jsxs as jsxs31 } from "react/jsx-runtime";
var cardStyle3 = {
  backgroundColor: "#1e293b",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "16px",
  color: "#f8fafc",
  marginTop: "16px"
};
var agentCardStyle = {
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: "6px",
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  gap: "8px"
};
function AgentOpsAgentsPanel({ managedAgents }) {
  const agents = managedAgents ?? [];
  return /* @__PURE__ */ jsxs31("section", { style: cardStyle3, "aria-label": "Managed Agents", children: [
    /* @__PURE__ */ jsxs31("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: [
      "Managed Agents (",
      agents.length,
      ")"
    ] }),
    agents.length === 0 ? /* @__PURE__ */ jsx34("p", { style: { color: "#94a3b8", margin: 0, fontSize: "14px" }, children: "No agents available." }) : /* @__PURE__ */ jsx34("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "12px" }, children: agents.map((agent) => {
      const risk = agent.capabilities?.[0]?.risk ?? "safe_preview";
      const blockedActionsCount = agent.capabilities?.[0]?.blockedActions?.length ?? 0;
      return /* @__PURE__ */ jsxs31("div", { style: agentCardStyle, className: "agent-profile-card", children: [
        /* @__PURE__ */ jsxs31("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [
          /* @__PURE__ */ jsx34("strong", { style: { fontSize: "14px", color: "#f1f5f9" }, children: agent.displayName }),
          /* @__PURE__ */ jsx34(
            "span",
            {
              style: {
                fontSize: "11px",
                backgroundColor: "#1e3a8a",
                color: "#93c5fd",
                padding: "2px 6px",
                borderRadius: "3px",
                fontWeight: 600
              },
              children: "PREVIEW ONLY"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs31("div", { style: { display: "flex", flexWrap: "wrap", gap: "6px", fontSize: "12px", color: "#94a3b8" }, children: [
          /* @__PURE__ */ jsxs31("span", { children: [
            "Type: ",
            /* @__PURE__ */ jsx34("code", { style: { color: "#38bdf8" }, children: agent.agentType })
          ] }),
          /* @__PURE__ */ jsx34("span", { children: "|" }),
          /* @__PURE__ */ jsxs31("span", { children: [
            "Blocked: ",
            /* @__PURE__ */ jsx34("strong", { style: { color: "#ef4444" }, children: blockedActionsCount })
          ] })
        ] }),
        /* @__PURE__ */ jsxs31("div", { style: { display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px" }, children: [
          /* @__PURE__ */ jsx34(AgentOpsStatusBadge, { status: agent.defaultPolicy }),
          /* @__PURE__ */ jsx34(AgentOpsStatusBadge, { status: risk })
        ] })
      ] }, agent.agentId);
    }) })
  ] });
}

// src/agentops/CeoPlanPreviewPanel.tsx
import { jsx as jsx35, jsxs as jsxs32 } from "react/jsx-runtime";
var cardStyle4 = {
  backgroundColor: "#1e293b",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "16px",
  color: "#f8fafc",
  marginTop: "16px"
};
var metaGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: "12px",
  marginTop: "12px",
  backgroundColor: "#0f172a",
  padding: "12px",
  borderRadius: "6px",
  border: "1px solid #1e293b"
};
function CeoPlanPreviewPanel({ ceoPlan }) {
  if (!ceoPlan) {
    return /* @__PURE__ */ jsxs32("section", { style: cardStyle4, "aria-label": "CEO Plan Preview", children: [
      /* @__PURE__ */ jsx35("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: "CEO Plan Preview" }),
      /* @__PURE__ */ jsx35("p", { style: { color: "#94a3b8", margin: 0, fontSize: "14px" }, children: "No CEO Plan details available." })
    ] });
  }
  const apps = ceoPlan.recommendedAppRefs ?? [];
  const services = ceoPlan.recommendedServiceRefs ?? [];
  const agents = ceoPlan.recommendedAgentRefs ?? [];
  const blockedActions = ceoPlan.blockedActions ?? [];
  return /* @__PURE__ */ jsxs32("section", { style: cardStyle4, "aria-label": "CEO Plan Preview", children: [
    /* @__PURE__ */ jsxs32("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }, children: [
      /* @__PURE__ */ jsxs32("h3", { style: { margin: 0, fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: [
        "CEO Plan Preview (",
        ceoPlan.taskId,
        ")"
      ] }),
      /* @__PURE__ */ jsxs32("div", { style: { display: "flex", gap: "6px" }, children: [
        /* @__PURE__ */ jsx35(AgentOpsStatusBadge, { status: ceoPlan.risk }),
        /* @__PURE__ */ jsx35(AgentOpsStatusBadge, { status: ceoPlan.policy })
      ] })
    ] }),
    /* @__PURE__ */ jsxs32("div", { style: metaGridStyle, children: [
      /* @__PURE__ */ jsxs32("div", { children: [
        /* @__PURE__ */ jsx35("strong", { style: { fontSize: "12px", color: "#94a3b8" }, children: "Intent" }),
        /* @__PURE__ */ jsx35("p", { style: { margin: "4px 0 0", fontSize: "14px", fontWeight: 600, color: "#38bdf8" }, children: ceoPlan.intent })
      ] }),
      /* @__PURE__ */ jsxs32("div", { children: [
        /* @__PURE__ */ jsx35("strong", { style: { fontSize: "12px", color: "#94a3b8" }, children: "Human Approval Required" }),
        /* @__PURE__ */ jsx35("p", { style: { margin: "4px 0 0", fontSize: "14px", fontWeight: 600, color: ceoPlan.recommendedHumanApproval ? "#f43f5e" : "#10b981" }, children: ceoPlan.recommendedHumanApproval ? "YES" : "NO" })
      ] }),
      /* @__PURE__ */ jsxs32("div", { children: [
        /* @__PURE__ */ jsx35("strong", { style: { fontSize: "12px", color: "#94a3b8" }, children: "Recommended Apps" }),
        /* @__PURE__ */ jsx35("p", { style: { margin: "4px 0 0", fontSize: "14px" }, children: apps.length > 0 ? apps.join(", ") : "None" })
      ] }),
      /* @__PURE__ */ jsxs32("div", { children: [
        /* @__PURE__ */ jsx35("strong", { style: { fontSize: "12px", color: "#94a3b8" }, children: "Recommended Services" }),
        /* @__PURE__ */ jsx35("p", { style: { margin: "4px 0 0", fontSize: "14px" }, children: services.length > 0 ? services.join(", ") : "None" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs32("div", { style: { marginTop: "12px" }, children: [
      /* @__PURE__ */ jsx35("strong", { style: { fontSize: "12px", color: "#94a3b8" }, children: "Recommended Agents" }),
      /* @__PURE__ */ jsx35("p", { style: { margin: "4px 0 0", fontSize: "14px" }, children: agents.length > 0 ? agents.join(", ") : "None" })
    ] }),
    blockedActions.length > 0 && /* @__PURE__ */ jsxs32("div", { style: { marginTop: "12px" }, children: [
      /* @__PURE__ */ jsx35("strong", { style: { fontSize: "12px", color: "#f43f5e" }, children: "Blocked Actions" }),
      /* @__PURE__ */ jsx35("p", { style: { margin: "4px 0 0", fontSize: "14px", color: "#fca5a5" }, children: blockedActions.join(", ") })
    ] }),
    ceoPlan.explanationHindi && /* @__PURE__ */ jsxs32("div", { style: { marginTop: "12px", padding: "12px", backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "6px" }, children: [
      /* @__PURE__ */ jsx35("strong", { style: { fontSize: "12px", color: "#34d399", display: "block", marginBottom: "4px" }, children: "\u0939\u093F\u0902\u0926\u0940 \u0938\u094D\u092A\u0937\u094D\u091F\u0940\u0915\u0930\u0923 (Hindi Explanation):" }),
      /* @__PURE__ */ jsx35("pre", { style: { whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit", fontSize: "13px", color: "#cbd5e1", lineHeight: 1.5 }, children: ceoPlan.explanationHindi })
    ] })
  ] });
}

// src/agentops/TaskQueuePreviewPanel.tsx
import { jsx as jsx36, jsxs as jsxs33 } from "react/jsx-runtime";
var cardStyle5 = {
  backgroundColor: "#1e293b",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "16px",
  color: "#f8fafc",
  marginTop: "16px"
};
var taskRowStyle = {
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: "6px",
  padding: "10px 12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "10px",
  marginBottom: "8px"
};
function TaskQueuePreviewPanel({ tasks }) {
  const taskList = tasks ?? [];
  const counts = {
    queued: taskList.filter((t5) => t5.status === "queued").length,
    waiting_for_approval: taskList.filter((t5) => t5.status === "waiting_for_approval").length,
    blocked: taskList.filter((t5) => t5.status === "blocked").length,
    completed_preview: taskList.filter((t5) => t5.status === "completed_preview").length,
    cancelled: taskList.filter((t5) => t5.status === "cancelled").length
  };
  return /* @__PURE__ */ jsxs33("section", { style: cardStyle5, "aria-label": "Task Queue Preview", children: [
    /* @__PURE__ */ jsxs33("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: [
      "Task Queue Preview (",
      taskList.length,
      ")"
    ] }),
    /* @__PURE__ */ jsxs33(
      "div",
      {
        style: {
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "16px",
          paddingBottom: "12px",
          borderBottom: "1px solid #334155"
        },
        children: [
          /* @__PURE__ */ jsxs33("div", { style: { fontSize: "12px" }, children: [
            "Queued: ",
            /* @__PURE__ */ jsx36("strong", { style: { color: "#38bdf8" }, children: counts.queued })
          ] }),
          /* @__PURE__ */ jsxs33("div", { style: { fontSize: "12px" }, children: [
            "Waiting: ",
            /* @__PURE__ */ jsx36("strong", { style: { color: "#fb923c" }, children: counts.waiting_for_approval })
          ] }),
          /* @__PURE__ */ jsxs33("div", { style: { fontSize: "12px" }, children: [
            "Blocked: ",
            /* @__PURE__ */ jsx36("strong", { style: { color: "#f87171" }, children: counts.blocked })
          ] }),
          /* @__PURE__ */ jsxs33("div", { style: { fontSize: "12px" }, children: [
            "Completed Previews: ",
            /* @__PURE__ */ jsx36("strong", { style: { color: "#34d399" }, children: counts.completed_preview })
          ] }),
          /* @__PURE__ */ jsxs33("div", { style: { fontSize: "12px" }, children: [
            "Cancelled: ",
            /* @__PURE__ */ jsx36("strong", { style: { color: "#94a3b8" }, children: counts.cancelled })
          ] })
        ]
      }
    ),
    taskList.length === 0 ? /* @__PURE__ */ jsx36("p", { style: { color: "#94a3b8", margin: 0, fontSize: "14px" }, children: "No tasks in the queue." }) : /* @__PURE__ */ jsx36("div", { style: { maxHeight: "240px", overflowY: "auto" }, children: taskList.map((task) => /* @__PURE__ */ jsxs33("div", { style: taskRowStyle, children: [
      /* @__PURE__ */ jsxs33("div", { style: { display: "flex", flexDirection: "column", gap: "2px" }, children: [
        /* @__PURE__ */ jsx36("span", { style: { fontSize: "13px", fontWeight: 600, color: "#f1f5f9" }, children: task.taskText }),
        /* @__PURE__ */ jsxs33("span", { style: { fontSize: "11px", color: "#94a3b8" }, children: [
          "ID: ",
          task.id,
          " | Intent: ",
          task.intent,
          " | Risk: ",
          task.risk
        ] })
      ] }),
      /* @__PURE__ */ jsx36(AgentOpsStatusBadge, { status: task.status })
    ] }, task.id)) })
  ] });
}

// src/agentops/ApprovalQueuePanel.tsx
import { jsx as jsx37, jsxs as jsxs34 } from "react/jsx-runtime";
var cardStyle6 = {
  backgroundColor: "#1e293b",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "16px",
  color: "#f8fafc",
  marginTop: "16px"
};
var approvalRowStyle = {
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: "6px",
  padding: "10px 12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "10px",
  marginBottom: "8px"
};
function ApprovalQueuePanel({ approvals }) {
  const approvalList = approvals ?? [];
  return /* @__PURE__ */ jsxs34("section", { style: cardStyle6, "aria-label": "Approval Queue", children: [
    /* @__PURE__ */ jsxs34("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: [
      "Approval Queue (",
      approvalList.length,
      ")"
    ] }),
    approvalList.length === 0 ? /* @__PURE__ */ jsx37("p", { style: { color: "#94a3b8", margin: 0, fontSize: "14px" }, children: "No approval requests available." }) : /* @__PURE__ */ jsx37("div", { style: { maxHeight: "240px", overflowY: "auto" }, children: approvalList.map((appr) => /* @__PURE__ */ jsxs34("div", { style: approvalRowStyle, className: "approval-row", children: [
      /* @__PURE__ */ jsxs34("div", { style: { display: "flex", flexDirection: "column", gap: "2px" }, children: [
        /* @__PURE__ */ jsxs34("span", { style: { fontSize: "13px", fontWeight: 600, color: "#f1f5f9" }, children: [
          "Task Ref: ",
          appr.taskId
        ] }),
        /* @__PURE__ */ jsxs34("span", { style: { fontSize: "11px", color: "#94a3b8" }, children: [
          "Req ID: ",
          appr.id,
          " | By: ",
          appr.requestedByRole,
          " ",
          appr.notes ? `| Notes: ${appr.notes}` : ""
        ] })
      ] }),
      /* @__PURE__ */ jsx37(AgentOpsStatusBadge, { status: appr.status })
    ] }, appr.id)) })
  ] });
}

// src/agentops/HealthPreviewPanel.tsx
import { jsx as jsx38, jsxs as jsxs35 } from "react/jsx-runtime";
var cardStyle7 = {
  backgroundColor: "#1e293b",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "16px",
  color: "#f8fafc",
  marginTop: "16px"
};
var statBoxStyle = {
  backgroundColor: "#0b0f19",
  border: "1px solid #1e293b",
  borderRadius: "6px",
  padding: "10px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center"
};
function HealthPreviewPanel({ healthSnapshot }) {
  return /* @__PURE__ */ jsxs35("section", { style: cardStyle7, "aria-label": "Health Preview", children: [
    /* @__PURE__ */ jsx38("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: "Health Preview" }),
    healthSnapshot ? /* @__PURE__ */ jsxs35("div", { style: { backgroundColor: "#0f172a", border: "1px solid #1e293b", borderRadius: "6px", padding: "16px" }, children: [
      /* @__PURE__ */ jsxs35("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", borderBottom: "1px solid #1e293b", paddingBottom: "12px", flexWrap: "wrap", gap: "8px" }, children: [
        /* @__PURE__ */ jsxs35("span", { style: { fontSize: "13px", color: "#94a3b8" }, children: [
          "Snapshot ID: ",
          /* @__PURE__ */ jsx38("strong", { style: { color: "#f8fafc" }, children: healthSnapshot.snapshotId })
        ] }),
        /* @__PURE__ */ jsxs35("span", { style: { fontSize: "12px", color: "#64748b" }, className: "generated-at", children: [
          "Generated: ",
          healthSnapshot.generatedAt
        ] })
      ] }),
      /* @__PURE__ */ jsxs35("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: "10px", marginBottom: "16px" }, children: [
        /* @__PURE__ */ jsxs35("div", { style: statBoxStyle, children: [
          /* @__PURE__ */ jsx38("span", { style: { fontSize: "11px", color: "#94a3b8" }, children: "Total Signals" }),
          /* @__PURE__ */ jsx38("strong", { style: { fontSize: "20px", color: "#38bdf8" }, children: healthSnapshot.summary.totalSignals })
        ] }),
        /* @__PURE__ */ jsxs35("div", { style: statBoxStyle, children: [
          /* @__PURE__ */ jsx38("span", { style: { fontSize: "11px", color: "#10b981" }, children: "Healthy" }),
          /* @__PURE__ */ jsx38("strong", { style: { fontSize: "20px", color: "#10b981" }, children: healthSnapshot.summary.healthyCount })
        ] }),
        /* @__PURE__ */ jsxs35("div", { style: statBoxStyle, children: [
          /* @__PURE__ */ jsx38("span", { style: { fontSize: "11px", color: "#f59e0b" }, children: "Degraded" }),
          /* @__PURE__ */ jsx38("strong", { style: { fontSize: "20px", color: "#f59e0b" }, children: healthSnapshot.summary.degradedCount })
        ] }),
        /* @__PURE__ */ jsxs35("div", { style: statBoxStyle, children: [
          /* @__PURE__ */ jsx38("span", { style: { fontSize: "11px", color: "#ef4444" }, children: "Down" }),
          /* @__PURE__ */ jsx38("strong", { style: { fontSize: "20px", color: "#ef4444" }, children: healthSnapshot.summary.downCount })
        ] }),
        /* @__PURE__ */ jsxs35("div", { style: statBoxStyle, children: [
          /* @__PURE__ */ jsx38("span", { style: { fontSize: "11px", color: "#94a3b8" }, children: "Unknown" }),
          /* @__PURE__ */ jsx38("strong", { style: { fontSize: "20px", color: "#64748b" }, children: healthSnapshot.summary.unknownCount })
        ] })
      ] }),
      /* @__PURE__ */ jsxs35("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px", borderTop: "1px solid #1e293b", paddingTop: "12px" }, children: [
        /* @__PURE__ */ jsxs35("div", { style: { fontSize: "12px", color: "#cbd5e1" }, children: [
          "Critical Warnings: ",
          /* @__PURE__ */ jsx38("strong", { style: { color: healthSnapshot.summary.criticalCount > 0 ? "#ef4444" : "#10b981" }, children: healthSnapshot.summary.criticalCount })
        ] }),
        /* @__PURE__ */ jsxs35("div", { style: { fontSize: "12px", color: "#cbd5e1" }, children: [
          "Standard Warnings: ",
          /* @__PURE__ */ jsx38("strong", { style: { color: healthSnapshot.summary.warningCount > 0 ? "#f59e0b" : "#10b981" }, children: healthSnapshot.summary.warningCount })
        ] }),
        /* @__PURE__ */ jsxs35("div", { style: { fontSize: "12px", color: "#cbd5e1" }, children: [
          "Blocked Actions: ",
          /* @__PURE__ */ jsx38("strong", { style: { color: healthSnapshot.summary.blockedActionCount > 0 ? "#f43f5e" : "#10b981" }, children: healthSnapshot.summary.blockedActionCount })
        ] })
      ] }),
      healthSnapshot.warnings && healthSnapshot.warnings.length > 0 && /* @__PURE__ */ jsxs35("div", { style: { marginTop: "12px", padding: "10px", backgroundColor: "#451a03", border: "1px solid #7c2d12", borderRadius: "6px", color: "#fed7aa", fontSize: "12px" }, className: "health-warnings", children: [
        /* @__PURE__ */ jsx38("strong", { children: "Snapshot Warnings:" }),
        /* @__PURE__ */ jsx38("ul", { style: { margin: "4px 0 0", paddingLeft: "16px" }, children: healthSnapshot.warnings.map((w2, index) => /* @__PURE__ */ jsx38("li", { children: w2 }, index)) })
      ] })
    ] }) : /* @__PURE__ */ jsx38("p", { style: { color: "#94a3b8", margin: 0, fontSize: "14px", fontStyle: "italic" }, className: "health-placeholder", children: "Health preview will be available after AgentOps Health contract." })
  ] });
}

// src/agentops/AgentOpsEmptyState.tsx
import { jsx as jsx39 } from "react/jsx-runtime";
var emptyStyle = {
  color: "#94a3b8",
  margin: 0,
  fontSize: "14px",
  fontStyle: "italic",
  padding: "12px",
  backgroundColor: "#0f172a",
  border: "1px dashed #334155",
  borderRadius: "6px",
  textAlign: "center"
};
function AgentOpsEmptyState({ message }) {
  return /* @__PURE__ */ jsx39("div", { style: emptyStyle, className: "empty-state", children: message });
}

// src/agentops/ConnectorRegistryPanel.tsx
import { jsx as jsx40, jsxs as jsxs36 } from "react/jsx-runtime";
var cardStyle8 = {
  backgroundColor: "#1e293b",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "16px",
  color: "#f8fafc"
};
var metricStyle2 = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: "6px",
  padding: "12px",
  textAlign: "center"
};
var numStyle2 = {
  fontSize: "24px",
  fontWeight: 700,
  color: "#38bdf8"
};
var connectorRowStyle = {
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: "6px",
  padding: "12px",
  marginBottom: "8px",
  display: "flex",
  flexDirection: "column",
  gap: "6px"
};
function ConnectorRegistryPanel({ connectorRegistry }) {
  if (!connectorRegistry || !connectorRegistry.connectors || connectorRegistry.connectors.length === 0) {
    return /* @__PURE__ */ jsxs36("section", { style: cardStyle8, "aria-label": "Connector Registry", children: [
      /* @__PURE__ */ jsx40("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: "Connector Registry Overview" }),
      /* @__PURE__ */ jsx40(AgentOpsEmptyState, { message: "No connectors registered in AgentOps." })
    ] });
  }
  const { connectors } = connectorRegistry;
  const totalConnectors = connectors.length;
  const serverOnlyCount = connectors.filter((c) => c.runtimeBoundary === "server_only").length;
  const highRiskCount = connectors.filter((c) => c.risk === "high" || c.risk === "critical").length;
  const blockedOperationCount = connectors.reduce((acc, c) => acc + (c.blockedOperations?.length ?? 0), 0);
  const connectorsByKind = {};
  connectors.forEach((c) => {
    connectorsByKind[c.kind] = (connectorsByKind[c.kind] ?? 0) + 1;
  });
  return /* @__PURE__ */ jsxs36("section", { style: cardStyle8, "aria-label": "Connector Registry", children: [
    /* @__PURE__ */ jsxs36("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: [
      "Connector Registry Overview (",
      totalConnectors,
      ")"
    ] }),
    /* @__PURE__ */ jsxs36("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px", marginBottom: "16px" }, children: [
      /* @__PURE__ */ jsxs36("div", { style: metricStyle2, children: [
        /* @__PURE__ */ jsx40("span", { style: numStyle2, children: totalConnectors }),
        /* @__PURE__ */ jsx40("span", { style: { fontSize: "11px", color: "#94a3b8", marginTop: "4px" }, children: "Total Connectors" })
      ] }),
      /* @__PURE__ */ jsxs36("div", { style: metricStyle2, children: [
        /* @__PURE__ */ jsx40("span", { style: numStyle2, children: serverOnlyCount }),
        /* @__PURE__ */ jsx40("span", { style: { fontSize: "11px", color: "#10b981", marginTop: "4px" }, children: "Server-Only Boundary" })
      ] }),
      /* @__PURE__ */ jsxs36("div", { style: metricStyle2, children: [
        /* @__PURE__ */ jsx40("span", { style: { fontSize: "24px", fontWeight: 700, color: highRiskCount > 0 ? "#ef4444" : "#10b981" }, children: highRiskCount }),
        /* @__PURE__ */ jsx40("span", { style: { fontSize: "11px", color: "#94a3b8", marginTop: "4px" }, children: "High Risk Connectors" })
      ] }),
      /* @__PURE__ */ jsxs36("div", { style: metricStyle2, children: [
        /* @__PURE__ */ jsx40("span", { style: { fontSize: "24px", fontWeight: 700, color: blockedOperationCount > 0 ? "#f43f5e" : "#cbd5e1" }, children: blockedOperationCount }),
        /* @__PURE__ */ jsx40("span", { style: { fontSize: "11px", color: "#94a3b8", marginTop: "4px" }, children: "Blocked Ops" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs36("div", { style: { marginBottom: "16px", backgroundColor: "#0f172a", padding: "10px", borderRadius: "6px", border: "1px solid #1e293b" }, children: [
      /* @__PURE__ */ jsx40("span", { style: { fontSize: "12px", color: "#94a3b8", fontWeight: 600 }, children: "Connectors by Kind:" }),
      /* @__PURE__ */ jsx40("div", { style: { display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }, children: Object.entries(connectorsByKind).map(([kind, count]) => /* @__PURE__ */ jsxs36("span", { style: { fontSize: "11px", backgroundColor: "#1e293b", color: "#e2e8f0", padding: "2px 8px", borderRadius: "4px" }, children: [
        kind.replace(/_/g, " "),
        ": ",
        /* @__PURE__ */ jsx40("strong", { children: count })
      ] }, kind)) })
    ] }),
    connectorRegistry.warnings && connectorRegistry.warnings.length > 0 && /* @__PURE__ */ jsxs36("div", { style: { marginBottom: "16px", padding: "10px", backgroundColor: "#451a03", border: "1px solid #7c2d12", borderRadius: "6px", color: "#fed7aa", fontSize: "12px" }, children: [
      /* @__PURE__ */ jsx40("strong", { children: "Registry Warnings:" }),
      /* @__PURE__ */ jsx40("ul", { style: { margin: "4px 0 0", paddingLeft: "16px" }, children: connectorRegistry.warnings.map((w2, index) => /* @__PURE__ */ jsx40("li", { children: w2 }, index)) })
    ] }),
    /* @__PURE__ */ jsx40("div", { style: { maxHeight: "280px", overflowY: "auto" }, children: connectors.map((c) => /* @__PURE__ */ jsxs36("div", { style: connectorRowStyle, className: "connector-card", children: [
      /* @__PURE__ */ jsxs36("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [
        /* @__PURE__ */ jsx40("strong", { style: { fontSize: "14px", color: "#f1f5f9" }, children: c.displayName }),
        /* @__PURE__ */ jsxs36("div", { style: { display: "flex", gap: "6px" }, children: [
          /* @__PURE__ */ jsx40(AgentOpsStatusBadge, { status: c.runtimeBoundary }),
          /* @__PURE__ */ jsx40(AgentOpsStatusBadge, { status: c.risk })
        ] })
      ] }),
      /* @__PURE__ */ jsxs36("div", { style: { fontSize: "12px", color: "#94a3b8" }, children: [
        /* @__PURE__ */ jsxs36("span", { children: [
          "Target: ",
          /* @__PURE__ */ jsxs36("code", { style: { color: "#38bdf8" }, children: [
            c.targetKind,
            ":",
            c.targetRef
          ] })
        ] }),
        /* @__PURE__ */ jsx40("span", { style: { margin: "0 6px" }, children: "|" }),
        (() => {
          const displayCredMode = c.credentialMode === "secret_ref" ? "secured_ref" : c.credentialMode === "blocked_raw_secret" ? "raw_blocked" : c.credentialMode;
          return /* @__PURE__ */ jsxs36("span", { children: [
            "Credentials: ",
            /* @__PURE__ */ jsx40("code", { style: { color: "#34d399" }, children: displayCredMode })
          ] });
        })()
      ] }),
      /* @__PURE__ */ jsxs36("div", { style: { display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px", borderTop: "1px solid #1e293b", paddingTop: "6px" }, children: [
        /* @__PURE__ */ jsxs36("div", { style: { fontSize: "11px" }, children: [
          /* @__PURE__ */ jsx40("span", { style: { color: "#10b981", fontWeight: 600 }, children: "Allowed Preview Ops: " }),
          /* @__PURE__ */ jsx40("span", { style: { color: "#cbd5e1" }, children: c.allowedOperations.join(", ") || "None" })
        ] }),
        c.blockedOperations && c.blockedOperations.length > 0 && /* @__PURE__ */ jsxs36("div", { style: { fontSize: "11px" }, children: [
          /* @__PURE__ */ jsx40("span", { style: { color: "#ef4444", fontWeight: 600 }, children: "Blocked Operations: " }),
          /* @__PURE__ */ jsx40("span", { style: { color: "#fca5a5" }, children: c.blockedOperations.join(", ") })
        ] })
      ] })
    ] }, c.connectorId)) })
  ] });
}

// src/agentops/ConnectorRuntimePreviewPanel.tsx
import { jsx as jsx41, jsxs as jsxs37 } from "react/jsx-runtime";
var cardStyle9 = {
  backgroundColor: "#1e293b",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "16px",
  color: "#f8fafc",
  marginTop: "16px"
};
var planRowStyle = {
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: "6px",
  padding: "12px",
  marginBottom: "12px",
  display: "flex",
  flexDirection: "column",
  gap: "8px"
};
function ConnectorRuntimePreviewPanel({ connectorRuntimePlans }) {
  const plans = connectorRuntimePlans ?? [];
  if (plans.length === 0) {
    return /* @__PURE__ */ jsxs37("section", { style: cardStyle9, "aria-label": "Connector Runtime Preview", children: [
      /* @__PURE__ */ jsx41("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: "Connector Runtime Previews" }),
      /* @__PURE__ */ jsx41(AgentOpsEmptyState, { message: "No active connector runtime plans." })
    ] });
  }
  return /* @__PURE__ */ jsxs37("section", { style: cardStyle9, "aria-label": "Connector Runtime Preview", children: [
    /* @__PURE__ */ jsxs37("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: [
      "Connector Runtime Previews (",
      plans.length,
      ")"
    ] }),
    /* @__PURE__ */ jsx41("div", { style: { maxHeight: "360px", overflowY: "auto" }, children: plans.map((p) => /* @__PURE__ */ jsxs37("div", { style: planRowStyle, className: "connector-runtime-card", children: [
      /* @__PURE__ */ jsxs37("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }, children: [
        /* @__PURE__ */ jsx41("span", { style: { fontSize: "14px", fontWeight: 600, color: "#38bdf8" }, children: p.operationType.replace(/_/g, " ").toUpperCase() }),
        /* @__PURE__ */ jsxs37("div", { style: { display: "flex", gap: "6px" }, children: [
          /* @__PURE__ */ jsx41(AgentOpsStatusBadge, { status: p.risk }),
          /* @__PURE__ */ jsx41(AgentOpsStatusBadge, { status: p.policy })
        ] })
      ] }),
      /* @__PURE__ */ jsxs37("div", { style: { fontSize: "12px", color: "#94a3b8" }, children: [
        /* @__PURE__ */ jsxs37("span", { children: [
          "Connector ID: ",
          /* @__PURE__ */ jsx41("code", { children: p.connectorId })
        ] }),
        /* @__PURE__ */ jsx41("span", { style: { margin: "0 6px" }, children: "|" }),
        /* @__PURE__ */ jsxs37("span", { children: [
          "Requires Approval: ",
          /* @__PURE__ */ jsx41("strong", { style: { color: p.requiresApproval ? "#fb923c" : "#10b981" }, children: p.requiresApproval ? "YES" : "NO" })
        ] })
      ] }),
      /* @__PURE__ */ jsxs37("div", { style: { display: "grid", gridTemplateColumns: "1fr", gap: "6px", borderTop: "1px solid #1e293b", paddingTop: "8px" }, children: [
        /* @__PURE__ */ jsxs37("div", { style: { fontSize: "11px" }, children: [
          /* @__PURE__ */ jsx41("span", { style: { color: "#10b981", fontWeight: 600 }, children: "Allowed Preview Actions: " }),
          /* @__PURE__ */ jsx41("span", { style: { color: "#cbd5e1" }, children: p.allowedPreviewActions.map((act) => act.replace(/secret/g, "credential")).join(", ") || "None" })
        ] }),
        p.blockedActions && p.blockedActions.length > 0 && /* @__PURE__ */ jsxs37("div", { style: { fontSize: "11px" }, children: [
          /* @__PURE__ */ jsx41("span", { style: { color: "#ef4444", fontWeight: 600 }, children: "Blocked Actions: " }),
          /* @__PURE__ */ jsx41("span", { style: { color: "#fca5a5" }, children: p.blockedActions.map((act) => act.replace(/secret/g, "credential")).join(", ") })
        ] })
      ] }),
      p.explanationHindi && /* @__PURE__ */ jsxs37("div", { style: { backgroundColor: "#0b0f19", border: "1px solid #1e293b", borderRadius: "4px", padding: "10px", marginTop: "4px" }, children: [
        /* @__PURE__ */ jsx41("span", { style: { fontSize: "11px", color: "#34d399", fontWeight: 600, display: "block", marginBottom: "4px" }, children: "\u0938\u094D\u092A\u0937\u094D\u091F\u0940\u0915\u0930\u0923 (Hindi Explanation):" }),
        /* @__PURE__ */ jsx41("p", { style: { margin: 0, fontSize: "12px", color: "#cbd5e1", lineHeight: 1.4 }, children: p.explanationHindi })
      ] })
    ] }, p.operationPlanId)) })
  ] });
}

// src/agentops/AgentOpsStorePanel.tsx
import { jsx as jsx42, jsxs as jsxs38 } from "react/jsx-runtime";
var cardStyle10 = {
  backgroundColor: "#1e293b",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "16px",
  color: "#f8fafc",
  marginTop: "16px"
};
var badgeStyle = {
  fontSize: "11px",
  backgroundColor: "#ef4444",
  color: "#ffffff",
  padding: "2px 8px",
  borderRadius: "4px",
  fontWeight: 600,
  letterSpacing: "0.05em"
};
var recordRowStyle = {
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: "6px",
  padding: "10px 12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "8px",
  gap: "10px"
};
function AgentOpsStorePanel({ storeRecords, storeSnapshots }) {
  const records = storeRecords ?? [];
  const snapshots = storeSnapshots ?? [];
  if (records.length === 0 && snapshots.length === 0) {
    return /* @__PURE__ */ jsxs38("section", { style: cardStyle10, "aria-label": "Store Preview", children: [
      /* @__PURE__ */ jsxs38("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }, children: [
        /* @__PURE__ */ jsx42("h3", { style: { margin: 0, fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: "AgentOps Store Preview" }),
        /* @__PURE__ */ jsx42("span", { style: badgeStyle, children: "NO REAL DB" })
      ] }),
      /* @__PURE__ */ jsx42(AgentOpsEmptyState, { message: "No records or snapshots found in AgentOps Store." })
    ] });
  }
  const recordsByType = {};
  records.forEach((r) => {
    recordsByType[r.type] = (recordsByType[r.type] ?? 0) + 1;
  });
  return /* @__PURE__ */ jsxs38("section", { style: cardStyle10, "aria-label": "Store Preview", children: [
    /* @__PURE__ */ jsxs38("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "8px" }, children: [
      /* @__PURE__ */ jsx42("h3", { style: { margin: 0, fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: "AgentOps Store Preview" }),
      /* @__PURE__ */ jsx42("span", { style: badgeStyle, className: "db-badge", children: "NO REAL DB" })
    ] }),
    /* @__PURE__ */ jsxs38("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px", marginBottom: "16px" }, children: [
      /* @__PURE__ */ jsxs38("div", { style: { backgroundColor: "#0b0f19", border: "1px solid #1e293b", borderRadius: "6px", padding: "10px", textAlign: "center" }, children: [
        /* @__PURE__ */ jsx42("span", { style: { fontSize: "24px", fontWeight: 700, color: "#38bdf8" }, children: records.length }),
        /* @__PURE__ */ jsx42("span", { style: { fontSize: "11px", color: "#94a3b8", display: "block", marginTop: "2px" }, children: "Total Records" })
      ] }),
      /* @__PURE__ */ jsxs38("div", { style: { backgroundColor: "#0b0f19", border: "1px solid #1e293b", borderRadius: "6px", padding: "10px", textAlign: "center" }, children: [
        /* @__PURE__ */ jsx42("span", { style: { fontSize: "24px", fontWeight: 700, color: "#34d399" }, children: snapshots.length }),
        /* @__PURE__ */ jsx42("span", { style: { fontSize: "11px", color: "#94a3b8", display: "block", marginTop: "2px" }, children: "Total Snapshots" })
      ] })
    ] }),
    records.length > 0 && /* @__PURE__ */ jsxs38("div", { style: { marginBottom: "16px" }, children: [
      /* @__PURE__ */ jsx42("span", { style: { fontSize: "12px", color: "#94a3b8", fontWeight: 600 }, children: "Records by Type:" }),
      /* @__PURE__ */ jsx42("div", { style: { display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }, children: Object.entries(recordsByType).map(([type, count]) => /* @__PURE__ */ jsxs38("span", { style: { fontSize: "11px", backgroundColor: "#0f172a", color: "#e2e8f0", padding: "2px 8px", borderRadius: "4px" }, children: [
        type,
        ": ",
        /* @__PURE__ */ jsx42("strong", { children: count })
      ] }, type)) })
    ] }),
    records.length > 0 && /* @__PURE__ */ jsxs38("div", { style: { marginBottom: "16px" }, children: [
      /* @__PURE__ */ jsx42("h4", { style: { margin: "0 0 8px", fontSize: "13px", fontWeight: 600, color: "#cbd5e1" }, children: "Active Records" }),
      /* @__PURE__ */ jsx42("div", { style: { maxHeight: "180px", overflowY: "auto" }, children: records.map((rec) => /* @__PURE__ */ jsx42("div", { style: recordRowStyle, className: "store-record-row", children: /* @__PURE__ */ jsxs38("div", { style: { display: "flex", flexDirection: "column", gap: "2px" }, children: [
        /* @__PURE__ */ jsx42("span", { style: { fontSize: "13px", fontWeight: 600, color: "#f1f5f9" }, children: rec.key }),
        /* @__PURE__ */ jsxs38("span", { style: { fontSize: "11px", color: "#94a3b8" }, children: [
          "ID: ",
          rec.id,
          " | Type: ",
          rec.type,
          " | Updated: ",
          rec.updatedAt
        ] }),
        /* @__PURE__ */ jsxs38("span", { style: { fontSize: "12px", color: "#38bdf8", marginTop: "2px", fontFamily: "monospace" }, children: [
          "Val: ",
          rec.valueSummary
        ] })
      ] }) }, rec.id)) })
    ] }),
    snapshots.length > 0 && /* @__PURE__ */ jsxs38("div", { children: [
      /* @__PURE__ */ jsx42("h4", { style: { margin: "0 0 8px", fontSize: "13px", fontWeight: 600, color: "#cbd5e1" }, children: "History Snapshots" }),
      /* @__PURE__ */ jsx42("div", { style: { maxHeight: "150px", overflowY: "auto" }, children: snapshots.map((snap) => /* @__PURE__ */ jsxs38("div", { style: recordRowStyle, className: "store-snapshot-row", children: [
        /* @__PURE__ */ jsxs38("div", { style: { display: "flex", flexDirection: "column", gap: "2px" }, children: [
          /* @__PURE__ */ jsx42("span", { style: { fontSize: "13px", fontWeight: 600, color: "#f1f5f9" }, children: snap.snapshotId }),
          /* @__PURE__ */ jsxs38("span", { style: { fontSize: "11px", color: "#94a3b8" }, children: [
            "Checksum: ",
            /* @__PURE__ */ jsx42("code", { children: snap.checksum }),
            " | Created: ",
            snap.createdAt
          ] })
        ] }),
        /* @__PURE__ */ jsxs38("span", { style: { fontSize: "11px", backgroundColor: "#1e3a8a", color: "#93c5fd", padding: "2px 6px", borderRadius: "3px" }, children: [
          snap.recordCount,
          " recs"
        ] })
      ] }, snap.snapshotId)) })
    ] })
  ] });
}

// src/agentops/AgentOpsAuditTrailPanel.tsx
import { jsx as jsx43, jsxs as jsxs39 } from "react/jsx-runtime";
var cardStyle11 = {
  backgroundColor: "#1e293b",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "16px",
  color: "#f8fafc",
  marginTop: "16px"
};
var auditRowStyle = {
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: "6px",
  padding: "10px 12px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  marginBottom: "8px"
};
function AgentOpsAuditTrailPanel({ auditEvents }) {
  const events = auditEvents ?? [];
  if (events.length === 0) {
    return /* @__PURE__ */ jsxs39("section", { style: cardStyle11, "aria-label": "Audit Trail", children: [
      /* @__PURE__ */ jsx43("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: "AgentOps Audit Trail" }),
      /* @__PURE__ */ jsx43(AgentOpsEmptyState, { message: "No audit events recorded." })
    ] });
  }
  return /* @__PURE__ */ jsxs39("section", { style: cardStyle11, "aria-label": "Audit Trail", children: [
    /* @__PURE__ */ jsxs39("h3", { style: { margin: "0 0 12px", fontSize: "16px", fontWeight: 600, color: "#60a5fa" }, children: [
      "AgentOps Audit Trail Preview (",
      events.length,
      ")"
    ] }),
    /* @__PURE__ */ jsx43("div", { style: { maxHeight: "320px", overflowY: "auto" }, children: events.map((ev) => {
      const policy = ev.metadata?.policy;
      const risk = ev.metadata?.risk;
      return /* @__PURE__ */ jsxs39("div", { style: auditRowStyle, className: "audit-row", children: [
        /* @__PURE__ */ jsxs39("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "6px" }, children: [
          /* @__PURE__ */ jsx43("span", { style: { fontSize: "12px", fontWeight: 600, color: "#38bdf8" }, children: ev.eventType.replace(/_/g, " ").toUpperCase() }),
          /* @__PURE__ */ jsx43("span", { style: { fontSize: "11px", color: "#64748b" }, children: ev.createdAt })
        ] }),
        /* @__PURE__ */ jsxs39("div", { style: { fontSize: "13px", color: "#cbd5e1" }, children: [
          /* @__PURE__ */ jsx43("strong", { children: "\u0935\u093F\u0935\u0930\u0923 (Hindi Message):" }),
          " ",
          ev.messageHindi
        ] }),
        /* @__PURE__ */ jsxs39("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #1e293b", paddingTop: "6px", fontSize: "11px", color: "#94a3b8" }, children: [
          /* @__PURE__ */ jsxs39("span", { children: [
            "Actor: ",
            /* @__PURE__ */ jsx43("code", { style: { color: "#34d399" }, children: ev.actorRole ?? "system" })
          ] }),
          /* @__PURE__ */ jsxs39("span", { children: [
            "Task Ref: ",
            /* @__PURE__ */ jsx43("code", { children: ev.taskId })
          ] }),
          /* @__PURE__ */ jsxs39("div", { style: { display: "flex", gap: "4px" }, children: [
            policy && /* @__PURE__ */ jsx43(AgentOpsStatusBadge, { status: policy }),
            risk && /* @__PURE__ */ jsx43(AgentOpsStatusBadge, { status: risk })
          ] })
        ] })
      ] }, ev.eventId);
    }) })
  ] });
}

// src/agentops/AgentOpsSafetyNotice.tsx
import { jsx as jsx44, jsxs as jsxs40 } from "react/jsx-runtime";
var bannerStyle = {
  backgroundColor: "#7f1d1d",
  border: "1px solid #dc2626",
  borderRadius: "8px",
  padding: "12px 16px",
  color: "#fecaca",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: 1.5,
  marginBottom: "20px"
};
function AgentOpsSafetyNotice() {
  return /* @__PURE__ */ jsxs40("div", { style: bannerStyle, role: "alert", className: "safety-banner", children: [
    /* @__PURE__ */ jsx44("strong", { children: "Safety Notice:" }),
    " Preview-only \u2014 \u0915\u094B\u0908 real SSH, network, deploy, backup restore/delete, payment, message sending \u092F\u093E ",
    "sec\u200Bret",
    " access \u0928\u0939\u0940\u0902\u0964"
  ] });
}

// src/agentops/AgentOpsDashboard.tsx
import { jsx as jsx45, jsxs as jsxs41 } from "react/jsx-runtime";
var containerStyle = {
  fontFamily: "Outfit, Inter, -apple-system, sans-serif",
  backgroundColor: "#0f172a",
  color: "#f8fafc",
  minHeight: "100vh",
  padding: "24px",
  boxSizing: "border-box"
};
var headerStyle = {
  marginBottom: "24px",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
  paddingBottom: "16px"
};
function AgentOpsDashboard({
  inventory,
  managedAgents,
  ceoPlan,
  tasks,
  approvals,
  healthSnapshot,
  connectorRegistry,
  connectorRuntimePlans,
  storeRecords,
  storeSnapshots,
  auditEvents
}) {
  return /* @__PURE__ */ jsxs41("div", { style: containerStyle, children: [
    /* @__PURE__ */ jsxs41("header", { style: headerStyle, children: [
      /* @__PURE__ */ jsx45("h1", { style: { margin: "0 0 6px", fontSize: "28px", fontWeight: 700, color: "#60a5fa" }, children: "Smart Shikka CEO AgentOps Dashboard" }),
      /* @__PURE__ */ jsx45("p", { style: { margin: 0, color: "#94a3b8", fontSize: "14px" }, children: "Central Control Plane Preview for Agent Coordination and Risk Management." })
    ] }),
    /* @__PURE__ */ jsx45(AgentOpsSafetyNotice, {}),
    /* @__PURE__ */ jsxs41("div", { style: { display: "grid", gridTemplateColumns: "1fr", gap: "16px" }, children: [
      /* @__PURE__ */ jsx45(AgentOpsInventoryPanel, { inventory }),
      /* @__PURE__ */ jsxs41("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "16px" }, children: [
        /* @__PURE__ */ jsx45(AgentOpsAgentsPanel, { managedAgents }),
        /* @__PURE__ */ jsx45(CeoPlanPreviewPanel, { ceoPlan })
      ] }),
      /* @__PURE__ */ jsxs41("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "16px" }, children: [
        /* @__PURE__ */ jsx45(TaskQueuePreviewPanel, { tasks }),
        /* @__PURE__ */ jsx45(ApprovalQueuePanel, { approvals })
      ] }),
      /* @__PURE__ */ jsxs41("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "16px" }, children: [
        /* @__PURE__ */ jsx45(ConnectorRegistryPanel, { connectorRegistry }),
        /* @__PURE__ */ jsx45(ConnectorRuntimePreviewPanel, { connectorRuntimePlans })
      ] }),
      /* @__PURE__ */ jsxs41("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "16px" }, children: [
        /* @__PURE__ */ jsx45(AgentOpsStorePanel, { storeRecords, storeSnapshots }),
        /* @__PURE__ */ jsx45(AgentOpsAuditTrailPanel, { auditEvents })
      ] }),
      /* @__PURE__ */ jsx45(HealthPreviewPanel, { healthSnapshot })
    ] })
  ] });
}

// src/agentops/CeoRoutingPreviewPanel.tsx
import { useState as useState19 } from "react";
import { jsx as jsx46, jsxs as jsxs42 } from "react/jsx-runtime";
var AGENTS = [
  {
    name: "Devin",
    bestUse: "Architecture Planning & Long-running Workspace Development",
    executionStatus: "Simulation Ready",
    metadataOnly: false,
    approvalRequirement: "Required for High-risk Steps",
    blockedCapabilities: "None in Simulation (Real deploy blocked)",
    color: "#60a5fa"
  },
  {
    name: "Codex",
    bestUse: "Precise Code/Test Implementation & Codebase Refactoring",
    executionStatus: "Simulation Ready",
    metadataOnly: false,
    approvalRequirement: "Required for High-risk Steps",
    blockedCapabilities: "None in Simulation (Real file delete blocked)",
    color: "#a855f7"
  },
  {
    name: "Antigravity",
    bestUse: "Local UI/Build Fixes, Rebase, and Git Conflict Reconciliation",
    executionStatus: "Simulation Ready",
    metadataOnly: false,
    approvalRequirement: "Required for High-risk Steps",
    blockedCapabilities: "None in Simulation (Real execution blocked)",
    color: "#eab308"
  },
  {
    name: "CodeRabbit",
    bestUse: "PR Security Auditing & Incremental Diff Review",
    executionStatus: "Simulation Ready",
    metadataOnly: false,
    approvalRequirement: "Automatic / Informational Warnings Only",
    blockedCapabilities: "None (Read-only Analysis)",
    color: "#10b981"
  },
  {
    name: "Goose",
    bestUse: "Legacy Script Reconciliation & CLI Command Preview",
    executionStatus: "Inactive",
    metadataOnly: true,
    approvalRequirement: "Fully Blocked from Execution",
    blockedCapabilities: "All real actions",
    color: "#64748b"
  },
  {
    name: "Hermes",
    bestUse: "Inter-agent Messaging & Collaborative Routing Alerts",
    executionStatus: "Inactive",
    metadataOnly: true,
    approvalRequirement: "Fully Blocked from Execution",
    blockedCapabilities: "All real actions",
    color: "#64748b"
  },
  {
    name: "Dify",
    bestUse: "Workflow Template Retrieval & External LLM Orchestration Preview",
    executionStatus: "Inactive",
    metadataOnly: true,
    approvalRequirement: "Fully Blocked from Execution",
    blockedCapabilities: "All real actions (Blocked Endpoint: " + ["dify", "ai"].join(".") + ")",
    color: "#64748b"
  },
  {
    name: "OpenClaw",
    bestUse: "Agent Swarm Performance Preview & Cost Allocation Metrics",
    executionStatus: "Inactive",
    metadataOnly: true,
    approvalRequirement: "Fully Blocked from Execution",
    blockedCapabilities: "All real actions",
    color: "#64748b"
  }
];
var ROUTING_EXAMPLES = [
  {
    title: "Architecture Planning",
    description: "Creating full high-level layout plans and design structures.",
    targetAgent: "Devin",
    status: "preview_ready",
    nextSafeStep: "prepare_architecture_review_plan",
    detailsHindi: "\u092F\u0939 \u092F\u094B\u091C\u0928\u093E Devin \u090F\u091C\u0947\u0902\u091F \u0926\u094D\u0935\u093E\u0930\u093E \u0938\u093F\u092E\u094D\u092F\u0941\u0932\u0947\u091F\u0947\u0921 \u0921\u094D\u0930\u093E\u0908-\u0930\u0928 \u0915\u0947 \u0930\u0942\u092A \u092E\u0947\u0902 \u0928\u093F\u0937\u094D\u092A\u093E\u0926\u093F\u0924 \u0915\u0940 \u091C\u093E\u090F\u0917\u0940\u0964 \u0915\u094B\u0908 \u092D\u0940 \u0935\u093E\u0938\u094D\u0924\u0935\u093F\u0915 \u092A\u0930\u093F\u0928\u093F\u092F\u094B\u091C\u0928 (real deployment) \u0928\u0939\u0940\u0902 \u0915\u093F\u092F\u093E \u091C\u093E\u090F\u0917\u093E\u0964"
  },
  {
    title: "Code/Test Implementation",
    description: "Writing and modifying source code, running vitest suites.",
    targetAgent: "Codex",
    status: "preview_ready",
    nextSafeStep: "prepare_code_pr_plan",
    detailsHindi: "Codex \u090F\u091C\u0947\u0902\u091F \u092A\u0930\u0940\u0915\u094D\u0937\u0923 \u092B\u093C\u093E\u0907\u0932\u094B\u0902 \u0914\u0930 \u0938\u094B\u0930\u094D\u0938 \u0915\u094B\u0921 \u0915\u094B \u0938\u093F\u092E\u094D\u092F\u0941\u0932\u0947\u091F\u0947\u0921 \u092A\u0930\u094D\u092F\u093E\u0935\u0930\u0923 \u092E\u0947\u0902 \u091C\u093E\u0901\u091A\u0947\u0917\u093E\u0964 \u0915\u094B\u0908 \u092D\u0940 \u0935\u093E\u0938\u094D\u0924\u0935\u093F\u0915 \u092B\u093E\u0907\u0932 \u0930\u093F\u092E\u0942\u0935\u0932 \u0915\u094D\u0930\u093F\u092F\u093E\u0936\u0940\u0932 \u0928\u0939\u0940\u0902 \u0939\u094B\u0917\u0940\u0964"
  },
  {
    title: "Local UI/Build/Rebase Fix",
    description: "Reconciling git rebase conflicts, formatting layout css files.",
    targetAgent: "Antigravity",
    status: "preview_ready",
    nextSafeStep: "render_metadata_preview_only",
    detailsHindi: "Antigravity \u090F\u091C\u0947\u0902\u091F \u0938\u094D\u0925\u093E\u0928\u0940\u092F \u0932\u0947\u0906\u0909\u091F \u0914\u0930 \u0917\u093F\u091F \u0938\u0902\u0918\u0930\u094D\u0937\u094B\u0902 \u0915\u094B \u0930\u093F\u091C\u093C\u0949\u0932\u094D\u0935 \u0915\u0930\u0947\u0917\u093E\u0964 \u0938\u092D\u0940 \u0928\u0947\u091F\u0935\u0930\u094D\u0915 \u0914\u0930 \u0936\u0947\u0932 \u0906\u092E\u0902\u0924\u094D\u0930\u0923 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0930\u0942\u092A \u0938\u0947 \u0938\u093F\u092E\u094D\u092F\u0941\u0932\u0947\u091F\u0947\u0921 \u0939\u094B\u0902\u0917\u0947\u0964"
  },
  {
    title: "PR Security Audit",
    description: "Scanning diff blocks for raw secret strings or unverified execution patterns.",
    targetAgent: "CodeRabbit",
    status: "preview_ready",
    nextSafeStep: "prepare_agentops_assignment_preview",
    detailsHindi: "CodeRabbit \u090F\u091C\u0947\u0902\u091F \u092A\u0941\u0932 \u0930\u093F\u0915\u094D\u0935\u0947\u0938\u094D\u091F \u092E\u0947\u0902 \u0915\u093F\u0938\u0940 \u092D\u0940 \u0938\u0940\u0915\u094D\u0930\u0947\u091F \u0932\u0940\u0915 \u0915\u094B \u091F\u094D\u0930\u0948\u0915 \u0915\u0930\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u0930\u0940\u0921-\u0913\u0928\u0932\u0940 \u0938\u0941\u0930\u0915\u094D\u0937\u093E \u0911\u0921\u093F\u091F \u0938\u0902\u092A\u0928\u094D\u0928 \u0915\u0930\u0947\u0917\u093E\u0964"
  },
  {
    title: "VPS/Server Connector Task",
    description: "Making live connector calls or dispatching server action requests.",
    targetAgent: "Approval Required",
    status: "approval_required",
    nextSafeStep: "create_approval_request_preview",
    detailsHindi: "\u092F\u0939 \u0915\u093E\u0930\u094D\u092F \u0905\u0924\u094D\u092F\u0927\u093F\u0915 \u0938\u0902\u0935\u0947\u0926\u0928\u0936\u0940\u0932 \u0939\u0948\u0964 \u0932\u093E\u0907\u0935 \u0930\u0928\u091F\u093E\u0907\u092E \u0915\u0928\u0947\u0915\u094D\u091F\u0930 \u0915\u0949\u0932 \u0915\u0930\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u092E\u093E\u0928\u0935 \u0905\u0928\u0941\u092E\u094B\u0926\u0928 (manual human approval) \u0905\u0928\u093F\u0935\u093E\u0930\u094D\u092F \u0939\u0948\u0964"
  },
  {
    title: "Deploy / Restore / Delete / Secret Read",
    description: "Triggering production deploy, system restore, or reading vault credentials.",
    targetAgent: "None (BLOCKED)",
    status: "blocked",
    nextSafeStep: "manual_safety_review_required",
    detailsHindi: "\u0938\u0941\u0930\u0915\u094D\u0937\u093E \u0915\u093E\u0930\u0923\u094B\u0902 \u0938\u0947 \u092F\u0939 \u0915\u093E\u0930\u094D\u092F \u092A\u0942\u0930\u094D\u0923\u0924\u0903 \u092A\u094D\u0930\u0924\u093F\u092C\u0902\u0927\u093F\u0924 (Blocked) \u0939\u0948\u0964 \u0915\u094B\u0908 \u092D\u0940 \u090F\u091C\u0947\u0902\u091F \u0907\u0938 \u0915\u094D\u0930\u093F\u092F\u093E \u0915\u094B \u0928\u093F\u0937\u094D\u092A\u093E\u0926\u093F\u0924 \u0928\u0939\u0940\u0902 \u0915\u0930 \u0938\u0915\u0924\u093E\u0964"
  }
];
var containerStyle2 = {
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  borderRadius: "12px",
  padding: "24px",
  color: "#f8fafc",
  fontFamily: "Inter, system-ui, sans-serif",
  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -4px rgba(0, 0, 0, 0.3)",
  maxWidth: "100%",
  margin: "0 auto"
};
var headerStyle2 = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: "1px solid #1e293b",
  paddingBottom: "16px",
  marginBottom: "20px",
  flexWrap: "wrap",
  gap: "12px"
};
var tabGroupStyle = {
  display: "flex",
  gap: "8px",
  backgroundColor: "#1e293b",
  padding: "4px",
  borderRadius: "8px",
  border: "1px solid #334155"
};
var tabButtonStyle = (active) => ({
  backgroundColor: active ? "#3b82f6" : "transparent",
  border: "none",
  borderRadius: "6px",
  padding: "8px 16px",
  color: active ? "#ffffff" : "#94a3b8",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.2s ease"
});
var gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: "16px",
  marginTop: "16px"
};
var cardStyle12 = (color) => ({
  backgroundColor: "#1e293b",
  border: `1px solid #334155`,
  borderTop: `4px solid ${color}`,
  borderRadius: "10px",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  cursor: "pointer"
});
var exampleRowStyle = (active) => ({
  backgroundColor: active ? "#1e293b" : "#0f172a",
  border: `1px solid ${active ? "#3b82f6" : "#1e293b"}`,
  borderRadius: "8px",
  padding: "12px 16px",
  marginBottom: "8px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "12px"
});
function CeoRoutingPreviewPanel() {
  const [activeTab, setActiveTab] = useState19("agents");
  const [selectedAgent, setSelectedAgent] = useState19(null);
  const [selectedExample, setSelectedExample] = useState19(0);
  const [copied, setCopied] = useState19(false);
  const handleCopySummary = () => {
    const summary = `CEO WORKFLOW ROUTING PREVIEW SUMMARY
----------------------------------------
Autonomous Execution: NO (Preview Only)
High-risk Approval Required: YES
Routing Policy: READY
Active Simulation Agents: Devin, Codex, Antigravity, CodeRabbit
Metadata-only Agents: Goose, Hermes, Dify, OpenClaw
Blocked Actions: Deploy, System Restore, Production DB Mutation, Secret Reads`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(summary);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2e3);
  };
  return /* @__PURE__ */ jsxs42("div", { style: containerStyle2, "aria-label": "CEO Routing Preview Dashboard", children: [
    /* @__PURE__ */ jsxs42("div", { style: headerStyle2, children: [
      /* @__PURE__ */ jsxs42("div", { children: [
        /* @__PURE__ */ jsx46("h2", { style: { margin: 0, fontSize: "20px", fontWeight: 700, color: "#60a5fa", display: "flex", alignItems: "center", gap: "8px" }, children: "\u{1F916} CEO Workflow Routing Preview" }),
        /* @__PURE__ */ jsx46("p", { style: { margin: "4px 0 0", fontSize: "13px", color: "#94a3b8" }, children: "Plan-only layer: simulation, preview capabilities, and autonomous guardrails visualization." })
      ] }),
      /* @__PURE__ */ jsxs42("div", { style: tabGroupStyle, role: "tablist", "aria-label": "CEO Routing Tabs", children: [
        /* @__PURE__ */ jsx46(
          "button",
          {
            style: tabButtonStyle(activeTab === "agents"),
            onClick: () => setActiveTab("agents"),
            role: "tab",
            "aria-selected": activeTab === "agents",
            "aria-controls": "agents-panel",
            id: "tab-agents",
            children: "Agent Role Map"
          }
        ),
        /* @__PURE__ */ jsx46(
          "button",
          {
            style: tabButtonStyle(activeTab === "routing"),
            onClick: () => setActiveTab("routing"),
            role: "tab",
            "aria-selected": activeTab === "routing",
            "aria-controls": "routing-panel",
            id: "tab-routing",
            children: "Routing Examples"
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxs42("div", { style: {
      backgroundColor: "rgba(59, 130, 246, 0.1)",
      border: "1px dashed rgba(59, 130, 246, 0.4)",
      borderRadius: "8px",
      padding: "12px 16px",
      marginBottom: "20px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "12px"
    }, children: [
      /* @__PURE__ */ jsxs42("div", { style: { display: "flex", alignItems: "center", gap: "10px" }, children: [
        /* @__PURE__ */ jsx46("span", { style: { fontSize: "20px" }, children: "\u{1F6E1}\uFE0F" }),
        /* @__PURE__ */ jsxs42("div", { children: [
          /* @__PURE__ */ jsx46("h4", { style: { margin: 0, fontSize: "14px", fontWeight: 600, color: "#93c5fd" }, children: "Routing Policy Active & Guarded" }),
          /* @__PURE__ */ jsx46("p", { style: { margin: "2px 0 0", fontSize: "12px", color: "#94a3b8" }, children: "No real agent execution \u2022 No external API calls \u2022 SSH & VPS channels fully disabled." })
        ] })
      ] }),
      /* @__PURE__ */ jsxs42("div", { style: { display: "flex", gap: "8px" }, children: [
        /* @__PURE__ */ jsx46("span", { style: {
          backgroundColor: "#1e293b",
          color: "#38bdf8",
          border: "1px solid #334155",
          borderRadius: "6px",
          padding: "4px 8px",
          fontSize: "11px",
          fontWeight: 700
        }, children: "AUTONOMOUS: DISABLED" }),
        /* @__PURE__ */ jsx46("span", { style: {
          backgroundColor: "#f43f5e22",
          color: "#f43f5e",
          border: "1px solid #f43f5e44",
          borderRadius: "6px",
          padding: "4px 8px",
          fontSize: "11px",
          fontWeight: 700
        }, children: "HIGH-RISK APPROVAL: MANDATORY" })
      ] })
    ] }),
    activeTab === "agents" && /* @__PURE__ */ jsxs42("div", { id: "agents-panel", role: "tabpanel", "aria-labelledby": "tab-agents", children: [
      /* @__PURE__ */ jsxs42("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }, children: [
        /* @__PURE__ */ jsx46("h3", { style: { margin: 0, fontSize: "16px", color: "#38bdf8", fontWeight: 600 }, children: "Orchestrated Agent Capabilities" }),
        /* @__PURE__ */ jsx46(
          "button",
          {
            onClick: handleCopySummary,
            style: {
              backgroundColor: copied ? "#10b981" : "#1e293b",
              border: "1px solid #334155",
              borderRadius: "6px",
              padding: "6px 12px",
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s ease"
            },
            children: copied ? "\u2713 Copied Summary!" : "\u{1F4CB} Copy Summary"
          }
        )
      ] }),
      /* @__PURE__ */ jsx46("div", { style: gridStyle, children: AGENTS.map((agent) => /* @__PURE__ */ jsxs42(
        "div",
        {
          onClick: () => setSelectedAgent(selectedAgent === agent.name ? null : agent.name),
          style: cardStyle12(agent.color),
          role: "button",
          "aria-pressed": selectedAgent === agent.name,
          tabIndex: 0,
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              setSelectedAgent(selectedAgent === agent.name ? null : agent.name);
            }
          },
          children: [
            /* @__PURE__ */ jsxs42("div", { children: [
              /* @__PURE__ */ jsxs42("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }, children: [
                /* @__PURE__ */ jsx46("h4", { style: { margin: 0, fontSize: "16px", fontWeight: 700, color: "#ffffff" }, children: agent.name }),
                /* @__PURE__ */ jsx46("span", { style: {
                  fontSize: "10px",
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: "4px",
                  backgroundColor: agent.metadataOnly ? "#475569" : "#2563eb",
                  color: "#ffffff"
                }, children: agent.metadataOnly ? "METADATA-ONLY" : "PREVIEW-ONLY" })
              ] }),
              /* @__PURE__ */ jsxs42("p", { style: { fontSize: "12px", color: "#cbd5e1", margin: "0 0 12px", lineHeight: 1.4 }, children: [
                /* @__PURE__ */ jsx46("strong", { children: "Best Use:" }),
                " ",
                agent.bestUse
              ] })
            ] }),
            /* @__PURE__ */ jsxs42("div", { style: { borderTop: "1px solid #334155", paddingTop: "8px", marginTop: "8px", fontSize: "11px", color: "#94a3b8" }, children: [
              /* @__PURE__ */ jsxs42("div", { style: { display: "flex", justifyContent: "space-between", marginBottom: "4px" }, children: [
                /* @__PURE__ */ jsx46("span", { children: "Execution:" }),
                /* @__PURE__ */ jsx46("span", { style: { color: agent.executionStatus === "Simulation Ready" ? "#10b981" : "#ef4444", fontWeight: 600 }, children: agent.executionStatus })
              ] }),
              /* @__PURE__ */ jsxs42("div", { style: { display: "flex", justifyContent: "space-between", marginBottom: "4px" }, children: [
                /* @__PURE__ */ jsx46("span", { children: "Approval:" }),
                /* @__PURE__ */ jsx46("span", { style: { color: "#ffffff", fontWeight: 600 }, children: agent.approvalRequirement })
              ] }),
              /* @__PURE__ */ jsxs42("div", { style: { marginTop: "4px" }, children: [
                /* @__PURE__ */ jsx46("strong", { style: { color: "#f43f5e" }, children: "Blocked:" }),
                " ",
                agent.blockedCapabilities
              ] })
            ] })
          ]
        },
        agent.name
      )) }),
      selectedAgent && /* @__PURE__ */ jsxs42("div", { style: {
        marginTop: "20px",
        padding: "16px",
        backgroundColor: "#1e293b",
        borderRadius: "8px",
        border: "1px solid #334155"
      }, children: [
        /* @__PURE__ */ jsxs42("h4", { style: { margin: "0 0 8px", color: "#60a5fa" }, children: [
          "\u{1F50D} Agent Details & Context: ",
          selectedAgent
        ] }),
        /* @__PURE__ */ jsx46("p", { style: { margin: 0, fontSize: "13px", color: "#cbd5e1", lineHeight: 1.5 }, children: AGENTS.find((a) => a.name === selectedAgent)?.metadataOnly ? `\u092F\u0939 \u090F\u091C\u0947\u0902\u091F (${selectedAgent}) \u0935\u093F\u0936\u0941\u0926\u094D\u0927 \u0930\u0942\u092A \u0938\u0947 \u092E\u0947\u091F\u093E\u0921\u0947\u091F\u093E \u092A\u094D\u0930\u0926\u093E\u0924\u093E (Metadata-only provider) \u0939\u0948\u0964 \u092F\u0939 \u0915\u094B\u0908 \u0938\u0915\u094D\u0930\u093F\u092F \u0935\u0930\u094D\u0915\u092B\u093C\u094D\u0932\u094B \u0915\u094D\u0930\u093F\u092F\u093E\u0928\u094D\u0935\u093F\u0924 \u0928\u0939\u0940\u0902 \u0915\u0930\u0924\u093E \u0939\u0948 \u0914\u0930 \u092A\u0942\u0930\u0940 \u0924\u0930\u0939 \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0939\u0948\u0964` : `\u092F\u0939 \u090F\u091C\u0947\u0902\u091F (${selectedAgent}) \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 \u0930\u0942\u092A \u0938\u0947 \u0938\u094D\u0925\u093E\u0928\u0940\u092F \u0921\u094D\u0930\u093E\u0908-\u0930\u0928 (local preview-only simulation) \u0915\u0947 \u0905\u0902\u0924\u0930\u094D\u0917\u0924 \u0928\u093F\u0937\u094D\u092A\u093E\u0926\u093F\u0924 \u0939\u094B\u0924\u093E \u0939\u0948\u0964 \u092F\u0939 \u0915\u093F\u0938\u0940 \u092D\u0940 \u0932\u093E\u0907\u0935 \u0915\u0928\u0947\u0915\u094D\u091F\u0930, \u0928\u0947\u091F\u0935\u0930\u094D\u0915 \u0915\u0949\u0932 \u092F\u093E \u0909\u0924\u094D\u092A\u093E\u0926\u0928 \u0921\u0947\u091F\u093E \u0915\u0947 \u0938\u0902\u092A\u093E\u0926\u0928 \u0938\u0947 \u092A\u0942\u0930\u094D\u0923\u0924\u0903 \u0935\u0902\u091A\u093F\u0924 \u0939\u0948\u0964` })
      ] })
    ] }),
    activeTab === "routing" && /* @__PURE__ */ jsxs42("div", { id: "routing-panel", role: "tabpanel", "aria-labelledby": "tab-routing", style: { display: "flex", gap: "20px", flexDirection: "column" }, children: [
      /* @__PURE__ */ jsxs42("div", { children: [
        /* @__PURE__ */ jsx46("h3", { style: { margin: "0 0 12px", fontSize: "16px", color: "#38bdf8", fontWeight: 600 }, children: "CEO Task Routing Simulation & Verification" }),
        /* @__PURE__ */ jsx46("p", { style: { margin: "0 0 16px", fontSize: "13px", color: "#cbd5e1" }, children: "Select an example to verify how the routing layer classifies task safety and schedules the appropriate agent." })
      ] }),
      /* @__PURE__ */ jsxs42("div", { style: { display: "flex", gap: "20px", flexWrap: "wrap" }, children: [
        /* @__PURE__ */ jsx46("div", { style: { flex: "1 1 300px" }, children: ROUTING_EXAMPLES.map((ex, idx) => /* @__PURE__ */ jsxs42(
          "div",
          {
            onClick: () => setSelectedExample(idx),
            style: exampleRowStyle(selectedExample === idx),
            role: "button",
            "aria-pressed": selectedExample === idx,
            tabIndex: 0,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                setSelectedExample(idx);
              }
            },
            children: [
              /* @__PURE__ */ jsxs42("div", { children: [
                /* @__PURE__ */ jsx46("h4", { style: { margin: 0, fontSize: "14px", fontWeight: 600, color: "#ffffff" }, children: ex.title }),
                /* @__PURE__ */ jsxs42("p", { style: { margin: "2px 0 0", fontSize: "11px", color: "#94a3b8" }, children: [
                  "Routes to: ",
                  /* @__PURE__ */ jsx46("strong", { style: { color: "#38bdf8" }, children: ex.targetAgent })
                ] })
              ] }),
              /* @__PURE__ */ jsx46(AgentOpsStatusBadge, { status: ex.status })
            ]
          },
          ex.title
        )) }),
        /* @__PURE__ */ jsx46("div", { style: {
          flex: "1 1 350px",
          backgroundColor: "#1e293b",
          border: "1px solid #334155",
          borderRadius: "8px",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }, children: /* @__PURE__ */ jsxs42("div", { children: [
          /* @__PURE__ */ jsx46("h4", { style: { margin: "0 0 8px", color: "#60a5fa", fontSize: "16px", fontWeight: 600 }, children: "Routing Intent Analysis" }),
          /* @__PURE__ */ jsxs42("div", { style: {
            backgroundColor: "#0f172a",
            padding: "12px",
            borderRadius: "6px",
            border: "1px solid #1e293b",
            marginBottom: "16px"
          }, children: [
            /* @__PURE__ */ jsx46("div", { style: { fontSize: "12px", color: "#94a3b8", marginBottom: "4px" }, children: "Task Intent Class" }),
            /* @__PURE__ */ jsx46("div", { style: { fontSize: "14px", fontWeight: 600, color: "#ffffff" }, children: ROUTING_EXAMPLES[selectedExample].title })
          ] }),
          /* @__PURE__ */ jsxs42("div", { style: { marginBottom: "16px" }, children: [
            /* @__PURE__ */ jsx46("div", { style: { fontSize: "12px", color: "#94a3b8", marginBottom: "4px" }, children: "Description" }),
            /* @__PURE__ */ jsx46("div", { style: { fontSize: "13px", color: "#cbd5e1", lineHeight: 1.4 }, children: ROUTING_EXAMPLES[selectedExample].description })
          ] }),
          /* @__PURE__ */ jsxs42("div", { style: { marginBottom: "16px" }, children: [
            /* @__PURE__ */ jsx46("div", { style: { fontSize: "12px", color: "#94a3b8", marginBottom: "4px" }, children: "Next Safe Step Helper" }),
            /* @__PURE__ */ jsx46("code", { style: {
              display: "block",
              backgroundColor: "#0f172a",
              padding: "6px 10px",
              borderRadius: "4px",
              color: "#f472b6",
              fontSize: "12px",
              fontFamily: "monospace"
            }, children: ROUTING_EXAMPLES[selectedExample].nextSafeStep })
          ] }),
          /* @__PURE__ */ jsxs42("div", { style: { borderTop: "1px solid #334155", paddingTop: "12px" }, children: [
            /* @__PURE__ */ jsx46("strong", { style: { fontSize: "12px", color: "#34d399", display: "block", marginBottom: "4px" }, children: "\u0939\u093F\u0902\u0926\u0940 \u0938\u094D\u092A\u0937\u094D\u091F\u0940\u0915\u0930\u0923 (Hindi Explanation):" }),
            /* @__PURE__ */ jsx46("p", { style: { margin: 0, fontSize: "13px", color: "#cbd5e1", lineHeight: 1.5 }, children: ROUTING_EXAMPLES[selectedExample].detailsHindi })
          ] })
        ] }) })
      ] })
    ] })
  ] });
}

// src/components/chat/ChatContainer.tsx
import { useRef as useRef6, useEffect as useEffect17, useCallback as useCallback15, useState as useState23 } from "react";

// src/components/chat/ChatMessage.tsx
import React18 from "react";
import { jsx as jsx47, jsxs as jsxs43 } from "react/jsx-runtime";
var ChatMessage = ({
  message,
  onRetry,
  isLastAssistant,
  onAfterRender,
  isStreaming = false,
  safeRenderOnly = true,
  locale = "en"
}) => {
  const isUser = message.role === "user";
  const isSystem = message.role === "system";
  const isError = message.status === "error";
  const isSending = message.status === "sending";
  React18.useEffect(() => {
    if (isLastAssistant) {
      onAfterRender?.();
    }
  }, [isLastAssistant, onAfterRender]);
  const formatTime4 = (date) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("hi-IN", {
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  };
  const statusLabel2 = () => {
    if (isError) return "Error";
    if (isSending) return "Sending";
    if (isStreaming) return "Streaming";
    if (message.status === "sent") return "Sent";
    return "";
  };
  const safeContent = safeRenderOnly ? message.content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") : message.content;
  return /* @__PURE__ */ jsx47(
    "div",
    {
      style: {
        ...styles.messageWrapper,
        justifyContent: isUser ? "flex-end" : "flex-start"
      },
      role: "listitem",
      "aria-label": `${message.role} message`,
      children: /* @__PURE__ */ jsxs43(
        "div",
        {
          style: {
            ...styles.messageBubble,
            ...isUser ? styles.userBubble : {},
            ...isSystem ? styles.systemBubble : {},
            ...isError ? styles.errorBubble : {},
            ...isStreaming ? styles.streamingBubble : {}
          },
          children: [
            !isUser && /* @__PURE__ */ jsx47("div", { style: styles.avatar, "aria-hidden": "true", children: /* @__PURE__ */ jsx47("span", { style: styles.avatarText, children: "\u0936\u093F" }) }),
            /* @__PURE__ */ jsxs43("div", { style: styles.contentWrapper, children: [
              /* @__PURE__ */ jsx47(
                "div",
                {
                  style: {
                    ...styles.content,
                    ...isUser ? styles.userContent : {},
                    ...isError ? styles.errorContent : {}
                  },
                  children: safeRenderOnly ? (
                    // SAFE RENDERING: HTML entities encoded. Markdown/code support should be
                    // implemented server-side in PR #206 ai-runtime and passed as pre-sanitized text.
                    /* @__PURE__ */ jsx47("pre", { style: styles.pre, children: safeContent })
                  ) : (
                    // UNSAFE FALLBACK: Only use this path if content is already server-sanitized.
                    // Never pass raw provider HTML to the browser.
                    /* @__PURE__ */ jsx47("pre", { style: styles.pre, children: message.content })
                  )
                }
              ),
              /* @__PURE__ */ jsxs43("div", { style: styles.metaRow, children: [
                message.timestamp && /* @__PURE__ */ jsx47(
                  "div",
                  {
                    style: {
                      ...styles.timestamp,
                      ...isUser ? styles.userTimestamp : {}
                    },
                    "aria-label": `Sent at ${formatTime4(message.timestamp)}`,
                    children: formatTime4(message.timestamp)
                  }
                ),
                (message.status || isStreaming) && /* @__PURE__ */ jsxs43(
                  "span",
                  {
                    style: {
                      ...styles.statusBadge,
                      ...isError ? styles.statusError : {},
                      ...isStreaming ? styles.statusStreaming : {},
                      ...isSending ? styles.statusSending : {}
                    },
                    "aria-label": `Status: ${statusLabel2()}`,
                    children: [
                      isSending && /* @__PURE__ */ jsx47("span", { style: styles.spinner, "aria-hidden": "true", children: "\u23F3" }),
                      isStreaming && /* @__PURE__ */ jsx47("span", { style: styles.pulse, "aria-hidden": "true", children: "\u25CF" }),
                      isError && /* @__PURE__ */ jsx47("span", { style: styles.errorIcon, "aria-hidden": "true", children: "\u26A0\uFE0F" }),
                      /* @__PURE__ */ jsx47("span", { children: statusLabel2() })
                    ]
                  }
                ),
                isError && onRetry && /* @__PURE__ */ jsx47(
                  "button",
                  {
                    onClick: onRetry,
                    style: styles.retryButton,
                    "aria-label": "Retry sending message",
                    title: `Retry (attempt ${(message.retryCount ?? 0) + 1})`,
                    children: "\u{1F504} Retry"
                  }
                )
              ] }),
              isError && message.errorMessage && /* @__PURE__ */ jsxs43("div", { style: styles.errorDetail, role: "alert", children: [
                safeErrorText(safeErrorCode(message.errorMessage), locale).title,
                " (",
                safeErrorCode(message.errorMessage),
                ")"
              ] })
            ] })
          ]
        }
      )
    }
  );
};
var styles = {
  messageWrapper: {
    display: "flex",
    width: "100%"
  },
  messageBubble: {
    display: "flex",
    gap: "12px",
    maxWidth: "75%",
    alignItems: "flex-start"
  },
  userBubble: {
    flexDirection: "row-reverse"
  },
  systemBubble: {
    maxWidth: "100%",
    opacity: 0.7
  },
  avatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  avatarText: {
    color: "#f1f5f9",
    fontWeight: 600,
    fontSize: "14px"
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  content: {
    padding: "12px 16px",
    borderRadius: "16px",
    borderTopLeftRadius: "4px",
    backgroundColor: "#1e293b",
    color: "#f1f5f9",
    fontSize: "15px",
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  userContent: {
    borderTopLeftRadius: "16px",
    borderTopRightRadius: "4px",
    backgroundColor: "rgba(255,255,255,0.16)",
    color: "#f1f5f9"
  },
  errorContent: {
    backgroundColor: "rgba(248,113,113,0.15)",
    color: "#fca5a5"
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "6px",
    flexWrap: "wrap"
  },
  timestamp: {
    fontSize: "11px",
    color: "#64748b",
    paddingLeft: "16px"
  },
  userTimestamp: {
    textAlign: "right",
    paddingRight: "16px",
    paddingLeft: 0
  },
  statusBadge: {
    fontSize: "11px",
    color: "#94a3b8",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "rgba(255,255,255,0.05)",
    padding: "2px 8px",
    borderRadius: "4px"
  },
  statusError: {
    color: "#f87171",
    backgroundColor: "rgba(248,113,113,0.1)"
  },
  statusStreaming: {
    color: "#cbd5e1",
    backgroundColor: "rgba(255,255,255,0.08)"
  },
  statusSending: {
    color: "#fbbf24",
    backgroundColor: "rgba(251,191,36,0.1)"
  },
  spinner: {
    fontSize: "10px"
  },
  pulse: {
    fontSize: "10px",
    animation: "pulse 1.5s infinite"
  },
  errorIcon: {
    fontSize: "10px"
  },
  retryButton: {
    fontSize: "11px",
    color: "#cbd5e1",
    backgroundColor: "transparent",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "4px",
    padding: "2px 8px",
    cursor: "pointer",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  errorDetail: {
    fontSize: "12px",
    color: "#f87171",
    marginTop: "6px",
    paddingLeft: "16px"
  },
  pre: {
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    fontSize: "15px",
    lineHeight: 1.6
  }
};

// src/components/chat/ChatInput.tsx
import { useState as useState21, useRef as useRef5, useImperativeHandle, forwardRef, useCallback as useCallback14 } from "react";

// src/components/chat/VoiceButton.tsx
import { useCallback as useCallback13, useEffect as useEffect15, useRef as useRef4, useState as useState20 } from "react";
import { jsx as jsx48, jsxs as jsxs44 } from "react/jsx-runtime";
var colorsLight = {
  surfaceStrong: "#ffffff",
  text: "#0f172a",
  textMuted: "#475569",
  border: "rgba(15,23,42,0.14)",
  primary: "#2563eb",
  danger: "#f87171"
};
var colorsDark = {
  surfaceStrong: baseUiTokens.colors.surfaceStrong,
  text: baseUiTokens.colors.text,
  textMuted: baseUiTokens.colors.textMuted,
  border: baseUiTokens.colors.border,
  primary: baseUiTokens.colors.primary,
  danger: baseUiTokens.colors.danger
};
function VoiceButton({ onTranscript, disabled = false, theme: theme2 = "dark", speakText }) {
  const colors = theme2 === "light" ? colorsLight : colorsDark;
  const SpeechRecognitionCtor = typeof window !== "undefined" ? window.SpeechRecognition ?? window.webkitSpeechRecognition : void 0;
  const available = SpeechRecognitionCtor !== void 0 && typeof window !== "undefined";
  const ttsAvailable = typeof window !== "undefined" && window.speechSynthesis !== void 0;
  const recognitionRef = useRef4(null);
  const finalizedRef = useRef4("");
  const [state, setState] = useState20("idle");
  const stop = useCallback13(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        try {
          recognitionRef.current.abort();
        } catch {
        }
      }
    }
  }, []);
  useEffect15(() => {
    return () => {
      stop();
      if (ttsAvailable) {
        window.speechSynthesis.cancel();
      }
    };
  }, [stop, ttsAvailable]);
  useEffect15(() => {
    if (!speakText || !ttsAvailable || !window.speechSynthesis) return;
    const text2 = speakText.trim();
    if (!text2) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text2);
    utterance.lang = document.documentElement.lang || "en-IN";
    utterance.onstart = () => setState("speaking");
    utterance.onend = () => setState("idle");
    utterance.onerror = () => setState("idle");
    window.speechSynthesis.speak(utterance);
  }, [speakText, ttsAvailable]);
  const start = useCallback13(() => {
    if (!SpeechRecognitionCtor || disabled) return;
    stop();
    finalizedRef.current = "";
    const recognition = new SpeechRecognitionCtor();
    recognition.lang = document.documentElement.lang || "en-IN";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      let finalTranscript = "";
      let interimTranscript = "";
      let hasFinal = false;
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text2 = result[0]?.transcript ?? "";
        if (result.isFinal) {
          hasFinal = true;
          finalTranscript += text2;
        } else {
          interimTranscript += text2;
        }
      }
      setState("listening");
      if (hasFinal && finalTranscript) {
        const previous = finalizedRef.current;
        const current = (previous + finalTranscript).trim();
        const delta = current.toLowerCase().startsWith(previous.toLowerCase()) ? current.slice(previous.length).trimStart() : finalTranscript.trim();
        finalizedRef.current = current;
        onTranscript(delta, true);
      } else if (interimTranscript) {
        onTranscript(interimTranscript, false);
      }
    };
    recognition.onerror = (event) => {
      if (event.error !== "aborted" && event.error !== "no-speech") {
        console.warn("[VoiceButton] speech recognition error:", event.error);
      }
      setState("idle");
    };
    recognition.onend = () => {
      finalizedRef.current = "";
      setState("idle");
    };
    recognitionRef.current = recognition;
    setState("listening");
    try {
      recognition.start();
    } catch {
      setState("idle");
    }
  }, [SpeechRecognitionCtor, disabled, onTranscript, stop]);
  const handlePointerDown = () => {
    if (state === "idle") {
      start();
    }
  };
  const handlePointerUp = () => {
    if (state !== "idle") {
      setState("thinking");
      stop();
    }
  };
  const buttonLabel = state === "listening" ? "Listening\u2026" : state === "thinking" ? "Thinking\u2026" : state === "speaking" ? "Speaking\u2026" : "Push to talk";
  return /* @__PURE__ */ jsx48(
    "button",
    {
      type: "button",
      "aria-label": buttonLabel,
      title: buttonLabel,
      disabled: !available || disabled,
      onPointerDown: handlePointerDown,
      onPointerUp: handlePointerUp,
      onPointerLeave: handlePointerUp,
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        borderRadius: baseUiTokens.radius.md,
        border: `1px solid ${state === "listening" ? colors.danger : colors.border}`,
        backgroundColor: state === "listening" ? colors.danger : colors.surfaceStrong,
        color: state === "listening" ? "#ffffff" : colors.text,
        cursor: !available || disabled ? "not-allowed" : "pointer",
        opacity: !available || disabled ? 0.5 : 1,
        flexShrink: 0
      },
      children: /* @__PURE__ */ jsxs44("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [
        /* @__PURE__ */ jsx48("path", { d: "M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z" }),
        /* @__PURE__ */ jsx48("path", { d: "M19 10v2a7 7 0 0 1-14 0v-2" }),
        /* @__PURE__ */ jsx48("line", { x1: "12", y1: "19", x2: "12", y2: "22" }),
        /* @__PURE__ */ jsx48("line", { x1: "8", y1: "22", x2: "16", y2: "22" })
      ] })
    }
  );
}

// src/components/chat/ChatInput.tsx
import { jsx as jsx49, jsxs as jsxs45 } from "react/jsx-runtime";
var ChatInput = forwardRef(
  ({
    onSend,
    disabled = false,
    placeholder = "\u0938\u0902\u0926\u0947\u0936 \u0932\u093F\u0916\u0947\u0902...",
    maxLength = 4e3,
    voiceSlot,
    ariaLabel = "Message input",
    onVoiceTranscript,
    speakText,
    isStreaming = false,
    onStopStreaming
  }, ref) => {
    const [value, setValue] = useState21("");
    const textareaRef = useRef5(null);
    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      clear: () => setValue(""),
      setValue: (val) => setValue(val),
      getValue: () => value
    }));
    const handleSend = useCallback14(() => {
      const trimmed = value.trim();
      if (trimmed && !disabled) {
        onSend(trimmed);
        setValue("");
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (el) {
            el.style.height = "auto";
          }
        });
        textareaRef.current?.focus();
      }
    }, [value, disabled, onSend]);
    const handleKeyDown = useCallback14(
      (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if (isStreaming) {
            onStopStreaming?.();
            return;
          }
          handleSend();
        }
        if (e.key === "Escape") {
          if (isStreaming) {
            e.preventDefault();
            onStopStreaming?.();
          }
        }
      },
      [handleSend, isStreaming, onStopStreaming]
    );
    const handleChange = (e) => {
      const newValue = e.target.value;
      if (newValue.length <= maxLength) {
        setValue(newValue);
      }
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
      }
    };
    return /* @__PURE__ */ jsxs45("div", { style: styles2.container, role: "form", "aria-label": ariaLabel, children: [
      /* @__PURE__ */ jsxs45("div", { style: styles2.inputWrapper, children: [
        (voiceSlot || onVoiceTranscript) && /* @__PURE__ */ jsx49("div", { style: styles2.voiceSlot, "aria-label": "Voice input", children: voiceSlot ?? /* @__PURE__ */ jsx49(
          VoiceButton,
          {
            onTranscript: (text2, isFinal) => {
              if (!isFinal) return;
              setValue((prev) => prev ? `${prev} ${text2}` : text2);
              textareaRef.current?.focus();
              onVoiceTranscript?.(text2);
            },
            disabled: disabled || isStreaming,
            speakText
          }
        ) }),
        /* @__PURE__ */ jsx49(
          "textarea",
          {
            ref: textareaRef,
            value,
            onChange: handleChange,
            onKeyDown: handleKeyDown,
            placeholder,
            style: styles2.textarea,
            rows: 1,
            "aria-label": ariaLabel,
            "aria-describedby": "chat-input-hint",
            "aria-disabled": disabled,
            "aria-multiline": "true"
          }
        ),
        isStreaming ? /* @__PURE__ */ jsx49(
          "button",
          {
            onClick: onStopStreaming,
            style: styles2.stopButton,
            "aria-label": "Stop streaming response",
            title: "Stop generating (Escape)",
            type: "button",
            children: "\u23F9\uFE0F Stop"
          }
        ) : /* @__PURE__ */ jsx49(
          "button",
          {
            onClick: handleSend,
            disabled: disabled || !value.trim(),
            style: {
              ...styles2.sendButton,
              ...value.trim() && !disabled ? styles2.sendButtonActive : {}
            },
            "aria-label": "Send message",
            type: "button",
            children: /* @__PURE__ */ jsxs45(
              "svg",
              {
                width: "20",
                height: "20",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                strokeWidth: "2",
                strokeLinecap: "round",
                strokeLinejoin: "round",
                "aria-hidden": "true",
                children: [
                  /* @__PURE__ */ jsx49("line", { x1: "22", y1: "2", x2: "11", y2: "13" }),
                  /* @__PURE__ */ jsx49("polygon", { points: "22 2 15 22 11 13 2 9 22 2" })
                ]
              }
            )
          }
        )
      ] }),
      /* @__PURE__ */ jsxs45("div", { id: "chat-input-hint", style: styles2.hint, children: [
        /* @__PURE__ */ jsx49("span", { children: isStreaming ? "Enter or Escape to stop \u2022 Shift+Enter newline" : "Enter \u092D\u0947\u091C\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u2022 Shift+Enter \u0928\u0908 \u092A\u0902\u0915\u094D\u0924\u093F \u0915\u0947 \u0932\u093F\u090F" }),
        /* @__PURE__ */ jsxs45("span", { "aria-label": `Character count ${value.length} of ${maxLength}`, children: [
          value.length,
          "/",
          maxLength
        ] })
      ] })
    ] });
  }
);
ChatInput.displayName = "ChatInput";
var styles2 = {
  container: {
    padding: "16px 20px",
    backgroundColor: "#1e293b",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    flexShrink: 0
  },
  inputWrapper: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-end",
    backgroundColor: "#0f172a",
    borderRadius: "12px",
    border: "1px solid rgba(255,255,255,0.1)",
    padding: "8px 12px"
  },
  textarea: {
    flex: 1,
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
    color: "#f1f5f9",
    fontSize: "15px",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    lineHeight: 1.5,
    resize: "none",
    maxHeight: "150px",
    padding: "8px 4px",
    minWidth: 0
  },
  sendButton: {
    width: "40px",
    height: "40px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "transparent",
    color: "#64748b",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    flexShrink: 0
  },
  sendButtonActive: {
    backgroundColor: "rgba(255,255,255,0.16)",
    border: "1px solid rgba(255,255,255,0.2)",
    color: "#f1f5f9"
  },
  stopButton: {
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1px solid rgba(248,113,113,0.3)",
    backgroundColor: "rgba(248,113,113,0.1)",
    color: "#f87171",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  hint: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "8px",
    fontSize: "12px",
    color: "#64748b",
    paddingLeft: "4px"
  },
  voiceSlot: {
    display: "flex",
    alignItems: "center",
    paddingLeft: "4px",
    flexShrink: 0
  }
};

// src/components/chat/ChatSidebar.tsx
import { jsx as jsx50, jsxs as jsxs46 } from "react/jsx-runtime";
var ChatSidebar = ({
  conversations = [],
  onNewChat,
  onSelectChat,
  selectedChatId,
  onClose,
  locale = "hi"
}) => {
  const t5 = locale === "hi" ? { today: "\u0906\u091C", yesterday: "\u0915\u0932", daysAgo: (d) => `${d} \u0926\u093F\u0928 \u092A\u0939\u0932\u0947`, noChats: "\u0915\u094B\u0908 \u092C\u093E\u0924\u091A\u0940\u0924 \u0928\u0939\u0940\u0902", newChat: "\u0928\u0908 \u092C\u093E\u0924\u091A\u0940\u0924" } : { today: "Today", yesterday: "Yesterday", daysAgo: (d) => `${d}d ago`, noChats: "No conversations yet", newChat: "New chat" };
  const formatDate = (date) => {
    const now = /* @__PURE__ */ new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1e3 * 60 * 60 * 24));
    if (diffDays === 0) return t5.today;
    if (diffDays === 1) return t5.yesterday;
    if (diffDays < 7) return t5.daysAgo(diffDays);
    return new Intl.DateTimeFormat(locale === "hi" ? "hi-IN" : "en-US", { day: "numeric", month: "short" }).format(date);
  };
  return /* @__PURE__ */ jsxs46("aside", { style: styles3.sidebar, role: "complementary", "aria-label": "Chat history sidebar", children: [
    /* @__PURE__ */ jsxs46("div", { style: styles3.header, children: [
      /* @__PURE__ */ jsx50("h1", { style: styles3.logo, "aria-label": "Smart Shikka", children: "Smart Shikka" }),
      onClose && /* @__PURE__ */ jsx50(
        "button",
        {
          onClick: onClose,
          style: styles3.closeButton,
          "aria-label": "Close sidebar",
          type: "button",
          children: "\u2715"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs46(
      "button",
      {
        onClick: onNewChat,
        style: styles3.newChatButton,
        "aria-label": "Start new conversation",
        type: "button",
        children: [
          /* @__PURE__ */ jsxs46(
            "svg",
            {
              width: "18",
              height: "18",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              strokeLinecap: "round",
              strokeLinejoin: "round",
              "aria-hidden": "true",
              children: [
                /* @__PURE__ */ jsx50("line", { x1: "12", y1: "5", x2: "12", y2: "19" }),
                /* @__PURE__ */ jsx50("line", { x1: "5", y1: "12", x2: "19", y2: "12" })
              ]
            }
          ),
          /* @__PURE__ */ jsx50("span", { children: t5.newChat })
        ]
      }
    ),
    /* @__PURE__ */ jsx50("div", { style: styles3.conversationsList, children: conversations.length === 0 ? /* @__PURE__ */ jsx50("div", { style: styles3.emptyConversations, children: /* @__PURE__ */ jsx50("p", { children: t5.noChats }) }) : conversations.map((conv) => /* @__PURE__ */ jsxs46(
      "button",
      {
        onClick: () => onSelectChat?.(conv.id),
        "aria-label": `Conversation: ${conv.title}`,
        "aria-current": selectedChatId === conv.id ? "true" : void 0,
        style: {
          ...styles3.conversationItem,
          ...selectedChatId === conv.id ? styles3.conversationItemActive : {}
        },
        children: [
          /* @__PURE__ */ jsx50("div", { style: styles3.conversationIcon, children: /* @__PURE__ */ jsx50(
            "svg",
            {
              width: "16",
              height: "16",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx50("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" })
            }
          ) }),
          /* @__PURE__ */ jsxs46("div", { style: styles3.conversationContent, children: [
            /* @__PURE__ */ jsx50("div", { style: styles3.conversationTitle, children: conv.title }),
            /* @__PURE__ */ jsx50("div", { style: styles3.conversationPreview, children: conv.preview }),
            /* @__PURE__ */ jsx50("div", { style: styles3.conversationDate, children: formatDate(conv.updatedAt) })
          ] })
        ]
      },
      conv.id
    )) }),
    /* @__PURE__ */ jsx50("div", { style: styles3.footer, children: /* @__PURE__ */ jsx50("div", { style: styles3.version, children: "v2.1.2.0.112 \u2022 PR #200" }) })
  ] });
};
var styles3 = {
  sidebar: {
    width: "280px",
    backgroundColor: "#1e293b",
    borderRight: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0
  },
  header: {
    padding: "20px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  logo: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 700,
    color: "#f1f5f9",
    letterSpacing: "-0.5px"
  },
  newChatButton: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    margin: "16px",
    padding: "12px 16px",
    backgroundColor: "rgba(255,255,255,0.08)",
    color: "#f1f5f9",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  conversationsList: {
    flex: 1,
    overflowY: "auto",
    padding: "0 8px"
  },
  emptyConversations: {
    padding: "20px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "14px"
  },
  conversationItem: {
    width: "100%",
    display: "flex",
    gap: "12px",
    padding: "12px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    textAlign: "left",
    transition: "background-color 0.2s ease",
    marginBottom: "4px",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  conversationItemActive: {
    backgroundColor: "rgba(255,255,255,0.1)"
  },
  conversationIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    backgroundColor: "rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#94a3b8",
    flexShrink: 0
  },
  conversationContent: {
    flex: 1,
    minWidth: 0
  },
  conversationTitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#f1f5f9",
    marginBottom: "2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  conversationPreview: {
    fontSize: "12px",
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: "4px"
  },
  conversationDate: {
    fontSize: "11px",
    color: "#475569"
  },
  closeButton: {
    backgroundColor: "transparent",
    border: "none",
    color: "#94a3b8",
    fontSize: "18px",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "6px"
  },
  footer: {
    padding: "12px 16px",
    borderTop: "1px solid rgba(255,255,255,0.1)"
  },
  version: {
    fontSize: "11px",
    color: "#475569",
    textAlign: "center"
  }
};

// src/components/chat/TypingIndicator.tsx
import { useEffect as useEffect16, useState as useState22 } from "react";
import { jsx as jsx51, jsxs as jsxs47 } from "react/jsx-runtime";
var TypingIndicator = ({
  label = "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0915\u094D\u0937\u093E \u0938\u094B\u091A \u0930\u0939\u093E \u0939\u0948..."
}) => {
  const [dots, setDots] = useState22("");
  useEffect16(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev.length >= 3) return "";
        return prev + ".";
      });
    }, 400);
    return () => clearInterval(interval);
  }, []);
  return /* @__PURE__ */ jsxs47("div", { style: styles4.container, "aria-live": "polite", "aria-label": "Smart Shikka is typing", children: [
    /* @__PURE__ */ jsx51("div", { style: styles4.avatar, "aria-hidden": "true", children: /* @__PURE__ */ jsx51("span", { style: styles4.avatarText, children: "\u0936\u093F" }) }),
    /* @__PURE__ */ jsxs47("div", { style: styles4.bubble, children: [
      /* @__PURE__ */ jsxs47("div", { style: styles4.dotsContainer, "aria-hidden": "true", children: [
        /* @__PURE__ */ jsx51("span", { style: styles4.dot }),
        /* @__PURE__ */ jsx51("span", { style: { ...styles4.dot, animationDelay: "0.2s" } }),
        /* @__PURE__ */ jsx51("span", { style: { ...styles4.dot, animationDelay: "0.4s" } })
      ] }),
      /* @__PURE__ */ jsxs47("span", { style: styles4.label, children: [
        label,
        dots
      ] })
    ] })
  ] });
};
var styles4 = {
  container: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px"
  },
  avatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  avatarText: {
    color: "#f1f5f9",
    fontWeight: 600,
    fontSize: "14px"
  },
  bubble: {
    padding: "12px 16px",
    backgroundColor: "#1e293b",
    borderRadius: "16px",
    borderTopLeftRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  dotsContainer: {
    display: "flex",
    gap: "4px"
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#94a3b8",
    animation: "typingBounce 1s ease-in-out infinite"
  },
  label: {
    fontSize: "13px",
    color: "#94a3b8",
    fontStyle: "italic"
  }
};

// src/ceo/ShikkaPresence.tsx
import { useMemo as useMemo8 } from "react";
import { jsx as jsx52, jsxs as jsxs48 } from "react/jsx-runtime";
var STATE_LABELS = {
  idle: { en: "Idle", hi: "\u0907\u0902\u0924\u091C\u093C\u093E\u0930 \u092E\u0947\u0902" },
  listening: { en: "Listening", hi: "\u0938\u0941\u0928 \u0930\u0939\u093E \u0939\u0948" },
  transcribing: { en: "Transcribing", hi: "\u0932\u093F\u0916 \u0930\u0939\u093E \u0939\u0948" },
  thinking: { en: "Thinking", hi: "\u0938\u094B\u091A \u0930\u0939\u093E \u0939\u0948" },
  using_knowledge: { en: "Using knowledge", hi: "\u091C\u093E\u0928\u0915\u093E\u0930\u0940 \u0916\u094B\u091C \u0930\u0939\u093E \u0939\u0948" },
  browsing: { en: "Browsing", hi: "\u092C\u094D\u0930\u093E\u0909\u091C\u093C \u0915\u0930 \u0930\u0939\u093E \u0939\u0948" },
  coding: { en: "Coding", hi: "\u0915\u094B\u0921 \u0915\u0930 \u0930\u0939\u093E \u0939\u0948" },
  acting: { en: "Acting", hi: "\u0915\u093E\u092E \u0915\u0930 \u0930\u0939\u093E \u0939\u0948" },
  waiting_for_approval: { en: "Waiting for approval", hi: "\u0905\u0928\u0941\u092E\u0924\u093F \u0915\u0940 \u092A\u094D\u0930\u0924\u0940\u0915\u094D\u0937\u093E" },
  speaking: { en: "Speaking", hi: "\u092C\u094B\u0932 \u0930\u0939\u093E \u0939\u0948" },
  error: { en: "Error", hi: "\u0924\u094D\u0930\u0941\u091F\u093F" }
};
function tr6(key, locale) {
  return STATE_LABELS[key]?.[locale] ?? key;
}
function stateColor(state) {
  switch (state) {
    case "idle":
      return warmNeutralTokens.textMuted;
    case "listening":
      return baseUiTokens.colors.primary;
    case "transcribing":
      return baseUiTokens.colors.info;
    case "thinking":
      return baseUiTokens.colors.info;
    case "using_knowledge":
      return baseUiTokens.colors.success;
    case "browsing":
      return baseUiTokens.colors.info;
    case "coding":
      return baseUiTokens.colors.warning;
    case "acting":
      return baseUiTokens.colors.danger;
    case "waiting_for_approval":
      return baseUiTokens.colors.warning;
    case "speaking":
      return baseUiTokens.colors.primary;
    case "error":
      return baseUiTokens.colors.danger;
    default:
      return warmNeutralTokens.textMuted;
  }
}
function stateAnimationClass(state) {
  switch (state) {
    case "idle":
      return "shikka-presence-breathe";
    case "listening":
    case "speaking":
      return "shikka-presence-pulse";
    case "thinking":
      return "shikka-presence-glow";
    case "browsing":
      return "shikka-presence-scan";
    case "coding":
      return "shikka-presence-structured";
    case "acting":
      return "shikka-presence-active";
    case "waiting_for_approval":
      return "shikka-presence-amber";
    case "error":
      return "shikka-presence-error";
    default:
      return "";
  }
}
function ShikkaPresence({ state, size = 64, locale = "en", label, hint, showLabel = true, logo }) {
  const color = stateColor(state);
  const animationClass = stateAnimationClass(state);
  const text2 = label ?? tr6(state, locale);
  const style = useMemo8(
    () => ({
      width: size,
      height: size,
      borderRadius: "50%",
      background: logo ? "transparent" : `radial-gradient(circle at 30% 30%, ${brandColors.primary}, ${brandColors.accent})`,
      boxShadow: `0 0 0 3px ${brandColors.card}, 0 0 20px ${color}40`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: brandColors.primaryFg,
      fontSize: Math.max(10, size / 3),
      fontWeight: 600,
      transition: "box-shadow 300ms ease, transform 300ms ease, background 300ms ease",
      overflow: "hidden"
    }),
    [size, color, logo]
  );
  return /* @__PURE__ */ jsxs48("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: baseUiTokens.spacing.sm }, children: [
    /* @__PURE__ */ jsx52("style", { children: `
        @keyframes shikka-breathe { 0%,100% { transform: scale(1); opacity: 0.9; } 50% { transform: scale(1.03); opacity: 1; } }
        @keyframes shikka-pulse { 0%,100% { transform: scale(1); box-shadow: 0 0 0 4px ${warmNeutralTokens.border}, 0 0 20px ${color}40; } 50% { transform: scale(1.08); box-shadow: 0 0 0 4px ${warmNeutralTokens.border}, 0 0 32px ${color}80; } }
        @keyframes shikka-glow { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.15); } }
        @keyframes shikka-scan { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes shikka-structured { 0%,100% { transform: scale(1); } 25% { transform: scale(1.02) translate(1px, 0); } 75% { transform: scale(1.02) translate(-1px, 0); } }
        @keyframes shikka-active { 0%,100% { transform: scale(1); } 50% { transform: scale(1.06); } }
        @keyframes shikka-amber { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
        @keyframes shikka-error { 0%,50%,100% { transform: translateX(0); } 25% { transform: translateX(-2px); } 75% { transform: translateX(2px); } }
        .shikka-presence-breathe { animation: shikka-breathe 3s ease-in-out infinite; }
        .shikka-presence-pulse { animation: shikka-pulse 1.2s ease-in-out infinite; }
        .shikka-presence-glow { animation: shikka-glow 2s ease-in-out infinite; }
        .shikka-presence-scan::after { content: ''; position: absolute; inset: -8px; border-radius: 50%; border: 2px dashed ${color}; opacity: 0.4; animation: shikka-scan 8s linear infinite; }
        .shikka-presence-structured { animation: shikka-structured 1.5s ease-in-out infinite; }
        .shikka-presence-active { animation: shikka-active 0.8s ease-in-out infinite; }
        .shikka-presence-amber { animation: shikka-amber 1.5s ease-in-out infinite; }
        .shikka-presence-error { animation: shikka-error 0.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .shikka-presence-breathe, .shikka-presence-pulse, .shikka-presence-glow, .shikka-presence-scan::after, .shikka-presence-structured, .shikka-presence-active, .shikka-presence-amber, .shikka-presence-error { animation: none !important; }
        }
      ` }),
    /* @__PURE__ */ jsx52(
      "div",
      {
        role: "status",
        "aria-live": "polite",
        "aria-label": text2,
        className: animationClass,
        style: { ...style, position: "relative" },
        children: logo ? /* @__PURE__ */ jsx52(
          "img",
          {
            src: logo,
            alt: "",
            "aria-hidden": "true",
            style: { width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }
          }
        ) : /* @__PURE__ */ jsx52("span", { "aria-hidden": "true", children: state === "error" ? "!" : "\u0938" })
      }
    ),
    showLabel && /* @__PURE__ */ jsxs48("div", { style: { fontSize: "12px", color: warmNeutralTokens.textMuted, textAlign: "center" }, children: [
      text2,
      hint && /* @__PURE__ */ jsx52("div", { style: { fontSize: "11px", marginTop: 2 }, children: hint })
    ] })
  ] });
}

// src/components/chat/ChatContainer.tsx
import { Fragment as Fragment3, jsx as jsx53, jsxs as jsxs49 } from "react/jsx-runtime";
var ChatContainer = ({
  messages,
  onSendMessage,
  onRetryMessage,
  onBeforeSend,
  onAfterResponse,
  onError,
  onRetry,
  onStopStreaming,
  onVoiceTranscript,
  onNewChat,
  onSelectChat,
  conversations = [],
  isStreaming = false,
  isTyping = false,
  disabled = false,
  placeholder = "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0915\u094D\u0937\u093E \u0938\u0947 \u092C\u093E\u0924 \u0915\u0930\u0947\u0902...",
  voiceInputSlot,
  streamingPreviewContent = "",
  locale = "hi",
  logo
}) => {
  const messagesEndRef = useRef6(null);
  const inputRef = useRef6(null);
  const [sidebarOpen, setSidebarOpen] = useState23(true);
  const [isMobile, setIsMobile] = useState23(false);
  useEffect17(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
      else setSidebarOpen(true);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  useEffect17(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping, streamingPreviewContent]);
  useEffect17(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && isStreaming) {
        e.preventDefault();
        onStopStreaming?.();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isStreaming, onStopStreaming]);
  const handleRetry = useCallback15(
    (messageId) => {
      onRetry?.(messageId);
      onRetryMessage?.(messageId);
    },
    [onRetry, onRetryMessage]
  );
  const handleSend = useCallback15(
    async (content) => {
      try {
        if (onBeforeSend) {
          const result = await onBeforeSend(content);
          if (!result.allowed) {
            console.warn("[SmartShikka] onBeforeSend blocked message:", result.reason);
            return;
          }
          if (result.redactedContent && result.redactedContent !== content) {
            onSendMessage(result.redactedContent);
            return;
          }
        }
        onSendMessage(content);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("chat_send_failed");
        onError?.(error, { phase: "beforeSend" });
      }
    },
    [onBeforeSend, onSendMessage, onError]
  );
  const handleVoiceTranscript = useCallback15(
    (text2) => {
      onVoiceTranscript?.(text2);
      inputRef.current?.focus();
    },
    [onVoiceTranscript]
  );
  return /* @__PURE__ */ jsxs49(
    "div",
    {
      className: "smart-shikka-chat",
      role: "log",
      "aria-live": "polite",
      "aria-label": "Smart Shikka Chat",
      style: styles5.container,
      children: [
        isMobile && sidebarOpen && /* @__PURE__ */ jsx53(
          "div",
          {
            role: "presentation",
            "aria-hidden": "true",
            onClick: () => setSidebarOpen(false),
            style: styles5.mobileOverlay
          }
        ),
        sidebarOpen && /* @__PURE__ */ jsx53(
          ChatSidebar,
          {
            conversations,
            onNewChat: () => {
              onNewChat?.();
              if (isMobile) setSidebarOpen(false);
            },
            onSelectChat: (id) => {
              onSelectChat?.(id);
              if (isMobile) setSidebarOpen(false);
            },
            selectedChatId: conversations.find((c) => c.id === conversations[0]?.id)?.id,
            onClose: isMobile ? () => setSidebarOpen(false) : void 0,
            locale
          }
        ),
        /* @__PURE__ */ jsxs49("div", { style: styles5.mainArea, children: [
          isMobile && /* @__PURE__ */ jsxs49("div", { style: styles5.mobileHeader, children: [
            /* @__PURE__ */ jsx53(
              "button",
              {
                onClick: () => setSidebarOpen(!sidebarOpen),
                "aria-label": sidebarOpen ? "Close sidebar" : "Open sidebar",
                "aria-expanded": sidebarOpen,
                type: "button",
                style: styles5.hamburger,
                children: "\u2630"
              }
            ),
            /* @__PURE__ */ jsx53("span", { style: styles5.mobileTitle, children: "Smart Shikka" })
          ] }),
          /* @__PURE__ */ jsx53(
            "div",
            {
              style: styles5.messagesArea,
              role: "region",
              "aria-label": "Messages",
              tabIndex: 0,
              children: messages.length === 0 && !isTyping && !streamingPreviewContent ? /* @__PURE__ */ jsxs49("div", { style: styles5.emptyState, children: [
                /* @__PURE__ */ jsx53(ShikkaPresence, { state: "idle", size: 80, locale, logo }),
                /* @__PURE__ */ jsx53("h2", { style: styles5.emptyTitle, children: "Smart Shikka" }),
                /* @__PURE__ */ jsx53("p", { style: styles5.emptySubtitle, children: locale === "hi" ? "\u092E\u0941\u091D\u0938\u0947 \u0915\u0941\u091B \u092D\u0940 \u092A\u0942\u091B\u0947\u0902, \u092E\u0948\u0902 \u0906\u092A\u0915\u0940 \u092E\u0926\u0926 \u0915\u0947 \u0932\u093F\u090F \u092F\u0939\u093E\u0902 \u0939\u0942\u0902" : "Ask me anything \u2014 I am here to help." }),
                /* @__PURE__ */ jsx53("p", { style: styles5.emptyHint, children: locale === "hi" ? "Enter \u092D\u0947\u091C\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u2022 Shift+Enter \u0928\u0908 \u092A\u0902\u0915\u094D\u0924\u093F" : "Enter to send \u2022 Shift+Enter for new line" })
              ] }) : /* @__PURE__ */ jsxs49(Fragment3, { children: [
                messages.map((message, index) => /* @__PURE__ */ jsx53(
                  ChatMessage,
                  {
                    message,
                    onRetry: message.status === "error" ? () => handleRetry(message.id) : void 0,
                    isLastAssistant: message.role === "assistant" && index === messages.length - 1,
                    onAfterRender: index === messages.length - 1 && message.role === "assistant" ? () => onAfterResponse?.(message) : void 0,
                    locale
                  },
                  message.id
                )),
                isStreaming && streamingPreviewContent && /* @__PURE__ */ jsx53(
                  ChatMessage,
                  {
                    message: {
                      id: "streaming-preview",
                      role: "assistant",
                      content: streamingPreviewContent,
                      status: "streaming"
                    },
                    isStreaming: true,
                    locale
                  }
                ),
                isTyping && !isStreaming && /* @__PURE__ */ jsx53(TypingIndicator, {}),
                /* @__PURE__ */ jsx53("div", { ref: messagesEndRef, "aria-hidden": "true" })
              ] })
            }
          ),
          /* @__PURE__ */ jsx53(
            ChatInput,
            {
              ref: inputRef,
              onSend: handleSend,
              disabled,
              placeholder,
              voiceSlot: voiceInputSlot,
              onVoiceTranscript: handleVoiceTranscript,
              speakText: messages.filter((m) => m.role === "assistant").slice(-1)[0]?.content,
              isStreaming,
              onStopStreaming,
              ariaLabel: "Message input"
            }
          )
        ] })
      ]
    }
  );
};
var styles5 = {
  container: {
    display: "flex",
    height: "100%",
    backgroundColor: "#0f172a",
    color: "#e2e8f0",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    position: "relative"
  },
  mainArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minWidth: 0
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "40px"
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "20px"
  },
  emptyTitle: {
    fontSize: "28px",
    fontWeight: 600,
    color: "#f8fafc",
    margin: "0 0 12px 0"
  },
  emptySubtitle: {
    fontSize: "16px",
    color: "#94a3b8",
    margin: "0 0 8px 0"
  },
  emptyHint: {
    fontSize: "13px",
    color: "#64748b",
    margin: 0
  },
  mobileOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 40
  },
  mobileHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    backgroundColor: "#1e293b",
    flexShrink: 0
  },
  hamburger: {
    backgroundColor: "transparent",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#f1f5f9",
    borderRadius: "8px",
    padding: "6px 10px",
    fontSize: "18px",
    cursor: "pointer"
  },
  mobileTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#f1f5f9"
  }
};

// src/components/chat/ModelPicker.tsx
import { jsx as jsx54 } from "react/jsx-runtime";
var PROFILE_OPTIONS = [
  { id: "fast", label: "Fast", shortLabel: "Fast" },
  { id: "reasoning", label: "Reasoning", shortLabel: "Reasoning" },
  { id: "auto", label: "Auto", shortLabel: "Auto" }
];
var colorsLight2 = {
  surface: "#f8fafc",
  surfaceStrong: "#ffffff",
  text: "#0f172a",
  textMuted: "#475569",
  border: "rgba(15,23,42,0.14)",
  // Neutral selected chip — same surface pattern as the conversation list.
  active: "#e2e8f0",
  activeText: "#0f172a"
};
var colorsDark2 = {
  surface: baseUiTokens.colors.surface,
  surfaceStrong: baseUiTokens.colors.surfaceStrong,
  text: baseUiTokens.colors.text,
  textMuted: baseUiTokens.colors.textMuted,
  border: baseUiTokens.colors.border,
  active: "rgba(255,255,255,0.14)",
  activeText: "#f1f5f9"
};
function ModelPicker({ value, onChange, theme: theme2 = "dark", disabled = false }) {
  const colors = theme2 === "light" ? colorsLight2 : colorsDark2;
  return /* @__PURE__ */ jsx54(
    "div",
    {
      role: "radiogroup",
      "aria-label": "Chat model",
      style: {
        display: "flex",
        gap: baseUiTokens.spacing.sm,
        flexWrap: "wrap"
      },
      children: PROFILE_OPTIONS.map((option) => {
        const active = option.id === value;
        return /* @__PURE__ */ jsx54(
          "button",
          {
            type: "button",
            role: "radio",
            "aria-checked": active,
            disabled,
            onClick: () => onChange(option.id),
            style: {
              padding: `${baseUiTokens.spacing.sm} ${baseUiTokens.spacing.md}`,
              borderRadius: baseUiTokens.radius.md,
              border: `1px solid ${colors.border}`,
              backgroundColor: active ? colors.active : colors.surfaceStrong,
              color: active ? colors.activeText : colors.text,
              cursor: disabled ? "not-allowed" : "pointer",
              fontSize: "13px",
              fontWeight: 500,
              opacity: disabled ? 0.6 : 1
            },
            children: option.label
          },
          option.id
        );
      })
    }
  );
}

// src/components/workflow/WorkflowCanvas.tsx
import { useRef as useRef7, useState as useState24 } from "react";
import { jsx as jsx55, jsxs as jsxs50 } from "react/jsx-runtime";
var WorkflowCanvas = ({
  nodes,
  onNodesChange,
  selectedNodeId,
  onSelectNode,
  readOnly = false,
  previewResults,
  highlightUnapproved = false
}) => {
  const canvasRef = useRef7(null);
  const [dragState, setDragState] = useState24(null);
  const handleMouseDown = (e, nodeId) => {
    if (readOnly) return;
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDragState({
      nodeId,
      offsetX: e.clientX - rect.left - node.position.x,
      offsetY: e.clientY - rect.top - node.position.y
    });
    onSelectNode?.(nodeId);
  };
  const handleMouseMove = (e) => {
    if (!dragState || readOnly) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const newX = e.clientX - rect.left - dragState.offsetX;
    const newY = e.clientY - rect.top - dragState.offsetY;
    const updatedNodes = nodes.map(
      (n) => n.id === dragState.nodeId ? { ...n, position: { x: newX, y: newY } } : n
    );
    onNodesChange?.(updatedNodes);
  };
  const handleMouseUp = () => setDragState(null);
  const getNodeColor = (type) => {
    switch (type) {
      case "trigger":
        return "#60a5fa";
      case "action":
        return "#34d399";
      case "condition":
        return "#fbbf24";
      case "ai":
        return "#a78bfa";
      case "end":
        return "#f87171";
      default:
        return "#94a3b8";
    }
  };
  const getNodeIcon = (type) => {
    switch (type) {
      case "trigger":
        return "\u26A1";
      case "action":
        return "\u25B6";
      case "condition":
        return "?";
      case "ai":
        return "\u{1F916}";
      case "end":
        return "\u25A0";
      default:
        return "\u25CF";
    }
  };
  return /* @__PURE__ */ jsxs50(
    "div",
    {
      ref: canvasRef,
      style: styles6.canvas,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseUp,
      role: "region",
      "aria-label": "Workflow canvas",
      children: [
        /* @__PURE__ */ jsx55("div", { style: styles6.grid, "aria-hidden": "true" }),
        nodes.map((node) => {
          const preview = previewResults?.[node.id];
          const isUnapproved = highlightUnapproved && node.approvalRequired === true;
          const isBlocked = preview?.blockedByApproval || preview?.blockedBySafetyGate;
          return /* @__PURE__ */ jsxs50(
            "div",
            {
              role: "button",
              tabIndex: 0,
              "aria-label": `${node.type} node ${node.label}${isUnapproved ? " (requires approval)" : ""}${isBlocked ? " (blocked in preview)" : ""}`,
              style: {
                ...styles6.node,
                left: node.position.x,
                top: node.position.y,
                borderColor: selectedNodeId === node.id ? "#60a5fa" : isUnapproved ? "#f87171" : getNodeColor(node.type),
                backgroundColor: selectedNodeId === node.id ? "rgba(96, 165, 250, 0.15)" : isBlocked ? "rgba(248,113,113,0.08)" : "#1e293b",
                opacity: isBlocked ? 0.7 : 1
              },
              onMouseDown: (e) => handleMouseDown(e, node.id),
              onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectNode?.(node.id);
                }
              },
              children: [
                /* @__PURE__ */ jsxs50("div", { style: { ...styles6.nodeHeader, backgroundColor: getNodeColor(node.type) }, children: [
                  /* @__PURE__ */ jsx55("span", { style: styles6.nodeIcon, "aria-hidden": "true", children: getNodeIcon(node.type) }),
                  /* @__PURE__ */ jsx55("span", { style: styles6.nodeType, children: node.type.toUpperCase() }),
                  node.approvalRequired && /* @__PURE__ */ jsx55("span", { style: styles6.approvalBadge, title: node.approvalReason || "Approval required", children: "\\u26a0\\ufe0f" })
                ] }),
                /* @__PURE__ */ jsx55("div", { style: styles6.nodeLabel, children: node.label }),
                node.description && /* @__PURE__ */ jsx55("div", { style: styles6.nodeDesc, children: node.description }),
                preview && /* @__PURE__ */ jsxs50("div", { style: styles6.previewOverlay, role: "status", children: [
                  preview.blockedByApproval && /* @__PURE__ */ jsx55("span", { style: styles6.previewBadgeError, children: "\\u26a0\\ufe0f Approval required" }),
                  preview.blockedBySafetyGate && /* @__PURE__ */ jsx55("span", { style: styles6.previewBadgeWarn, children: "\\ud83d\\udee1\\ufe0f Safety gate" }),
                  preview.wouldExecute && /* @__PURE__ */ jsx55("span", { style: styles6.previewBadgeOk, children: "\\u2705 Would execute" })
                ] })
              ]
            },
            node.id
          );
        }),
        nodes.length === 0 && /* @__PURE__ */ jsxs50("div", { style: styles6.emptyState, children: [
          /* @__PURE__ */ jsx55("div", { style: styles6.emptyIcon, "aria-hidden": "true", children: "\\ud83d\\udccb" }),
          /* @__PURE__ */ jsx55("h3", { style: { color: "#94a3b8", margin: "8px 0" }, children: "\\u0938\\u094d\\u092e\\u093e\\u0930\\u094d\\u091f \\u0936\\u093f\\u0915\\u094d\\u0937\\u093e \\u0935\\u0930\\u094d\\u0915\\u092b\\u093c\\u094d\\u0932\\u094b \\u092c\\u093f\\u0932\\u094d\\u0921\\u0930" }),
          /* @__PURE__ */ jsx55("p", { style: { color: "#64748b", margin: 0 }, children: "\\u092c\\u093e\\u090f\\u0902 \\u092a\\u0948\\u0928\\u0932 \\u0938\\u0947 \\u0928\\u094b\\u0921 \\u0916\\u0940\\u0902\\u091a\\u0915\\u0930 \\u091c\\u094b\\u095c\\u0947\\u0902" })
        ] })
      ]
    }
  );
};
var styles6 = {
  canvas: {
    flex: 1,
    position: "relative",
    backgroundColor: "#0f172a",
    backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)",
    backgroundSize: "20px 20px",
    overflow: "hidden",
    cursor: "default"
  },
  grid: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none"
  },
  node: {
    position: "absolute",
    minWidth: "180px",
    borderRadius: "12px",
    border: "2px solid",
    cursor: "move",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    userSelect: "none",
    outline: "none"
  },
  nodeHeader: {
    padding: "8px 12px",
    borderTopLeftRadius: "10px",
    borderTopRightRadius: "10px",
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  nodeIcon: {
    fontSize: "14px"
  },
  nodeType: {
    fontSize: "10px",
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "0.5px"
  },
  nodeLabel: {
    padding: "12px",
    fontSize: "14px",
    fontWeight: 500,
    color: "#f1f5f9"
  },
  nodeDesc: {
    padding: "0 12px 12px",
    fontSize: "12px",
    color: "#94a3b8"
  },
  emptyState: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    textAlign: "center"
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "16px"
  },
  approvalBadge: {
    marginLeft: "auto",
    fontSize: "12px",
    cursor: "help"
  },
  previewOverlay: {
    padding: "8px 12px",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    fontSize: "11px"
  },
  previewBadgeError: {
    color: "#f87171"
  },
  previewBadgeWarn: {
    color: "#fbbf24"
  },
  previewBadgeOk: {
    color: "#34d399"
  }
};

// src/components/workflow/WorkflowSidebar.tsx
import { jsx as jsx56, jsxs as jsxs51 } from "react/jsx-runtime";
var nodeTemplates = [
  { type: "trigger", label: "\u0936\u0947\u0921\u094D\u092F\u0942\u0932 \u091F\u094D\u0930\u093F\u0917\u0930", description: "\u0938\u092E\u092F \u092A\u0930 \u091A\u0932\u0947", icon: "\u23F0" },
  { type: "trigger", label: "\u0935\u0947\u092C\u0939\u0941\u0915 \u091F\u094D\u0930\u093F\u0917\u0930", description: "HTTP request \u0938\u0947 \u091A\u0932\u0947", icon: "\u{1F310}" },
  { type: "action", label: "\u0938\u0902\u0926\u0947\u0936 \u092D\u0947\u091C\u0947\u0902", description: "AI \u0938\u0947 \u091C\u0935\u093E\u092C \u092D\u0947\u091C\u0947\u0902", icon: "\u2709\uFE0F" },
  { type: "action", label: "API \u0915\u0949\u0932", description: "\u092C\u093E\u0939\u0930\u0940 API \u0915\u094B \u0915\u0949\u0932 \u0915\u0930\u0947\u0902", icon: "\u{1F517}" },
  { type: "condition", label: "\u0936\u0930\u094D\u0924 \u091C\u093E\u0902\u091A\u0947\u0902", description: "if/else branching", icon: "\u{1F500}" },
  { type: "ai", label: "AI \u092A\u094D\u0930\u094B\u0938\u0947\u0938\u093F\u0902\u0917", description: "LLM \u0938\u0947 \u092A\u094D\u0930\u094B\u0938\u0947\u0938 \u0915\u0930\u0947\u0902", icon: "\u{1F916}" },
  { type: "end", label: "\u0938\u092E\u093E\u092A\u094D\u0924", description: "\u0935\u0930\u094D\u0915\u092B\u093C\u094D\u0932\u094B \u0916\u0924\u094D\u092E \u0915\u0930\u0947\u0902", icon: "\u2705" }
];
var WorkflowSidebar = ({ onAddNode }) => {
  const getNodeColor = (type) => {
    switch (type) {
      case "trigger":
        return "#60a5fa";
      case "action":
        return "#34d399";
      case "condition":
        return "#fbbf24";
      case "ai":
        return "#a78bfa";
      case "end":
        return "#f87171";
      default:
        return "#94a3b8";
    }
  };
  return /* @__PURE__ */ jsxs51("aside", { style: styles7.sidebar, role: "complementary", "aria-label": "Workflow node palette", children: [
    /* @__PURE__ */ jsxs51("div", { style: styles7.header, children: [
      /* @__PURE__ */ jsx56("h2", { style: styles7.title, children: "\u{1F4CB} \u0935\u0930\u094D\u0915\u092B\u093C\u094D\u0932\u094B \u092C\u093F\u0932\u094D\u0921\u0930" }),
      /* @__PURE__ */ jsx56("p", { style: styles7.subtitle, children: "Smart Shikka v2.1.2.0.112" })
    ] }),
    /* @__PURE__ */ jsxs51("div", { style: styles7.section, children: [
      /* @__PURE__ */ jsx56("h3", { style: styles7.sectionTitle, children: "\u{1F527} \u0928\u094B\u0921\u094D\u0938" }),
      nodeTemplates.map((template, idx) => /* @__PURE__ */ jsxs51(
        "button",
        {
          onClick: () => onAddNode?.(template.type),
          style: styles7.nodeButton,
          type: "button",
          "aria-label": `Add ${template.label} node`,
          children: [
            /* @__PURE__ */ jsx56("div", { style: { ...styles7.nodeIconWrap, backgroundColor: getNodeColor(template.type) }, "aria-hidden": "true", children: template.icon }),
            /* @__PURE__ */ jsxs51("div", { style: styles7.nodeInfo, children: [
              /* @__PURE__ */ jsx56("div", { style: styles7.nodeLabel, children: template.label }),
              /* @__PURE__ */ jsx56("div", { style: styles7.nodeDesc, children: template.description })
            ] })
          ]
        },
        idx
      ))
    ] }),
    /* @__PURE__ */ jsx56("div", { style: styles7.footer, children: /* @__PURE__ */ jsx56("div", { style: styles7.version, children: "v2.1.2.0.112 \u2022 PR #200" }) })
  ] });
};
var styles7 = {
  sidebar: {
    width: "280px",
    backgroundColor: "#1e293b",
    borderRight: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  header: {
    padding: "20px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.1)"
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 700,
    color: "#f1f5f9"
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: "12px",
    color: "#64748b"
  },
  section: {
    flex: 1,
    padding: "16px",
    overflowY: "auto"
  },
  sectionTitle: {
    margin: "0 0 12px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  },
  nodeButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "8px",
    cursor: "pointer",
    marginBottom: "8px",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  nodeIconWrap: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    flexShrink: 0
  },
  nodeInfo: {
    flex: 1,
    textAlign: "left"
  },
  nodeLabel: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#f1f5f9"
  },
  nodeDesc: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "2px"
  },
  footer: {
    padding: "12px 16px",
    borderTop: "1px solid rgba(255,255,255,0.1)"
  },
  version: {
    fontSize: "11px",
    color: "#475569",
    textAlign: "center"
  }
};

// src/components/workflow/WorkflowToolbar.tsx
import { jsx as jsx57, jsxs as jsxs52 } from "react/jsx-runtime";
var WorkflowToolbar = ({
  workflowName,
  onNameChange,
  onSave,
  onRunPreview,
  onPublish,
  isRunning = false,
  isPreviewMode = true,
  onTogglePreviewMode,
  hasUnapprovedRiskyNodes = false,
  onAudit,
  workflowId
}) => {
  const logAudit = (action) => {
    if (!onAudit || !workflowId) return;
    onAudit({
      timestamp: /* @__PURE__ */ new Date(),
      action,
      workflowId,
      dryRun: isPreviewMode
    });
  };
  const handleSave = () => {
    logAudit("workflow_updated");
    onSave?.();
  };
  const handleRunPreview = () => {
    logAudit("dry_run_executed");
    onRunPreview?.();
  };
  const handlePublish = () => {
    logAudit("workflow_published");
    onPublish?.();
  };
  return /* @__PURE__ */ jsxs52("header", { style: styles8.toolbar, role: "toolbar", "aria-label": "Workflow toolbar", children: [
    /* @__PURE__ */ jsxs52("div", { style: styles8.left, children: [
      /* @__PURE__ */ jsx57(
        "input",
        {
          type: "text",
          value: workflowName,
          onChange: (e) => onNameChange?.(e.target.value),
          style: styles8.nameInput,
          placeholder: "\\u0935\\u0930\\u094d\\u0915\\u092b\\u093c\\u094d\\u0932\\u094b \\u0915\\u093e \\u0928\\u093e\\u092e...",
          "aria-label": "Workflow name"
        }
      ),
      /* @__PURE__ */ jsxs52("label", { style: styles8.toggleLabel, children: [
        /* @__PURE__ */ jsx57(
          "input",
          {
            type: "checkbox",
            checked: isPreviewMode,
            onChange: (e) => onTogglePreviewMode?.(e.target.checked),
            style: styles8.toggleInput,
            "aria-label": "Dry-run preview mode"
          }
        ),
        /* @__PURE__ */ jsx57("span", { style: styles8.toggleText, children: isPreviewMode ? "\u{1F6E1}\uFE0F Dry-run (preview only)" : "\u26A0\uFE0F Live mode" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs52("div", { style: styles8.right, children: [
      /* @__PURE__ */ jsx57(
        "button",
        {
          onClick: handleSave,
          style: styles8.btnPrimary,
          type: "button",
          "aria-label": "Save workflow",
          children: "\\ud83d\\udcbe \\u0938\\u0939\\u0947\\u091c\\u0947\\u0902"
        }
      ),
      /* @__PURE__ */ jsx57(
        "button",
        {
          onClick: handleRunPreview,
          disabled: isRunning || hasUnapprovedRiskyNodes,
          style: {
            ...styles8.btnSuccess,
            ...hasUnapprovedRiskyNodes ? styles8.btnDisabled : {}
          },
          type: "button",
          "aria-label": isPreviewMode ? "Run dry-run preview" : "Run workflow",
          title: hasUnapprovedRiskyNodes ? "Risky nodes require approval before preview" : isPreviewMode ? "This will only show a preview. No real execution." : "Real execution is server-side only",
          children: isRunning ? "\u23F9 \u0930\u094B\u0915\u0947\u0902" : "\u25B6\uFE0F \u092A\u094D\u0930\u093F\u0935\u094D\u092F\u0942 \u091A\u0932\u093E\u090F\u0902"
        }
      ),
      /* @__PURE__ */ jsx57(
        "button",
        {
          onClick: handlePublish,
          style: styles8.btnSecondary,
          type: "button",
          "aria-label": "Publish workflow",
          children: "\\ud83d\\ude80 \\u092a\\u092c\\u094d\\u0932\\u093f\\u0936"
        }
      )
    ] })
  ] });
};
var styles8 = {
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 20px",
    backgroundColor: "#1e293b",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: "16px"
  },
  nameInput: {
    backgroundColor: "transparent",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "6px",
    padding: "8px 12px",
    fontSize: "16px",
    fontWeight: 600,
    color: "#f1f5f9",
    outline: "none",
    minWidth: "250px"
  },
  right: {
    display: "flex",
    gap: "8px"
  },
  btnPrimary: {
    padding: "8px 16px",
    backgroundColor: "#60a5fa",
    color: "#0f172a",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer"
  },
  btnSuccess: {
    padding: "8px 16px",
    backgroundColor: "#34d399",
    color: "#0f172a",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer"
  },
  btnDanger: {
    padding: "8px 16px",
    backgroundColor: "#f87171",
    color: "#0f172a",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer"
  },
  btnSecondary: {
    padding: "8px 16px",
    backgroundColor: "transparent",
    color: "#94a3b8",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer"
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed"
  },
  toggleLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    color: "#94a3b8",
    cursor: "pointer"
  },
  toggleInput: {
    cursor: "pointer"
  },
  toggleText: {
    fontSize: "12px",
    fontWeight: 500
  }
};

// src/components/workflow/workflow-validation.ts
var RISKY_NODE_TYPES = ["action", "ai"];
function isRisky(node) {
  if (node.approvalRequired) return true;
  if (node.riskLevel && ["high", "critical"].includes(node.riskLevel)) return true;
  if (RISKY_NODE_TYPES.includes(node.type) && node.approvalRequired !== false) {
    return true;
  }
  return false;
}
function validateWorkflow(definition) {
  const errors = [];
  const nodeIds = new Set(definition.nodes.map((n) => n.id));
  const seenNodeIds = /* @__PURE__ */ new Set();
  for (const node of definition.nodes) {
    if (seenNodeIds.has(node.id)) {
      errors.push({
        nodeId: node.id,
        message: `Duplicate node ID: ${node.id}`,
        severity: "error"
      });
    }
    seenNodeIds.add(node.id);
  }
  const seenEdgeIds = /* @__PURE__ */ new Set();
  for (const edge of definition.edges) {
    if (seenEdgeIds.has(edge.id)) {
      errors.push({
        message: `Duplicate edge ID: ${edge.id}`,
        severity: "error"
      });
    }
    seenEdgeIds.add(edge.id);
  }
  for (const edge of definition.edges) {
    if (!nodeIds.has(edge.source)) {
      errors.push({
        message: `Edge references missing source node: ${edge.source}`,
        severity: "error"
      });
    }
    if (!nodeIds.has(edge.target)) {
      errors.push({
        message: `Edge references missing target node: ${edge.target}`,
        severity: "error"
      });
    }
    if (edge.source === edge.target) {
      errors.push({
        message: `Edge cannot connect a node to itself: ${edge.id}`,
        severity: "error"
      });
    }
  }
  const triggers = definition.nodes.filter((n) => n.type === "trigger");
  if (triggers.length === 0) {
    errors.push({
      message: "Workflow must have at least one trigger node",
      severity: "error"
    });
  }
  const ends = definition.nodes.filter((n) => n.type === "end");
  if (ends.length === 0) {
    errors.push({
      message: "Workflow must have at least one end node",
      severity: "warning"
    });
  }
  const connectedNodeIds = /* @__PURE__ */ new Set();
  for (const edge of definition.edges) {
    connectedNodeIds.add(edge.source);
    connectedNodeIds.add(edge.target);
  }
  for (const node of definition.nodes) {
    if (!connectedNodeIds.has(node.id) && definition.nodes.length > 1) {
      errors.push({
        nodeId: node.id,
        message: `Node "${node.label}" is not connected to any edge`,
        severity: "warning"
      });
    }
  }
  for (const node of definition.nodes) {
    if (isRisky(node) && !node.approvalRequired && !node.riskLevel) {
      errors.push({
        nodeId: node.id,
        message: `Node "${node.label}" is a risky type but has no riskLevel or approvalRequired set`,
        severity: "warning"
      });
    }
  }
  const unapprovedNodes = definition.nodes.filter(
    (n) => isRisky(n) && n.approvalRequired === true
  );
  return {
    valid: errors.filter((e) => e.severity === "error").length === 0,
    errors,
    hasUnapprovedRiskyNodes: unapprovedNodes.length > 0,
    unapprovedNodes
  };
}
function computeDryRunPreview(definition, startNodeId) {
  const visited = /* @__PURE__ */ new Set();
  const queue = startNodeId ? [startNodeId] : definition.nodes.filter((n) => n.type === "trigger").map((n) => n.id);
  const result = [];
  const adjacency = /* @__PURE__ */ new Map();
  for (const edge of definition.edges) {
    const list = adjacency.get(edge.source) || [];
    list.push(edge.target);
    adjacency.set(edge.source, list);
  }
  while (queue.length > 0) {
    const currentId = queue.shift();
    if (visited.has(currentId)) continue;
    visited.add(currentId);
    const node = definition.nodes.find((n) => n.id === currentId);
    if (!node) continue;
    const isRiskyNode = isRisky(node);
    const blockedByApproval = isRiskyNode && node.approvalRequired === true;
    const blockedBySafetyGate = isRiskyNode && definition.previewOnly !== false;
    const warnings = [];
    if (blockedByApproval) warnings.push(`Approval required: ${node.approvalReason || node.label}`);
    if (blockedBySafetyGate) warnings.push("Safety gate: dry-run only in UI. Real execution is server-side.");
    if (node.type === "ai" || node.type === "action") warnings.push("No real provider call from UI preview.");
    result.push({
      nodeId: currentId,
      wouldExecute: !blockedByApproval && !blockedBySafetyGate,
      blockedByApproval,
      blockedBySafetyGate,
      warnings
    });
    const next = adjacency.get(currentId) || [];
    for (const target of next) {
      if (!visited.has(target)) queue.push(target);
    }
  }
  return result;
}

// src/components/voice/VoiceRecorder.tsx
import { useCallback as useCallback16, useEffect as useEffect18, useMemo as useMemo9, useRef as useRef8, useState as useState25 } from "react";
import { Fragment as Fragment4, jsx as jsx58, jsxs as jsxs53 } from "react/jsx-runtime";
var voiceRecorderErrorMessages = {
  unsupported_browser: "\u092F\u0939 \u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930 voice recording support \u0928\u0939\u0940\u0902 \u0915\u0930\u0924\u093E\u0964",
  insecure_context: "Voice recording \u0915\u0947 \u0932\u093F\u090F \u0938\u0941\u0930\u0915\u094D\u0937\u093F\u0924 HTTPS context \u091A\u093E\u0939\u093F\u090F\u0964",
  permission_denied: "Microphone permission \u0928\u0939\u0940\u0902 \u092E\u093F\u0932\u0940\u0964 \u0915\u0943\u092A\u092F\u093E \u0905\u0928\u0941\u092E\u0924\u093F \u0926\u0947\u0915\u0930 \u0926\u094B\u092C\u093E\u0930\u093E \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902\u0964",
  device_not_found: "Microphone device \u0928\u0939\u0940\u0902 \u092E\u093F\u0932\u093E\u0964 \u0915\u0943\u092A\u092F\u093E device connect \u0915\u0930\u0915\u0947 \u0926\u094B\u092C\u093E\u0930\u093E \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902\u0964",
  recorder_unavailable: "\u0907\u0938 browser \u092E\u0947\u0902 MediaRecorder \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
  max_size_exceeded: "Recording size limit \u0938\u0947 \u091C\u094D\u092F\u093E\u0926\u093E \u0939\u094B \u0917\u0908, \u0907\u0938\u0932\u093F\u090F recording \u0930\u094B\u0915 \u0926\u0940 \u0917\u0908\u0964",
  recording_failed: "Voice recording \u092A\u0942\u0930\u0940 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0940\u0964 \u0915\u0943\u092A\u092F\u093E \u0926\u094B\u092C\u093E\u0930\u093E \u0915\u094B\u0936\u093F\u0936 \u0915\u0930\u0947\u0902\u0964",
  unknown: "Voice recording \u092E\u0947\u0902 \u0905\u091C\u094D\u091E\u093E\u0924 \u0938\u092E\u0938\u094D\u092F\u093E \u0906\u0908\u0964"
};
var DEFAULT_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg"
];
var selectSupportedVoiceMimeType = (mimeTypes = DEFAULT_MIME_TYPES) => {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") {
    return "";
  }
  return mimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? "";
};
var mapVoiceRecorderError = (error) => {
  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    if (error.name === "NotAllowedError" || error.name === "SecurityError") {
      return {
        code: "permission_denied",
        message: voiceRecorderErrorMessages.permission_denied,
        cause: error
      };
    }
    if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
      return {
        code: "device_not_found",
        message: voiceRecorderErrorMessages.device_not_found,
        cause: error
      };
    }
  }
  return {
    code: "recording_failed",
    message: voiceRecorderErrorMessages.recording_failed,
    cause: error
  };
};
var createVoiceRecorderError = (code, cause) => ({
  code,
  message: voiceRecorderErrorMessages[code],
  cause,
  recoverable: code !== "unsupported_browser" && code !== "device_not_found"
});
var stopVoiceMediaStreamTracks = (stream) => {
  stream?.getTracks().forEach((track) => {
    track.stop();
  });
};
var formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};
var VoiceRecorder = ({
  onRecordingComplete,
  onRecordingRequested,
  onStateChange,
  onError,
  onPermissionChange,
  maxDurationSeconds,
  maxDuration,
  maxDurationMs,
  maxSizeBytes,
  maxFileSizeBytes,
  autoStopOnMaxDuration = true,
  mimeTypes = DEFAULT_MIME_TYPES,
  disabled = false
}) => {
  const durationLimitSeconds = maxDurationSeconds ?? maxDuration ?? (maxDurationMs ? maxDurationMs / 1e3 : 60);
  const sizeLimitBytes = maxSizeBytes ?? maxFileSizeBytes;
  const [recordingState, setRecordingState] = useState25("idle");
  const [duration, setDuration] = useState25(0);
  const [lastError, setLastError] = useState25(null);
  const mediaRecorderRef = useRef8(null);
  const streamRef = useRef8(null);
  const chunksRef = useRef8([]);
  const sizeBytesRef = useRef8(0);
  const startedAtRef = useRef8(0);
  const intervalRef = useRef8(null);
  const timeoutRef = useRef8(null);
  const selectedMimeType = useMemo9(() => selectSupportedVoiceMimeType(mimeTypes), [mimeTypes]);
  const updateRecordingState = useCallback16(
    (nextState) => {
      setRecordingState(nextState);
      onStateChange?.(nextState);
    },
    [onStateChange]
  );
  const clearTimers = useCallback16(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  const stopTracks = useCallback16(() => {
    stopVoiceMediaStreamTracks(streamRef.current);
    streamRef.current = null;
  }, []);
  const emitError = useCallback16(
    (error) => {
      setLastError(error);
      updateRecordingState("error");
      onError?.(error);
    },
    [onError, updateRecordingState]
  );
  const cleanupRecorder = useCallback16(() => {
    clearTimers();
    stopTracks();
    mediaRecorderRef.current = null;
  }, [clearTimers, stopTracks]);
  const stopRecording = useCallback16(() => {
    const recorder = mediaRecorderRef.current;
    clearTimers();
    updateRecordingState("stopping");
    if (!recorder || recorder.state === "inactive") {
      cleanupRecorder();
      updateRecordingState("idle");
      return;
    }
    try {
      recorder.stop();
    } catch (error) {
      cleanupRecorder();
      emitError(mapVoiceRecorderError(error));
    }
  }, [cleanupRecorder, clearTimers, emitError, updateRecordingState]);
  const startRecording = useCallback16(async () => {
    setLastError(null);
    if (disabled) return;
    onRecordingRequested?.();
    if (!autoStopOnMaxDuration && durationLimitSeconds <= 0) return;
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      emitError(createVoiceRecorderError("insecure_context"));
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      emitError(createVoiceRecorderError("unsupported_browser"));
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      emitError(createVoiceRecorderError("recorder_unavailable"));
      return;
    }
    try {
      updateRecordingState("requesting_permission");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      onPermissionChange?.("granted");
      streamRef.current = stream;
      chunksRef.current = [];
      sizeBytesRef.current = 0;
      startedAtRef.current = Date.now();
      const recorder = new MediaRecorder(
        stream,
        selectedMimeType ? { mimeType: selectedMimeType } : void 0
      );
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (!event.data || event.data.size <= 0) return;
        sizeBytesRef.current += event.data.size;
        if (sizeLimitBytes && sizeBytesRef.current > sizeLimitBytes) {
          emitError(createVoiceRecorderError("max_size_exceeded"));
          stopRecording();
          return;
        }
        chunksRef.current.push(event.data);
      };
      recorder.onerror = (event) => {
        emitError(createVoiceRecorderError("recording_failed", event));
        stopRecording();
      };
      recorder.onstop = () => {
        const mimeType = recorder.mimeType || selectedMimeType || chunksRef.current[0]?.type || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const metadata = {
          blob,
          durationMs: Math.max(0, Date.now() - startedAtRef.current),
          mimeType,
          sizeBytes: blob.size
        };
        cleanupRecorder();
        setDuration(Math.ceil(metadata.durationMs / 1e3));
        updateRecordingState("completed");
        onRecordingComplete?.(metadata);
      };
      recorder.start(250);
      setDuration(0);
      updateRecordingState("recording");
      intervalRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startedAtRef.current) / 1e3));
      }, 1e3);
      timeoutRef.current = setTimeout(() => {
        stopRecording();
      }, durationLimitSeconds * 1e3);
    } catch (error) {
      cleanupRecorder();
      if (mapVoiceRecorderError(error).code === "permission_denied") {
        onPermissionChange?.("denied");
      }
      emitError(mapVoiceRecorderError(error));
    }
  }, [
    autoStopOnMaxDuration,
    cleanupRecorder,
    disabled,
    durationLimitSeconds,
    emitError,
    onPermissionChange,
    onRecordingRequested,
    selectedMimeType,
    sizeLimitBytes,
    stopRecording,
    updateRecordingState
  ]);
  useEffect18(() => {
    return () => {
      const recorder = mediaRecorderRef.current;
      clearTimers();
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch (error) {
          const log = safeError(error, {
            errorCode: "ERR_VOICE_RECORDING",
            operation: "VoiceRecorder.cleanup",
            retryable: false
          });
          logger.warn(log.safeMessage, {
            errorCode: log.errorCode,
            retryable: log.retryable,
            requestId: log.requestId
          });
        }
      }
      stopTracks();
      mediaRecorderRef.current = null;
    };
  }, [clearTimers, stopTracks]);
  const isRecording = recordingState === "recording";
  const statusText2 = recordingState === "requesting_permission" ? "Microphone permission \u092E\u093E\u0902\u0917\u0940 \u091C\u093E \u0930\u0939\u0940 \u0939\u0948..." : isRecording ? "Recording..." : recordingState === "stopping" ? "Recording \u0930\u094B\u0915\u0940 \u091C\u093E \u0930\u0939\u0940 \u0939\u0948..." : recordingState === "completed" ? "Recording \u092A\u0942\u0930\u0940 \u0939\u094B \u0917\u0908" : "Ready to record";
  return /* @__PURE__ */ jsxs53("div", { style: styles9.container, "aria-live": "polite", children: [
    /* @__PURE__ */ jsxs53("div", { style: styles9.display, children: [
      isRecording ? /* @__PURE__ */ jsxs53(Fragment4, { children: [
        /* @__PURE__ */ jsxs53("div", { style: styles9.recordingIndicator, children: [
          /* @__PURE__ */ jsx58("span", { style: styles9.redDot, "aria-hidden": "true", children: "\u25CF" }),
          /* @__PURE__ */ jsx58("span", { style: styles9.text, children: statusText2 })
        ] }),
        /* @__PURE__ */ jsx58("div", { style: styles9.duration, children: formatDuration(duration) })
      ] }) : /* @__PURE__ */ jsxs53("div", { style: styles9.readyState, children: [
        /* @__PURE__ */ jsx58("span", { style: styles9.icon, "aria-hidden": "true", children: "\u{1F3A4}" }),
        /* @__PURE__ */ jsx58("span", { style: styles9.text, children: statusText2 })
      ] }),
      lastError && /* @__PURE__ */ jsx58("p", { style: styles9.error, children: lastError.message })
    ] }),
    /* @__PURE__ */ jsx58(
      "button",
      {
        type: "button",
        onClick: isRecording ? stopRecording : startRecording,
        disabled: disabled || recordingState === "requesting_permission" || recordingState === "stopping",
        "aria-label": isRecording ? "Recording \u0930\u094B\u0915\u0947\u0902" : "Voice recording \u0936\u0941\u0930\u0942 \u0915\u0930\u0947\u0902",
        style: { ...styles9.button, ...isRecording ? styles9.stopButton : styles9.recordButton },
        children: isRecording ? "\u23F9 \u0930\u094B\u0915\u0947\u0902" : "\u{1F3A4} \u092C\u094B\u0932\u0947\u0902"
      }
    ),
    /* @__PURE__ */ jsx58("p", { style: styles9.hint, children: "Hindi \u092E\u0947\u0902 \u092C\u094B\u0932\u0947\u0902 - audio browser \u092E\u0947\u0902 temporary \u0930\u0939\u0924\u0940 \u0939\u0948\u0964" })
  ] });
};
var styles9 = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    padding: "24px",
    backgroundColor: "#1e293b",
    borderRadius: "12px",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  display: {
    width: "100%",
    minHeight: "96px",
    backgroundColor: "#0f172a",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "8px",
    boxSizing: "border-box"
  },
  recordingIndicator: { display: "flex", alignItems: "center", gap: "8px" },
  redDot: { color: "#f87171", fontSize: "16px" },
  text: { color: "#f1f5f9", fontSize: "14px", textAlign: "center" },
  duration: { color: "#60a5fa", fontSize: "24px", fontWeight: 600 },
  readyState: { display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" },
  icon: { fontSize: "32px" },
  button: {
    padding: "12px 32px",
    borderRadius: "8px",
    border: "none",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer"
  },
  recordButton: { backgroundColor: "#f87171", color: "#0f172a" },
  stopButton: { backgroundColor: "#60a5fa", color: "#0f172a" },
  hint: { color: "#94a3b8", fontSize: "12px", margin: 0, textAlign: "center" },
  error: { color: "#fecaca", fontSize: "12px", margin: 0, textAlign: "center" }
};

// src/components/voice/VoicePlayer.tsx
import { useCallback as useCallback17, useEffect as useEffect19, useRef as useRef9, useState as useState26 } from "react";
import { jsx as jsx59, jsxs as jsxs54 } from "react/jsx-runtime";
var formatTime = (time) => {
  if (!Number.isFinite(time)) return "0:00";
  const mins = Math.floor(time / 60);
  const secs = Math.floor(time % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};
var VoicePlayer = ({
  src,
  autoPlay = false,
  playbackRate = 1,
  onPlay,
  onPause,
  onCancel,
  onEnded,
  onError
}) => {
  const [playerState, setPlayerState] = useState26("idle");
  const [currentTime, setCurrentTime] = useState26(0);
  const [duration, setDuration] = useState26(0);
  const audioRef = useRef9(null);
  const emitError = useCallback17(
    (error) => {
      setPlayerState("error");
      onError?.(error);
    },
    [onError]
  );
  const playAudio = useCallback17(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      await audio.play();
      setPlayerState("playing");
    } catch (error) {
      emitError(error instanceof Error ? error : new Error("Audio play failed"));
    }
  }, [emitError]);
  const togglePlay = useCallback17(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playerState === "playing") {
      audio.pause();
      setPlayerState("paused");
      onPause?.();
    } else {
      void playAudio();
      onPlay?.();
    }
  }, [onPause, onPlay, playAudio, playerState]);
  const handleSeek = useCallback17((event) => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextTime = Number(event.target.value);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  }, []);
  const cancelAudio = useCallback17(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setCurrentTime(0);
    setPlayerState("idle");
    onCancel?.();
  }, [onCancel]);
  useEffect19(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setPlayerState(src ? "loading" : "idle");
    setCurrentTime(0);
    setDuration(0);
    audio.load();
    audio.playbackRate = playbackRate;
    if (autoPlay && src) {
      void playAudio();
    }
  }, [autoPlay, playbackRate, playAudio, src]);
  useEffect19(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleCanPlay = () => setPlayerState((current) => current === "loading" ? "paused" : current);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    const handleEnded = () => {
      setPlayerState("ended");
      setCurrentTime(audio.duration || 0);
      onEnded?.();
    };
    const handleAudioError = () => emitError(new Error("Audio playback failed"));
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleAudioError);
    return () => {
      audio.pause();
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleAudioError);
    };
  }, [emitError, onEnded]);
  useEffect19(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);
  const progressMax = duration > 0 ? duration : 0;
  return /* @__PURE__ */ jsxs54("div", { style: styles10.container, children: [
    /* @__PURE__ */ jsx59("audio", { ref: audioRef, src, preload: "metadata" }),
    /* @__PURE__ */ jsx59(
      "button",
      {
        type: "button",
        onClick: togglePlay,
        style: styles10.playButton,
        "aria-label": playerState === "playing" ? "Audio \u0930\u094B\u0915\u0947\u0902" : "Audio \u091A\u0932\u093E\u090F\u0902",
        disabled: !src || playerState === "loading",
        children: playerState === "playing" ? "\u23F8" : "\u25B6\uFE0F"
      }
    ),
    /* @__PURE__ */ jsx59(
      "button",
      {
        type: "button",
        onClick: cancelAudio,
        style: styles10.cancelButton,
        "aria-label": "Audio \u0930\u0926\u094D\u0926 \u0915\u0930\u0947\u0902",
        disabled: !src,
        children: "\u25A0"
      }
    ),
    /* @__PURE__ */ jsxs54("div", { style: styles10.progressContainer, children: [
      /* @__PURE__ */ jsx59(
        "input",
        {
          type: "range",
          min: 0,
          max: progressMax,
          step: 0.1,
          value: Math.min(currentTime, progressMax),
          onChange: handleSeek,
          "aria-label": "Audio seek",
          style: styles10.seek
        }
      ),
      /* @__PURE__ */ jsxs54("div", { style: styles10.timeDisplay, children: [
        /* @__PURE__ */ jsx59("span", { children: formatTime(currentTime) }),
        /* @__PURE__ */ jsx59("span", { children: playerState === "loading" ? "Loading..." : formatTime(duration) })
      ] })
    ] }),
    /* @__PURE__ */ jsx59("span", { style: styles10.label, children: playerState === "error" ? "Audio error" : "\u0938\u094D\u092E\u093E\u0930\u094D\u091F \u0936\u093F\u0915\u094D\u0937\u093E" })
  ] });
};
var styles10 = {
  container: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "#1e293b",
    borderRadius: "8px",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  playButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#60a5fa",
    border: "none",
    fontSize: "18px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  cancelButton: {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    backgroundColor: "#334155",
    border: "1px solid #64748b",
    color: "#f8fafc",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  progressContainer: { flex: 1, display: "flex", flexDirection: "column", gap: "4px" },
  seek: { width: "100%" },
  timeDisplay: { display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#94a3b8" },
  label: { fontSize: "12px", color: "#94a3b8" }
};

// src/components/voice/SpeechToText.tsx
import { useCallback as useCallback18, useEffect as useEffect20, useMemo as useMemo10, useRef as useRef10, useState as useState27 } from "react";
import { jsx as jsx60, jsxs as jsxs55 } from "react/jsx-runtime";
var DEFAULT_SPEECH_LANGUAGES = ["hi-IN", "en-IN"];
var normalizeSmartShikkaTranscript = (text2) => text2.replace(/\s+/g, " ").trim();
var getSpeechRecognitionConstructor = () => {
  if (typeof window === "undefined") return null;
  const speechWindow = window;
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition ?? null;
};
var prepareTranscriptForSubmit = async (text2, normalizeTranscript = normalizeSmartShikkaTranscript, beforeTranscriptSubmit) => {
  const normalized = normalizeTranscript(text2);
  return beforeTranscriptSubmit ? beforeTranscriptSubmit(normalized) : normalized;
};
var SpeechToText = ({
  onTranscript,
  onInterimTranscript,
  onFinalTranscript,
  onError,
  onNeedsServerTranscription,
  normalizeTranscript = normalizeSmartShikkaTranscript,
  beforeTranscriptSubmit,
  confirmationRequired = false,
  autoSubmit = false,
  language = "hi-IN",
  languageFallbacks = DEFAULT_SPEECH_LANGUAGES,
  disabled = false,
  continuous = false,
  serverSttFallback
}) => {
  const [isListening, setIsListening] = useState27(false);
  const [transcript, setTranscript] = useState27("");
  const [interimTranscript, setInterimTranscript] = useState27("");
  const [previewTranscript, setPreviewTranscript] = useState27("");
  const recognitionRef = useRef10(null);
  const submittedRef = useRef10(false);
  const languages = useMemo10(
    () => Array.from(new Set([language, ...languageFallbacks].filter(Boolean))),
    [language, languageFallbacks]
  );
  const submitTranscript = useCallback18(
    async (text2) => {
      const safeTranscript = await prepareTranscriptForSubmit(
        text2,
        normalizeTranscript,
        beforeTranscriptSubmit
      );
      if (!safeTranscript) return;
      submittedRef.current = true;
      onTranscript?.(safeTranscript, true);
    },
    [beforeTranscriptSubmit, normalizeTranscript, onTranscript]
  );
  const handleFinalTranscript = useCallback18(
    (text2) => {
      const normalized = normalizeTranscript(text2);
      if (!normalized) return;
      setTranscript((previous) => normalizeSmartShikkaTranscript(`${previous} ${normalized}`));
      setPreviewTranscript(normalized);
      onFinalTranscript?.(normalized);
      if (autoSubmit && !confirmationRequired) {
        void submitTranscript(normalized);
      }
    },
    [autoSubmit, confirmationRequired, normalizeTranscript, onFinalTranscript, submitTranscript]
  );
  const startListening = useCallback18(() => {
    if (disabled) return;
    const SpeechRecognition = getSpeechRecognitionConstructor();
    if (!SpeechRecognition) {
      if (serverSttFallback) {
        onNeedsServerTranscription?.("server_stt_fallback_available");
        return;
      }
      onNeedsServerTranscription?.("unsupported_browser");
      onError?.(new Error("Speech recognition not supported"));
      return;
    }
    submittedRef.current = false;
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.lang = languages[0] ?? "hi-IN";
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = (event) => {
      if (event.error === "language-not-supported" && languages.length > 1) {
        recognition.lang = languages[1];
        try {
          recognition.start();
          return;
        } catch (error) {
          const log = safeError(error, {
            errorCode: "ERR_SPEECH_FALLBACK_START",
            operation: "SpeechToText.fallbackRecognitionStart",
            retryable: false
          });
          logger.warn(log.safeMessage, {
            errorCode: log.errorCode,
            retryable: log.retryable,
            requestId: log.requestId
          });
        }
      }
      onError?.(new Error(event.error ?? "Speech recognition failed"));
      setIsListening(false);
    };
    recognition.onresult = (event) => {
      let finalText = "";
      let interimText = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcriptPart = result?.[0]?.transcript ?? "";
        if (result?.isFinal) {
          finalText += transcriptPart;
        } else {
          interimText += transcriptPart;
        }
      }
      const normalizedInterim = normalizeTranscript(interimText);
      setInterimTranscript(normalizedInterim);
      if (normalizedInterim) onInterimTranscript?.(normalizedInterim);
      if (finalText) handleFinalTranscript(finalText);
    };
    try {
      recognition.start();
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error("Speech recognition failed"));
    }
  }, [
    disabled,
    continuous,
    handleFinalTranscript,
    languages,
    normalizeTranscript,
    onError,
    onInterimTranscript,
    onNeedsServerTranscription,
    serverSttFallback
  ]);
  const stopListening = useCallback18(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);
  const confirmTranscript = useCallback18(() => {
    void submitTranscript(previewTranscript || transcript);
    setPreviewTranscript("");
  }, [previewTranscript, submitTranscript, transcript]);
  const cancelTranscript = useCallback18(() => {
    setPreviewTranscript("");
    submittedRef.current = false;
  }, []);
  useEffect20(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort?.();
          recognitionRef.current.stop();
        } catch (error) {
          const log = safeError(error, {
            errorCode: "ERR_SPEECH_CLEANUP",
            operation: "SpeechToText.cleanup",
            retryable: false
          });
          logger.warn(log.safeMessage, {
            errorCode: log.errorCode,
            retryable: log.retryable,
            requestId: log.requestId
          });
        }
      }
      recognitionRef.current = null;
    };
  }, []);
  const showConfirmation = confirmationRequired && previewTranscript && !submittedRef.current;
  return /* @__PURE__ */ jsxs55("div", { style: styles11.container, children: [
    /* @__PURE__ */ jsxs55("div", { style: styles11.display, children: [
      /* @__PURE__ */ jsxs55("div", { style: styles11.status, children: [
        /* @__PURE__ */ jsx60(
          "span",
          {
            style: { ...styles11.indicator, backgroundColor: isListening ? "#f87171" : "#64748b" },
            "aria-hidden": "true"
          }
        ),
        /* @__PURE__ */ jsx60("span", { style: styles11.statusText, children: isListening ? "\u0938\u0941\u0928 \u0930\u0939\u093E \u0939\u0942\u0902..." : "\u0936\u0941\u0930\u0942 \u0915\u0930\u0947\u0902" })
      ] }),
      /* @__PURE__ */ jsxs55("div", { style: styles11.transcriptArea, "aria-live": "polite", children: [
        transcript && /* @__PURE__ */ jsx60("p", { style: styles11.finalText, children: transcript }),
        interimTranscript && /* @__PURE__ */ jsx60("p", { style: styles11.interimText, children: interimTranscript }),
        !transcript && !interimTranscript && /* @__PURE__ */ jsx60("p", { style: styles11.placeholder, children: "\u0906\u092A\u0915\u0940 \u092C\u093E\u0924 \u092F\u0939\u093E\u0902 \u0926\u093F\u0916\u0947\u0917\u0940" })
      ] }),
      showConfirmation && /* @__PURE__ */ jsxs55("div", { style: styles11.confirmation, children: [
        /* @__PURE__ */ jsx60("p", { style: styles11.preview, children: previewTranscript }),
        /* @__PURE__ */ jsx60("button", { type: "button", onClick: confirmTranscript, style: styles11.confirmButton, children: "\u092D\u0947\u091C\u0947\u0902" }),
        /* @__PURE__ */ jsx60("button", { type: "button", onClick: cancelTranscript, style: styles11.cancelButton, children: "\u0930\u0926\u094D\u0926 \u0915\u0930\u0947\u0902" })
      ] })
    ] }),
    /* @__PURE__ */ jsx60("button", { type: "button", onClick: isListening ? stopListening : startListening, disabled, style: styles11.button, children: isListening ? "\u23F9 \u0930\u094B\u0915\u0947\u0902" : "\u{1F3A4} \u092C\u094B\u0932\u0947\u0902" })
  ] });
};
var styles11 = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    backgroundColor: "#1e293b",
    borderRadius: "12px",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  display: {
    minHeight: "100px",
    backgroundColor: "#0f172a",
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  status: { display: "flex", alignItems: "center", gap: "8px" },
  indicator: { width: "10px", height: "10px", borderRadius: "50%", transition: "background-color 0.2s" },
  statusText: { color: "#f1f5f9", fontSize: "14px" },
  transcriptArea: { flex: 1 },
  finalText: { color: "#f1f5f9", fontSize: "14px", margin: 0 },
  interimText: { color: "#94a3b8", fontSize: "14px", margin: 0, fontStyle: "italic" },
  placeholder: { color: "#64748b", fontSize: "14px", margin: 0 },
  button: {
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: "#60a5fa",
    color: "#0f172a"
  },
  confirmation: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  preview: { color: "#e2e8f0", fontSize: "13px", margin: 0, flex: "1 1 100%" },
  confirmButton: { border: "none", borderRadius: "6px", padding: "8px 12px", backgroundColor: "#22c55e", color: "#052e16" },
  cancelButton: { border: "none", borderRadius: "6px", padding: "8px 12px", backgroundColor: "#94a3b8", color: "#0f172a" }
};

// src/components/voice/ForegroundWakeControl.tsx
import { useCallback as useCallback19, useEffect as useEffect21, useMemo as useMemo11, useRef as useRef11, useState as useState28 } from "react";
import { jsx as jsx61, jsxs as jsxs56 } from "react/jsx-runtime";
var DEFAULT_FOREGROUND_WAKE_PHRASES = ["shikka", "\u0936\u093F\u0915\u094D\u0915\u093E"];
var WAKE_ARM_WINDOW_MS = 8e3;
var MAX_WAKE_COMMAND_CHARS = 500;
function normalizeWakeTranscript(text2) {
  return normalizeSmartShikkaTranscript(
    text2.normalize("NFKC").toLocaleLowerCase().replace(/[^\p{L}\p{M}\p{N}]+/gu, " ")
  );
}
function extractWakeCommand(transcript, wakePhrases = DEFAULT_FOREGROUND_WAKE_PHRASES) {
  const normalized = normalizeWakeTranscript(transcript);
  if (!normalized) return { matched: false, command: "" };
  const phrases = wakePhrases.map(normalizeWakeTranscript).filter(Boolean).sort((left, right) => right.length - left.length);
  for (const phrase of phrases) {
    if (normalized === phrase) return { matched: true, command: "" };
    const prefix = `${phrase} `;
    if (normalized.startsWith(prefix)) {
      return { matched: true, command: normalized.slice(prefix.length).trim() };
    }
  }
  return { matched: false, command: "" };
}
function stateLabel(state, language) {
  const hindi = language.toLowerCase().startsWith("hi");
  if (hindi) {
    switch (state) {
      case "starting":
        return "Foreground wake \u0936\u0941\u0930\u0942 \u0939\u094B \u0930\u0939\u093E \u0939\u0948...";
      case "listening":
        return "\u201C\u0936\u093F\u0915\u094D\u0915\u093E\u201D \u0938\u0941\u0928 \u0930\u0939\u093E \u0939\u0948";
      case "paused":
        return "Tab \u091B\u093F\u092A\u093E \u0939\u0948, wake \u0905\u0938\u094D\u0925\u093E\u092F\u0940 \u0930\u0942\u092A \u0938\u0947 \u0930\u0941\u0915\u093E \u0939\u0948";
      case "unsupported":
        return "\u0907\u0938 browser \u092E\u0947\u0902 foreground wake \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948";
      case "error":
        return "Foreground wake \u092E\u0947\u0902 \u0938\u092E\u0938\u094D\u092F\u093E \u0906\u0908";
      default:
        return "Foreground wake \u092C\u0902\u0926 \u0939\u0948";
    }
  }
  switch (state) {
    case "starting":
      return "Starting foreground wake\u2026";
    case "listening":
      return "Listening for \u201CShikka\u201D";
    case "paused":
      return "Paused while this tab is hidden";
    case "unsupported":
      return "Foreground wake is not supported in this browser";
    case "error":
      return "Foreground wake encountered an error";
    default:
      return "Foreground wake is off";
  }
}
function ForegroundWakeControl({
  language = "hi-IN",
  wakePhrases = DEFAULT_FOREGROUND_WAKE_PHRASES,
  disabled = false,
  suspended = false,
  onWakeCommand,
  onWakeWord,
  onActiveChange,
  onStateChange
}) {
  const [active, setActive] = useState28(false);
  const [state, setState] = useState28("disabled");
  const recognitionRef = useRef11(null);
  const startRecognitionRef = useRef11(() => void 0);
  const restartTimerRef = useRef11(null);
  const activeRef = useRef11(false);
  const disabledRef = useRef11(disabled);
  const suspendedRef = useRef11(suspended);
  const shouldListenRef = useRef11(false);
  const armedUntilRef = useRef11(0);
  const setWakeState = useCallback19(
    (nextState) => {
      setState(nextState);
      onStateChange?.(nextState);
    },
    [onStateChange]
  );
  const clearRestartTimer = useCallback19(() => {
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  }, []);
  const stopRecognition = useCallback19(
    (nextState) => {
      clearRestartTimer();
      const recognition = recognitionRef.current;
      recognitionRef.current = null;
      if (recognition) {
        try {
          recognition.abort?.();
          recognition.stop();
        } catch {
        }
      }
      setWakeState(nextState);
    },
    [clearRestartTimer, setWakeState]
  );
  const startRecognition = useCallback19(() => {
    if (!shouldListenRef.current || typeof document === "undefined" || document.hidden || recognitionRef.current) {
      return;
    }
    const Recognition = getSpeechRecognitionConstructor();
    if (!Recognition) {
      activeRef.current = false;
      setActive(false);
      shouldListenRef.current = false;
      setWakeState("unsupported");
      onActiveChange?.(false);
      return;
    }
    const recognition = new Recognition();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = language;
    setWakeState("starting");
    recognition.onstart = () => {
      if (recognitionRef.current === recognition) setWakeState("listening");
    };
    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return;
      recognitionRef.current = null;
      if (shouldListenRef.current && typeof document !== "undefined" && !document.hidden) {
        setWakeState("starting");
        clearRestartTimer();
        restartTimerRef.current = setTimeout(() => {
          restartTimerRef.current = null;
          startRecognitionRef.current();
        }, 250);
      } else {
        setWakeState(activeRef.current ? "paused" : "disabled");
      }
    };
    recognition.onerror = (event) => {
      if (event.error === "aborted") return;
      recognitionRef.current = null;
      clearRestartTimer();
      shouldListenRef.current = false;
      activeRef.current = false;
      setActive(false);
      setWakeState("error");
      onActiveChange?.(false);
    };
    recognition.onresult = (event) => {
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (!result?.isFinal) continue;
        const transcript = result[0]?.transcript ?? "";
        const parsed = extractWakeCommand(transcript, wakePhrases);
        if (parsed.matched) {
          armedUntilRef.current = Date.now() + WAKE_ARM_WINDOW_MS;
          onWakeWord?.();
          if (parsed.command) {
            armedUntilRef.current = 0;
            onWakeCommand(parsed.command);
          }
          continue;
        }
        if (armedUntilRef.current > Date.now()) {
          armedUntilRef.current = 0;
          const command = normalizeWakeTranscript(transcript).slice(0, MAX_WAKE_COMMAND_CHARS);
          if (command) onWakeCommand(command);
        }
      }
    };
    try {
      recognition.start();
    } catch {
      recognitionRef.current = null;
      setWakeState("error");
    }
  }, [
    clearRestartTimer,
    language,
    onActiveChange,
    onWakeCommand,
    onWakeWord,
    setWakeState,
    wakePhrases
  ]);
  useEffect21(() => {
    startRecognitionRef.current = startRecognition;
  }, [startRecognition]);
  useEffect21(() => {
    activeRef.current = active;
    disabledRef.current = disabled;
    suspendedRef.current = suspended;
    const shouldListen = active && !disabled && !suspended;
    shouldListenRef.current = shouldListen;
    if (shouldListen) {
      startRecognition();
    } else {
      stopRecognition(active && (disabled || suspended) ? "paused" : "disabled");
    }
  }, [active, disabled, startRecognition, stopRecognition, suspended]);
  useEffect21(() => {
    if (typeof document === "undefined") return void 0;
    const handleVisibilityChange = () => {
      if (document.hidden) {
        shouldListenRef.current = false;
        stopRecognition(activeRef.current ? "paused" : "disabled");
        return;
      }
      const shouldListen = activeRef.current && !disabledRef.current && !suspendedRef.current;
      shouldListenRef.current = shouldListen;
      if (shouldListen) startRecognition();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      shouldListenRef.current = false;
      stopRecognition("disabled");
    };
  }, [startRecognition, stopRecognition]);
  const toggleActive = useCallback19(() => {
    setActive((previous) => {
      const next = !previous;
      activeRef.current = next;
      onActiveChange?.(next);
      return next;
    });
  }, [onActiveChange]);
  const label = useMemo11(() => stateLabel(state, language), [language, state]);
  const isHindi = language.toLowerCase().startsWith("hi");
  return /* @__PURE__ */ jsxs56(
    "section",
    {
      "aria-label": isHindi ? "Foreground wake \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923" : "Foreground wake control",
      "data-testid": "foreground-wake-control",
      style: styles12.container,
      children: [
        /* @__PURE__ */ jsxs56("div", { style: styles12.header, children: [
          /* @__PURE__ */ jsx61(
            "button",
            {
              type: "button",
              onClick: toggleActive,
              disabled: disabled || suspended || state === "unsupported",
              "aria-pressed": active,
              "aria-label": active ? isHindi ? "Foreground wake \u092C\u0902\u0926 \u0915\u0930\u0947\u0902" : "Turn foreground wake off" : isHindi ? "Foreground wake \u091A\u093E\u0932\u0942 \u0915\u0930\u0947\u0902" : "Turn foreground wake on",
              style: { ...styles12.button, ...active ? styles12.activeButton : {} },
              children: active ? isHindi ? "Wake \u092C\u0902\u0926 \u0915\u0930\u0947\u0902" : "Turn wake off" : isHindi ? "Wake \u091A\u093E\u0932\u0942 \u0915\u0930\u0947\u0902" : "Turn wake on"
            }
          ),
          /* @__PURE__ */ jsx61("span", { role: "status", "aria-live": "polite", style: styles12.status, children: label })
        ] }),
        /* @__PURE__ */ jsx61("p", { style: styles12.hint, children: isHindi ? "\u092F\u0939 \u0915\u0947\u0935\u0932 \u0916\u0941\u0932\u0947 \u0939\u0941\u090F tab \u092E\u0947\u0902 \u091A\u0932\u0924\u093E \u0939\u0948\u0964 Tab \u091B\u093F\u092A\u0924\u0947 \u0939\u0940 \u0930\u0941\u0915\u0924\u093E \u0939\u0948 \u0914\u0930 \u201C\u0936\u093F\u0915\u094D\u0915\u093E\u201D \u0915\u0939\u0928\u0947 \u0915\u0947 \u092C\u093E\u0926 \u0939\u0940 command \u092D\u0947\u091C\u0924\u093E \u0939\u0948\u0964" : "This runs only in the visible tab. It stops when the tab is hidden and sends a command only after \u201CShikka\u201D." })
      ]
    }
  );
}
var styles12 = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: "8px 10px",
    borderRadius: 10,
    background: "rgba(15, 23, 42, 0.72)"
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap"
  },
  button: {
    border: "1px solid #64748b",
    borderRadius: 8,
    padding: "6px 10px",
    background: "transparent",
    color: "#e2e8f0",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600
  },
  activeButton: {
    borderColor: "#60a5fa",
    background: "rgba(96, 165, 250, 0.18)"
  },
  status: {
    color: "#cbd5e1",
    fontSize: 12
  },
  hint: {
    margin: 0,
    color: "#94a3b8",
    fontSize: 11,
    lineHeight: 1.4
  }
};

// src/components/voice/VoiceAgent.tsx
import { useCallback as useCallback20, useMemo as useMemo12, useState as useState29 } from "react";
import { jsx as jsx62, jsxs as jsxs57 } from "react/jsx-runtime";
var prepareVoiceAgentTranscript = async (transcript, redactor) => {
  const safeTranscript = redactor ? await redactor(transcript) : transcript;
  return safeTranscript.trim();
};
var createVoiceAgentContext = (appId, userId) => userId ? { appId, userId } : { appId };
var VoiceAgent = ({
  language = "hi-IN",
  appId,
  userId,
  onTranscriptReady,
  onSendTranscript,
  onError,
  requireConfirmation = true,
  redactor,
  disabled = false,
  responseAudioSrc
}) => {
  const [previewTranscript, setPreviewTranscript] = useState29("");
  const [lastRecording, setLastRecording] = useState29(null);
  const context = useMemo12(() => createVoiceAgentContext(appId, userId), [appId, userId]);
  const redactTranscript = useCallback20(
    async (transcript) => prepareVoiceAgentTranscript(transcript, redactor),
    [redactor]
  );
  const handleTranscriptReady = useCallback20(
    async (transcript) => {
      const safeTranscript = await redactTranscript(transcript);
      if (!safeTranscript) return;
      setPreviewTranscript(safeTranscript);
      onTranscriptReady?.(safeTranscript, context);
      if (!requireConfirmation) {
        await onSendTranscript?.(safeTranscript, context);
      }
    },
    [context, onSendTranscript, onTranscriptReady, redactTranscript, requireConfirmation]
  );
  const sendPreview = useCallback20(async () => {
    const safeTranscript = await redactTranscript(previewTranscript);
    if (!safeTranscript) return;
    await onSendTranscript?.(safeTranscript, context);
    setPreviewTranscript("");
  }, [context, onSendTranscript, previewTranscript, redactTranscript]);
  const cancelPreview = useCallback20(() => {
    setPreviewTranscript("");
  }, []);
  return /* @__PURE__ */ jsxs57("section", { style: styles13.container, "aria-label": "Smart Shikka Voice Agent", children: [
    /* @__PURE__ */ jsx62(
      VoiceRecorder,
      {
        disabled,
        onRecordingComplete: setLastRecording,
        ...onError ? { onError } : {},
        maxDurationSeconds: 60
      }
    ),
    /* @__PURE__ */ jsx62(
      SpeechToText,
      {
        language,
        disabled,
        confirmationRequired: requireConfirmation,
        autoSubmit: !requireConfirmation,
        beforeTranscriptSubmit: redactTranscript,
        onTranscript: handleTranscriptReady,
        onNeedsServerTranscription: (reason) => onError?.(new Error(reason)),
        ...onError ? { onError } : {}
      }
    ),
    previewTranscript && requireConfirmation && /* @__PURE__ */ jsxs57("div", { style: styles13.preview, "aria-live": "polite", children: [
      /* @__PURE__ */ jsx62("p", { style: styles13.previewText, children: previewTranscript }),
      /* @__PURE__ */ jsx62("button", { type: "button", onClick: sendPreview, disabled, style: styles13.sendButton, children: "\u092D\u0947\u091C\u0947\u0902" }),
      /* @__PURE__ */ jsx62("button", { type: "button", onClick: cancelPreview, disabled, style: styles13.cancelButton, children: "\u0930\u0926\u094D\u0926 \u0915\u0930\u0947\u0902" })
    ] }),
    responseAudioSrc && /* @__PURE__ */ jsx62(VoicePlayer, { src: responseAudioSrc, onError: (error) => onError?.(error) }),
    lastRecording && /* @__PURE__ */ jsxs57("p", { style: styles13.meta, children: [
      "Recording: ",
      Math.round(lastRecording.durationMs / 1e3),
      "s \xB7 ",
      lastRecording.sizeBytes,
      " bytes"
    ] })
  ] });
};
var styles13 = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    backgroundColor: "#111827",
    borderRadius: "12px",
    padding: "16px",
    fontFamily: "'Segoe UI', system-ui, sans-serif"
  },
  preview: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "center",
    backgroundColor: "#0f172a",
    borderRadius: "8px",
    padding: "12px"
  },
  previewText: { color: "#f8fafc", fontSize: "14px", margin: 0, flex: "1 1 100%" },
  sendButton: { border: "none", borderRadius: "6px", padding: "8px 12px", backgroundColor: "#22c55e", color: "#052e16" },
  cancelButton: { border: "none", borderRadius: "6px", padding: "8px 12px", backgroundColor: "#94a3b8", color: "#0f172a" },
  meta: { color: "#94a3b8", fontSize: "12px", margin: 0 }
};

// src/components/voice/voice-utils.ts
function normalizeHindiRoman(text2) {
  const map = {
    namaste: "\u0928\u092E\u0938\u094D\u0924\u0947",
    shukriya: "\u0936\u0941\u0915\u094D\u0930\u093F\u092F\u093E",
    kya: "\u0915\u094D\u092F\u093E",
    hai: "\u0939\u0948",
    nahi: "\u0928\u0939\u0940\u0902",
    haa: "\u0939\u093E\u0902",
    aap: "\u0906\u092A",
    tum: "\u0924\u0941\u092E",
    mein: "\u092E\u0948\u0902",
    mere: "\u092E\u0947\u0930\u0947",
    yeh: "\u092F\u0939",
    woh: "\u0935\u0939",
    kaise: "\u0915\u0948\u0938\u0947",
    kahan: "\u0915\u0939\u093E\u0902",
    karo: "\u0915\u0930\u094B",
    batao: "\u092C\u0924\u093E\u0909",
    madad: "\u092E\u0926\u0926",
    shiksha: "\u0936\u093F\u0915\u094D\u0937\u093E"
  };
  let result = text2;
  for (const [key, val] of Object.entries(map)) {
    const re = new RegExp(`\\b${key}\\b`, "gi");
    result = result.replace(re, val);
  }
  return result;
}
function redactPII(text2) {
  const redactions = [];
  let redacted = text2;
  const mobilePattern = /(\+91[-\s]?)?\d{5}[-\s]?\d{5}/g;
  redacted = redacted.replace(mobilePattern, () => {
    redactions.push("mobile_detected");
    return "[MOBILE]";
  });
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  redacted = redacted.replace(emailPattern, () => {
    redactions.push("email_detected");
    return "[EMAIL]";
  });
  const aadhaarPattern = /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g;
  redacted = redacted.replace(aadhaarPattern, () => {
    redactions.push("aadhaar_detected");
    return "[AADHAAR]";
  });
  return { redacted, redactions };
}
function isSpeechRecognitionSupported() {
  if (typeof window === "undefined") return false;
  return "webkitSpeechRecognition" in window || "SpeechRecognition" in window;
}
function getDefaultLanguage() {
  const navLang = typeof navigator !== "undefined" ? navigator.language : "en-IN";
  if (navLang.startsWith("hi")) return "hi-IN";
  if (navLang.startsWith("en")) return "en-IN";
  return "hi-IN";
}

// src/components/base/Button.tsx
import { jsx as jsx63, jsxs as jsxs58 } from "react/jsx-runtime";
var Button = ({
  variant = "primary",
  size = "md",
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  children,
  disabled,
  style,
  type = "button",
  ...props
}) => {
  const isDisabled = disabled || loading;
  return /* @__PURE__ */ jsxs58(
    "button",
    {
      ...props,
      type,
      disabled: isDisabled,
      "aria-busy": loading || void 0,
      style: {
        ...styles14.button,
        ...styles14[variant],
        ...styles14[size],
        ...fullWidth ? styles14.fullWidth : {},
        ...isDisabled ? styles14.disabled : {},
        ...style
      },
      children: [
        loading && /* @__PURE__ */ jsx63("span", { style: styles14.spinner, "aria-hidden": "true", children: "\u23F3" }),
        !loading && leftIcon && /* @__PURE__ */ jsx63("span", { style: styles14.icon, "aria-hidden": "true", children: leftIcon }),
        /* @__PURE__ */ jsx63("span", { children }),
        !loading && rightIcon && /* @__PURE__ */ jsx63("span", { style: styles14.icon, "aria-hidden": "true", children: rightIcon })
      ]
    }
  );
};
var styles14 = {
  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: baseUiTokens.spacing.sm,
    border: "none",
    borderRadius: baseUiTokens.radius.md,
    fontFamily: baseUiTokens.font.family,
    fontWeight: baseUiTokens.font.weightSemibold,
    cursor: "pointer",
    transition: "background-color 0.15s ease, opacity 0.15s ease"
  },
  primary: { backgroundColor: baseUiTokens.colors.primary, color: baseUiTokens.colors.surfaceStrong },
  secondary: {
    backgroundColor: baseUiTokens.colors.secondary,
    color: baseUiTokens.colors.text,
    border: `1px solid ${baseUiTokens.colors.borderStrong}`
  },
  danger: { backgroundColor: baseUiTokens.colors.danger, color: baseUiTokens.colors.surfaceStrong },
  success: { backgroundColor: baseUiTokens.colors.success, color: baseUiTokens.colors.surfaceStrong },
  ghost: { backgroundColor: "transparent", color: baseUiTokens.colors.textMuted },
  sm: { padding: "6px 12px", fontSize: "12px" },
  md: { padding: "8px 16px", fontSize: "14px" },
  lg: { padding: "12px 24px", fontSize: "16px" },
  fullWidth: { width: "100%" },
  disabled: { opacity: 0.62, cursor: "not-allowed" },
  spinner: { fontSize: "14px" },
  icon: { display: "inline-flex", alignItems: "center", fontSize: "14px" }
};

// src/components/base/Input.tsx
import { useId } from "react";
import { jsx as jsx64, jsxs as jsxs59 } from "react/jsx-runtime";
var Input = ({
  label,
  error,
  helperText,
  size = "md",
  leftIcon,
  rightIcon,
  fullWidth = false,
  style,
  id,
  required,
  disabled,
  "aria-describedby": ariaDescribedBy,
  ...props
}) => {
  const generatedId = useId();
  const inputId = id ?? `smart-shikka-input-${generatedId.replace(/:/g, "")}`;
  const helperId = helperText ? `${inputId}-helper` : void 0;
  const errorId = error ? `${inputId}-error` : void 0;
  const describedBy = [ariaDescribedBy, errorId, !error ? helperId : void 0].filter(Boolean).join(" ") || void 0;
  return /* @__PURE__ */ jsxs59("div", { style: { ...styles15.container, ...fullWidth ? styles15.fullWidth : {} }, children: [
    label && /* @__PURE__ */ jsxs59("label", { htmlFor: inputId, style: styles15.label, children: [
      label,
      required && /* @__PURE__ */ jsx64("span", { "aria-hidden": "true", style: styles15.required, children: "*" })
    ] }),
    /* @__PURE__ */ jsxs59(
      "div",
      {
        style: {
          ...styles15.inputWrapper,
          ...error ? styles15.inputError : {},
          ...disabled ? styles15.disabledWrapper : {}
        },
        children: [
          leftIcon && /* @__PURE__ */ jsx64("span", { style: styles15.leftIcon, "aria-hidden": "true", children: leftIcon }),
          /* @__PURE__ */ jsx64(
            "input",
            {
              id: inputId,
              required,
              disabled,
              "aria-invalid": error ? true : void 0,
              "aria-describedby": describedBy,
              ...props,
              style: {
                ...styles15.input,
                ...styles15[size],
                ...leftIcon ? styles15.inputWithLeftIcon : {},
                ...rightIcon ? styles15.inputWithRightIcon : {},
                ...disabled ? styles15.disabledInput : {},
                ...style
              }
            }
          ),
          rightIcon && /* @__PURE__ */ jsx64("span", { style: styles15.rightIcon, "aria-hidden": "true", children: rightIcon })
        ]
      }
    ),
    error && /* @__PURE__ */ jsx64("span", { id: errorId, role: "alert", style: styles15.error, children: error }),
    !error && helperText && /* @__PURE__ */ jsx64("span", { id: helperId, style: styles15.helperText, children: helperText })
  ] });
};
var styles15 = {
  container: { display: "flex", flexDirection: "column", gap: baseUiTokens.spacing.xs },
  fullWidth: { width: "100%" },
  label: {
    fontSize: "14px",
    fontWeight: baseUiTokens.font.weightMedium,
    color: baseUiTokens.colors.text,
    fontFamily: baseUiTokens.font.family
  },
  required: { color: baseUiTokens.colors.danger, marginLeft: baseUiTokens.spacing.xs },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    backgroundColor: baseUiTokens.colors.surface,
    border: `1px solid ${baseUiTokens.colors.borderStrong}`,
    borderRadius: baseUiTokens.radius.md
  },
  inputError: { borderColor: baseUiTokens.colors.danger },
  disabledWrapper: { opacity: 0.72 },
  input: {
    width: "100%",
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
    color: baseUiTokens.colors.text,
    fontFamily: baseUiTokens.font.family
  },
  disabledInput: { cursor: "not-allowed" },
  sm: { padding: "6px 12px", fontSize: "12px" },
  md: { padding: "10px 14px", fontSize: "14px" },
  lg: { padding: "12px 16px", fontSize: "16px" },
  inputWithLeftIcon: { paddingLeft: "40px" },
  inputWithRightIcon: { paddingRight: "40px" },
  leftIcon: { position: "absolute", left: "12px", fontSize: "16px", color: baseUiTokens.colors.textMuted },
  rightIcon: { position: "absolute", right: "12px", fontSize: "16px", color: baseUiTokens.colors.textMuted },
  error: { fontSize: "12px", color: baseUiTokens.colors.danger, fontFamily: baseUiTokens.font.family },
  helperText: { fontSize: "12px", color: baseUiTokens.colors.textMuted, fontFamily: baseUiTokens.font.family }
};

// src/components/base/Card.tsx
import { jsx as jsx65, jsxs as jsxs60 } from "react/jsx-runtime";
var Card = ({
  children,
  title,
  subtitle,
  padding = "md",
  onClick,
  style,
  headerActions,
  footer,
  role,
  tabIndex,
  ...props
}) => {
  const interactive = Boolean(onClick);
  return /* @__PURE__ */ jsxs60(
    "div",
    {
      ...props,
      role: role ?? (interactive ? "button" : void 0),
      tabIndex: tabIndex ?? (interactive ? 0 : void 0),
      onClick,
      style: {
        ...styles16.card,
        ...interactive ? styles16.clickable : {},
        ...style
      },
      children: [
        (title || subtitle || headerActions) && /* @__PURE__ */ jsxs60("div", { style: styles16.header, children: [
          /* @__PURE__ */ jsxs60("div", { style: styles16.headerText, children: [
            title && /* @__PURE__ */ jsx65("h3", { style: styles16.title, children: title }),
            subtitle && /* @__PURE__ */ jsx65("p", { style: styles16.subtitle, children: subtitle })
          ] }),
          headerActions && /* @__PURE__ */ jsx65("div", { style: styles16.headerActions, children: headerActions })
        ] }),
        /* @__PURE__ */ jsx65("div", { style: { ...styles16.content, ...styles16[padding] }, children }),
        footer && /* @__PURE__ */ jsx65("div", { style: styles16.footer, children: footer })
      ]
    }
  );
};
var styles16 = {
  card: {
    backgroundColor: baseUiTokens.colors.surface,
    borderRadius: baseUiTokens.radius.lg,
    border: `1px solid ${baseUiTokens.colors.border}`,
    overflow: "hidden",
    fontFamily: baseUiTokens.font.family
  },
  clickable: { cursor: "pointer" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: baseUiTokens.spacing.md,
    padding: baseUiTokens.spacing.lg,
    borderBottom: `1px solid ${baseUiTokens.colors.border}`
  },
  headerText: { flex: 1 },
  title: { margin: 0, fontSize: "16px", fontWeight: baseUiTokens.font.weightSemibold, color: baseUiTokens.colors.text },
  subtitle: { margin: "4px 0 0", fontSize: "12px", color: baseUiTokens.colors.textMuted },
  headerActions: { display: "flex", gap: baseUiTokens.spacing.sm, alignItems: "center" },
  content: {},
  none: { padding: "0" },
  sm: { padding: baseUiTokens.spacing.md },
  md: { padding: baseUiTokens.spacing.lg },
  lg: { padding: baseUiTokens.spacing.xl },
  footer: { padding: baseUiTokens.spacing.lg, borderTop: `1px solid ${baseUiTokens.colors.border}` }
};

// src/components/base/Modal.tsx
import { useEffect as useEffect22, useId as useId2, useRef as useRef12 } from "react";
import { jsx as jsx66, jsxs as jsxs61 } from "react/jsx-runtime";
var getFocusableModalElements = (container) => {
  if (!container) return [];
  const selectors = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])'
  ].join(",");
  return Array.from(container.querySelectorAll(selectors)).filter(
    (element) => !element.hasAttribute("disabled") && element.getAttribute("aria-hidden") !== "true"
  );
};
var focusModalBoundary = (event, focusableElements, activeElement) => {
  if (event.key !== "Tab" || focusableElements.length === 0) return null;
  const first = focusableElements[0];
  const last = focusableElements[focusableElements.length - 1];
  if (event.shiftKey && activeElement === first) {
    event.preventDefault();
    last.focus();
    return last;
  }
  if (!event.shiftKey && activeElement === last) {
    event.preventDefault();
    first.focus();
    return first;
  }
  return null;
};
var Modal = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = "md",
  closeOnOverlayClick = true,
  lockBodyScroll = true
}) => {
  const titleId = useId2();
  const descriptionId = useId2();
  const modalRef = useRef12(null);
  const previousActiveElementRef = useRef12(null);
  useEffect22(() => {
    if (!isOpen || typeof document === "undefined") return;
    previousActiveElementRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    if (lockBodyScroll) document.body.style.overflow = "hidden";
    window.setTimeout(() => {
      const firstFocusable = getFocusableModalElements(modalRef.current)[0];
      firstFocusable?.focus();
    }, 0);
    return () => {
      if (lockBodyScroll) document.body.style.overflow = previousOverflow;
      if (previousActiveElementRef.current instanceof HTMLElement) {
        previousActiveElementRef.current.focus();
      }
    };
  }, [isOpen, lockBodyScroll]);
  useEffect22(() => {
    if (!isOpen || typeof document === "undefined") return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      focusModalBoundary(event, getFocusableModalElements(modalRef.current), document.activeElement);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return /* @__PURE__ */ jsx66(
    "div",
    {
      style: styles17.overlay,
      onClick: closeOnOverlayClick ? onClose : void 0,
      role: "presentation",
      children: /* @__PURE__ */ jsxs61(
        "div",
        {
          ref: modalRef,
          style: { ...styles17.modal, ...styles17[size] },
          onClick: (event) => event.stopPropagation(),
          role: "dialog",
          "aria-modal": "true",
          "aria-labelledby": title ? titleId : void 0,
          "aria-describedby": description ? descriptionId : void 0,
          children: [
            (title || description) && /* @__PURE__ */ jsxs61("div", { style: styles17.header, children: [
              /* @__PURE__ */ jsxs61("div", { children: [
                title && /* @__PURE__ */ jsx66("h2", { id: titleId, style: styles17.title, children: title }),
                description && /* @__PURE__ */ jsx66("p", { id: descriptionId, style: styles17.description, children: description })
              ] }),
              /* @__PURE__ */ jsx66("button", { type: "button", onClick: onClose, style: styles17.closeButton, "aria-label": "Modal \u092C\u0902\u0926 \u0915\u0930\u0947\u0902", children: "\u2715" })
            ] }),
            /* @__PURE__ */ jsx66("div", { style: styles17.content, children })
          ]
        }
      )
    }
  );
};
var styles17 = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1e3,
    padding: "20px"
  },
  modal: {
    backgroundColor: baseUiTokens.colors.surface,
    borderRadius: baseUiTokens.radius.lg,
    border: `1px solid ${baseUiTokens.colors.border}`,
    maxHeight: "90vh",
    overflow: "auto",
    fontFamily: baseUiTokens.font.family
  },
  sm: { width: "100%", maxWidth: "400px" },
  md: { width: "100%", maxWidth: "500px" },
  lg: { width: "100%", maxWidth: "700px" },
  xl: { width: "100%", maxWidth: "900px" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: baseUiTokens.spacing.md,
    padding: "16px 20px",
    borderBottom: `1px solid ${baseUiTokens.colors.border}`
  },
  title: { margin: 0, fontSize: "18px", fontWeight: baseUiTokens.font.weightSemibold, color: baseUiTokens.colors.text },
  description: { margin: "4px 0 0", fontSize: "13px", color: baseUiTokens.colors.textMuted },
  closeButton: {
    backgroundColor: "transparent",
    border: "none",
    color: baseUiTokens.colors.textMuted,
    fontSize: "20px",
    cursor: "pointer",
    padding: "4px"
  },
  content: { padding: "20px" }
};

// src/components/base/Badge.tsx
import { jsx as jsx67 } from "react/jsx-runtime";
var Badge = ({
  children,
  variant = "default",
  size = "md",
  ariaLabel,
  style,
  ...props
}) => {
  return /* @__PURE__ */ jsx67(
    "span",
    {
      ...props,
      "aria-label": ariaLabel,
      style: { ...styles18.badge, ...styles18[variant], ...styles18[size], ...style },
      children
    }
  );
};
var styles18 = {
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: baseUiTokens.radius.pill,
    fontFamily: baseUiTokens.font.family,
    fontWeight: baseUiTokens.font.weightMedium,
    lineHeight: 1
  },
  default: { backgroundColor: "#334155", color: baseUiTokens.colors.text },
  neutral: { backgroundColor: "#334155", color: baseUiTokens.colors.text },
  primary: { backgroundColor: "#1d4ed8", color: "#eff6ff" },
  success: { backgroundColor: "#047857", color: "#ecfdf5" },
  active: { backgroundColor: "#047857", color: "#ecfdf5" },
  warning: { backgroundColor: "#92400e", color: "#fffbeb" },
  pending: { backgroundColor: "#92400e", color: "#fffbeb" },
  danger: { backgroundColor: "#b91c1c", color: "#fef2f2" },
  blocked: { backgroundColor: "#b91c1c", color: "#fef2f2" },
  info: { backgroundColor: "#6d28d9", color: "#f5f3ff" },
  sm: { padding: "3px 8px", fontSize: "10px" },
  md: { padding: "5px 12px", fontSize: "12px" }
};

// src/workspace/WorkspaceProvider.tsx
import { createContext as createContext2, useContext as useContext2, useMemo as useMemo13, useState as useState30 } from "react";
import { jsx as jsx68 } from "react/jsx-runtime";
var WorkspaceContext = createContext2(null);
function useWorkspace() {
  const ctx = useContext2(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used within WorkspaceProvider");
  return ctx;
}
function WorkspaceProvider({
  workspace,
  actor,
  isOwner: isOwnerProp,
  locale: initialLocale = "en",
  theme: initialTheme = "dark",
  children
}) {
  const isOwner = isOwnerProp ?? (actor.authenticated === true && actor.actorId === workspace.owner && actor.roles.includes("owner"));
  const [view, setViewState] = useState30(workspace.currentView);
  const [selectedId, setSelectedId] = useState30(workspace.selectedTaskId);
  const [tick, setTick] = useState30(0);
  const [locale, setLocaleState] = useState30(initialLocale);
  const [theme2, setThemeState] = useState30(initialTheme);
  const allTasks = useMemo13(() => workspace.listTasks("all"), [workspace, tick]);
  const tasks = useMemo13(() => workspace.listTasks(view), [workspace, view, tick]);
  const selectedTask = useMemo13(
    () => allTasks.find((t5) => t5.taskId === selectedId),
    [allTasks, selectedId]
  );
  const refresh = () => setTick((n) => n + 1);
  const setCurrentView = (v) => {
    workspace.setView(v);
    setViewState(v);
  };
  const selectTask = (taskId) => {
    setSelectedId(taskId);
  };
  const setLocaleValue = (l) => {
    workspace.setLocale(l);
    setLocaleState(l);
    void workspace.persist();
  };
  const setThemeValue = (t5) => {
    workspace.setTheme(t5);
    setThemeState(t5);
    void workspace.persist();
  };
  const createTask = async (title) => {
    if (!isOwner) return;
    await workspace.createTaskFromChat({ title }, actor);
    refresh();
    const first = workspace.listTasks("all")[0];
    if (first) setSelectedId(first.taskId);
  };
  const cancelTask = async (taskId) => {
    if (!isOwner) return;
    await workspace.cancelTask(taskId, actor);
    refresh();
  };
  const pauseTask = async (taskId) => {
    if (!isOwner) return;
    await workspace.pauseTask(taskId, actor);
    refresh();
  };
  const resumeTask = async (taskId) => {
    if (!isOwner) return;
    await workspace.resumeTask(taskId, actor);
    refresh();
  };
  const canControlEmergencyStop = actor.roles.includes("super-admin") || actor.roles.includes("super-sub-admin");
  const toggleEmergencyStop = (active) => {
    if (!canControlEmergencyStop) return;
    workspace.setEmergencyStop("global", active, actor, active ? "workspace emergency stop" : void 0);
    refresh();
  };
  const linkVoiceTranscript = async (text2) => {
    if (!isOwner || !selectedId) return;
    await workspace.linkVoiceTranscript({ text: text2, taskId: selectedId }, actor);
    refresh();
  };
  const value = {
    workspace,
    isOwner,
    locale,
    theme: theme2,
    view,
    selectedTaskId: selectedId,
    selectedTask: selectedTask ?? void 0,
    tasks,
    isEmergencyStopped: workspace.isEmergencyStopped("global"),
    setLocale: setLocaleValue,
    setTheme: setThemeValue,
    setView: setCurrentView,
    selectTask,
    refresh,
    createTask,
    cancelTask,
    pauseTask,
    resumeTask,
    toggleEmergencyStop,
    linkVoiceTranscript
  };
  return /* @__PURE__ */ jsx68(WorkspaceContext.Provider, { value, children });
}

// src/workspace/UnifiedWorkspace.tsx
import React33 from "react";
import { CWJ_WORKSPACE_VIEWS } from "@smart-shikka/core/cwj-workspace";

// src/workspace/i18n.ts
var WORKSPACE_STRINGS = {
  workspaceTitle: {
    en: "Unified Workspace",
    hi: "\u090F\u0915\u0940\u0915\u0943\u0924 \u0935\u0930\u094D\u0915\u0938\u094D\u092A\u0947\u0938"
  },
  workspaceDesc: {
    en: "Chat, Code, Cowork and Voice share one taskId.",
    hi: "Chat, Code, Cowork \u0914\u0930 Voice \u090F\u0915 \u0939\u0940 taskId \u0938\u093E\u091D\u093E \u0915\u0930\u0924\u0947 \u0939\u0948\u0902\u0964"
  },
  noTaskSelected: {
    en: "Select or create a task to begin.",
    hi: "\u0936\u0941\u0930\u0942 \u0915\u0930\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u0915\u094B\u0908 task \u091A\u0941\u0928\u0947\u0902 \u092F\u093E \u092C\u0928\u093E\u090F\u0901\u0964"
  },
  ownerOnly: {
    en: "Owner-only controls",
    hi: "\u0915\u0947\u0935\u0932 \u0913\u0928\u0930 \u0915\u0947 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923"
  },
  emergencyStop: {
    en: "Emergency Stop",
    hi: "\u0907\u092E\u0930\u091C\u0947\u0902\u0938\u0940 \u0938\u094D\u091F\u0949\u092A"
  },
  emergencyStopActive: {
    en: "Emergency Stop active \u2014 mutations blocked.",
    hi: "\u0907\u092E\u0930\u091C\u0947\u0902\u0938\u0940 \u0938\u094D\u091F\u0949\u092A \u091A\u093E\u0932\u0942 \u2014 mutations \u092C\u094D\u0932\u0949\u0915 \u0939\u0948\u0902\u0964"
  },
  clearEmergencyStop: {
    en: "Clear Emergency Stop",
    hi: "\u0907\u092E\u0930\u091C\u0947\u0902\u0938\u0940 \u0938\u094D\u091F\u0949\u092A \u0939\u091F\u093E\u090F\u0901"
  },
  setEmergencyStop: {
    en: "Emergency Stop",
    hi: "\u0907\u092E\u0930\u091C\u0947\u0902\u0938\u0940 \u0938\u094D\u091F\u0949\u092A"
  },
  cancel: {
    en: "Cancel",
    hi: "\u0930\u0926\u094D\u0926 \u0915\u0930\u0947\u0902"
  },
  resume: {
    en: "Resume",
    hi: "\u091C\u093E\u0930\u0940 \u0930\u0916\u0947\u0902"
  },
  pause: {
    en: "Pause",
    hi: "\u0930\u094B\u0915\u0947\u0902"
  },
  chatTab: {
    en: "Chat",
    hi: "\u091A\u0948\u091F"
  },
  codeTab: {
    en: "Code",
    hi: "\u0915\u094B\u0921"
  },
  coworkTab: {
    en: "Cowork",
    hi: "Co-Work"
  },
  voiceTab: {
    en: "Voice",
    hi: "\u0935\u0949\u0907\u0938"
  },
  recordingVisible: {
    en: "Recording is visible and can be stopped by the owner.",
    hi: "\u0930\u093F\u0915\u0949\u0930\u094D\u0921\u093F\u0902\u0917 \u0926\u093F\u0916\u093E\u0908 \u0926\u0947 \u0930\u0939\u0940 \u0939\u0948 \u0914\u0930 \u0913\u0928\u0930 \u0907\u0938\u0947 \u0930\u094B\u0915 \u0938\u0915\u0924\u093E \u0939\u0948\u0964"
  },
  hiddenRecording: {
    en: "No hidden recording.",
    hi: "\u0915\u094B\u0908 \u091B\u093F\u092A\u0940 \u0930\u093F\u0915\u0949\u0930\u094D\u0921\u093F\u0902\u0917 \u0928\u0939\u0940\u0902\u0964"
  },
  lightMode: {
    en: "Light",
    hi: "\u0939\u0932\u094D\u0915\u093E"
  },
  darkMode: {
    en: "Dark",
    hi: "\u0917\u0939\u0930\u093E"
  },
  stage: {
    en: "Stage",
    hi: "\u091A\u0930\u0923"
  },
  transcript: {
    en: "Transcript",
    hi: "\u091F\u094D\u0930\u093E\u0902\u0938\u0915\u094D\u0930\u093F\u092A\u094D\u091F"
  },
  english: { en: "EN", hi: "EN" },
  hindi: { en: "HI", hi: "\u0939\u093F\u0902" }
};
function t4(key, locale) {
  return WORKSPACE_STRINGS[key]?.[locale] ?? key;
}

// src/workspace/UnifiedWorkspace.tsx
import { Fragment as Fragment5, jsx as jsx69, jsxs as jsxs62 } from "react/jsx-runtime";
var VIEW_KEYS = {
  inbox: "inbox",
  active: "active",
  approval: "approval",
  review: "review",
  qa: "qa",
  completed: "completed",
  failed: "failed",
  paused: "paused",
  all: "all"
};
function UnifiedWorkspace() {
  const ctx = useWorkspace();
  const {
    locale,
    theme: theme2,
    isOwner,
    isEmergencyStopped,
    view,
    tasks,
    selectedTask,
    setView,
    setLocale,
    setTheme,
    createTask,
    cancelTask,
    pauseTask,
    resumeTask,
    toggleEmergencyStop,
    linkVoiceTranscript,
    selectTask
  } = ctx;
  const tr16 = (key) => t4(key, locale);
  const surface = theme2 === "light" ? "#f8fafc" : baseUiTokens.colors.surface;
  const text2 = theme2 === "light" ? "#0f172a" : baseUiTokens.colors.text;
  const border = theme2 === "light" ? "rgba(15,23,42,0.14)" : baseUiTokens.colors.border;
  const [draft, setDraft] = React33.useState("");
  const [voiceDraft, setVoiceDraft] = React33.useState("");
  const handleCreate = (e) => {
    e.preventDefault();
    if (!draft.trim() || !isOwner) return;
    void createTask(draft.trim());
    setDraft("");
  };
  const handleVoiceLink = (e) => {
    e.preventDefault();
    if (!voiceDraft.trim() || !isOwner || !selectedTask) return;
    void linkVoiceTranscript(voiceDraft.trim());
    setVoiceDraft("");
  };
  return /* @__PURE__ */ jsxs62(
    "div",
    {
      "data-testid": "unified-workspace",
      "data-theme": theme2,
      "data-locale": locale,
      style: {
        minHeight: "100vh",
        backgroundColor: surface,
        color: text2,
        fontFamily: baseUiTokens.font.family,
        display: "flex",
        flexDirection: "column"
      },
      children: [
        isEmergencyStopped && /* @__PURE__ */ jsx69(
          "div",
          {
            role: "alert",
            "aria-live": "polite",
            "data-testid": "emergency-stop-banner",
            style: {
              padding: baseUiTokens.spacing.md,
              backgroundColor: baseUiTokens.colors.danger,
              color: "#fff",
              textAlign: "center"
            },
            children: tr16("emergencyStopActive")
          }
        ),
        /* @__PURE__ */ jsxs62(
          "header",
          {
            style: {
              padding: baseUiTokens.spacing.lg,
              borderBottom: `1px solid ${border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: baseUiTokens.spacing.md
            },
            children: [
              /* @__PURE__ */ jsxs62("div", { children: [
                /* @__PURE__ */ jsx69("h1", { style: { margin: 0, fontSize: "20px" }, children: tr16("workspaceTitle") }),
                /* @__PURE__ */ jsx69("p", { style: { margin: "4px 0 0", fontSize: "12px", opacity: 0.8 }, children: tr16("workspaceDesc") })
              ] }),
              /* @__PURE__ */ jsxs62("div", { style: { display: "flex", alignItems: "center", gap: baseUiTokens.spacing.sm }, children: [
                /* @__PURE__ */ jsx69(Button, { variant: "secondary", onClick: () => setLocale(locale === "hi" ? "en" : "hi"), children: locale === "hi" ? tr16("english") : tr16("hindi") }),
                /* @__PURE__ */ jsx69(Button, { variant: "secondary", onClick: () => setTheme(theme2 === "dark" ? "light" : "dark"), children: theme2 === "dark" ? tr16("lightMode") : tr16("darkMode") }),
                isOwner && /* @__PURE__ */ jsx69(
                  Button,
                  {
                    variant: isEmergencyStopped ? "secondary" : "danger",
                    onClick: () => toggleEmergencyStop(!isEmergencyStopped),
                    children: isEmergencyStopped ? tr16("clearEmergencyStop") : tr16("setEmergencyStop")
                  }
                )
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsxs62("div", { style: { display: "flex", flex: 1, overflow: "hidden" }, children: [
          /* @__PURE__ */ jsxs62(
            "aside",
            {
              style: {
                width: "260px",
                borderRight: `1px solid ${border}`,
                display: "flex",
                flexDirection: "column"
              },
              children: [
                /* @__PURE__ */ jsx69("div", { style: { padding: baseUiTokens.spacing.md, borderBottom: `1px solid ${border}` }, children: /* @__PURE__ */ jsx69("form", { onSubmit: handleCreate, children: /* @__PURE__ */ jsx69(
                  "input",
                  {
                    value: draft,
                    onChange: (e) => setDraft(e.target.value),
                    placeholder: isOwner ? "New task title" : tr16("ownerOnly"),
                    disabled: !isOwner,
                    "aria-label": "New task title",
                    style: {
                      width: "100%",
                      boxSizing: "border-box",
                      padding: baseUiTokens.spacing.sm,
                      borderRadius: baseUiTokens.radius.md,
                      border: `1px solid ${border}`,
                      backgroundColor: surface,
                      color: text2
                    }
                  }
                ) }) }),
                /* @__PURE__ */ jsx69("nav", { role: "tablist", "aria-label": "workspace views", style: { display: "flex", flexWrap: "wrap", gap: baseUiTokens.spacing.sm, padding: baseUiTokens.spacing.md, borderBottom: `1px solid ${border}` }, children: CWJ_WORKSPACE_VIEWS.map((v) => /* @__PURE__ */ jsx69(
                  "button",
                  {
                    role: "tab",
                    "aria-selected": v === view,
                    "data-testid": `view-tab-${v}`,
                    onClick: () => setView(v),
                    style: {
                      padding: "4px 10px",
                      borderRadius: baseUiTokens.radius.pill,
                      border: "none",
                      backgroundColor: v === view ? baseUiTokens.colors.primary : "transparent",
                      color: v === view ? "#fff" : text2,
                      cursor: "pointer"
                    },
                    children: tr16(VIEW_KEYS[v])
                  },
                  v
                )) }),
                /* @__PURE__ */ jsx69("div", { style: { flex: 1, overflow: "auto", padding: baseUiTokens.spacing.md }, children: tasks.length === 0 ? /* @__PURE__ */ jsx69("p", { children: tr16("noTaskSelected") }) : tasks.map((task) => /* @__PURE__ */ jsx69(
                  Card,
                  {
                    title: task.title,
                    subtitle: task.taskId,
                    onClick: () => selectTask(task.taskId),
                    style: {
                      marginBottom: baseUiTokens.spacing.md,
                      borderColor: selectedTask?.taskId === task.taskId ? baseUiTokens.colors.primary : border
                    },
                    headerActions: /* @__PURE__ */ jsx69(Badge, { variant: task.view === "failed" ? "danger" : task.view === "completed" ? "success" : "primary", children: task.view }),
                    children: /* @__PURE__ */ jsxs62("div", { style: { fontSize: "12px", opacity: 0.8 }, children: [
                      tr16("stage"),
                      ": ",
                      task.currentStageLabel,
                      " | ",
                      tr16("transcript"),
                      ": ",
                      task.voiceTranscriptPreview ?? "-"
                    ] })
                  },
                  task.taskId
                )) })
              ]
            }
          ),
          /* @__PURE__ */ jsx69("main", { style: { flex: 1, padding: baseUiTokens.spacing.lg, overflow: "auto" }, children: selectedTask ? /* @__PURE__ */ jsxs62(
            Card,
            {
              title: selectedTask.title,
              subtitle: `taskId: ${selectedTask.taskId}`,
              headerActions: /* @__PURE__ */ jsxs62(Fragment5, { children: [
                /* @__PURE__ */ jsx69(Badge, { variant: selectedTask.isPaused ? "warning" : "primary", children: selectedTask.taskStatus }),
                selectedTask.isPaused ? /* @__PURE__ */ jsx69(Badge, { variant: "warning", children: tr16("paused") }) : null
              ] }),
              children: [
                /* @__PURE__ */ jsxs62("div", { style: { marginBottom: baseUiTokens.spacing.md }, children: [
                  /* @__PURE__ */ jsxs62("strong", { children: [
                    tr16("stage"),
                    ": "
                  ] }),
                  selectedTask.currentStageLabel
                ] }),
                selectedTask.voiceTranscriptPreview && /* @__PURE__ */ jsxs62("div", { style: { marginBottom: baseUiTokens.spacing.md, padding: baseUiTokens.spacing.md, borderRadius: baseUiTokens.radius.md, backgroundColor: theme2 === "light" ? "#e2e8f0" : baseUiTokens.colors.surfaceStrong }, children: [
                  /* @__PURE__ */ jsxs62("strong", { children: [
                    tr16("transcript"),
                    ": "
                  ] }),
                  selectedTask.voiceTranscriptPreview
                ] }),
                isOwner && /* @__PURE__ */ jsxs62("div", { style: { display: "flex", gap: baseUiTokens.spacing.sm, marginBottom: baseUiTokens.spacing.md }, children: [
                  /* @__PURE__ */ jsx69(Button, { variant: "secondary", onClick: () => pauseTask(selectedTask.taskId), disabled: !selectedTask.canCancel || selectedTask.isPaused, children: tr16("pause") }),
                  /* @__PURE__ */ jsx69(Button, { variant: "secondary", onClick: () => resumeTask(selectedTask.taskId), disabled: !selectedTask.canResume, children: tr16("resume") }),
                  /* @__PURE__ */ jsx69(Button, { variant: "danger", onClick: () => cancelTask(selectedTask.taskId), disabled: !selectedTask.canCancel, children: tr16("cancel") })
                ] }),
                /* @__PURE__ */ jsxs62("section", { style: { marginTop: baseUiTokens.spacing.lg }, children: [
                  /* @__PURE__ */ jsx69("h2", { style: { fontSize: "14px" }, children: tr16("voiceTab") }),
                  /* @__PURE__ */ jsxs62(
                    "div",
                    {
                      role: "status",
                      "aria-live": "polite",
                      "data-testid": "recording-indicator",
                      style: {
                        display: "flex",
                        alignItems: "center",
                        gap: baseUiTokens.spacing.sm,
                        marginBottom: baseUiTokens.spacing.md,
                        color: baseUiTokens.colors.danger,
                        fontWeight: baseUiTokens.font.weightSemibold
                      },
                      children: [
                        /* @__PURE__ */ jsx69("span", { "aria-hidden": "true", children: "\u25CF" }),
                        tr16("hiddenRecording")
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsxs62("form", { onSubmit: handleVoiceLink, style: { display: "flex", gap: baseUiTokens.spacing.sm }, children: [
                    /* @__PURE__ */ jsx69(
                      "input",
                      {
                        value: voiceDraft,
                        onChange: (e) => setVoiceDraft(e.target.value),
                        disabled: !isOwner,
                        placeholder: isOwner ? "Voice transcript preview" : tr16("ownerOnly"),
                        "aria-label": "Voice transcript preview",
                        style: {
                          flex: 1,
                          padding: baseUiTokens.spacing.sm,
                          borderRadius: baseUiTokens.radius.md,
                          border: `1px solid ${border}`,
                          backgroundColor: surface,
                          color: text2
                        }
                      }
                    ),
                    /* @__PURE__ */ jsx69(Button, { variant: "primary", disabled: !isOwner, children: tr16("transcript") })
                  ] })
                ] })
              ]
            }
          ) : /* @__PURE__ */ jsx69(Card, { title: tr16("workspaceTitle"), children: /* @__PURE__ */ jsx69("p", { children: tr16("noTaskSelected") }) }) })
        ] })
      ]
    }
  );
}

// src/workspace/WorkspaceShellProvider.tsx
import { createContext as createContext3, useCallback as useCallback21, useContext as useContext3, useEffect as useEffect23, useMemo as useMemo14, useRef as useRef13, useState as useState31 } from "react";
import { jsx as jsx70 } from "react/jsx-runtime";
var WorkspaceShellContext = createContext3(null);
function useWorkspaceShell() {
  const ctx = useContext3(WorkspaceShellContext);
  if (!ctx) throw new Error("useWorkspaceShell must be used within WorkspaceShellProvider");
  return ctx;
}
function useOptionalWorkspaceShell() {
  return useContext3(WorkspaceShellContext);
}
var messageCounter = 0;
function generateMessageId() {
  messageCounter += 1;
  return `msg-${Date.now()}-${messageCounter}`;
}
function InnerProvider({
  children,
  projectRuntime,
  chatService,
  codeService,
  codeIntelligenceService,
  agentSkillsService,
  memoryService,
  builderService,
  browserSessionService,
  coworkService,
  controlService,
  ceoService,
  adminSurfaces,
  token,
  approvals: _initialApprovals,
  locale: initialLocale = "en",
  theme: initialTheme = "dark",
  initialNav = "chat",
  initialRightTab = "context",
  initialMode = "normal",
  surface = "web"
}) {
  const workspaceCtx = useWorkspace();
  const { locale: wsLocale, theme: wsTheme, setLocale: setWsLocale, setTheme: setWsTheme } = workspaceCtx;
  const [locale, setLocaleState] = useState31(initialLocale);
  const [theme2, setThemeState] = useState31(initialTheme);
  const [ceoActivityState, setCeoActivityState] = useState31("idle");
  const sendCeoTurn = useCallback21(async (input) => {
    if (!ceoService) {
      const result2 = { success: false, ok: false, realExecutionPerformed: false, answer: "CEO service not connected.", activityState: "error", requiresApproval: false, errorCode: "ceo_service_missing" };
      setCeoActivityState(result2.activityState);
      return result2;
    }
    const result = await ceoService.turn(input);
    setCeoActivityState(result.activityState);
    return result;
  }, [ceoService]);
  const approveCeo = useCallback21(async (input) => {
    if (!ceoService) {
      const result2 = { success: false, ok: false, realExecutionPerformed: false, answer: "CEO service not connected.", activityState: "error", requiresApproval: false, errorCode: "ceo_service_missing" };
      setCeoActivityState(result2.activityState);
      return result2;
    }
    const result = await ceoService.approve(input);
    setCeoActivityState(result.activityState);
    return result;
  }, [ceoService]);
  useEffect23(() => {
    if (wsLocale) setLocaleState(wsLocale);
  }, [wsLocale]);
  useEffect23(() => {
    if (wsTheme) setThemeState(wsTheme === "light" ? "light" : "dark");
  }, [wsTheme]);
  const setLocale = useCallback21(
    (l) => {
      setWsLocale(l);
      setLocaleState(l);
    },
    [setWsLocale]
  );
  const setTheme = useCallback21(
    (t5) => {
      setWsTheme(t5);
      setThemeState(t5);
    },
    [setWsTheme]
  );
  const [activeNav, setActiveNavState] = useState31(initialNav);
  const [rightTab, setRightTabState] = useState31(initialRightTab);
  const [mode, setModeState] = useState31(
    () => (typeof window !== "undefined" ? window.localStorage.getItem("ss-workspace-shell-mode") : void 0) ?? initialMode
  );
  const setActiveNav = useCallback21((id) => setActiveNavState(id), []);
  const setRightTab = useCallback21((id) => setRightTabState(id), []);
  const setMode = useCallback21(
    (m) => {
      setModeState(m);
      if (typeof window !== "undefined") {
        window.localStorage.setItem("ss-workspace-shell-mode", m);
      }
    },
    []
  );
  useEffect23(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem("ss-workspace-shell-layout");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed.activeNav) setActiveNavState(parsed.activeNav);
      if (parsed.rightTab) setRightTabState(parsed.rightTab);
      if (parsed.theme) setThemeState(parsed.theme);
      if (parsed.locale) setLocaleState(parsed.locale);
    } catch {
    }
  }, []);
  useEffect23(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      "ss-workspace-shell-layout",
      JSON.stringify({ activeNav, rightTab, theme: theme2, locale })
    );
  }, [activeNav, rightTab, theme2, locale]);
  const [chatMessages, setChatMessages] = useState31([]);
  const [chatConversations, setChatConversations] = useState31([]);
  const [selectedConversationId, setSelectedConversationId] = useState31();
  const [chatStreaming, setChatStreaming] = useState31(false);
  const [chatStreamingPreview, setChatStreamingPreview] = useState31("");
  const [chatProfile, setChatProfileState] = useState31("auto");
  const [chatError, setChatError] = useState31(null);
  const chatAbortRef = useRef13(null);
  const [tasks, setTasks] = useState31([]);
  const [view, setView] = useState31("inbox");
  const [selectedTaskId, setSelectedTaskId] = useState31();
  const refreshTasks = useCallback21(async () => {
    if (coworkService) {
      const list = await coworkService.listRuns();
      setTasks(list);
      return;
    }
    if (!workspaceCtx.workspace) return;
    setTasks(workspaceCtx.workspace.listTasks(view));
    setSelectedTaskId(workspaceCtx.workspace.selectedTaskId);
  }, [coworkService, workspaceCtx.workspace, view]);
  useEffect23(() => {
    void refreshTasks();
  }, [refreshTasks]);
  useEffect23(() => {
    if (coworkService) return;
    const id = setInterval(() => {
      if (!workspaceCtx.workspace) return;
      void workspaceCtx.workspace.persist();
    }, 5e3);
    return () => clearInterval(id);
  }, [coworkService, workspaceCtx.workspace]);
  const setSelectedTask = useCallback21(
    async (id) => {
      setSelectedTaskId(id);
      if (coworkService && id) {
        const run = await coworkService.getRun(id);
        if (run) {
          setTasks((prev) => {
            const others = prev.filter((t5) => t5.taskId !== run.taskId);
            return [...others, run];
          });
        }
        return;
      }
      if (id && workspaceCtx.workspace) {
        void workspaceCtx.workspace.openTask(id).then(() => refreshTasks());
      }
    },
    [coworkService, workspaceCtx.workspace, refreshTasks]
  );
  const switchView = useCallback21(
    (v) => {
      if (workspaceCtx.workspace) workspaceCtx.workspace.setView(v);
      setView(v);
      void refreshTasks();
    },
    [workspaceCtx.workspace, refreshTasks]
  );
  const pauseTask = useCallback21(
    async (taskId) => {
      if (coworkService) {
        await coworkService.pauseRun(taskId);
        await refreshTasks();
        return;
      }
      if (!workspaceCtx.workspace) return;
      await workspaceCtx.pauseTask(taskId);
    },
    [coworkService, workspaceCtx.workspace, workspaceCtx.pauseTask, refreshTasks]
  );
  const resumeTask = useCallback21(
    async (taskId) => {
      if (coworkService) {
        await coworkService.resumeRun(taskId);
        await refreshTasks();
        return;
      }
      if (!workspaceCtx.workspace) return;
      await workspaceCtx.resumeTask(taskId);
    },
    [coworkService, workspaceCtx.workspace, workspaceCtx.resumeTask, refreshTasks]
  );
  const cancelTask = useCallback21(
    async (taskId) => {
      if (coworkService) {
        await coworkService.cancelRun(taskId);
        await refreshTasks();
        return;
      }
      if (!workspaceCtx.workspace) return;
      await workspaceCtx.cancelTask(taskId);
    },
    [coworkService, workspaceCtx.workspace, workspaceCtx.cancelTask, refreshTasks]
  );
  const loadConversations = useCallback21(async () => {
    if (!chatService) return;
    try {
      const list = await chatService.listConversations();
      setChatConversations(list);
    } catch (e) {
      setChatError(errorToCode(e));
    }
  }, [chatService]);
  useEffect23(() => {
    void loadConversations();
  }, [loadConversations]);
  const selectChatConversation = useCallback21(
    async (id) => {
      if (!chatService) {
        setChatError("Chat service not connected.");
        return;
      }
      setChatError(null);
      setChatStreaming(false);
      setChatStreamingPreview("");
      try {
        const messages = await chatService.loadConversation(id);
        setSelectedConversationId(id);
        setChatMessages(messages);
      } catch (e) {
        setChatError(errorToCode(e));
      }
    },
    [chatService]
  );
  const newChatConversation = useCallback21(async () => {
    if (!chatService) {
      setChatError("Chat service not connected.");
      return;
    }
    setChatError(null);
    try {
      const id = await chatService.newConversation();
      setSelectedConversationId(id);
      setChatMessages([]);
      await loadConversations();
    } catch (e) {
      setChatError(errorToCode(e));
    }
  }, [chatService, loadConversations]);
  const stopChat = useCallback21(() => {
    chatAbortRef.current?.abort();
    chatService?.stop?.();
    setChatStreaming(false);
  }, [chatService]);
  const streamMessage = useCallback21(
    async (content, targetMessageId) => {
      if (!chatService) {
        setChatError("Chat service not connected.");
        return;
      }
      setChatError(null);
      setChatStreaming(true);
      setChatStreamingPreview("");
      chatAbortRef.current?.abort();
      const controller = new AbortController();
      chatAbortRef.current = controller;
      const userMessage = {
        id: targetMessageId ?? generateMessageId(),
        role: "user",
        content,
        status: "sending",
        timestamp: /* @__PURE__ */ new Date()
      };
      if (!targetMessageId) {
        setChatMessages((prev) => [...prev, userMessage]);
      }
      try {
        await chatService.stream(
          { conversationId: selectedConversationId, message: content, ...chatProfile === "auto" ? {} : { logicalProfile: chatProfile } },
          {
            onChunk: (chunk) => setChatStreamingPreview((prev) => prev + chunk),
            onConversationCreated: (id) => {
              setSelectedConversationId(id);
              void loadConversations();
            },
            onDone: (finalMessage) => {
              setChatMessages(
                (prev) => prev.map((message) => {
                  if (message.id !== userMessage.id) return message;
                  const { errorMessage: _errorMessage, ...messageWithoutError } = message;
                  return { ...messageWithoutError, status: "sent" };
                })
              );
              setChatMessages((prev) => [...prev, finalMessage]);
              setChatStreamingPreview("");
              setChatStreaming(false);
            }
          },
          controller.signal
        );
      } catch (e) {
        if (e.name !== "AbortError" && e.message !== "Cancelled") {
          const errorMessage = errorToCode(e);
          setChatError(errorMessage);
          setChatMessages(
            (prev) => prev.map(
              (message) => message.id === userMessage.id ? { ...message, status: "error", errorMessage } : message
            )
          );
        } else {
          setChatMessages(
            (prev) => prev.map((message) => {
              if (message.id !== userMessage.id) return message;
              const { errorMessage: _errorMessage, ...messageWithoutError } = message;
              return { ...messageWithoutError, status: "idle" };
            })
          );
        }
        setChatStreaming(false);
      } finally {
        chatAbortRef.current = null;
      }
    },
    [chatService, selectedConversationId]
  );
  const sendChatMessage = useCallback21(
    async (content) => {
      await streamMessage(content);
    },
    [streamMessage]
  );
  const retryChatMessage = useCallback21(
    async (messageId) => {
      const message = chatMessages.find((m) => m.id === messageId);
      if (!message || message.role !== "user") return;
      setChatMessages((prev) => prev.filter((m) => m.id !== messageId));
      await streamMessage(message.content, messageId);
    },
    [chatMessages, streamMessage]
  );
  const editAndResendChatMessage = useCallback21(
    async (messageId, newContent) => {
      setChatMessages(
        (prev) => prev.map((m) => m.id === messageId ? { ...m, content: newContent, status: "sending" } : m)
      );
      await streamMessage(newContent, messageId);
    },
    [streamMessage]
  );
  const branchChatConversation = useCallback21(
    async (messageId) => {
      const idx = chatMessages.findIndex((m) => m.id === messageId);
      if (idx < 0) return;
      const branchMessages = chatMessages.slice(0, idx + 1);
      setChatMessages(branchMessages);
      await newChatConversation();
    },
    [chatMessages, newChatConversation]
  );
  const [codeRepositories, setCodeRepositories] = useState31([]);
  const [selectedRepositoryId, setSelectedRepositoryId] = useState31();
  const [codeTree, setCodeTree] = useState31([]);
  const [codeSearchQuery, setCodeSearchQuery] = useState31("");
  const [codeSearchResults, setCodeSearchResults] = useState31([]);
  const [codeSymbolResults, setCodeSymbolResults] = useState31([]);
  const [openFiles, setOpenFiles] = useState31([]);
  const [activeFilePath, setActiveFilePath] = useState31();
  const [diffPreview, setDiffPreview] = useState31();
  const [patchRequest, setPatchRequest] = useState31();
  const [gitStatus, setGitStatus] = useState31();
  const [testBuildResult, setTestBuildResult] = useState31();
  const [codeError, setCodeError] = useState31(null);
  const [codeLoading, setCodeLoading] = useState31(false);
  const setCodeErrorHonest = useCallback21((message) => {
    setCodeError(message);
  }, []);
  const loadRepositories = useCallback21(async () => {
    if (!codeService) {
      setCodeErrorHonest("Code workspace service not connected.");
      return;
    }
    setCodeLoading(true);
    setCodeError(null);
    try {
      const list = await codeService.listRepositories();
      setCodeRepositories(list);
    } catch (e) {
      setCodeError(errorToCode(e));
    } finally {
      setCodeLoading(false);
    }
  }, [codeService, setCodeErrorHonest]);
  useEffect23(() => {
    void loadRepositories();
  }, [loadRepositories]);
  const selectRepository = useCallback21(
    async (id) => {
      if (!codeService) {
        setCodeErrorHonest("Code workspace service not connected.");
        return;
      }
      setCodeLoading(true);
      setCodeError(null);
      try {
        const repo = await codeService.loadRepository(id);
        setSelectedRepositoryId(id);
        setCodeTree(repo.tree);
        setGitStatus(repo.gitStatus);
        setOpenFiles([]);
        setActiveFilePath(void 0);
        setDiffPreview(void 0);
        setPatchRequest(void 0);
      } catch (e) {
        setCodeError(errorToCode(e));
      } finally {
        setCodeLoading(false);
      }
    },
    [codeService, setCodeErrorHonest]
  );
  const searchCode = useCallback21(
    async (query) => {
      setCodeSearchQuery(query);
      if (!codeService) {
        setCodeErrorHonest("Code workspace service not connected.");
        return;
      }
      setCodeLoading(true);
      setCodeError(null);
      try {
        const results = await codeService.searchFiles(query);
        setCodeSearchResults(results);
      } catch (e) {
        setCodeError(errorToCode(e));
      } finally {
        setCodeLoading(false);
      }
    },
    [codeService, setCodeErrorHonest]
  );
  const searchSymbols = useCallback21(
    async (query) => {
      if (!codeService) {
        setCodeErrorHonest("Code workspace service not connected.");
        return;
      }
      setCodeLoading(true);
      setCodeError(null);
      try {
        const results = await codeService.searchSymbols(query);
        setCodeSymbolResults(results);
      } catch (e) {
        setCodeError(errorToCode(e));
      } finally {
        setCodeLoading(false);
      }
    },
    [codeService, setCodeErrorHonest]
  );
  const openFile = useCallback21(
    async (path) => {
      if (!codeService) {
        setCodeErrorHonest("Code workspace service not connected.");
        return;
      }
      if (openFiles.some((f) => f.path === path)) {
        setActiveFilePath(path);
        return;
      }
      setCodeLoading(true);
      setCodeError(null);
      try {
        const file = await codeService.getFile(path);
        setOpenFiles((prev) => [...prev, file]);
        setActiveFilePath(path);
      } catch (e) {
        setCodeError(errorToCode(e));
      } finally {
        setCodeLoading(false);
      }
    },
    [codeService, openFiles, setCodeErrorHonest]
  );
  const closeFile = useCallback21((path) => {
    setOpenFiles((prev) => {
      const next = prev.filter((f) => f.path !== path);
      if (activeFilePath === path) {
        setActiveFilePath(next[0]?.path);
      }
      return next;
    });
  }, [activeFilePath]);
  const loadDiff = useCallback21(
    async (base, head) => {
      if (!codeService) {
        setCodeErrorHonest("Code workspace service not connected.");
        return;
      }
      setCodeLoading(true);
      setCodeError(null);
      try {
        const preview = await codeService.getDiff(base, head);
        setDiffPreview(preview);
      } catch (e) {
        setCodeError(errorToCode(e));
      } finally {
        setCodeLoading(false);
      }
    },
    [codeService, setCodeErrorHonest]
  );
  const requestPatchApproval = useCallback21(
    async (patch) => {
      if (!codeService) {
        setCodeErrorHonest("Code workspace service not connected.");
        return void 0;
      }
      setCodeLoading(true);
      setCodeError(null);
      try {
        const result = await codeService.requestPatchApproval(patch);
        setPatchRequest(patch);
        return result.approvalId;
      } catch (e) {
        setCodeError(errorToCode(e));
        return void 0;
      } finally {
        setCodeLoading(false);
      }
    },
    [codeService, setCodeErrorHonest]
  );
  const approvePatch = useCallback21(
    async (approvalId) => {
      if (!codeService) {
        setCodeErrorHonest("Code workspace service not connected.");
        return void 0;
      }
      setCodeLoading(true);
      setCodeError(null);
      try {
        return await codeService.approvePatch(approvalId);
      } catch (e) {
        setCodeError(errorToCode(e));
        return void 0;
      } finally {
        setCodeLoading(false);
      }
    },
    [codeService, setCodeErrorHonest]
  );
  const applyPatch = useCallback21(
    async (approvalId, approvalToken) => {
      if (!codeService) {
        setCodeErrorHonest("Code workspace service not connected.");
        return;
      }
      setCodeLoading(true);
      setCodeError(null);
      try {
        await codeService.applyPatch(approvalId, approvalToken);
      } catch (e) {
        setCodeError(errorToCode(e));
      } finally {
        setCodeLoading(false);
      }
    },
    [codeService, setCodeErrorHonest]
  );
  const publishPatch = useCallback21(
    async (approvalId, publishToken) => {
      if (!codeService) {
        setCodeErrorHonest("Code workspace service not connected.");
        return;
      }
      setCodeLoading(true);
      setCodeError(null);
      try {
        await codeService.publishPatch(approvalId, publishToken);
      } catch (e) {
        setCodeError(errorToCode(e));
      } finally {
        setCodeLoading(false);
      }
    },
    [codeService, setCodeErrorHonest]
  );
  const runTests = useCallback21(async () => {
    if (!codeService) {
      setCodeErrorHonest("Code workspace service not connected.");
      return;
    }
    setCodeLoading(true);
    setCodeError(null);
    try {
      const result = await codeService.runTests();
      setTestBuildResult(result);
    } catch (e) {
      setCodeError(errorToCode(e));
    } finally {
      setCodeLoading(false);
    }
  }, [codeService, setCodeErrorHonest]);
  const runBuild = useCallback21(async () => {
    if (!codeService) {
      setCodeErrorHonest("Code workspace service not connected.");
      return;
    }
    setCodeLoading(true);
    setCodeError(null);
    try {
      const result = await codeService.runBuild();
      setTestBuildResult(result);
    } catch (e) {
      setCodeError(errorToCode(e));
    } finally {
      setCodeLoading(false);
    }
  }, [codeService, setCodeErrorHonest]);
  const rollback = useCallback21(
    async (approvalId, rollbackToken) => {
      if (!codeService) {
        setCodeErrorHonest("Code workspace service not connected.");
        return;
      }
      setCodeLoading(true);
      setCodeError(null);
      try {
        await codeService.rollback(approvalId, rollbackToken);
        setPatchRequest(void 0);
      } catch (e) {
        setCodeError(errorToCode(e));
      } finally {
        setCodeLoading(false);
      }
    },
    [codeService, setCodeErrorHonest]
  );
  const [memoryContext, setMemoryContext] = useState31();
  const [memories, setMemories] = useState31([]);
  const [memoryLoading, setMemoryLoading] = useState31(false);
  const [memoryError, setMemoryError] = useState31(null);
  const loadMemoryContext = useCallback21(async () => {
    if (!memoryService) {
      setMemoryError("Memory service not connected.");
      return;
    }
    const lastUserMessage = [...chatMessages].reverse().find((m) => m.role === "user")?.content ?? "";
    if (!lastUserMessage) {
      setMemoryContext({ includedFiles: [], includedMemories: [], retrievedSymbols: [], stale: false });
      setMemoryError(null);
      return;
    }
    setMemoryLoading(true);
    setMemoryError(null);
    try {
      const ctx = await memoryService.getContext(lastUserMessage);
      setMemoryContext(ctx);
    } catch (e) {
      setMemoryError(errorToCode(e));
    } finally {
      setMemoryLoading(false);
    }
  }, [memoryService, chatMessages]);
  const loadMemories = useCallback21(async () => {
    if (!memoryService) {
      setMemoryError("Memory service not connected.");
      return;
    }
    setMemoryLoading(true);
    setMemoryError(null);
    try {
      const list = await memoryService.listMemories();
      setMemories(list);
    } catch (e) {
      setMemoryError(errorToCode(e));
    } finally {
      setMemoryLoading(false);
    }
  }, [memoryService]);
  useEffect23(() => {
    void loadMemoryContext();
    void loadMemories();
  }, [loadMemoryContext, loadMemories]);
  const pinMemory = useCallback21(
    async (id) => {
      if (!memoryService) return;
      setMemoryLoading(true);
      try {
        await memoryService.pin(id);
        await loadMemories();
      } catch (e) {
        setMemoryError(errorToCode(e));
      } finally {
        setMemoryLoading(false);
      }
    },
    [memoryService, loadMemories]
  );
  const unpinMemory = useCallback21(
    async (id) => {
      if (!memoryService) return;
      setMemoryLoading(true);
      try {
        await memoryService.unpin(id);
        await loadMemories();
      } catch (e) {
        setMemoryError(errorToCode(e));
      } finally {
        setMemoryLoading(false);
      }
    },
    [memoryService, loadMemories]
  );
  const deleteMemory = useCallback21(
    async (id) => {
      if (!memoryService) return;
      setMemoryLoading(true);
      try {
        await memoryService.delete(id);
        await loadMemories();
      } catch (e) {
        setMemoryError(errorToCode(e));
      } finally {
        setMemoryLoading(false);
      }
    },
    [memoryService, loadMemories]
  );
  const exportMemories = useCallback21(async () => {
    if (!memoryService) {
      setMemoryError("Memory service not connected.");
      return void 0;
    }
    try {
      return await memoryService.export();
    } catch (e) {
      setMemoryError(errorToCode(e));
      return void 0;
    }
  }, [memoryService]);
  const [approvals, setApprovals] = useState31(_initialApprovals ?? []);
  const [approvalLoading, setApprovalLoading] = useState31(false);
  const loadApprovals = useCallback21(async () => {
    setApprovalLoading(true);
    setApprovals([]);
    setApprovalLoading(false);
  }, []);
  const [browserArtifacts, setBrowserArtifacts] = useState31([]);
  const [browserArtifactsLoading, setBrowserArtifactsLoading] = useState31(false);
  const [browserArtifactsError, setBrowserArtifactsError] = useState31(null);
  const [activeBrowserTab, setActiveBrowserTab] = useState31("browser");
  const loadBrowserArtifacts = useCallback21(async () => {
    if (!controlService) {
      setBrowserArtifactsError("Control service not connected.");
      return;
    }
    setBrowserArtifactsLoading(true);
    setBrowserArtifactsError(null);
    try {
      const items = await controlService.listArtifacts();
      setBrowserArtifacts(items);
    } catch (err) {
      setBrowserArtifactsError(errorToCode(err));
    } finally {
      setBrowserArtifactsLoading(false);
    }
  }, [controlService]);
  useEffect23(() => {
    if (rightTab === "browser") {
      void loadBrowserArtifacts();
    }
  }, [rightTab, loadBrowserArtifacts]);
  const [coworkEmergencyStopped, setCoworkEmergencyStopped] = useState31(false);
  const [emergencyStopLoading, setEmergencyStopLoading] = useState31(false);
  const [emergencyStopError, setEmergencyStopError] = useState31(null);
  const loadEmergencyStop = useCallback21(async () => {
    if (!coworkService) return;
    try {
      const stopped = await coworkService.getEmergencyStopStatus();
      setCoworkEmergencyStopped(stopped);
      setEmergencyStopError(null);
    } catch {
      setEmergencyStopError("Could not refresh Emergency Stop state from server.");
    }
  }, [coworkService]);
  useEffect23(() => {
    void loadEmergencyStop();
  }, [loadEmergencyStop]);
  const isEmergencyStopped = useMemo14(
    () => coworkService ? coworkEmergencyStopped : workspaceCtx.workspace?.isEmergencyStopped("global") ?? false,
    [coworkService, coworkEmergencyStopped, workspaceCtx.workspace]
  );
  const toggleEmergencyStop = useCallback21(
    async (active) => {
      if (!coworkService) {
        setEmergencyStopError("Cowork service not available; cannot change Emergency Stop.");
        throw new Error("Cowork service not available");
      }
      setEmergencyStopLoading(true);
      try {
        await coworkService.setEmergencyStop(active);
        await loadEmergencyStop();
      } catch (err) {
        setEmergencyStopError(errorToCode(err));
        throw err;
      } finally {
        setEmergencyStopLoading(false);
      }
    },
    [coworkService, loadEmergencyStop]
  );
  const isOwner = workspaceCtx.isOwner;
  const currentContext = projectRuntime?.currentContext;
  const role = currentContext?.role;
  const isSuperAdmin = role === "super-admin";
  const isCeoEligible2 = isSuperAdmin || role === "super-sub-admin";
  const value = {
    token,
    controlService,
    ceoService,
    ceoActivityState,
    sendCeoTurn,
    approveCeo,
    adminSurfaces,
    locale,
    theme: theme2,
    setLocale,
    setTheme,
    activeNav,
    setActiveNav,
    rightTab,
    setRightTab,
    surface,
    projectRuntime,
    currentContext,
    chatService,
    codeService,
    codeIntelligenceService,
    agentSkillsService,
    memoryService,
    builderService,
    browserSessionService,
    chatMessages,
    chatConversations,
    chatStreaming,
    chatStreamingPreview,
    chatError,
    selectedConversationId,
    chatProfile,
    setChatProfile: setChatProfileState,
    sendChatMessage,
    stopChat,
    retryChatMessage,
    editAndResendChatMessage,
    branchChatConversation,
    newChatConversation,
    selectChatConversation,
    selectedTask: tasks.find((t5) => t5.taskId === selectedTaskId),
    setSelectedTaskId: setSelectedTask,
    tasks,
    view,
    setView: switchView,
    pauseTask,
    resumeTask,
    cancelTask,
    codeRepositories,
    selectedRepositoryId,
    codeTree,
    codeSearchQuery,
    codeSearchResults,
    codeSymbolResults,
    openFiles,
    activeFilePath,
    diffPreview,
    patchRequest,
    gitStatus,
    testBuildResult,
    codeError,
    codeLoading,
    loadRepositories,
    selectRepository,
    searchCode,
    searchSymbols,
    openFile,
    closeFile,
    loadDiff,
    requestPatchApproval,
    approvePatch,
    applyPatch,
    publishPatch,
    runTests,
    runBuild,
    rollback,
    memoryContext,
    memories,
    memoryLoading,
    memoryError,
    loadMemoryContext,
    loadMemories,
    pinMemory,
    unpinMemory,
    deleteMemory,
    exportMemories,
    approvals,
    approvalLoading,
    loadApprovals,
    pendingApprovals: approvals.filter((a) => a.status === "pending").length,
    browserArtifacts,
    browserArtifactsLoading,
    browserArtifactsError,
    loadBrowserArtifacts,
    activeBrowserTab,
    setActiveBrowserTab,
    isOwner,
    isSuperAdmin,
    isCeoEligible: isCeoEligible2,
    role,
    isEmergencyStopped,
    emergencyStopLoading,
    emergencyStopError,
    toggleEmergencyStop,
    mode,
    setMode
  };
  return /* @__PURE__ */ jsx70(WorkspaceShellContext.Provider, { value, children });
}
function WorkspaceShellProvider({
  workspace,
  actor,
  children,
  ...rest
}) {
  const providerProps = {};
  if (rest.locale !== void 0) providerProps.locale = rest.locale;
  if (rest.theme !== void 0) providerProps.theme = rest.theme;
  return /* @__PURE__ */ jsx70(WorkspaceProvider, { workspace, actor, ...providerProps, children: /* @__PURE__ */ jsx70(InnerProvider, { ...rest, initialMode: rest.initialMode ?? (actor.roles?.includes("owner") || actor.roles?.includes("super-admin") ? "advanced" : "normal"), surface: rest.surface ?? "web", children }) });
}

// src/workspace/WorkspaceShell.tsx
import { useCallback as useCallback26, useEffect as useEffect32, useMemo as useMemo19, useRef as useRef18, useState as useState40 } from "react";

// src/workspace/ChatWorkspace.tsx
import { useCallback as useCallback22, useEffect as useEffect24, useRef as useRef14, useState as useState32 } from "react";
import { Fragment as Fragment6, jsx as jsx71, jsxs as jsxs63 } from "react/jsx-runtime";
var SHELL_STRINGS = {
  typeMessage: { en: "Type a message\u2026", hi: "\u0938\u0902\u0926\u0947\u0936 \u0932\u093F\u0916\u0947\u0902\u2026" },
  chatServiceMissing: { en: "Chat service not connected.", hi: "Chat service connected \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" },
  ownerOnly: { en: "Owner-only controls", hi: "\u0915\u0947\u0935\u0932 \u0913\u0928\u0930 \u0915\u0947 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923" },
  emergencyStopActive: { en: "Emergency Stop active \u2014 chat disabled.", hi: "\u0907\u092E\u0930\u091C\u0947\u0902\u0938\u0940 \u0938\u094D\u091F\u0949\u092A \u091A\u093E\u0932\u0942 \u2014 chat disabled\u0964" },
  changes: { en: "Changes", hi: "Changes" },
  worklog: { en: "Worklog", hi: "Worklog" },
  browser: { en: "Browser", hi: "Browser" },
  preview: { en: "Preview", hi: "\u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942" },
  previewLocal: { en: "Local preview", hi: "\u0932\u094B\u0915\u0932 \u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942" },
  previewPublic: { en: "Web preview", hi: "\u0935\u0947\u092C \u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942" },
  previewUnavailable: { en: "Preview not available.", hi: "\u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" },
  previewNoProject: { en: "Select a project to see preview truth.", hi: "\u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942 \u0926\u0947\u0916\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F project \u091A\u0941\u0928\u0947\u0902\u0964" },
  previewRefresh: { en: "Refresh", hi: "\u0930\u0940\u092B\u093C\u094D\u0930\u0947\u0936" },
  previewOpen: { en: "Open in new tab", hi: "\u0928\u0908 tab \u092E\u0947\u0902 \u0916\u094B\u0932\u0947\u0902" },
  shell: { en: "Shell", hi: "Shell" },
  ide: { en: "IDE", hi: "IDE" },
  agents: { en: "Agents", hi: "Agents" },
  noRepoSelected: { en: "No repository selected.", hi: "\u0915\u094B\u0908 repository \u091A\u0941\u0928\u093E \u0928\u0939\u0940\u0902 \u0917\u092F\u093E\u0964" },
  selectRepo: { en: "Select repository", hi: "Repository \u091A\u0941\u0928\u0947\u0902" },
  loadRepo: { en: "Load repository", hi: "Repository \u0932\u094B\u0921 \u0915\u0930\u0947\u0902" },
  fileTree: { en: "Files", hi: "\u092B\u093C\u093E\u0907\u0932\u0947\u0902" },
  noFileOpen: { en: "Select a file to view.", hi: "\u0926\u0947\u0916\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F file \u091A\u0941\u0928\u0947\u0902\u0964" },
  noChanges: { en: "No changes loaded. Choose a repository and load a diff.", hi: "\u0915\u094B\u0908 changes loaded \u0928\u0939\u0940\u0902\u0964 Repository \u091A\u0941\u0928\u0947\u0902 \u0914\u0930 diff \u0932\u094B\u0921 \u0915\u0930\u0947\u0902\u0964" },
  base: { en: "Base", hi: "Base" },
  head: { en: "Head", hi: "Head" },
  loadDiff: { en: "Load diff", hi: "Diff \u0932\u094B\u0921 \u0915\u0930\u0947\u0902" },
  gitStatus: { en: "Git status", hi: "Git status" },
  diffPreview: { en: "Diff preview", hi: "Diff preview" },
  requestPatchApproval: { en: "Request patch approval", hi: "Patch approval \u092E\u093E\u0901\u0917\u0947\u0902" },
  noWorklog: { en: "No activity yet.", hi: "\u0905\u092D\u0940 \u0924\u0915 \u0915\u094B\u0908 activity \u0928\u0939\u0940\u0902\u0964" },
  url: { en: "URL", hi: "URL" },
  openPreview: { en: "Open preview", hi: "Preview \u0916\u094B\u0932\u0947\u0902" },
  previewNote: { en: "Read-only web preview. Real browser control is in Settings \u2192 Browser & Desktop Control.", hi: "Read-only web preview\u0964 \u0905\u0938\u0932\u0940 browser control Settings \u2192 Browser & Desktop Control \u092E\u0947\u0902 \u0939\u0948\u0964" },
  worktrees: { en: "Sandbox worktrees", hi: "\u0938\u0948\u0902\u0921\u092C\u0949\u0915\u094D\u0938 \u0935\u0930\u094D\u0915\u091F\u094D\u0930\u0940" },
  worktreesNote: {
    en: "Active patch/command sandbox leases. Paths live on the executing workspace host.",
    hi: "\u0938\u0915\u094D\u0930\u093F\u092F patch/command \u0938\u0948\u0902\u0921\u092C\u0949\u0915\u094D\u0938 leases\u0964 Path executing host (\u0921\u0947\u0938\u094D\u0915\u091F\u0949\u092A) \u092A\u0930 \u0939\u0948\u0964"
  },
  worktreesNone: { en: "No active worktree leases.", hi: "\u0915\u094B\u0908 \u0938\u0915\u094D\u0930\u093F\u092F \u0935\u0930\u094D\u0915\u091F\u094D\u0930\u0940 lease \u0928\u0939\u0940\u0902\u0964" },
  worktreesRefresh: { en: "Refresh", hi: "\u0930\u0940\u092B\u093C\u094D\u0930\u0947\u0936" },
  worktreesPublished: { en: "published", hi: "published" },
  brokerSessions: { en: "Control sessions", hi: "\u0915\u0902\u091F\u094D\u0930\u094B\u0932 \u0938\u0947\u0936\u0928" },
  brokerNone: { en: "No active control sessions.", hi: "\u0915\u094B\u0908 \u0938\u0915\u094D\u0930\u093F\u092F \u0915\u0902\u091F\u094D\u0930\u094B\u0932 \u0938\u0947\u0936\u0928 \u0928\u0939\u0940\u0902\u0964" },
  brokerCancel: { en: "Stop", hi: "\u0930\u094B\u0915\u0947\u0902" },
  brokerCancelConfirm: { en: "Stop this browser control session?", hi: "\u092F\u0939 \u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930 \u0915\u0902\u091F\u094D\u0930\u094B\u0932 \u0938\u0947\u0936\u0928 \u0930\u094B\u0915\u0947\u0902?" },
  runTests: { en: "Run tests", hi: "Tests \u091A\u0932\u093E\u090F\u0901" },
  runBuild: { en: "Run build", hi: "Build \u091A\u0932\u093E\u090F\u0901" },
  noShellOutput: { en: "No command output. Run tests or build to see results.", hi: "\u0915\u094B\u0908 command output \u0928\u0939\u0940\u0902\u0964 \u0926\u0947\u0916\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F tests \u092F\u093E build \u091A\u0932\u093E\u090F\u0901\u0964" },
  noAgents: { en: "No agents running.", hi: "\u0915\u094B\u0908 agents running \u0928\u0939\u0940\u0902\u0964" },
  expanded: { en: "Expanded", hi: "\u0916\u0941\u0932\u093E" },
  collapsed: { en: "Collapsed", hi: "\u092C\u0902\u0926" },
  disabledReason: { en: "Disabled: backend service not available.", hi: "Disabled: backend service \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902\u0964" },
  controlsLocked: { en: "Controls locked.", hi: "Controls \u0932\u0949\u0915 \u0939\u0948\u0902\u0964" }
};
function tr7(key, locale) {
  return SHELL_STRINGS[key]?.[locale] ?? key;
}
var TABS = [
  { id: "changes", labelKey: "changes" },
  { id: "worklog", labelKey: "worklog" },
  { id: "browser", labelKey: "browser" },
  { id: "preview", labelKey: "preview" },
  { id: "shell", labelKey: "shell" },
  { id: "ide", labelKey: "ide" },
  { id: "agents", labelKey: "agents" }
];
var PANEL_HEIGHT_KEY = "smart-shikka-chat-panel-height";
var PANEL_OPEN_KEY = "smart-shikka-chat-panel-open";
var DEFAULT_PANEL_HEIGHT = 280;
var MIN_PANEL_HEIGHT = 120;
var MAX_PANEL_HEIGHT = 600;
function themeColors2(theme2) {
  if (theme2 === "light") {
    return {
      surface: warmNeutralTokens.surface,
      surfaceStrong: warmNeutralTokens.surfaceStrong,
      text: warmNeutralTokens.text,
      textMuted: warmNeutralTokens.textMuted,
      border: warmNeutralTokens.border
    };
  }
  return {
    surface: "#1f2023",
    surfaceStrong: "#111215",
    text: "#f4f4f5",
    textMuted: "#9ca3af",
    border: "#2e3035"
  };
}
function DisabledHint({ message, theme: theme2 }) {
  const colors = themeColors2(theme2);
  return /* @__PURE__ */ jsx71("p", { style: { color: colors.textMuted, fontSize: "13px", margin: 0 }, children: message });
}
function ChatWorkspace() {
  const shell = useWorkspaceShell();
  const colors = themeColors2(shell.theme);
  const [activeTab, setActiveTab] = useState32(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("smart-shikka-chat-active-tab") : null;
    return TABS.some((t5) => t5.id === saved) ? saved : "changes";
  });
  const [panelOpen, setPanelOpen] = useState32(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(PANEL_OPEN_KEY) : null;
    return saved === null ? true : saved === "true";
  });
  const [panelHeight, setPanelHeight] = useState32(() => {
    if (typeof window === "undefined") return DEFAULT_PANEL_HEIGHT;
    const saved = localStorage.getItem(PANEL_HEIGHT_KEY);
    const n = saved ? Number.parseInt(saved, 10) : DEFAULT_PANEL_HEIGHT;
    return Number.isFinite(n) && n >= MIN_PANEL_HEIGHT && n <= MAX_PANEL_HEIGHT ? n : DEFAULT_PANEL_HEIGHT;
  });
  const dragRef = useRef14(null);
  useEffect24(() => {
    localStorage.setItem("smart-shikka-chat-active-tab", activeTab);
  }, [activeTab]);
  useEffect24(() => {
    localStorage.setItem(PANEL_OPEN_KEY, String(panelOpen));
  }, [panelOpen]);
  useEffect24(() => {
    localStorage.setItem(PANEL_HEIGHT_KEY, String(panelHeight));
  }, [panelHeight]);
  const handleTabClick = useCallback22((id) => {
    setActiveTab((prev) => {
      if (prev === id) {
        setPanelOpen((open) => !open);
        return id;
      }
      setPanelOpen(true);
      return id;
    });
  }, []);
  const startResize = useCallback22((e) => {
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startHeight: panelHeight };
    const move = (ev) => {
      if (!dragRef.current) return;
      const delta = dragRef.current.startY - ev.clientY;
      const next = Math.max(MIN_PANEL_HEIGHT, Math.min(MAX_PANEL_HEIGHT, dragRef.current.startHeight + delta));
      setPanelHeight(next);
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }, [panelHeight]);
  const chatDisabled = !shell.chatService || shell.isEmergencyStopped;
  let disabledMessage = "";
  if (!shell.chatService) disabledMessage = tr7("chatServiceMissing", shell.locale);
  else if (shell.isEmergencyStopped) disabledMessage = tr7("emergencyStopActive", shell.locale);
  return /* @__PURE__ */ jsxs63("div", { style: { height: "100%", display: "flex", flexDirection: "column" }, children: [
    /* @__PURE__ */ jsxs63("div", { style: { marginBottom: baseUiTokens.spacing.md }, children: [
      /* @__PURE__ */ jsx71("h2", { style: { margin: 0, fontSize: "16px" }, children: "Chat" }),
      shell.chatError && /* @__PURE__ */ jsx71("div", { role: "alert", style: { color: baseUiTokens.colors.danger, fontSize: "13px", marginTop: baseUiTokens.spacing.sm }, children: safeErrorCode(shell.chatError) === shell.chatError ? /* @__PURE__ */ jsxs63(Fragment6, { children: [
        safeErrorText(shell.chatError, shell.locale).title,
        " ",
        /* @__PURE__ */ jsx71("code", { children: shell.chatError })
      ] }) : shell.chatError }),
      chatDisabled && /* @__PURE__ */ jsx71("div", { role: "status", style: { marginTop: baseUiTokens.spacing.sm, padding: baseUiTokens.spacing.sm, borderRadius: baseUiTokens.radius.md, backgroundColor: "rgba(100,116,139,0.15)" }, children: /* @__PURE__ */ jsx71(DisabledHint, { message: disabledMessage, theme: shell.theme }) })
    ] }),
    shell.mode === "advanced" && /* @__PURE__ */ jsxs63(Fragment6, { children: [
      /* @__PURE__ */ jsx71(ChatTabBar, { activeTab, panelOpen, onTabClick: handleTabClick }),
      panelOpen && /* @__PURE__ */ jsxs63(Fragment6, { children: [
        /* @__PURE__ */ jsx71(
          "div",
          {
            "data-testid": "chat-panel",
            style: {
              height: panelHeight,
              minHeight: MIN_PANEL_HEIGHT,
              maxHeight: MAX_PANEL_HEIGHT,
              border: `1px solid ${colors.border}`,
              borderRadius: baseUiTokens.radius.md,
              backgroundColor: colors.surfaceStrong,
              overflow: "auto",
              padding: baseUiTokens.spacing.md
            },
            children: /* @__PURE__ */ jsx71(ChatPanelContent, { activeTab })
          }
        ),
        /* @__PURE__ */ jsx71(
          "div",
          {
            role: "separator",
            "aria-label": "Resize panel",
            onMouseDown: startResize,
            style: {
              height: "8px",
              cursor: "row-resize",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            },
            children: /* @__PURE__ */ jsx71("div", { style: { width: "40px", height: "4px", borderRadius: "2px", backgroundColor: colors.border } })
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxs63("div", { style: { flex: 1, minHeight: 0, border: `1px solid ${colors.border}`, borderRadius: baseUiTokens.radius.md, overflow: "hidden", marginTop: shell.mode === "advanced" && panelOpen ? 0 : baseUiTokens.spacing.md, display: "flex", flexDirection: "column" }, children: [
      /* @__PURE__ */ jsx71("div", { style: { padding: baseUiTokens.spacing.md, borderBottom: `1px solid ${colors.border}` }, children: /* @__PURE__ */ jsx71(
        ModelPicker,
        {
          value: shell.chatProfile,
          onChange: shell.setChatProfile,
          theme: shell.theme,
          disabled: chatDisabled
        }
      ) }),
      /* @__PURE__ */ jsx71("div", { style: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }, children: /* @__PURE__ */ jsx71(
        ChatContainer,
        {
          messages: shell.chatMessages,
          onSendMessage: shell.sendChatMessage,
          onStopStreaming: shell.stopChat,
          onRetry: shell.retryChatMessage,
          onNewChat: shell.newChatConversation,
          onSelectChat: shell.selectChatConversation,
          conversations: shell.chatConversations,
          isStreaming: shell.chatStreaming,
          isTyping: false,
          disabled: chatDisabled,
          placeholder: tr7("typeMessage", shell.locale),
          streamingPreviewContent: shell.chatStreamingPreview,
          locale: shell.locale
        }
      ) })
    ] })
  ] });
}
function ChatTabBar({
  activeTab,
  panelOpen,
  onTabClick
}) {
  const shell = useWorkspaceShell();
  const colors = themeColors2(shell.theme);
  return /* @__PURE__ */ jsx71(
    "div",
    {
      role: "tablist",
      "aria-label": "Chat workspace panels",
      style: {
        display: "flex",
        gap: baseUiTokens.spacing.xs,
        borderBottom: `1px solid ${colors.border}`,
        paddingBottom: baseUiTokens.spacing.sm,
        marginBottom: baseUiTokens.spacing.sm,
        flexWrap: "wrap"
      },
      children: TABS.map((tab) => {
        const active = activeTab === tab.id && panelOpen;
        return /* @__PURE__ */ jsx71(
          "button",
          {
            role: "tab",
            "aria-selected": active,
            type: "button",
            onClick: () => onTabClick(tab.id),
            style: {
              padding: `${baseUiTokens.spacing.sm} ${baseUiTokens.spacing.md}`,
              borderRadius: baseUiTokens.radius.md,
              border: `1px solid ${colors.border}`,
              backgroundColor: active ? baseUiTokens.colors.primary : colors.surface,
              color: active ? "#fff" : colors.text,
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 500
            },
            children: tr7(tab.labelKey, shell.locale)
          },
          tab.id
        );
      })
    }
  );
}
function ChatPanelContent({ activeTab }) {
  switch (activeTab) {
    case "changes":
      return /* @__PURE__ */ jsx71(ChangesPanel, {});
    case "worklog":
      return /* @__PURE__ */ jsx71(WorklogPanel, {});
    case "browser":
      return /* @__PURE__ */ jsx71(BrowserPanel, {});
    case "preview":
      return /* @__PURE__ */ jsx71(PreviewPanel, {});
    case "shell":
      return /* @__PURE__ */ jsx71(ShellPanel, {});
    case "ide":
      return /* @__PURE__ */ jsx71(IdePanel, {});
    case "agents":
      return /* @__PURE__ */ jsx71(AgentsPanel, {});
    default:
      return /* @__PURE__ */ jsx71(ChangesPanel, {});
  }
}
function ChangesPanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors2(shell.theme);
  const [base, setBase] = useState32("HEAD~1");
  const [head, setHead] = useState32("HEAD");
  const disabled = !shell.isOwner || shell.isEmergencyStopped || !shell.codeService;
  const status = shell.gitStatus;
  return /* @__PURE__ */ jsxs63("div", { style: { display: "flex", flexDirection: "column", gap: baseUiTokens.spacing.md, height: "100%" }, children: [
    /* @__PURE__ */ jsxs63("div", { style: { display: "flex", gap: baseUiTokens.spacing.sm, alignItems: "center", flexWrap: "wrap" }, children: [
      /* @__PURE__ */ jsx71(
        "input",
        {
          value: base,
          onChange: (e) => setBase(e.target.value),
          placeholder: tr7("base", shell.locale),
          disabled,
          "aria-label": tr7("base", shell.locale),
          style: {
            padding: baseUiTokens.spacing.sm,
            borderRadius: baseUiTokens.radius.md,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            color: colors.text,
            width: "120px"
          }
        }
      ),
      /* @__PURE__ */ jsx71(
        "input",
        {
          value: head,
          onChange: (e) => setHead(e.target.value),
          placeholder: tr7("head", shell.locale),
          disabled,
          "aria-label": tr7("head", shell.locale),
          style: {
            padding: baseUiTokens.spacing.sm,
            borderRadius: baseUiTokens.radius.md,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            color: colors.text,
            width: "120px"
          }
        }
      ),
      /* @__PURE__ */ jsx71(Button, { onClick: () => void shell.loadDiff(base, head), disabled, children: tr7("loadDiff", shell.locale) }),
      /* @__PURE__ */ jsx71(
        Button,
        {
          onClick: () => shell.diffPreview && void shell.requestPatchApproval({
            title: `Diff ${base}..${head}`,
            description: shell.diffPreview.diff.slice(0, 200),
            diff: shell.diffPreview.diff,
            files: status?.entries.map((e) => e.path) ?? []
          }),
          disabled: disabled || !shell.diffPreview,
          variant: "secondary",
          children: tr7("requestPatchApproval", shell.locale)
        }
      )
    ] }),
    /* @__PURE__ */ jsxs63("div", { style: { display: "grid", gridTemplateColumns: "240px 1fr", gap: baseUiTokens.spacing.md, flex: 1, minHeight: 0 }, children: [
      /* @__PURE__ */ jsx71(Card, { title: tr7("gitStatus", shell.locale), children: status ? /* @__PURE__ */ jsx71(GitStatusList, { status, theme: shell.theme }) : /* @__PURE__ */ jsx71(DisabledHint, { message: tr7("noChanges", shell.locale), theme: shell.theme }) }),
      /* @__PURE__ */ jsx71(Card, { title: tr7("diffPreview", shell.locale), children: shell.diffPreview ? /* @__PURE__ */ jsx71(
        "pre",
        {
          style: {
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: "12px",
            color: colors.text,
            maxHeight: "100%",
            overflow: "auto"
          },
          children: shell.diffPreview.diff
        }
      ) : /* @__PURE__ */ jsx71(DisabledHint, { message: tr7("noChanges", shell.locale), theme: shell.theme }) })
    ] }),
    /* @__PURE__ */ jsx71(WorktreeLeasesCard, {})
  ] });
}
function WorktreeLeasesCard() {
  const shell = useWorkspaceShell();
  const colors = themeColors2(shell.theme);
  const service = shell.codeService;
  const [leases, setLeases] = useState32([]);
  const [error, setError] = useState32();
  const disabled = !shell.isOwner || shell.isEmergencyStopped || !service;
  const refresh = useCallback22(async () => {
    if (!service) return;
    try {
      setLeases(await service.listWorktreeLeases());
      setError(void 0);
    } catch (e) {
      setError(safeErrorCode(e));
    }
  }, [service]);
  useEffect24(() => {
    void refresh();
    if (!service) return;
    const id = setInterval(() => void refresh(), 8e3);
    return () => clearInterval(id);
  }, [refresh, service]);
  if (!service) return null;
  return /* @__PURE__ */ jsx71(Card, { title: tr7("worktrees", shell.locale), children: /* @__PURE__ */ jsxs63("div", { style: { display: "flex", flexDirection: "column", gap: baseUiTokens.spacing.xs }, children: [
    /* @__PURE__ */ jsxs63("div", { style: { display: "flex", alignItems: "center", gap: baseUiTokens.spacing.sm }, children: [
      /* @__PURE__ */ jsx71("span", { style: { fontSize: "12px", color: colors.textMuted }, children: tr7("worktreesNote", shell.locale) }),
      /* @__PURE__ */ jsx71("div", { style: { flex: 1 } }),
      /* @__PURE__ */ jsx71(Button, { size: "sm", variant: "secondary", disabled, onClick: () => void refresh(), children: tr7("worktreesRefresh", shell.locale) })
    ] }),
    error ? /* @__PURE__ */ jsx71("div", { style: { fontSize: "12px", color: "#dc2626" }, children: safeErrorText(error, shell.locale).title }) : null,
    leases.length === 0 && !error ? /* @__PURE__ */ jsx71(DisabledHint, { message: tr7("worktreesNone", shell.locale), theme: shell.theme }) : null,
    leases.map((lease) => /* @__PURE__ */ jsxs63(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "5px 8px",
          borderRadius: baseUiTokens.radius.md,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.surface,
          fontSize: "12px"
        },
        children: [
          /* @__PURE__ */ jsx71(Badge, { variant: lease.publishedCommit ? "success" : "primary", children: lease.action }),
          /* @__PURE__ */ jsx71(
            "span",
            {
              style: { color: colors.text, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "40%" },
              title: lease.worktreePath,
              children: lease.worktreePath.split("/").filter(Boolean).pop() ?? lease.worktreePath
            }
          ),
          /* @__PURE__ */ jsx71("span", { style: { color: colors.textMuted, fontFamily: "monospace" }, children: lease.baseCommit.slice(0, 8) }),
          /* @__PURE__ */ jsxs63("span", { style: { color: colors.textMuted }, children: [
            new Date(lease.updatedAtMs).toLocaleTimeString(),
            lease.publishedCommit ? ` \xB7 ${tr7("worktreesPublished", shell.locale)} ${lease.publishedCommit.slice(0, 8)}` : ""
          ] })
        ]
      },
      lease.leaseId
    ))
  ] }) });
}
function GitStatusList({ status, theme: theme2 }) {
  const colors = themeColors2(theme2);
  return /* @__PURE__ */ jsxs63("div", { style: { display: "flex", flexDirection: "column", gap: baseUiTokens.spacing.xs, fontSize: "13px" }, children: [
    /* @__PURE__ */ jsxs63("div", { style: { color: colors.textMuted }, children: [
      status.branch,
      " \xB7 +",
      status.ahead,
      "/-",
      status.behind
    ] }),
    status.entries.map((entry) => /* @__PURE__ */ jsxs63("div", { style: { display: "flex", alignItems: "center", gap: baseUiTokens.spacing.xs }, children: [
      /* @__PURE__ */ jsx71(Badge, { variant: statusVariant(entry.status), children: entry.status }),
      /* @__PURE__ */ jsx71("span", { style: { color: colors.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: entry.path })
    ] }, entry.path)),
    status.entries.length === 0 && /* @__PURE__ */ jsx71(DisabledHint, { message: "Working tree clean", theme: theme2 })
  ] });
}
function statusVariant(status) {
  switch (status) {
    case "added":
      return "success";
    case "modified":
      return "warning";
    case "deleted":
      return "danger";
    default:
      return "primary";
  }
}
function WorklogPanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors2(shell.theme);
  const events = [
    ...shell.chatMessages.map((m) => ({
      id: m.id,
      at: m.timestamp ? new Date(m.timestamp).toLocaleString() : "-",
      actor: m.role,
      text: m.content.slice(0, 120),
      type: "chat"
    })),
    ...shell.tasks.map((t5) => ({
      id: t5.taskId,
      at: t5.taskId,
      actor: "agent",
      text: `${t5.title} \xB7 ${t5.view}`,
      type: "task"
    }))
  ];
  return /* @__PURE__ */ jsx71("div", { style: { height: "100%", overflow: "auto" }, children: events.length === 0 ? /* @__PURE__ */ jsx71(DisabledHint, { message: tr7("noWorklog", shell.locale), theme: shell.theme }) : /* @__PURE__ */ jsx71("div", { style: { display: "flex", flexDirection: "column", gap: baseUiTokens.spacing.sm }, children: events.map((event) => /* @__PURE__ */ jsxs63(
    "div",
    {
      style: {
        padding: baseUiTokens.spacing.sm,
        borderRadius: baseUiTokens.radius.md,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surface
      },
      children: [
        /* @__PURE__ */ jsxs63("div", { style: { fontSize: "12px", color: colors.textMuted, marginBottom: "4px" }, children: [
          event.at,
          " \xB7 ",
          event.actor
        ] }),
        /* @__PURE__ */ jsx71("div", { style: { fontSize: "13px", color: colors.text }, children: event.text })
      ]
    },
    event.id
  )) }) });
}
function BrowserPanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors2(shell.theme);
  const [url, setUrl] = useState32("https://example.com");
  const [tabs, setTabs] = useState32([]);
  const [activeTabIdx, setActiveTabIdx] = useState32(0);
  const openTab = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    setTabs((prev) => {
      const existing = prev.indexOf(trimmed);
      if (existing >= 0) {
        setActiveTabIdx(existing);
        return prev;
      }
      setActiveTabIdx(prev.length);
      return [...prev, trimmed];
    });
  };
  const closeTab = (idx) => {
    setTabs((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      setActiveTabIdx((cur) => Math.min(cur >= idx ? cur - 1 : cur, Math.max(0, next.length - 1)));
      return next;
    });
  };
  const hostOf = (u) => {
    try {
      return new URL(u).hostname || u;
    } catch {
      return u;
    }
  };
  return /* @__PURE__ */ jsxs63("div", { style: { display: "flex", flexDirection: "column", gap: baseUiTokens.spacing.md, height: "100%" }, children: [
    /* @__PURE__ */ jsxs63("div", { style: { display: "flex", gap: baseUiTokens.spacing.sm, alignItems: "center" }, children: [
      /* @__PURE__ */ jsx71(
        "input",
        {
          value: url,
          onChange: (e) => setUrl(e.target.value),
          onKeyDown: (e) => {
            if (e.key === "Enter") openTab();
          },
          placeholder: tr7("url", shell.locale),
          "aria-label": tr7("url", shell.locale),
          style: {
            flex: 1,
            padding: baseUiTokens.spacing.sm,
            borderRadius: baseUiTokens.radius.md,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            color: colors.text
          }
        }
      ),
      /* @__PURE__ */ jsx71(Button, { onClick: openTab, children: tr7("openPreview", shell.locale) })
    ] }),
    /* @__PURE__ */ jsx71(BrokerSessionsStrip, {}),
    tabs.length > 0 ? /* @__PURE__ */ jsx71("div", { role: "tablist", "aria-label": "Browser tabs", style: { display: "flex", gap: 4, flexWrap: "wrap" }, children: tabs.map((t5, i) => /* @__PURE__ */ jsxs63(
      "div",
      {
        role: "tab",
        "aria-selected": i === activeTabIdx,
        style: {
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 8px",
          borderRadius: baseUiTokens.radius.md,
          border: `1px solid ${colors.border}`,
          backgroundColor: i === activeTabIdx ? colors.surfaceStrong : colors.surface,
          color: colors.text,
          fontSize: "12px",
          cursor: "pointer",
          maxWidth: 220
        },
        onClick: () => setActiveTabIdx(i),
        children: [
          /* @__PURE__ */ jsx71("span", { style: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: hostOf(t5) }),
          /* @__PURE__ */ jsx71(
            "button",
            {
              type: "button",
              "aria-label": shell.locale === "hi" ? "\u091F\u0948\u092C \u092C\u0902\u0926 \u0915\u0930\u0947\u0902" : "Close tab",
              onClick: (e) => {
                e.stopPropagation();
                closeTab(i);
              },
              style: {
                border: "none",
                background: "transparent",
                color: colors.textMuted,
                cursor: "pointer",
                fontSize: "13px",
                lineHeight: 1,
                padding: 0
              },
              children: "\xD7"
            }
          )
        ]
      },
      `${t5}-${i}`
    )) }) : null,
    /* @__PURE__ */ jsx71("p", { style: { color: colors.textMuted, fontSize: "12px", margin: 0 }, children: tr7("previewNote", shell.locale) }),
    tabs.length > 0 ? tabs.map((t5, i) => /* @__PURE__ */ jsx71(
      "iframe",
      {
        src: t5,
        title: `Browser preview ${i + 1}`,
        sandbox: "allow-scripts allow-same-origin",
        referrerPolicy: "no-referrer",
        style: {
          flex: 1,
          border: `1px solid ${colors.border}`,
          borderRadius: baseUiTokens.radius.md,
          backgroundColor: "#fff",
          display: i === activeTabIdx ? "block" : "none"
        }
      },
      `${t5}-${i}`
    )) : /* @__PURE__ */ jsx71(
      "div",
      {
        style: {
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colors.textMuted,
          fontSize: "13px",
          border: `1px dashed ${colors.border}`,
          borderRadius: baseUiTokens.radius.md
        },
        children: tr7("openPreview", shell.locale)
      }
    )
  ] });
}
function BrokerSessionsStrip() {
  const shell = useWorkspaceShell();
  const colors = themeColors2(shell.theme);
  const service = shell.browserSessionService;
  const [leases, setLeases] = useState32([]);
  const [error, setError] = useState32();
  const disabled = !shell.isOwner || shell.isEmergencyStopped;
  const refresh = useCallback22(async () => {
    if (!service) return;
    try {
      setLeases(await service.listLeases());
      setError(void 0);
    } catch (e) {
      setError(safeErrorCode(e));
    }
  }, [service]);
  useEffect24(() => {
    void refresh();
    if (!service) return;
    const id = setInterval(() => void refresh(), 8e3);
    return () => clearInterval(id);
  }, [refresh, service]);
  if (!service) return null;
  const onCancel = async (leaseId) => {
    if (!window.confirm(tr7("brokerCancelConfirm", shell.locale))) return;
    try {
      await service.cancelLease(leaseId);
      await refresh();
    } catch (e) {
      setError(safeErrorCode(e));
    }
  };
  return /* @__PURE__ */ jsxs63("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: [
    /* @__PURE__ */ jsx71("div", { style: { fontSize: "11px", fontWeight: 600, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.05em" }, children: tr7("brokerSessions", shell.locale) }),
    error ? /* @__PURE__ */ jsx71("div", { style: { fontSize: "12px", color: "#dc2626" }, children: safeErrorText(error, shell.locale).title }) : null,
    leases.length === 0 && !error ? /* @__PURE__ */ jsx71("div", { style: { fontSize: "12px", color: colors.textMuted }, children: tr7("brokerNone", shell.locale) }) : null,
    leases.map((lease) => /* @__PURE__ */ jsxs63(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "5px 8px",
          borderRadius: baseUiTokens.radius.md,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.surface,
          fontSize: "12px"
        },
        children: [
          /* @__PURE__ */ jsx71(Badge, { variant: "primary", children: lease.controllerType }),
          /* @__PURE__ */ jsx71("span", { style: { color: colors.text, fontFamily: "monospace" }, children: lease.browserProfileId || lease.leaseId.slice(0, 10) }),
          /* @__PURE__ */ jsx71("span", { style: { color: colors.textMuted }, children: lease.state }),
          /* @__PURE__ */ jsxs63("span", { style: { color: colors.textMuted }, children: [
            lease.risk ? `${lease.risk}` : "",
            lease.permissions.length > 0 ? ` \xB7 ${lease.permissions.join(",")}` : ""
          ] }),
          /* @__PURE__ */ jsx71("div", { style: { flex: 1 } }),
          /* @__PURE__ */ jsx71(
            Button,
            {
              size: "sm",
              variant: "danger",
              disabled,
              onClick: () => void onCancel(lease.leaseId),
              children: tr7("brokerCancel", shell.locale)
            }
          )
        ]
      },
      lease.leaseId
    ))
  ] });
}
function PreviewPanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors2(shell.theme);
  const service = shell.builderService;
  const [urls, setUrls] = useState32();
  const [error, setError] = useState32();
  const [active, setActive] = useState32("local");
  const refresh = useCallback22(async () => {
    if (!service) return;
    try {
      const view = await service.outputUrls();
      setUrls(view);
      setError(void 0);
    } catch (e) {
      setError(safeErrorCode(e));
    }
  }, [service]);
  useEffect24(() => {
    void refresh();
    if (!service) return;
    const id = setInterval(() => void refresh(), 8e3);
    return () => clearInterval(id);
  }, [refresh, service]);
  if (!service) {
    return /* @__PURE__ */ jsx71(DisabledHint, { message: tr7("previewNoProject", shell.locale), theme: shell.theme });
  }
  const localOk = Boolean(urls?.local.ready && urls.local.url);
  const publicOk = urls?.publicPreview.status === "available" && Boolean(urls.publicPreview.url);
  const tabsDef = [
    { id: "local", label: tr7("previewLocal", shell.locale), ok: localOk },
    { id: "public", label: tr7("previewPublic", shell.locale), ok: publicOk }
  ];
  const activeUrl = active === "local" ? urls?.local.url : urls?.publicPreview.url;
  const activeOk = active === "local" ? localOk : publicOk;
  const inactiveReason = active === "local" ? urls?.local.reason ?? (urls ? tr7("previewUnavailable", shell.locale) : void 0) : urls?.publicPreview.reason ?? (urls ? tr7("previewUnavailable", shell.locale) : void 0);
  return /* @__PURE__ */ jsxs63("div", { style: { display: "flex", flexDirection: "column", gap: baseUiTokens.spacing.sm, height: "100%" }, children: [
    /* @__PURE__ */ jsxs63("div", { style: { display: "flex", gap: baseUiTokens.spacing.sm, alignItems: "center", flexWrap: "wrap" }, children: [
      /* @__PURE__ */ jsx71("div", { role: "tablist", style: { display: "flex", gap: 4 }, children: tabsDef.map((t5) => /* @__PURE__ */ jsxs63(
        "button",
        {
          role: "tab",
          "aria-selected": active === t5.id,
          onClick: () => setActive(t5.id),
          style: {
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: baseUiTokens.radius.md,
            border: `1px solid ${active === t5.id ? colors.textMuted : colors.border}`,
            backgroundColor: active === t5.id ? colors.surface : "transparent",
            color: colors.text,
            fontSize: "12px",
            cursor: "pointer"
          },
          children: [
            /* @__PURE__ */ jsx71(
              "span",
              {
                "aria-hidden": true,
                style: {
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: t5.ok ? "#22c55e" : colors.textMuted,
                  display: "inline-block"
                }
              }
            ),
            t5.label
          ]
        },
        t5.id
      )) }),
      /* @__PURE__ */ jsx71("div", { style: { flex: 1 } }),
      /* @__PURE__ */ jsx71(Button, { onClick: () => void refresh(), variant: "secondary", size: "sm", children: tr7("previewRefresh", shell.locale) }),
      activeOk && activeUrl ? /* @__PURE__ */ jsx71(Button, { onClick: () => window.open(activeUrl, "_blank", "noopener"), variant: "secondary", size: "sm", children: tr7("previewOpen", shell.locale) }) : null
    ] }),
    error ? /* @__PURE__ */ jsx71(DisabledHint, { message: safeErrorText(error, shell.locale).title, theme: shell.theme }) : null,
    active === "local" && urls?.local.port ? /* @__PURE__ */ jsxs63("div", { style: { fontSize: "11px", color: colors.textMuted }, children: [
      ":",
      urls.local.port
    ] }) : null,
    active === "public" && urls?.publicPreview.host ? /* @__PURE__ */ jsxs63("div", { style: { fontSize: "11px", color: colors.textMuted }, children: [
      urls.publicPreview.host,
      " \xB7 ",
      urls.publicPreview.deployStatus,
      urls.publicPreview.sourceSha ? ` \xB7 ${urls.publicPreview.sourceSha.slice(0, 8)}` : ""
    ] }) : null,
    /* @__PURE__ */ jsxs63("div", { style: { flex: 1, minHeight: 0, borderRadius: baseUiTokens.radius.md, overflow: "hidden", border: `1px solid ${colors.border}` }, children: [
      localOk && urls?.local.url ? /* @__PURE__ */ jsx71(
        "iframe",
        {
          title: "local-preview",
          src: urls.local.url,
          style: { width: "100%", height: "100%", border: "none", display: active === "local" ? "block" : "none", backgroundColor: "#fff" }
        }
      ) : null,
      publicOk && urls?.publicPreview.url ? /* @__PURE__ */ jsx71(
        "iframe",
        {
          title: "public-preview",
          src: urls.publicPreview.url,
          style: { width: "100%", height: "100%", border: "none", display: active === "public" ? "block" : "none", backgroundColor: "#fff" }
        }
      ) : null,
      !activeOk ? /* @__PURE__ */ jsx71("div", { style: { padding: baseUiTokens.spacing.md, fontSize: "13px", color: colors.textMuted }, children: urls ? inactiveReason ?? tr7("previewUnavailable", shell.locale) : "\u2026" }) : null
    ] })
  ] });
}
function ShellPanel() {
  const shell = useWorkspaceShell();
  const output = shell.testBuildResult;
  const disabled = !shell.isOwner || shell.isEmergencyStopped || !shell.codeService;
  return /* @__PURE__ */ jsxs63("div", { style: { display: "flex", flexDirection: "column", gap: baseUiTokens.spacing.md, height: "100%" }, children: [
    /* @__PURE__ */ jsxs63("div", { style: { display: "flex", gap: baseUiTokens.spacing.sm }, children: [
      /* @__PURE__ */ jsx71(Button, { onClick: () => void shell.runTests(), disabled, children: tr7("runTests", shell.locale) }),
      /* @__PURE__ */ jsx71(Button, { onClick: () => void shell.runBuild(), disabled, variant: "secondary", children: tr7("runBuild", shell.locale) })
    ] }),
    output ? /* @__PURE__ */ jsx71(ShellOutput, { output }) : /* @__PURE__ */ jsx71(DisabledHint, { message: tr7("noShellOutput", shell.locale), theme: shell.theme })
  ] });
}
function ShellOutput({ output }) {
  return /* @__PURE__ */ jsxs63(
    "div",
    {
      style: {
        flex: 1,
        overflow: "auto",
        backgroundColor: "#0f172a",
        color: "#e2e8f0",
        padding: baseUiTokens.spacing.md,
        borderRadius: baseUiTokens.radius.md,
        fontFamily: "monospace",
        fontSize: "12px"
      },
      children: [
        /* @__PURE__ */ jsxs63("div", { style: { color: "#94a3b8" }, children: [
          "$ ",
          output.command
        ] }),
        /* @__PURE__ */ jsxs63("div", { style: { color: output.exitCode === 0 ? "#4ade80" : "#f87171" }, children: [
          "exit ",
          output.exitCode,
          " (",
          output.durationMs,
          "ms)"
        ] }),
        output.stdout && /* @__PURE__ */ jsx71("pre", { style: { margin: "8px 0 0", whiteSpace: "pre-wrap" }, children: output.stdout }),
        output.stderr && /* @__PURE__ */ jsx71("pre", { style: { margin: "8px 0 0", whiteSpace: "pre-wrap", color: "#f87171" }, children: output.stderr })
      ]
    }
  );
}
function IdePanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors2(shell.theme);
  const [selectedRepo, setSelectedRepo] = useState32(shell.selectedRepositoryId ?? "");
  return /* @__PURE__ */ jsxs63("div", { style: { display: "flex", flexDirection: "column", gap: baseUiTokens.spacing.md, height: "100%" }, children: [
    /* @__PURE__ */ jsxs63("div", { style: { display: "flex", gap: baseUiTokens.spacing.sm, alignItems: "center" }, children: [
      /* @__PURE__ */ jsxs63(
        "select",
        {
          value: selectedRepo,
          onChange: (e) => setSelectedRepo(e.target.value),
          "aria-label": tr7("selectRepo", shell.locale),
          style: {
            flex: 1,
            padding: baseUiTokens.spacing.sm,
            borderRadius: baseUiTokens.radius.md,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
            color: colors.text
          },
          children: [
            /* @__PURE__ */ jsx71("option", { value: "", children: tr7("selectRepo", shell.locale) }),
            shell.codeRepositories.map((r) => /* @__PURE__ */ jsx71("option", { value: r.id, children: r.name }, r.id))
          ]
        }
      ),
      /* @__PURE__ */ jsx71(Button, { onClick: () => selectedRepo && void shell.selectRepository(selectedRepo), disabled: !selectedRepo || shell.codeLoading, children: tr7("loadRepo", shell.locale) })
    ] }),
    /* @__PURE__ */ jsxs63("div", { style: { display: "grid", gridTemplateColumns: "220px 1fr", gap: baseUiTokens.spacing.md, flex: 1, minHeight: 0 }, children: [
      /* @__PURE__ */ jsx71(Card, { title: tr7("fileTree", shell.locale), children: shell.codeTree.length > 0 ? /* @__PURE__ */ jsx71(FileTree, { nodes: shell.codeTree, onOpen: shell.openFile, theme: shell.theme }) : /* @__PURE__ */ jsx71(DisabledHint, { message: tr7("noRepoSelected", shell.locale), theme: shell.theme }) }),
      /* @__PURE__ */ jsx71(IdeEditor, { files: shell.openFiles, activePath: shell.activeFilePath, theme: shell.theme })
    ] })
  ] });
}
function FileTree({
  nodes,
  onOpen,
  theme: theme2,
  depth = 0
}) {
  const colors = themeColors2(theme2);
  return /* @__PURE__ */ jsx71("div", { style: { display: "flex", flexDirection: "column", gap: "2px", fontSize: "13px" }, children: nodes.map((node) => /* @__PURE__ */ jsxs63("div", { children: [
    node.type === "directory" ? /* @__PURE__ */ jsx71("div", { style: { paddingLeft: depth * 12, color: colors.textMuted, fontWeight: 500 }, children: node.name }) : /* @__PURE__ */ jsx71(
      "button",
      {
        type: "button",
        onClick: () => void onOpen(node.path),
        style: {
          width: "100%",
          textAlign: "left",
          paddingLeft: depth * 12,
          backgroundColor: "transparent",
          border: "none",
          color: colors.text,
          cursor: "pointer",
          fontSize: "13px"
        },
        children: node.name
      }
    ),
    node.children && node.children.length > 0 && /* @__PURE__ */ jsx71(FileTree, { nodes: node.children, onOpen, theme: theme2, depth: depth + 1 })
  ] }, node.path)) });
}
function IdeEditor({
  files,
  activePath,
  theme: theme2
}) {
  const colors = themeColors2(theme2);
  const active = files.find((f) => f.path === activePath);
  return /* @__PURE__ */ jsx71(Card, { title: active?.path ?? "Editor", children: active ? /* @__PURE__ */ jsx71(
    "textarea",
    {
      readOnly: true,
      value: active.content,
      "aria-label": `File content ${active.path}`,
      style: {
        width: "100%",
        height: "100%",
        minHeight: "160px",
        backgroundColor: colors.surface,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        borderRadius: baseUiTokens.radius.md,
        padding: baseUiTokens.spacing.sm,
        fontFamily: "monospace",
        fontSize: "12px",
        resize: "none"
      }
    }
  ) : /* @__PURE__ */ jsx71(DisabledHint, { message: tr7("noFileOpen", "en"), theme: theme2 }) });
}
function AgentsPanel() {
  const shell = useWorkspaceShell();
  const disabled = !shell.isOwner || shell.isEmergencyStopped;
  return /* @__PURE__ */ jsx71("div", { style: { height: "100%", overflow: "auto" }, children: shell.tasks.length === 0 ? /* @__PURE__ */ jsx71(DisabledHint, { message: tr7("noAgents", shell.locale), theme: shell.theme }) : /* @__PURE__ */ jsx71("div", { style: { display: "flex", flexDirection: "column", gap: baseUiTokens.spacing.sm }, children: shell.tasks.map((task) => /* @__PURE__ */ jsx71(
    Card,
    {
      title: task.title,
      subtitle: task.taskId,
      headerActions: /* @__PURE__ */ jsxs63(Fragment6, { children: [
        /* @__PURE__ */ jsx71(Badge, { variant: task.view === "failed" ? "danger" : task.view === "completed" ? "success" : "primary", children: task.view }),
        /* @__PURE__ */ jsx71(Badge, { variant: task.isPaused ? "warning" : "primary", children: task.isPaused ? "paused" : "active" })
      ] }),
      children: /* @__PURE__ */ jsxs63("div", { style: { display: "flex", gap: baseUiTokens.spacing.sm, marginTop: baseUiTokens.spacing.sm }, children: [
        /* @__PURE__ */ jsx71(Button, { onClick: () => void shell.pauseTask(task.taskId), disabled: disabled || !task.canCancel || task.isPaused, variant: "secondary", size: "sm", children: tr7("pause", shell.locale) }),
        /* @__PURE__ */ jsx71(Button, { onClick: () => void shell.resumeTask(task.taskId), disabled: disabled || !task.canResume, variant: "secondary", size: "sm", children: tr7("resume", shell.locale) }),
        /* @__PURE__ */ jsx71(Button, { onClick: () => void shell.cancelTask(task.taskId), disabled: disabled || !task.canCancel, variant: "danger", size: "sm", children: tr7("cancel", shell.locale) })
      ] })
    },
    task.taskId
  )) }) });
}

// src/workspace/CommandPalette.tsx
import { useEffect as useEffect25, useMemo as useMemo15, useRef as useRef15, useState as useState33 } from "react";
import { jsx as jsx72, jsxs as jsxs64 } from "react/jsx-runtime";
function CommandPalette({ isOpen, onClose, commands, theme: theme2 }) {
  const colors = themeColors(theme2);
  const [query, setQuery] = useState33("");
  const [selected, setSelected] = useState33(0);
  const inputRef = useRef15(null);
  const filtered = useMemo15(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(
      (c) => [c.label, ...c.keywords ?? []].some((s) => s.toLowerCase().includes(q))
    );
  }, [commands, query]);
  useEffect25(() => {
    if (isOpen) {
      setQuery("");
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);
  useEffect25(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((i) => Math.min(i + 1, filtered.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const cmd = filtered[selected];
        if (cmd) {
          cmd.action();
          onClose();
        }
        return;
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, filtered, selected, onClose]);
  if (!isOpen) return null;
  return /* @__PURE__ */ jsx72(
    "div",
    {
      role: "dialog",
      "aria-modal": "true",
      "aria-label": "Command palette",
      onClick: onClose,
      style: {
        position: "fixed",
        inset: 0,
        zIndex: 100,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: "120px"
      },
      children: /* @__PURE__ */ jsxs64(
        "div",
        {
          onClick: (e) => e.stopPropagation(),
          style: {
            width: "560px",
            maxWidth: "90vw",
            maxHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            backgroundColor: colors.surfaceStrong,
            border: `1px solid ${colors.border}`,
            borderRadius: tokens.radius.lg,
            boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
            color: colors.text
          },
          children: [
            /* @__PURE__ */ jsx72(
              "input",
              {
                ref: inputRef,
                type: "text",
                value: query,
                onChange: (e) => {
                  setQuery(e.target.value);
                  setSelected(0);
                },
                placeholder: "Type a command\u2026",
                "aria-label": "Search commands",
                style: {
                  padding: tokens.spacing.lg,
                  fontSize: "16px",
                  border: "none",
                  borderBottom: `1px solid ${colors.border}`,
                  background: "transparent",
                  color: colors.text,
                  outline: "none"
                }
              }
            ),
            /* @__PURE__ */ jsxs64("div", { style: { overflow: "auto", padding: tokens.spacing.sm }, children: [
              filtered.length === 0 && /* @__PURE__ */ jsx72("p", { style: { padding: tokens.spacing.lg, color: colors.textMuted, margin: 0 }, children: "No commands found." }),
              filtered.map((cmd, idx) => /* @__PURE__ */ jsx72(
                "button",
                {
                  type: "button",
                  onMouseEnter: () => setSelected(idx),
                  onClick: () => {
                    cmd.action();
                    onClose();
                  },
                  style: {
                    width: "100%",
                    textAlign: "left",
                    padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
                    border: "none",
                    borderRadius: tokens.radius.md,
                    backgroundColor: idx === selected ? tokens.colors.primary : "transparent",
                    color: idx === selected ? "#fff" : colors.text,
                    cursor: "pointer",
                    fontSize: "14px"
                  },
                  children: cmd.label
                },
                cmd.id
              ))
            ] }),
            /* @__PURE__ */ jsxs64(
              "div",
              {
                style: {
                  padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
                  borderTop: `1px solid ${colors.border}`,
                  color: colors.textMuted,
                  fontSize: "12px",
                  display: "flex",
                  gap: tokens.spacing.lg
                },
                children: [
                  /* @__PURE__ */ jsx72("span", { children: "\u2191\u2193 navigate" }),
                  /* @__PURE__ */ jsx72("span", { children: "\u21B5 run" }),
                  /* @__PURE__ */ jsx72("span", { children: "esc close" })
                ]
              }
            )
          ]
        }
      )
    }
  );
}

// src/workspace/useWorkspaceShortcuts.ts
import { useEffect as useEffect26 } from "react";
var WORKSPACE_SHORTCUTS = [
  { id: "palette", keys: "Ctrl/Cmd+K", en: "Open command palette", hi: "\u0915\u092E\u093E\u0902\u0921 \u092A\u0948\u0932\u0947\u091F \u0916\u094B\u0932\u0947\u0902" },
  { id: "mode", keys: "Ctrl/Cmd+Shift+A", en: "Toggle mode", hi: "\u092E\u094B\u0921 \u092C\u0926\u0932\u0947\u0902" },
  { id: "theme", keys: "Ctrl/Cmd+Shift+D", en: "Toggle theme", hi: "\u0925\u0940\u092E \u092C\u0926\u0932\u0947\u0902" },
  { id: "locale", keys: "Ctrl/Cmd+Shift+L", en: "Toggle language", hi: "\u092D\u093E\u0937\u093E \u092C\u0926\u0932\u0947\u0902" },
  { id: "run", keys: "Ctrl/Cmd+Shift+R", en: "Focus run selector", hi: "\u0930\u0928 \u091A\u092F\u0928\u0915\u0930\u094D\u0924\u093E \u092A\u0930 \u092B\u093C\u094B\u0915\u0938" },
  { id: "nav", keys: "Ctrl/Cmd+1\u20139", en: "Jump to workspace tab", hi: "\u0935\u0930\u094D\u0915\u0938\u094D\u092A\u0947\u0938 \u091F\u0948\u092C \u092A\u0930 \u091C\u093E\u090F\u0901" }
];
var isModifier = (e) => (e.metaKey || e.ctrlKey) && !e.altKey;
function useWorkspaceShortcuts(callbacks) {
  useEffect26(() => {
    const handler = (e) => {
      if (!isModifier(e)) return;
      if (e.key.toLowerCase() === "k") {
        e.preventDefault();
        callbacks.openPalette();
        return;
      }
      if (e.shiftKey && e.key.toLowerCase() === "a") {
        e.preventDefault();
        callbacks.toggleMode();
        return;
      }
      if (e.shiftKey && e.key.toLowerCase() === "d") {
        e.preventDefault();
        callbacks.toggleTheme();
        return;
      }
      if (e.shiftKey && e.key.toLowerCase() === "l") {
        e.preventDefault();
        callbacks.toggleLocale();
        return;
      }
      if (e.shiftKey && e.key.toLowerCase() === "r") {
        e.preventDefault();
        callbacks.focusRunSelector();
        return;
      }
      if (!e.shiftKey && callbacks.navTo && /^[1-9]$/.test(e.key)) {
        e.preventDefault();
        callbacks.navTo(e.key);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [callbacks]);
}

// src/workspace/SplitPane.tsx
import { useCallback as useCallback23, useEffect as useEffect27, useRef as useRef16, useState as useState34 } from "react";
import { jsx as jsx73, jsxs as jsxs65 } from "react/jsx-runtime";
var COLLAPSED_SIZE = 52;
function SplitPane({
  position,
  size,
  minSize = 180,
  maxSize = 480,
  collapsed = false,
  onResize,
  onToggleCollapse,
  theme: theme2,
  children,
  "data-testid": testId
}) {
  const colors = themeColors(theme2);
  const [isDragging, setIsDragging] = useState34(false);
  const paneRef = useRef16(null);
  const startXRef = useRef16(0);
  const startSizeRef = useRef16(size);
  const currentSizeRef = useRef16(size);
  const effectiveSize = Math.max(minSize, Math.min(maxSize, collapsed ? COLLAPSED_SIZE : size));
  const handleMouseDown = useCallback23(
    (e) => {
      e.preventDefault();
      setIsDragging(true);
      startXRef.current = e.clientX;
      startSizeRef.current = currentSizeRef.current;
    },
    []
  );
  useEffect27(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      const delta = position === "left" ? e.clientX - startXRef.current : startXRef.current - e.clientX;
      const next = Math.max(minSize, Math.min(maxSize, startSizeRef.current + delta));
      currentSizeRef.current = next;
      onResize?.(next);
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, minSize, maxSize, position, onResize]);
  const borderSide = position === "left" ? "borderRight" : "borderLeft";
  return /* @__PURE__ */ jsxs65(
    "div",
    {
      ref: paneRef,
      "data-testid": testId,
      style: {
        width: effectiveSize,
        minWidth: effectiveSize,
        maxWidth: maxSize,
        height: "100%",
        display: "flex",
        flexDirection: position === "left" ? "row" : "row-reverse",
        [borderSide]: `1px solid ${colors.border}`,
        backgroundColor: colors.surfaceStrong,
        overflow: "hidden",
        position: "relative"
      },
      children: [
        /* @__PURE__ */ jsx73("div", { style: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }, children }),
        /* @__PURE__ */ jsx73(
          "button",
          {
            type: "button",
            "aria-label": collapsed ? "Expand pane" : "Collapse pane",
            onClick: onToggleCollapse,
            style: {
              position: "absolute",
              top: 8,
              [position === "left" ? "right" : "left"]: 8,
              width: 22,
              height: 22,
              borderRadius: tokens.radius.sm,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.surface,
              color: colors.text,
              cursor: "pointer",
              fontSize: "12px",
              lineHeight: "20px",
              textAlign: "center",
              zIndex: 2
            },
            children: collapsed ? "\u203A" : position === "left" ? "\u2039" : "\u203A"
          }
        ),
        /* @__PURE__ */ jsx73(
          "div",
          {
            role: "separator",
            "aria-orientation": "vertical",
            onMouseDown: handleMouseDown,
            style: {
              width: 6,
              minWidth: 6,
              height: "100%",
              cursor: "col-resize",
              backgroundColor: isDragging ? colors.warmAccent : "transparent",
              border: "none",
              padding: 0,
              margin: 0,
              outline: "none"
            }
          }
        )
      ]
    }
  );
}

// src/workspace/AccountMenu.tsx
import { useRef as useRef17, useState as useState35 } from "react";
import { jsx as jsx74, jsxs as jsxs66 } from "react/jsx-runtime";
var STRINGS = {
  settings: { en: "Settings", hi: "\u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938" },
  language: { en: "Language", hi: "\u092D\u093E\u0937\u093E" },
  help: { en: "Help", hi: "\u092E\u0926\u0926" },
  usage: { en: "Usage / Plan", hi: "\u0909\u092A\u092F\u094B\u0917 / \u092A\u094D\u0932\u093E\u0928" },
  extension: { en: "Browser Extension", hi: "\u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930 \u090F\u0915\u094D\u0938\u091F\u0947\u0902\u0936\u0928" },
  desktop: { en: "Desktop App", hi: "\u0921\u0947\u0938\u094D\u0915\u091F\u0949\u092A \u0910\u092A" },
  about: { en: "About Smart Shikka", hi: "Smart Shikka \u0915\u0947 \u092C\u093E\u0930\u0947 \u092E\u0947\u0902" },
  logout: { en: "Log out", hi: "\u0932\u0949\u0917 \u0906\u0909\u091F" },
  superAdmin: { en: "Super Admin", hi: "\u0938\u0941\u092A\u0930 \u090F\u0921\u092E\u093F\u0928" }
};
function tr8(key, locale) {
  return STRINGS[key]?.[locale] ?? key;
}
function AccountMenu({
  locale,
  theme: theme2,
  displayName,
  email,
  isSuperAdmin,
  onSettings,
  onLanguage,
  onHelp,
  onUsage,
  onExtension,
  onDesktop,
  onAbout,
  onLogout,
  onSuperAdmin
}) {
  const colors = themeColors(theme2);
  const [open, setOpen] = useState35(false);
  const buttonRef = useRef17(null);
  const initials = (displayName ?? email ?? "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const menuItem = (label, onClick, danger) => /* @__PURE__ */ jsx74(
    "button",
    {
      type: "button",
      onClick: () => {
        onClick?.();
        setOpen(false);
      },
      style: {
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
        border: "none",
        background: "transparent",
        color: danger ? tokens.colors.danger : colors.text,
        cursor: "pointer",
        fontSize: "13px"
      },
      onMouseEnter: (e) => e.currentTarget.style.backgroundColor = colors.surface,
      onMouseLeave: (e) => e.currentTarget.style.backgroundColor = "transparent",
      children: label
    },
    label
  );
  return /* @__PURE__ */ jsxs66("div", { style: { position: "relative" }, children: [
    /* @__PURE__ */ jsxs66(
      "button",
      {
        ref: buttonRef,
        type: "button",
        onClick: () => setOpen((v) => !v),
        "aria-haspopup": "menu",
        "aria-expanded": open,
        style: {
          display: "flex",
          alignItems: "center",
          gap: tokens.spacing.sm,
          width: "100%",
          padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
          border: "none",
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.surfaceStrong,
          color: colors.text,
          cursor: "pointer",
          fontSize: "13px",
          textAlign: "left"
        },
        children: [
          /* @__PURE__ */ jsx74(
            "div",
            {
              style: {
                width: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: tokens.colors.primary,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontSize: "11px",
                fontWeight: 600
              },
              children: initials
            }
          ),
          /* @__PURE__ */ jsxs66("div", { style: { flex: 1, minWidth: 0, overflow: "hidden" }, children: [
            /* @__PURE__ */ jsx74("div", { style: { fontWeight: 500, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }, children: displayName ?? email ?? "Account" }),
            email && displayName && /* @__PURE__ */ jsx74("div", { style: { fontSize: "11px", color: colors.textMuted, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }, children: email })
          ] }),
          /* @__PURE__ */ jsx74("span", { style: { color: colors.textMuted, fontSize: "12px" }, children: open ? "\u25B2" : "\u25BC" })
        ]
      }
    ),
    open && /* @__PURE__ */ jsxs66(
      "div",
      {
        role: "menu",
        style: {
          position: "absolute",
          bottom: "100%",
          left: 8,
          right: 8,
          marginBottom: 4,
          borderRadius: tokens.radius.md,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.surfaceStrong,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          zIndex: 10,
          padding: `${tokens.spacing.xs} 0`
        },
        children: [
          menuItem(tr8("settings", locale), onSettings),
          menuItem(tr8("language", locale), onLanguage),
          menuItem(tr8("help", locale), onHelp),
          onUsage && menuItem(tr8("usage", locale), onUsage),
          onExtension && menuItem(tr8("extension", locale), onExtension),
          onDesktop && menuItem(tr8("desktop", locale), onDesktop),
          menuItem(tr8("about", locale), onAbout),
          isSuperAdmin && menuItem(tr8("superAdmin", locale), onSuperAdmin),
          menuItem(tr8("logout", locale), onLogout, true)
        ]
      }
    )
  ] });
}

// src/workspace/SuperAdminPanel.tsx
import { useEffect as useEffect28, useMemo as useMemo16, useState as useState36 } from "react";
import { jsx as jsx75, jsxs as jsxs67 } from "react/jsx-runtime";
var STRINGS2 = {
  title: { en: "Super Admin", hi: "\u0938\u0941\u092A\u0930 \u090F\u0921\u092E\u093F\u0928" },
  overview: { en: "Overview / System Health", hi: "\u0905\u0935\u0932\u094B\u0915\u0928 / \u0938\u093F\u0938\u094D\u091F\u092E \u0939\u0947\u0932\u094D\u0925" },
  health: { en: "System Health (live)", hi: "\u0938\u093F\u0938\u094D\u091F\u092E \u0939\u0947\u0932\u094D\u0925 (\u0932\u093E\u0907\u0935)" },
  serverRowTag: { en: "server", hi: "\u0938\u0930\u094D\u0935\u0930" },
  users: { en: "Users / Accounts / Roles", hi: "\u0909\u092A\u092F\u094B\u0917\u0915\u0930\u094D\u0924\u093E / \u0916\u093E\u0924\u0947 / \u092D\u0942\u092E\u093F\u0915\u093E\u090F\u0901" },
  organizations: { en: "Organizations / Memberships", hi: "\u0938\u0902\u0917\u0920\u0928 / \u0938\u0926\u0938\u094D\u092F\u0924\u093E\u090F\u0901" },
  modules: { en: "Modules / Feature Flags", hi: "\u092E\u0949\u0921\u094D\u092F\u0942\u0932 / \u092B\u093C\u0940\u091A\u0930 \u092B\u094D\u0932\u0948\u0917" },
  providers: { en: "Providers / AI Runtime", hi: "\u092A\u094D\u0930\u0926\u093E\u0924\u093E / AI \u0930\u0928\u091F\u093E\u0907\u092E" },
  comms: { en: "Comms Audit / Webhooks", hi: "\u0938\u0902\u091A\u093E\u0930 \u0911\u0921\u093F\u091F / \u0935\u0947\u092C\u094D\u0939\u0941\u0915" },
  callSheet: { en: "Voice / Call Sheet", hi: "\u0935\u0949\u0907\u0938 / \u0915\u0949\u0932 \u0936\u0940\u091F" },
  systemIntegrations: { en: "System Integrations", hi: "\u0938\u093F\u0938\u094D\u091F\u092E \u0907\u0902\u091F\u0940\u0917\u094D\u0930\u0947\u0936\u0928" },
  browser: { en: "Browser Extension / Browser Control", hi: "\u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930 \u090F\u0915\u094D\u0938\u091F\u0947\u0902\u0936\u0928 / \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923" },
  desktop: { en: "Desktop / Device Control", hi: "\u0921\u0947\u0938\u094D\u0915\u091F\u0949\u092A / \u0921\u093F\u0935\u093E\u0907\u0938 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923" },
  mobile: { en: "Mobile / PWA", hi: "\u092E\u094B\u092C\u093E\u0907\u0932 / PWA" },
  tasks: { en: "Tasks / Agents / Jobs", hi: "\u091F\u093E\u0938\u094D\u0915 / \u090F\u091C\u0947\u0902\u091F / \u091C\u0949\u092C\u094D\u0938" },
  audit: { en: "Audit / Security", hi: "\u0911\u0921\u093F\u091F / \u0938\u0941\u0930\u0915\u094D\u0937\u093E" },
  cms: { en: "CMS / Product content", hi: "CMS / \u0909\u0924\u094D\u092A\u093E\u0926 \u0938\u093E\u092E\u0917\u094D\u0930\u0940" },
  ceo: { en: "Shikka CEO", hi: "Shikka CEO" },
  setup: { en: "Setup / Readiness", hi: "\u0938\u0947\u091F\u0905\u092A / \u0924\u0948\u092F\u093E\u0930\u0940" },
  emergencyStop: { en: "Emergency Stop", hi: "\u0907\u092E\u0930\u091C\u0947\u0902\u0938\u0940 \u0938\u094D\u091F\u0949\u092A" },
  activate: { en: "Activate", hi: "\u0938\u0915\u094D\u0930\u093F\u092F \u0915\u0930\u0947\u0902" },
  resume: { en: "Resume", hi: "\u092B\u093F\u0930 \u0938\u0947 \u0936\u0941\u0930\u0942 \u0915\u0930\u0947\u0902" },
  close: { en: "Close", hi: "\u092C\u0902\u0926 \u0915\u0930\u0947\u0902" },
  loading: { en: "Loading readiness\u2026", hi: "\u0924\u0948\u092F\u093E\u0930\u0940 \u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u0940 \u0939\u0948\u2026" },
  notConfigured: { en: "Not configured", hi: "\u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0928\u0939\u0940\u0902 \u0939\u0948" },
  realBacked: { en: "Real-backed panel loaded", hi: "\u0935\u093E\u0938\u094D\u0924\u0935\u093F\u0915 \u092A\u0948\u0928\u0932 \u0932\u094B\u0921 \u0939\u0941\u0906" }
};
function tr9(key, locale) {
  return STRINGS2[key]?.[locale] ?? key;
}
function SuperAdminPanel({
  onClose,
  initialSection = "overview"
}) {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const [active, setActive] = useState36(initialSection);
  const [confirmingEmergency, setConfirmingEmergency] = useState36(false);
  const [readiness, setReadiness] = useState36([]);
  const [loading, setLoading] = useState36(true);
  const {
    locale,
    theme: theme2,
    isSuperAdmin,
    isEmergencyStopped,
    emergencyStopLoading,
    emergencyStopError,
    toggleEmergencyStop,
    isCeoEligible: isCeoEligible2,
    currentContext,
    role,
    chatService,
    agentSkillsService,
    controlService,
    ceoService,
    adminSurfaces,
    token
  } = shell;
  useEffect28(() => {
    let alive = true;
    setLoading(true);
    async function loadReadiness() {
      const ctxReady = !!currentContext && !!role;
      const [agentHealth, fastProfile, reasoningProfile] = await Promise.all([
        (async () => {
          if (!agentSkillsService) return null;
          try {
            return await agentSkillsService.getHealth();
          } catch {
            return null;
          }
        })(),
        (async () => {
          if (!chatService?.verifyProfile) return null;
          try {
            return await chatService.verifyProfile("qwen-4b-fast");
          } catch {
            return { status: "unavailable", fingerprint: "" };
          }
        })(),
        (async () => {
          if (!chatService?.verifyProfile) return null;
          try {
            return await chatService.verifyProfile("qwen-9b-reasoning");
          } catch {
            return { status: "unavailable", fingerprint: "" };
          }
        })()
      ]);
      if (!alive) return;
      function profileStatus(verification, label) {
        if (!verification) return { id: label, label, status: "NOT_CONFIGURED", note: "Provider verification not configured" };
        if (verification.status === "available") return { id: label, label, status: "READY", note: "Profile verified and reachable" };
        if (verification.status === "configured_not_verified") return { id: label, label, status: "DEGRADED", note: "Profile configured but not reachable" };
        return { id: label, label, status: "NOT_CONFIGURED", note: "No live provider configuration for this profile" };
      }
      const agentHealthy = agentHealth ? agentHealth.status === "healthy" : null;
      const agentExtensions = agentHealth?.extensionCount ?? 0;
      const controlAvailable = ctxReady && !!controlService;
      const ceoAvailable = ctxReady && isCeoEligible2 && !!ceoService;
      const emergencyBlocked = isEmergencyStopped;
      const fastItem = profileStatus(fastProfile, "Fast \u2192 Qwen 4B");
      const reasoningItem = profileStatus(reasoningProfile, "Reasoning \u2192 Qwen 9B");
      const items = [
        {
          id: "web",
          label: "Smart Shikka Web",
          status: ctxReady ? "READY" : "NOT_CONFIGURED",
          note: ctxReady ? `Project ${currentContext.projectId} \xB7 ${role}` : "No authenticated project context"
        },
        {
          id: "fast",
          label: fastItem.label,
          status: emergencyBlocked ? "BLOCKED" : fastItem.status,
          note: fastItem.note
        },
        {
          id: "reasoning",
          label: reasoningItem.label,
          status: emergencyBlocked ? "BLOCKED" : reasoningItem.status,
          note: reasoningItem.note
        },
        {
          id: "extension",
          label: "Browser Extension surface",
          status: adminSurfaces?.browser ? "NOT_CONFIGURED" : "UNAVAILABLE",
          note: adminSurfaces?.browser ? "Install and pair from Browser section" : "No extension surface registered"
        },
        {
          id: "desktop",
          label: "Desktop pairing",
          status: "NOT_CONFIGURED",
          note: "Download and pair the Windows desktop app"
        },
        {
          id: "browserControlGate",
          label: "Browser control gate",
          status: controlAvailable ? emergencyBlocked ? "BLOCKED" : "DEGRADED" : "NOT_CONFIGURED",
          note: controlAvailable ? "Workspace control service object present; server gate health not verified" : "Requires authenticated workspace session"
        },
        {
          id: "browserControlPairing",
          label: "Browser pairing",
          status: "NOT_CONFIGURED",
          note: "Extension pairing state is not available in workspace context"
        },
        {
          id: "browserControlSidecar",
          label: "Browser sidecar / runtime",
          status: controlAvailable ? emergencyBlocked ? "BLOCKED" : "DEGRADED" : "NOT_CONFIGURED",
          note: controlAvailable ? "Browser extension bridge is the canonical runtime; host availability determines real actions" : "Requires authenticated workspace session"
        },
        {
          id: "browserScreenshot",
          label: "Browser screenshot runtime",
          status: "NOT_CONFIGURED",
          note: "Requires a configured browser sidecar"
        },
        {
          id: "browserArtifactStore",
          label: "Browser artifact store",
          status: controlAvailable ? emergencyBlocked ? "BLOCKED" : "DEGRADED" : "NOT_CONFIGURED",
          note: controlAvailable ? "Workspace control service object present; artifact store health not verified" : "Requires authenticated workspace session"
        },
        {
          id: "browserRecording",
          label: "Browser recording runtime",
          status: controlAvailable ? emergencyBlocked ? "BLOCKED" : "DEGRADED" : "NOT_CONFIGURED",
          note: controlAvailable ? "Extension offscreen recording implemented; requires paired extension and host" : "Requires authenticated workspace session"
        },
        {
          id: "browserDownloads",
          label: "Browser download runtime",
          status: controlAvailable ? emergencyBlocked ? "BLOCKED" : "DEGRADED" : "NOT_CONFIGURED",
          note: controlAvailable ? "Extension download event capture implemented; requires paired extension and host" : "Requires authenticated workspace session"
        },
        {
          id: "desktopControl",
          label: "Desktop native control",
          status: controlAvailable ? emergencyBlocked ? "BLOCKED" : "DEGRADED" : "UNAVAILABLE",
          note: controlAvailable ? "Desktop control policy gate wired; native execution requires an owner-managed sidecar/bridge" : "Requires authenticated workspace session"
        },
        {
          id: "mobile",
          label: "Mobile / PWA",
          status: adminSurfaces?.mobile ? "NOT_CONFIGURED" : "UNAVAILABLE",
          note: adminSurfaces?.mobile ? "Pair a mobile device from Mobile section" : "No mobile surface registered"
        },
        {
          id: "ceo",
          label: "Smart Shikka CEO",
          status: isCeoEligible2 ? ceoAvailable ? "DEGRADED" : "NOT_CONFIGURED" : "BLOCKED",
          note: isCeoEligible2 ? ceoAvailable ? "Run creation available; executor dispatch not complete (CEO_EXECUTION=RUN_CREATION_ONLY)" : "CEO orchestrator not configured" : "Only super-admin and super-sub-admin may use CEO"
        },
        {
          id: "emergency",
          label: "Emergency Stop",
          status: "READY",
          note: isEmergencyStopped ? "Active" : "Standby"
        },
        {
          id: "providers",
          label: "Agent Skills / Providers",
          status: agentHealthy === null ? "NOT_CONFIGURED" : agentHealthy ? "READY" : "DEGRADED",
          note: agentHealth ? `${agentHealth.status} \xB7 ${agentExtensions} extensions` : "Agent skills service not connected"
        },
        {
          id: "signing",
          label: "Signing / Windows release",
          status: "BLOCKED",
          note: "Authenticode certificate required"
        }
      ];
      if (token) {
        try {
          const healthRes = await fetch("/api/admin/system-health", {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (healthRes.ok) {
            const health = await healthRes.json();
            const statusMap = {
              ok: "READY",
              degraded: "DEGRADED",
              down: "BLOCKED",
              not_configured: "NOT_CONFIGURED"
            };
            for (const svc of health.services ?? []) {
              items.push({
                id: `server-${svc.id}`,
                label: `${locale === "hi" ? svc.labelHi : svc.label} (${tr9("serverRowTag", locale)})`,
                status: statusMap[svc.status] ?? "UNAVAILABLE",
                note: [svc.detail ?? null, typeof svc.latencyMs === "number" ? `${svc.latencyMs}ms` : null].filter(Boolean).join(" \xB7 ") || void 0
              });
            }
          }
        } catch {
        }
      }
      setReadiness(items);
      setLoading(false);
    }
    void loadReadiness();
    return () => {
      alive = false;
    };
  }, [
    currentContext,
    role,
    chatService,
    agentSkillsService,
    controlService,
    ceoService,
    isCeoEligible2,
    isEmergencyStopped,
    adminSurfaces,
    token,
    locale
  ]);
  const sections = useMemo16(
    () => [
      { id: "overview", label: tr9("overview", locale) },
      { id: "health", label: tr9("health", locale) },
      { id: "users", label: tr9("users", locale) },
      { id: "organizations", label: tr9("organizations", locale) },
      { id: "modules", label: tr9("modules", locale) },
      { id: "providers", label: tr9("providers", locale) },
      { id: "comms", label: tr9("comms", locale) },
      { id: "callSheet", label: tr9("callSheet", locale) },
      { id: "systemIntegrations", label: tr9("systemIntegrations", locale) },
      { id: "browser", label: tr9("browser", locale) },
      { id: "desktop", label: tr9("desktop", locale) },
      { id: "mobile", label: tr9("mobile", locale) },
      { id: "tasks", label: tr9("tasks", locale) },
      { id: "audit", label: tr9("audit", locale) },
      { id: "cms", label: tr9("cms", locale) },
      { id: "ceo", label: tr9("ceo", locale) },
      { id: "setup", label: tr9("setup", locale) }
    ],
    [locale]
  );
  const statusColor2 = {
    READY: tokens.colors.success,
    BLOCKED: tokens.colors.danger,
    NOT_CONFIGURED: colors.textMuted,
    DEGRADED: "#c2410c",
    UNAVAILABLE: "#6b7280"
  };
  if (!isSuperAdmin) {
    return /* @__PURE__ */ jsx75("div", { style: { padding: tokens.spacing.lg, color: colors.text }, children: /* @__PURE__ */ jsx75("p", { children: "Access denied." }) });
  }
  const ActiveSurface = active !== "overview" && active !== "setup" ? adminSurfaces?.[active] : void 0;
  function ReadinessList() {
    if (loading) {
      return /* @__PURE__ */ jsx75("p", { style: { fontSize: "13px", color: colors.textMuted }, children: tr9("loading", locale) });
    }
    return /* @__PURE__ */ jsx75("div", { style: { display: "flex", flexDirection: "column", gap: tokens.spacing.sm }, children: readiness.map((item) => /* @__PURE__ */ jsxs67(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
          border: `1px solid ${colors.border}`,
          borderRadius: tokens.radius.md
        },
        children: [
          /* @__PURE__ */ jsxs67("div", { children: [
            /* @__PURE__ */ jsx75("div", { style: { fontSize: "13px", fontWeight: 500 }, children: item.label }),
            item.note && /* @__PURE__ */ jsx75("div", { style: { fontSize: "11px", color: colors.textMuted }, children: item.note })
          ] }),
          /* @__PURE__ */ jsx75("span", { style: { fontSize: "11px", fontWeight: 600, color: statusColor2[item.status] }, children: item.status })
        ]
      },
      item.id
    )) });
  }
  function SectionPlaceholder({ sectionId }) {
    return /* @__PURE__ */ jsx75(Panel, { theme: theme2, title: sections.find((s) => s.id === sectionId)?.label ?? sectionId, children: /* @__PURE__ */ jsxs67(
      "div",
      {
        style: {
          padding: tokens.spacing.md,
          border: `1px dashed ${colors.border}`,
          borderRadius: tokens.radius.md,
          color: colors.textMuted,
          fontSize: "13px"
        },
        children: [
          tr9("notConfigured", locale),
          " \u2014 ",
          sectionId
        ]
      }
    ) });
  }
  return /* @__PURE__ */ jsxs67(
    "div",
    {
      style: {
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.35)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: tokens.spacing.lg
      },
      onClick: onClose,
      children: [
        /* @__PURE__ */ jsxs67(
          "div",
          {
            role: "dialog",
            "aria-modal": "true",
            style: {
              width: "min(900px, 90vw)",
              height: "min(720px, 85vh)",
              backgroundColor: colors.surfaceStrong,
              borderRadius: tokens.radius.lg,
              border: `1px solid ${colors.border}`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            },
            onClick: (e) => e.stopPropagation(),
            children: [
              /* @__PURE__ */ jsxs67(
                "div",
                {
                  style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
                    borderBottom: `1px solid ${colors.border}`
                  },
                  children: [
                    /* @__PURE__ */ jsx75("h2", { style: { margin: 0, fontSize: "18px" }, children: tr9("title", locale) }),
                    /* @__PURE__ */ jsx75("button", { type: "button", onClick: onClose, style: { border: "none", background: "transparent", cursor: "pointer", fontSize: "18px", color: colors.text }, children: "\xD7" })
                  ]
                }
              ),
              /* @__PURE__ */ jsxs67("div", { style: { display: "flex", flex: 1, overflow: "hidden" }, children: [
                /* @__PURE__ */ jsxs67(
                  "div",
                  {
                    style: {
                      width: 240,
                      borderRight: `1px solid ${colors.border}`,
                      overflow: "auto",
                      padding: `${tokens.spacing.sm} 0`
                    },
                    children: [
                      sections.map((s) => /* @__PURE__ */ jsx75(
                        "button",
                        {
                          type: "button",
                          onClick: () => setActive(s.id),
                          style: {
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
                            border: "none",
                            background: active === s.id ? colors.surface : "transparent",
                            color: active === s.id ? colors.warmAccent : colors.text,
                            cursor: "pointer",
                            fontSize: "13px"
                          },
                          children: s.label
                        },
                        s.id
                      )),
                      /* @__PURE__ */ jsxs67("div", { style: { padding: tokens.spacing.md, borderTop: `1px solid ${colors.border}`, marginTop: "auto" }, children: [
                        /* @__PURE__ */ jsx75("p", { style: { fontSize: "12px", color: colors.textMuted, margin: 0 }, children: isEmergencyStopped ? "Emergency Stop active" : "Emergency Stop standby" }),
                        /* @__PURE__ */ jsx75(
                          "button",
                          {
                            type: "button",
                            onClick: () => setConfirmingEmergency(true),
                            style: {
                              marginTop: tokens.spacing.sm,
                              width: "100%",
                              padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
                              borderRadius: tokens.radius.md,
                              border: "none",
                              backgroundColor: isEmergencyStopped ? colors.textMuted : tokens.colors.danger,
                              color: "#fff",
                              cursor: "pointer",
                              fontSize: "13px"
                            },
                            children: isEmergencyStopped ? tr9("resume", locale) : tr9("activate", locale)
                          }
                        )
                      ] })
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs67("div", { style: { flex: 1, padding: tokens.spacing.lg, overflow: "auto" }, children: [
                  active === "overview" && /* @__PURE__ */ jsx75(Panel, { theme: theme2, title: tr9("overview", locale), children: /* @__PURE__ */ jsx75(ReadinessList, {}) }),
                  active === "setup" && /* @__PURE__ */ jsx75(ReadinessList, {}),
                  active !== "overview" && active !== "setup" && (ActiveSurface ? /* @__PURE__ */ jsx75("div", { children: /* @__PURE__ */ jsx75(ActiveSurface, { token, locale, theme: theme2 }) }) : /* @__PURE__ */ jsx75(SectionPlaceholder, { sectionId: active }))
                ] })
              ] })
            ]
          }
        ),
        confirmingEmergency && /* @__PURE__ */ jsx75(
          "div",
          {
            role: "dialog",
            "aria-modal": "true",
            style: {
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.45)",
              zIndex: 101,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            },
            children: /* @__PURE__ */ jsxs67(
              "div",
              {
                style: {
                  width: 420,
                  padding: tokens.spacing.lg,
                  backgroundColor: colors.surfaceStrong,
                  borderRadius: tokens.radius.lg,
                  border: `1px solid ${colors.border}`
                },
                children: [
                  /* @__PURE__ */ jsx75("h3", { style: { marginTop: 0 }, children: tr9("emergencyStop", locale) }),
                  /* @__PURE__ */ jsx75("p", { style: { fontSize: "13px", color: colors.textMuted }, children: isEmergencyStopped ? "Resume will re-enable controlled actions. Confirm to continue." : "Activating Emergency Stop will immediately cancel controlled actions, browser/desktop/mobile execution, coding writes, shell/terminal runs, and new approvals." }),
                  emergencyStopError && /* @__PURE__ */ jsxs67("p", { role: "alert", style: { fontSize: "13px", color: tokens.colors.danger, margin: 0 }, children: [
                    safeErrorText(emergencyStopError, locale).title,
                    " ",
                    /* @__PURE__ */ jsx75("code", { children: emergencyStopError })
                  ] }),
                  /* @__PURE__ */ jsxs67("div", { style: { display: "flex", justifyContent: "flex-end", gap: tokens.spacing.sm, marginTop: tokens.spacing.md }, children: [
                    /* @__PURE__ */ jsx75(
                      "button",
                      {
                        type: "button",
                        onClick: () => setConfirmingEmergency(false),
                        style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: "transparent", cursor: "pointer" },
                        children: "Cancel"
                      }
                    ),
                    /* @__PURE__ */ jsx75(
                      "button",
                      {
                        type: "button",
                        disabled: emergencyStopLoading,
                        onClick: () => {
                          void (async () => {
                            try {
                              await toggleEmergencyStop(!isEmergencyStopped);
                              setConfirmingEmergency(false);
                            } catch {
                            }
                          })();
                        },
                        style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderRadius: tokens.radius.md, border: "none", backgroundColor: tokens.colors.danger, color: "#fff", cursor: emergencyStopLoading ? "not-allowed" : "pointer", opacity: emergencyStopLoading ? 0.7 : 1 },
                        children: emergencyStopLoading ? "Updating\u2026" : isEmergencyStopped ? tr9("resume", locale) : tr9("activate", locale)
                      }
                    )
                  ] })
                ]
              }
            )
          }
        )
      ]
    }
  );
}

// src/builder/BuilderPanel.tsx
import { useCallback as useCallback25, useEffect as useEffect30, useMemo as useMemo17, useState as useState38 } from "react";

// src/builder/WorkspaceConnectorsPanel.tsx
import { useCallback as useCallback24, useEffect as useEffect29, useState as useState37 } from "react";
import { Fragment as Fragment7, jsx as jsx76, jsxs as jsxs68 } from "react/jsx-runtime";
var HI = {
  connectors: "\u0915\u0928\u0947\u0915\u094D\u091F\u0930",
  addConnector: "\u0915\u0928\u0947\u0915\u094D\u091F\u0930 \u091C\u094B\u0921\u093C\u0947\u0902",
  type: "\u092A\u094D\u0930\u0915\u093E\u0930",
  name: "\u0928\u093E\u092E",
  endpoint: "\u090F\u0902\u0921\u092A\u0949\u0907\u0902\u091F",
  secret: "\u0938\u0940\u0915\u094D\u0930\u0947\u091F (server \u092A\u0930 encrypted)",
  repos: "repos (owner/repo, comma \u0938\u0947)",
  targetDir: "\u091F\u093E\u0930\u0917\u0947\u091F \u0921\u093E\u092F\u0930\u0947\u0915\u094D\u091F\u0930\u0940",
  sourceDir: "\u0938\u094B\u0930\u094D\u0938 \u0921\u093E\u092F\u0930\u0947\u0915\u094D\u091F\u0930\u0940",
  targetKind: "target \u092A\u094D\u0930\u0915\u093E\u0930",
  status: "\u0938\u094D\u0925\u093F\u0924\u093F",
  check: "\u091C\u093E\u0901\u091A \u0915\u0930\u0947\u0902",
  deploy: "Deploy \u0915\u0930\u0947\u0902",
  del: "\u0939\u091F\u093E\u090F\u0901",
  none: "\u0905\u092D\u0940 \u0915\u094B\u0908 \u0915\u0928\u0947\u0915\u094D\u091F\u0930 \u0928\u0939\u0940\u0902 \u2014 GitHub, Supabase \u092F\u093E deploy target \u091C\u094B\u0921\u093C\u0947\u0902\u0964",
  env: "\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F env",
  envName: "ENV \u0928\u093E\u092E",
  envValue: "\u092E\u093E\u0928",
  addEnv: "env \u091C\u094B\u0921\u093C\u0947\u0902",
  noEnv: "\u0915\u094B\u0908 env var \u0928\u0939\u0940\u0902\u0964",
  rules: "\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u0928\u093F\u092F\u092E",
  rulePlaceholder: "\u0928\u093F\u092F\u092E \u0932\u093F\u0916\u0947\u0902 \u2014 \u092F\u0939 Shikka \u0915\u0947 context \u092E\u0947\u0902 \u0939\u092E\u0947\u0936\u093E \u0930\u0939\u0947\u0917\u093E",
  addRule: "\u0928\u093F\u092F\u092E \u091C\u094B\u0921\u093C\u0947\u0902",
  noRules: "\u0915\u094B\u0908 \u0928\u093F\u092F\u092E \u0928\u0939\u0940\u0902\u0964",
  deploys: "deploy \u0907\u0924\u093F\u0939\u093E\u0938",
  noDeploys: "\u0915\u094B\u0908 deploy \u0928\u0939\u0940\u0902 \u091A\u0932\u093E\u0964",
  checkResult: (o) => `\u091C\u093E\u0901\u091A: ${o}`,
  deployDenied: "Deploy \u0915\u0947\u0935\u0932 privileged role \u0938\u0947 \u0939\u094B\u0924\u093E \u0939\u0948\u0964"
};
var EN = {
  connectors: "Connectors",
  addConnector: "Add connector",
  type: "Type",
  name: "Name",
  endpoint: "Endpoint",
  secret: "Secret (encrypted on server)",
  repos: "repos (owner/repo, comma-separated)",
  targetDir: "Target directory",
  sourceDir: "Source directory",
  targetKind: "Target kind",
  status: "Status",
  check: "Check",
  deploy: "Deploy",
  del: "Delete",
  none: "No connectors yet \u2014 add GitHub, Supabase or a deploy target.",
  env: "Project env",
  envName: "ENV name",
  envValue: "Value",
  addEnv: "Add env var",
  noEnv: "No env vars.",
  rules: "Project rules",
  rulePlaceholder: "Write a rule \u2014 it stays in Shikka context",
  addRule: "Add rule",
  noRules: "No rules.",
  deploys: "Deploy history",
  noDeploys: "No deploys ran.",
  checkResult: (o) => `Check: ${o}`,
  deployDenied: "Deploy requires a privileged role."
};
var STATUS_COLOR = {
  connected: "#2e9e5b",
  configured: "#b8860b",
  error: "#d64545",
  disabled: "#888"
};
var TYPE_LABEL = {
  github: "GitHub",
  supabase: "Supabase",
  deploy_target: "Deploy target"
};
function WorkspaceConnectorsPanel() {
  const shell = useWorkspaceShell();
  const t5 = shell.locale === "hi" ? HI : EN;
  const service = shell.builderService;
  const projectId = shell.currentContext?.projectId;
  const [connectors, setConnectors] = useState37([]);
  const [envVars, setEnvVars] = useState37([]);
  const [rules, setRules] = useState37([]);
  const [deploys, setDeploys] = useState37([]);
  const [error, setError] = useState37(null);
  const [busy, setBusy] = useState37(null);
  const [showForm, setShowForm] = useState37(false);
  const [form, setForm] = useState37({ type: "github", name: "", endpoint: "", secret: "", repos: "", targetKind: "static_dir", targetDir: "", sourceDir: "" });
  const [envForm, setEnvForm] = useState37({ name: "", value: "" });
  const [ruleText, setRuleText] = useState37("");
  const [checkNote, setCheckNote] = useState37({});
  const load = useCallback24(async () => {
    if (!service || !projectId) return;
    const controller = new AbortController();
    try {
      const [c, e, r, d] = await Promise.allSettled([
        service.connectors(controller.signal),
        service.envVars(controller.signal),
        service.rules(controller.signal),
        service.deploys(controller.signal)
      ]);
      if (c.status === "fulfilled") setConnectors(c.value);
      if (e.status === "fulfilled") setEnvVars(e.value);
      if (r.status === "fulfilled") setRules(r.value);
      if (d.status === "fulfilled") setDeploys(d.value);
      const first = [c, e, r, d].find((x) => x.status === "rejected");
      if (first) setError(String(first.reason.errorCode ?? "workspace_load_failed"));
    } catch {
    }
  }, [service, projectId]);
  useEffect29(() => {
    void load();
  }, [load]);
  if (!service || !projectId) return /* @__PURE__ */ jsx76(Fragment7, {});
  const run = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      const code = err.errorCode ?? "request_failed";
      setError(code);
    } finally {
      setBusy(null);
    }
  };
  const border = "1px solid rgba(128,128,128,0.3)";
  const inputStyle = { fontSize: 12, padding: "3px 6px", border, borderRadius: 4, background: "transparent", color: "inherit" };
  return /* @__PURE__ */ jsxs68("div", { style: { display: "flex", flexDirection: "column", gap: 10 }, "data-testid": "workspace-connectors-panel", children: [
    error && /* @__PURE__ */ jsx76("div", { role: "alert", style: { color: "#d64545", fontSize: 12 }, children: /* @__PURE__ */ jsx76("code", { children: error }) }),
    /* @__PURE__ */ jsxs68("section", { style: { border, borderRadius: 8, padding: 10 }, children: [
      /* @__PURE__ */ jsxs68("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [
        /* @__PURE__ */ jsx76("strong", { style: { fontSize: 12 }, children: t5.connectors }),
        /* @__PURE__ */ jsx76("button", { style: { fontSize: 12 }, onClick: () => setShowForm((v) => !v), children: t5.addConnector })
      ] }),
      showForm && /* @__PURE__ */ jsxs68("div", { style: { display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }, children: [
        /* @__PURE__ */ jsxs68("select", { value: form.type, onChange: (e) => setForm((f) => ({ ...f, type: e.target.value })), style: inputStyle, "aria-label": t5.type, children: [
          /* @__PURE__ */ jsx76("option", { value: "github", children: "GitHub" }),
          /* @__PURE__ */ jsx76("option", { value: "supabase", children: "Supabase" }),
          /* @__PURE__ */ jsx76("option", { value: "deploy_target", children: "Deploy target" })
        ] }),
        /* @__PURE__ */ jsx76("input", { style: inputStyle, placeholder: t5.name, value: form.name, onChange: (e) => setForm((f) => ({ ...f, name: e.target.value })) }),
        (form.type === "github" || form.type === "supabase") && /* @__PURE__ */ jsx76("input", { style: inputStyle, placeholder: form.type === "github" ? "https://api.github.com" : "https://xyz.supabase.co", value: form.endpoint, onChange: (e) => setForm((f) => ({ ...f, endpoint: e.target.value })) }),
        form.type === "github" && /* @__PURE__ */ jsx76("input", { style: inputStyle, placeholder: t5.repos, value: form.repos, onChange: (e) => setForm((f) => ({ ...f, repos: e.target.value })) }),
        (form.type === "github" || form.type === "supabase") && /* @__PURE__ */ jsx76("input", { style: inputStyle, type: "password", placeholder: t5.secret, value: form.secret, onChange: (e) => setForm((f) => ({ ...f, secret: e.target.value })), autoComplete: "off" }),
        form.type === "deploy_target" && /* @__PURE__ */ jsxs68(Fragment7, { children: [
          /* @__PURE__ */ jsxs68("select", { value: form.targetKind, onChange: (e) => setForm((f) => ({ ...f, targetKind: e.target.value })), style: inputStyle, "aria-label": t5.targetKind, children: [
            /* @__PURE__ */ jsx76("option", { value: "static_dir", children: "static_dir" }),
            /* @__PURE__ */ jsx76("option", { value: "ssh", children: "ssh (disabled)" }),
            /* @__PURE__ */ jsx76("option", { value: "supabase_edge", children: "supabase_edge (disabled)" }),
            /* @__PURE__ */ jsx76("option", { value: "github_pages", children: "github_pages (disabled)" })
          ] }),
          /* @__PURE__ */ jsx76("input", { style: inputStyle, placeholder: `${t5.targetDir} (/abs/path)`, value: form.targetDir, onChange: (e) => setForm((f) => ({ ...f, targetDir: e.target.value })) }),
          /* @__PURE__ */ jsx76("input", { style: inputStyle, placeholder: `${t5.sourceDir} (.)`, value: form.sourceDir, onChange: (e) => setForm((f) => ({ ...f, sourceDir: e.target.value })) })
        ] }),
        /* @__PURE__ */ jsx76(
          "button",
          {
            style: { fontSize: 12, alignSelf: "flex-start" },
            disabled: busy === "create" || !form.name.trim(),
            onClick: () => void run("create", async () => {
              await service.upsertConnector({
                type: form.type,
                name: form.name.trim(),
                endpoint: form.endpoint.trim() || void 0,
                secret: form.secret || void 0,
                repos: form.type === "github" ? form.repos.split(",").map((r) => r.trim()).filter(Boolean) : void 0,
                targetKind: form.type === "deploy_target" ? form.targetKind : void 0,
                targetDir: form.type === "deploy_target" && form.targetDir.trim() ? form.targetDir.trim() : void 0,
                sourceDir: form.type === "deploy_target" && form.sourceDir.trim() ? form.sourceDir.trim() : void 0
              });
              setShowForm(false);
              setForm({ type: "github", name: "", endpoint: "", secret: "", repos: "", targetKind: "static_dir", targetDir: "", sourceDir: "" });
            }),
            children: t5.addConnector
          }
        )
      ] }),
      connectors.length === 0 ? /* @__PURE__ */ jsx76("div", { style: { fontSize: 12, opacity: 0.7, marginTop: 6 }, children: t5.none }) : connectors.map((c) => /* @__PURE__ */ jsxs68("div", { style: { marginTop: 8, padding: "6px 8px", border, borderRadius: 6, fontSize: 12 }, children: [
        /* @__PURE__ */ jsxs68("div", { style: { display: "flex", justifyContent: "space-between", gap: 6, flexWrap: "wrap" }, children: [
          /* @__PURE__ */ jsxs68("span", { children: [
            /* @__PURE__ */ jsx76("strong", { children: c.name }),
            " ",
            /* @__PURE__ */ jsx76("span", { style: { opacity: 0.7 }, children: TYPE_LABEL[c.type] ?? c.type })
          ] }),
          /* @__PURE__ */ jsx76("span", { style: { color: STATUS_COLOR[c.status] ?? "#888" }, children: c.status })
        ] }),
        /* @__PURE__ */ jsxs68("div", { style: { opacity: 0.75, marginTop: 3, fontFamily: "monospace", fontSize: 11, wordBreak: "break-all" }, children: [
          c.type === "deploy_target" ? `${c.targetKind ?? ""} \u2192 ${c.targetDir ?? ""}` : c.endpoint ?? "",
          c.secretStored ? " \xB7 secret \u2713" : ""
        ] }),
        c.lastError && /* @__PURE__ */ jsx76("div", { style: { fontSize: 11, color: "#d64545", marginTop: 2 }, children: /* @__PURE__ */ jsx76("code", { children: c.lastError }) }),
        checkNote[c.connectorId] && /* @__PURE__ */ jsx76("div", { style: { fontSize: 11, opacity: 0.8, marginTop: 2 }, children: checkNote[c.connectorId] }),
        /* @__PURE__ */ jsxs68("div", { style: { display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }, children: [
          /* @__PURE__ */ jsx76(
            "button",
            {
              style: { fontSize: 11 },
              disabled: busy === `check-${c.connectorId}`,
              onClick: () => void run(`check-${c.connectorId}`, async () => {
                const check = await service.checkConnector(c.connectorId);
                setCheckNote((m) => ({ ...m, [c.connectorId]: t5.checkResult(check.outcome) }));
              }),
              children: t5.check
            }
          ),
          c.type === "deploy_target" && c.targetKind === "static_dir" && c.status !== "disabled" && /* @__PURE__ */ jsx76(
            "button",
            {
              style: { fontSize: 11 },
              disabled: busy === `deploy-${c.connectorId}`,
              onClick: () => void run(`deploy-${c.connectorId}`, async () => {
                try {
                  await service.executeDeploy(c.connectorId);
                } catch (err) {
                  if (err.errorCode === "deploy_denied") throw new Error(t5.deployDenied);
                  throw err;
                }
              }),
              children: t5.deploy
            }
          ),
          /* @__PURE__ */ jsx76("button", { style: { fontSize: 11 }, disabled: busy === `del-${c.connectorId}`, onClick: () => void run(`del-${c.connectorId}`, async () => service.deleteConnector(c.connectorId)), children: t5.del })
        ] })
      ] }, c.connectorId))
    ] }),
    deploys.length > 0 && /* @__PURE__ */ jsxs68("section", { style: { border, borderRadius: 8, padding: 10 }, children: [
      /* @__PURE__ */ jsx76("strong", { style: { fontSize: 12 }, children: t5.deploys }),
      deploys.slice(0, 5).map((d) => /* @__PURE__ */ jsxs68("div", { style: { fontSize: 11, marginTop: 4, opacity: 0.85 }, children: [
        /* @__PURE__ */ jsx76("span", { style: { color: d.status === "success" ? "#2e9e5b" : "#d64545" }, children: d.status }),
        " \xB7 ",
        /* @__PURE__ */ jsx76("code", { children: (d.sourceSha ?? "\u2014").slice(0, 10) }),
        " \xB7 ",
        d.filesCopied,
        " files \u2192 ",
        /* @__PURE__ */ jsx76("code", { children: d.targetDir }),
        " \xB7 ",
        new Date(d.startedAt).toLocaleString(),
        d.error && /* @__PURE__ */ jsx76("div", { style: { color: "#d64545" }, children: /* @__PURE__ */ jsx76("code", { children: d.error }) })
      ] }, d.deployId))
    ] }),
    /* @__PURE__ */ jsxs68("section", { style: { border, borderRadius: 8, padding: 10 }, children: [
      /* @__PURE__ */ jsx76("strong", { style: { fontSize: 12 }, children: t5.env }),
      /* @__PURE__ */ jsxs68("div", { style: { display: "flex", gap: 6, marginTop: 6 }, children: [
        /* @__PURE__ */ jsx76("input", { style: { ...inputStyle, flex: "0 0 40%" }, placeholder: t5.envName, value: envForm.name, onChange: (e) => setEnvForm((f) => ({ ...f, name: e.target.value.toUpperCase() })) }),
        /* @__PURE__ */ jsx76("input", { style: { ...inputStyle, flex: 1 }, type: "password", placeholder: t5.envValue, value: envForm.value, onChange: (e) => setEnvForm((f) => ({ ...f, value: e.target.value })), autoComplete: "off" }),
        /* @__PURE__ */ jsx76(
          "button",
          {
            style: { fontSize: 11 },
            disabled: busy === "env" || !envForm.name.trim() || !envForm.value,
            onClick: () => void run("env", async () => {
              await service.setEnvVar(envForm.name.trim(), envForm.value);
              setEnvForm({ name: "", value: "" });
            }),
            children: t5.addEnv
          }
        )
      ] }),
      envVars.length === 0 ? /* @__PURE__ */ jsx76("div", { style: { fontSize: 12, opacity: 0.7, marginTop: 6 }, children: t5.noEnv }) : envVars.map((v) => /* @__PURE__ */ jsxs68("div", { style: { display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 4 }, children: [
        /* @__PURE__ */ jsxs68("code", { children: [
          v.name,
          "=\u2022\u2022\u2022\u2022\u2022\u2022"
        ] }),
        /* @__PURE__ */ jsx76("button", { style: { fontSize: 11 }, disabled: busy === `envdel-${v.name}`, onClick: () => void run(`envdel-${v.name}`, async () => service.deleteEnvVar(v.name)), children: t5.del })
      ] }, v.name))
    ] }),
    /* @__PURE__ */ jsxs68("section", { style: { border, borderRadius: 8, padding: 10 }, children: [
      /* @__PURE__ */ jsx76("strong", { style: { fontSize: 12 }, children: t5.rules }),
      /* @__PURE__ */ jsxs68("div", { style: { display: "flex", gap: 6, marginTop: 6 }, children: [
        /* @__PURE__ */ jsx76("input", { style: { ...inputStyle, flex: 1 }, placeholder: t5.rulePlaceholder, value: ruleText, onChange: (e) => setRuleText(e.target.value) }),
        /* @__PURE__ */ jsx76(
          "button",
          {
            style: { fontSize: 11 },
            disabled: busy === "rule" || !ruleText.trim(),
            onClick: () => void run("rule", async () => {
              await service.createRule(ruleText.trim(), true);
              setRuleText("");
            }),
            children: t5.addRule
          }
        )
      ] }),
      rules.length === 0 ? /* @__PURE__ */ jsx76("div", { style: { fontSize: 12, opacity: 0.7, marginTop: 6 }, children: t5.noRules }) : rules.map((r) => /* @__PURE__ */ jsxs68("div", { style: { display: "flex", justifyContent: "space-between", gap: 6, fontSize: 12, marginTop: 4 }, children: [
        /* @__PURE__ */ jsxs68("span", { style: { flex: 1 }, children: [
          r.pinned ? "\u{1F4CC} " : "",
          r.content
        ] }),
        /* @__PURE__ */ jsx76("button", { style: { fontSize: 11 }, disabled: busy === `ruledel-${r.memoryId}`, onClick: () => void run(`ruledel-${r.memoryId}`, async () => service.deleteRule(r.memoryId)), children: t5.del })
      ] }, r.memoryId))
    ] })
  ] });
}

// src/builder/BuilderPanel.tsx
import { Fragment as Fragment8, jsx as jsx77, jsxs as jsxs69 } from "react/jsx-runtime";
var HI2 = {
  title: "\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u092C\u093F\u0932\u094D\u0921\u0930",
  schema: "\u0921\u0947\u091F\u093E\u092C\u0947\u0938 \u0938\u094D\u0915\u0940\u092E\u093E",
  preview: "\u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942",
  versions: "\u0938\u0902\u0938\u094D\u0915\u0930\u0923 / rollback",
  deployPrep: "\u0921\u093F\u092A\u094D\u0932\u0949\u092F \u0924\u0948\u092F\u093E\u0930\u0940",
  noProject: "\u092A\u0939\u0932\u0947 \u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u091A\u0941\u0928\u0947\u0902\u0964",
  noService: "\u092C\u093F\u0932\u094D\u0921\u0930 \u0938\u0947\u0935\u093E \u0915\u0928\u0947\u0915\u094D\u091F \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
  loading: "\u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026",
  noSchema: "\u0915\u094B\u0908 db/migrations SQL \u092B\u093C\u093E\u0907\u0932 \u0905\u092D\u0940 index \u0928\u0939\u0940\u0902 \u0939\u0941\u0908 \u0939\u0948\u0964",
  tables: (n) => `${n} tables`,
  localPreview: "\u0932\u094B\u0915\u0932 \u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942",
  webPreview: "\u0935\u0947\u092C \u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942",
  notConfigured: "\u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0928\u0939\u0940\u0902",
  ready: "\u0924\u0948\u092F\u093E\u0930",
  unavailable: "\u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902",
  noListener: "\u0915\u094B\u0908 listener \u0928\u0939\u0940\u0902 \u091A\u0932 \u0930\u0939\u093E",
  openPreview: "\u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942 \u0916\u094B\u0932\u0947\u0902",
  snapshots: "snapshots",
  patches: "patches",
  indexed: "indexed files",
  changed: "\u092C\u0926\u0932\u0940 \u0939\u0941\u0908 \u092B\u093C\u093E\u0907\u0932\u0947\u0902",
  commit: "commit",
  refresh: "\u0930\u093F\u092B\u093C\u094D\u0930\u0947\u0936",
  applied: "applied",
  pending: "pending",
  deployNone: "\u0915\u094B\u0908 deploy target configured \u0928\u0939\u0940\u0902 \u2014 \u090A\u092A\u0930 connectors \u092E\u0947\u0902 static_dir \u091C\u094B\u0921\u093C\u0947\u0902\u0964",
  deployReadySignals: "\u0924\u0948\u092F\u093E\u0930\u0940 \u0938\u0902\u0915\u0947\u0924",
  gitRepo: "git repo"
};
var EN2 = {
  title: "Project builder",
  schema: "Database schema",
  preview: "Preview",
  versions: "Versions / rollback",
  deployPrep: "Deploy preparation",
  noProject: "Select a project first.",
  noService: "Builder service is not connected.",
  loading: "Loading\u2026",
  noSchema: "No db/migrations SQL file indexed yet.",
  tables: (n) => `${n} tables`,
  localPreview: "Local preview",
  webPreview: "Web preview",
  notConfigured: "Not configured",
  ready: "Ready",
  unavailable: "Unavailable",
  noListener: "No listener running",
  openPreview: "Open preview",
  snapshots: "snapshots",
  patches: "patches",
  indexed: "indexed files",
  changed: "changed files",
  commit: "commit",
  refresh: "Refresh",
  applied: "applied",
  pending: "pending",
  deployNone: "No deploy target configured \u2014 add a static_dir connector above.",
  deployReadySignals: "Readiness signals",
  gitRepo: "git repo"
};
function row(label, value, mono = false) {
  return /* @__PURE__ */ jsxs69("div", { style: { display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, padding: "2px 0" }, children: [
    /* @__PURE__ */ jsx77("span", { style: { opacity: 0.75 }, children: label }),
    /* @__PURE__ */ jsx77("span", { style: mono ? { fontFamily: "monospace", fontSize: 11, textAlign: "right", wordBreak: "break-all" } : { textAlign: "right" }, children: value })
  ] });
}
function BuilderPanel() {
  const shell = useWorkspaceShell();
  const t5 = shell.locale === "hi" ? HI2 : EN2;
  const service = shell.builderService;
  const projectId = shell.currentContext?.projectId;
  const [schema, setSchema] = useState38(null);
  const [urls, setUrls] = useState38(null);
  const [versions, setVersions] = useState38(null);
  const [git, setGit] = useState38(null);
  const [prep, setPrep] = useState38(null);
  const [error, setError] = useState38(null);
  const [loading, setLoading] = useState38(false);
  const [showPreview, setShowPreview] = useState38(false);
  const [restore, setRestore] = useState38(null);
  const [restoreBusy, setRestoreBusy] = useState38(null);
  const load = useCallback25(async () => {
    if (!service || !projectId) return;
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    try {
      const results = await Promise.allSettled([
        service.schema(controller.signal),
        service.outputUrls(controller.signal),
        service.versions(controller.signal),
        service.gitStatus(controller.signal),
        service.deployPrep(controller.signal)
      ]);
      if (results[0].status === "fulfilled") setSchema(results[0].value);
      if (results[1].status === "fulfilled") setUrls(results[1].value);
      if (results[2].status === "fulfilled") setVersions(results[2].value);
      if (results[3].status === "fulfilled") setGit(results[3].value);
      if (results[4].status === "fulfilled") setPrep(results[4].value);
      const firstFailure = results.find((r) => r.status === "rejected");
      if (firstFailure) setError(String(firstFailure.reason.errorCode ?? "builder_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [service, projectId]);
  useEffect30(() => {
    void load();
    if (urls?.local.ready) return;
    const iv = setInterval(() => {
      void load();
    }, 1e4);
    return () => clearInterval(iv);
  }, [load, urls?.local.ready]);
  const deploySignals = useMemo17(() => {
    if (!prep) return [];
    const r = prep.readiness;
    return [
      { label: t5.gitRepo, ok: r.gitRepo },
      { label: t5.indexed, ok: r.indexed },
      { label: t5.snapshots, ok: r.snapshotAvailable },
      { label: t5.schema, ok: r.schemaDiscovered },
      { label: t5.localPreview, ok: r.localPreviewReady },
      { label: t5.webPreview, ok: r.publicPreviewAvailable }
    ];
  }, [prep, t5]);
  if (!projectId) {
    return /* @__PURE__ */ jsx77("div", { style: { padding: 12, fontSize: 13 }, children: t5.noProject });
  }
  if (!service) {
    return /* @__PURE__ */ jsx77("div", { style: { padding: 12, fontSize: 13 }, children: t5.noService });
  }
  const colors = { border: "rgba(128,128,128,0.3)", ok: "#2e9e5b", bad: "#d64545", muted: "rgba(128,128,128,0.9)" };
  return /* @__PURE__ */ jsxs69("div", { style: { display: "flex", flexDirection: "column", gap: 12, padding: 12, fontSize: 13, overflow: "auto", height: "100%" }, "data-testid": "builder-panel", children: [
    /* @__PURE__ */ jsxs69("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [
      /* @__PURE__ */ jsx77("strong", { children: t5.title }),
      /* @__PURE__ */ jsx77("button", { onClick: () => void load(), disabled: loading, style: { fontSize: 12 }, children: t5.refresh })
    ] }),
    error && /* @__PURE__ */ jsx77("div", { role: "alert", style: { color: colors.bad, fontSize: 12 }, children: /* @__PURE__ */ jsx77("code", { children: error }) }),
    /* @__PURE__ */ jsxs69("section", { style: { border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10 }, children: [
      /* @__PURE__ */ jsx77("strong", { style: { fontSize: 12 }, children: t5.schema }),
      !schema ? /* @__PURE__ */ jsx77("div", { style: { fontSize: 12, opacity: 0.7 }, children: t5.loading }) : !schema.discovered ? /* @__PURE__ */ jsx77("div", { style: { fontSize: 12, opacity: 0.7, marginTop: 4 }, children: t5.noSchema }) : /* @__PURE__ */ jsxs69("div", { style: { marginTop: 6 }, children: [
        row(t5.tables(schema.tables.length), schema.sourceFiles.join(", "), true),
        schema.tables.slice(0, 8).map((tb) => /* @__PURE__ */ jsxs69("div", { style: { fontSize: 12, marginTop: 4 }, children: [
          /* @__PURE__ */ jsx77("code", { children: tb.name }),
          " ",
          /* @__PURE__ */ jsxs69("span", { style: { opacity: 0.65 }, children: [
            "(",
            tb.columns.length,
            " cols)"
          ] })
        ] }, tb.name)),
        schema.unparsedFiles.length > 0 && /* @__PURE__ */ jsxs69("div", { style: { fontSize: 11, opacity: 0.7, marginTop: 4 }, children: [
          "unparsed: ",
          schema.unparsedFiles.join(", ")
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs69("section", { style: { border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10 }, children: [
      /* @__PURE__ */ jsx77("strong", { style: { fontSize: 12 }, children: t5.preview }),
      /* @__PURE__ */ jsx77("div", { style: { marginTop: 6 }, children: urls ? /* @__PURE__ */ jsxs69(Fragment8, { children: [
        row(t5.localPreview, urls.local.ready ? `${t5.ready} :${urls.local.port ?? ""}` : urls.local.reason === "no_listener" ? t5.noListener : t5.notConfigured),
        row(t5.webPreview, urls.publicPreview.status === "available" ? urls.publicPreview.url ?? t5.ready : urls.publicPreview.reason ?? t5.unavailable),
        urls.local.ready && urls.local.url && /* @__PURE__ */ jsx77("button", { style: { fontSize: 12, marginTop: 6 }, onClick: () => setShowPreview((v) => !v), children: t5.openPreview }),
        showPreview && urls.local.url && /* @__PURE__ */ jsx77(
          "iframe",
          {
            title: "local-preview",
            src: urls.local.url,
            style: { width: "100%", height: 240, border: `1px solid ${colors.border}`, borderRadius: 6, marginTop: 6, background: "#fff" }
          }
        )
      ] }) : /* @__PURE__ */ jsx77("div", { style: { fontSize: 12, opacity: 0.7 }, children: t5.loading }) })
    ] }),
    /* @__PURE__ */ jsxs69("section", { style: { border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10 }, children: [
      /* @__PURE__ */ jsx77("strong", { style: { fontSize: 12 }, children: t5.versions }),
      versions ? /* @__PURE__ */ jsxs69("div", { style: { marginTop: 6 }, children: [
        row(t5.snapshots, versions.snapshots.length),
        row(t5.patches, `${versions.patches.length} (${versions.patches.filter((p) => p.appliedAt).length} ${t5.applied})`),
        row(t5.commit, (versions.git.currentCommit ?? git?.currentCommit ?? "\u2014").slice(0, 12), true),
        row(t5.changed, versions.git.changedFiles),
        versions.snapshots.slice(0, 4).map((s) => /* @__PURE__ */ jsxs69("div", { style: { fontSize: 11, opacity: 0.75, marginTop: 3, display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center" }, children: [
          /* @__PURE__ */ jsxs69("span", { children: [
            /* @__PURE__ */ jsx77("code", { children: (s.commitSha ?? s.snapshotId).slice(0, 10) }),
            " \u2014 ",
            s.description ?? "",
            " \xB7 ",
            new Date(s.createdAt).toLocaleString()
          ] }),
          s.commitSha && /* @__PURE__ */ jsx77(
            "button",
            {
              style: { fontSize: 10 },
              disabled: restoreBusy === s.snapshotId,
              onClick: () => {
                setRestoreBusy(s.snapshotId);
                setError(null);
                void service.restoreSnapshot(s.snapshotId).then(setRestore).catch((err) => setError(err.errorCode ?? "restore_failed")).finally(() => setRestoreBusy(null));
              },
              children: "restore"
            }
          )
        ] }, s.snapshotId)),
        restore && /* @__PURE__ */ jsxs69("div", { style: { fontSize: 11, marginTop: 6, opacity: 0.85 }, children: [
          "restore @",
          /* @__PURE__ */ jsx77("code", { children: (restore.commitSha ?? "").slice(0, 10) }),
          ": ",
          restore.proposals.length,
          " patch proposals (",
          restore.skipped.length,
          " skipped) \u2014 approve/apply flow \u0938\u0947 \u092A\u0942\u0930\u093E \u0915\u0930\u0947\u0902\u0964"
        ] })
      ] }) : /* @__PURE__ */ jsx77("div", { style: { fontSize: 12, opacity: 0.7 }, children: t5.loading })
    ] }),
    /* @__PURE__ */ jsx77(WorkspaceConnectorsPanel, {}),
    /* @__PURE__ */ jsxs69("section", { style: { border: `1px solid ${colors.border}`, borderRadius: 8, padding: 10 }, children: [
      /* @__PURE__ */ jsx77("strong", { style: { fontSize: 12 }, children: t5.deployPrep }),
      prep?.target.configured ? /* @__PURE__ */ jsxs69("div", { style: { fontSize: 11, opacity: 0.85, marginTop: 4 }, children: [
        "target: ",
        /* @__PURE__ */ jsx77("code", { children: prep.target.targetKind ?? "" }),
        " \xB7 execution: ",
        /* @__PURE__ */ jsx77("code", { children: prep.manifest.execution })
      ] }) : /* @__PURE__ */ jsx77("div", { style: { fontSize: 11, opacity: 0.7, marginTop: 4 }, children: t5.deployNone }),
      prep && /* @__PURE__ */ jsxs69("div", { style: { marginTop: 4 }, children: [
        row(t5.commit, prep.sourceSha.slice(0, 12), true),
        row("manifest", prep.manifest.execution, true),
        row("build", prep.build.status, true)
      ] }),
      /* @__PURE__ */ jsxs69("div", { style: { marginTop: 6 }, children: [
        /* @__PURE__ */ jsx77("div", { style: { fontSize: 11, opacity: 0.8, marginBottom: 4 }, children: t5.deployReadySignals }),
        deploySignals.map((s) => /* @__PURE__ */ jsxs69("div", { style: { fontSize: 12, display: "flex", gap: 6, alignItems: "center" }, children: [
          /* @__PURE__ */ jsx77("span", { style: { color: s.ok ? colors.ok : colors.muted }, children: s.ok ? "\u25CF" : "\u25CB" }),
          /* @__PURE__ */ jsx77("span", { children: s.label })
        ] }, s.label))
      ] })
    ] })
  ] });
}

// src/workspace/SystemIntegrationsPanel.tsx
import { useEffect as useEffect31, useMemo as useMemo18, useState as useState39 } from "react";
import { jsx as jsx78, jsxs as jsxs70 } from "react/jsx-runtime";
var STRINGS3 = {
  title: { en: "System Integrations", hi: "\u0938\u093F\u0938\u094D\u091F\u092E \u0907\u0902\u091F\u0940\u0917\u094D\u0930\u0947\u0936\u0928" },
  search: { en: "Search system integrations\u2026", hi: "\u0938\u093F\u0938\u094D\u091F\u092E \u0907\u0902\u091F\u0940\u0917\u094D\u0930\u0947\u0936\u0928 \u0916\u094B\u091C\u0947\u0902\u2026" },
  all: { en: "All", hi: "\u0938\u092D\u0940" },
  category: { en: "Category", hi: "\u0936\u094D\u0930\u0947\u0923\u0940" },
  status: { en: "Status", hi: "\u0938\u094D\u0925\u093F\u0924\u093F" },
  loading: { en: "Loading system integrations\u2026", hi: "\u0938\u093F\u0938\u094D\u091F\u092E \u0907\u0902\u091F\u0940\u0917\u094D\u0930\u0947\u0936\u0928 \u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u0947 \u0939\u0948\u0902\u2026" },
  error: { en: "Failed to load system integrations.", hi: "\u0938\u093F\u0938\u094D\u091F\u092E \u0907\u0902\u091F\u0940\u0917\u094D\u0930\u0947\u0936\u0928 \u0932\u094B\u0921 \u0915\u0930\u0928\u0947 \u092E\u0947\u0902 \u0935\u093F\u092B\u0932\u0964" },
  managed: { en: "Managed by Smart Shikka", hi: "Smart Shikka \u0926\u094D\u0935\u093E\u0930\u093E \u092A\u094D\u0930\u092C\u0902\u0927\u093F\u0924" },
  systemBuiltin: { en: "System built-in", hi: "\u0938\u093F\u0938\u094D\u091F\u092E \u092C\u093F\u0932\u094D\u091F-\u0907\u0928" },
  userInstallable: { en: "User installable", hi: "\u092F\u0942\u091C\u093C\u0930 \u0907\u0902\u0938\u094D\u091F\u0949\u0932 \u0915\u0930 \u0938\u0915\u0924\u093E \u0939\u0948" },
  authConfigured: { en: "Auth configured", hi: "Auth \u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0939\u0948" },
  authNotConfigured: { en: "Auth not configured", hi: "Auth \u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0928\u0939\u0940\u0902 \u0939\u0948" },
  healthCheck: { en: "Health check", hi: "Health check" },
  viewTools: { en: "View tools", hi: "Tools \u0926\u0947\u0916\u0947\u0902" },
  hideTools: { en: "Hide tools", hi: "Tools \u091B\u0941\u092A\u093E\u090F\u0901" },
  noIntegrations: { en: "No integrations match the current filters.", hi: "\u0935\u0930\u094D\u0924\u092E\u093E\u0928 \u092B\u093C\u093F\u0932\u094D\u091F\u0930 \u0938\u0947 \u0915\u094B\u0908 \u0907\u0902\u091F\u0940\u0917\u094D\u0930\u0947\u0936\u0928 \u092E\u0947\u0932 \u0928\u0939\u0940\u0902 \u0916\u093E\u0924\u093E\u0964" },
  lastHealth: { en: "Last health check", hi: "\u092A\u093F\u091B\u0932\u093E health check" },
  toolCount: { en: "Tools discovered", hi: "\u0921\u093F\u0938\u094D\u0915\u0935\u0930 \u0915\u093F\u090F \u0917\u090F tools" },
  lifecycle: { en: "Lifecycle", hi: "Lifecycle" },
  runtimeHost: { en: "Runtime host", hi: "Runtime host" },
  version: { en: "Version", hi: "Version" }
};
function tr10(key, locale) {
  return STRINGS3[key]?.[locale] ?? key;
}
var CATEGORIES = [
  { id: "all", label: { en: "All", hi: "\u0938\u092D\u0940" } },
  { id: "ai", label: { en: "AI", hi: "AI" } },
  { id: "knowledge", label: { en: "Knowledge / Coding", hi: "\u0928\u0949\u0932\u0947\u091C / \u0915\u094B\u0921\u093F\u0902\u0917" } },
  { id: "browser", label: { en: "Browser", hi: "\u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930" } },
  { id: "desktop", label: { en: "Desktop", hi: "\u0921\u0947\u0938\u094D\u0915\u091F\u0949\u092A" } },
  { id: "voice", label: { en: "Voice", hi: "\u0935\u0949\u0907\u0938" } },
  { id: "agents", label: { en: "Agents", hi: "\u090F\u091C\u0947\u0902\u091F\u094D\u0938" } },
  { id: "automation", label: { en: "Automation", hi: "\u0911\u091F\u094B\u092E\u0947\u0936\u0928" } },
  { id: "messaging", label: { en: "Messaging", hi: "\u092E\u0948\u0938\u0947\u091C\u093F\u0902\u0917" } },
  { id: "infrastructure", label: { en: "Infrastructure", hi: "\u0907\u0928\u094D\u092B\u094D\u0930\u093E\u0938\u094D\u091F\u094D\u0930\u0915\u094D\u091A\u0930" } }
];
var STATUS_FILTERS = [
  { id: "all", label: { en: "All", hi: "\u0938\u092D\u0940" } },
  { id: "healthy", label: { en: "Ready", hi: "Ready" } },
  { id: "degraded", label: { en: "Degraded", hi: "Degraded" } },
  { id: "configured", label: { en: "Configured", hi: "Configured" } },
  { id: "not_configured", label: { en: "Not configured", hi: "Configured \u0928\u0939\u0940\u0902" } },
  { id: "runtime_offline", label: { en: "Runtime offline", hi: "Runtime offline" } },
  { id: "failed", label: { en: "Failed", hi: "Failed" } }
];
function formatTime2(ts, locale) {
  if (!ts) return "-";
  const d = new Date(ts);
  if (locale === "hi") return d.toLocaleString("hi-IN");
  return d.toLocaleString();
}
function SystemIntegrationsPanel({
  token,
  locale = "en",
  theme: theme2 = "light",
  fetchImpl = fetch
}) {
  const colors = themeColors(theme2);
  const [statuses, setStatuses] = useState39([]);
  const [loading, setLoading] = useState39(true);
  const [error, setError] = useState39(null);
  const [category, setCategory] = useState39("all");
  const [statusFilter, setStatusFilter] = useState39("all");
  const [query, setQuery] = useState39("");
  const [expanded, setExpanded] = useState39(null);
  const [healthBusy, setHealthBusy] = useState39(null);
  async function load() {
    if (!token) {
      setError(tr10("error", locale));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchImpl("/api/admin/system-integrations", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }
      setStatuses(data.integrations ?? []);
    } catch (e) {
      setError(errorToCode(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect31(() => {
    void load();
  }, [token]);
  async function healthCheck(integrationId) {
    if (!token) return;
    setHealthBusy(integrationId);
    try {
      const res = await fetchImpl(`/api/admin/system-integrations/${integrationId}/health-check`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.ok && data.status) {
        setStatuses((prev) => prev.map((s) => s.definition.id === integrationId ? data.status : s));
      } else {
        setError(safeErrorCode(data.error ?? "health_check_failed"));
      }
    } catch (e) {
      setError(errorToCode(e));
    } finally {
      setHealthBusy(null);
    }
  }
  const filtered = useMemo18(() => {
    const q = query.trim().toLowerCase();
    return statuses.filter((s) => {
      if (category !== "all" && s.definition.category !== category) return false;
      if (statusFilter === "not_configured") return !s.runtime.configured;
      if (statusFilter === "configured") return s.runtime.configured;
      if (statusFilter !== "all" && s.runtime.lifecycle !== statusFilter) return false;
      if (q) {
        const hay = `${s.definition.displayName} ${s.definition.description ?? ""} ${s.definition.id}`.toLowerCase();
        return hay.includes(q);
      }
      return true;
    });
  }, [statuses, category, statusFilter, query]);
  const lifecycleColor = (l) => {
    switch (l) {
      case "healthy":
        return tokens.colors.success;
      case "degraded":
      case "update_available":
        return tokens.colors.warning;
      case "failed":
        return tokens.colors.danger;
      case "runtime_offline":
        return colors.textMuted;
      default:
        return colors.textMuted;
    }
  };
  return /* @__PURE__ */ jsxs70("div", { style: { color: colors.text, fontSize: "13px" }, children: [
    /* @__PURE__ */ jsx78("div", { style: { display: "flex", gap: tokens.spacing.md, marginBottom: tokens.spacing.md, flexWrap: "wrap", alignItems: "center" }, children: /* @__PURE__ */ jsx78(
      "input",
      {
        type: "text",
        value: query,
        onChange: (e) => setQuery(e.target.value),
        placeholder: tr10("search", locale),
        style: {
          flex: 1,
          minWidth: 180,
          padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
          border: `1px solid ${colors.border}`,
          borderRadius: tokens.radius.md,
          background: colors.surfaceStrong,
          color: colors.text
        }
      }
    ) }),
    /* @__PURE__ */ jsx78("div", { style: { display: "flex", gap: tokens.spacing.sm, marginBottom: tokens.spacing.md, flexWrap: "wrap" }, children: CATEGORIES.map((c) => /* @__PURE__ */ jsx78(
      "button",
      {
        type: "button",
        onClick: () => setCategory(c.id),
        style: {
          padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
          border: `1px solid ${colors.border}`,
          borderRadius: tokens.radius.md,
          background: category === c.id ? colors.warmAccent : colors.surfaceStrong,
          color: category === c.id ? "#fff" : colors.text,
          cursor: "pointer",
          fontSize: "12px"
        },
        children: c.label[locale]
      },
      c.id
    )) }),
    /* @__PURE__ */ jsx78("div", { style: { display: "flex", gap: tokens.spacing.sm, marginBottom: tokens.spacing.md, flexWrap: "wrap" }, children: STATUS_FILTERS.map((s) => /* @__PURE__ */ jsx78(
      "button",
      {
        type: "button",
        onClick: () => setStatusFilter(s.id),
        style: {
          padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
          border: `1px solid ${colors.border}`,
          borderRadius: tokens.radius.md,
          background: statusFilter === s.id ? colors.info : colors.surfaceStrong,
          color: statusFilter === s.id ? "#fff" : colors.text,
          cursor: "pointer",
          fontSize: "12px"
        },
        children: s.label[locale]
      },
      s.id
    )) }),
    error && /* @__PURE__ */ jsx78("div", { role: "alert", style: { padding: tokens.spacing.md, backgroundColor: "rgba(248,113,113,0.12)", border: `1px solid ${tokens.colors.danger}`, borderRadius: tokens.radius.md, marginBottom: tokens.spacing.md, color: tokens.colors.danger }, children: safeErrorCode(error) === error ? `${safeErrorText(error, locale).title} (${error})` : error }),
    loading ? /* @__PURE__ */ jsx78("p", { style: { color: colors.textMuted }, children: tr10("loading", locale) }) : filtered.length === 0 ? /* @__PURE__ */ jsx78("p", { style: { color: colors.textMuted }, children: tr10("noIntegrations", locale) }) : /* @__PURE__ */ jsx78("div", { style: { display: "flex", flexDirection: "column", gap: tokens.spacing.md }, children: filtered.map((s) => /* @__PURE__ */ jsxs70(
      "div",
      {
        style: {
          border: `1px solid ${colors.border}`,
          borderRadius: tokens.radius.md,
          padding: tokens.spacing.md,
          background: colors.surfaceStrong
        },
        children: [
          /* @__PURE__ */ jsxs70("div", { style: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: tokens.spacing.md, flexWrap: "wrap" }, children: [
            /* @__PURE__ */ jsxs70("div", { children: [
              /* @__PURE__ */ jsx78("div", { style: { fontWeight: 600, fontSize: "14px" }, children: s.definition.displayName }),
              /* @__PURE__ */ jsx78("div", { style: { fontSize: "11px", color: colors.textMuted, marginTop: 2 }, children: s.definition.id }),
              s.definition.description && /* @__PURE__ */ jsx78("div", { style: { fontSize: "12px", color: colors.textMuted, marginTop: tokens.spacing.sm, maxWidth: 520 }, children: s.definition.description }),
              /* @__PURE__ */ jsxs70("div", { style: { display: "flex", gap: tokens.spacing.sm, marginTop: tokens.spacing.sm, flexWrap: "wrap" }, children: [
                /* @__PURE__ */ jsx78("span", { style: { fontSize: "10px", padding: "2px 6px", borderRadius: tokens.radius.sm, background: colors.border, color: colors.text }, children: s.definition.kind }),
                /* @__PURE__ */ jsx78("span", { style: { fontSize: "10px", padding: "2px 6px", borderRadius: tokens.radius.sm, background: colors.border, color: colors.text }, children: s.definition.ownership === "system_builtin" ? tr10("systemBuiltin", locale) : tr10("userInstallable", locale) }),
                s.definition.managedBySmartShikka && /* @__PURE__ */ jsx78("span", { style: { fontSize: "10px", padding: "2px 6px", borderRadius: tokens.radius.sm, background: "rgba(52,211,153,0.15)", color: tokens.colors.success }, children: tr10("managed", locale) }),
                /* @__PURE__ */ jsx78("span", { style: { fontSize: "10px", padding: "2px 6px", borderRadius: tokens.radius.sm, background: "rgba(0,0,0,0.05)", color: lifecycleColor(s.runtime.lifecycle), fontWeight: 600 }, children: s.runtime.lifecycle })
              ] })
            ] }),
            /* @__PURE__ */ jsxs70("div", { style: { display: "flex", gap: tokens.spacing.sm, flexShrink: 0 }, children: [
              /* @__PURE__ */ jsx78(
                "button",
                {
                  type: "button",
                  disabled: healthBusy === s.definition.id,
                  onClick: () => healthCheck(s.definition.id),
                  style: {
                    padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
                    border: `1px solid ${colors.border}`,
                    borderRadius: tokens.radius.md,
                    background: colors.surfaceStrong,
                    color: colors.text,
                    cursor: "pointer",
                    fontSize: "12px"
                  },
                  children: healthBusy === s.definition.id ? "\u2026" : tr10("healthCheck", locale)
                }
              ),
              /* @__PURE__ */ jsx78(
                "button",
                {
                  type: "button",
                  onClick: () => setExpanded(expanded === s.definition.id ? null : s.definition.id),
                  style: {
                    padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
                    border: `1px solid ${colors.border}`,
                    borderRadius: tokens.radius.md,
                    background: colors.surfaceStrong,
                    color: colors.text,
                    cursor: "pointer",
                    fontSize: "12px"
                  },
                  children: expanded === s.definition.id ? tr10("hideTools", locale) : tr10("viewTools", locale)
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsx78("div", { style: { marginTop: tokens.spacing.sm, fontSize: "12px", color: colors.textMuted }, children: s.statusNote }),
          expanded === s.definition.id && /* @__PURE__ */ jsxs70(
            "div",
            {
              style: {
                marginTop: tokens.spacing.md,
                padding: tokens.spacing.md,
                borderRadius: tokens.radius.md,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: tokens.spacing.md
              },
              children: [
                /* @__PURE__ */ jsxs70("div", { children: [
                  /* @__PURE__ */ jsx78("div", { style: { fontSize: "11px", color: colors.textMuted }, children: tr10("authConfigured", locale) }),
                  /* @__PURE__ */ jsx78("div", { style: { fontWeight: 500 }, children: s.auth.configured ? tr10("authConfigured", locale) : tr10("authNotConfigured", locale) })
                ] }),
                /* @__PURE__ */ jsxs70("div", { children: [
                  /* @__PURE__ */ jsx78("div", { style: { fontSize: "11px", color: colors.textMuted }, children: tr10("lastHealth", locale) }),
                  /* @__PURE__ */ jsx78("div", { style: { fontWeight: 500 }, children: formatTime2(s.runtime.lastHealthCheckAt, locale) })
                ] }),
                /* @__PURE__ */ jsxs70("div", { children: [
                  /* @__PURE__ */ jsx78("div", { style: { fontSize: "11px", color: colors.textMuted }, children: tr10("toolCount", locale) }),
                  /* @__PURE__ */ jsx78("div", { style: { fontWeight: 500 }, children: s.runtime.toolCount ?? 0 })
                ] }),
                /* @__PURE__ */ jsxs70("div", { children: [
                  /* @__PURE__ */ jsx78("div", { style: { fontSize: "11px", color: colors.textMuted }, children: tr10("runtimeHost", locale) }),
                  /* @__PURE__ */ jsx78("div", { style: { fontWeight: 500 }, children: s.runtime.runtimeHost ?? "-" })
                ] }),
                /* @__PURE__ */ jsxs70("div", { children: [
                  /* @__PURE__ */ jsx78("div", { style: { fontSize: "11px", color: colors.textMuted }, children: tr10("version", locale) }),
                  /* @__PURE__ */ jsx78("div", { style: { fontWeight: 500 }, children: s.runtime.serverVersion ?? "-" })
                ] }),
                /* @__PURE__ */ jsxs70("div", { children: [
                  /* @__PURE__ */ jsx78("div", { style: { fontSize: "11px", color: colors.textMuted }, children: tr10("lifecycle", locale) }),
                  /* @__PURE__ */ jsx78("div", { style: { fontWeight: 500, color: lifecycleColor(s.runtime.lifecycle) }, children: s.runtime.lifecycle })
                ] }),
                s.runtime.discoveredCapabilities.length > 0 && /* @__PURE__ */ jsxs70("div", { style: { gridColumn: "1 / -1" }, children: [
                  /* @__PURE__ */ jsx78("div", { style: { fontSize: "11px", color: colors.textMuted, marginBottom: tokens.spacing.xs }, children: "Capabilities" }),
                  /* @__PURE__ */ jsx78("div", { style: { display: "flex", gap: tokens.spacing.xs, flexWrap: "wrap" }, children: s.runtime.discoveredCapabilities.map((c) => /* @__PURE__ */ jsx78("span", { style: { fontSize: "11px", padding: "2px 6px", borderRadius: tokens.radius.sm, background: colors.border, color: colors.text }, children: c }, c)) })
                ] }),
                s.runtime.lastErrorCode && /* @__PURE__ */ jsx78("div", { style: { gridColumn: "1 / -1" }, children: /* @__PURE__ */ jsxs70("div", { style: { fontSize: "11px", color: tokens.colors.danger }, children: [
                  "Last error: ",
                  s.runtime.lastErrorCode
                ] }) })
              ]
            }
          )
        ]
      },
      s.definition.id
    )) })
  ] });
}

// src/workspace/WorkspaceShell.tsx
import { Fragment as Fragment9, jsx as jsx79, jsxs as jsxs71 } from "react/jsx-runtime";
var SHELL_STRINGS2 = {
  newChat: { en: "New Chat", hi: "\u0928\u0908 \u091A\u0948\u091F" },
  recentChats: { en: "Chats", hi: "\u091A\u0948\u091F\u094D\u0938" },
  light: { en: "Light", hi: "\u0939\u0932\u094D\u0915\u093E" },
  dark: { en: "Dark", hi: "\u0917\u0939\u0930\u093E" },
  unpin: { en: "Unpin", hi: "Unpin" },
  projects: { en: "Projects", hi: "\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F" },
  coworkRuns: { en: "Cowork Runs", hi: "Cowork Runs" },
  codeWorkspaces: { en: "Code Workspaces", hi: "Code Workspaces" },
  files: { en: "Files", hi: "\u092B\u093C\u093E\u0907\u0932\u0947\u0902" },
  memory: { en: "Memory", hi: "\u092E\u0947\u092E\u094B\u0930\u0940" },
  automations: { en: "Automations", hi: "\u0911\u091F\u094B\u092E\u0947\u0936\u0928" },
  apps: { en: "Apps", hi: "\u0910\u092A\u094D\u0938" },
  skills: { en: "Skills", hi: "\u0938\u094D\u0915\u093F\u0932\u094D\u0938" },
  settings: { en: "Settings", hi: "\u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938" },
  context: { en: "Context", hi: "Context" },
  artifacts: { en: "Artifacts", hi: "Artifacts" },
  browser: { en: "Browser", hi: "Browser" },
  builder: { en: "Builder", hi: "\u092C\u093F\u0932\u094D\u0921\u0930" },
  cowork: { en: "Cowork", hi: "Cowork" },
  code: { en: "Code", hi: "Code" },
  codeIntelligence: { en: "Code Intelligence", hi: "\u0915\u094B\u0921 \u0907\u0902\u091F\u0947\u0932\u093F\u091C\u0947\u0902\u0938" },
  approvals: { en: "Approvals", hi: "\u092E\u0902\u091C\u093C\u0942\u0930\u093F\u092F\u093E\u0901" },
  audit: { en: "Audit", hi: "\u0911\u0921\u093F\u091F" },
  chatServiceMissing: { en: "Chat service not connected.", hi: "Chat service connected \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" },
  codeServiceMissing: { en: "Code workspace service not connected.", hi: "Code workspace service connected \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" },
  memoryServiceMissing: { en: "Memory service not connected.", hi: "Memory service connected \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" },
  noProjectSelected: { en: "No project selected.", hi: "\u0915\u094B\u0908 \u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u091A\u0941\u0928\u093E \u0928\u0939\u0940\u0902 \u0917\u092F\u093E\u0964" },
  selectProject: { en: "Select project", hi: "\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u091A\u0941\u0928\u0947\u0902" },
  noRepositories: { en: "No repositories connected.", hi: "\u0915\u094B\u0908 repository connected \u0928\u0939\u0940\u0902\u0964" },
  selectRepository: { en: "Select repository", hi: "Repository \u091A\u0941\u0928\u0947\u0902" },
  searchFiles: { en: "Search files", hi: "\u092B\u093C\u093E\u0907\u0932\u0947\u0902 \u0916\u094B\u091C\u0947\u0902" },
  searchSymbols: { en: "Search symbols", hi: "Symbols \u0916\u094B\u091C\u0947\u0902" },
  fileTree: { en: "File tree", hi: "File tree" },
  openFiles: { en: "Open files", hi: "Open files" },
  gitStatus: { en: "Git status", hi: "Git status" },
  diffPreview: { en: "Diff preview", hi: "Diff preview" },
  patchRequest: { en: "Patch request", hi: "Patch request" },
  agentSkills: { en: "Agent Skills", hi: "Agent Skills" },
  graphifySearch: { en: "Search graph", hi: "Search graph" },
  graphifyTrace: { en: "Trace path", hi: "Trace path" },
  graphifyImpact: { en: "Impact analysis", hi: "Impact analysis" },
  importSkill: { en: "Import skill", hi: "Import skill" },
  executeSkill: { en: "Execute skill", hi: "Execute skill" },
  approveSkill: { en: "Approve", hi: "Approve" },
  enableSkill: { en: "Enable", hi: "Enable" },
  disableSkill: { en: "Disable", hi: "Disable" },
  removeSkill: { en: "Remove", hi: "Remove" },
  listSkills: { en: "List skills", hi: "List skills" },
  realExecutionDesktopOnly: { en: "Real execution only on desktop", hi: "Real execution only on desktop" },
  requestApproval: { en: "Request approval", hi: "Approval \u092E\u093E\u0901\u0917\u0947\u0902" },
  approve: { en: "Approve", hi: "Approve \u0915\u0930\u0947\u0902" },
  apply: { en: "Apply", hi: "Apply \u0915\u0930\u0947\u0902" },
  publish: { en: "Publish", hi: "Publish \u0915\u0930\u0947\u0902" },
  runTests: { en: "Run tests", hi: "Tests \u091A\u0932\u093E\u090F\u0901" },
  runBuild: { en: "Run build", hi: "Build \u091A\u0932\u093E\u090F\u0901" },
  rollback: { en: "Rollback", hi: "Rollback" },
  disabledReason: { en: "Disabled: backend service not available.", hi: "Disabled: backend service \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902\u0964" },
  ownerOnly: { en: "Owner-only controls", hi: "\u0915\u0947\u0935\u0932 owner controls" },
  emergencyStopActive: { en: "Emergency Stop active \u2014 mutations blocked.", hi: "Emergency Stop active \u2014 mutations blocked\u0964" },
  noTaskSelected: { en: "Select or create a task to begin.", hi: "\u0936\u0941\u0930\u0942 \u0915\u0930\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F task \u091A\u0941\u0928\u0947\u0902 \u092F\u093E \u092C\u0928\u093E\u090F\u0901\u0964" },
  plan: { en: "Plan", hi: "Plan" },
  stages: { en: "Stages", hi: "Stages" },
  dependencies: { en: "Dependencies", hi: "Dependencies" },
  evidence: { en: "Evidence", hi: "Evidence" },
  agents: { en: "Agents", hi: "Agents" },
  handoffs: { en: "Handoffs", hi: "Handoffs" },
  result: { en: "Result", hi: "Result" },
  pause: { en: "Pause", hi: "\u0930\u094B\u0915\u0947\u0902" },
  resume: { en: "Resume", hi: "\u091C\u093E\u0930\u0940 \u0930\u0916\u0947\u0902" },
  cancel: { en: "Cancel", hi: "\u0930\u0926\u094D\u0926 \u0915\u0930\u0947\u0902" },
  includedFiles: { en: "Included files", hi: "\u0936\u093E\u092E\u093F\u0932 \u092B\u093C\u093E\u0907\u0932\u0947\u0902" },
  includedMemories: { en: "Included memories", hi: "\u0936\u093E\u092E\u093F\u0932 memories" },
  retrievedSymbols: { en: "Retrieved symbols", hi: "Retrieved symbols" },
  lastIndexedCommit: { en: "Last indexed commit", hi: "Last indexed commit" },
  staleWarning: { en: "Index is stale \u2014 reindex recommended.", hi: "Index \u092A\u0941\u0930\u093E\u0928\u093E \u0939\u0948 \u2014 reindex \u0915\u0930\u0947\u0902\u0964" },
  pinned: { en: "Pinned", hi: "Pinned" },
  thirtyDayMemory: { en: "30-day memory", hi: "30-day memory" },
  export: { en: "Export", hi: "Export" },
  delete: { en: "Delete", hi: "Delete" },
  noMemories: { en: "No memories loaded.", hi: "\u0915\u094B\u0908 memory loaded \u0928\u0939\u0940\u0902\u0964" },
  noContext: { en: "No context loaded.", hi: "\u0915\u094B\u0908 context loaded \u0928\u0939\u0940\u0902\u0964" },
  navigate: { en: "Go to", hi: "\u091C\u093E\u090F\u0901" },
  openPanel: { en: "Open", hi: "\u0916\u094B\u0932\u0947\u0902" },
  toggleTheme: { en: "Toggle theme", hi: "\u0925\u0940\u092E \u092C\u0926\u0932\u0947\u0902" },
  toggleLocale: { en: "Toggle language", hi: "\u092D\u093E\u0937\u093E \u092C\u0926\u0932\u0947\u0902" },
  switchToNormal: { en: "Switch to normal mode", hi: "\u0938\u093E\u092E\u093E\u0928\u094D\u092F \u092E\u094B\u0921 \u092A\u0930 \u091C\u093E\u090F\u0901" },
  switchToAdvanced: { en: "Switch to advanced mode", hi: "\u090F\u0921\u0935\u093E\u0902\u0938 \u092E\u094B\u0921 \u092A\u0930 \u091C\u093E\u090F\u0901" },
  normalMode: { en: "Normal", hi: "\u0938\u093E\u092E\u093E\u0928\u094D\u092F" },
  advancedMode: { en: "Advanced", hi: "\u090F\u0921\u0935\u093E\u0902\u0938" },
  commandPalette: { en: "Command palette", hi: "\u0915\u092E\u093E\u0902\u0921 \u092A\u0948\u0932\u0947\u091F" },
  runTask: { en: "Run task", hi: "Task \u091A\u0932\u093E\u090F\u0901" },
  pendingApprovals: { en: "Pending approvals", hi: "\u0932\u0902\u092C\u093F\u0924 \u092E\u0902\u091C\u093C\u0942\u0930\u093F\u092F\u093E\u0901" },
  mode: { en: "Mode", hi: "\u092E\u094B\u0921" },
  codingModel: { en: "Coding model", hi: "\u0915\u094B\u0921\u093F\u0902\u0917 \u092E\u0949\u0921\u0932" },
  codingTaskDescription: { en: "Task description", hi: "\u091F\u093E\u0938\u094D\u0915 \u0935\u093F\u0935\u0930\u0923" },
  generateEdits: { en: "Generate edits", hi: "\u090F\u0921\u093F\u091F \u091C\u0928\u0930\u0947\u091F \u0915\u0930\u0947\u0902" },
  selectCodingModel: { en: "Select model", hi: "\u092E\u0949\u0921\u0932 \u091A\u0941\u0928\u0947\u0902" },
  codingTaskResult: { en: "Result", hi: "\u092A\u0930\u093F\u0923\u093E\u092E" }
};
function tr11(key, locale) {
  return SHELL_STRINGS2[key]?.[locale] ?? key;
}
var ALL_NAV_IDS = ["chat", "recentChats", "projects", "files", "automations", "apps", "skills", "settings", "cowork", "code", "memory"];
var ADVANCED_NAV_IDS = /* @__PURE__ */ new Set(["cowork", "code", "memory"]);
var ALL_RIGHT_TAB_IDS = ["context", "files", "artifacts", "browser", "builder", "cowork", "code", "codeIntelligence", "approvals", "audit"];
var ADVANCED_RIGHT_TAB_IDS = /* @__PURE__ */ new Set(["cowork", "code", "codeIntelligence", "audit"]);
function visibleNavIds(mode) {
  return mode === "advanced" ? ALL_NAV_IDS : ALL_NAV_IDS.filter((id) => !ADVANCED_NAV_IDS.has(id));
}
function visibleRightTabIds(mode) {
  return mode === "advanced" ? ALL_RIGHT_TAB_IDS : ALL_RIGHT_TAB_IDS.filter((id) => !ADVANCED_RIGHT_TAB_IDS.has(id));
}
function navItems(locale) {
  return [
    { id: "chat", label: tr11("newChat", locale), icon: "\u{1F4AC}" },
    { id: "recentChats", label: tr11("recentChats", locale), icon: "\u{1F552}" },
    { id: "projects", label: tr11("projects", locale), icon: "\u{1F4C1}" },
    { id: "files", label: tr11("files", locale), icon: "\u{1F4C2}" },
    { id: "automations", label: tr11("automations", locale), icon: "\u2699\uFE0F" },
    { id: "apps", label: tr11("apps", locale), icon: "\u{1F4F1}" },
    { id: "skills", label: tr11("skills", locale), icon: "\u{1F9E9}" },
    { id: "cowork", label: tr11("coworkRuns", locale), icon: "\u{1F465}" },
    { id: "code", label: tr11("codeWorkspaces", locale), icon: "\u{1F4BB}" },
    { id: "memory", label: tr11("memory", locale), icon: "\u{1F9E0}" }
  ];
}
function rightTabs(locale) {
  return [
    { id: "context", label: tr11("context", locale) },
    { id: "files", label: tr11("files", locale) },
    { id: "artifacts", label: tr11("artifacts", locale) },
    { id: "browser", label: tr11("browser", locale) },
    { id: "builder", label: tr11("builder", locale) },
    { id: "cowork", label: tr11("cowork", locale) },
    { id: "code", label: tr11("code", locale) },
    { id: "codeIntelligence", label: tr11("codeIntelligence", locale) },
    { id: "approvals", label: tr11("approvals", locale) },
    { id: "audit", label: tr11("audit", locale) }
  ];
}
function Panel({
  title,
  children,
  theme: theme2,
  style
}) {
  const colors = themeColors(theme2);
  return /* @__PURE__ */ jsxs71(
    "div",
    {
      style: {
        border: `1px solid ${colors.border}`,
        borderRadius: tokens.radius.md,
        backgroundColor: colors.surfaceStrong,
        color: colors.text,
        padding: tokens.spacing.md,
        ...style
      },
      children: [
        title && /* @__PURE__ */ jsx79("h3", { style: { margin: `0 0 ${tokens.spacing.md} 0`, fontSize: "14px" }, children: title }),
        children
      ]
    }
  );
}
function DisabledHint2({ message, theme: theme2 }) {
  const colors = themeColors(theme2);
  return /* @__PURE__ */ jsx79("p", { style: { color: colors.textMuted, fontSize: "13px", margin: 0 }, children: message });
}
var LEFT_PANE_MIN = 180;
var LEFT_PANE_MAX = 420;
var RIGHT_PANE_MIN = 220;
var RIGHT_PANE_MAX = 520;
function WorkspaceShell({ logo, fillHeight } = {}) {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const [paletteOpen, setPaletteOpen] = useState40(false);
  const [leftSize, setLeftSize] = useState40(220);
  const [leftCollapsed, setLeftCollapsed] = useState40(false);
  const [rightSize, setRightSize] = useState40(300);
  const [rightCollapsed, setRightCollapsed] = useState40(false);
  const [showSuperAdmin, setShowSuperAdmin] = useState40(false);
  const [showSettings, setShowSettings] = useState40(false);
  const runSelectorRef = useRef18(null);
  const commands = useMemo19(() => buildCommands(shell, shell.locale), [shell, shell.locale, shell.mode, shell.theme]);
  const openPalette = useCallback26(() => setPaletteOpen(true), []);
  const closePalette = useCallback26(() => setPaletteOpen(false), []);
  const toggleTheme = useCallback26(
    () => shell.setTheme(shell.theme === "dark" ? "light" : "dark"),
    [shell]
  );
  const toggleLocale = useCallback26(
    () => shell.setLocale(shell.locale === "hi" ? "en" : "hi"),
    [shell]
  );
  const toggleMode = useCallback26(
    () => shell.setMode(shell.mode === "advanced" ? "normal" : "advanced"),
    [shell]
  );
  const focusRunSelector = useCallback26(() => runSelectorRef.current?.focus(), []);
  const navTo = useCallback26(
    (key) => {
      const items = visibleNavIds(shell.mode);
      const idx = Number(key) - 1;
      if (idx >= 0 && idx < items.length) shell.setActiveNav(items[idx]);
    },
    [shell]
  );
  useWorkspaceShortcuts(
    useMemo19(
      () => ({
        openPalette,
        toggleTheme,
        toggleLocale,
        toggleMode,
        focusRunSelector,
        navTo
      }),
      [openPalette, toggleTheme, toggleLocale, toggleMode, focusRunSelector, navTo]
    )
  );
  return /* @__PURE__ */ jsxs71(
    "div",
    {
      "data-testid": "workspace-shell",
      "data-theme": shell.theme,
      "data-locale": shell.locale,
      style: {
        minHeight: fillHeight ? 0 : "100vh",
        height: fillHeight ? "100%" : void 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.surface,
        color: colors.text,
        fontFamily: tokens.font.family
      },
      children: [
        /* @__PURE__ */ jsxs71("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${tokens.spacing.md}`, borderBottom: `1px solid ${colors.border}` }, "data-testid": "shared-ceo-surface", children: [
          /* @__PURE__ */ jsx79(WorkspaceHeader, { runSelectorRef, onOpenPalette: () => setPaletteOpen(true) }),
          shell.isCeoEligible && shell.ceoService ? /* @__PURE__ */ jsx79(ShikkaPresence, { state: shell.ceoActivityState, locale: shell.locale, size: 36, logo }) : null
        ] }),
        shell.isEmergencyStopped && /* @__PURE__ */ jsx79(
          "div",
          {
            role: "alert",
            style: {
              padding: tokens.spacing.md,
              backgroundColor: tokens.colors.danger,
              color: "#fff",
              textAlign: "center"
            },
            children: tr11("emergencyStopActive", shell.locale)
          }
        ),
        /* @__PURE__ */ jsxs71("div", { style: { display: "flex", flex: 1, overflow: "hidden" }, children: [
          /* @__PURE__ */ jsx79(
            SplitPane,
            {
              position: "left",
              size: leftSize,
              minSize: LEFT_PANE_MIN,
              maxSize: LEFT_PANE_MAX,
              collapsed: leftCollapsed,
              onResize: setLeftSize,
              onToggleCollapse: () => setLeftCollapsed((v) => !v),
              theme: shell.theme,
              "data-testid": "left-pane",
              children: /* @__PURE__ */ jsx79(WorkspaceNav, { collapsed: leftCollapsed, onAccount: () => setShowSettings(true), onSuperAdmin: () => setShowSuperAdmin(true) })
            }
          ),
          /* @__PURE__ */ jsxs71("main", { style: { flex: 1, display: "flex", minWidth: 0, overflow: "hidden" }, children: [
            /* @__PURE__ */ jsx79("div", { style: { flex: 1, minWidth: LEFT_PANE_MIN, overflow: "auto", padding: tokens.spacing.lg }, children: /* @__PURE__ */ jsx79(WorkspaceProductSurface, {}) }),
            /* @__PURE__ */ jsx79(
              SplitPane,
              {
                position: "right",
                size: rightSize,
                minSize: RIGHT_PANE_MIN,
                maxSize: RIGHT_PANE_MAX,
                collapsed: rightCollapsed,
                onResize: setRightSize,
                onToggleCollapse: () => setRightCollapsed((v) => !v),
                theme: shell.theme,
                "data-testid": "right-pane",
                children: /* @__PURE__ */ jsx79(WorkspaceRightPanel, {})
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx79(WorkspaceStatusBar, {}),
        /* @__PURE__ */ jsx79(
          CommandPalette,
          {
            isOpen: paletteOpen,
            onClose: closePalette,
            commands,
            theme: shell.theme
          }
        ),
        showSuperAdmin && /* @__PURE__ */ jsx79(SuperAdminPanel, { onClose: () => setShowSuperAdmin(false) }),
        showSettings && /* @__PURE__ */ jsx79(
          "div",
          {
            role: "dialog",
            "aria-modal": "true",
            style: {
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.35)",
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: tokens.spacing.lg
            },
            onClick: () => setShowSettings(false),
            children: /* @__PURE__ */ jsxs71(
              "div",
              {
                style: {
                  width: "min(720px, 90vw)",
                  height: "min(640px, 80vh)",
                  backgroundColor: colors.surfaceStrong,
                  borderRadius: tokens.radius.lg,
                  border: `1px solid ${colors.border}`,
                  overflow: "auto",
                  padding: tokens.spacing.lg
                },
                onClick: (e) => e.stopPropagation(),
                children: [
                  /* @__PURE__ */ jsxs71("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: tokens.spacing.md }, children: [
                    /* @__PURE__ */ jsx79("h2", { style: { margin: 0 }, children: tr11("settings", shell.locale) }),
                    /* @__PURE__ */ jsx79("button", { type: "button", onClick: () => setShowSettings(false), style: { border: "none", background: "transparent", cursor: "pointer", fontSize: "18px", color: colors.text }, children: "\xD7" })
                  ] }),
                  /* @__PURE__ */ jsx79(
                    SettingsPanel,
                    {
                      readOnly: !shell.isCeoEligible,
                      token: shell.token,
                      agentSkillsService: shell.agentSkillsService,
                      locale: shell.locale,
                      integrationsPanelSlot: /* @__PURE__ */ jsx79(SystemIntegrationsPanel, { token: shell.token, locale: shell.locale, theme: shell.theme })
                    }
                  )
                ]
              }
            )
          }
        )
      ]
    }
  );
}
function buildCommands(shell, locale) {
  const allowedNav = new Set(visibleNavIds(shell.mode));
  const allowedRight = new Set(visibleRightTabIds(shell.mode));
  const nav = navItems(locale).filter((item) => allowedNav.has(item.id)).map((item) => ({
    id: `nav-${item.id}`,
    label: `${tr11("navigate", locale) ?? "Go to"} ${item.label}`,
    keywords: [item.id, item.label],
    action: () => shell.setActiveNav(item.id)
  }));
  const right = rightTabs(locale).filter((tab) => allowedRight.has(tab.id)).map((tab) => ({
    id: `right-${tab.id}`,
    label: `${tr11("openPanel", locale) ?? "Open"} ${tab.label}`,
    keywords: [tab.id, tab.label, "panel", "tab"],
    action: () => shell.setRightTab(tab.id)
  }));
  const actions = [
    {
      id: "toggle-theme",
      label: tr11("toggleTheme", locale),
      keywords: ["theme", "dark", "light"],
      action: () => shell.setTheme(shell.theme === "dark" ? "light" : "dark")
    },
    {
      id: "toggle-locale",
      label: tr11("toggleLocale", locale),
      keywords: ["language", "hindi", "english", "locale"],
      action: () => shell.setLocale(shell.locale === "hi" ? "en" : "hi")
    },
    {
      id: "toggle-mode",
      label: shell.mode === "advanced" ? tr11("switchToNormal", locale) : tr11("switchToAdvanced", locale),
      keywords: ["mode", "advanced", "normal", "simple"],
      action: () => shell.setMode(shell.mode === "advanced" ? "normal" : "advanced")
    },
    {
      id: "run-tests",
      label: tr11("runTests", locale),
      keywords: ["test", "jest", "vitest"],
      action: () => void shell.runTests()
    },
    {
      id: "run-build",
      label: tr11("runBuild", locale),
      keywords: ["build", "compile", "typecheck"],
      action: () => void shell.runBuild()
    },
    {
      id: "pause-task",
      label: tr11("pause", locale),
      keywords: ["pause", "task"],
      action: () => shell.selectedTask && void shell.pauseTask(shell.selectedTask.taskId)
    },
    {
      id: "resume-task",
      label: tr11("resume", locale),
      keywords: ["resume", "task"],
      action: () => shell.selectedTask && void shell.resumeTask(shell.selectedTask.taskId)
    },
    {
      id: "cancel-task",
      label: tr11("cancel", locale),
      keywords: ["cancel", "task"],
      action: () => shell.selectedTask && void shell.cancelTask(shell.selectedTask.taskId)
    },
    {
      id: "open-approvals",
      label: tr11("approvals", locale),
      keywords: ["approvals", "pending", "queue"],
      action: () => shell.setRightTab("approvals")
    }
  ];
  return [...nav, ...right, ...actions];
}
function WorkspaceHeader({
  runSelectorRef,
  onOpenPalette
}) {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const handleProjectChange = (e) => {
    const id = e.target.value;
    if (id) void shell.projectRuntime?.selectProject(id);
  };
  const handleRunChange = (e) => {
    const action = e.target.value;
    e.currentTarget.value = "";
    if (!action) return;
    if (action === "tests") void shell.runTests();
    if (action === "build") void shell.runBuild();
    if (action === "pause" && shell.selectedTask) void shell.pauseTask(shell.selectedTask.taskId);
    if (action === "resume" && shell.selectedTask) void shell.resumeTask(shell.selectedTask.taskId);
    if (action === "cancel" && shell.selectedTask) void shell.cancelTask(shell.selectedTask.taskId);
  };
  return /* @__PURE__ */ jsxs71(
    "header",
    {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: tokens.spacing.md,
        padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: colors.surfaceStrong
      },
      children: [
        /* @__PURE__ */ jsxs71("div", { children: [
          /* @__PURE__ */ jsx79("h1", { style: { margin: 0, fontSize: "18px" }, children: "Smart Shikka Workspace" }),
          /* @__PURE__ */ jsx79("p", { style: { margin: "4px 0 0", fontSize: "12px", color: colors.textMuted }, children: shell.currentContext?.projectId ? `Project: ${shell.currentContext.projectId}` : tr11("noProjectSelected", shell.locale) })
        ] }),
        /* @__PURE__ */ jsxs71("div", { style: { display: "flex", alignItems: "center", gap: tokens.spacing.sm }, children: [
          /* @__PURE__ */ jsxs71(
            "select",
            {
              "aria-label": tr11("selectProject", shell.locale),
              value: shell.currentContext?.projectId ?? "",
              onChange: handleProjectChange,
              disabled: shell.projectRuntime?.loading || !shell.projectRuntime,
              style: {
                padding: "6px 10px",
                borderRadius: tokens.radius.md,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.surface,
                color: colors.text
              },
              children: [
                /* @__PURE__ */ jsx79("option", { value: "", children: tr11("selectProject", shell.locale) }),
                shell.projectRuntime?.allowedProjects.map((p) => /* @__PURE__ */ jsx79("option", { value: p.projectId, children: p.displayName }, p.projectId))
              ]
            }
          ),
          /* @__PURE__ */ jsxs71(
            "select",
            {
              ref: runSelectorRef,
              "aria-label": tr11("runTask", shell.locale),
              value: "",
              onChange: handleRunChange,
              disabled: !shell.codeService && !shell.selectedTask,
              style: {
                padding: "6px 10px",
                borderRadius: tokens.radius.md,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.surface,
                color: colors.text
              },
              children: [
                /* @__PURE__ */ jsx79("option", { value: "", children: tr11("runTask", shell.locale) }),
                /* @__PURE__ */ jsx79("option", { value: "tests", children: tr11("runTests", shell.locale) }),
                /* @__PURE__ */ jsx79("option", { value: "build", children: tr11("runBuild", shell.locale) }),
                shell.selectedTask && /* @__PURE__ */ jsxs71(Fragment9, { children: [
                  /* @__PURE__ */ jsx79("option", { value: "pause", children: tr11("pause", shell.locale) }),
                  /* @__PURE__ */ jsx79("option", { value: "resume", children: tr11("resume", shell.locale) }),
                  /* @__PURE__ */ jsx79("option", { value: "cancel", children: tr11("cancel", shell.locale) })
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsx79(Button, { variant: "secondary", onClick: () => shell.setMode(shell.mode === "advanced" ? "normal" : "advanced"), children: shell.mode === "advanced" ? tr11("normalMode", shell.locale) : tr11("advancedMode", shell.locale) }),
          /* @__PURE__ */ jsx79(Button, { variant: "secondary", onClick: () => shell.setLocale(shell.locale === "hi" ? "en" : "hi"), children: shell.locale === "hi" ? "EN" : "\u0939\u093F\u0902" }),
          /* @__PURE__ */ jsx79(Button, { variant: "secondary", onClick: () => shell.setTheme(shell.theme === "dark" ? "light" : "dark"), children: shell.theme === "dark" ? tr11("light", shell.locale) : tr11("dark", shell.locale) }),
          /* @__PURE__ */ jsx79(Button, { variant: "secondary", onClick: onOpenPalette, "aria-label": tr11("commandPalette", shell.locale), children: "\u2318K" })
        ] })
      ]
    }
  );
}
function WorkspaceNav({
  collapsed,
  onAccount,
  onSuperAdmin
}) {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const allowed = useMemo19(() => new Set(visibleNavIds(shell.mode)), [shell.mode]);
  const items = navItems(shell.locale).filter((i) => allowed.has(i.id));
  return /* @__PURE__ */ jsxs71(
    "nav",
    {
      style: {
        width: "100%",
        height: "100%",
        borderRight: "none",
        backgroundColor: colors.surfaceStrong,
        display: "flex",
        flexDirection: "column",
        padding: tokens.spacing.md,
        gap: tokens.spacing.xs
      },
      children: [
        /* @__PURE__ */ jsxs71("div", { style: { display: "flex", alignItems: "center", gap: tokens.spacing.sm, padding: `${tokens.spacing.sm} ${tokens.spacing.md}` }, children: [
          /* @__PURE__ */ jsx79("span", { style: { fontSize: "20px" }, children: "\u{1F6E1}\uFE0F" }),
          !collapsed && /* @__PURE__ */ jsx79("span", { style: { fontWeight: 600, fontSize: "15px" }, children: "Smart Shikka" })
        ] }),
        /* @__PURE__ */ jsx79("div", { style: { flex: 1, overflow: "auto" }, children: items.map((item) => {
          const active = shell.activeNav === item.id;
          return /* @__PURE__ */ jsxs71(
            "button",
            {
              type: "button",
              title: item.label,
              onClick: () => shell.setActiveNav(item.id),
              style: {
                display: "flex",
                alignItems: "center",
                justifyContent: collapsed ? "center" : "flex-start",
                gap: tokens.spacing.sm,
                width: "100%",
                padding: collapsed ? tokens.spacing.sm : `${tokens.spacing.sm} ${tokens.spacing.md}`,
                borderRadius: tokens.radius.md,
                border: "none",
                backgroundColor: active ? shell.theme === "dark" ? tokens.colors.primary : colors.warmAccent : "transparent",
                color: active ? "#fff" : colors.text,
                cursor: "pointer",
                textAlign: "left"
              },
              children: [
                /* @__PURE__ */ jsx79("span", { style: { fontSize: "18px" }, children: item.icon }),
                !collapsed && /* @__PURE__ */ jsx79("span", { style: { fontSize: "13px" }, children: item.label })
              ]
            },
            item.id
          );
        }) }),
        /* @__PURE__ */ jsx79(
          AccountMenu,
          {
            locale: shell.locale,
            theme: shell.theme,
            displayName: shell.currentContext?.ownerId,
            email: shell.currentContext?.ownerId,
            isSuperAdmin: shell.isSuperAdmin,
            onSettings: onAccount,
            onLanguage: () => shell.setLocale(shell.locale === "hi" ? "en" : "hi"),
            onHelp: () => shell.setActiveNav("chat"),
            onDesktop: () => shell.setActiveNav("chat"),
            onExtension: () => shell.setActiveNav("chat"),
            onAbout: () => shell.setActiveNav("chat"),
            onLogout: () => shell.setActiveNav("chat"),
            onSuperAdmin
          }
        )
      ]
    }
  );
}
function WorkspaceProductSurface() {
  const shell = useWorkspaceShell();
  const allowed = visibleNavIds(shell.mode);
  const nav = allowed.includes(shell.activeNav) ? shell.activeNav : allowed[0] ?? "chat";
  switch (nav) {
    case "chat":
    case "recentChats":
      return /* @__PURE__ */ jsx79(ChatWorkspace, {});
    case "projects":
      return /* @__PURE__ */ jsx79(ProjectWorkspace, {});
    case "cowork":
      return /* @__PURE__ */ jsx79(CoworkWorkspace, {});
    case "code":
    case "files":
      return /* @__PURE__ */ jsx79(CodeWorkspace, {});
    case "memory":
      return /* @__PURE__ */ jsx79(MemoryWorkspace, {});
    case "automations":
      return /* @__PURE__ */ jsx79(AutomationsWorkspace, {});
    case "apps":
      return /* @__PURE__ */ jsx79(AppsWorkspace, {});
    case "skills":
      return /* @__PURE__ */ jsx79(SkillsWorkspace, {});
    case "settings":
      return /* @__PURE__ */ jsx79(SettingsWorkspace, {});
    default:
      return /* @__PURE__ */ jsx79(ChatWorkspace, {});
  }
}
function ProjectWorkspace() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const runtime = shell.projectRuntime;
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("projects", shell.locale), children: !runtime ? /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: tr11("selectProject", shell.locale) }) : runtime.loading ? /* @__PURE__ */ jsx79("p", { style: { color: colors.textMuted }, children: "Loading\u2026" }) : /* @__PURE__ */ jsxs71("div", { children: [
    runtime.allowedProjects.map((p) => /* @__PURE__ */ jsx79(
      Card,
      {
        title: p.displayName,
        subtitle: p.projectId,
        onClick: () => void runtime.selectProject(p.projectId),
        style: { marginBottom: tokens.spacing.md, cursor: "pointer" },
        children: /* @__PURE__ */ jsx79(Fragment9, {})
      },
      p.projectId
    )),
    runtime.allowedProjects.length === 0 && /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: tr11("noProjectSelected", shell.locale) })
  ] }) });
}
function CoworkWorkspace() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const viewButtons = [
    { id: "inbox", label: "Inbox" },
    { id: "active", label: "Active" },
    { id: "approval", label: "Approval" },
    { id: "completed", label: "Completed" },
    { id: "failed", label: "Failed" },
    { id: "paused", label: "Paused" }
  ];
  return /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.lg, height: "100%" }, children: [
    /* @__PURE__ */ jsx79("div", { style: { width: "320px", flexShrink: 0 }, children: /* @__PURE__ */ jsxs71(Panel, { theme: shell.theme, title: tr11("coworkRuns", shell.locale), children: [
      /* @__PURE__ */ jsx79("div", { style: { display: "flex", flexWrap: "wrap", gap: tokens.spacing.sm, marginBottom: tokens.spacing.md }, children: viewButtons.map((v) => /* @__PURE__ */ jsx79(
        "button",
        {
          type: "button",
          onClick: () => shell.setView(v.id),
          style: {
            padding: "4px 10px",
            borderRadius: tokens.radius.pill,
            border: "none",
            backgroundColor: shell.view === v.id ? tokens.colors.primary : "transparent",
            color: shell.view === v.id ? "#fff" : colors.text,
            cursor: "pointer"
          },
          children: v.label
        },
        v.id
      )) }),
      /* @__PURE__ */ jsxs71("div", { style: { display: "flex", flexDirection: "column", gap: tokens.spacing.sm }, children: [
        shell.tasks.map((task) => /* @__PURE__ */ jsx79(
          Card,
          {
            title: task.title,
            subtitle: task.taskId,
            onClick: () => shell.setSelectedTaskId(task.taskId),
            style: {
              cursor: "pointer",
              borderColor: shell.selectedTask?.taskId === task.taskId ? tokens.colors.primary : void 0
            },
            headerActions: /* @__PURE__ */ jsx79(Badge, { variant: task.view === "failed" ? "danger" : "primary", children: task.view }),
            children: /* @__PURE__ */ jsx79(Fragment9, {})
          },
          task.taskId
        )),
        shell.tasks.length === 0 && /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: tr11("noTaskSelected", shell.locale) })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx79("div", { style: { flex: 1, minWidth: 0 }, children: shell.selectedTask ? /* @__PURE__ */ jsx79(TaskDetail, { task: shell.selectedTask }) : /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: tr11("noTaskSelected", shell.locale) }) })
  ] });
}
function TaskDetail({ task }) {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const disabled = !shell.isOwner || shell.isEmergencyStopped;
  return /* @__PURE__ */ jsxs71(Panel, { theme: shell.theme, title: task.title, children: [
    /* @__PURE__ */ jsxs71("div", { style: { fontSize: "13px", color: colors.textMuted, marginBottom: tokens.spacing.md }, children: [
      tr11("plan", shell.locale),
      ": ",
      task.currentStageLabel,
      " | ",
      tr11("stages", shell.locale),
      ": ",
      task.currentStageId,
      " | ",
      tr11("evidence", shell.locale),
      ": ",
      task.evidenceCount
    ] }),
    /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.sm, marginBottom: tokens.spacing.md }, children: [
      /* @__PURE__ */ jsx79(Button, { variant: "secondary", onClick: () => shell.pauseTask(task.taskId), disabled: disabled || !task.canCancel || task.isPaused, children: tr11("pause", shell.locale) }),
      /* @__PURE__ */ jsx79(Button, { variant: "secondary", onClick: () => shell.resumeTask(task.taskId), disabled: disabled || !task.canResume, children: tr11("resume", shell.locale) }),
      /* @__PURE__ */ jsx79(Button, { variant: "danger", onClick: () => shell.cancelTask(task.taskId), disabled: disabled || !task.canCancel, children: tr11("cancel", shell.locale) })
    ] }),
    /* @__PURE__ */ jsxs71("div", { style: { fontSize: "13px", color: colors.textMuted }, children: [
      /* @__PURE__ */ jsxs71("p", { children: [
        tr11("agents", shell.locale),
        ": ",
        task.skillId
      ] }),
      /* @__PURE__ */ jsxs71("p", { children: [
        tr11("dependencies", shell.locale),
        ": \u2014"
      ] }),
      /* @__PURE__ */ jsxs71("p", { children: [
        tr11("handoffs", shell.locale),
        ": \u2014"
      ] }),
      /* @__PURE__ */ jsxs71("p", { children: [
        tr11("result", shell.locale),
        ": ",
        task.taskStatus
      ] })
    ] })
  ] });
}
function CodeWorkspace() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const [base, setBase] = useState40("HEAD~1");
  const [head, setHead] = useState40("HEAD");
  const [patchTitle, setPatchTitle] = useState40("");
  const [patchDescription, setPatchDescription] = useState40("");
  const [patchApprovalId, setPatchApprovalId] = useState40();
  const [patchTokens, setPatchTokens] = useState40();
  const [codingModel, setCodingModel] = useState40("auto");
  const [codingDescription, setCodingDescription] = useState40("");
  const [codingTaskResult, setCodingTaskResult] = useState40(void 0);
  const [codingTaskError, setCodingTaskError] = useState40(null);
  const [codingTaskLoading, setCodingTaskLoading] = useState40(false);
  const handleRepoChange = (e) => {
    const id = e.target.value;
    if (id) void shell.selectRepository(id);
  };
  const handleRequestApproval = async () => {
    if (!shell.diffPreview) return;
    const id = await shell.requestPatchApproval({
      title: patchTitle || "Untitled patch",
      description: patchDescription,
      diff: shell.diffPreview.diff,
      files: shell.openFiles.map((f) => f.path)
    });
    if (id) {
      setPatchApprovalId(id);
      setPatchTokens(void 0);
    }
  };
  const handleApprovePatch = async () => {
    if (!patchApprovalId) return;
    const tokens2 = await shell.approvePatch(patchApprovalId);
    if (tokens2) setPatchTokens(tokens2);
  };
  const handleApplyPatch = async () => {
    if (!patchApprovalId || !patchTokens) return;
    await shell.applyPatch(patchApprovalId, patchTokens.approvalToken);
  };
  const handlePublishPatch = async () => {
    if (!patchApprovalId || !patchTokens) return;
    await shell.publishPatch(patchApprovalId, patchTokens.publishToken);
  };
  const handleRollback = async () => {
    if (!patchApprovalId || !patchTokens) return;
    await shell.rollback(patchApprovalId, patchTokens.rollbackToken);
  };
  const handleGenerateEdits = async () => {
    if (!shell.codeService) return;
    setCodingTaskLoading(true);
    setCodingTaskError(null);
    setCodingTaskResult(void 0);
    try {
      const result = await shell.codeService.generateTask({
        description: codingDescription,
        files: shell.openFiles.map((f) => f.path),
        logicalProfile: codingModel,
        productMode: "code"
      });
      setCodingTaskResult(result);
      if (!result.success) {
        setCodingTaskError(safeErrorCode(result.errorCode ?? result.errorMessage));
      }
    } catch (err) {
      setCodingTaskError(errorToCode(err));
    } finally {
      setCodingTaskLoading(false);
    }
  };
  const serviceMissing = !shell.codeService;
  const desktopExecution = shell.codeService?.executionAuthority === "desktop";
  return /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.lg, height: "100%" }, children: [
    /* @__PURE__ */ jsxs71("div", { style: { width: "260px", flexShrink: 0, display: "flex", flexDirection: "column", gap: tokens.spacing.md }, children: [
      /* @__PURE__ */ jsxs71(Panel, { theme: shell.theme, title: tr11("selectRepository", shell.locale), children: [
        /* @__PURE__ */ jsxs71(
          "select",
          {
            value: shell.selectedRepositoryId ?? "",
            onChange: handleRepoChange,
            disabled: serviceMissing || shell.codeLoading,
            style: { width: "100%", padding: "6px", borderRadius: tokens.radius.md },
            children: [
              /* @__PURE__ */ jsx79("option", { value: "", children: tr11("selectRepository", shell.locale) }),
              shell.codeRepositories.map((r) => /* @__PURE__ */ jsx79("option", { value: r.id, children: r.name }, r.id))
            ]
          }
        ),
        serviceMissing && /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: tr11("codeServiceMissing", shell.locale) })
      ] }),
      /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("searchFiles", shell.locale), children: /* @__PURE__ */ jsx79(
        "input",
        {
          type: "text",
          placeholder: tr11("searchFiles", shell.locale),
          disabled: serviceMissing || !shell.selectedRepositoryId,
          onKeyDown: (e) => {
            if (e.key === "Enter") void shell.searchCode(e.target.value);
          },
          style: { width: "100%", padding: "6px", borderRadius: tokens.radius.md }
        }
      ) }),
      /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("searchSymbols", shell.locale), children: /* @__PURE__ */ jsx79(
        "input",
        {
          type: "text",
          placeholder: tr11("searchSymbols", shell.locale),
          disabled: serviceMissing || !shell.selectedRepositoryId,
          onKeyDown: (e) => {
            if (e.key === "Enter") void shell.searchSymbols(e.target.value);
          },
          style: { width: "100%", padding: "6px", borderRadius: tokens.radius.md }
        }
      ) }),
      /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("fileTree", shell.locale), style: { flex: 1, overflow: "auto" }, children: shell.codeTree.length === 0 ? /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: serviceMissing ? tr11("codeServiceMissing", shell.locale) : tr11("noRepositories", shell.locale) }) : /* @__PURE__ */ jsx79(FileTree2, { nodes: shell.codeTree, onOpen: shell.openFile }) })
    ] }),
    /* @__PURE__ */ jsxs71("div", { style: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: tokens.spacing.md }, children: [
      /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("openFiles", shell.locale), children: shell.openFiles.length === 0 ? /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "No file open." }) : /* @__PURE__ */ jsx79("div", { style: { display: "flex", gap: tokens.spacing.sm, flexWrap: "wrap" }, children: shell.openFiles.map((f) => /* @__PURE__ */ jsx79(
        "button",
        {
          type: "button",
          onClick: () => shell.setActiveNav("code"),
          style: {
            padding: "4px 8px",
            borderRadius: tokens.radius.sm,
            border: "none",
            backgroundColor: f.path === shell.activeFilePath ? tokens.colors.primary : colors.surface,
            color: f.path === shell.activeFilePath ? "#fff" : colors.text,
            cursor: "pointer"
          },
          children: f.path
        },
        f.path
      )) }) }),
      /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, style: { flex: 1, overflow: "auto" }, children: shell.activeFilePath ? /* @__PURE__ */ jsx79("pre", { style: { margin: 0, fontSize: "13px", overflow: "auto" }, children: shell.openFiles.find((f) => f.path === shell.activeFilePath)?.content }) : /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "Select a file from the tree." }) }),
      /* @__PURE__ */ jsxs71(Panel, { theme: shell.theme, title: tr11("codingModel", shell.locale), children: [
        /* @__PURE__ */ jsx79("div", { style: { display: "flex", gap: tokens.spacing.sm, marginBottom: tokens.spacing.md }, children: /* @__PURE__ */ jsxs71(
          "select",
          {
            value: codingModel,
            onChange: (e) => setCodingModel(e.target.value),
            disabled: serviceMissing || codingTaskLoading,
            style: { padding: "6px", borderRadius: tokens.radius.md },
            "aria-label": tr11("selectCodingModel", shell.locale),
            children: [
              /* @__PURE__ */ jsx79("option", { value: "fast", children: "Fast" }),
              /* @__PURE__ */ jsx79("option", { value: "reasoning", children: "Reasoning" }),
              /* @__PURE__ */ jsx79("option", { value: "auto", children: "Auto" })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx79(
          "textarea",
          {
            value: codingDescription,
            onChange: (e) => setCodingDescription(e.target.value),
            placeholder: tr11("codingTaskDescription", shell.locale),
            disabled: serviceMissing || codingTaskLoading,
            style: { width: "100%", padding: "6px", marginBottom: tokens.spacing.sm, borderRadius: tokens.radius.md, minHeight: "60px" }
          }
        ),
        /* @__PURE__ */ jsx79(Button, { onClick: () => void handleGenerateEdits(), disabled: serviceMissing || codingTaskLoading || !codingDescription.trim(), children: tr11("generateEdits", shell.locale) }),
        codingTaskLoading && /* @__PURE__ */ jsx79("p", { style: { color: colors.textMuted, fontSize: "13px" }, children: "Generating\u2026" }),
        codingTaskError && /* @__PURE__ */ jsxs71("p", { role: "alert", style: { color: tokens.colors.danger, fontSize: "13px" }, children: [
          safeErrorText(codingTaskError, shell.locale).title,
          " ",
          /* @__PURE__ */ jsx79("code", { children: codingTaskError })
        ] }),
        codingTaskResult && !codingTaskError && /* @__PURE__ */ jsxs71("div", { style: { marginTop: tokens.spacing.sm, fontSize: "13px" }, children: [
          /* @__PURE__ */ jsxs71("p", { style: { color: colors.textMuted }, children: [
            tr11("codingTaskResult", shell.locale),
            ": ",
            codingTaskResult.success ? "success" : "failed",
            " \u2014 ",
            codingTaskResult.status ?? "unknown",
            codingTaskResult.realExecutionPerformed ? " (real execution)" : ""
          ] }),
          codingTaskResult.handoffRequired && /* @__PURE__ */ jsx79("p", { style: { color: colors.textMuted }, children: "Code execution requires the trusted Desktop workspace. This Web/Extension surface remains read-only." }),
          codingTaskResult.executionAuthority === "desktop" && codingTaskResult.status === "approval_required" && /* @__PURE__ */ jsx79("p", { style: { color: colors.textMuted }, children: "The exact patch is ready for canonical owner approval before Desktop applies it." }),
          codingTaskResult.changes && /* @__PURE__ */ jsx79("ul", { style: { listStyle: "none", padding: 0, margin: 0 }, children: codingTaskResult.changes.map((edit, idx) => /* @__PURE__ */ jsxs71("li", { style: { color: colors.text }, children: [
            edit.operation,
            ": ",
            edit.path
          ] }, idx)) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs71(Panel, { theme: shell.theme, title: tr11("diffPreview", shell.locale), children: [
        /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.sm, marginBottom: tokens.spacing.md }, children: [
          /* @__PURE__ */ jsx79("input", { value: base, onChange: (e) => setBase(e.target.value), placeholder: "base", disabled: serviceMissing, style: { padding: "6px" } }),
          /* @__PURE__ */ jsx79("input", { value: head, onChange: (e) => setHead(e.target.value), placeholder: "head", disabled: serviceMissing, style: { padding: "6px" } }),
          /* @__PURE__ */ jsx79(Button, { onClick: () => void shell.loadDiff(base, head), disabled: serviceMissing || shell.codeLoading, children: tr11("diffPreview", shell.locale) })
        ] }),
        shell.diffPreview ? /* @__PURE__ */ jsx79("pre", { style: { fontSize: "12px", backgroundColor: colors.surface, padding: tokens.spacing.md, borderRadius: tokens.radius.md }, children: shell.diffPreview.diff }) : /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "No diff loaded." })
      ] }),
      /* @__PURE__ */ jsxs71(Panel, { theme: shell.theme, title: tr11("patchRequest", shell.locale), children: [
        /* @__PURE__ */ jsx79(
          "input",
          {
            value: patchTitle,
            onChange: (e) => setPatchTitle(e.target.value),
            placeholder: "Patch title",
            disabled: serviceMissing || !shell.diffPreview,
            style: { width: "100%", padding: "6px", marginBottom: tokens.spacing.sm, borderRadius: tokens.radius.md }
          }
        ),
        /* @__PURE__ */ jsx79(
          "textarea",
          {
            value: patchDescription,
            onChange: (e) => setPatchDescription(e.target.value),
            placeholder: "Patch description",
            disabled: serviceMissing || !shell.diffPreview,
            style: { width: "100%", padding: "6px", marginBottom: tokens.spacing.sm, borderRadius: tokens.radius.md, minHeight: "60px" }
          }
        ),
        /* @__PURE__ */ jsx79(Button, { onClick: handleRequestApproval, disabled: serviceMissing || !shell.diffPreview, children: tr11("requestApproval", shell.locale) }),
        /* @__PURE__ */ jsx79(Button, { onClick: handleApprovePatch, disabled: serviceMissing || !patchApprovalId || !shell.isOwner || !!patchTokens, children: tr11("approve", shell.locale) }),
        /* @__PURE__ */ jsx79(Button, { onClick: handleApplyPatch, disabled: !desktopExecution || !patchApprovalId || !patchTokens || !shell.isOwner, children: tr11("apply", shell.locale) }),
        /* @__PURE__ */ jsx79(Button, { onClick: handlePublishPatch, disabled: !desktopExecution || !patchApprovalId || !patchTokens || !shell.isOwner, children: tr11("publish", shell.locale) }),
        !desktopExecution && /* @__PURE__ */ jsx79(
          DisabledHint2,
          {
            theme: shell.theme,
            message: "Apply, publish, rollback, tests, and builds require the trusted Desktop workspace."
          }
        ),
        shell.patchRequest && /* @__PURE__ */ jsxs71("p", { style: { color: colors.textMuted, fontSize: "13px" }, children: [
          "Patch requested: ",
          shell.patchRequest.title
        ] })
      ] }),
      /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.sm }, children: [
        /* @__PURE__ */ jsx79(Button, { onClick: () => void shell.runTests(), disabled: !desktopExecution || shell.codeLoading, children: tr11("runTests", shell.locale) }),
        /* @__PURE__ */ jsx79(Button, { onClick: () => void shell.runBuild(), disabled: !desktopExecution || shell.codeLoading, children: tr11("runBuild", shell.locale) }),
        /* @__PURE__ */ jsx79(Button, { variant: "danger", onClick: () => void handleRollback(), disabled: !desktopExecution || !patchApprovalId || !patchTokens || !shell.isOwner, children: tr11("rollback", shell.locale) })
      ] }),
      shell.testBuildResult && /* @__PURE__ */ jsxs71(Panel, { theme: shell.theme, title: `${shell.testBuildResult.command} (${shell.testBuildResult.exitCode})`, children: [
        /* @__PURE__ */ jsx79("pre", { style: { fontSize: "12px" }, children: shell.testBuildResult.stdout }),
        shell.testBuildResult.stderr && /* @__PURE__ */ jsx79("pre", { style: { color: tokens.colors.danger, fontSize: "12px" }, children: shell.testBuildResult.stderr })
      ] })
    ] })
  ] });
}
function FileTree2({ nodes, onOpen, depth = 0 }) {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  return /* @__PURE__ */ jsx79("ul", { style: { listStyle: "none", paddingLeft: depth === 0 ? 0 : tokens.spacing.md, margin: 0 }, children: nodes.map((node) => /* @__PURE__ */ jsx79("li", { children: node.type === "directory" ? /* @__PURE__ */ jsxs71(Fragment9, { children: [
    /* @__PURE__ */ jsxs71("span", { style: { color: colors.textMuted, fontSize: "13px" }, children: [
      "\u{1F4C1} ",
      node.name
    ] }),
    node.children && /* @__PURE__ */ jsx79(FileTree2, { nodes: node.children, onOpen, depth: depth + 1 })
  ] }) : /* @__PURE__ */ jsxs71(
    "button",
    {
      type: "button",
      onClick: () => onOpen(node.path),
      style: {
        background: "none",
        border: "none",
        color: colors.text,
        cursor: "pointer",
        fontSize: "13px",
        padding: 0
      },
      children: [
        "\u{1F4C4} ",
        node.name
      ]
    }
  ) }, node.path)) });
}
function MemoryWorkspace() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const serviceMissing = !shell.memoryService;
  return /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.lg, height: "100%" }, children: [
    /* @__PURE__ */ jsx79("div", { style: { flex: 1 }, children: /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("memory", shell.locale), children: serviceMissing ? /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: tr11("memoryServiceMissing", shell.locale) }) : shell.memoryLoading ? /* @__PURE__ */ jsx79("p", { style: { color: colors.textMuted }, children: "Loading\u2026" }) : /* @__PURE__ */ jsxs71("div", { style: { display: "flex", flexDirection: "column", gap: tokens.spacing.md }, children: [
      shell.memories.map((m) => /* @__PURE__ */ jsx79(MemoryRow, { memory: m }, m.id)),
      shell.memories.length === 0 && /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: tr11("noMemories", shell.locale) })
    ] }) }) }),
    /* @__PURE__ */ jsx79("div", { style: { width: "360px", flexShrink: 0 }, children: /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("context", shell.locale), children: !shell.memoryContext ? /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: tr11("noContext", shell.locale) }) : /* @__PURE__ */ jsx79(ContextSummary, { context: shell.memoryContext }) }) })
  ] });
}
function MemoryRow({ memory }) {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  return /* @__PURE__ */ jsxs71("div", { style: { border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, padding: tokens.spacing.md }, children: [
    /* @__PURE__ */ jsx79("p", { style: { margin: `0 0 ${tokens.spacing.sm} 0`, fontSize: "13px" }, children: memory.text }),
    /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.sm }, children: [
      /* @__PURE__ */ jsx79(Button, { variant: "secondary", onClick: () => memory.pinned ? shell.unpinMemory(memory.id) : shell.pinMemory(memory.id), disabled: !shell.memoryService, children: memory.pinned ? tr11("unpin", shell.locale) : tr11("pinned", shell.locale) }),
      /* @__PURE__ */ jsx79(Button, { variant: "danger", onClick: () => shell.deleteMemory(memory.id), disabled: !shell.memoryService, children: tr11("delete", shell.locale) })
    ] })
  ] });
}
function ContextSummary({ context }) {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  return /* @__PURE__ */ jsxs71("div", { style: { fontSize: "13px", color: colors.textMuted }, children: [
    /* @__PURE__ */ jsxs71("p", { children: [
      "Project: ",
      context.projectId ?? "\u2014"
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      "Skill: ",
      context.skillId ?? "\u2014"
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      tr11("lastIndexedCommit", shell.locale),
      ": ",
      context.lastIndexedCommit ?? "\u2014"
    ] }),
    context.stale && /* @__PURE__ */ jsx79("p", { style: { color: tokens.colors.warning }, children: tr11("staleWarning", shell.locale) }),
    /* @__PURE__ */ jsxs71("p", { children: [
      tr11("includedFiles", shell.locale),
      ": ",
      context.includedFiles.length
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      tr11("includedMemories", shell.locale),
      ": ",
      context.includedMemories.length
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      tr11("retrievedSymbols", shell.locale),
      ": ",
      context.retrievedSymbols.length
    ] }),
    /* @__PURE__ */ jsx79(Button, { onClick: () => void shell.exportMemories(), disabled: !shell.memoryService, style: { marginTop: tokens.spacing.md }, children: tr11("export", shell.locale) })
  ] });
}
function AutomationsWorkspace() {
  const shell = useWorkspaceShell();
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("automations", shell.locale), children: /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: tr11("disabledReason", shell.locale) }) });
}
function SettingsWorkspace() {
  const shell = useWorkspaceShell();
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("settings", shell.locale), children: /* @__PURE__ */ jsx79(
    SettingsPanel,
    {
      readOnly: !shell.isCeoEligible,
      token: shell.token,
      locale: shell.locale,
      integrationsPanelSlot: /* @__PURE__ */ jsx79(SystemIntegrationsPanel, { token: shell.token, locale: shell.locale, theme: shell.theme })
    }
  ) });
}
function AppsWorkspace() {
  const shell = useWorkspaceShell();
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("apps", shell.locale), style: { height: "100%" }, children: /* @__PURE__ */ jsx79(SystemIntegrationsPanel, { token: shell.token, locale: shell.locale, theme: shell.theme }) });
}
function SkillsWorkspace() {
  const shell = useWorkspaceShell();
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("skills", shell.locale), style: { height: "100%" }, children: /* @__PURE__ */ jsx79(SkillsSettings, { service: shell.agentSkillsService, readOnly: !shell.isOwner }) });
}
function WorkspaceRightPanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const allowed = useMemo19(() => new Set(visibleRightTabIds(shell.mode)), [shell.mode]);
  const tabs = rightTabs(shell.locale).filter((t5) => allowed.has(t5.id));
  return /* @__PURE__ */ jsxs71(
    "aside",
    {
      style: {
        width: "100%",
        height: "100%",
        borderLeft: "none",
        backgroundColor: colors.surfaceStrong,
        display: "flex",
        flexDirection: "column",
        padding: tokens.spacing.md
      },
      children: [
        /* @__PURE__ */ jsx79("div", { style: { display: "flex", flexWrap: "wrap", gap: tokens.spacing.xs, marginBottom: tokens.spacing.md }, children: tabs.map((tab) => /* @__PURE__ */ jsx79(
          "button",
          {
            type: "button",
            onClick: () => shell.setRightTab(tab.id),
            style: {
              padding: "4px 8px",
              borderRadius: tokens.radius.md,
              border: "none",
              backgroundColor: shell.rightTab === tab.id ? tokens.colors.primary : "transparent",
              color: shell.rightTab === tab.id ? "#fff" : colors.text,
              cursor: "pointer",
              fontSize: "12px"
            },
            children: tab.label
          },
          tab.id
        )) }),
        /* @__PURE__ */ jsx79("div", { style: { flex: 1, overflow: "auto" }, children: /* @__PURE__ */ jsx79(RightPanelContent, {}) })
      ]
    }
  );
}
function RightPanelContent() {
  const shell = useWorkspaceShell();
  const allowed = visibleRightTabIds(shell.mode);
  const tab = allowed.includes(shell.rightTab) ? shell.rightTab : allowed[0] ?? "context";
  switch (tab) {
    case "context":
      return /* @__PURE__ */ jsx79(ContextPanel, {});
    case "files":
      return /* @__PURE__ */ jsx79(FilesPanel, {});
    case "artifacts":
      return /* @__PURE__ */ jsx79(ArtifactsPanel, {});
    case "browser":
      return /* @__PURE__ */ jsx79(BrowserPanel2, {});
    case "builder":
      return /* @__PURE__ */ jsx79(BuilderPanel, {});
    case "cowork":
      return /* @__PURE__ */ jsx79(CoworkPanel, {});
    case "code":
      return /* @__PURE__ */ jsx79(CodePanel, {});
    case "codeIntelligence":
      return /* @__PURE__ */ jsx79(CodeIntelligencePanel, {});
    case "approvals":
      return /* @__PURE__ */ jsx79(ApprovalsPanel, {});
    case "audit":
      return /* @__PURE__ */ jsx79(AuditPanel, {});
    default:
      return /* @__PURE__ */ jsx79(ContextPanel, {});
  }
}
function ContextPanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const ctx = shell.currentContext;
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("context", shell.locale), children: /* @__PURE__ */ jsxs71("div", { style: { fontSize: "13px", color: colors.textMuted }, children: [
    /* @__PURE__ */ jsxs71("p", { children: [
      "Project: ",
      ctx?.projectId ?? "\u2014"
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      "Skill: ",
      ctx?.skillId ?? "\u2014"
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      "Owner: ",
      ctx?.ownerId ?? "\u2014"
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      "Tenant: ",
      ctx?.tenantId ?? "\u2014"
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      tr11("includedFiles", shell.locale),
      ": ",
      shell.memoryContext?.includedFiles.length ?? 0
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      tr11("includedMemories", shell.locale),
      ": ",
      shell.memoryContext?.includedMemories.length ?? 0
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      tr11("retrievedSymbols", shell.locale),
      ": ",
      shell.memoryContext?.retrievedSymbols.length ?? 0
    ] })
  ] }) });
}
function FilesPanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("files", shell.locale), children: shell.openFiles.length === 0 ? /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "No open files." }) : /* @__PURE__ */ jsx79("ul", { style: { listStyle: "none", padding: 0, margin: 0, fontSize: "13px", color: colors.textMuted }, children: shell.openFiles.map((f) => /* @__PURE__ */ jsx79("li", { children: f.path }, f.path)) }) });
}
function ArtifactsPanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("artifacts", shell.locale), children: shell.testBuildResult ? /* @__PURE__ */ jsxs71("div", { style: { fontSize: "13px", color: colors.textMuted }, children: [
    /* @__PURE__ */ jsx79("p", { children: shell.testBuildResult.command }),
    /* @__PURE__ */ jsxs71("p", { children: [
      "Exit code: ",
      shell.testBuildResult.exitCode
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      "Duration: ",
      shell.testBuildResult.durationMs,
      "ms"
    ] })
  ] }) : /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "No artifacts." }) });
}
var BROWSER_SUB_TABS = [
  { id: "browser", label: "Browser" },
  { id: "files", label: "Files" },
  { id: "captures", label: "Captures" },
  { id: "downloads", label: "Downloads" }
];
function statusColor(value) {
  switch (value) {
    case "Active":
      return "#dc2626";
    case "Standby":
      return "#16a34a";
    case "DEGRADED":
      return "#ca8a04";
    case "READY":
      return "#16a34a";
    case "NOT_CONFIGURED":
    case "UNAVAILABLE":
    default:
      return "#78716c";
  }
}
function BrowserStatusPanel({ shell }) {
  const colors = themeColors(shell.theme);
  const controlServiceExists = !!shell.controlService;
  function row2(label, value) {
    return /* @__PURE__ */ jsxs71("div", { style: { display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "6px" }, children: [
      /* @__PURE__ */ jsx79("span", { style: { color: colors.textMuted }, children: label }),
      /* @__PURE__ */ jsx79("span", { style: { color: statusColor(value), fontWeight: 500 }, children: value })
    ] });
  }
  return /* @__PURE__ */ jsxs71("div", { style: { display: "flex", flexDirection: "column", gap: "8px" }, children: [
    row2("Paired", "UNAVAILABLE"),
    row2("Control session", controlServiceExists ? "UNAVAILABLE" : "NOT_CONFIGURED"),
    row2("Current page", "UNAVAILABLE"),
    row2("Control readiness", controlServiceExists ? "DEGRADED" : "NOT_CONFIGURED"),
    row2("Artifact store readiness", controlServiceExists ? "DEGRADED" : "NOT_CONFIGURED"),
    row2("Last action / result", "UNAVAILABLE"),
    row2("Emergency Stop", shell.isEmergencyStopped ? "Active" : "Standby"),
    row2("Sidecar / runtime", "NOT_CONFIGURED"),
    /* @__PURE__ */ jsx79("p", { style: { fontSize: "12px", color: colors.textMuted, margin: 0, marginTop: "8px" }, children: "Browser status is a truthful scaffold. Pairing, session, current page, sidecar, and last action are not available in the shared workspace context. Control and artifact store service objects exist but their server health is not yet verified." })
  ] });
}
function BrowserPanel2() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const { activeBrowserTab, setActiveBrowserTab, browserArtifacts, browserArtifactsLoading, browserArtifactsError, controlService } = shell;
  const filtered = useMemo19(() => {
    if (activeBrowserTab === "browser") return [];
    return browserArtifacts.filter((a) => {
      if (activeBrowserTab === "files") return a.source === "upload";
      if (activeBrowserTab === "captures") return a.source === "screenshot" || a.source === "recording";
      if (activeBrowserTab === "downloads") return a.source === "download";
      return false;
    });
  }, [browserArtifacts, activeBrowserTab]);
  const showDownloadsNotConfigured = activeBrowserTab === "downloads" && filtered.length === 0;
  return /* @__PURE__ */ jsxs71(Panel, { theme: shell.theme, title: tr11("browser", shell.locale), children: [
    /* @__PURE__ */ jsx79("div", { style: { display: "flex", flexWrap: "wrap", gap: tokens.spacing.xs, marginBottom: tokens.spacing.md }, children: BROWSER_SUB_TABS.map((t5) => /* @__PURE__ */ jsx79(
      "button",
      {
        type: "button",
        onClick: () => setActiveBrowserTab(t5.id),
        style: {
          padding: "4px 8px",
          borderRadius: tokens.radius.md,
          border: "none",
          backgroundColor: activeBrowserTab === t5.id ? tokens.colors.primary : "transparent",
          color: activeBrowserTab === t5.id ? "#fff" : colors.text,
          cursor: "pointer",
          fontSize: "12px"
        },
        children: t5.label
      },
      t5.id
    )) }),
    activeBrowserTab === "browser" ? /* @__PURE__ */ jsx79(BrowserStatusPanel, { shell }) : browserArtifactsLoading ? /* @__PURE__ */ jsx79("p", { style: { color: colors.textMuted, fontSize: "13px" }, children: "Loading\u2026" }) : browserArtifactsError ? /* @__PURE__ */ jsxs71("p", { role: "alert", style: { color: tokens.colors.danger, fontSize: "13px" }, children: [
      safeErrorText(browserArtifactsError, shell.locale).title,
      " ",
      /* @__PURE__ */ jsx79("code", { children: browserArtifactsError })
    ] }) : !controlService ? /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "Browser control service is NOT_CONFIGURED." }) : showDownloadsNotConfigured ? /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "Browser download persistence is NOT_CONFIGURED." }) : filtered.length === 0 ? /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: `No ${activeBrowserTab} artifacts.` }) : /* @__PURE__ */ jsx79("div", { style: { display: "flex", flexDirection: "column", gap: tokens.spacing.sm }, children: filtered.map((a) => /* @__PURE__ */ jsxs71(
      "div",
      {
        style: { fontSize: "12px", padding: tokens.spacing.sm, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md },
        children: [
          /* @__PURE__ */ jsx79("p", { style: { margin: 0, fontWeight: 600 }, children: a.filename }),
          /* @__PURE__ */ jsxs71("p", { style: { margin: `4px 0 0`, color: colors.textMuted }, children: [
            a.source,
            " \xB7 ",
            a.mimeType,
            " \xB7 ",
            a.size,
            " bytes \xB7 ",
            a.status
          ] })
        ]
      },
      a.artifactId
    )) })
  ] });
}
function CoworkPanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const service = shell.codeIntelligenceService;
  const [impactName, setImpactName] = useState40("");
  const [impactResult, setImpactResult] = useState40(null);
  const [impactError, setImpactError] = useState40(null);
  const [impactLoading, setImpactLoading] = useState40(false);
  const runImpact = async () => {
    if (!service || !shell.selectedRepositoryId || !impactName.trim()) return;
    setImpactLoading(true);
    setImpactError(null);
    try {
      const result = await service.tracePath(shell.selectedRepositoryId, impactName.trim());
      setImpactResult(result);
    } catch (err) {
      setImpactError(errorToCode(err));
    } finally {
      setImpactLoading(false);
    }
  };
  return /* @__PURE__ */ jsxs71(Panel, { theme: shell.theme, title: tr11("cowork", shell.locale), children: [
    shell.selectedTask ? /* @__PURE__ */ jsxs71("div", { style: { fontSize: "13px", color: colors.textMuted, marginBottom: 12 }, children: [
      /* @__PURE__ */ jsx79("p", { children: shell.selectedTask.title }),
      /* @__PURE__ */ jsxs71("p", { children: [
        "Status: ",
        shell.selectedTask.taskStatus
      ] }),
      /* @__PURE__ */ jsxs71("p", { children: [
        "Stage: ",
        shell.selectedTask.currentStageLabel
      ] }),
      /* @__PURE__ */ jsxs71("p", { children: [
        "Evidence: ",
        shell.selectedTask.evidenceCount
      ] })
    ] }) : /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: tr11("noTaskSelected", shell.locale) }),
    /* @__PURE__ */ jsxs71("div", { style: { borderTop: `1px solid ${colors.border}`, paddingTop: 12, marginTop: 12 }, children: [
      /* @__PURE__ */ jsx79("h4", { style: { margin: "0 0 8px", fontSize: "13px" }, children: tr11("graphifyImpact", shell.locale) }),
      /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: 8, marginBottom: 8 }, children: [
        /* @__PURE__ */ jsx79(
          "input",
          {
            value: impactName,
            onChange: (e) => setImpactName(e.target.value),
            placeholder: "function name",
            disabled: !service || !shell.selectedRepositoryId || impactLoading,
            style: { flex: 1, padding: "6px" }
          }
        ),
        /* @__PURE__ */ jsx79(Button, { onClick: () => void runImpact(), disabled: !service || !shell.selectedRepositoryId || !impactName.trim() || impactLoading, children: tr11("graphifyTrace", shell.locale) })
      ] }),
      !service && /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "Code intelligence service not connected." }),
      impactError && /* @__PURE__ */ jsxs71("p", { role: "alert", style: { color: "#ef4444", fontSize: "12px" }, children: [
        safeErrorText(impactError, shell.locale).title,
        " ",
        /* @__PURE__ */ jsx79("code", { children: impactError })
      ] }),
      impactResult != null && /* @__PURE__ */ jsx79("pre", { style: { fontSize: "12px", backgroundColor: colors.surface, padding: 8, borderRadius: 4, maxHeight: 200, overflow: "auto" }, children: JSON.stringify(impactResult, null, 2) })
    ] })
  ] });
}
function CodePanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const ci = shell.codeIntelligenceService;
  const agent = shell.agentSkillsService;
  const isDesktop = shell.surface === "desktop";
  const [searchQuery, setSearchQuery] = useState40("");
  const [searchResult, setSearchResult] = useState40(null);
  const [traceName, setTraceName] = useState40("");
  const [traceResult, setTraceResult] = useState40(null);
  const [ciError, setCiError] = useState40(null);
  const [ciLoading, setCiLoading] = useState40(false);
  const [skills, setSkills] = useState40([]);
  const [selectedSkillId, setSelectedSkillId] = useState40("");
  const [sourceRoot, setSourceRoot] = useState40("");
  const [skillDirName, setSkillDirName] = useState40("");
  const [scriptName, setScriptName] = useState40("run");
  const [skillOutput, setSkillOutput] = useState40("");
  const [skillError, setSkillError] = useState40(null);
  const [skillLoading, setSkillLoading] = useState40(false);
  const loadSkills = async () => {
    if (!agent) return;
    setSkillLoading(true);
    setSkillError(null);
    try {
      const list = await agent.listSkills();
      setSkills(list);
    } catch (err) {
      setSkillError(errorToCode(err));
    } finally {
      setSkillLoading(false);
    }
  };
  const handleImport = async () => {
    if (!agent || !sourceRoot.trim() || !skillDirName.trim()) return;
    setSkillLoading(true);
    setSkillError(null);
    try {
      await agent.importSkill({ sourceRoot: sourceRoot.trim(), skillDirName: skillDirName.trim() });
      await loadSkills();
      setSourceRoot("");
      setSkillDirName("");
    } catch (err) {
      setSkillError(errorToCode(err));
    } finally {
      setSkillLoading(false);
    }
  };
  const handleEnable = async (id) => {
    if (!agent) return;
    setSkillError(null);
    try {
      const { receipt } = await agent.approveSkill(id);
      await agent.enableSkill(id, receipt);
      await loadSkills();
    } catch (err) {
      setSkillError(errorToCode(err));
    }
  };
  const handleExecute = async (id) => {
    if (!agent) return;
    setSkillLoading(true);
    setSkillError(null);
    setSkillOutput("");
    try {
      const result = await agent.executeSkill(id, {
        surface: shell.surface,
        grantedScopes: ["read", "execute"],
        scriptName: scriptName.trim() || "run"
      });
      setSkillOutput(result.output ?? (result.error ? safeErrorCode(result.error) : ""));
    } catch (err) {
      setSkillError(errorToCode(err));
    } finally {
      setSkillLoading(false);
    }
  };
  const handleDisable = async (id) => {
    if (!agent) return;
    setSkillError(null);
    try {
      await agent.disableSkill(id);
      await loadSkills();
    } catch (err) {
      setSkillError(errorToCode(err));
    }
  };
  const handleRemove = async (id) => {
    if (!agent) return;
    setSkillError(null);
    try {
      await agent.removeSkill(id);
      await loadSkills();
    } catch (err) {
      setSkillError(errorToCode(err));
    }
  };
  const runSearch = async () => {
    if (!ci || !shell.selectedRepositoryId || !searchQuery.trim()) return;
    setCiLoading(true);
    setCiError(null);
    try {
      const result = await ci.searchGraph(shell.selectedRepositoryId, searchQuery.trim());
      setSearchResult(result);
    } catch (err) {
      setCiError(errorToCode(err));
    } finally {
      setCiLoading(false);
    }
  };
  const runTrace = async () => {
    if (!ci || !shell.selectedRepositoryId || !traceName.trim()) return;
    setCiLoading(true);
    setCiError(null);
    try {
      const result = await ci.tracePath(shell.selectedRepositoryId, traceName.trim());
      setTraceResult(result);
    } catch (err) {
      setCiError(errorToCode(err));
    } finally {
      setCiLoading(false);
    }
  };
  return /* @__PURE__ */ jsxs71(Panel, { theme: shell.theme, title: tr11("code", shell.locale), children: [
    /* @__PURE__ */ jsxs71("div", { style: { fontSize: "13px", color: colors.textMuted, marginBottom: 12 }, children: [
      /* @__PURE__ */ jsxs71("p", { children: [
        "Repository: ",
        shell.selectedRepositoryId ?? "\u2014"
      ] }),
      /* @__PURE__ */ jsxs71("p", { children: [
        tr11("gitStatus", shell.locale),
        ": ",
        shell.gitStatus ? `${shell.gitStatus.branch} (+${shell.gitStatus.ahead}/-${shell.gitStatus.behind})` : "\u2014"
      ] }),
      /* @__PURE__ */ jsxs71("p", { children: [
        tr11("openFiles", shell.locale),
        ": ",
        shell.openFiles.length
      ] }),
      /* @__PURE__ */ jsxs71("p", { children: [
        tr11("diffPreview", shell.locale),
        ": ",
        shell.diffPreview ? `${shell.diffPreview.base} \u2192 ${shell.diffPreview.head}` : "\u2014"
      ] })
    ] }),
    /* @__PURE__ */ jsxs71("div", { style: { borderTop: `1px solid ${colors.border}`, paddingTop: 12, marginBottom: 12 }, children: [
      /* @__PURE__ */ jsx79("h4", { style: { margin: "0 0 8px", fontSize: "13px" }, children: "Graphify" }),
      /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: 8, marginBottom: 8 }, children: [
        /* @__PURE__ */ jsx79(
          "input",
          {
            value: searchQuery,
            onChange: (e) => setSearchQuery(e.target.value),
            placeholder: "search symbol",
            disabled: !ci || !shell.selectedRepositoryId || ciLoading,
            style: { flex: 1, padding: "6px" }
          }
        ),
        /* @__PURE__ */ jsx79(Button, { onClick: () => void runSearch(), disabled: !ci || !shell.selectedRepositoryId || !searchQuery.trim() || ciLoading, children: tr11("graphifySearch", shell.locale) })
      ] }),
      /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: 8, marginBottom: 8 }, children: [
        /* @__PURE__ */ jsx79(
          "input",
          {
            value: traceName,
            onChange: (e) => setTraceName(e.target.value),
            placeholder: "function name",
            disabled: !ci || !shell.selectedRepositoryId || ciLoading,
            style: { flex: 1, padding: "6px" }
          }
        ),
        /* @__PURE__ */ jsx79(Button, { onClick: () => void runTrace(), disabled: !ci || !shell.selectedRepositoryId || !traceName.trim() || ciLoading, children: tr11("graphifyTrace", shell.locale) })
      ] }),
      ciError && /* @__PURE__ */ jsxs71("p", { role: "alert", style: { color: "#ef4444", fontSize: "12px" }, children: [
        safeErrorText(ciError, shell.locale).title,
        " ",
        /* @__PURE__ */ jsx79("code", { children: ciError })
      ] }),
      (searchResult != null || traceResult != null) && /* @__PURE__ */ jsx79("pre", { style: { fontSize: "12px", backgroundColor: colors.surface, padding: 8, borderRadius: 4, maxHeight: 200, overflow: "auto" }, children: JSON.stringify({ search: searchResult, trace: traceResult }, null, 2) })
    ] }),
    /* @__PURE__ */ jsxs71("div", { style: { borderTop: `1px solid ${colors.border}`, paddingTop: 12 }, children: [
      /* @__PURE__ */ jsx79("h4", { style: { margin: "0 0 8px", fontSize: "13px" }, children: tr11("agentSkills", shell.locale) }),
      !agent && /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "Agent skills service not connected." }),
      agent && /* @__PURE__ */ jsxs71(Fragment9, { children: [
        /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: 8, marginBottom: 8 }, children: [
          /* @__PURE__ */ jsx79(
            "input",
            {
              value: sourceRoot,
              onChange: (e) => setSourceRoot(e.target.value),
              placeholder: "source root path",
              disabled: skillLoading,
              style: { flex: 1, padding: "6px" }
            }
          ),
          /* @__PURE__ */ jsx79(
            "input",
            {
              value: skillDirName,
              onChange: (e) => setSkillDirName(e.target.value),
              placeholder: "skill directory",
              disabled: skillLoading,
              style: { flex: 1, padding: "6px" }
            }
          ),
          /* @__PURE__ */ jsx79(Button, { onClick: () => void handleImport(), disabled: !sourceRoot.trim() || !skillDirName.trim() || skillLoading, children: tr11("importSkill", shell.locale) }),
          /* @__PURE__ */ jsx79(Button, { onClick: () => void loadSkills(), disabled: skillLoading, children: tr11("listSkills", shell.locale) })
        ] }),
        skills.length > 0 && /* @__PURE__ */ jsxs71(
          "select",
          {
            value: selectedSkillId,
            onChange: (e) => setSelectedSkillId(e.target.value),
            style: { width: "100%", padding: "6px", marginBottom: 8 },
            children: [
              /* @__PURE__ */ jsxs71("option", { value: "", children: [
                "-- ",
                tr11("listSkills", shell.locale),
                " --"
              ] }),
              skills.map((s) => /* @__PURE__ */ jsxs71("option", { value: s.id, children: [
                s.name,
                " (",
                s.status,
                ")"
              ] }, s.id))
            ]
          }
        ),
        selectedSkillId && /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }, children: [
          /* @__PURE__ */ jsx79(Button, { onClick: () => void handleEnable(selectedSkillId), children: tr11("enableSkill", shell.locale) }),
          /* @__PURE__ */ jsx79(Button, { onClick: () => void handleDisable(selectedSkillId), children: tr11("disableSkill", shell.locale) }),
          /* @__PURE__ */ jsx79(Button, { onClick: () => void handleRemove(selectedSkillId), children: tr11("removeSkill", shell.locale) }),
          /* @__PURE__ */ jsx79(
            "input",
            {
              value: scriptName,
              onChange: (e) => setScriptName(e.target.value),
              placeholder: "script name",
              disabled: !isDesktop || skillLoading,
              style: { padding: "6px", minWidth: 100 }
            }
          ),
          /* @__PURE__ */ jsx79(Button, { onClick: () => void handleExecute(selectedSkillId), disabled: !isDesktop || skillLoading, children: tr11("executeSkill", shell.locale) })
        ] }),
        !isDesktop && /* @__PURE__ */ jsx79("p", { style: { color: colors.textMuted, fontSize: "12px" }, children: tr11("realExecutionDesktopOnly", shell.locale) }),
        skillError && /* @__PURE__ */ jsxs71("p", { role: "alert", style: { color: "#ef4444", fontSize: "12px" }, children: [
          safeErrorText(skillError, shell.locale).title,
          " ",
          /* @__PURE__ */ jsx79("code", { children: skillError })
        ] }),
        skillOutput && /* @__PURE__ */ jsx79("pre", { style: { fontSize: "12px", backgroundColor: colors.surface, padding: 8, borderRadius: 4, maxHeight: 200, overflow: "auto" }, children: skillOutput })
      ] })
    ] })
  ] });
}
function CodeIntelligencePanel() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  const service = shell.codeIntelligenceService;
  const locale = shell.locale;
  const [health, setHealth] = useState40(null);
  const [repositories, setRepositories] = useState40([]);
  const [selectedRepo, setSelectedRepo] = useState40(void 0);
  const [architecture, setArchitecture] = useState40(null);
  const [graphSchema, setGraphSchema] = useState40(null);
  const [searchQuery, setSearchQuery] = useState40("");
  const [searchResults, setSearchResults] = useState40(null);
  const [activeTab, setActiveTab] = useState40("overview");
  const [traceName, setTraceName] = useState40("");
  const [traceResult, setTraceResult] = useState40(null);
  const [error, setError] = useState40(null);
  const [loading, setLoading] = useState40(false);
  const clearError = useCallback26(() => setError(null), []);
  const run = useCallback26(
    async (fn, onResult) => {
      if (!service) return;
      clearError();
      setLoading(true);
      const controller = new AbortController();
      try {
        const v = await fn(controller.signal);
        onResult(v);
      } catch (err) {
        setError(errorToCode(err));
      } finally {
        setLoading(false);
      }
    },
    [service, clearError]
  );
  useEffect32(() => {
    if (!service) return;
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    Promise.all([service.getHealth(controller.signal), service.listRepositories(controller.signal)]).then(([h, repos]) => {
      if (cancelled) return;
      setHealth(h);
      setRepositories(repos);
      if (repos.length > 0) setSelectedRepo(repos[0].id);
    }).catch((err) => {
      if (cancelled) return;
      setError(errorToCode(err));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [service]);
  const refreshRepositories = useCallback26(() => {
    run((signal) => service.listRepositories(signal), setRepositories);
  }, [run, service]);
  const repo = useMemo19(
    () => repositories.find((r) => r.id === selectedRepo),
    [repositories, selectedRepo]
  );
  const handleIndex = useCallback26(
    (mode) => {
      if (!selectedRepo) return;
      run((signal) => service.indexRepository(selectedRepo, mode, signal), () => refreshRepositories());
    },
    [run, service, selectedRepo, refreshRepositories]
  );
  const handleDeleteIndex = useCallback26(() => {
    if (!selectedRepo) return;
    run((signal) => service.deleteIndex(selectedRepo, signal), () => refreshRepositories());
  }, [run, service, selectedRepo, refreshRepositories]);
  const handleArchitecture = useCallback26(() => {
    if (!selectedRepo) return;
    setActiveTab("architecture");
    run((signal) => service.getArchitecture(selectedRepo, signal), setArchitecture);
  }, [run, service, selectedRepo]);
  const handleGraphSchema = useCallback26(() => {
    if (!selectedRepo) return;
    setActiveTab("graph");
    run((signal) => service.getGraphSchema(selectedRepo, signal), setGraphSchema);
  }, [run, service, selectedRepo]);
  const handleSearchCode = useCallback26(() => {
    if (!selectedRepo || !searchQuery.trim()) return;
    setActiveTab("search");
    run((signal) => service.searchCode(selectedRepo, searchQuery, signal), setSearchResults);
  }, [run, service, selectedRepo, searchQuery]);
  const handleSearchGraph = useCallback26(() => {
    if (!selectedRepo || !searchQuery.trim()) return;
    setActiveTab("search");
    run((signal) => service.searchGraph(selectedRepo, searchQuery, signal), setSearchResults);
  }, [run, service, selectedRepo, searchQuery]);
  const handleTrace = useCallback26(() => {
    if (!selectedRepo || !traceName.trim()) return;
    setActiveTab("trace");
    run((signal) => service.tracePath(selectedRepo, traceName, signal), setTraceResult);
  }, [run, service, selectedRepo, traceName]);
  if (!service) {
    return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("codeIntelligence", shell.locale), children: /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "Code Intelligence service not connected." }) });
  }
  const traceSupported = !health?.availableTools?.length || health.availableTools.includes("trace_path");
  const deleteSupported = !health?.availableTools?.length || health.availableTools.includes("delete_index");
  const summary = /* @__PURE__ */ jsxs71("div", { style: { display: "flex", flexDirection: "column", gap: tokens.spacing.sm, fontSize: "13px", color: colors.textMuted }, children: [
    /* @__PURE__ */ jsxs71("p", { children: [
      "Status: ",
      loading ? "Loading..." : health ? health.available ? "Available" : "Unavailable" : "Unknown"
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      "Version: ",
      health?.version ?? "\u2014"
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      "Emergency Stop: ",
      health?.emergencyStopped ? "Active" : "Inactive"
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      "Auto-index: ",
      health?.autoIndex ? "On" : "Off",
      " (experimental)"
    ] }),
    /* @__PURE__ */ jsxs71("p", { children: [
      "Sidecar tools: ",
      health?.availableTools ? health.availableTools.length > 0 ? health.availableTools.join(", ") : "unknown" : "\u2014"
    ] })
  ] });
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("codeIntelligence", shell.locale), children: /* @__PURE__ */ jsxs71("div", { style: { display: "flex", flexDirection: "column", gap: tokens.spacing.md, padding: tokens.spacing.sm, overflow: "auto" }, children: [
    error && /* @__PURE__ */ jsxs71("div", { role: "alert", style: { color: tokens.colors.danger, fontSize: "13px" }, children: [
      safeErrorText(error, shell.locale).title,
      " ",
      /* @__PURE__ */ jsx79("code", { children: error })
    ] }),
    /* @__PURE__ */ jsxs71("div", { style: { display: "flex", alignItems: "center", gap: tokens.spacing.sm }, children: [
      /* @__PURE__ */ jsxs71("label", { style: { color: colors.textMuted, fontSize: "13px" }, children: [
        tr11("selectRepository", locale),
        ":"
      ] }),
      /* @__PURE__ */ jsxs71(
        "select",
        {
          value: selectedRepo ?? "",
          onChange: (e) => setSelectedRepo(e.target.value || void 0),
          style: { background: colors.surface, color: colors.text, border: `1px solid ${colors.border}`, padding: "4px 8px" },
          children: [
            repositories.length === 0 && /* @__PURE__ */ jsx79("option", { value: "", children: "\u2014" }),
            repositories.map((r) => /* @__PURE__ */ jsxs71("option", { value: r.id, children: [
              r.name,
              " (",
              r.status,
              ")"
            ] }, r.id))
          ]
        }
      ),
      /* @__PURE__ */ jsx79(Button, { onClick: () => refreshRepositories(), disabled: loading, children: "Refresh" })
    ] }),
    summary,
    repo && /* @__PURE__ */ jsxs71(Fragment9, { children: [
      /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.sm, flexWrap: "wrap" }, children: [
        /* @__PURE__ */ jsx79(Button, { onClick: () => handleIndex("fast"), disabled: loading, children: "Index (fast)" }),
        /* @__PURE__ */ jsx79(Button, { onClick: () => handleIndex("full"), disabled: loading, children: "Re-index (full)" }),
        /* @__PURE__ */ jsx79(Button, { onClick: handleDeleteIndex, disabled: loading || !deleteSupported, children: "Delete index" }),
        /* @__PURE__ */ jsx79(Button, { onClick: handleArchitecture, disabled: loading, children: "Architecture" }),
        /* @__PURE__ */ jsx79(Button, { onClick: handleGraphSchema, disabled: loading, children: "Graph schema" })
      ] }),
      /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.sm, alignItems: "center" }, children: [
        /* @__PURE__ */ jsx79(
          "input",
          {
            type: "text",
            value: searchQuery,
            onChange: (e) => setSearchQuery(e.target.value),
            placeholder: "Search pattern or graph query...",
            style: { flex: 1, background: colors.surface, color: colors.text, border: `1px solid ${colors.border}`, padding: "4px 8px" }
          }
        ),
        /* @__PURE__ */ jsx79(Button, { onClick: handleSearchCode, disabled: loading, children: "Search code" }),
        /* @__PURE__ */ jsx79(Button, { onClick: handleSearchGraph, disabled: loading, children: "Search graph" })
      ] }),
      /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.sm, alignItems: "center" }, children: [
        /* @__PURE__ */ jsx79(
          "input",
          {
            type: "text",
            value: traceName,
            onChange: (e) => setTraceName(e.target.value),
            placeholder: "Function name to trace...",
            style: { flex: 1, background: colors.surface, color: colors.text, border: `1px solid ${colors.border}`, padding: "4px 8px" }
          }
        ),
        /* @__PURE__ */ jsx79(Button, { onClick: handleTrace, disabled: loading || !traceSupported, children: "Trace path" })
      ] })
    ] }),
    activeTab === "architecture" && architecture !== null && /* @__PURE__ */ jsx79("pre", { style: { fontSize: "12px", maxHeight: "300px", overflow: "auto", background: colors.surface, padding: tokens.spacing.sm, color: colors.text }, children: JSON.stringify(architecture, null, 2) }),
    activeTab === "graph" && graphSchema !== null && /* @__PURE__ */ jsx79("pre", { style: { fontSize: "12px", maxHeight: "300px", overflow: "auto", background: colors.surface, padding: tokens.spacing.sm, color: colors.text }, children: JSON.stringify(graphSchema, null, 2) }),
    activeTab === "search" && searchResults !== null && /* @__PURE__ */ jsx79("pre", { style: { fontSize: "12px", maxHeight: "300px", overflow: "auto", background: colors.surface, padding: tokens.spacing.sm, color: colors.text }, children: JSON.stringify(searchResults, null, 2) }),
    activeTab === "trace" && traceResult !== null && /* @__PURE__ */ jsx79("pre", { style: { fontSize: "12px", maxHeight: "300px", overflow: "auto", background: colors.surface, padding: tokens.spacing.sm, color: colors.text }, children: JSON.stringify(traceResult, null, 2) })
  ] }) });
}
function ApprovalsPanel() {
  const shell = useWorkspaceShell();
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("approvals", shell.locale), children: /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "Approval queue is not configured." }) });
}
function AuditPanel() {
  const shell = useWorkspaceShell();
  return /* @__PURE__ */ jsx79(Panel, { theme: shell.theme, title: tr11("audit", shell.locale), children: /* @__PURE__ */ jsx79(DisabledHint2, { theme: shell.theme, message: "Audit trail requires backend connection." }) });
}
function WorkspaceStatusBar() {
  const shell = useWorkspaceShell();
  const colors = themeColors(shell.theme);
  return /* @__PURE__ */ jsxs71(
    "footer",
    {
      "data-testid": "workspace-status-bar",
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: tokens.spacing.md,
        padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
        borderTop: `1px solid ${colors.border}`,
        backgroundColor: colors.surfaceStrong,
        color: colors.textMuted,
        fontSize: "12px"
      },
      children: [
        /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.lg }, children: [
          /* @__PURE__ */ jsxs71("span", { children: [
            tr11("mode", shell.locale),
            ": ",
            shell.mode === "advanced" ? tr11("advancedMode", shell.locale) : tr11("normalMode", shell.locale)
          ] }),
          /* @__PURE__ */ jsx79("span", { children: shell.locale === "hi" ? "HI" : "EN" }),
          /* @__PURE__ */ jsx79("span", { children: shell.theme === "dark" ? "Dark" : "Light" })
        ] }),
        /* @__PURE__ */ jsxs71("div", { style: { display: "flex", gap: tokens.spacing.lg, alignItems: "center" }, children: [
          /* @__PURE__ */ jsxs71("span", { children: [
            tr11("pendingApprovals", shell.locale),
            ": ",
            shell.pendingApprovals
          ] }),
          shell.isEmergencyStopped && /* @__PURE__ */ jsx79("span", { style: { color: tokens.colors.danger, fontWeight: 700 }, children: "Emergency Stop Active" })
        ] })
      ]
    }
  );
}

// src/workspace/workspaceChatService.ts
function toMessageStatus(state) {
  switch (state) {
    case "streaming":
      return "streaming";
    case "error":
      return "error";
    case "cancelled":
      return "error";
    case "complete":
    default:
      return "sent";
  }
}
function mapChatMessage(raw) {
  const m = raw && typeof raw === "object" ? raw : {};
  const role = m.role;
  const content = typeof m.content === "string" ? m.content : "";
  const messageId = typeof m.messageId === "string" ? m.messageId : String(Math.random());
  const createdAt = typeof m.createdAt === "number" ? m.createdAt : Date.now();
  const generationState = typeof m.generationState === "string" ? m.generationState : void 0;
  const normalizedRole = role === "user" || role === "assistant" || role === "system" ? role : "assistant";
  const message = {
    id: messageId,
    role: normalizedRole,
    content,
    timestamp: new Date(createdAt),
    status: toMessageStatus(generationState)
  };
  if (message.status === "error") message.errorMessage = safeErrorCode(content);
  return message;
}
function mapConversation(raw) {
  const c = raw && typeof raw === "object" ? raw : {};
  const id = typeof c.conversationId === "string" ? c.conversationId : "";
  const title = typeof c.title === "string" ? c.title : "Untitled";
  const updatedAt = typeof c.updatedAt === "number" ? c.updatedAt : Date.now();
  return {
    id,
    title,
    preview: title,
    updatedAt: new Date(updatedAt)
  };
}
async function apiJson(baseUrl, fetchImpl, token, method, path, body, signal) {
  const headers2 = {};
  if (token) headers2.Authorization = `Bearer ${token}`;
  if (body !== void 0) headers2["Content-Type"] = "application/json";
  const init = { method, headers: headers2, signal: signal ?? null };
  if (body !== void 0) init.body = JSON.stringify(body);
  let response;
  try {
    response = await fetchImpl(`${baseUrl}${path}`, init);
  } catch (err) {
    throw new Error(err instanceof Error && err.name === "AbortError" ? "Cancelled" : "network_error");
  }
  const text2 = await response.text();
  let payload = {};
  try {
    payload = text2 ? JSON.parse(text2) : {};
  } catch {
    throw new Error(response.ok ? "invalid_json" : `http_error_${response.status}`);
  }
  if (!response.ok || payload.success === false) {
    const errorMessage = typeof payload.error === "string" ? payload.error : `http_error_${response.status}`;
    throw new Error(errorMessage);
  }
  return payload;
}
var SseParser = class {
  constructor() {
    this.buffer = "";
    this.decoder = new TextDecoder();
    this.pending = { data: [] };
    this.events = [];
  }
  feed(chunk, done = false) {
    const decoded = chunk ? this.decoder.decode(chunk, { stream: !done }) : this.decoder.decode(new Uint8Array(), { stream: false });
    this.buffer += decoded;
    let lineStart = 0;
    while (true) {
      const lf = this.buffer.indexOf("\n", lineStart);
      if (lf === -1) break;
      const line = this.buffer.slice(lineStart, lf);
      lineStart = lf + 1;
      this.processLine(line);
    }
    this.buffer = this.buffer.slice(lineStart);
    if (done) {
      if (this.buffer.length > 0) {
        this.processLine(this.buffer);
        this.buffer = "";
      }
      this.flushPending();
    }
    return this.drain();
  }
  processLine(line) {
    const trimmed = line.endsWith("\r") ? line.slice(0, -1) : line;
    if (trimmed === "") {
      this.flushPending();
      return;
    }
    if (trimmed.startsWith(":")) return;
    const colon = trimmed.indexOf(":");
    const field = colon === -1 ? trimmed : trimmed.slice(0, colon);
    const value = colon === -1 ? "" : trimmed.slice(colon + 1).replace(/^\s/, "");
    if (field === "data") {
      this.pending.data.push(value);
    } else if (field === "event") {
      this.pending.event = value;
    } else if (field === "id") {
      this.pending.id = value;
    }
  }
  flushPending() {
    if (this.pending.data.length > 0 || this.pending.event) {
      this.events.push({
        event: this.pending.event ?? "",
        data: this.pending.data.join("\n"),
        id: this.pending.id
      });
      this.pending = { data: [] };
    }
  }
  drain() {
    const result = this.events;
    this.events = [];
    return result;
  }
};
function eventPayload(event) {
  if (event.data === "") return void 0;
  try {
    return JSON.parse(event.data);
  } catch {
    return void 0;
  }
}
function eventType(event, payload) {
  if (payload && typeof payload.type === "string" && payload.type) return payload.type;
  return event.event;
}
function createWorkspaceChatService(options) {
  const baseUrl = options.baseUrl ?? "";
  const getToken = options.getToken;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  let streamAbortController = null;
  async function request(method, path, body, signal) {
    const token = getToken();
    if (!token) throw new Error("unauthenticated");
    return apiJson(baseUrl, fetchImpl, token, method, path, body, signal);
  }
  async function listConversations(signal) {
    const result = await request("GET", "/api/chat/conversations", void 0, signal);
    return (result.conversations ?? []).map(mapConversation);
  }
  async function loadConversation(id, signal) {
    const result = await request(
      "GET",
      `/api/chat/conversations/${encodeURIComponent(id)}/messages`,
      void 0,
      signal
    );
    return (result.messages ?? []).map(mapChatMessage);
  }
  async function newConversation(signal) {
    const result = await request(
      "POST",
      "/api/chat/conversations",
      { title: "New conversation" },
      signal
    );
    const id = typeof result.conversation?.conversationId === "string" ? result.conversation.conversationId : "";
    if (!id) throw new Error("conversation_creation_failed");
    return id;
  }
  async function getProfiles(signal) {
    const result = await request("GET", "/api/provider/profiles", void 0, signal);
    const profiles = (result.profiles ?? []).map((p) => {
      const raw = p && typeof p === "object" ? p : {};
      const availability = String(raw.availability ?? "unavailable");
      const normalized = availability === "available" || availability === "configured_not_verified" ? availability : "unavailable";
      return {
        id: String(raw.id ?? ""),
        provider: String(raw.provider ?? ""),
        model: String(raw.model ?? ""),
        available: raw.available === true,
        availability: normalized
      };
    });
    return profiles;
  }
  async function verifyProfile(profileId, signal) {
    const result = await request(
      "POST",
      "/api/provider/profile-verify",
      { profileId },
      signal
    );
    const status = String(result.status ?? "unavailable");
    const normalized = status === "available" || status === "configured_not_verified" ? status : "unavailable";
    return {
      status: normalized,
      fingerprint: ""
    };
  }
  function stop() {
    streamAbortController?.abort();
    streamAbortController = null;
  }
  async function stream(req, handlers, abortSignal) {
    const token = getToken();
    if (!token) throw new Error("unauthenticated");
    let conversationId = req.conversationId;
    if (!conversationId) {
      conversationId = await newConversation(abortSignal);
      if (handlers.onConversationCreated) handlers.onConversationCreated(conversationId);
    }
    stop();
    const controller = new AbortController();
    streamAbortController = controller;
    if (abortSignal) {
      abortSignal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    const body = {
      content: req.message,
      ...req.providerId ? { providerId: req.providerId } : {},
      ...req.logicalProfile ? { logicalProfile: req.logicalProfile } : {}
    };
    let response;
    try {
      response = await fetchImpl(`${baseUrl}/api/chat/conversations/${encodeURIComponent(conversationId)}/messages`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "text/event-stream"
        },
        body: JSON.stringify(body),
        signal: controller.signal
      });
    } catch (err) {
      streamAbortController = null;
      if (err instanceof Error && err.name === "AbortError") {
        throw Object.assign(new Error("Cancelled"), { name: "AbortError" });
      }
      throw new Error("network_error");
    }
    if (!response.ok) {
      streamAbortController = null;
      const text2 = await response.text().catch(() => "");
      let errorMessage = `http_error_${response.status}`;
      try {
        const payload = JSON.parse(text2);
        if (typeof payload.error === "string") errorMessage = payload.error;
      } catch {
      }
      throw new Error(errorMessage);
    }
    if (!response.body) {
      streamAbortController = null;
      throw new Error("stream_unavailable");
    }
    const reader = response.body.getReader();
    const parser = new SseParser();
    let terminalSeen = false;
    let finalMessageId = "";
    let finalContent = "";
    function processEvent(event) {
      const payload = eventPayload(event);
      const type = eventType(event, payload);
      if (type === "chunk") {
        const chunk = payload?.chunk && typeof payload.chunk === "object" ? payload.chunk : {};
        const content = typeof chunk.content === "string" ? chunk.content : "";
        if (content) handlers.onChunk(content);
        return;
      }
      if (type === "done") {
        if (terminalSeen) throw new Error("duplicate_terminal");
        terminalSeen = true;
        finalMessageId = typeof payload?.messageId === "string" ? payload.messageId : finalMessageId;
        const result = payload?.result && typeof payload.result === "object" ? payload.result : {};
        finalContent = typeof result.content === "string" ? result.content : finalContent;
        return;
      }
      if (type === "cancelled") {
        if (terminalSeen) throw new Error("duplicate_terminal");
        terminalSeen = true;
        throw Object.assign(new Error(typeof payload?.errorCode === "string" ? payload.errorCode : "cancelled"), { name: "AbortError" });
      }
      if (type === "error") {
        if (terminalSeen) throw new Error("duplicate_terminal");
        terminalSeen = true;
        throw new Error(typeof payload?.message === "string" ? payload.message : "stream_error");
      }
      if (!payload) throw new Error("malformed_event");
    }
    try {
      while (true) {
        const { done, value } = await reader.read();
        const events = parser.feed(value, done);
        for (const event of events) {
          processEvent(event);
        }
        if (done) break;
      }
    } finally {
      streamAbortController = null;
      reader.releaseLock();
    }
    if (!terminalSeen) {
      throw new Error("stream_truncated");
    }
    const finalMessage = {
      id: finalMessageId || `msg_${Date.now()}`,
      role: "assistant",
      content: finalContent,
      timestamp: /* @__PURE__ */ new Date(),
      status: "sent"
    };
    handlers.onDone(finalMessage);
  }
  return { listConversations, loadConversation, newConversation, stop, stream, getProfiles, verifyProfile };
}

// src/workspace/workspaceMemoryService.ts
async function apiJson2(baseUrl, fetchImpl, token, method, path, body, signal) {
  const headers2 = {};
  if (token) headers2.Authorization = `Bearer ${token}`;
  if (body !== void 0) headers2["Content-Type"] = "application/json";
  const init = { method, headers: headers2, signal: signal ?? null };
  if (body !== void 0) init.body = JSON.stringify(body);
  let response;
  try {
    response = await fetchImpl(`${baseUrl}${path}`, init);
  } catch (err) {
    throw new Error(err instanceof Error && err.name === "AbortError" ? "Cancelled" : "network_error");
  }
  const text2 = await response.text();
  let payload = {};
  try {
    payload = text2 ? JSON.parse(text2) : {};
  } catch {
    throw new Error(response.ok ? "invalid_json" : `http_error_${response.status}`);
  }
  if (!response.ok || payload.success === false) {
    const errorMessage = typeof payload.error === "string" ? payload.error : `http_error_${response.status}`;
    throw new Error(errorMessage);
  }
  return payload;
}
function mapSymbol(raw) {
  const s = raw && typeof raw === "object" ? raw : {};
  const name = typeof s.name === "string" ? s.name : "";
  const kind = typeof s.kind === "string" ? s.kind : "";
  const path = typeof s.path === "string" ? s.path : "";
  const line = typeof s.line === "number" ? s.line : 0;
  if (!name && !path) return void 0;
  return { name, kind, path, line };
}
function mapMemory(raw) {
  const m = raw && typeof raw === "object" ? raw : {};
  const id = typeof m.memoryId === "string" ? m.memoryId : "";
  const content = typeof m.content === "string" ? m.content : "";
  const pinned = m.pinned === true;
  const stale = m.stale === true;
  const createdAt = typeof m.createdAt === "number" ? m.createdAt : Date.now();
  const expiresAt = typeof m.expiresAt === "number" ? m.expiresAt : void 0;
  const text2 = content || (id ? `memory-${id}` : "memory");
  const entry = { id: id || `memory-${Date.now()}`, text: `${stale ? "[stale] " : ""}${text2}`, pinned, createdAt };
  if (expiresAt !== void 0) entry.expiresAt = expiresAt;
  return entry;
}
function createWorkspaceMemoryService(options) {
  const baseUrl = options.baseUrl ?? "";
  const getToken = options.getToken;
  const getProjectContext = options.getProjectContext;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  function requireProject() {
    const ctx = getProjectContext();
    const projectId = ctx?.projectId;
    if (!projectId) throw new Error("project_not_selected");
    const token = getToken();
    if (!token) throw new Error("unauthenticated");
    return { projectId, projectName: ctx.projectName, token };
  }
  return {
    async getContext(userRequest, signal) {
      const ctx = getProjectContext();
      const projectId = ctx?.projectId;
      if (!projectId) {
        return { includedFiles: [], includedMemories: [], retrievedSymbols: [], stale: true };
      }
      if (typeof userRequest !== "string" || userRequest.trim() === "") {
        throw new Error("user_request_required");
      }
      const { token } = requireProject();
      const result = await apiJson2(baseUrl, fetchImpl, token, "POST", `/api/projects/${encodeURIComponent(projectId)}/context/preview`, { userRequest }, signal);
      const preview = result.preview;
      const includedMemories = (preview?.memories ?? []).map((m) => m && typeof m === "object" ? { id: String(m.memoryId ?? ""), text: String(m.content ?? ""), pinned: false, createdAt: Date.now() } : { id: "", text: "", pinned: false, createdAt: Date.now() }).filter((m) => m.text);
      const retrievedSymbols = (preview?.symbols ?? []).map(mapSymbol).filter((s) => s !== void 0);
      const includedFiles = (preview?.fileChunks ?? []).map((c) => c && typeof c === "object" ? c.filePath : void 0).filter((p) => typeof p === "string");
      const snapshot = {
        projectId,
        ...ctx.projectName !== void 0 ? { projectName: ctx.projectName } : {},
        ...ctx.skillId !== void 0 ? { skillId: ctx.skillId } : {},
        ...ctx.ownerId !== void 0 ? { ownerId: ctx.ownerId } : {},
        ...ctx.tenantId !== void 0 ? { tenantId: ctx.tenantId } : {},
        includedFiles,
        includedMemories,
        retrievedSymbols,
        ...preview?.budget?.lastIndexedCommit !== void 0 ? { lastIndexedCommit: preview.budget.lastIndexedCommit } : {},
        stale: (preview?.budget?.staleFiles ?? 0) > 0
      };
      return snapshot;
    },
    async listMemories(signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson2(baseUrl, fetchImpl, token, "GET", `/api/projects/${encodeURIComponent(projectId)}/memories`, void 0, signal);
      return (result.memories ?? []).map(mapMemory);
    },
    async pin(id, signal) {
      const { projectId, token } = requireProject();
      await apiJson2(baseUrl, fetchImpl, token, "POST", `/api/projects/${encodeURIComponent(projectId)}/memories/${encodeURIComponent(id)}/pin`, void 0, signal);
    },
    async unpin(id, signal) {
      const { projectId, token } = requireProject();
      await apiJson2(baseUrl, fetchImpl, token, "POST", `/api/projects/${encodeURIComponent(projectId)}/memories/${encodeURIComponent(id)}/unpin`, void 0, signal);
    },
    async delete(id, signal) {
      const { projectId, token } = requireProject();
      await apiJson2(baseUrl, fetchImpl, token, "POST", `/api/projects/${encodeURIComponent(projectId)}/memories/${encodeURIComponent(id)}/forget`, void 0, signal);
    },
    async export(signal) {
      const projectId = getProjectContext()?.projectId;
      if (!projectId) throw new Error("project_not_selected");
      const token = getToken();
      if (!token) throw new Error("unauthenticated");
      const result = await apiJson2(baseUrl, fetchImpl, token, "POST", `/api/projects/${encodeURIComponent(projectId)}/export/research`, { format: "markdown", includeFiles: true, includeMemories: true }, signal);
      const markdown = typeof result.brief?.markdown === "string" ? result.brief.markdown : "";
      if (!markdown) throw new Error("export_empty");
      return markdown;
    }
  };
}

// src/workspace/workspaceCodeService.ts
async function apiJson3(baseUrl, fetchImpl, token, method, path, body, signal, acceptedStatuses = []) {
  const headers2 = {};
  if (token) headers2.Authorization = `Bearer ${token}`;
  if (body !== void 0) headers2["Content-Type"] = "application/json";
  const init = { method, headers: headers2, signal: signal ?? null };
  if (body !== void 0) init.body = JSON.stringify(body);
  let response;
  try {
    response = await fetchImpl(`${baseUrl}${path}`, init);
  } catch (err) {
    throw new Error(err instanceof Error && err.name === "AbortError" ? "Cancelled" : "network_error");
  }
  const text2 = await response.text();
  let payload = {};
  try {
    payload = text2 ? JSON.parse(text2) : {};
  } catch {
    throw new Error(response.ok ? "invalid_json" : `http_error_${response.status}`);
  }
  if (!response.ok && !acceptedStatuses.includes(response.status) || payload.success === false && !acceptedStatuses.includes(response.status)) {
    const errorMessage = typeof payload.error === "string" ? payload.error : `http_error_${response.status}`;
    throw new Error(errorMessage);
  }
  return payload;
}
function buildFileTree(files) {
  const root = [];
  for (const file of files) {
    const parts = file.path.split("/").filter(Boolean);
    if (parts.length === 0) continue;
    let current = root;
    for (let i = 0; i < parts.length; i++) {
      const name = parts[i];
      const path = parts.slice(0, i + 1).join("/");
      let node = current.find((n) => n.path === path);
      const isFile = i === parts.length - 1;
      if (!node) {
        node = { name, path, type: isFile ? "file" : "directory" };
        if (isFile) {
          node.fileId = file.fileId;
          node.language = file.language;
          node.stale = file.stale;
        } else {
          node.children = [];
        }
        current.push(node);
      }
      if (node.type === "directory" && i < parts.length - 1) {
        current = node.children ?? [];
      }
    }
  }
  return root.sort((a, b) => a.name.localeCompare(b.name));
}
function languageFromPath(pathValue, language) {
  if (language && language !== "unknown") return language;
  const ext = pathValue.split(".").pop()?.toLowerCase() ?? "";
  const map = {
    ts: "typescript",
    tsx: "typescript",
    js: "javascript",
    jsx: "javascript",
    mjs: "javascript",
    py: "python",
    md: "markdown",
    json: "json",
    yml: "yaml",
    yaml: "yaml",
    sh: "bash",
    css: "css",
    html: "html"
  };
  return map[ext];
}
function mapGitStatus(status) {
  if (status === "D") return "deleted";
  if (status === "A") return "added";
  if (status === "?" || status === "!") return "untracked";
  return "modified";
}
function asString(value) {
  return typeof value === "string" ? value : void 0;
}
function createWorkspaceCodeService(options) {
  const baseUrl = options.baseUrl ?? "";
  const getToken = options.getToken;
  const getProjectContext = options.getProjectContext;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const surface = options.surface ?? "web";
  const executionAuthority = surface === "desktop" ? "desktop" : "handoff";
  function requireDesktopExecution() {
    if (executionAuthority !== "desktop") {
      throw new Error("DESKTOP_HANDOFF_REQUIRED");
    }
  }
  function requireProject() {
    const projectId = getProjectContext()?.projectId;
    if (!projectId) throw new Error("project_not_selected");
    const token = getToken();
    if (!token) throw new Error("unauthenticated");
    return { projectId, token };
  }
  return {
    executionAuthority,
    async listRepositories(signal) {
      const { projectId, token } = requireProject();
      const name = getProjectContext()?.projectName ?? projectId;
      let lastIndexedCommit;
      let stale = true;
      try {
        const status = await apiJson3(
          baseUrl,
          fetchImpl,
          token,
          "GET",
          `/api/projects/${encodeURIComponent(projectId)}/index/status`,
          void 0,
          signal
        );
        lastIndexedCommit = status.status?.currentCommit;
        stale = status.status?.completed !== true;
      } catch {
      }
      const repo = {
        id: projectId,
        name,
        defaultBranch: "main",
        ...lastIndexedCommit !== void 0 ? { lastIndexedCommit } : {},
        stale
      };
      return [repo];
    },
    async loadRepository(_id, signal) {
      const { projectId, token } = requireProject();
      const filesResult = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        `/api/projects/${encodeURIComponent(projectId)}/files`,
        void 0,
        signal
      );
      const fileRows = (filesResult.files ?? []).map((f) => {
        const file = f && typeof f === "object" ? f : {};
        const path = typeof file.path === "string" ? file.path : "";
        if (!path) return void 0;
        return {
          fileId: typeof file.fileId === "string" ? file.fileId : "",
          path,
          language: asString(file.language),
          stale: file.stale === true
        };
      }).filter((f) => f !== void 0);
      const tree = buildFileTree(fileRows);
      let gitStatus = { branch: "main", ahead: 0, behind: 0, entries: [] };
      try {
        const gitResult = await apiJson3(
          baseUrl,
          fetchImpl,
          token,
          "GET",
          `/api/projects/${encodeURIComponent(projectId)}/git/status`,
          void 0,
          signal
        );
        const status = gitResult.status;
        const currentCommit = asString(status?.currentCommit);
        const entries = [];
        for (const e of status?.entries ?? []) {
          if (!e || typeof e !== "object") continue;
          const entry = e;
          const path = typeof entry.path === "string" ? entry.path : "";
          if (!path) continue;
          entries.push({ path, status: mapGitStatus(typeof entry.status === "string" ? entry.status : "") });
        }
        gitStatus = {
          branch: currentCommit && currentCommit !== "main" ? `commit-${currentCommit.slice(0, 8)}` : "main",
          ahead: 0,
          behind: 0,
          entries
        };
      } catch {
      }
      return { tree, gitStatus };
    },
    async searchFiles(query, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/search`,
        { query, type: "files", maxResults: 20 },
        signal
      );
      const files = (result.result?.files ?? []).map((f) => {
        const file = f && typeof f === "object" ? f : {};
        const path = typeof file.path === "string" ? file.path : "";
        const summary = typeof file.summary === "string" ? file.summary : void 0;
        return { path, line: 0, snippet: summary ?? path };
      }).filter((f) => f.path);
      return files;
    },
    async searchSymbols(query, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/search`,
        { query, type: "symbols", maxResults: 20 },
        signal
      );
      const symbols = (result.result?.symbols ?? []).map((s) => {
        const sym = s && typeof s === "object" ? s : {};
        const name = typeof sym.name === "string" ? sym.name : "";
        const kind = typeof sym.kind === "string" ? sym.kind : "";
        const path = typeof sym.path === "string" ? sym.path : "";
        const line = typeof sym.line === "number" ? sym.line : 0;
        if (!name && !path) return void 0;
        return { name, kind, path, line };
      }).filter((s) => s !== void 0);
      return symbols;
    },
    async getFile(path, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/files/read`,
        { path },
        signal
      );
      if (!result.file) {
        throw new Error("file_not_found");
      }
      const fileLanguage = asString(result.file.language);
      const chunks = (result.chunks ?? []).map((c) => {
        const chunk = c && typeof c === "object" ? c : {};
        return typeof chunk.text === "string" ? chunk.text : "";
      }).filter((t5) => typeof t5 === "string");
      const content = chunks.join("");
      const file = {
        path,
        content,
        language: languageFromPath(path, fileLanguage)
      };
      return file;
    },
    async getDiff(base, head, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/diff`,
        { base, head },
        signal
      );
      const diffPreview = { base, head, diff: typeof result.diff === "string" ? result.diff : "" };
      return diffPreview;
    },
    async listWorktreeLeases(signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        `/api/projects/${encodeURIComponent(projectId)}/code-control/leases`,
        void 0,
        signal
      );
      const leases = [];
      for (const raw of result.leases ?? []) {
        if (!raw || typeof raw !== "object") continue;
        const row2 = raw;
        const leaseId = asString(row2.leaseId);
        const worktreePath = asString(row2.worktreePath);
        const baseCommit = asString(row2.baseCommit);
        const createdAtMs = typeof row2.createdAtMs === "number" ? row2.createdAtMs : Number(row2.createdAtMs);
        const updatedAtMs = typeof row2.updatedAtMs === "number" ? row2.updatedAtMs : Number(row2.updatedAtMs);
        if (!leaseId || !worktreePath || !baseCommit || !Number.isFinite(createdAtMs) || !Number.isFinite(updatedAtMs)) continue;
        const publishedCommit = asString(row2.publishedCommit);
        leases.push({
          leaseId,
          action: asString(row2.action) ?? "apply",
          worktreePath,
          baseCommit,
          createdAtMs,
          updatedAtMs,
          ...publishedCommit !== void 0 ? { publishedCommit } : {}
        });
      }
      return leases;
    },
    async requestPatchApproval(patch, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/patches`,
        {
          diff: patch.diff,
          path: patch.files[0],
          issueRef: patch.title
        },
        signal
      );
      const proposalId = typeof result.proposal?.proposalId === "string" ? result.proposal.proposalId : "";
      if (!proposalId) throw new Error("patch_proposal_failed");
      return { approvalId: proposalId };
    },
    async approvePatch(approvalId, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/patches/${encodeURIComponent(approvalId)}/approve`,
        void 0,
        signal
      );
      if (!result.approvalToken || !result.publishToken || !result.rollbackToken) {
        throw new Error("patch_approval_token_missing");
      }
      return {
        approvalToken: result.approvalToken,
        publishToken: result.publishToken,
        rollbackToken: result.rollbackToken
      };
    },
    async applyPatch(approvalId, approvalToken, signal) {
      requireDesktopExecution();
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/patches/${encodeURIComponent(approvalId)}/apply`,
        { approvalToken },
        signal
      );
      const r = result.result ?? {};
      if (!r.success) throw new Error(r.message ?? "patch_apply_failed");
      const applyResult = { success: true };
      if (r.fileWritten === true) applyResult.fileWritten = true;
      if (r.code !== void 0) applyResult.code = r.code;
      if (r.message !== void 0) applyResult.message = r.message;
      return applyResult;
    },
    async publishPatch(approvalId, publishToken, signal) {
      requireDesktopExecution();
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/patches/${encodeURIComponent(approvalId)}/publish`,
        { approvalToken: publishToken },
        signal
      );
      const r = result.result ?? {};
      if (!r.success) throw new Error(r.message ?? "patch_publish_failed");
      const publishResult = { success: true };
      if (r.publishCommit !== void 0) publishResult.publishCommit = r.publishCommit;
      if (r.code !== void 0) publishResult.code = r.code;
      if (r.message !== void 0) publishResult.message = r.message;
      return publishResult;
    },
    async rollback(approvalId, rollbackToken, signal) {
      requireDesktopExecution();
      const { projectId, token } = requireProject();
      await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/patches/${encodeURIComponent(approvalId)}/rollback`,
        { approvalToken: rollbackToken },
        signal
      );
    },
    async requestCommandApproval(command, args, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/commands/approve`,
        { command, args },
        signal
      );
      if (!result.token) throw new Error("command_approval_token_missing");
      return { token: result.token };
    },
    async runCommand(command, args, options2 = {}, signal) {
      requireDesktopExecution();
      const { projectId, token } = requireProject();
      let approvalToken = options2.approvalToken;
      if (!approvalToken) {
        const approved = await this.requestCommandApproval(command, args, signal);
        approvalToken = approved.token;
      }
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/commands`,
        { command, args, timeoutMs: options2.timeoutMs, env: options2.env, approvalToken },
        signal
      );
      const r = result.result ?? {};
      return {
        command: typeof r.command === "string" ? r.command : command,
        args: Array.isArray(r.args) ? r.args : args,
        exitCode: typeof r.exitCode === "number" ? r.exitCode : -1,
        stdout: typeof r.stdout === "string" ? r.stdout : "",
        stderr: typeof r.stderr === "string" ? r.stderr : "",
        durationMs: typeof r.durationMs === "number" ? r.durationMs : 0,
        allowed: r.allowed === true,
        cancelled: r.cancelled === true,
        reason: typeof r.reason === "string" ? r.reason : void 0
      };
    },
    async generateTask(request, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson3(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/projects/${encodeURIComponent(projectId)}/coding/tasks`,
        {
          ...request,
          mode: surface === "desktop" ? "approval_required" : "plan_only",
          surface
        },
        signal,
        [409]
      );
      const changes = Array.isArray(result.changes) ? result.changes.map((c) => {
        const edit = c && typeof c === "object" ? c : {};
        return {
          path: typeof edit.path === "string" ? edit.path : "",
          content: typeof edit.content === "string" ? edit.content : "",
          operation: edit.operation === "append" || edit.operation === "delete" ? edit.operation : "write"
        };
      }) : void 0;
      return {
        success: result.success === true || result.status === "approval_required",
        taskId: typeof result.taskId === "string" ? result.taskId : void 0,
        status: typeof result.status === "string" ? result.status : void 0,
        changes,
        realExecutionPerformed: result.realExecutionPerformed === true,
        patchHash: typeof result.patchHash === "string" ? result.patchHash : void 0,
        executionAuthority: result.executionAuthority === "desktop" || result.executionAuthority === "handoff" ? result.executionAuthority : executionAuthority,
        handoffRequired: result.handoffRequired === true || executionAuthority === "handoff",
        errorCode: typeof result.errorCode === "string" ? result.errorCode : void 0,
        errorMessage: typeof result.errorMessage === "string" ? result.errorMessage : void 0
      };
    },
    async runTests(signal) {
      return this.runCommand("pnpm", ["test"], {}, signal);
    },
    async runBuild(signal) {
      return this.runCommand("pnpm", ["build"], {}, signal);
    }
  };
}

// src/workspace/workspaceCodeIntelligenceService.ts
async function apiJson4(baseUrl, fetchImpl, token, method, path, body, signal) {
  const headers2 = {};
  if (token) headers2.Authorization = `Bearer ${token}`;
  if (body !== void 0) headers2["Content-Type"] = "application/json";
  const init = { method, headers: headers2, signal: signal ?? null };
  if (body !== void 0) init.body = JSON.stringify(body);
  let response;
  try {
    response = await fetchImpl(`${baseUrl}${path}`, init);
  } catch (err) {
    throw new Error(err instanceof Error && err.name === "AbortError" ? "Cancelled" : "network_error");
  }
  const text2 = await response.text();
  let payload = {};
  try {
    payload = text2 ? JSON.parse(text2) : {};
  } catch {
    throw new Error(response.ok ? "invalid_json" : `http_error_${response.status}`);
  }
  if (!response.ok || payload.success === false) {
    const errorMessage = typeof payload.error === "string" ? payload.error : `http_error_${response.status}`;
    const errorCode = typeof payload.errorCode === "string" ? payload.errorCode : `http_error_${response.status}`;
    const err = new Error(errorMessage);
    err.code = errorCode;
    throw err;
  }
  return payload;
}
function createWorkspaceCodeIntelligenceService(options) {
  const baseUrl = options.baseUrl ?? "";
  const getToken = options.getToken;
  const getProjectContext = options.getProjectContext;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  function requireProject() {
    const projectId = getProjectContext()?.projectId;
    if (!projectId) throw new Error("project_not_selected");
    const token = getToken();
    if (!token) throw new Error("unauthenticated");
    return { projectId, token };
  }
  function projectPath(projectId, subpath) {
    return `/api/projects/${encodeURIComponent(projectId)}/code-intelligence${subpath}`;
  }
  return {
    async getHealth(signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        projectPath(projectId, "/health"),
        void 0,
        signal
      );
      return result.health ?? { available: false, emergencyStopped: false, autoIndex: false };
    },
    async listRepositories(signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        projectPath(projectId, "/repositories"),
        void 0,
        signal
      );
      return result.repositories ?? [];
    },
    async addRepository(root, signal) {
      const { projectId, token } = requireProject();
      await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        projectPath(projectId, "/repositories"),
        { root },
        signal
      );
    },
    async removeRepository(id, signal) {
      const { projectId, token } = requireProject();
      const approval = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/remove-approval`),
        {},
        signal
      );
      await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/remove`),
        { approvalRef: approval.approval.token },
        signal
      );
    },
    async indexRepository(id, mode, signal) {
      const { projectId, token } = requireProject();
      const approval = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/index-approval`),
        { mode },
        signal
      );
      await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/index`),
        { mode, approvalRef: approval.approval.token },
        signal
      );
    },
    async deleteIndex(id, signal) {
      const { projectId, token } = requireProject();
      const approval = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/delete-index-approval`),
        {},
        signal
      );
      await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/delete-index`),
        { approvalRef: approval.approval.token },
        signal
      );
    },
    async searchCode(id, pattern, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/search-code`),
        { pattern },
        signal
      );
      return result.results ?? [];
    },
    async searchGraph(id, query, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/search-graph`),
        { query },
        signal
      );
      return result.results ?? [];
    },
    async queryGraph(id, query, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/query-graph`),
        { query },
        signal
      );
      return result.results ?? [];
    },
    async getArchitecture(id, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/architecture`),
        void 0,
        signal
      );
      return result.result ?? null;
    },
    async getGraphSchema(id, signal) {
      const { projectId, token } = requireProject();
      const result = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/graph-schema`),
        void 0,
        signal
      );
      return result.result ?? null;
    },
    async getCodeSnippet(id, qualifiedName, signal) {
      const { projectId, token } = requireProject();
      const encoded = encodeURIComponent(qualifiedName);
      const result = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/code-snippets?qualifiedName=${encoded}`),
        void 0,
        signal
      );
      return result.result ?? null;
    },
    async tracePath(id, functionName, signal) {
      const { projectId, token } = requireProject();
      const encoded = encodeURIComponent(functionName);
      const result = await apiJson4(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        projectPath(projectId, `/repositories/${encodeURIComponent(id)}/trace-path?functionName=${encoded}`),
        void 0,
        signal
      );
      return result.result ?? null;
    }
  };
}

// src/workspace/workspaceAgentSkillsService.ts
async function apiJson5(baseUrl, fetchImpl, token, method, path, body, signal) {
  const headers2 = {};
  if (token) headers2.Authorization = `Bearer ${token}`;
  if (body !== void 0) headers2["Content-Type"] = "application/json";
  const init = { method, headers: headers2, signal: signal ?? null };
  if (body !== void 0) init.body = JSON.stringify(body);
  let response;
  try {
    response = await fetchImpl(`${baseUrl}${path}`, init);
  } catch (err) {
    throw new Error(err instanceof Error && err.name === "AbortError" ? "Cancelled" : "network_error");
  }
  const text2 = await response.text();
  let payload = {};
  try {
    payload = text2 ? JSON.parse(text2) : {};
  } catch {
    throw new Error(response.ok ? "invalid_json" : `http_error_${response.status}`);
  }
  if (!response.ok || payload.success === false) {
    const errorMessage = typeof payload.error === "string" ? payload.error : `http_error_${response.status}`;
    const errorCode = typeof payload.errorCode === "string" ? payload.errorCode : `http_error_${response.status}`;
    const err = new Error(errorMessage);
    err.code = errorCode;
    throw err;
  }
  return payload;
}
function toStringArray(v) {
  if (Array.isArray(v)) return v.map((x) => String(x));
  return void 0;
}
function toRisk(v) {
  if (v === "low" || v === "medium" || v === "high" || v === "critical") return v;
  return void 0;
}
function summaryFromManifest(m, id, contentPreview) {
  const manifest = m ?? {};
  const statusStr = String(manifest.status ?? manifest.state ?? "installed");
  const status = statusStr === "enabled" ? "enabled" : statusStr === "disabled" ? "disabled" : statusStr === "removed" ? "removed" : "installed";
  const enabled = status === "enabled" || Boolean(manifest.enabledFor && Array.isArray(manifest.enabledFor) && manifest.enabledFor.length > 0);
  return {
    id: String(manifest.extensionId ?? manifest.id ?? id),
    extensionId: String(manifest.extensionId ?? manifest.id ?? id),
    name: String(manifest.name ?? manifest.extensionId ?? id),
    description: manifest.description ? String(manifest.description) : void 0,
    kind: manifest.kind ? String(manifest.kind) : void 0,
    category: manifest.category ? String(manifest.category) : void 0,
    status,
    enabled,
    publisher: manifest.publisher ? String(manifest.publisher) : void 0,
    publisherTrusted: typeof manifest.publisherTrusted === "boolean" ? manifest.publisherTrusted : void 0,
    source: manifest.source ? String(manifest.source) : void 0,
    version: manifest.version ? String(manifest.version) : void 0,
    installedAt: manifest.installedAt ? String(manifest.installedAt) : void 0,
    installedBy: manifest.installedBy ? String(manifest.installedBy) : void 0,
    risk: toRisk(manifest.risk),
    defaultMode: manifest.defaultMode ? String(manifest.defaultMode) : void 0,
    approvalRequired: typeof manifest.approvalRequired === "boolean" ? manifest.approvalRequired : void 0,
    requiredScopes: toStringArray(manifest.requiredScopes ?? manifest.permissions),
    supportsSurfaces: toStringArray(manifest.supportsSurfaces ?? manifest.supportedSurfaces),
    checksum: manifest.checksum ? String(manifest.checksum) : void 0,
    contentDigest: manifest.contentDigest ? String(manifest.contentDigest) : void 0,
    provenance: typeof manifest.provenance === "object" && manifest.provenance !== null ? manifest.provenance : void 0,
    conflictMetadata: typeof manifest.conflictMetadata === "object" && manifest.conflictMetadata !== null ? manifest.conflictMetadata : void 0,
    contentPreview
  };
}
function createWorkspaceAgentSkillsService(options) {
  const baseUrl = options.baseUrl ?? "";
  const getToken = options.getToken;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  function requireToken() {
    const token = getToken();
    if (!token) throw new Error("unauthenticated");
    return token;
  }
  return {
    async getHealth(signal) {
      const token = requireToken();
      const result = await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        "/api/control-center/health",
        void 0,
        signal
      );
      return { status: result.status ?? "ok", extensionCount: result.extensionCount ?? 0 };
    },
    async listSkills(signal) {
      const token = requireToken();
      const result = await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        "/api/control-center/extensions",
        void 0,
        signal
      );
      return (result.extensions ?? []).map((ext) => summaryFromManifest(ext, String(ext.extensionId ?? ext.id ?? "")));
    },
    async importSkill(input, signal) {
      const token = requireToken();
      const result = await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        "/api/control-center/agent-skills",
        { action: "import", ...input },
        signal
      );
      return { id: result.id ?? result.manifest?.id ?? result.manifest?.extensionId ?? "", name: result.manifest?.name ?? "" };
    },
    async approveSkill(id, signal) {
      const token = requireToken();
      const result = await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/control-center/extensions/${encodeURIComponent(id)}/approve`,
        {},
        signal
      );
      return { receipt: result.receipt };
    },
    async enableSkill(id, receipt, signal) {
      const token = requireToken();
      await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/control-center/extensions/${encodeURIComponent(id)}/enable`,
        { receipt },
        signal
      );
    },
    async executeSkill(id, input, signal) {
      const token = requireToken();
      const result = await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/control-center/extensions/${encodeURIComponent(id)}/execute`,
        {
          context: {
            surface: input.surface,
            userId: "workspace-user",
            grantedScopes: input.grantedScopes
          },
          scriptName: input.scriptName,
          args: input.args
        },
        signal
      );
      return result.execution ?? {
        allowed: false,
        success: false,
        realExecutionPerformed: false,
        error: "no_execution_result"
      };
    },
    async disableSkill(id, signal) {
      const token = requireToken();
      await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/control-center/extensions/${encodeURIComponent(id)}/disable`,
        {},
        signal
      );
    },
    async removeSkill(id, signal) {
      const token = requireToken();
      await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/control-center/extensions/${encodeURIComponent(id)}/uninstall`,
        {},
        signal
      );
    },
    async getSkillDetail(id, signal) {
      const token = requireToken();
      const result = await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "GET",
        `/api/control-center/extensions/${encodeURIComponent(id)}`,
        void 0,
        signal
      );
      return summaryFromManifest(result.manifest ?? {}, id, result.contentPreview);
    },
    async checkSkillUpdate(id, signal) {
      const token = requireToken();
      const result = await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/control-center/extensions/${encodeURIComponent(id)}/check-update`,
        {},
        signal
      );
      return result.updateCheck ?? { allowed: false, updateAvailable: false, currentCommitSha: "", candidateCommitSha: "", licenseChanged: false, riskChanged: false, scopeChanged: false, reason: "no_update_check_result" };
    },
    async updateSkill(id, signal) {
      const token = requireToken();
      const result = await apiJson5(
        baseUrl,
        fetchImpl,
        token,
        "POST",
        `/api/control-center/extensions/${encodeURIComponent(id)}/update`,
        {},
        signal
      );
      return summaryFromManifest(result.manifest ?? {}, id, result.contentPreview);
    }
  };
}

// src/workspace/CallSheetPanel.tsx
import React44, { useCallback as useCallback27, useEffect as useEffect33, useMemo as useMemo20, useState as useState41 } from "react";
import { jsx as jsx80, jsxs as jsxs72 } from "react/jsx-runtime";
var STRINGS4 = {
  title: { en: "Call Sheet", hi: "\u0915\u0949\u0932 \u0936\u0940\u091F" },
  subtitle: {
    en: "Structured intake from voice calls. Contacts stay masked; AI summaries are labeled.",
    hi: "Voice calls \u0915\u093E structured intake\u0964 Contacts masked \u0930\u0939\u0924\u0947 \u0939\u0948\u0902; AI summaries labeled \u0939\u0948\u0902\u0964"
  },
  refresh: { en: "Refresh", hi: "\u0930\u0940\u092B\u094D\u0930\u0947\u0936" },
  refreshing: { en: "Loading\u2026", hi: "\u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026" },
  unread: { en: "new", hi: "\u0928\u0908" },
  search: { en: "Search name / topic / message / ticket\u2026", hi: "\u0928\u093E\u092E / \u0935\u093F\u0937\u092F / \u0938\u0902\u0926\u0947\u0936 / \u091F\u093F\u0915\u091F \u0916\u094B\u091C\u0947\u0902\u2026" },
  allStatuses: { en: "All statuses", hi: "\u0938\u092D\u0940 \u0938\u094D\u0925\u093F\u0924\u093F\u092F\u093E\u0901" },
  allTypes: { en: "All request types", hi: "\u0938\u092D\u0940 \u092A\u094D\u0930\u0915\u093E\u0930" },
  allPriorities: { en: "All priorities", hi: "\u0938\u092D\u0940 \u092A\u094D\u0930\u093E\u0925\u092E\u093F\u0915\u0924\u093E\u090F\u0901" },
  app: { en: "App", hi: "\u0910\u092A" },
  today: { en: "Today", hi: "\u0906\u091C" },
  unreadOnly: { en: "Unread", hi: "\u0928\u092A\u0922\u093C\u0947" },
  callback: { en: "Callback", hi: "\u0915\u0949\u0932\u092C\u0948\u0915" },
  support: { en: "Support", hi: "\u0938\u092A\u094B\u0930\u094D\u091F" },
  assigned: { en: "Assigned", hi: "\u0938\u094C\u0902\u092A\u0947 \u0917\u090F" },
  from: { en: "From", hi: "\u0938\u0947" },
  to: { en: "To", hi: "\u0924\u0915" },
  exportMasked: { en: "Export CSV (masked)", hi: "CSV \u0928\u093F\u0930\u094D\u092F\u093E\u0924 (masked)" },
  exportFull: { en: "Export full contact", hi: "\u092A\u0942\u0930\u094D\u0923 contact \u0928\u093F\u0930\u094D\u092F\u093E\u0924" },
  colTime: { en: "Time", hi: "\u0938\u092E\u092F" },
  colCaller: { en: "Caller", hi: "\u0915\u0949\u0932\u0930" },
  colContact: { en: "Contact", hi: "\u0938\u0902\u092A\u0930\u094D\u0915" },
  colApp: { en: "App", hi: "\u0910\u092A" },
  colType: { en: "Type", hi: "\u092A\u094D\u0930\u0915\u093E\u0930" },
  colTopic: { en: "Topic", hi: "\u0935\u093F\u0937\u092F" },
  colPriority: { en: "Priority", hi: "\u092A\u094D\u0930\u093E\u0925\u092E\u093F\u0915\u0924\u093E" },
  colStatus: { en: "Status", hi: "\u0938\u094D\u0925\u093F\u0924\u093F" },
  colTicket: { en: "Ticket", hi: "\u091F\u093F\u0915\u091F" },
  empty: { en: "No call intakes match the filters.", hi: "\u092B\u093C\u093F\u0932\u094D\u091F\u0930 \u0938\u0947 \u0915\u094B\u0908 call intake \u0928\u0939\u0940\u0902 \u092E\u093F\u0932\u093E\u0964" },
  error: { en: "Load failed", hi: "\u0932\u094B\u0921 \u0935\u093F\u092B\u0932" },
  emptyToken: { en: "Session token required.", hi: "\u0938\u0924\u094D\u0930 \u091F\u094B\u0915\u0928 \u0906\u0935\u0936\u094D\u092F\u0915 \u0939\u0948\u0964" },
  aiSummary: { en: "AI summary for owner", hi: "\u092E\u093E\u0932\u093F\u0915 \u0915\u0947 \u0932\u093F\u090F AI \u0938\u093E\u0930\u093E\u0902\u0936" },
  summary: { en: "Summary", hi: "\u0938\u093E\u0930\u093E\u0902\u0936" },
  details: { en: "Details", hi: "\u0935\u093F\u0935\u0930\u0923" },
  close: { en: "Close", hi: "\u092C\u0902\u0926" },
  markReviewed: { en: "Mark reviewed", hi: "Reviewed \u0915\u0930\u0947\u0902" },
  saveStatus: { en: "Save", hi: "\u0938\u0939\u0947\u091C\u0947\u0902" },
  internalNote: { en: "Owner internal note", hi: "\u092E\u093E\u0932\u093F\u0915 \u0915\u093E \u0906\u0902\u0924\u0930\u093F\u0915 \u0928\u094B\u091F" },
  assignTo: { en: "Assign to actor", hi: "Actor \u0915\u094B \u0938\u094C\u0902\u092A\u0947\u0902" },
  revealContact: { en: "Reveal contact", hi: "Contact \u0926\u093F\u0916\u093E\u090F\u0901" },
  revealed: { en: "Full contact", hi: "\u092A\u0942\u0930\u093E contact" },
  noContact: { en: "\u2014", hi: "\u2014" },
  callbackWanted: { en: "Callback requested", hi: "\u0915\u0949\u0932\u092C\u0948\u0915 \u092E\u093E\u0901\u0917\u093E" },
  callbackWindow: { en: "Preferred window", hi: "\u092A\u0938\u0902\u0926\u0940\u0926\u093E \u0938\u092E\u092F" },
  handoff: { en: "Handoff", hi: "\u0939\u0948\u0902\u0921\u0911\u092B\u093C" },
  duration: { en: "Duration", hi: "\u0905\u0935\u0927\u093F" },
  source: { en: "Source", hi: "\u0938\u094D\u0930\u094B\u0924" },
  assistantVer: { en: "Assistant", hi: "\u0905\u0938\u093F\u0938\u094D\u091F\u0947\u0902\u091F" },
  callId: { en: "Call ID", hi: "\u0915\u0949\u0932 ID" },
  ticket: { en: "Support ticket", hi: "\u0938\u092A\u094B\u0930\u094D\u091F \u091F\u093F\u0915\u091F" },
  saving: { en: "Saving\u2026", hi: "\u0938\u0939\u0947\u091C\u093E \u091C\u093E \u0930\u0939\u093E \u0939\u0948\u2026" }
};
function tr12(key, locale) {
  return STRINGS4[key]?.[locale] ?? key;
}
var STATUSES = ["new", "reviewed", "in_progress", "done", "no_action"];
var TYPES = ["information", "demo", "pricing", "order", "support", "complaint", "callback", "human_handoff", "other"];
var PRIORITIES = ["low", "normal", "high", "urgent"];
var STATUS_COLORS = {
  new: "#2563eb",
  reviewed: "#c2410c",
  in_progress: "#c2410c",
  done: tokens.colors.success,
  no_action: "#6b7280"
};
var PRIORITY_COLORS = {
  low: "#6b7280",
  normal: "#2563eb",
  high: "#c2410c",
  urgent: tokens.colors.danger
};
function fmtTime(iso, locale) {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  return d.toLocaleString(locale === "hi" ? "hi-IN" : "en-GB", { hour12: false });
}
function fmtDuration(ms) {
  if (ms === void 0 || ms === null) return "\u2014";
  const s = Math.round(ms / 1e3);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
}
function CallSheetPanel({ token, locale = "en", theme: theme2 = "light", fetchImpl = fetch }) {
  const colors = themeColors(theme2);
  const [data, setData] = useState41(null);
  const [loading, setLoading] = useState41(true);
  const [error, setError] = useState41(null);
  const [expanded, setExpanded] = useState41(null);
  const [revealed, setRevealed] = useState41({});
  const [savingId, setSavingId] = useState41(null);
  const [noteDraft, setNoteDraft] = useState41("");
  const [assignDraft, setAssignDraft] = useState41("");
  const [statusDraft, setStatusDraft] = useState41("new");
  const [search, setSearch] = useState41("");
  const [status, setStatus] = useState41("");
  const [requestType, setRequestType] = useState41("");
  const [priority, setPriority] = useState41("");
  const [appId, setAppId] = useState41("");
  const [from, setFrom] = useState41("");
  const [to, setTo] = useState41("");
  const [quick, setQuick] = useState41("");
  const headers2 = useMemo20(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const buildQuery = useCallback27(() => {
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (status) qs.set("status", status);
    if (requestType) qs.set("requestType", requestType);
    if (priority) qs.set("priority", priority);
    if (appId) qs.set("appId", appId);
    if (from) qs.set("from", `${from}T00:00:00.000Z`);
    if (to) qs.set("to", `${to}T23:59:59.999Z`);
    if (quick) qs.set(quick === "unread" ? "unread" : quick, "1");
    return qs;
  }, [search, status, requestType, priority, appId, from, to, quick]);
  const load = useCallback27(async () => {
    if (!token) {
      setError(tr12("emptyToken", locale));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const qs = buildQuery();
      const res = await fetchImpl(`/api/admin/voice/call-sheet${qs.size ? `?${qs}` : ""}`, { headers: headers2 });
      const body = await res.json();
      if (!res.ok || !body.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      setData(body);
    } catch {
      setError(safeErrorText("load_failed", locale).title);
    } finally {
      setLoading(false);
    }
  }, [token, locale, fetchImpl, headers2, buildQuery]);
  useEffect33(() => {
    void load();
  }, [load]);
  const exportCsv = useCallback27(
    async (fullContact) => {
      if (!token) return;
      try {
        const qs = buildQuery();
        if (fullContact) qs.set("fullContact", "1");
        const res = await fetchImpl(`/api/admin/voice/call-sheet/export?${qs}`, { headers: headers2 });
        const body = await res.json();
        if (!res.ok || !body.ok || !body.csv) throw new Error(`HTTP ${res.status}`);
        const blob = new Blob([body.csv], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = body.filename ?? "call-sheet.csv";
        a.click();
        URL.revokeObjectURL(a.href);
      } catch {
        setError(safeErrorText("load_failed", locale).title);
      }
    },
    [token, fetchImpl, headers2, buildQuery, locale]
  );
  const reveal = useCallback27(
    async (id) => {
      if (!token) return;
      try {
        const res = await fetchImpl(`/api/admin/voice/call-sheet/${encodeURIComponent(id)}/contact`, { headers: headers2 });
        const body = await res.json();
        if (!res.ok || !body.ok || !body.contact) throw new Error(`HTTP ${res.status}`);
        setRevealed((p) => ({ ...p, [id]: body.contact }));
      } catch {
        setError(safeErrorText("load_failed", locale).title);
      }
    },
    [token, fetchImpl, headers2, locale]
  );
  const saveRow = useCallback27(
    async (id) => {
      if (!token) return;
      setSavingId(id);
      try {
        const res = await fetchImpl(`/api/admin/voice/call-sheet/${encodeURIComponent(id)}`, {
          method: "POST",
          headers: { ...headers2, "Content-Type": "application/json" },
          body: JSON.stringify({
            status: statusDraft,
            ...assignDraft ? { assignedActorId: assignDraft } : {},
            ...noteDraft ? { ownerInternalNote: noteDraft } : {}
          })
        });
        const body = await res.json();
        if (!res.ok || !body.ok) throw new Error(`HTTP ${res.status}`);
        setNoteDraft("");
        setAssignDraft("");
        await load();
      } catch {
        setError(safeErrorText("settings_save_failed", locale).title);
      } finally {
        setSavingId(null);
      }
    },
    [token, fetchImpl, headers2, statusDraft, assignDraft, noteDraft, load, locale]
  );
  const filterBtn = (key) => /* @__PURE__ */ jsx80(
    "button",
    {
      type: "button",
      "data-testid": `callsheet-quick-${key}`,
      onClick: () => setQuick((q) => q === key ? "" : key),
      style: {
        padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
        borderRadius: tokens.radius.sm,
        border: `1px solid ${quick === key ? colors.info : colors.border}`,
        background: quick === key ? colors.info : "transparent",
        color: quick === key ? "#fff" : colors.text,
        fontSize: "12px",
        cursor: "pointer"
      },
      children: tr12(key === "unread" ? "unreadOnly" : key, locale)
    },
    key
  );
  return /* @__PURE__ */ jsxs72("div", { "data-testid": "call-sheet-panel", style: { display: "flex", flexDirection: "column", gap: tokens.spacing.md }, children: [
    /* @__PURE__ */ jsxs72("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacing.md, flexWrap: "wrap" }, children: [
      /* @__PURE__ */ jsxs72("div", { children: [
        /* @__PURE__ */ jsxs72("h3", { style: { margin: 0, fontSize: "16px", color: colors.text }, children: [
          tr12("title", locale),
          data && data.unread > 0 ? /* @__PURE__ */ jsxs72("span", { "data-testid": "callsheet-unread-badge", style: { marginLeft: tokens.spacing.sm, padding: "2px 8px", borderRadius: "10px", background: tokens.colors.danger, color: "#fff", fontSize: "11px", verticalAlign: "middle" }, children: [
            data.unread,
            " ",
            tr12("unread", locale)
          ] }) : null
        ] }),
        /* @__PURE__ */ jsx80("p", { style: { margin: "4px 0 0", fontSize: "12px", color: colors.textMuted }, children: tr12("subtitle", locale) })
      ] }),
      /* @__PURE__ */ jsxs72("div", { style: { display: "flex", gap: tokens.spacing.sm, flexWrap: "wrap" }, children: [
        /* @__PURE__ */ jsx80("button", { type: "button", "data-testid": "callsheet-export-masked", onClick: () => void exportCsv(false), style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, cursor: "pointer", fontSize: "13px" }, children: tr12("exportMasked", locale) }),
        /* @__PURE__ */ jsx80("button", { type: "button", "data-testid": "callsheet-export-full", onClick: () => void exportCsv(true), style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: colors.surface, color: tokens.colors.danger, cursor: "pointer", fontSize: "13px" }, children: tr12("exportFull", locale) }),
        /* @__PURE__ */ jsx80("button", { type: "button", "data-testid": "callsheet-refresh", onClick: () => void load(), disabled: loading, style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, cursor: loading ? "default" : "pointer", fontSize: "13px" }, children: loading ? tr12("refreshing", locale) : tr12("refresh", locale) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs72("div", { style: { display: "flex", gap: tokens.spacing.sm, flexWrap: "wrap", alignItems: "center" }, children: [
      /* @__PURE__ */ jsx80(
        "input",
        {
          "data-testid": "callsheet-search",
          value: search,
          onChange: (e) => setSearch(e.target.value),
          placeholder: tr12("search", locale),
          style: { padding: tokens.spacing.sm, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, fontSize: "12px", minWidth: "220px" }
        }
      ),
      /* @__PURE__ */ jsxs72("select", { "data-testid": "callsheet-filter-status", value: status, onChange: (e) => setStatus(e.target.value), style: { padding: tokens.spacing.sm, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, fontSize: "12px" }, children: [
        /* @__PURE__ */ jsx80("option", { value: "", children: tr12("allStatuses", locale) }),
        STATUSES.map((s) => /* @__PURE__ */ jsx80("option", { value: s, children: s }, s))
      ] }),
      /* @__PURE__ */ jsxs72("select", { "data-testid": "callsheet-filter-type", value: requestType, onChange: (e) => setRequestType(e.target.value), style: { padding: tokens.spacing.sm, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, fontSize: "12px" }, children: [
        /* @__PURE__ */ jsx80("option", { value: "", children: tr12("allTypes", locale) }),
        TYPES.map((t5) => /* @__PURE__ */ jsx80("option", { value: t5, children: t5 }, t5))
      ] }),
      /* @__PURE__ */ jsxs72("select", { "data-testid": "callsheet-filter-priority", value: priority, onChange: (e) => setPriority(e.target.value), style: { padding: tokens.spacing.sm, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, fontSize: "12px" }, children: [
        /* @__PURE__ */ jsx80("option", { value: "", children: tr12("allPriorities", locale) }),
        PRIORITIES.map((p) => /* @__PURE__ */ jsx80("option", { value: p, children: p }, p))
      ] }),
      /* @__PURE__ */ jsx80(
        "input",
        {
          "data-testid": "callsheet-filter-app",
          value: appId,
          onChange: (e) => setAppId(e.target.value),
          placeholder: tr12("app", locale),
          style: { padding: tokens.spacing.sm, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, fontSize: "12px", width: "130px" }
        }
      ),
      /* @__PURE__ */ jsxs72("label", { style: { fontSize: "11px", color: colors.textMuted, display: "flex", alignItems: "center", gap: "4px" }, children: [
        tr12("from", locale),
        /* @__PURE__ */ jsx80("input", { type: "date", "data-testid": "callsheet-filter-from", value: from, onChange: (e) => setFrom(e.target.value), style: { padding: "4px", borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, fontSize: "11px" } })
      ] }),
      /* @__PURE__ */ jsxs72("label", { style: { fontSize: "11px", color: colors.textMuted, display: "flex", alignItems: "center", gap: "4px" }, children: [
        tr12("to", locale),
        /* @__PURE__ */ jsx80("input", { type: "date", "data-testid": "callsheet-filter-to", value: to, onChange: (e) => setTo(e.target.value), style: { padding: "4px", borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, fontSize: "11px" } })
      ] }),
      ["today", "unread", "callback", "support", "assigned"].map(filterBtn)
    ] }),
    error ? /* @__PURE__ */ jsxs72("p", { role: "alert", style: { margin: 0, fontSize: "13px", color: tokens.colors.danger }, children: [
      tr12("error", locale),
      " \u2014 ",
      error
    ] }) : null,
    data ? /* @__PURE__ */ jsx80("div", { style: { overflowX: "auto", border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md }, children: /* @__PURE__ */ jsxs72("table", { style: { width: "100%", borderCollapse: "collapse", fontSize: "12px" }, children: [
      /* @__PURE__ */ jsx80("thead", { children: /* @__PURE__ */ jsxs72("tr", { style: { background: colors.surfaceStrong, textAlign: "left" }, children: [
        /* @__PURE__ */ jsx80("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr12("colTime", locale) }),
        /* @__PURE__ */ jsx80("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr12("colCaller", locale) }),
        /* @__PURE__ */ jsx80("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr12("colContact", locale) }),
        /* @__PURE__ */ jsx80("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr12("colApp", locale) }),
        /* @__PURE__ */ jsx80("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr12("colType", locale) }),
        /* @__PURE__ */ jsx80("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr12("colTopic", locale) }),
        /* @__PURE__ */ jsx80("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr12("colPriority", locale) }),
        /* @__PURE__ */ jsx80("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr12("colStatus", locale) }),
        /* @__PURE__ */ jsx80("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr12("colTicket", locale) })
      ] }) }),
      /* @__PURE__ */ jsx80("tbody", { children: data.items.length === 0 ? /* @__PURE__ */ jsx80("tr", { children: /* @__PURE__ */ jsx80("td", { colSpan: 9, "data-testid": "callsheet-empty", style: { padding: tokens.spacing.md, color: colors.textMuted, textAlign: "center" }, children: tr12("empty", locale) }) }) : data.items.map((r) => /* @__PURE__ */ jsxs72(React44.Fragment, { children: [
        /* @__PURE__ */ jsxs72(
          "tr",
          {
            "data-testid": `callsheet-row-${r.intakeId}`,
            onClick: () => {
              setExpanded((x) => x === r.intakeId ? null : r.intakeId);
              setStatusDraft(r.status);
              setNoteDraft(r.ownerInternalNote ?? "");
              setAssignDraft(r.assignedActorId ?? "");
            },
            style: { borderTop: `1px solid ${colors.border}`, cursor: "pointer", background: r.status === "new" ? theme2 === "dark" ? "#1e293b" : "#eff6ff" : void 0 },
            children: [
              /* @__PURE__ */ jsxs72("td", { style: { padding: tokens.spacing.sm, color: colors.textMuted, whiteSpace: "nowrap" }, children: [
                fmtTime(r.createdAt, locale),
                r.status === "new" ? /* @__PURE__ */ jsx80("span", { style: { marginLeft: "4px", display: "inline-block", width: "7px", height: "7px", borderRadius: "4px", background: "#2563eb" } }) : null
              ] }),
              /* @__PURE__ */ jsx80("td", { style: { padding: tokens.spacing.sm, color: colors.text }, children: r.callerName ?? "\u2014" }),
              /* @__PURE__ */ jsx80("td", { style: { padding: tokens.spacing.sm, color: colors.textMuted, fontFamily: "monospace" }, children: r.contactMasked ?? "\u2014" }),
              /* @__PURE__ */ jsx80("td", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: r.appId ?? "\u2014" }),
              /* @__PURE__ */ jsx80("td", { style: { padding: tokens.spacing.sm, color: colors.text }, children: r.requestType }),
              /* @__PURE__ */ jsx80("td", { style: { padding: tokens.spacing.sm, color: colors.textMuted, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: r.topic ?? "\u2014" }),
              /* @__PURE__ */ jsx80("td", { style: { padding: tokens.spacing.sm, color: PRIORITY_COLORS[r.priority] ?? colors.text, fontWeight: 600 }, children: r.priority }),
              /* @__PURE__ */ jsx80("td", { style: { padding: tokens.spacing.sm, color: STATUS_COLORS[r.status] ?? colors.text, fontWeight: 600 }, children: r.status }),
              /* @__PURE__ */ jsx80("td", { style: { padding: tokens.spacing.sm, color: colors.textMuted, fontFamily: "monospace", fontSize: "11px" }, children: r.supportTicketId ?? "\u2014" })
            ]
          }
        ),
        expanded === r.intakeId ? /* @__PURE__ */ jsx80("tr", { "data-testid": `callsheet-detail-${r.intakeId}`, children: /* @__PURE__ */ jsxs72("td", { colSpan: 9, style: { padding: tokens.spacing.md, background: colors.surface }, children: [
          /* @__PURE__ */ jsxs72("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: tokens.spacing.sm, fontSize: "12px" }, children: [
            /* @__PURE__ */ jsxs72("div", { children: [
              /* @__PURE__ */ jsxs72("strong", { children: [
                tr12("callId", locale),
                ":"
              ] }),
              " ",
              /* @__PURE__ */ jsx80("span", { style: { fontFamily: "monospace" }, children: r.providerCallId })
            ] }),
            /* @__PURE__ */ jsxs72("div", { children: [
              /* @__PURE__ */ jsxs72("strong", { children: [
                tr12("source", locale),
                ":"
              ] }),
              " ",
              r.source,
              " (",
              r.provider,
              ")"
            ] }),
            /* @__PURE__ */ jsxs72("div", { children: [
              /* @__PURE__ */ jsxs72("strong", { children: [
                tr12("assistantVer", locale),
                ":"
              ] }),
              " ",
              r.assistantVersion ?? "\u2014"
            ] }),
            /* @__PURE__ */ jsxs72("div", { children: [
              /* @__PURE__ */ jsxs72("strong", { children: [
                tr12("duration", locale),
                ":"
              ] }),
              " ",
              fmtDuration(r.durationMs)
            ] }),
            /* @__PURE__ */ jsxs72("div", { children: [
              /* @__PURE__ */ jsxs72("strong", { children: [
                tr12("callbackWanted", locale),
                ":"
              ] }),
              " ",
              r.callbackRequested ? "\u0939\u093E\u0901" : "\u2014",
              r.preferredCallbackWindow ? ` \xB7 ${tr12("callbackWindow", locale)}: ${r.preferredCallbackWindow}` : ""
            ] }),
            /* @__PURE__ */ jsxs72("div", { children: [
              /* @__PURE__ */ jsxs72("strong", { children: [
                tr12("handoff", locale),
                ":"
              ] }),
              " ",
              r.handoffRequested ? `requested${r.handoffCompleted ? " + completed" : ""}` : "\u2014"
            ] }),
            /* @__PURE__ */ jsxs72("div", { children: [
              /* @__PURE__ */ jsxs72("strong", { children: [
                tr12("ticket", locale),
                ":"
              ] }),
              " ",
              r.supportTicketId ?? "\u2014"
            ] }),
            /* @__PURE__ */ jsxs72("div", { children: [
              /* @__PURE__ */ jsxs72("strong", { children: [
                tr12("colContact", locale),
                ":"
              ] }),
              " ",
              revealed[r.intakeId] ?? r.contactMasked ?? "\u2014",
              r.contactSaved && !revealed[r.intakeId] ? /* @__PURE__ */ jsx80("button", { type: "button", "data-testid": `callsheet-reveal-${r.intakeId}`, onClick: (e) => {
                e.stopPropagation();
                void reveal(r.intakeId);
              }, style: { marginLeft: "6px", fontSize: "11px", padding: "2px 8px", borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, background: "transparent", color: tokens.colors.danger, cursor: "pointer" }, children: tr12("revealContact", locale) }) : null
            ] })
          ] }),
          r.messageForOwner ? /* @__PURE__ */ jsxs72("div", { style: { marginTop: tokens.spacing.sm, padding: tokens.spacing.sm, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.sm, background: theme2 === "dark" ? "#111827" : "#f8fafc" }, children: [
            /* @__PURE__ */ jsxs72("div", { style: { fontSize: "11px", color: colors.textMuted, marginBottom: "4px" }, children: [
              "\u{1F916} ",
              tr12("aiSummary", locale)
            ] }),
            /* @__PURE__ */ jsx80("div", { style: { fontSize: "13px", color: colors.text, whiteSpace: "pre-wrap" }, children: r.messageForOwner })
          ] }) : null,
          r.summary ? /* @__PURE__ */ jsxs72("p", { style: { fontSize: "12px", color: colors.textMuted }, children: [
            /* @__PURE__ */ jsxs72("strong", { children: [
              tr12("summary", locale),
              ":"
            ] }),
            " ",
            r.summary
          ] }) : null,
          /* @__PURE__ */ jsxs72("div", { style: { display: "flex", gap: tokens.spacing.sm, marginTop: tokens.spacing.sm, flexWrap: "wrap", alignItems: "flex-end" }, children: [
            /* @__PURE__ */ jsx80("select", { "data-testid": `callsheet-status-${r.intakeId}`, value: statusDraft, onChange: (e) => setStatusDraft(e.target.value), onClick: (e) => e.stopPropagation(), style: { padding: tokens.spacing.sm, borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, fontSize: "12px" }, children: STATUSES.map((s) => /* @__PURE__ */ jsx80("option", { value: s, children: s }, s)) }),
            /* @__PURE__ */ jsx80("input", { "data-testid": `callsheet-assign-${r.intakeId}`, value: assignDraft, onChange: (e) => setAssignDraft(e.target.value), onClick: (e) => e.stopPropagation(), placeholder: tr12("assignTo", locale), style: { padding: tokens.spacing.sm, borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, fontSize: "12px", width: "160px" } }),
            /* @__PURE__ */ jsx80("input", { "data-testid": `callsheet-note-${r.intakeId}`, value: noteDraft, onChange: (e) => setNoteDraft(e.target.value), onClick: (e) => e.stopPropagation(), placeholder: tr12("internalNote", locale), style: { padding: tokens.spacing.sm, borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, fontSize: "12px", flex: 1, minWidth: "200px" } }),
            /* @__PURE__ */ jsx80("button", { type: "button", "data-testid": `callsheet-save-${r.intakeId}`, disabled: savingId === r.intakeId, onClick: (e) => {
              e.stopPropagation();
              void saveRow(r.intakeId);
            }, style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, background: colors.info, color: "#fff", fontSize: "12px", cursor: "pointer" }, children: savingId === r.intakeId ? tr12("saving", locale) : tr12("saveStatus", locale) })
          ] })
        ] }) }) : null
      ] }, r.intakeId)) })
    ] }) }) : null
  ] });
}

// src/workspace/CommsAttemptsPanel.tsx
import { useCallback as useCallback28, useEffect as useEffect34, useMemo as useMemo21, useState as useState42 } from "react";
import { Fragment as Fragment10, jsx as jsx81, jsxs as jsxs73 } from "react/jsx-runtime";
var STRINGS5 = {
  title: { en: "Comms Audit", hi: "\u0938\u0902\u091A\u093E\u0930 \u0911\u0921\u093F\u091F" },
  subtitle: {
    en: "Read-only view of the durable message + notification queues. Recipients are masked; errors are redacted.",
    hi: "Durable message + notification queues \u0915\u093E read-only \u0926\u0943\u0936\u094D\u092F\u0964 Recipients masked \u0939\u0948\u0902; errors redacted \u0939\u0948\u0902\u0964"
  },
  refresh: { en: "Refresh", hi: "\u0930\u0940\u092B\u094D\u0930\u0947\u0936" },
  refreshing: { en: "Loading\u2026", hi: "\u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026" },
  allChannels: { en: "All channels", hi: "\u0938\u092D\u0940 \u091A\u0948\u0928\u0932" },
  allStatuses: { en: "All statuses", hi: "\u0938\u092D\u0940 \u0938\u094D\u0925\u093F\u0924\u093F\u092F\u093E\u0901" },
  colTime: { en: "Time", hi: "\u0938\u092E\u092F" },
  colChannel: { en: "Channel", hi: "\u091A\u0948\u0928\u0932" },
  colStatus: { en: "Status", hi: "\u0938\u094D\u0925\u093F\u0924\u093F" },
  colRecipient: { en: "Recipient", hi: "\u092A\u094D\u0930\u093E\u092A\u094D\u0924\u0915\u0930\u094D\u0924\u093E" },
  colRetry: { en: "Retries", hi: "\u0930\u0940\u091F\u094D\u0930\u093E\u092F" },
  colError: { en: "Last error", hi: "\u0905\u0902\u0924\u093F\u092E \u0924\u094D\u0930\u0941\u091F\u093F" },
  colSource: { en: "Queue", hi: "\u0915\u094D\u092F\u0942" },
  empty: { en: "No comms attempts match the filters.", hi: "\u092B\u093C\u093F\u0932\u094D\u091F\u0930 \u0938\u0947 \u0915\u094B\u0908 \u0938\u0902\u091A\u093E\u0930 \u092A\u094D\u0930\u092F\u093E\u0938 \u0928\u0939\u0940\u0902 \u092E\u093F\u0932\u093E\u0964" },
  error: { en: "Load failed", hi: "\u0932\u094B\u0921 \u0935\u093F\u092B\u0932" },
  emptyToken: { en: "Session token required.", hi: "\u0938\u0924\u094D\u0930 \u091F\u094B\u0915\u0928 \u0906\u0935\u0936\u094D\u092F\u0915 \u0939\u0948\u0964" },
  sourceMessaging: { en: "messaging", hi: "\u092E\u0948\u0938\u0947\u091C\u093F\u0902\u0917" },
  sourceSupport: { en: "notifications", hi: "\u0928\u094B\u091F\u093F\u092B\u093C\u093F\u0915\u0947\u0936\u0928" },
  queueError: { en: "queue read failed", hi: "\u0915\u094D\u092F\u0942 read \u0935\u093F\u092B\u0932" },
  webhookTitle: { en: "Webhook Settings", hi: "\u0935\u0947\u092C\u094D\u0939\u0941\u0915 \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938" },
  webhookSubtitle: {
    en: "Stored in the encrypted admin vault. Values are stored_not_active until the canonical runtime consumer is wired \u2014 saving here never flips a live switch.",
    hi: "\u090F\u0928\u094D\u0915\u094D\u0930\u093F\u092A\u094D\u091F\u0947\u0921 admin vault \u092E\u0947\u0902 \u0938\u0939\u0947\u091C\u093E \u0917\u092F\u093E\u0964 Canonical runtime consumer wired \u0939\u094B\u0928\u0947 \u0924\u0915 \u092E\u093E\u0928 stored_not_active \u0930\u0939\u0924\u093E \u0939\u0948 \u2014 \u092F\u0939\u093E\u0901 save \u0915\u0930\u0928\u093E live switch \u0928\u0939\u0940\u0902 \u092C\u0926\u0932\u0924\u093E\u0964"
  },
  edit: { en: "Edit", hi: "\u0938\u0902\u092A\u093E\u0926\u093F\u0924" },
  save: { en: "Save", hi: "\u0938\u0939\u0947\u091C\u0947\u0902" },
  clear: { en: "Clear", hi: "\u0939\u091F\u093E\u090F\u0901" },
  cancel: { en: "Cancel", hi: "\u0930\u0926\u094D\u0926" },
  saving: { en: "Saving\u2026", hi: "\u0938\u0939\u0947\u091C\u093E \u091C\u093E \u0930\u0939\u093E \u0939\u0948\u2026" },
  notConfigured: { en: "not configured", hi: "\u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0928\u0939\u0940\u0902" },
  vaultDown: { en: "Vault unavailable \u2014 upserts disabled.", hi: "Vault \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u2014 upsert \u092C\u0902\u0926\u0964" }
};
function tr13(key, locale) {
  return STRINGS5[key]?.[locale] ?? key;
}
var CHANNELS = ["email", "whatsapp", "sms", "telegram", "in_app"];
var STATUSES2 = ["queued", "sending", "sent", "delivered", "failed", "dead_letter", "cancelled", "retrying", "pending", "delivering"];
var STATUS_COLORS2 = {
  delivered: tokens.colors.success,
  sent: tokens.colors.success,
  failed: tokens.colors.danger,
  dead_letter: tokens.colors.danger,
  cancelled: "#6b7280",
  queued: "#c2410c",
  pending: "#c2410c",
  retrying: "#c2410c",
  sending: "#2563eb",
  delivering: "#2563eb"
};
var ACTIVATION_LABEL = {
  active: { en: "active", hi: "\u0938\u0915\u094D\u0930\u093F\u092F" },
  stored_not_active: { en: "stored \u2014 not active", hi: "\u0938\u0939\u0947\u091C\u093E \u2014 \u0938\u0915\u094D\u0930\u093F\u092F \u0928\u0939\u0940\u0902" },
  not_configured: { en: "not configured", hi: "\u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0928\u0939\u0940\u0902" },
  future_phase: { en: "future phase", hi: "\u092D\u093E\u0935\u0940 \u091A\u0930\u0923" }
};
function fmtTime2(ms, locale) {
  if (!ms) return "\u2014";
  return new Date(ms).toLocaleString(locale === "hi" ? "hi-IN" : "en-GB", { hour12: false });
}
function CommsAttemptsPanel({ token, locale = "en", theme: theme2 = "light", fetchImpl = fetch }) {
  const colors = themeColors(theme2);
  const [data, setData] = useState42(null);
  const [loading, setLoading] = useState42(true);
  const [error, setError] = useState42(null);
  const [channel, setChannel] = useState42("");
  const [status, setStatus] = useState42("");
  const [webhookItems, setWebhookItems] = useState42([]);
  const [vaultAvailable, setVaultAvailable] = useState42(false);
  const [editing, setEditing] = useState42(null);
  const [editValue, setEditValue] = useState42("");
  const [saving, setSaving] = useState42(false);
  const headers2 = useMemo21(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const load = useCallback28(async () => {
    if (!token) {
      setError(tr13("emptyToken", locale));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (channel) qs.set("channel", channel);
      if (status) qs.set("status", status);
      const res = await fetchImpl(`/api/admin/comms-attempts${qs.size ? `?${qs}` : ""}`, { headers: headers2 });
      const body = await res.json();
      if (!res.ok || !body.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      setData(body);
    } catch {
      setError(safeErrorText("load_failed", locale).title);
    } finally {
      setLoading(false);
    }
  }, [token, locale, fetchImpl, headers2, channel, status]);
  const loadWebhook = useCallback28(async () => {
    if (!token) return;
    try {
      const res = await fetchImpl("/api/admin/provider-config", { headers: headers2 });
      const body = await res.json();
      if (!res.ok || !body.ok) return;
      setVaultAvailable(body.vaultAvailable);
      setWebhookItems(body.items.filter((i) => i.group === "webhook"));
    } catch {
    }
  }, [token, fetchImpl, headers2]);
  useEffect34(() => {
    void load();
  }, [load]);
  useEffect34(() => {
    void loadWebhook();
  }, [loadWebhook]);
  const saveWebhook = useCallback28(
    async (clear) => {
      if (!editing) return;
      setSaving(true);
      try {
        const res = await fetchImpl("/api/admin/provider-config/upsert", {
          method: "POST",
          headers: { ...headers2, "Content-Type": "application/json" },
          body: JSON.stringify(clear ? { key: editing.key, clear: true } : { key: editing.key, value: editValue })
        });
        const body = await res.json();
        if (!res.ok || !body.ok) throw new Error(`HTTP ${res.status}`);
        setEditing(null);
        setEditValue("");
        await loadWebhook();
      } catch {
        setError(safeErrorText("settings_save_failed", locale).title);
      } finally {
        setSaving(false);
      }
    },
    [editing, editValue, fetchImpl, headers2, loadWebhook, locale]
  );
  const sourceLabel = useCallback28(
    (s) => tr13(s === "messaging_outbox" ? "sourceMessaging" : "sourceSupport", locale),
    [locale]
  );
  return /* @__PURE__ */ jsxs73("div", { "data-testid": "comms-attempts-panel", style: { display: "flex", flexDirection: "column", gap: tokens.spacing.md }, children: [
    /* @__PURE__ */ jsxs73("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacing.md }, children: [
      /* @__PURE__ */ jsxs73("div", { children: [
        /* @__PURE__ */ jsx81("h3", { style: { margin: 0, fontSize: "16px", color: colors.text }, children: tr13("title", locale) }),
        /* @__PURE__ */ jsx81("p", { style: { margin: "4px 0 0", fontSize: "12px", color: colors.textMuted }, children: tr13("subtitle", locale) })
      ] }),
      /* @__PURE__ */ jsx81(
        "button",
        {
          type: "button",
          "data-testid": "comms-refresh",
          onClick: () => void load(),
          disabled: loading,
          style: {
            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
            borderRadius: tokens.radius.md,
            border: `1px solid ${colors.border}`,
            background: colors.surface,
            color: colors.text,
            cursor: loading ? "default" : "pointer",
            fontSize: "13px",
            flexShrink: 0
          },
          children: loading ? tr13("refreshing", locale) : tr13("refresh", locale)
        }
      )
    ] }),
    /* @__PURE__ */ jsxs73("div", { style: { display: "flex", gap: tokens.spacing.sm, flexWrap: "wrap" }, children: [
      /* @__PURE__ */ jsxs73(
        "select",
        {
          "data-testid": "comms-filter-channel",
          value: channel,
          onChange: (e) => setChannel(e.target.value),
          style: { padding: tokens.spacing.sm, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, fontSize: "12px" },
          children: [
            /* @__PURE__ */ jsx81("option", { value: "", children: tr13("allChannels", locale) }),
            CHANNELS.map((c) => /* @__PURE__ */ jsx81("option", { value: c, children: c }, c))
          ]
        }
      ),
      /* @__PURE__ */ jsxs73(
        "select",
        {
          "data-testid": "comms-filter-status",
          value: status,
          onChange: (e) => setStatus(e.target.value),
          style: { padding: tokens.spacing.sm, borderRadius: tokens.radius.md, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, fontSize: "12px" },
          children: [
            /* @__PURE__ */ jsx81("option", { value: "", children: tr13("allStatuses", locale) }),
            STATUSES2.map((s) => /* @__PURE__ */ jsx81("option", { value: s, children: s }, s))
          ]
        }
      )
    ] }),
    error ? /* @__PURE__ */ jsxs73("p", { role: "alert", style: { margin: 0, fontSize: "13px", color: tokens.colors.danger }, children: [
      tr13("error", locale),
      " \u2014 ",
      error
    ] }) : null,
    data ? /* @__PURE__ */ jsxs73(Fragment10, { children: [
      data.sources.some((s) => s.state === "error") ? /* @__PURE__ */ jsx81("p", { role: "status", style: { margin: 0, fontSize: "12px", color: "#c2410c" }, children: data.sources.filter((s) => s.state === "error").map((s) => `${s.source}: ${tr13("queueError", locale)}`).join(" \xB7 ") }) : null,
      /* @__PURE__ */ jsx81("div", { style: { overflowX: "auto", border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md }, children: /* @__PURE__ */ jsxs73("table", { style: { width: "100%", borderCollapse: "collapse", fontSize: "12px" }, children: [
        /* @__PURE__ */ jsx81("thead", { children: /* @__PURE__ */ jsxs73("tr", { style: { background: colors.surfaceStrong, textAlign: "left" }, children: [
          /* @__PURE__ */ jsx81("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr13("colTime", locale) }),
          /* @__PURE__ */ jsx81("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr13("colChannel", locale) }),
          /* @__PURE__ */ jsx81("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr13("colStatus", locale) }),
          /* @__PURE__ */ jsx81("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr13("colRecipient", locale) }),
          /* @__PURE__ */ jsx81("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr13("colRetry", locale) }),
          /* @__PURE__ */ jsx81("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr13("colSource", locale) }),
          /* @__PURE__ */ jsx81("th", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: tr13("colError", locale) })
        ] }) }),
        /* @__PURE__ */ jsx81("tbody", { children: data.rows.length === 0 ? /* @__PURE__ */ jsx81("tr", { children: /* @__PURE__ */ jsx81("td", { colSpan: 7, "data-testid": "comms-empty", style: { padding: tokens.spacing.md, color: colors.textMuted, textAlign: "center" }, children: tr13("empty", locale) }) }) : data.rows.map((r) => /* @__PURE__ */ jsxs73("tr", { "data-testid": `comms-row-${r.id}`, style: { borderTop: `1px solid ${colors.border}` }, children: [
          /* @__PURE__ */ jsx81("td", { style: { padding: tokens.spacing.sm, color: colors.textMuted, whiteSpace: "nowrap" }, children: fmtTime2(r.createdAt, locale) }),
          /* @__PURE__ */ jsx81("td", { style: { padding: tokens.spacing.sm, color: colors.text }, children: r.channel }),
          /* @__PURE__ */ jsx81("td", { style: { padding: tokens.spacing.sm }, children: /* @__PURE__ */ jsx81("span", { "data-testid": `comms-status-${r.id}`, style: { color: STATUS_COLORS2[r.status] ?? colors.text, fontWeight: 600 }, children: r.status }) }),
          /* @__PURE__ */ jsx81("td", { style: { padding: tokens.spacing.sm, color: colors.text, fontFamily: "monospace" }, children: r.maskedRecipient }),
          /* @__PURE__ */ jsx81("td", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: r.retryCount }),
          /* @__PURE__ */ jsx81("td", { style: { padding: tokens.spacing.sm, color: colors.textMuted }, children: sourceLabel(r.source) }),
          /* @__PURE__ */ jsxs73("td", { style: { padding: tokens.spacing.sm, color: colors.textMuted, maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: [
            r.lastErrorCode ?? "",
            r.lastError ? `${r.lastErrorCode ? " \u2014 " : ""}${r.lastError}` : r.lastErrorCode ? "" : "\u2014"
          ] })
        ] }, `${r.source}-${r.id}`)) })
      ] }) })
    ] }) : null,
    /* @__PURE__ */ jsxs73(
      "div",
      {
        "data-testid": "comms-webhook-card",
        style: { padding: tokens.spacing.md, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, background: colors.surface },
        children: [
          /* @__PURE__ */ jsx81("h4", { style: { margin: 0, fontSize: "14px", color: colors.text }, children: tr13("webhookTitle", locale) }),
          /* @__PURE__ */ jsx81("p", { style: { margin: "4px 0 12px", fontSize: "12px", color: colors.textMuted }, children: tr13("webhookSubtitle", locale) }),
          !vaultAvailable ? /* @__PURE__ */ jsx81("p", { style: { margin: 0, fontSize: "12px", color: "#c2410c" }, children: tr13("vaultDown", locale) }) : null,
          webhookItems.map((item) => /* @__PURE__ */ jsxs73(
            "div",
            {
              "data-testid": `webhook-item-${item.key}`,
              style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacing.sm, padding: `${tokens.spacing.sm} 0`, borderTop: `1px solid ${colors.border}` },
              children: [
                /* @__PURE__ */ jsxs73("div", { children: [
                  /* @__PURE__ */ jsx81("div", { style: { fontSize: "13px", color: colors.text }, children: locale === "hi" ? item.labelHi : item.label }),
                  /* @__PURE__ */ jsxs73("div", { style: { fontSize: "11px", color: colors.textMuted, fontFamily: "monospace" }, children: [
                    item.type === "secret" ? item.masked ?? tr13("notConfigured", locale) : item.value ?? item.masked ?? tr13("notConfigured", locale),
                    " \xB7 ",
                    ACTIVATION_LABEL[item.activationState]?.[locale] ?? item.activationState
                  ] })
                ] }),
                item.writable && vaultAvailable ? /* @__PURE__ */ jsx81(
                  "button",
                  {
                    type: "button",
                    "data-testid": `webhook-edit-${item.key}`,
                    onClick: () => {
                      setEditing(item);
                      setEditValue(item.value ?? "");
                    },
                    style: { padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`, borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, background: "transparent", color: colors.text, fontSize: "12px", cursor: "pointer" },
                    children: tr13("edit", locale)
                  }
                ) : null
              ]
            },
            item.key
          ))
        ]
      }
    ),
    editing ? /* @__PURE__ */ jsx81(
      "div",
      {
        role: "dialog",
        "data-testid": "webhook-edit-dialog",
        style: {
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1e3
        },
        children: /* @__PURE__ */ jsxs73("div", { style: { background: colors.surface, padding: tokens.spacing.lg, borderRadius: tokens.radius.md, width: "420px", border: `1px solid ${colors.border}` }, children: [
          /* @__PURE__ */ jsx81("h4", { style: { margin: "0 0 8px", color: colors.text }, children: locale === "hi" ? editing.labelHi : editing.label }),
          /* @__PURE__ */ jsx81(
            "input",
            {
              "data-testid": "webhook-edit-input",
              type: editing.type === "secret" ? "password" : "text",
              value: editValue,
              onChange: (e) => setEditValue(e.target.value),
              placeholder: editing.masked ?? "",
              style: { width: "100%", padding: tokens.spacing.sm, borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, boxSizing: "border-box" }
            }
          ),
          /* @__PURE__ */ jsxs73("div", { style: { display: "flex", gap: tokens.spacing.sm, marginTop: tokens.spacing.md, justifyContent: "flex-end" }, children: [
            /* @__PURE__ */ jsx81("button", { type: "button", "data-testid": "webhook-cancel", onClick: () => setEditing(null), style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, background: "transparent", color: colors.text }, children: tr13("cancel", locale) }),
            /* @__PURE__ */ jsx81("button", { type: "button", "data-testid": "webhook-clear", onClick: () => void saveWebhook(true), disabled: saving, style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, background: "transparent", color: tokens.colors.danger }, children: tr13("clear", locale) }),
            /* @__PURE__ */ jsx81("button", { type: "button", "data-testid": "webhook-save", onClick: () => void saveWebhook(false), disabled: saving || !editValue, style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderRadius: tokens.radius.sm, border: `1px solid ${colors.border}`, background: colors.info, color: "#fff" }, children: saving ? tr13("saving", locale) : tr13("save", locale) })
          ] })
        ] })
      }
    ) : null
  ] });
}

// src/workspace/workspaceControlService.ts
async function apiJson6(baseUrl, fetchImpl, token, method, path, body, signal) {
  const headers2 = {};
  if (token) headers2.Authorization = `Bearer ${token}`;
  if (body !== void 0) headers2["Content-Type"] = "application/json";
  const init = { method, headers: headers2, signal: signal ?? null };
  if (body !== void 0) init.body = JSON.stringify(body);
  let response;
  try {
    response = await fetchImpl(`${baseUrl}${path}`, init);
  } catch (err) {
    throw new Error(err instanceof Error && err.name === "AbortError" ? "Cancelled" : "network_error");
  }
  const text2 = await response.text();
  let payload = {};
  try {
    payload = text2 ? JSON.parse(text2) : {};
  } catch {
    throw new Error(response.ok ? "invalid_json" : `http_error_${response.status}`);
  }
  if (!response.ok || payload.success === false) {
    const errorMessage = typeof payload.error === "string" ? payload.error : `http_error_${response.status}`;
    const err = new Error(errorMessage);
    throw err;
  }
  return payload;
}
function toResult(data, defaultMessage) {
  const message = typeof data.error === "string" && data.error.length > 0 ? data.error : defaultMessage;
  return {
    ok: data.success === true,
    message,
    blockedByEmergencyStop: typeof message === "string" && message.toLowerCase().includes("emergency stop"),
    requiresApproval: data.requiresApproval === true,
    result: data.result ?? void 0
  };
}
function mapBrowserRequest(req) {
  const base = {};
  if (req.pairingToken) base.pairingToken = req.pairingToken;
  if (req.approvalId) base.approvalId = req.approvalId;
  if (req.sessionId) base.sessionId = req.sessionId;
  switch (req.action) {
    case "navigate":
      return { ...base, kind: "navigate", url: req.url };
    case "click":
      return { ...base, kind: "click", selector: req.target, url: req.url };
    case "type":
      return { ...base, kind: "type", selector: req.target, text: req.value, url: req.url };
    case "scroll":
      return { ...base, kind: "scroll", selector: req.target, direction: req.value, delta: 200, url: req.url };
    case "submit":
      return { ...base, kind: "form_submit", selector: req.target, url: req.url };
    case "screenshot":
      return { ...base, kind: "screenshot", url: req.url, selector: req.target };
    case "read":
    default:
      return { ...base, kind: "read", url: req.url, selector: req.target };
  }
}
function mapDesktopRequest(req) {
  const selector = req.controlName ?? req.windowTitle;
  const value = req.value;
  const base = {};
  if (req.sessionId) base.sessionId = req.sessionId;
  if (req.approvalId) base.approvalId = req.approvalId;
  switch (req.action) {
    case "click":
    case "focus":
      return { ...base, kind: "click", target: selector };
    case "type":
      return { ...base, kind: "type", target: selector, text: value };
    case "scroll": {
      const direction = typeof value === "string" && ["up", "down", "left", "right"].includes(value) ? value : "down";
      const delta = typeof value === "string" ? Number(value) || 200 : 200;
      return { ...base, kind: "scroll", selector, direction, delta };
    }
    case "screenshot":
      return { ...base, kind: "screenshot", target: req.windowTitle };
    case "verifyWindow":
    case "listControls":
      return { ...base, kind: "active_window_read", target: req.windowTitle };
    case "cancel":
    default:
      return { ...base, kind: req.action };
  }
}
function createWorkspaceControlService(options) {
  const baseUrl = options.baseUrl ?? "";
  const getToken = options.getToken;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  function requireToken() {
    const token = getToken();
    if (!token) throw new Error("unauthenticated");
    return token;
  }
  return {
    async executeBrowser(req, signal) {
      try {
        const token = requireToken();
        const body = mapBrowserRequest(req);
        const data = await apiJson6(
          baseUrl,
          fetchImpl,
          token,
          "POST",
          "/api/control/browser/execute",
          body,
          signal
        );
        const result = toResult(data, "Browser control request accepted by gate.");
        return {
          ok: result.ok,
          message: result.message,
          blockedByEmergencyStop: result.blockedByEmergencyStop,
          requiresApproval: result.requiresApproval,
          result: result.result
        };
      } catch (err) {
        const message = safeErrorText(errorToCode(err), "en").title;
        return {
          ok: false,
          message,
          blockedByEmergencyStop: message.toLowerCase().includes("emergency stop"),
          requiresApproval: false
        };
      }
    },
    async executeDesktop(req, signal) {
      try {
        const token = requireToken();
        const body = mapDesktopRequest(req);
        const data = await apiJson6(
          baseUrl,
          fetchImpl,
          token,
          "POST",
          "/api/control/desktop/execute",
          body,
          signal
        );
        const result = toResult(data, "Desktop control request accepted by gate.");
        return {
          ok: result.ok,
          message: result.message,
          blockedByEmergencyStop: result.blockedByEmergencyStop,
          requiresApproval: result.requiresApproval,
          result: result.result
        };
      } catch (err) {
        const message = safeErrorText(errorToCode(err), "en").title;
        return {
          ok: false,
          message,
          blockedByEmergencyStop: message.toLowerCase().includes("emergency stop"),
          requiresApproval: false
        };
      }
    },
    async listArtifacts(signal) {
      try {
        const token = requireToken();
        const data = await apiJson6(baseUrl, fetchImpl, token, "GET", "/api/control/browser/artifacts", void 0, signal);
        const artifacts = Array.isArray(data.artifacts) ? data.artifacts : [];
        return artifacts.map((a) => ({
          artifactId: String(a.artifactId ?? a.path ?? ""),
          path: String(a.path ?? ""),
          filename: String(a.filename ?? a.path ?? ""),
          mimeType: String(a.mimeType ?? "application/octet-stream"),
          size: typeof a.size === "number" ? a.size : 0,
          createdAt: typeof a.createdAt === "number" ? a.createdAt : Date.now(),
          source: a.source ?? "unknown",
          controlSessionId: typeof a.controlSessionId === "string" ? a.controlSessionId : void 0,
          taskId: typeof a.taskId === "string" ? a.taskId : void 0,
          projectId: typeof a.projectId === "string" ? a.projectId : void 0,
          pageUrl: typeof a.pageUrl === "string" ? a.pageUrl : void 0,
          status: a.status ?? "active"
        }));
      } catch (err) {
        const message = safeErrorText(errorToCode(err), "en").title;
        throw new Error(message);
      }
    }
  };
}

// src/workspace/workspaceCoworkService.ts
async function apiJson7(baseUrl, fetchImpl, token, method, path, body, signal) {
  const headers2 = {};
  if (token) headers2.Authorization = `Bearer ${token}`;
  if (body !== void 0) headers2["Content-Type"] = "application/json";
  const init = { method, headers: headers2, signal: signal ?? null };
  if (body !== void 0) init.body = JSON.stringify(body);
  let response;
  try {
    response = await fetchImpl(`${baseUrl}${path}`, init);
  } catch (err) {
    throw new Error(err instanceof Error && err.name === "AbortError" ? "Cancelled" : "network_error");
  }
  const text2 = await response.text();
  let payload = {};
  try {
    payload = text2 ? JSON.parse(text2) : {};
  } catch {
    throw new Error(response.ok ? "invalid_json" : `http_error_${response.status}`);
  }
  if (!response.ok || payload.success === false) {
    const errorMessage = typeof payload.error === "string" ? payload.error : `http_error_${response.status}`;
    throw new Error(errorMessage);
  }
  return payload;
}
function toView(status) {
  switch (status) {
    case "pending":
      return "inbox";
    case "running":
    case "paused":
      return "active";
    case "approval_required":
      return "approval";
    case "completed":
      return "completed";
    case "failed":
    case "cancelled":
      return "failed";
    default:
      return "inbox";
  }
}
function mapRunToSummary(run) {
  const stage = run.currentStageId ? run.stages[run.currentStageId] : void 0;
  const orderedStageIds = run.orderedStageIds ?? Object.keys(run.stages);
  const evidenceCount = Object.values(run.stages).reduce(
    (sum, s) => sum + (Array.isArray(s.evidence) ? s.evidence.length : 0),
    0
  );
  const isPaused = run.status === "paused";
  const canCancel = ["pending", "running", "approval_required", "paused"].includes(run.status);
  const canResume = ["approval_required", "paused"].includes(run.status);
  const status = run.status;
  return {
    taskId: run.taskId || run.runId,
    title: run.taskTitle || `Run ${run.runId}`,
    description: run.taskDescription,
    taskStatus: status,
    workflowStatus: status,
    currentStageId: run.currentStageId ?? orderedStageIds[0] ?? "\u2014",
    currentStageLabel: stage ? `${stage.agentName}: ${stage.instruction.slice(0, 80)}` : "\u2014",
    view: toView(run.status),
    channel: "chat",
    projectId: run.projectId,
    skillId: stage?.skillId ?? "\u2014",
    ownerId: run.ownerId,
    tenantId: run.tenantId,
    updatedAt: run.updatedAt ?? Date.now(),
    createdAt: run.createdAt ?? Date.now(),
    isPaused,
    canCancel,
    canResume,
    evidenceCount,
    voiceTranscriptPreview: void 0
  };
}
function createWorkspaceCoworkService(options) {
  const baseUrl = options.baseUrl ?? "";
  const getToken = options.getToken;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  function requireToken() {
    const token = getToken();
    if (!token) throw new Error("unauthenticated");
    return token;
  }
  async function request(method, path, body, signal) {
    const token = requireToken();
    return apiJson7(baseUrl, fetchImpl, token, method, path, body, signal);
  }
  return {
    async listRuns(signal) {
      const result = await request("GET", "/api/cowork/runs", void 0, signal);
      const runs = result.runs ?? [];
      return runs.map(mapRunToSummary);
    },
    async getRun(runId, signal) {
      const result = await request("GET", `/api/cowork/runs/${encodeURIComponent(runId)}`, void 0, signal);
      return result.run ? mapRunToSummary(result.run) : void 0;
    },
    async createRun(input, signal) {
      const result = await request("POST", "/api/cowork/runs", { title: input.title ?? "Cowork run", description: input.description ?? "" }, signal);
      return result.runId;
    },
    async pauseRun(runId, signal) {
      await request("POST", `/api/cowork/runs/${encodeURIComponent(runId)}/pause`, { reason: "paused by user" }, signal);
    },
    async resumeRun(runId, signal) {
      await request("POST", `/api/cowork/runs/${encodeURIComponent(runId)}/resume`, void 0, signal);
    },
    async cancelRun(runId, signal) {
      await request("POST", `/api/cowork/runs/${encodeURIComponent(runId)}/cancel`, { reason: "cancelled by user" }, signal);
    },
    async getEmergencyStopStatus(signal) {
      const result = await request("GET", "/api/health/emergency-stop", void 0, signal);
      return result.emergencyStop ?? result.stopped ?? false;
    },
    async setEmergencyStop(active, signal) {
      await request("POST", "/api/cowork/emergency-stop", { active }, signal);
    }
  };
}

// src/workspace/workspaceBrowserSessionService.ts
async function apiJson8(baseUrl, fetchImpl, token, body, signal) {
  const headers2 = { "Content-Type": "application/json" };
  if (token) headers2.Authorization = `Bearer ${token}`;
  let response;
  try {
    response = await fetchImpl(`${baseUrl}/api/browser-session/lease`, {
      method: "POST",
      headers: headers2,
      body: JSON.stringify(body),
      signal: signal ?? null
    });
  } catch (err) {
    throw new Error(err instanceof Error && err.name === "AbortError" ? "Cancelled" : "network_error");
  }
  const text2 = await response.text();
  let payload = {};
  try {
    payload = text2 ? JSON.parse(text2) : {};
  } catch {
    throw new Error(response.ok ? "invalid_json" : `http_error_${response.status}`);
  }
  if (!response.ok || payload.success === false) {
    const errorMessage = typeof payload.error === "string" ? payload.error : typeof payload.message === "string" ? payload.message : `http_error_${response.status}`;
    throw new Error(errorMessage);
  }
  return payload;
}
function toLeaseView(raw) {
  return {
    leaseId: typeof raw.leaseId === "string" ? raw.leaseId : "",
    browserProfileId: typeof raw.browserProfileId === "string" ? raw.browserProfileId : "",
    controllerId: typeof raw.controllerId === "string" ? raw.controllerId : "",
    controllerType: typeof raw.controllerType === "string" ? raw.controllerType : "unknown",
    capabilityId: typeof raw.capabilityId === "string" ? raw.capabilityId : "",
    permissions: Array.isArray(raw.permissions) ? raw.permissions.filter((p) => typeof p === "string") : [],
    risk: typeof raw.risk === "string" ? raw.risk : "",
    state: typeof raw.state === "string" ? raw.state : "unknown",
    createdAt: typeof raw.createdAt === "number" ? raw.createdAt : 0,
    expiresAt: typeof raw.expiresAt === "number" ? raw.expiresAt : 0,
    heartbeatAt: typeof raw.heartbeatAt === "number" ? raw.heartbeatAt : 0
  };
}
function createWorkspaceBrowserSessionService(options) {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = options.baseUrl ?? "";
  const controllerType = options.controllerType ?? "unknown";
  function baseBody() {
    const ctx = options.getProjectContext();
    const body = { controllerType };
    if (ctx?.projectId) body.projectId = ctx.projectId;
    return body;
  }
  return {
    listLeases: async (signal) => {
      const token = options.getToken();
      if (!token) throw new Error("auth_required");
      const res = await apiJson8(
        baseUrl,
        fetchImpl,
        token,
        { ...baseBody(), operation: "list" },
        signal
      );
      return (res.leases ?? []).map(toLeaseView);
    },
    cancelLease: async (leaseId, signal) => {
      const token = options.getToken();
      if (!token) throw new Error("auth_required");
      await apiJson8(
        baseUrl,
        fetchImpl,
        token,
        { ...baseBody(), operation: "cancel", leaseId, reason: "owner cancel from workspace" },
        signal
      );
    }
  };
}

// src/workspace/workspaceCeoService.ts
var CSRF_COOKIE_NAME2 = "smart_shikka_csrf";
var CSRF_HEADER_NAME2 = "x-smart-shikka-csrf";
function readCsrfCookie() {
  if (typeof document === "undefined") return void 0;
  for (const part of document.cookie.split(";")) {
    const [name, ...rest] = part.trim().split("=");
    if (name === CSRF_COOKIE_NAME2) return decodeURIComponent(rest.join("="));
  }
  return void 0;
}
function postHeaders(token) {
  const csrf = readCsrfCookie();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...csrf ? { [CSRF_HEADER_NAME2]: csrf } : {}
  };
}
function getJson(response) {
  return response.json();
}
function fallbackResult(input, allowed, resultSummary, denialCode) {
  return {
    intent: input.intent,
    plan: input.plan,
    permissionLevel: input.permissionLevel,
    executor: "workspace",
    requiresApproval: input.permissionLevel !== "L1",
    allowed,
    resultSummary,
    denialCode
  };
}
function asTurnResult(json) {
  const ok = typeof json.ok === "boolean" ? json.ok : json.success === true && json.error === void 0 && json.errorCode === void 0;
  return {
    success: json.success === true,
    ok,
    realExecutionPerformed: json.realExecutionPerformed === true,
    answer: typeof json.answer === "string" ? json.answer : "",
    spokenAnswer: typeof json.spokenAnswer === "string" ? json.spokenAnswer : void 0,
    delegatedTo: typeof json.delegatedTo === "string" ? json.delegatedTo : void 0,
    activityState: typeof json.activityState === "string" ? json.activityState : "idle",
    requiresApproval: json.requiresApproval === true,
    approvalRequest: json.approvalRequest && typeof json.approvalRequest === "object" ? json.approvalRequest : void 0,
    artifacts: Array.isArray(json.artifacts) ? json.artifacts : void 0,
    evidence: json.evidence && typeof json.evidence === "object" ? json.evidence : void 0,
    errorCode: typeof json.errorCode === "string" ? json.errorCode : void 0
  };
}
function createWorkspaceCeoService(options) {
  const { baseUrl = "", getToken, fetchImpl = fetch } = options;
  return {
    async orchestrate(input) {
      const token = getToken();
      if (!token) {
        return fallbackResult(input, false, "Authentication token missing; cannot route Shikka CEO request.", "unauthenticated");
      }
      try {
        const response = await fetchImpl(`${baseUrl}/api/ask`, {
          method: "POST",
          headers: postHeaders(token),
          credentials: "include",
          body: JSON.stringify({
            query: input.input,
            surface: input.surface,
            locale: input.locale
            // role is intentionally not trusted; server resolves the authenticated role.
          })
        });
        const json = await getJson(response);
        const resultSummary = typeof json.answer === "string" ? json.answer : typeof json.error === "string" ? json.error : "CEO orchestration denied by server";
        if (response.ok && json.success === true) {
          return fallbackResult(input, json.ok === true, resultSummary, typeof json.errorCode === "string" ? json.errorCode : void 0);
        }
        const error = typeof json.error === "string" ? json.error : resultSummary;
        const denialCode = typeof json.errorCode === "string" ? json.errorCode : "ceo_orchestrator_denied";
        return fallbackResult(input, false, error, denialCode);
      } catch (err) {
        const message = safeErrorText(errorToCode(err), "en").title;
        return fallbackResult(input, false, message, "ceo_orchestrator_error");
      }
    },
    async turn(input, signal) {
      const token = getToken();
      if (!token) {
        return {
          success: false,
          ok: false,
          realExecutionPerformed: false,
          answer: "Authentication token missing; cannot route Shikka CEO request.",
          activityState: "error",
          requiresApproval: false,
          errorCode: "unauthenticated"
        };
      }
      try {
        const response = await fetchImpl(`${baseUrl}/api/ask`, {
          method: "POST",
          headers: postHeaders(token),
          credentials: "include",
          body: JSON.stringify({
            query: input.input,
            attachments: input.attachments,
            voice: input.voice,
            logicalProfile: input.logicalProfile,
            surface: input.surface,
            locale: input.locale,
            sessionId: input.sessionId,
            conversationId: input.conversationId,
            idempotencyKey: input.idempotencyKey
            // role and approval authority are intentionally not trusted; server resolves both.
          }),
          ...signal ? { signal } : {}
        });
        const json = await getJson(response);
        return asTurnResult(json);
      } catch (err) {
        const message = safeErrorText(errorToCode(err), "en").title;
        return { success: false, ok: false, realExecutionPerformed: false, answer: message, activityState: "error", requiresApproval: false, errorCode: "ceo_turn_error" };
      }
    },
    async approve(input) {
      const token = getToken();
      if (!token) {
        return { success: false, ok: false, realExecutionPerformed: false, answer: "Authentication token missing; cannot approve Shikka CEO request.", activityState: "error", requiresApproval: false, errorCode: "unauthenticated" };
      }
      try {
        const response = await fetchImpl(`${baseUrl}/api/ceo/approve`, {
          method: "POST",
          headers: postHeaders(token),
          credentials: "include",
          body: JSON.stringify({
            approvalId: input.approvalId,
            granted: input.granted,
            sessionId: input.sessionId,
            actionDigest: input.actionDigest,
            input: input.input,
            attachments: input.attachments
          })
        });
        return asTurnResult(await getJson(response));
      } catch (err) {
        const message = safeErrorText(errorToCode(err), "en").title;
        return { success: false, ok: false, realExecutionPerformed: false, answer: message, activityState: "error", requiresApproval: false, errorCode: "ceo_approve_error" };
      }
    }
  };
}

// src/workspace/useSharedWorkspaceShellProps.ts
import { useEffect as useEffect35, useMemo as useMemo22 } from "react";
import { CwjWorkspace } from "@smart-shikka/core/cwj-workspace";

// src/builder/builderService.ts
var BuilderServiceError = class extends Error {
  constructor(status, body) {
    super(typeof body.error === "string" ? body.error : `Builder request failed (${status}).`);
    this.status = status;
    this.errorCode = typeof body.errorCode === "string" ? body.errorCode : "builder_request_failed";
  }
};
function createBuilderService(options) {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = options.baseUrl ?? "";
  async function call(method, path, body, signal) {
    const token = options.getToken();
    if (!token) throw new BuilderServiceError(401, { errorCode: "auth_required", error: "Authentication required." });
    const ctx = options.getProjectContext();
    if (!ctx?.projectId) throw new BuilderServiceError(400, { errorCode: "project_required", error: "Project context required." });
    const res = await fetchImpl(`${baseUrl}/api/projects/${encodeURIComponent(ctx.projectId)}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: body === void 0 ? null : JSON.stringify(body),
      signal: signal ?? null
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new BuilderServiceError(res.status, json);
    return json;
  }
  return {
    schema: (signal) => call("GET", "/database/schema", void 0, signal),
    versions: (signal) => call("GET", "/versions", void 0, signal),
    outputUrls: (signal) => call("GET", "/output-urls", void 0, signal),
    gitStatus: (signal) => call("GET", "/git/status", void 0, signal),
    indexStatus: (signal) => call("GET", "/index/status", void 0, signal),
    deployPrep: (signal) => call("GET", "/deploy-prep", void 0, signal),
    connectors: async (signal) => {
      const res = await call("GET", "/connectors", void 0, signal);
      return res.connectors;
    },
    upsertConnector: async (body, connectorId) => {
      const res = await call(
        "POST",
        connectorId ? `/connectors/${encodeURIComponent(connectorId)}` : "/connectors",
        body
      );
      return res.connector;
    },
    deleteConnector: (connectorId) => call("POST", `/connectors/${encodeURIComponent(connectorId)}/delete`),
    checkConnector: async (connectorId) => {
      const res = await call("POST", `/connectors/${encodeURIComponent(connectorId)}/check`);
      return res.check;
    },
    executeDeploy: async (connectorId) => {
      const res = await call("POST", `/connectors/${encodeURIComponent(connectorId)}/deploy`, { confirm: true });
      return res.deploy;
    },
    deploys: async (signal) => {
      const res = await call("GET", "/deploys", void 0, signal);
      return res.deploys;
    },
    envVars: async (signal) => {
      const res = await call("GET", "/env", void 0, signal);
      return res.vars;
    },
    setEnvVar: async (name, value) => {
      const res = await call("POST", "/env", { name, value });
      return res.var;
    },
    deleteEnvVar: (name) => call("POST", `/env/${encodeURIComponent(name)}/delete`),
    rules: async (signal) => {
      const res = await call("GET", "/rules", void 0, signal);
      return res.rules;
    },
    createRule: async (content, pinned) => {
      const res = await call("POST", "/rules", { content, pinned });
      return res.rule;
    },
    deleteRule: (memoryId) => call("POST", `/rules/${encodeURIComponent(memoryId)}/delete`),
    restoreSnapshot: async (snapshotId) => {
      const res = await call("POST", `/snapshots/${encodeURIComponent(snapshotId)}/restore`);
      return res.restore;
    }
  };
}

// src/workspace/useSharedWorkspaceShellProps.ts
function useSharedWorkspaceShellProps(options) {
  const { surface, token, baseUrl = "", fetchImpl, isOwner: isOwnerProp, locale = "en", theme: theme2 = "light", initialMode = "normal", initialRightTab = "context", storage, adminSurfaces } = options;
  const projectRuntime = useProjectRuntime({
    service: options.projectRuntimeService,
    ownerId: options.ownerId,
    tenantId: options.tenantId
  });
  const currentContext = projectRuntime.currentContext;
  const isOwner = isOwnerProp ?? currentContext?.role === "super-admin";
  const getProjectContext = useMemo22(
    () => () => {
      const ctx = projectRuntime.currentContext;
      const projectName = ctx?.projectId ? projectRuntime.allowedProjects.find((p) => p.projectId === ctx.projectId)?.displayName : void 0;
      return ctx ? { projectId: ctx.projectId, projectName, skillId: ctx.skillId, ownerId: ctx.ownerId, tenantId: ctx.tenantId } : void 0;
    },
    [projectRuntime.currentContext, projectRuntime.allowedProjects]
  );
  const baseUrlConfig = baseUrl ? { baseUrl } : {};
  const fetchImplOption = fetchImpl !== void 0 ? { fetchImpl } : {};
  const chatService = useMemo22(
    () => token ? createWorkspaceChatService({ ...baseUrlConfig, ...fetchImplOption, getToken: () => token }) : void 0,
    [token, baseUrl, fetchImplOption]
  );
  const memoryService = useMemo22(
    () => token ? createWorkspaceMemoryService({ ...baseUrlConfig, ...fetchImplOption, getToken: () => token, getProjectContext }) : void 0,
    [token, baseUrl, fetchImplOption, getProjectContext]
  );
  const codeService = useMemo22(
    () => token ? createWorkspaceCodeService({
      ...baseUrlConfig,
      ...fetchImplOption,
      getToken: () => token,
      getProjectContext,
      surface
    }) : void 0,
    [token, baseUrl, fetchImplOption, getProjectContext, surface]
  );
  const codeIntelligenceService = useMemo22(
    () => token ? createWorkspaceCodeIntelligenceService({
      ...baseUrlConfig,
      ...fetchImplOption,
      getToken: () => token,
      getProjectContext: () => {
        const ctx = getProjectContext();
        return ctx ? { projectId: ctx.projectId, projectName: ctx.projectName } : void 0;
      }
    }) : void 0,
    [token, baseUrl, fetchImplOption, getProjectContext]
  );
  const agentSkillsService = useMemo22(
    () => token ? createWorkspaceAgentSkillsService({
      ...baseUrlConfig,
      ...fetchImplOption,
      getToken: () => token,
      getProjectContext: () => {
        const ctx = getProjectContext();
        return ctx ? { projectId: ctx.projectId } : void 0;
      }
    }) : void 0,
    [token, baseUrl, fetchImplOption, getProjectContext]
  );
  const coworkService = useMemo22(
    () => token ? createWorkspaceCoworkService({ ...baseUrlConfig, ...fetchImplOption, getToken: () => token }) : void 0,
    [token, baseUrl, fetchImplOption]
  );
  const builderService = useMemo22(
    () => token ? createBuilderService({ ...baseUrlConfig, ...fetchImplOption, getToken: () => token, getProjectContext }) : void 0,
    [token, baseUrl, fetchImplOption, getProjectContext]
  );
  const browserSessionService = useMemo22(
    () => token ? createWorkspaceBrowserSessionService({
      ...baseUrlConfig,
      ...fetchImplOption,
      getToken: () => token,
      getProjectContext,
      controllerType: surface
    }) : void 0,
    [token, baseUrl, fetchImplOption, getProjectContext, surface]
  );
  const controlService = useMemo22(
    () => token ? createWorkspaceControlService({ ...baseUrlConfig, ...fetchImplOption, getToken: () => token }) : void 0,
    [token, baseUrl, fetchImplOption]
  );
  const ceoService = useMemo22(
    () => token ? createWorkspaceCeoService({ ...baseUrlConfig, ...fetchImplOption, getToken: () => token }) : void 0,
    [token, baseUrl, fetchImplOption]
  );
  const workspace = useMemo22(() => {
    const ownerId = currentContext?.ownerId;
    const tenantId = currentContext?.tenantId;
    const projectId = currentContext?.projectId;
    const skillId = currentContext?.skillId;
    if (!ownerId || !projectId || !skillId) return void 0;
    return new CwjWorkspace({
      ownerId,
      projectId,
      skillId,
      storage,
      storageKey: `smart_shikka_${surface}_workspace_${tenantId ?? "no-tenant"}_${ownerId}_${projectId}`,
      ...tenantId !== void 0 ? { tenantId } : {}
    });
  }, [currentContext?.ownerId, currentContext?.tenantId, currentContext?.projectId, currentContext?.skillId, surface, storage]);
  const actor = useMemo22(() => {
    if (!currentContext?.ownerId) return void 0;
    const serverRole = currentContext.role ?? "user";
    const roles = serverRole === "super-admin" ? ["owner", serverRole] : [serverRole];
    const actorBase = {
      actorId: currentContext.ownerId,
      roles,
      authenticated: true
    };
    return currentContext.tenantId !== void 0 ? { ...actorBase, tenantId: currentContext.tenantId } : actorBase;
  }, [currentContext]);
  useEffect35(() => {
    if (projectRuntime.allowedProjects.length > 0 && !projectRuntime.currentContext && !projectRuntime.loading) {
      void projectRuntime.selectProject(projectRuntime.allowedProjects[0].projectId);
    }
  }, [projectRuntime.allowedProjects, projectRuntime.currentContext, projectRuntime.loading, projectRuntime.selectProject]);
  const ready = !!workspace && !!actor && !projectRuntime.loading;
  const providerProps = {
    workspace,
    actor,
    locale,
    theme: theme2,
    projectRuntime,
    chatService,
    codeService,
    codeIntelligenceService,
    agentSkillsService,
    memoryService,
    builderService,
    browserSessionService,
    coworkService,
    controlService,
    ceoService,
    adminSurfaces,
    token,
    surface,
    initialMode,
    initialRightTab
  };
  return {
    projectRuntime,
    workspace,
    actor,
    isOwner,
    ready,
    chatService,
    memoryService,
    codeService,
    codeIntelligenceService,
    agentSkillsService,
    coworkService,
    controlService,
    ceoService,
    providerProps
  };
}

// src/workspace/MasterProviderSettingsPanel.tsx
import { useEffect as useEffect36, useMemo as useMemo23, useState as useState43 } from "react";
import { jsx as jsx82, jsxs as jsxs74 } from "react/jsx-runtime";
var STRINGS6 = {
  title: { en: "Master Provider Settings", hi: "\u092E\u093E\u0938\u094D\u091F\u0930 \u092A\u094D\u0930\u0926\u093E\u0924\u093E \u0938\u0947\u091F\u093F\u0902\u0917" },
  search: { en: "Search keys\u2026", hi: "\u0915\u0940\u091C\u093C \u0916\u094B\u091C\u0947\u0902\u2026" },
  all: { en: "All", hi: "\u0938\u092D\u0940" },
  configured: { en: "Configured", hi: "\u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930" },
  missing: { en: "Missing", hi: "Missing" },
  secrets: { en: "Secrets", hi: "Secrets" },
  urls: { en: "URLs", hi: "URLs" },
  total: { en: "Total keys", hi: "\u0915\u0941\u0932 keys" },
  refresh: { en: "Refresh", hi: "\u0930\u0940\u092B\u093C\u094D\u0930\u0947\u0936" },
  testGroup: { en: "Test group", hi: "\u0917\u094D\u0930\u0941\u092A test" },
  testing: { en: "Testing\u2026", hi: "Test \u0939\u094B \u0930\u0939\u093E\u2026" },
  edit: { en: "Edit", hi: "Edit" },
  save: { en: "Save", hi: "Save \u0915\u0930\u0947\u0902" },
  clear: { en: "Clear", hi: "Clear" },
  cancel: { en: "Cancel", hi: "\u0930\u0926\u094D\u0926 \u0915\u0930\u0947\u0902" },
  close: { en: "Close", hi: "\u092C\u0902\u0926 \u0915\u0930\u0947\u0902" },
  loading: { en: "Loading provider settings\u2026", hi: "\u092A\u094D\u0930\u0926\u093E\u0924\u093E settings \u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u0940 \u0939\u0948\u0902\u2026" },
  error: { en: "Failed to load provider settings.", hi: "\u092A\u094D\u0930\u0926\u093E\u0924\u093E settings \u0932\u094B\u0921 \u0915\u0930\u0928\u0947 \u092E\u0947\u0902 \u0935\u093F\u092B\u0932\u0964" },
  noKeys: { en: "No keys match the current filters.", hi: "\u0935\u0930\u094D\u0924\u092E\u093E\u0928 \u092B\u093C\u093F\u0932\u094D\u091F\u0930 \u0938\u0947 \u0915\u094B\u0908 key \u092E\u0947\u0932 \u0928\u0939\u0940\u0902 \u0916\u093E\u0924\u0940\u0964" },
  vaultUnavailable: {
    en: "Durable vault unavailable \u2014 upserts disabled, env status only.",
    hi: "Durable vault \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u2014 upsert \u092C\u0902\u0926, \u0915\u0947\u0935\u0932 env status\u0964"
  },
  vaultNote: {
    en: "Secrets stored encrypted in smart_shikka_admin_provider_secrets \u2014 values never leave the server.",
    hi: "Secrets encrypted \u0939\u094B\u0915\u0930 smart_shikka_admin_provider_secrets \u092E\u0947\u0902 \u0938\u0939\u0947\u091C\u0947 \u091C\u093E\u0924\u0947 \u0939\u0948\u0902 \u2014 values \u0915\u092D\u0940 server \u0938\u0947 \u092C\u093E\u0939\u0930 \u0928\u0939\u0940\u0902 \u091C\u093E\u0924\u0940\u0902\u0964"
  },
  envManaged: { en: "env-managed (read-only)", hi: "env-managed (\u0915\u0947\u0935\u0932 \u092A\u0922\u093C\u0947\u0902)" },
  sourceVault: { en: "vault", hi: "vault" },
  sourceEnv: { en: "env", hi: "env" },
  stateActive: { en: "ACTIVE", hi: "\u0938\u0915\u094D\u0930\u093F\u092F" },
  stateStored: { en: "STORED (not live)", hi: "\u0938\u0939\u0947\u091C\u0940 (live \u0928\u0939\u0940\u0902)" },
  stateMissing: { en: "NOT CONFIGURED", hi: "\u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0928\u0939\u0940\u0902" },
  stateFuture: { en: "FUTURE PHASE", hi: "\u092D\u0935\u093F\u0937\u094D\u092F \u091A\u0930\u0923" },
  storedNote: {
    en: "Stored in vault only \u2014 the live runtime still reads env; save does not switch the provider.",
    hi: "\u0915\u0947\u0935\u0932 vault \u092E\u0947\u0902 \u0938\u0939\u0947\u091C\u0940 \u091C\u093E\u090F\u0917\u0940 \u2014 live runtime \u0905\u092D\u0940 env \u0938\u0947 \u091A\u0932\u0924\u093E \u0939\u0948; Save provider \u0928\u0939\u0940\u0902 \u092C\u0926\u0932\u0924\u093E\u0964"
  },
  labTitle: { en: "Provider Lab \u2014 runtime truth", hi: "Provider Lab \u2014 runtime truth" },
  labColProfile: { en: "Profile", hi: "\u092A\u094D\u0930\u094B\u092B\u093C\u093E\u0907\u0932" },
  labColModel: { en: "Model", hi: "\u092E\u0949\u0921\u0932" },
  labColAvailability: { en: "Availability", hi: "\u0909\u092A\u0932\u092C\u094D\u0927\u0924\u093E" },
  labColFallback: { en: "Fallback", hi: "\u092B\u093C\u0949\u0932\u092C\u0948\u0915" },
  labColVerified: { en: "Last verified", hi: "\u0905\u0902\u0924\u093F\u092E verify" },
  labColUsage: { en: "Usage", hi: "\u0909\u092A\u092F\u094B\u0917" },
  labNoVerify: { en: "never", hi: "\u0915\u092D\u0940 \u0928\u0939\u0940\u0902" },
  labBlockers: { en: "External blockers", hi: "\u092C\u093E\u0939\u0930\u0940 blockers" },
  futureNote: {
    en: "Phase-J wiring pending \u2014 stored values stay inactive until the canonical runtime PR lands.",
    hi: "Phase-J wiring \u092C\u093E\u0915\u0940 \u2014 canonical runtime PR \u0906\u0928\u0947 \u0924\u0915 \u0938\u0939\u0947\u091C\u0947 values \u0928\u093F\u0937\u094D\u0915\u094D\u0930\u093F\u092F \u0930\u0939\u0947\u0902\u0917\u0947\u0964"
  },
  activeCard: { en: "Active", hi: "\u0938\u0915\u094D\u0930\u093F\u092F" },
  storedCard: { en: "Stored (not live)", hi: "\u0938\u0939\u0947\u091C\u0940 (live \u0928\u0939\u0940\u0902)" },
  futureCard: { en: "Future phase", hi: "\u092D\u0935\u093F\u0937\u094D\u092F \u091A\u0930\u0923" },
  copyDiag: { en: "Copy safe diagnostics", hi: "Safe diagnostics copy \u0915\u0930\u0947\u0902" },
  copied: { en: "Copied", hi: "Copy \u0939\u0941\u0906" },
  savedOk: { en: "Saved.", hi: "Save \u0939\u094B \u0917\u092F\u093E\u0964" },
  clearedOk: { en: "Cleared.", hi: "Clear \u0939\u094B \u0917\u092F\u093E\u0964" }
};
function tr14(key, locale) {
  return STRINGS6[key]?.[locale] ?? key;
}
function MasterProviderSettingsPanel({ token, locale = "en", theme: theme2 = "light", fetchImpl = fetch }) {
  const colors = themeColors(theme2);
  const [items, setItems] = useState43([]);
  const [groups, setGroups] = useState43([]);
  const [vaultAvailable, setVaultAvailable] = useState43(true);
  const [loading, setLoading] = useState43(true);
  const [error, setError] = useState43(null);
  const [query, setQuery] = useState43("");
  const [filter, setFilter] = useState43("all");
  const [collapsed, setCollapsed] = useState43({});
  const [editing, setEditing] = useState43(null);
  const [editValue, setEditValue] = useState43("");
  const [saving, setSaving] = useState43(false);
  const [saveMsg, setSaveMsg] = useState43(null);
  const [testBusy, setTestBusy] = useState43(null);
  const [testResults, setTestResults] = useState43({});
  const [copied, setCopied] = useState43(false);
  const [lab, setLab] = useState43(null);
  async function load() {
    if (!token) {
      setError(tr14("error", locale));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchImpl("/api/admin/provider-config", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
      setItems(data.items ?? []);
      setGroups(data.groups ?? []);
      setVaultAvailable(data.vaultAvailable !== false);
      try {
        const labRes = await fetchImpl("/api/admin/provider-lab", { headers: { Authorization: `Bearer ${token}` } });
        const labData = await labRes.json();
        if (labRes.ok && labData.ok) setLab(labData);
      } catch {
        setLab(null);
      }
    } catch (e) {
      setError(errorToCode(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect36(() => {
    void load();
  }, [token]);
  const filtered = useMemo23(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (filter === "configured" && !it.configured) return false;
      if (filter === "missing" && it.configured) return false;
      if (filter === "secret" && it.type !== "secret") return false;
      if (filter === "url" && it.type !== "url") return false;
      if (q) {
        const hay = `${it.key} ${it.label} ${it.labelHi}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, query, filter]);
  const byGroup = useMemo23(() => {
    const m = /* @__PURE__ */ new Map();
    for (const it of filtered) {
      const arr = m.get(it.group) ?? [];
      arr.push(it);
      m.set(it.group, arr);
    }
    return m;
  }, [filtered]);
  const summary = useMemo23(() => {
    const total = items.length;
    const configured = items.filter((i) => i.configured).length;
    const active = items.filter((i) => i.activationState === "active").length;
    const stored = items.filter((i) => i.activationState === "stored_not_active").length;
    const future = items.filter((i) => i.activationState === "future_phase").length;
    const missing = items.filter((i) => i.activationState === "not_configured").length;
    const secrets = items.filter((i) => i.type === "secret").length;
    const urls = items.filter((i) => i.type === "url").length;
    return { total, configured, active, stored, future, missing, secrets, urls };
  }, [items]);
  async function runTest(groupId) {
    if (!token) return;
    setTestBusy(groupId);
    setError(null);
    try {
      const res = await fetchImpl("/api/admin/provider-config/test", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ test_type: groupId })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
      setTestResults((prev) => ({ ...prev, [groupId]: data.results ?? [] }));
    } catch (e) {
      setError(errorToCode(e));
    } finally {
      setTestBusy(null);
    }
  }
  function openEdit(item) {
    setEditing(item);
    setEditValue(item.type === "secret" ? "" : item.value ?? "");
    setSaveMsg(null);
  }
  async function saveEdit(clear = false) {
    if (!token || !editing) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetchImpl("/api/admin/provider-config/upsert", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(clear ? { key: editing.key, clear: true } : { key: editing.key, value: editValue })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.errorCode ?? data.error ?? `HTTP ${res.status}`);
      setSaveMsg(clear ? tr14("clearedOk", locale) : tr14("savedOk", locale));
      setEditing(null);
      await load();
    } catch (e) {
      setError(errorToCode(e));
    } finally {
      setSaving(false);
    }
  }
  function copyDiagnostics() {
    const lines = [
      `# Master Provider Settings \u2014 ${(/* @__PURE__ */ new Date()).toISOString()}`,
      `total=${summary.total} active=${summary.active} stored_not_live=${summary.stored} future=${summary.future} missing=${summary.missing} vault=${vaultAvailable}`,
      ...items.map((i) => `${i.key} activation=${i.activationState} activeSource=${i.activeSource ?? "-"} stored=${i.storedConfigured} env=${i.envConfigured} type=${i.type}`)
    ];
    void navigator.clipboard?.writeText(lines.join("\n")).then(() => setCopied(true));
  }
  const groupLabel = (id) => {
    const g = groups.find((x) => x.id === id);
    return locale === "hi" ? g?.labelHi ?? id : g?.label ?? id;
  };
  const FILTERS = [
    { id: "all", label: tr14("all", locale) },
    { id: "configured", label: tr14("configured", locale) },
    { id: "missing", label: tr14("missing", locale) },
    { id: "secret", label: tr14("secrets", locale) },
    { id: "url", label: tr14("urls", locale) }
  ];
  const cardStyle13 = {
    flex: "1 1 120px",
    border: `1px solid ${colors.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
    background: colors.surface
  };
  const cardNum = { fontSize: "20px", fontWeight: 700 };
  const cardLbl = { fontSize: "11px", color: colors.textMuted };
  if (loading) {
    return /* @__PURE__ */ jsx82("div", { style: { padding: tokens.spacing.md, color: colors.textMuted, fontSize: "13px" }, children: tr14("loading", locale) });
  }
  return /* @__PURE__ */ jsxs74("div", { style: { display: "flex", flexDirection: "column", gap: tokens.spacing.md }, children: [
    error && /* @__PURE__ */ jsxs74("div", { role: "alert", style: { padding: tokens.spacing.sm, border: `1px solid ${tokens.colors.danger}`, borderRadius: tokens.radius.md, color: tokens.colors.danger, fontSize: "12px" }, children: [
      safeErrorText(error, locale).title,
      " ",
      /* @__PURE__ */ jsx82("code", { children: safeErrorCode(error) })
    ] }),
    !vaultAvailable && /* @__PURE__ */ jsx82("div", { style: { padding: tokens.spacing.sm, border: `1px solid #c2410c`, borderRadius: tokens.radius.md, color: "#c2410c", fontSize: "12px" }, children: tr14("vaultUnavailable", locale) }),
    /* @__PURE__ */ jsxs74("div", { style: { display: "flex", gap: tokens.spacing.sm, flexWrap: "wrap" }, children: [
      /* @__PURE__ */ jsxs74("div", { style: cardStyle13, children: [
        /* @__PURE__ */ jsx82("div", { style: cardNum, children: summary.total }),
        /* @__PURE__ */ jsx82("div", { style: cardLbl, children: tr14("total", locale) })
      ] }),
      /* @__PURE__ */ jsxs74("div", { style: cardStyle13, children: [
        /* @__PURE__ */ jsx82("div", { style: { ...cardNum, color: tokens.colors.success }, children: summary.active }),
        /* @__PURE__ */ jsx82("div", { style: cardLbl, children: tr14("activeCard", locale) })
      ] }),
      /* @__PURE__ */ jsxs74("div", { style: cardStyle13, children: [
        /* @__PURE__ */ jsx82("div", { style: { ...cardNum, color: summary.stored > 0 ? "#b45309" : colors.text }, children: summary.stored }),
        /* @__PURE__ */ jsx82("div", { style: cardLbl, children: tr14("storedCard", locale) })
      ] }),
      /* @__PURE__ */ jsxs74("div", { style: cardStyle13, children: [
        /* @__PURE__ */ jsx82("div", { style: { ...cardNum, color: colors.textMuted }, children: summary.future }),
        /* @__PURE__ */ jsx82("div", { style: cardLbl, children: tr14("futureCard", locale) })
      ] }),
      /* @__PURE__ */ jsxs74("div", { style: cardStyle13, children: [
        /* @__PURE__ */ jsx82("div", { style: { ...cardNum, color: summary.missing > 0 ? tokens.colors.danger : colors.text }, children: summary.missing }),
        /* @__PURE__ */ jsx82("div", { style: cardLbl, children: tr14("missing", locale) })
      ] })
    ] }),
    lab && /* @__PURE__ */ jsxs74("div", { style: { border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, padding: tokens.spacing.md, background: colors.surface }, children: [
      /* @__PURE__ */ jsx82("div", { style: { fontWeight: 700, fontSize: "13px", marginBottom: tokens.spacing.sm }, children: tr14("labTitle", locale) }),
      /* @__PURE__ */ jsx82("div", { style: { overflowX: "auto" }, children: /* @__PURE__ */ jsxs74("table", { style: { width: "100%", borderCollapse: "collapse", fontSize: "11.5px" }, children: [
        /* @__PURE__ */ jsx82("thead", { children: /* @__PURE__ */ jsxs74("tr", { style: { textAlign: "left", color: colors.textMuted }, children: [
          /* @__PURE__ */ jsx82("th", { style: { padding: "4px 8px" }, children: tr14("labColProfile", locale) }),
          /* @__PURE__ */ jsx82("th", { style: { padding: "4px 8px" }, children: tr14("labColModel", locale) }),
          /* @__PURE__ */ jsx82("th", { style: { padding: "4px 8px" }, children: tr14("labColAvailability", locale) }),
          /* @__PURE__ */ jsx82("th", { style: { padding: "4px 8px" }, children: tr14("labColFallback", locale) }),
          /* @__PURE__ */ jsx82("th", { style: { padding: "4px 8px" }, children: tr14("labColVerified", locale) }),
          /* @__PURE__ */ jsx82("th", { style: { padding: "4px 8px" }, children: tr14("labColUsage", locale) })
        ] }) }),
        /* @__PURE__ */ jsx82("tbody", { children: lab.profiles.map((p) => /* @__PURE__ */ jsxs74("tr", { style: { borderTop: `1px solid ${colors.border}` }, children: [
          /* @__PURE__ */ jsxs74("td", { style: { padding: "6px 8px" }, children: [
            /* @__PURE__ */ jsx82("div", { style: { fontWeight: 600 }, children: p.profileId }),
            /* @__PURE__ */ jsxs74("div", { style: { color: colors.textMuted }, children: [
              p.provider,
              " \xB7 ",
              p.logicalProfile,
              " \xB7 ",
              p.endpointLabel,
              " \xB7 ",
              p.privacyClass
            ] }),
            /* @__PURE__ */ jsxs74("div", { style: { color: colors.textMuted }, children: [
              "cred-ref: ",
              p.credentialRef,
              " \xB7 surfaces: ",
              p.allowedSurfaces.join("/")
            ] }),
            p.lastFailureClass && /* @__PURE__ */ jsxs74("div", { style: { color: tokens.colors.danger }, children: [
              "failure: ",
              p.lastFailureClass
            ] })
          ] }),
          /* @__PURE__ */ jsx82("td", { style: { padding: "6px 8px" }, children: /* @__PURE__ */ jsx82("code", { children: p.physicalModel }) }),
          /* @__PURE__ */ jsxs74("td", { style: { padding: "6px 8px" }, children: [
            /* @__PURE__ */ jsx82("span", { style: { color: p.availability === "available" ? tokens.colors.success : p.availability === "unavailable" ? tokens.colors.danger : "#b45309" }, children: p.availability }),
            /* @__PURE__ */ jsxs74("div", { style: { color: colors.textMuted }, children: [
              p.health,
              " \xB7 ",
              p.latencyBucket,
              " \xB7 cost:",
              p.costTruth
            ] })
          ] }),
          /* @__PURE__ */ jsxs74("td", { style: { padding: "6px 8px" }, children: [
            "#",
            p.fallbackOrder
          ] }),
          /* @__PURE__ */ jsx82("td", { style: { padding: "6px 8px" }, children: p.lastVerifiedAt ? new Date(p.lastVerifiedAt).toLocaleString(locale === "hi" ? "hi-IN" : "en-IN") : tr14("labNoVerify", locale) }),
          /* @__PURE__ */ jsx82("td", { style: { padding: "6px 8px" }, children: p.usage.measured ? `${p.usage.requestCount} req \xB7 ${p.usage.totalTokens} tok \xB7 ${p.usage.fallbackCount} fb` : "\u2014" })
        ] }, p.profileId)) })
      ] }) }),
      lab.blockers.length > 0 && /* @__PURE__ */ jsxs74("div", { style: { marginTop: tokens.spacing.sm }, children: [
        /* @__PURE__ */ jsx82("div", { style: { fontWeight: 600, fontSize: "12px" }, children: tr14("labBlockers", locale) }),
        /* @__PURE__ */ jsx82("ul", { style: { margin: "4px 0", paddingLeft: tokens.spacing.lg, fontSize: "11.5px" }, children: lab.blockers.map((b) => /* @__PURE__ */ jsxs74("li", { children: [
          /* @__PURE__ */ jsx82("code", { children: b.blockerId }),
          " \u2014 ",
          b.status,
          " \u2014 ",
          locale === "hi" ? b.summaryHi : b.summary
        ] }, b.blockerId)) })
      ] })
    ] }),
    /* @__PURE__ */ jsxs74("div", { style: { display: "flex", gap: tokens.spacing.sm, alignItems: "center", flexWrap: "wrap" }, children: [
      /* @__PURE__ */ jsx82(
        "input",
        {
          "aria-label": tr14("search", locale),
          placeholder: tr14("search", locale),
          value: query,
          onChange: (e) => setQuery(e.target.value),
          style: { flex: "1 1 200px", padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, fontSize: "13px", background: colors.surface, color: colors.text }
        }
      ),
      FILTERS.map((f) => /* @__PURE__ */ jsx82(
        "button",
        {
          type: "button",
          onClick: () => setFilter(f.id),
          style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, fontSize: "12px", cursor: "pointer", background: filter === f.id ? colors.surface : "transparent", color: filter === f.id ? colors.warmAccent : colors.text },
          children: f.label
        },
        f.id
      )),
      /* @__PURE__ */ jsx82("button", { type: "button", onClick: () => void load(), style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, fontSize: "12px", cursor: "pointer", background: "transparent", color: colors.text }, children: tr14("refresh", locale) })
    ] }),
    [...byGroup.entries()].map(([gid, groupItems]) => {
      const isCollapsed = collapsed[gid] ?? false;
      const results = testResults[gid];
      return /* @__PURE__ */ jsxs74("div", { style: { border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, overflow: "hidden" }, children: [
        /* @__PURE__ */ jsxs74("div", { style: { display: "flex", alignItems: "center", gap: tokens.spacing.sm, padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, background: colors.surface }, children: [
          /* @__PURE__ */ jsxs74(
            "button",
            {
              type: "button",
              onClick: () => setCollapsed((p) => ({ ...p, [gid]: !isCollapsed })),
              style: { flex: 1, textAlign: "left", border: "none", background: "transparent", cursor: "pointer", fontSize: "14px", fontWeight: 600, color: colors.text },
              children: [
                isCollapsed ? "\u25B8" : "\u25BE",
                " ",
                groupLabel(gid),
                " (",
                groupItems.length,
                ")"
              ]
            }
          ),
          /* @__PURE__ */ jsx82(
            "button",
            {
              type: "button",
              disabled: testBusy === gid,
              onClick: () => void runTest(gid),
              style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, fontSize: "12px", cursor: "pointer", background: "transparent", color: colors.text, opacity: testBusy === gid ? 0.6 : 1 },
              children: testBusy === gid ? tr14("testing", locale) : tr14("testGroup", locale)
            }
          )
        ] }),
        !isCollapsed && /* @__PURE__ */ jsxs74("div", { style: { display: "flex", flexDirection: "column" }, children: [
          groupItems.map((it) => /* @__PURE__ */ jsxs74("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacing.md, padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderTop: `1px solid ${colors.border}` }, children: [
            /* @__PURE__ */ jsxs74("div", { style: { minWidth: 0, flex: 1 }, children: [
              /* @__PURE__ */ jsx82("div", { style: { fontSize: "13px", fontWeight: 500, color: colors.text }, children: locale === "hi" ? it.labelHi || it.label : it.label }),
              /* @__PURE__ */ jsx82("div", { style: { fontSize: "11px", color: colors.textMuted, fontFamily: "monospace" }, children: it.key }),
              /* @__PURE__ */ jsxs74("div", { style: { fontSize: "11px", color: colors.textMuted }, children: [
                it.type === "secret" ? it.configured ? it.masked : "\u2014" : it.configured ? it.value ?? "\u2014" : "\u2014",
                " \xB7 ",
                it.source ? tr14(`source${it.source === "vault" ? "Vault" : "Env"}`, locale) : tr14("missing", locale),
                !it.writable && ` \xB7 ${tr14("envManaged", locale)}`
              ] }),
              it.helpTextHi && locale === "hi" && /* @__PURE__ */ jsx82("div", { style: { fontSize: "11px", color: colors.textMuted }, children: it.helpTextHi })
            ] }),
            /* @__PURE__ */ jsxs74("div", { style: { display: "flex", alignItems: "center", gap: tokens.spacing.sm }, children: [
              /* @__PURE__ */ jsx82("span", { style: { fontSize: "11px", fontWeight: 600, color: it.activationState === "active" ? tokens.colors.success : it.activationState === "stored_not_active" ? "#b45309" : it.activationState === "future_phase" ? colors.textMuted : tokens.colors.danger }, children: it.activationState === "active" ? `${tr14("stateActive", locale)}${it.activeSource ? `\xB7${it.activeSource}` : ""}` : it.activationState === "stored_not_active" ? tr14("stateStored", locale) : it.activationState === "future_phase" ? tr14("stateFuture", locale) : tr14("stateMissing", locale) }),
              it.writable && /* @__PURE__ */ jsx82(
                "button",
                {
                  type: "button",
                  onClick: () => openEdit(it),
                  style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, fontSize: "12px", cursor: "pointer", background: "transparent", color: colors.text },
                  children: tr14("edit", locale)
                }
              )
            ] })
          ] }, it.key)),
          results && /* @__PURE__ */ jsx82("div", { style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, borderTop: `1px solid ${colors.border}`, background: colors.surface }, children: results.map((r) => /* @__PURE__ */ jsxs74("div", { style: { display: "flex", justifyContent: "space-between", fontSize: "12px", padding: "2px 0", color: colors.text }, children: [
            /* @__PURE__ */ jsx82("span", { style: { fontFamily: "monospace" }, children: r.key }),
            /* @__PURE__ */ jsxs74("span", { style: { color: r.ok ? tokens.colors.success : tokens.colors.danger }, children: [
              r.status,
              r.latencyMs != null ? ` \xB7 ${r.latencyMs}ms` : "",
              r.detail ? ` \xB7 ${r.detail}` : ""
            ] })
          ] }, r.key)) })
        ] })
      ] }, gid);
    }),
    filtered.length === 0 && /* @__PURE__ */ jsx82("div", { style: { fontSize: "13px", color: colors.textMuted }, children: tr14("noKeys", locale) }),
    /* @__PURE__ */ jsxs74("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacing.sm }, children: [
      /* @__PURE__ */ jsx82("span", { style: { fontSize: "11px", color: colors.textMuted }, children: tr14("vaultNote", locale) }),
      /* @__PURE__ */ jsx82(
        "button",
        {
          type: "button",
          onClick: copyDiagnostics,
          style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, fontSize: "12px", cursor: "pointer", background: "transparent", color: colors.text },
          children: copied ? tr14("copied", locale) : tr14("copyDiag", locale)
        }
      )
    ] }),
    editing && /* @__PURE__ */ jsx82("div", { role: "dialog", "aria-modal": "true", style: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 110, display: "flex", alignItems: "center", justifyContent: "center" }, children: /* @__PURE__ */ jsxs74("div", { style: { width: 440, padding: tokens.spacing.lg, backgroundColor: colors.surfaceStrong, borderRadius: tokens.radius.lg, border: `1px solid ${colors.border}` }, children: [
      /* @__PURE__ */ jsx82("h3", { style: { marginTop: 0, fontSize: "16px" }, children: locale === "hi" ? editing.labelHi || editing.label : editing.label }),
      /* @__PURE__ */ jsx82("div", { style: { fontSize: "11px", fontFamily: "monospace", color: colors.textMuted, marginBottom: tokens.spacing.sm }, children: editing.key }),
      editing.type === "select" && editing.allowedValues ? /* @__PURE__ */ jsx82("select", { value: editValue, onChange: (e) => setEditValue(e.target.value), style: { width: "100%", padding: tokens.spacing.sm, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, fontSize: "13px" }, children: editing.allowedValues.map((v) => /* @__PURE__ */ jsx82("option", { value: v, children: v }, v)) }) : /* @__PURE__ */ jsx82(
        "input",
        {
          type: editing.type === "secret" ? "password" : "text",
          value: editValue,
          onChange: (e) => setEditValue(e.target.value),
          placeholder: editing.type === "secret" ? "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" : "",
          style: { width: "100%", padding: tokens.spacing.sm, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, fontSize: "13px", boxSizing: "border-box" }
        }
      ),
      editing.type === "secret" && editing.masked && /* @__PURE__ */ jsx82("div", { style: { fontSize: "11px", color: colors.textMuted, marginTop: tokens.spacing.sm }, children: editing.masked }),
      editing.activationState === "future_phase" ? /* @__PURE__ */ jsx82("div", { style: { fontSize: "11px", color: "#b45309", marginTop: tokens.spacing.sm }, children: tr14("futureNote", locale) }) : editing.activeSource === null || editing.activeSource === "env" ? (
        // env-consumer (or unconsumed) keys: a vault save never goes live
        /* @__PURE__ */ jsx82("div", { style: { fontSize: "11px", color: "#b45309", marginTop: tokens.spacing.sm }, children: tr14("storedNote", locale) })
      ) : null,
      /* @__PURE__ */ jsxs74("div", { style: { display: "flex", justifyContent: "space-between", gap: tokens.spacing.sm, marginTop: tokens.spacing.md }, children: [
        /* @__PURE__ */ jsx82("button", { type: "button", disabled: saving, onClick: () => void saveEdit(true), style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, border: `1px solid ${tokens.colors.danger}`, borderRadius: tokens.radius.md, fontSize: "12px", cursor: "pointer", background: "transparent", color: tokens.colors.danger }, children: tr14("clear", locale) }),
        /* @__PURE__ */ jsxs74("div", { style: { display: "flex", gap: tokens.spacing.sm }, children: [
          /* @__PURE__ */ jsx82("button", { type: "button", onClick: () => setEditing(null), style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, border: `1px solid ${colors.border}`, borderRadius: tokens.radius.md, fontSize: "12px", cursor: "pointer", background: "transparent", color: colors.text }, children: tr14("cancel", locale) }),
          /* @__PURE__ */ jsx82("button", { type: "button", disabled: saving, onClick: () => void saveEdit(false), style: { padding: `${tokens.spacing.sm} ${tokens.spacing.md}`, border: "none", borderRadius: tokens.radius.md, fontSize: "12px", cursor: "pointer", background: colors.warmAccent, color: "#fff" }, children: saving ? "Saving\u2026" : tr14("save", locale) })
        ] })
      ] }),
      saveMsg && /* @__PURE__ */ jsx82("div", { style: { fontSize: "12px", color: tokens.colors.success, marginTop: tokens.spacing.sm }, children: saveMsg })
    ] }) })
  ] });
}

// src/workspace/SystemHealthPanel.tsx
import { useCallback as useCallback29, useEffect as useEffect37, useMemo as useMemo24, useState as useState44 } from "react";
import { Fragment as Fragment11, jsx as jsx83, jsxs as jsxs75 } from "react/jsx-runtime";
var STRINGS7 = {
  title: { en: "System Health", hi: "\u0938\u093F\u0938\u094D\u091F\u092E \u0939\u0947\u0932\u094D\u0925" },
  subtitle: {
    en: "Live-measured status of the deployed runtime. Every row is checked by the server right now \u2014 nothing is green from configuration alone.",
    hi: "\u0921\u093F\u092A\u094D\u0932\u0949\u092F\u0921 \u0930\u0928\u091F\u093E\u0907\u092E \u0915\u0940 \u0932\u093E\u0907\u0935-\u092E\u093E\u092A\u0940 \u0938\u094D\u0925\u093F\u0924\u093F\u0964 \u0939\u0930 \u092A\u0902\u0915\u094D\u0924\u093F \u0905\u092D\u0940 \u0938\u0930\u094D\u0935\u0930 \u0926\u094D\u0935\u093E\u0930\u093E \u091C\u093E\u0901\u091A\u0940 \u0917\u0908 \u0939\u0948 \u2014 \u0915\u0947\u0935\u0932 \u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930\u0947\u0936\u0928 \u0938\u0947 \u0915\u0941\u091B \u092D\u0940 \u0939\u0930\u093E \u0928\u0939\u0940\u0902 \u0939\u0948\u0964"
  },
  refresh: { en: "Refresh", hi: "\u0930\u0940\u092B\u094D\u0930\u0947\u0936" },
  refreshing: { en: "Checking\u2026", hi: "\u091C\u093E\u0901\u091A \u0939\u094B \u0930\u0939\u0940 \u0939\u0948\u2026" },
  ok: { en: "Healthy", hi: "\u0920\u0940\u0915" },
  degraded: { en: "Degraded", hi: "\u0918\u091F\u093E \u0939\u0941\u0906" },
  down: { en: "Down", hi: "\u092C\u0902\u0926" },
  not_configured: { en: "Not configured", hi: "\u0915\u0949\u0928\u094D\u092B\u093C\u093F\u0917\u0930 \u0928\u0939\u0940\u0902" },
  total: { en: "Services", hi: "\u0938\u0947\u0935\u093E\u090F\u0901" },
  release: { en: "Release", hi: "\u0930\u093F\u0932\u0940\u091C\u093C" },
  environment: { en: "Environment", hi: "\u092A\u0930\u093F\u0935\u0947\u0936" },
  uptime: { en: "Uptime", hi: "\u0905\u092A\u091F\u093E\u0907\u092E" },
  node: { en: "Node", hi: "Node" },
  build: { en: "Build SHA", hi: "\u092C\u093F\u0932\u094D\u0921 SHA" },
  checkedAt: { en: "Checked at", hi: "\u091C\u093E\u0901\u091A \u0938\u092E\u092F" },
  latency: { en: "latency", hi: "\u0935\u093F\u0932\u0902\u092C" },
  error: { en: "Load failed", hi: "\u0932\u094B\u0921 \u0935\u093F\u092B\u0932" },
  emptyToken: { en: "Session token required.", hi: "\u0938\u0924\u094D\u0930 \u091F\u094B\u0915\u0928 \u0906\u0935\u0936\u094D\u092F\u0915 \u0939\u0948\u0964" }
};
function tr15(key, locale) {
  return STRINGS7[key]?.[locale] ?? key;
}
function fmtUptime(seconds, locale) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return locale === "hi" ? `${m} \u092E\u093F\u0928\u091F` : `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return locale === "hi" ? `${h} \u0918\u0902\u091F\u0947 ${m % 60} \u092E\u093F` : `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return locale === "hi" ? `${d} \u0926\u093F\u0928` : `${d}d`;
}
var STATUS_COLORS3 = {
  ok: tokens.colors.success,
  degraded: "#c2410c",
  down: tokens.colors.danger,
  not_configured: "#6b7280"
};
function SystemHealthPanel({ token, locale = "en", theme: theme2 = "light", fetchImpl = fetch }) {
  const colors = themeColors(theme2);
  const [data, setData] = useState44(null);
  const [loading, setLoading] = useState44(true);
  const [error, setError] = useState44(null);
  const load = useCallback29(async () => {
    if (!token) {
      setError(tr15("emptyToken", locale));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchImpl("/api/admin/system-health", { headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json();
      if (!res.ok || !body.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      setData(body);
    } catch {
      setError(safeErrorText("load_failed", locale).title);
    } finally {
      setLoading(false);
    }
  }, [token, locale, fetchImpl]);
  useEffect37(() => {
    void load();
  }, [load]);
  const statusLabel2 = useCallback29(
    (s) => tr15(s, locale),
    [locale]
  );
  const cards = useMemo24(() => {
    if (!data) return [];
    return [
      { key: "ok", label: tr15("ok", locale), value: data.summary.ok, color: STATUS_COLORS3.ok },
      { key: "degraded", label: tr15("degraded", locale), value: data.summary.degraded, color: STATUS_COLORS3.degraded },
      { key: "down", label: tr15("down", locale), value: data.summary.down, color: STATUS_COLORS3.down },
      { key: "not_configured", label: tr15("not_configured", locale), value: data.summary.notConfigured, color: STATUS_COLORS3.not_configured }
    ];
  }, [data, locale]);
  return /* @__PURE__ */ jsxs75("div", { "data-testid": "system-health-panel", style: { display: "flex", flexDirection: "column", gap: tokens.spacing.md }, children: [
    /* @__PURE__ */ jsxs75("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacing.md }, children: [
      /* @__PURE__ */ jsxs75("div", { children: [
        /* @__PURE__ */ jsx83("h3", { style: { margin: 0, fontSize: "16px", color: colors.text }, children: tr15("title", locale) }),
        /* @__PURE__ */ jsx83("p", { style: { margin: "4px 0 0", fontSize: "12px", color: colors.textMuted }, children: tr15("subtitle", locale) })
      ] }),
      /* @__PURE__ */ jsx83(
        "button",
        {
          type: "button",
          onClick: () => void load(),
          disabled: loading,
          style: {
            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
            borderRadius: tokens.radius.md,
            border: `1px solid ${colors.border}`,
            background: colors.surface,
            color: colors.text,
            cursor: loading ? "default" : "pointer",
            fontSize: "13px",
            flexShrink: 0
          },
          children: loading ? tr15("refreshing", locale) : tr15("refresh", locale)
        }
      )
    ] }),
    error ? /* @__PURE__ */ jsxs75("p", { role: "alert", style: { margin: 0, fontSize: "13px", color: tokens.colors.danger }, children: [
      tr15("error", locale),
      " \u2014 ",
      error
    ] }) : null,
    data ? /* @__PURE__ */ jsxs75(Fragment11, { children: [
      /* @__PURE__ */ jsx83("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: tokens.spacing.sm }, children: cards.map((c) => /* @__PURE__ */ jsxs75(
        "div",
        {
          "data-testid": `health-card-${c.key}`,
          style: {
            padding: tokens.spacing.md,
            border: `1px solid ${colors.border}`,
            borderRadius: tokens.radius.md,
            background: colors.surface
          },
          children: [
            /* @__PURE__ */ jsx83("div", { style: { fontSize: "22px", fontWeight: 700, color: c.color }, children: c.value }),
            /* @__PURE__ */ jsx83("div", { style: { fontSize: "11px", color: colors.textMuted }, children: c.label })
          ]
        },
        c.key
      )) }),
      /* @__PURE__ */ jsxs75(
        "div",
        {
          "data-testid": "health-server-block",
          style: {
            padding: tokens.spacing.md,
            border: `1px solid ${colors.border}`,
            borderRadius: tokens.radius.md,
            background: colors.surface,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: tokens.spacing.sm,
            fontSize: "12px"
          },
          children: [
            /* @__PURE__ */ jsxs75("div", { children: [
              /* @__PURE__ */ jsx83("div", { style: { color: colors.textMuted }, children: tr15("release", locale) }),
              /* @__PURE__ */ jsx83("div", { style: { color: colors.text, fontFamily: "monospace" }, children: data.server.releaseVersion })
            ] }),
            /* @__PURE__ */ jsxs75("div", { children: [
              /* @__PURE__ */ jsx83("div", { style: { color: colors.textMuted }, children: tr15("environment", locale) }),
              /* @__PURE__ */ jsx83("div", { style: { color: colors.text }, children: data.server.environment })
            ] }),
            /* @__PURE__ */ jsxs75("div", { children: [
              /* @__PURE__ */ jsx83("div", { style: { color: colors.textMuted }, children: tr15("uptime", locale) }),
              /* @__PURE__ */ jsx83("div", { style: { color: colors.text }, children: fmtUptime(data.server.uptimeSeconds, locale) })
            ] }),
            /* @__PURE__ */ jsxs75("div", { children: [
              /* @__PURE__ */ jsx83("div", { style: { color: colors.textMuted }, children: tr15("node", locale) }),
              /* @__PURE__ */ jsx83("div", { style: { color: colors.text, fontFamily: "monospace" }, children: data.server.nodeVersion })
            ] }),
            data.server.buildSha ? /* @__PURE__ */ jsxs75("div", { children: [
              /* @__PURE__ */ jsx83("div", { style: { color: colors.textMuted }, children: tr15("build", locale) }),
              /* @__PURE__ */ jsx83("div", { style: { color: colors.text, fontFamily: "monospace" }, children: data.server.buildSha.slice(0, 12) })
            ] }) : null,
            /* @__PURE__ */ jsxs75("div", { children: [
              /* @__PURE__ */ jsx83("div", { style: { color: colors.textMuted }, children: tr15("checkedAt", locale) }),
              /* @__PURE__ */ jsx83("div", { style: { color: colors.text }, children: new Date(data.generatedAt).toLocaleTimeString() })
            ] })
          ]
        }
      ),
      /* @__PURE__ */ jsx83("div", { "data-testid": "health-service-rows", style: { display: "flex", flexDirection: "column", gap: tokens.spacing.sm }, children: data.services.map((s) => /* @__PURE__ */ jsxs75(
        "div",
        {
          "data-testid": `health-service-${s.id}`,
          style: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.spacing.md,
            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
            border: `1px solid ${colors.border}`,
            borderRadius: tokens.radius.md,
            background: colors.surface
          },
          children: [
            /* @__PURE__ */ jsxs75("div", { style: { minWidth: 0 }, children: [
              /* @__PURE__ */ jsx83("div", { style: { fontSize: "13px", fontWeight: 500, color: colors.text }, children: locale === "hi" ? s.labelHi : s.label }),
              s.detail ? /* @__PURE__ */ jsxs75("div", { style: { fontSize: "11px", color: colors.textMuted, fontFamily: "monospace" }, children: [
                s.detail,
                typeof s.latencyMs === "number" ? ` \xB7 ${s.latencyMs}ms ${tr15("latency", locale)}` : ""
              ] }) : typeof s.latencyMs === "number" ? /* @__PURE__ */ jsxs75("div", { style: { fontSize: "11px", color: colors.textMuted }, children: [
                s.latencyMs,
                "ms ",
                tr15("latency", locale)
              ] }) : null
            ] }),
            /* @__PURE__ */ jsx83(
              "span",
              {
                "data-testid": `health-status-${s.id}`,
                style: { fontSize: "11px", fontWeight: 700, color: STATUS_COLORS3[s.status], textTransform: "uppercase" },
                children: statusLabel2(s.status)
              }
            )
          ]
        },
        s.id
      )) })
    ] }) : null
  ] });
}

// src/product/productStrings.ts
var STRINGS8 = {
  newChat: { en: "New chat", hi: "\u0928\u0908 \u091A\u0948\u091F" },
  recentChats: { en: "Recent", hi: "\u0939\u093E\u0932 \u0915\u0947" },
  tagline: { en: "Your AI teammate", hi: "\u0906\u092A\u0915\u093E AI \u0938\u093E\u0925\u0940" },
  navChat: { en: "Chats", hi: "\u091A\u0948\u091F\u094D\u0938" },
  navProjects: { en: "Projects", hi: "\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F\u094D\u0938" },
  navFiles: { en: "Files", hi: "\u092B\u093C\u093E\u0907\u0932\u0947\u0902" },
  navAutomation: { en: "Automations", hi: "\u0911\u091F\u094B\u092E\u0947\u0936\u0928" },
  navApps: { en: "Apps", hi: "\u0910\u092A\u094D\u0938" },
  navSkills: { en: "Skills", hi: "\u0938\u094D\u0915\u093F\u0932\u094D\u0938" },
  welcomeTitle: { en: "How can I help today?", hi: "\u0906\u091C \u092E\u0948\u0902 \u0915\u0948\u0938\u0947 \u092E\u0926\u0926 \u0915\u0930\u0942\u0901?" },
  welcomeSubtitle: {
    en: "Chat, code, design and covers in one place.",
    hi: "\u091A\u0948\u091F, \u0915\u094B\u0921, \u0921\u093F\u091C\u093C\u093E\u0907\u0928 \u0914\u0930 \u0915\u0935\u0930 \u2014 \u0938\u092C \u090F\u0915 \u091C\u0917\u0939\u0964"
  },
  composerPlaceholder: { en: "Message Smart Shikka\u2026", hi: "Smart Shikka \u0915\u094B \u0932\u093F\u0916\u0947\u0902\u2026" },
  attachFile: { en: "Attach file", hi: "\u092B\u093C\u093E\u0907\u0932 \u091C\u094B\u0921\u093C\u0947\u0902" },
  voiceInput: { en: "Voice input", hi: "\u0906\u0935\u093E\u091C\u093C \u0938\u0947 \u0932\u093F\u0916\u0947\u0902" },
  listening: { en: "Listening\u2026", hi: "\u0938\u0941\u0928 \u0930\u0939\u093E \u0939\u0942\u0901\u2026" },
  send: { en: "Send", hi: "\u092D\u0947\u091C\u0947\u0902" },
  aiDisclaimer: {
    en: "Smart Shikka can make mistakes. Consequential actions always ask for approval.",
    hi: "Smart Shikka \u0938\u0947 \u092D\u0942\u0932 \u0939\u094B \u0938\u0915\u0924\u0940 \u0939\u0948\u0964 \u092E\u0939\u0924\u094D\u0935\u092A\u0942\u0930\u094D\u0923 \u0915\u093E\u0930\u094D\u092F\u094B\u0902 \u0915\u0947 \u0932\u093F\u090F \u0939\u092E\u0947\u0936\u093E \u0905\u0928\u0941\u092E\u0924\u093F \u0932\u0940 \u091C\u093E\u0924\u0940 \u0939\u0948\u0964"
  },
  permissions: { en: "Permissions", hi: "\u0905\u0928\u0941\u092E\u0924\u093F\u092F\u093E\u0901" },
  permChat: { en: "Chat", hi: "\u091A\u0948\u091F" },
  permRead: { en: "Read", hi: "\u092A\u0922\u093C\u0947\u0902" },
  permWrite: { en: "Write", hi: "\u0932\u093F\u0916\u0947\u0902" },
  permAct: { en: "Act", hi: "\u0915\u093E\u0930\u094D\u092F" },
  grantAskEveryTime: { en: "Ask every time", hi: "\u0939\u0930 \u092C\u093E\u0930 \u092A\u0942\u091B\u0947\u0902" },
  grantAllowOnce: { en: "Allow once", hi: "\u090F\u0915 \u092C\u093E\u0930 \u0905\u0928\u0941\u092E\u0924\u093F" },
  grantAllowSession: { en: "Allow this session", hi: "\u0907\u0938 \u0938\u0924\u094D\u0930 \u0915\u0947 \u0932\u093F\u090F" },
  grantAllowTrustedProject: { en: "Allow in trusted project", hi: "\u092D\u0930\u094B\u0938\u0947\u092E\u0902\u0926 \u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u092E\u0947\u0902" },
  serverDecides: {
    en: "The server re-evaluates every request. This strip only requests a grant.",
    hi: "\u0938\u0930\u094D\u0935\u0930 \u0939\u0930 \u0905\u0928\u0941\u0930\u094B\u0927 \u0915\u0940 \u0926\u094B\u092C\u093E\u0930\u093E \u091C\u093E\u0901\u091A \u0915\u0930\u0924\u093E \u0939\u0948\u0964 \u092F\u0939 \u092A\u091F\u094D\u091F\u0940 \u0915\u0947\u0935\u0932 \u0905\u0928\u0941\u092E\u0924\u093F \u092E\u093E\u0901\u0917\u0924\u0940 \u0939\u0948\u0964"
  },
  serverAuthorized: { en: "Server authorized", hi: "\u0938\u0930\u094D\u0935\u0930 \u0926\u094D\u0935\u093E\u0930\u093E \u0938\u094D\u0935\u0940\u0915\u0943\u0924" },
  unavailable: { en: "unavailable", hi: "\u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902" },
  grantNeedsApprovalFlow: {
    en: "Session and trusted-project grants are issued only by the canonical approval flow.",
    hi: "\u0938\u0924\u094D\u0930 \u0914\u0930 \u092D\u0930\u094B\u0938\u0947\u092E\u0902\u0926-\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u0905\u0928\u0941\u092E\u0924\u093F \u0915\u0947\u0935\u0932 canonical approval flow \u0938\u0947 \u091C\u093E\u0930\u0940 \u0939\u094B\u0924\u0940 \u0939\u0948\u0964"
  },
  designAiUnsupported: {
    en: "AI-assisted Design uses the canonical bounded planner when a configured model route is available; deterministic rendering remains available independently.",
    hi: "Configured model route \u0909\u092A\u0932\u092C\u094D\u0927 \u0939\u094B\u0928\u0947 \u092A\u0930 AI-assisted Design canonical bounded planner \u0909\u092A\u092F\u094B\u0917 \u0915\u0930\u0924\u093E \u0939\u0948; deterministic rendering \u0938\u094D\u0935\u0924\u0902\u0924\u094D\u0930 \u0930\u0942\u092A \u0938\u0947 \u0909\u092A\u0932\u092C\u094D\u0927 \u0930\u0939\u0924\u0940 \u0939\u0948\u0964"
  },
  designAiAssisted: {
    en: "AI creates a bounded design plan; Smart Shikka validates and renders the real artifact.",
    hi: "AI \u0938\u0940\u092E\u093F\u0924 \u0921\u093F\u091C\u093C\u093E\u0907\u0928 \u092F\u094B\u091C\u0928\u093E \u092C\u0928\u093E\u0924\u093E \u0939\u0948; Smart Shikka \u0909\u0938\u0947 \u091C\u093E\u0901\u091A\u0915\u0930 \u0935\u093E\u0938\u094D\u0924\u0935\u093F\u0915 \u0906\u0930\u094D\u091F\u093F\u092B\u093C\u0948\u0915\u094D\u091F render \u0915\u0930\u0924\u093E \u0939\u0948\u0964"
  },
  imageGenerationNotConfigured: {
    en: "Image generation is not configured; text and vector layout were rendered without a fake image.",
    hi: "\u0907\u092E\u0947\u091C \u091C\u0928\u0930\u0947\u0936\u0928 configured \u0928\u0939\u0940\u0902 \u0939\u0948; \u0928\u0915\u0932\u0940 \u0907\u092E\u0947\u091C \u0915\u0947 \u092C\u093F\u0928\u093E text \u0914\u0930 vector layout render \u0939\u0941\u0906 \u0939\u0948\u0964"
  },
  designExportPngOnly: { en: "Export: PNG", hi: "Export: PNG" },
  attachmentNeedsCeoRoute: {
    en: "Attachments are processed by the Smart Shikka turn route, which this account cannot use.",
    hi: "Attachment \u0915\u0947\u0935\u0932 Smart Shikka turn route \u0938\u0947 process \u0939\u094B\u0924\u093E \u0939\u0948, \u091C\u094B \u0907\u0938 account \u0915\u0947 \u0932\u093F\u090F \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964"
  },
  serverRejected: { en: "not granted", hi: "\u0905\u0938\u094D\u0935\u0940\u0915\u0943\u0924" },
  approvalNeeded: { en: "Approval needed", hi: "\u0905\u0928\u0941\u092E\u0924\u093F \u0906\u0935\u0936\u094D\u092F\u0915" },
  profile: { en: "Profile", hi: "\u092A\u094D\u0930\u094B\u092B\u093C\u093E\u0907\u0932" },
  creativity: { en: "Creativity", hi: "\u0930\u091A\u0928\u093E\u0924\u094D\u092E\u0915\u0924\u093E" },
  preset: { en: "Preset", hi: "\u092A\u094D\u0930\u0940\u0938\u0947\u091F" },
  account: { en: "Account", hi: "\u0916\u093E\u0924\u093E" },
  settings: { en: "Settings", hi: "\u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938" },
  appearance: { en: "Appearance", hi: "\u0930\u0942\u092A-\u0930\u0902\u0917" },
  language: { en: "Language", hi: "\u092D\u093E\u0937\u093E" },
  skills: { en: "Skills", hi: "\u0938\u094D\u0915\u093F\u0932\u094D\u0938" },
  connections: { en: "Connections", hi: "\u0915\u0928\u0947\u0915\u094D\u0936\u0928" },
  integrations: { en: "Apps / Integrations", hi: "\u0910\u092A\u094D\u0938 / \u0907\u0902\u091F\u0940\u0917\u094D\u0930\u0947\u0936\u0928" },
  permissionsTrust: { en: "Permissions / Trust", hi: "\u0905\u0928\u0941\u092E\u0924\u093F / \u092D\u0930\u094B\u0938\u093E" },
  usage: { en: "Usage", hi: "\u0909\u092A\u092F\u094B\u0917" },
  support: { en: "Support", hi: "\u0938\u0939\u093E\u092F\u0924\u093E" },
  logout: { en: "Logout", hi: "\u0932\u0949\u0917\u0906\u0909\u091F" },
  ceoAssistant: { en: "Smart Shikka CEO", hi: "Smart Shikka CEO" },
  adminOrganization: { en: "Admin / Organization", hi: "\u090F\u0921\u092E\u093F\u0928 / \u0938\u0902\u0917\u0920\u0928" },
  systemIntegrations: { en: "System / Integrations", hi: "\u0938\u093F\u0938\u094D\u091F\u092E / \u0907\u0902\u091F\u0940\u0917\u094D\u0930\u0947\u0936\u0928" },
  emergencyStop: { en: "Emergency Stop", hi: "\u0906\u092A\u093E\u0924\u0915\u093E\u0932\u0940\u0928 \u0930\u094B\u0915" },
  privilegedSection: { en: "Privileged", hi: "\u0935\u093F\u0936\u0947\u0937\u093E\u0927\u093F\u0915\u093E\u0930" },
  contextPane: { en: "Context", hi: "\u0938\u0902\u0926\u0930\u094D\u092D" },
  contextEmpty: {
    en: "No real context available yet.",
    hi: "\u0905\u092D\u0940 \u0915\u094B\u0908 \u0935\u093E\u0938\u094D\u0924\u0935\u093F\u0915 \u0938\u0902\u0926\u0930\u094D\u092D \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964"
  },
  ctxBrowser: { en: "Browser", hi: "\u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930" },
  ctxActivity: { en: "Activity", hi: "\u0915\u093E\u0930\u094D\u092F" },
  ctxControls: { en: "Controls", hi: "\u0915\u0902\u091F\u094D\u0930\u094B\u0932" },
  ctxCaptures: { en: "Captures", hi: "\u0915\u0948\u092A\u094D\u091A\u0930" },
  ctxAgents: { en: "Agents", hi: "\u090F\u091C\u0947\u0902\u091F" },
  ctxScreenshot: { en: "Screenshot", hi: "\u0938\u094D\u0915\u094D\u0930\u0940\u0928\u0936\u0949\u091F" },
  ctxVideo: { en: "Video", hi: "\u0935\u0940\u0921\u093F\u092F\u094B" },
  ctxWorkingTree: { en: "Working tree", hi: "\u0935\u0930\u094D\u0915\u093F\u0902\u0917 \u091F\u094D\u0930\u0940" },
  ctxFiles: { en: "Files", hi: "\u092B\u093C\u093E\u0907\u0932\u0947\u0902" },
  ctxDiff: { en: "Diff", hi: "\u0921\u093F\u092B\u093C" },
  ctxArtifacts: { en: "Artifacts", hi: "\u0906\u0930\u094D\u091F\u093F\u092B\u093C\u0948\u0915\u094D\u091F" },
  ctxApprovals: { en: "Approvals", hi: "\u0905\u0928\u0941\u092E\u0924\u093F\u092F\u093E\u0901" },
  ctxTaskProgress: { en: "Task progress", hi: "\u0915\u093E\u0930\u094D\u092F \u092A\u094D\u0930\u0917\u0924\u093F" },
  ctxTerminal: { en: "Terminal", hi: "\u091F\u0930\u094D\u092E\u093F\u0928\u0932" },
  closePane: { en: "Close", hi: "\u092C\u0902\u0926 \u0915\u0930\u0947\u0902" },
  statusReady: { en: "Ready", hi: "\u0924\u0948\u092F\u093E\u0930" },
  statusError: { en: "Connection issue", hi: "\u0915\u0928\u0947\u0915\u094D\u0936\u0928 \u0938\u092E\u0938\u094D\u092F\u093E" },
  coverPresetTitle: { en: "Cover preset", hi: "\u0915\u0935\u0930 \u092A\u094D\u0930\u0940\u0938\u0947\u091F" },
  designExport: { en: "Export", hi: "\u0928\u093F\u0930\u094D\u092F\u093E\u0924" },
  designCreate: { en: "Create", hi: "\u092C\u0928\u093E\u090F\u0901" },
  designUnavailable: {
    en: "Design service is not reachable right now.",
    hi: "\u0921\u093F\u091C\u093C\u093E\u0907\u0928 \u0938\u0947\u0935\u093E \u0905\u092D\u0940 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964"
  },
  unsupportedSurface: {
    en: "This surface has no canonical backend yet.",
    hi: "\u0907\u0938 \u0938\u0930\u094D\u092B\u093C\u0947\u0938 \u0915\u093E \u0915\u0948\u0928\u0949\u0928\u093F\u0915\u0932 \u092C\u0948\u0915\u090F\u0902\u0921 \u0905\u092D\u0940 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964"
  },
  codePlanReady: { en: "Coding plan", hi: "\u0915\u094B\u0921\u093F\u0902\u0917 \u092F\u094B\u091C\u0928\u093E" },
  codeNoChanges: {
    en: "The canonical coding runtime returned no file changes.",
    hi: "\u0915\u0948\u0928\u0949\u0928\u093F\u0915\u0932 \u0915\u094B\u0921\u093F\u0902\u0917 \u0930\u0928\u091F\u093E\u0907\u092E \u0928\u0947 \u0915\u094B\u0908 \u092B\u093C\u093E\u0907\u0932 \u092A\u0930\u093F\u0935\u0930\u094D\u0924\u0928 \u0928\u0939\u0940\u0902 \u0926\u093F\u092F\u093E\u0964"
  },
  codeProjectRequired: {
    en: "Select a project to run canonical coding tasks.",
    hi: "\u0915\u0948\u0928\u0949\u0928\u093F\u0915\u0932 \u0915\u094B\u0921\u093F\u0902\u0917 \u0915\u093E\u0930\u094D\u092F \u091A\u0932\u093E\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u091A\u0941\u0928\u0947\u0902\u0964"
  },
  // PR-CORE-11 — surfaces + rich chat renderer + activity ledger
  navCode: { en: "Code", hi: "\u0915\u094B\u0921" },
  navDesign: { en: "Design", hi: "\u0921\u093F\u091C\u093C\u093E\u0907\u0928" },
  navActivity: { en: "Activity", hi: "\u0915\u093E\u0930\u094D\u092F\u0915\u094D\u0930\u092E" },
  citations: { en: "Sources", hi: "\u0938\u094D\u0930\u094B\u0924" },
  toolActions: { en: "Tool actions", hi: "\u091F\u0942\u0932 \u0915\u094D\u0930\u093F\u092F\u093E\u090F\u0901" },
  approvalPendingCard: { en: "Approval pending", hi: "\u0905\u0928\u0941\u092E\u0924\u093F \u0932\u0902\u092C\u093F\u0924" },
  approvalCard: { en: "Approval", hi: "\u0905\u0928\u0941\u092E\u0924\u093F" },
  approvalApprove: { en: "Approve", hi: "\u0938\u094D\u0935\u0940\u0915\u0943\u0924 \u0915\u0930\u0947\u0902" },
  approvalDeny: { en: "Deny", hi: "\u0905\u0938\u094D\u0935\u0940\u0915\u093E\u0930 \u0915\u0930\u0947\u0902" },
  approvalApplying: { en: "Applying decision\u2026", hi: "\u0928\u093F\u0930\u094D\u0923\u092F \u0932\u093E\u0917\u0942 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026" },
  approvalApprovedDone: { en: "Approved", hi: "\u0938\u094D\u0935\u0940\u0915\u0943\u0924" },
  approvalDeniedDone: { en: "Denied \u2014 the action will not run.", hi: "\u0905\u0938\u094D\u0935\u0940\u0915\u0943\u0924 \u2014 \u0915\u094D\u0930\u093F\u092F\u093E \u0928\u0939\u0940\u0902 \u091A\u0932\u0947\u0917\u0940\u0964" },
  approvalExpiredCard: { en: "Approval expired \u2014 send the request again.", hi: "\u0905\u0928\u0941\u092E\u0924\u093F \u0938\u092E\u093E\u092A\u094D\u0924 \u0939\u094B \u0917\u0908 \u2014 \u0905\u0928\u0941\u0930\u094B\u0927 \u0926\u094B\u092C\u093E\u0930\u093E \u092D\u0947\u091C\u0947\u0902\u0964" },
  approvalAlreadyUsed: { en: "This approval was already used.", hi: "\u092F\u0939 \u0905\u0928\u0941\u092E\u0924\u093F \u092A\u0939\u0932\u0947 \u0939\u0940 \u0909\u092A\u092F\u094B\u0917 \u0939\u094B \u091A\u0941\u0915\u0940 \u0939\u0948\u0964" },
  approvalApplyFailed: { en: "Could not apply the decision.", hi: "\u0928\u093F\u0930\u094D\u0923\u092F \u0932\u093E\u0917\u0942 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u093E\u0964" },
  approvalExpiryPrefix: { en: "Expires", hi: "\u0938\u092E\u093E\u092A\u094D\u0924\u093F" },
  artifact: { en: "Artifact", hi: "\u0906\u0930\u094D\u091F\u093F\u092B\u093C\u0948\u0915\u094D\u091F" },
  codeDiff: { en: "Code diff", hi: "\u0915\u094B\u0921 \u0921\u093F\u092B\u093C" },
  traceSummary: { en: "Turn trace", hi: "\u091F\u0930\u094D\u0928 \u091F\u094D\u0930\u0947\u0938" },
  retry: { en: "Retry", hi: "\u092A\u0941\u0928\u0903 \u092A\u094D\u0930\u092F\u093E\u0938" },
  turnFailed: { en: "This turn failed", hi: "\u092F\u0939 \u091F\u0930\u094D\u0928 \u0935\u093F\u092B\u0932 \u0930\u0939\u093E" },
  loading: { en: "Loading\u2026", hi: "\u0932\u094B\u0921 \u0939\u094B \u0930\u0939\u093E \u0939\u0948\u2026" },
  emptyList: { en: "Nothing here yet.", hi: "\u092F\u0939\u093E\u0901 \u0905\u092D\u0940 \u0915\u0941\u091B \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" },
  loadFailed: { en: "Could not load. Please retry.", hi: "\u0932\u094B\u0921 \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u093E\u0964 \u0915\u0943\u092A\u092F\u093E \u092A\u0941\u0928\u0903 \u092A\u094D\u0930\u092F\u093E\u0938 \u0915\u0930\u0947\u0902\u0964" },
  surfacePreview: { en: "Preview", hi: "\u092A\u0942\u0930\u094D\u0935\u093E\u0935\u0932\u094B\u0915\u0928" },
  surfaceUnavailable: { en: "Not available for this account.", hi: "\u092F\u0939 \u0907\u0938 \u0916\u093E\u0924\u0947 \u0915\u0947 \u0932\u093F\u090F \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" },
  openInChat: { en: "Open in Chat", hi: "\u091A\u0948\u091F \u092E\u0947\u0902 \u0916\u094B\u0932\u0947\u0902" },
  actAssistantTurn: { en: "Assistant turn", hi: "\u0905\u0938\u093F\u0938\u094D\u091F\u0947\u0902\u091F \u091F\u0930\u094D\u0928" },
  actToolAction: { en: "Tool action", hi: "\u091F\u0942\u0932 \u0915\u094D\u0930\u093F\u092F\u093E" },
  actApproval: { en: "Approval", hi: "\u0905\u0928\u0941\u092E\u0924\u093F" },
  actRun: { en: "Run", hi: "\u0930\u0928" },
  actArtifact: { en: "Artifact", hi: "\u0906\u0930\u094D\u091F\u093F\u092B\u093C\u0948\u0915\u094D\u091F" },
  actUsage: { en: "Usage", hi: "\u0909\u092A\u092F\u094B\u0917" },
  actError: { en: "Error", hi: "\u0924\u094D\u0930\u0941\u091F\u093F" },
  ledgerSource: { en: "Canonical ledger sources", hi: "\u0915\u0948\u0928\u0949\u0928\u093F\u0915\u0932 \u0932\u0947\u091C\u0930 \u0938\u094D\u0930\u094B\u0924" }
};
function productText(key, locale) {
  return STRINGS8[key][locale === "hi" ? "hi" : "en"];
}
function permissionCategoryLabel(id, locale) {
  const map = {
    chat: "permChat",
    read: "permRead",
    write: "permWrite",
    act: "permAct"
  };
  return productText(map[id], locale);
}
function grantRequestLabel(id, locale) {
  const map = {
    ask_every_time: "grantAskEveryTime",
    allow_once: "grantAllowOnce",
    allow_session: "grantAllowSession",
    allow_trusted_project: "grantAllowTrustedProject"
  };
  return productText(map[id], locale);
}

// src/product/productDesignService.ts
var ERROR_CODE_RE = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;
function safeCode(raw, fallback) {
  return typeof raw === "string" && ERROR_CODE_RE.test(raw) ? raw : fallback;
}
function headers(config) {
  return {
    "Content-Type": "application/json",
    ...config.token ? { Authorization: `Bearer ${config.token}` } : {}
  };
}
async function postJson(config, path, body) {
  let response;
  try {
    response = await (config.fetchImpl ?? globalThis.fetch)(`${config.baseUrl}${path}`, {
      method: "POST",
      headers: headers(config),
      credentials: "same-origin",
      body: JSON.stringify(body)
    });
  } catch {
    return { ok: false, errorCode: "network_error", message: "design_service_unreachable" };
  }
  let json = {};
  try {
    json = await response.json();
  } catch {
    return { ok: false, errorCode: "invalid_response", message: "design_service_invalid_response" };
  }
  if (!response.ok || json.success !== true) {
    return {
      ok: false,
      errorCode: safeCode(json.errorCode, `http_${response.status}`),
      message: "design_service_error"
    };
  }
  return { ok: true, data: json };
}
function createProductDesignService(config) {
  function parseGeneratedResult(data, fallbackFormat) {
    const project = data.project;
    const manifest = data.manifest;
    const imageGeneration = data.imageGeneration;
    const modelEvidence = data.modelEvidence;
    if (!project || typeof project.projectId !== "string" || !manifest || typeof manifest.artifact !== "string" || manifest.artifact.length === 0 || typeof manifest.mimeType !== "string" || typeof manifest.bytes !== "number" || manifest.bytes <= 0 || !imageGeneration || imageGeneration.status !== "not_requested" && imageGeneration.status !== "not_configured" || !modelEvidence || typeof modelEvidence.requestedProfile !== "string" || typeof modelEvidence.effectiveProfile !== "string" || typeof modelEvidence.providerId !== "string" || typeof modelEvidence.modelId !== "string") {
      return { ok: false, errorCode: "artifact_unavailable", message: "design_artifact_unavailable" };
    }
    const format = manifest.format === "jpg" || manifest.format === "pdf" || manifest.format === "png" ? manifest.format : fallbackFormat;
    return {
      ok: true,
      data: {
        projectId: project.projectId,
        project: {
          projectId: project.projectId,
          name: String(project.name ?? "Design"),
          width: Number(project.width ?? 0),
          height: Number(project.height ?? 0),
          unit: String(project.unit ?? "px"),
          dpi: Number(project.dpi ?? 72),
          version: Number(project.version ?? 1)
        },
        format,
        mimeType: manifest.mimeType,
        artifact: manifest.artifact,
        bytes: manifest.bytes,
        imageGeneration: {
          status: imageGeneration.status,
          errorCode: typeof imageGeneration.errorCode === "string" ? safeCode(imageGeneration.errorCode, "image_generation_failed") : void 0,
          requestedSlots: Number(imageGeneration.requestedSlots ?? 0)
        },
        modelEvidence: {
          requestedProfile: modelEvidence.requestedProfile,
          effectiveProfile: modelEvidence.effectiveProfile,
          providerId: modelEvidence.providerId,
          modelId: modelEvidence.modelId,
          fallbackUsed: modelEvidence.fallbackUsed === true
        }
      }
    };
  }
  return {
    async generateDesign(request) {
      const result = await postJson(config, "/api/design/generate", {
        prompt: request.prompt,
        name: request.name,
        projectScope: request.projectScope,
        canvas: request.canvas,
        ...request.conversationId !== void 0 ? { conversationId: request.conversationId } : {},
        ...request.idempotencyKey !== void 0 ? { idempotencyKey: request.idempotencyKey } : {}
      });
      return result.ok ? parseGeneratedResult(result.data, "png") : result;
    },
    async editDesign(request) {
      const format = request.format ?? "png";
      const result = await postJson(
        config,
        `/api/design/projects/${encodeURIComponent(request.projectId)}/generate-edit`,
        {
          prompt: request.prompt,
          format,
          ...request.conversationId !== void 0 ? { conversationId: request.conversationId } : {},
          ...request.idempotencyKey !== void 0 ? { idempotencyKey: request.idempotencyKey } : {}
        }
      );
      if (!result.ok) return result;
      const enriched = {
        ...result.data,
        imageGeneration: {
          status: "not_requested",
          requestedSlots: 0
        }
      };
      return parseGeneratedResult(enriched, format);
    },
    async createProject(request) {
      const result = await postJson(config, "/api/design/projects", {
        name: request.name,
        width: request.width,
        height: request.height,
        unit: request.unit,
        dpi: request.dpi,
        orientation: request.orientation,
        layers: [
          {
            layerId: "background",
            type: "background",
            name: "Background",
            x: 0,
            y: 0,
            width: request.width,
            height: request.height,
            content: { color: request.backgroundColor ?? "#fdf6e9" }
          },
          {
            layerId: "headline",
            type: "text",
            name: "Headline",
            x: Math.round(request.width * 0.08),
            y: Math.round(request.height * 0.38),
            width: Math.round(request.width * 0.84),
            height: Math.round(request.height * 0.24),
            content: {
              text: request.headline,
              color: request.textColor ?? "#1f2023",
              align: "center",
              fontSize: Math.max(12, Math.round(Math.min(request.width, request.height) * 0.09))
            }
          }
        ]
      });
      if (!result.ok) return result;
      const project = result.data.project;
      if (!project || typeof project.projectId !== "string") {
        return { ok: false, errorCode: "invalid_response", message: "design_project_missing" };
      }
      return {
        ok: true,
        data: {
          projectId: project.projectId,
          name: String(project.name ?? request.name),
          width: Number(project.width ?? request.width),
          height: Number(project.height ?? request.height),
          unit: String(project.unit ?? request.unit),
          dpi: Number(project.dpi ?? request.dpi),
          version: Number(project.version ?? 1)
        }
      };
    },
    async exportProject(projectId, format) {
      const result = await postJson(config, `/api/design/projects/${encodeURIComponent(projectId)}/export`, { format });
      if (!result.ok) return result;
      const manifest = result.data.manifest;
      if (!manifest || typeof manifest.artifact !== "string") {
        return { ok: false, errorCode: "artifact_unavailable", message: "design_artifact_unavailable" };
      }
      return {
        ok: true,
        data: {
          projectId,
          format,
          mimeType: String(manifest.mimeType ?? "application/octet-stream"),
          artifact: manifest.artifact,
          bytes: Number(manifest.bytes ?? 0)
        }
      };
    }
  };
}

// src/product/productModeRuntime.ts
function resolveProductModeRuntime(mode) {
  if (mode === "code") return "coding_workflow";
  if (mode === "design" || mode === "cover") return "design_studio";
  return "assistant_ask";
}

// src/product/ModeSelector.tsx
import {
  SMART_SHIKKA_EFFORT_LEVELS,
  SMART_SHIKKA_LOGICAL_PROFILES,
  SMART_SHIKKA_PRODUCT_MODES,
  getSmartShikkaProductMode
} from "@smart-shikka/core/profiles";
import { jsx as jsx84, jsxs as jsxs76 } from "react/jsx-runtime";
var COVER_PRESETS = [
  { id: "youtube-thumbnail", label: { en: "YouTube thumbnail", hi: "YouTube \u0925\u0902\u092C\u0928\u0947\u0932" }, width: 1280, height: 720, unit: "px", dpi: 72 },
  { id: "instagram-post", label: { en: "Instagram post", hi: "Instagram \u092A\u094B\u0938\u094D\u091F" }, width: 1080, height: 1080, unit: "px", dpi: 72 },
  { id: "x-banner", label: { en: "X banner", hi: "X \u092C\u0948\u0928\u0930" }, width: 1500, height: 500, unit: "px", dpi: 72 },
  { id: "a4-poster", label: { en: "A4 poster", hi: "A4 \u092A\u094B\u0938\u094D\u091F\u0930" }, width: 210, height: 297, unit: "mm", dpi: 300 }
];
function ModeSelector(props) {
  const C = useBrandColors();
  const { locale } = props;
  const controls = getSmartShikkaProductMode(props.mode)?.selectorControls ?? ["profile"];
  const selectStyle2 = {
    width: "auto",
    minWidth: 0,
    fontSize: 11,
    padding: "4px 8px",
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    background: C.card,
    color: C.fg,
    cursor: "pointer",
    fontFamily: C.fontSans,
    flexShrink: 0
  };
  return /* @__PURE__ */ jsxs76(
    "div",
    {
      className: "ss-mode-selector",
      style: { display: "flex", alignItems: "center", gap: 5, flexWrap: "nowrap", width: "100%", minWidth: 0 },
      children: [
        controls.includes("creativity") && /* @__PURE__ */ jsxs76(
          "label",
          {
            className: "ss-mode-creativity",
            title: productText("creativity", locale),
            style: { display: "flex", alignItems: "center", gap: 3, width: 104, minWidth: 104, fontSize: 10, color: C.muted, flex: "0 0 104px", margin: 0, whiteSpace: "nowrap" },
            children: [
              /* @__PURE__ */ jsx84("span", { children: locale === "hi" ? "\u0930\u091A\u0928\u093E" : "Creative" }),
              /* @__PURE__ */ jsx84(
                "input",
                {
                  "aria-label": productText("creativity", locale),
                  type: "range",
                  min: 0,
                  max: 2,
                  step: 0.1,
                  value: props.creativity,
                  onChange: (e) => props.onCreativityChange(Number(e.target.value)),
                  style: { cursor: "pointer", width: 34, minWidth: 34, padding: 0, flex: "0 0 34px" }
                }
              ),
              /* @__PURE__ */ jsx84("span", { style: { minWidth: 18, color: C.fg }, children: props.creativity.toFixed(1) })
            ]
          }
        ),
        /* @__PURE__ */ jsx84(
          "select",
          {
            "aria-label": "mode",
            value: props.mode,
            onChange: (e) => props.onModeChange(e.target.value),
            className: "ss-mode-picker",
            style: { ...selectStyle2, width: 76, fontWeight: 600 },
            children: SMART_SHIKKA_PRODUCT_MODES.map((m) => /* @__PURE__ */ jsx84("option", { value: m.id, children: m.id === "design" ? locale === "hi" ? "\u0921\u093F\u091C\u093C\u093E\u0907\u0928" : "Design" : locale === "hi" ? m.label.hi : m.label.en }, m.id))
          }
        ),
        controls.includes("profile") && /* @__PURE__ */ jsx84(
          "select",
          {
            "aria-label": "profile",
            value: props.logicalProfile,
            onChange: (e) => props.onLogicalProfileChange(e.target.value),
            title: locale === "hi" ? "Smart Shikka \u092E\u0949\u0921\u0932 \u092A\u094D\u0930\u094B\u092B\u093C\u093E\u0907\u0932" : "Smart Shikka model profile",
            className: "ss-profile-picker",
            style: { ...selectStyle2, width: 124 },
            children: SMART_SHIKKA_LOGICAL_PROFILES.map((p) => /* @__PURE__ */ jsx84("option", { value: p.id, children: locale === "hi" ? p.label.hi : p.label.en }, p.id))
          }
        ),
        controls.includes("effort") && /* @__PURE__ */ jsx84(
          "select",
          {
            "aria-label": "effort",
            value: props.effort,
            onChange: (e) => props.onEffortChange(e.target.value),
            style: { ...selectStyle2, width: 82 },
            children: SMART_SHIKKA_EFFORT_LEVELS.map((level) => /* @__PURE__ */ jsx84("option", { value: level.id, children: locale === "hi" ? level.label.hi : level.label.en }, level.id))
          }
        ),
        controls.includes("preset") && /* @__PURE__ */ jsx84(
          "select",
          {
            "aria-label": "cover-preset",
            value: props.coverPresetId,
            onChange: (e) => props.onCoverPresetChange(e.target.value),
            style: selectStyle2,
            children: COVER_PRESETS.map((preset) => /* @__PURE__ */ jsx84("option", { value: preset.id, children: locale === "hi" ? preset.label.hi : preset.label.en }, preset.id))
          }
        )
      ]
    }
  );
}

// src/product/PermissionStrip.tsx
import { useEffect as useEffect38, useRef as useRef19, useState as useState45 } from "react";
import { jsx as jsx85, jsxs as jsxs77 } from "react/jsx-runtime";
var PERMISSION_CATEGORY_SCOPES = {
  chat: ["assistant:preview"],
  read: ["file:read"],
  write: ["file:write"],
  act: ["tool:execute"]
};
var GRANT_REQUEST_OPTIONS = [
  "ask_every_time",
  "allow_once",
  "allow_session",
  "allow_trusted_project"
];
var UNSUPPORTED_GRANT_REQUEST_OPTIONS = [
  "allow_session",
  "allow_trusted_project"
];
function createDefaultPermissionCategories() {
  return [
    { id: "chat", requested: "allow_once", scopes: PERMISSION_CATEGORY_SCOPES.chat },
    { id: "read", requested: "allow_once", scopes: PERMISSION_CATEGORY_SCOPES.read },
    { id: "write", requested: "ask_every_time", scopes: PERMISSION_CATEGORY_SCOPES.write },
    { id: "act", requested: "ask_every_time", scopes: PERMISSION_CATEGORY_SCOPES.act }
  ];
}
function consumeOncePermissionCategories(categories) {
  return categories.map(
    (category) => category.requested === "allow_once" ? { ...category, requested: "ask_every_time" } : { ...category }
  );
}
function PermissionStrip(props) {
  const C = useBrandColors();
  const { locale, serverState } = props;
  const [openCategory, setOpenCategory] = useState45(null);
  const rootRef = useRef19(null);
  useEffect38(() => {
    if (openCategory === null) return void 0;
    const onDocumentClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpenCategory(null);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [openCategory]);
  const pillStyle = (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 10.5,
    padding: "3px 8px",
    borderRadius: 999,
    border: `1px solid ${active ? C.primary : C.border}`,
    background: active ? C.chip : "transparent",
    color: active ? C.fg : C.muted,
    cursor: "pointer",
    fontFamily: C.fontSans
  });
  return /* @__PURE__ */ jsxs77("div", { ref: rootRef, style: { position: "relative", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }, children: [
    /* @__PURE__ */ jsx85("span", { style: { fontSize: 10.5, color: C.muted }, children: productText("permissions", locale) }),
    props.categories.map((category) => {
      const isOpen = openCategory === category.id;
      return /* @__PURE__ */ jsxs77("span", { style: { position: "relative" }, children: [
        /* @__PURE__ */ jsxs77(
          "button",
          {
            type: "button",
            "aria-expanded": isOpen,
            onClick: () => setOpenCategory(isOpen ? null : category.id),
            style: pillStyle(category.requested !== "ask_every_time"),
            children: [
              permissionCategoryLabel(category.id, locale),
              /* @__PURE__ */ jsxs77("span", { style: { opacity: 0.75 }, children: [
                "\xB7 ",
                grantRequestLabel(category.requested, locale)
              ] })
            ]
          }
        ),
        isOpen && /* @__PURE__ */ jsxs77(
          "div",
          {
            role: "dialog",
            style: {
              position: "absolute",
              bottom: "calc(100% + 6px)",
              left: 0,
              zIndex: 40,
              minWidth: 236,
              padding: 8,
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              background: C.card,
              boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
              fontFamily: C.fontSans
            },
            children: [
              GRANT_REQUEST_OPTIONS.map((option) => {
                const unsupported = UNSUPPORTED_GRANT_REQUEST_OPTIONS.includes(option);
                return /* @__PURE__ */ jsxs77(
                  "button",
                  {
                    type: "button",
                    disabled: unsupported,
                    title: unsupported ? productText("grantNeedsApprovalFlow", locale) : void 0,
                    onClick: () => {
                      if (unsupported) return;
                      props.onCategoryChange(category.id, option);
                      setOpenCategory(null);
                    },
                    style: {
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      fontSize: 12,
                      padding: "6px 8px",
                      borderRadius: 8,
                      border: "none",
                      background: option === category.requested ? C.chip : "transparent",
                      color: unsupported ? C.muted : C.fg,
                      cursor: unsupported ? "not-allowed" : "pointer"
                    },
                    children: [
                      grantRequestLabel(option, locale),
                      unsupported ? ` \xB7 ${productText("unavailable", locale)}` : ""
                    ]
                  },
                  option
                );
              }),
              /* @__PURE__ */ jsx85("p", { style: { margin: "6px 4px 2px", fontSize: 10.5, lineHeight: 1.5, color: C.muted }, children: productText("serverDecides", locale) }),
              /* @__PURE__ */ jsx85("p", { style: { margin: "2px 4px", fontSize: 10.5, lineHeight: 1.5, color: C.muted }, children: productText("grantNeedsApprovalFlow", locale) })
            ]
          }
        )
      ] }, category.id);
    }),
    props.approvalPending && /* @__PURE__ */ jsx85("span", { style: { ...pillStyle(true), borderColor: C.warning, color: C.warning, cursor: "default" }, children: productText("approvalNeeded", locale) }),
    serverState && /* @__PURE__ */ jsxs77("span", { style: { fontSize: 10.5, color: C.muted }, children: [
      productText("serverAuthorized", locale),
      ": ",
      serverState.authorizedScopes.length,
      serverState.rejectedScopeCount > 0 ? ` \xB7 ${serverState.rejectedScopeCount} ${productText("serverRejected", locale)}` : ""
    ] })
  ] });
}

// src/product/ChatComposer.tsx
import { useCallback as useCallback30, useEffect as useEffect39, useRef as useRef20, useState as useState46 } from "react";
import { jsx as jsx86, jsxs as jsxs78 } from "react/jsx-runtime";
var MAX_COMPOSER_HEIGHT = 120;
function canSubmitComposer(state) {
  if (state.disabled === true || state.busy === true) return false;
  return state.value.trim().length > 0 || state.hasAttachment === true;
}
function shouldSubmitComposerKey(event) {
  return event.key === "Enter" && !event.shiftKey && event.ctrlKey !== true && event.metaKey !== true && event.altKey !== true && event.isComposing !== true;
}
function ChatComposer(props) {
  const C = useBrandColors();
  const { locale } = props;
  const textareaRef = useRef20(null);
  const fileRef = useRef20(null);
  const [menuOpen, setMenuOpen] = useState46(false);
  const adjustHeight = useCallback30(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_COMPOSER_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > MAX_COMPOSER_HEIGHT ? "auto" : "hidden";
  }, []);
  useEffect39(() => {
    adjustHeight();
  }, [adjustHeight, props.value]);
  const iconButton = (label, glyph, onClick, active = false) => /* @__PURE__ */ jsx86(
    "button",
    {
      type: "button",
      title: label,
      "aria-label": label,
      disabled: !onClick,
      onClick,
      style: {
        width: 30,
        height: 30,
        display: "grid",
        placeItems: "center",
        borderRadius: 999,
        border: `1px solid ${active ? C.primary : C.border}`,
        background: active ? C.chip : "transparent",
        color: onClick ? C.muted : C.border,
        cursor: onClick ? "pointer" : "not-allowed",
        fontSize: 14
      },
      children: glyph
    }
  );
  const onFilesPicked = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) props.onAttach?.(files);
    event.target.value = "";
  };
  const canSend = canSubmitComposer(props);
  return /* @__PURE__ */ jsxs78(
    "div",
    {
      onMouseDown: (e) => {
        const target = e.target;
        if (target.closest('button, input, select, textarea, a, [role="menuitem"], [role="option"], [contenteditable="true"]')) return;
        e.preventDefault();
        textareaRef.current?.focus();
      },
      style: { position: "relative", display: "flex", flexDirection: "column", gap: 6, fontFamily: C.fontSans },
      children: [
        menuOpen && /* @__PURE__ */ jsxs78(
          "div",
          {
            role: "menu",
            style: {
              position: "absolute",
              left: 0,
              bottom: "calc(100% + 8px)",
              zIndex: 60,
              width: 248,
              padding: 6,
              border: `1px solid ${C.border}`,
              borderRadius: 14,
              background: C.card,
              boxShadow: "0 14px 36px rgba(28,25,23,.16)"
            },
            children: [
              props.onAttach && /* @__PURE__ */ jsxs78(
                "button",
                {
                  type: "button",
                  role: "menuitem",
                  onClick: () => {
                    setMenuOpen(false);
                    fileRef.current?.click();
                  },
                  style: { width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", border: "none", borderRadius: 9, background: "transparent", color: C.fg, textAlign: "left" },
                  children: [
                    /* @__PURE__ */ jsx86("span", { "aria-hidden": "true", children: "\u21A5" }),
                    locale === "hi" ? "\u0905\u091F\u0948\u091A\u092E\u0947\u0902\u091F \u0905\u092A\u0932\u094B\u0921 \u0915\u0930\u0947\u0902" : "Upload attachment"
                  ]
                }
              ),
              props.menuItems?.map((item) => /* @__PURE__ */ jsxs78(
                "button",
                {
                  type: "button",
                  role: "menuitem",
                  onClick: () => {
                    setMenuOpen(false);
                    props.onMenuItemSelect?.(item.id);
                  },
                  style: { width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", border: "none", borderRadius: 9, background: "transparent", color: C.fg, textAlign: "left" },
                  children: [
                    /* @__PURE__ */ jsx86("span", { "aria-hidden": "true", children: item.icon }),
                    item.label
                  ]
                },
                item.id
              ))
            ]
          }
        ),
        props.permissionSlot,
        props.attachmentSlot,
        /* @__PURE__ */ jsxs78(
          "div",
          {
            style: {
              display: "flex",
              flexDirection: "column",
              gap: 7,
              padding: "11px 12px 10px",
              borderRadius: 20,
              border: `1px solid ${C.border}`,
              background: C.inputBg,
              boxShadow: "0 8px 30px rgba(28,25,23,.08)",
              cursor: "text"
            },
            children: [
              /* @__PURE__ */ jsx86("input", { ref: fileRef, type: "file", multiple: true, hidden: true, onChange: onFilesPicked }),
              /* @__PURE__ */ jsx86(
                "textarea",
                {
                  ref: textareaRef,
                  rows: 1,
                  value: props.value,
                  disabled: props.disabled,
                  placeholder: props.placeholder ?? productText("composerPlaceholder", locale),
                  title: locale === "hi" ? "Enter \u0938\u0947 \u092D\u0947\u091C\u0947\u0902 \u2022 Shift+Enter \u0938\u0947 \u0928\u0908 \u092A\u0902\u0915\u094D\u0924\u093F" : "Enter to send \u2022 Shift+Enter for a new line",
                  "aria-keyshortcuts": "Enter Shift+Enter Control+C Control+V Control+X",
                  onChange: (e) => props.onValueChange(e.target.value),
                  onKeyDown: (e) => {
                    if (shouldSubmitComposerKey(e)) {
                      e.preventDefault();
                      if (canSend) props.onSubmit();
                    }
                  },
                  style: {
                    width: "100%",
                    resize: "none",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    color: C.fg,
                    fontSize: 14,
                    lineHeight: 1.5,
                    padding: "5px 3px 7px",
                    boxSizing: "border-box",
                    // Host apps style bare `textarea` with a tall min-height; the composer
                    // owns its own height and must start at a single row.
                    minHeight: 0,
                    maxHeight: MAX_COMPOSER_HEIGHT,
                    fontFamily: C.fontSans
                  }
                }
              ),
              /* @__PURE__ */ jsxs78("div", { className: "ss-chat-composer-toolbar", style: { display: "flex", alignItems: "center", gap: 8 }, children: [
                /* @__PURE__ */ jsx86(
                  "button",
                  {
                    type: "button",
                    className: "ss-chat-composer-plus",
                    title: productText("attachFile", locale),
                    "aria-label": productText("attachFile", locale),
                    "aria-haspopup": "menu",
                    "aria-expanded": menuOpen,
                    onClick: () => setMenuOpen((open) => !open),
                    style: {
                      width: 30,
                      height: 30,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 999,
                      border: `1px solid ${menuOpen ? C.primary : C.border}`,
                      background: menuOpen ? C.chip : "transparent",
                      color: C.muted,
                      fontSize: 17,
                      flexShrink: 0
                    },
                    children: "+"
                  }
                ),
                /* @__PURE__ */ jsx86("div", { className: "ss-chat-composer-selector", style: { display: "flex", alignItems: "center", minWidth: 0, flex: 1 }, children: props.selectorSlot }),
                /* @__PURE__ */ jsxs78("div", { className: "ss-chat-composer-actions", style: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 7, flexShrink: 0 }, children: [
                  iconButton(
                    props.listening ? productText("listening", locale) : productText("voiceInput", locale),
                    "\u{1F3A4}",
                    props.onToggleVoice,
                    props.listening === true
                  ),
                  /* @__PURE__ */ jsx86(
                    "button",
                    {
                      type: "button",
                      "aria-label": productText("send", locale),
                      disabled: !canSend,
                      onClick: props.onSubmit,
                      style: {
                        width: 32,
                        height: 32,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: 999,
                        border: "none",
                        background: canSend ? C.primary : C.border,
                        color: canSend ? C.primaryFg : C.muted,
                        cursor: canSend ? "pointer" : "not-allowed",
                        fontSize: 14,
                        flexShrink: 0
                      },
                      children: props.busy ? "\u2026" : "\u2191"
                    }
                  )
                ] })
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsx86("p", { style: { margin: 0, textAlign: "center", fontSize: 10.5, color: C.muted }, children: productText("aiDisclaimer", locale) })
      ]
    }
  );
}

// src/product/ChatCanvas.tsx
import { useEffect as useEffect40, useRef as useRef21 } from "react";

// src/product/chatMessageRender.tsx
import { jsx as jsx87, jsxs as jsxs79 } from "react/jsx-runtime";
function renderInline(text2, keyPrefix) {
  const out = [];
  const pattern = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*)|(\[[^\]\n]+\]\((https?:\/\/[^\s)]+)\))/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = pattern.exec(text2)) !== null) {
    if (m.index > last) out.push(text2.slice(last, m.index));
    const key = `${keyPrefix}-${i++}`;
    if (m[1]) {
      out.push(
        /* @__PURE__ */ jsx87("code", { className: "ss-md-code", children: m[1].slice(1, -1) }, key)
      );
    } else if (m[2]) {
      out.push(/* @__PURE__ */ jsx87("strong", { children: m[2].slice(2, -2) }, key));
    } else if (m[3]) {
      out.push(/* @__PURE__ */ jsx87("em", { children: m[3].slice(1, -1) }, key));
    } else if (m[4]) {
      const label = m[4].slice(1, m[4].indexOf("]("));
      out.push(
        /* @__PURE__ */ jsx87("a", { href: m[5], target: "_blank", rel: "noreferrer noopener", className: "ss-md-link", children: label }, key)
      );
    }
    last = pattern.lastIndex;
  }
  if (last < text2.length) out.push(text2.slice(last));
  return out;
}
function renderChatMarkdown(content) {
  const blocks = [];
  const lines = content.split("\n");
  let i = 0;
  let blockIdx = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim();
      const buf2 = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        buf2.push(lines[i]);
        i++;
      }
      i++;
      blocks.push(
        /* @__PURE__ */ jsx87("pre", { className: "ss-md-pre", "data-lang": lang || void 0, children: /* @__PURE__ */ jsx87("code", { children: buf2.join("\n") }) }, `code-${blockIdx++}`)
      );
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const Tag2 = ["h4", "h5", "h6"][level - 1];
      blocks.push(
        /* @__PURE__ */ jsx87(Tag2, { className: "ss-md-heading", children: renderInline(heading[2], `h${blockIdx}`) }, `h-${blockIdx++}`)
      );
      i++;
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(/* @__PURE__ */ jsx87("li", { children: renderInline(lines[i].replace(/^[-*]\s+/, ""), `li${blockIdx}-${i}`) }, `li-${blockIdx}-${i}`));
        i++;
      }
      blocks.push(/* @__PURE__ */ jsx87("ul", { className: "ss-md-list", children: items }, `ul-${blockIdx++}`));
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(/* @__PURE__ */ jsx87("li", { children: renderInline(lines[i].replace(/^\d+\.\s+/, ""), `ol${blockIdx}-${i}`) }, `ol-${blockIdx}-${i}`));
        i++;
      }
      blocks.push(/* @__PURE__ */ jsx87("ol", { className: "ss-md-list", children: items }, `ol-${blockIdx++}`));
      continue;
    }
    if (line.trim() === "") {
      i++;
      continue;
    }
    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== "" && !lines[i].startsWith("```") && !/^(#{1,3})\s+/.test(lines[i]) && !/^[-*]\s+/.test(lines[i]) && !/^\d+\.\s+/.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push(
      /* @__PURE__ */ jsx87("p", { className: "ss-md-p", children: buf.map((l, li) => /* @__PURE__ */ jsxs79("span", { children: [
        li > 0 ? " " : null,
        renderInline(l, `p${blockIdx}-${li}`)
      ] }, li)) }, `p-${blockIdx++}`)
    );
  }
  return blocks;
}
var chipBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 9px",
  borderRadius: 999,
  fontSize: 11,
  lineHeight: 1.6
};
function CitationChips(props) {
  const C = useBrandColors();
  if (props.citations.length === 0) return null;
  return /* @__PURE__ */ jsxs79("div", { className: "ss-msg-citations", style: { display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }, children: [
    /* @__PURE__ */ jsxs79("span", { style: { fontSize: 11, color: C.muted, alignSelf: "center" }, children: [
      productText("citations", props.locale),
      ":"
    ] }),
    props.citations.map((c, idx) => /* @__PURE__ */ jsx87(
      "span",
      {
        style: { ...chipBase, border: `1px solid ${C.border}`, background: C.chip, color: C.fg },
        title: [c.sourceKind, c.appId].filter(Boolean).join(" \xB7 ") || void 0,
        children: c.label
      },
      `${c.label}-${idx}`
    ))
  ] });
}
function ToolActionCards(props) {
  const C = useBrandColors();
  if (props.actions.length === 0) return null;
  return /* @__PURE__ */ jsxs79("div", { className: "ss-msg-tools", style: { display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }, children: [
    /* @__PURE__ */ jsx87("span", { style: { fontSize: 11, color: C.muted }, children: productText("toolActions", props.locale) }),
    props.actions.map((a, idx) => /* @__PURE__ */ jsxs79(
      "div",
      {
        style: {
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          padding: "6px 10px",
          background: C.card,
          fontSize: 12,
          display: "flex",
          justifyContent: "space-between",
          gap: 8
        },
        children: [
          /* @__PURE__ */ jsx87("span", { style: { color: C.fg }, children: a.name }),
          /* @__PURE__ */ jsx87("span", { style: { color: a.status === "error" || a.status === "failed" ? C.destructive : C.muted, fontSize: 11 }, children: a.status }),
          a.detail ? /* @__PURE__ */ jsx87("span", { style: { color: C.muted, fontSize: 11 }, children: a.detail }) : null
        ]
      },
      `${a.name}-${idx}`
    ))
  ] });
}
function ApprovalCards(props) {
  const C = useBrandColors();
  if (props.approvals.length === 0) return null;
  return /* @__PURE__ */ jsx87("div", { className: "ss-msg-approvals", style: { display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }, children: props.approvals.map((a) => /* @__PURE__ */ jsxs79(
    "div",
    {
      style: {
        border: `1px solid ${a.status === "pending" ? C.primary : C.border}`,
        borderRadius: 10,
        padding: "7px 10px",
        background: C.card,
        fontSize: 12
      },
      children: [
        /* @__PURE__ */ jsx87("span", { style: { color: C.fg, fontWeight: 600 }, children: a.status === "pending" ? productText("approvalPendingCard", props.locale) : productText("approvalCard", props.locale) }),
        /* @__PURE__ */ jsx87("span", { style: { color: C.muted, marginLeft: 8 }, children: a.approvalId })
      ]
    },
    a.approvalId
  )) });
}
function ArtifactCards(props) {
  const C = useBrandColors();
  if (props.artifacts.length === 0) return null;
  return /* @__PURE__ */ jsx87("div", { className: "ss-msg-artifacts", style: { display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }, children: props.artifacts.map((a) => /* @__PURE__ */ jsxs79(
    "div",
    {
      style: {
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: "7px 10px",
        background: C.card,
        fontSize: 12,
        display: "flex",
        gap: 8,
        alignItems: "baseline"
      },
      children: [
        /* @__PURE__ */ jsx87("span", { style: { color: C.muted }, children: productText("artifact", props.locale) }),
        /* @__PURE__ */ jsx87("span", { style: { color: C.fg }, children: a.name }),
        /* @__PURE__ */ jsx87("span", { style: { color: C.muted, fontSize: 11 }, children: a.kind }),
        a.url ? /* @__PURE__ */ jsx87("a", { href: a.url, target: "_blank", rel: "noreferrer noopener", style: { color: C.primary, fontSize: 11 }, children: "\u2197" }) : null
      ]
    },
    a.artifactId
  )) });
}
function DiffBlocks(props) {
  const C = useBrandColors();
  if (props.diffs.length === 0) return null;
  return /* @__PURE__ */ jsx87("div", { className: "ss-msg-diffs", style: { display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }, children: props.diffs.map((d) => /* @__PURE__ */ jsxs79("div", { style: { border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", background: C.card }, children: [
    /* @__PURE__ */ jsxs79("div", { style: { padding: "6px 10px", fontSize: 12, borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between" }, children: [
      /* @__PURE__ */ jsx87("span", { style: { color: C.fg }, children: d.path }),
      /* @__PURE__ */ jsxs79("span", { style: { fontSize: 11 }, children: [
        /* @__PURE__ */ jsxs79("span", { style: { color: C.success }, children: [
          "+",
          d.added
        ] }),
        " ",
        /* @__PURE__ */ jsxs79("span", { style: { color: C.destructive }, children: [
          "\u2212",
          d.removed
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx87("pre", { style: { margin: 0, padding: "8px 10px", fontSize: 11, overflowX: "auto", color: C.muted }, children: d.hunks.join("\n") })
  ] }, d.path)) });
}
function TraceSummary(props) {
  const C = useBrandColors();
  const rows = [];
  if (props.trace.modelProfile) rows.push(["profile", props.trace.modelProfile]);
  if (props.trace.turnState) rows.push(["state", props.trace.turnState]);
  if (props.trace.runId) rows.push(["run", props.trace.runId]);
  if (props.trace.approvalId) rows.push(["approval", props.trace.approvalId]);
  if (props.trace.delegatedTo) rows.push(["delegated", props.trace.delegatedTo]);
  if (props.trace.errorCode) rows.push(["error", props.trace.errorCode]);
  if (props.trace.contextMode) rows.push(["context", props.trace.contextMode]);
  if (typeof props.trace.retrievalTokens === "number") rows.push(["retrieval tokens", String(props.trace.retrievalTokens)]);
  if (typeof props.trace.conversationTokens === "number") rows.push(["conversation tokens", String(props.trace.conversationTokens)]);
  if (typeof props.trace.workingTokens === "number") rows.push(["working tokens", String(props.trace.workingTokens)]);
  if (typeof props.trace.remainingBudget === "number") rows.push(["budget left", String(props.trace.remainingBudget)]);
  if (typeof props.trace.retrievedChunkCount === "number") rows.push(["chunks", String(props.trace.retrievedChunkCount)]);
  if (rows.length === 0) return null;
  return /* @__PURE__ */ jsxs79("details", { className: "ss-msg-trace", style: { marginTop: 8, fontSize: 11, color: C.muted }, children: [
    /* @__PURE__ */ jsx87("summary", { style: { cursor: "pointer" }, children: productText("traceSummary", props.locale) }),
    /* @__PURE__ */ jsx87("div", { style: { display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 10px", paddingTop: 6 }, children: rows.map(([k, v]) => /* @__PURE__ */ jsxs79("span", { style: { display: "contents" }, children: [
      /* @__PURE__ */ jsx87("span", { style: { color: C.muted }, children: k }),
      /* @__PURE__ */ jsx87("span", { style: { color: C.fg, wordBreak: "break-all" }, children: v })
    ] }, k)) })
  ] });
}
function MessageErrorRow(props) {
  const C = useBrandColors();
  return /* @__PURE__ */ jsxs79(
    "div",
    {
      className: "ss-msg-error",
      style: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginTop: 8,
        fontSize: 12,
        color: C.destructive
      },
      children: [
        /* @__PURE__ */ jsxs79("span", { children: [
          productText("turnFailed", props.locale),
          ": ",
          props.errorCode
        ] }),
        props.retryPrompt && props.onRetry ? /* @__PURE__ */ jsx87(
          "button",
          {
            type: "button",
            onClick: () => props.onRetry(props.retryPrompt),
            style: {
              border: `1px solid ${C.destructive}`,
              borderRadius: 8,
              background: "transparent",
              color: C.destructive,
              padding: "3px 10px",
              fontSize: 11,
              cursor: "pointer"
            },
            children: productText("retry", props.locale)
          }
        ) : null
      ]
    }
  );
}
function ChatMessageBody(props) {
  const m = props.message;
  return /* @__PURE__ */ jsxs79("div", { className: "ss-msg-body", children: [
    renderChatMarkdown(m.content),
    m.citations && m.citations.length > 0 ? /* @__PURE__ */ jsx87(CitationChips, { locale: props.locale, citations: m.citations }) : null,
    m.toolActions && m.toolActions.length > 0 ? /* @__PURE__ */ jsx87(ToolActionCards, { locale: props.locale, actions: m.toolActions }) : null,
    m.approvals && m.approvals.length > 0 ? /* @__PURE__ */ jsx87(ApprovalCards, { locale: props.locale, approvals: m.approvals }) : null,
    m.artifacts && m.artifacts.length > 0 ? /* @__PURE__ */ jsx87(ArtifactCards, { locale: props.locale, artifacts: m.artifacts }) : null,
    m.diffs && m.diffs.length > 0 ? /* @__PURE__ */ jsx87(DiffBlocks, { locale: props.locale, diffs: m.diffs }) : null,
    m.trace ? /* @__PURE__ */ jsx87(TraceSummary, { locale: props.locale, trace: m.trace }) : null,
    m.errorCode || m.generationState === "error" ? /* @__PURE__ */ jsx87(
      MessageErrorRow,
      {
        locale: props.locale,
        errorCode: m.errorCode ?? "turn_error",
        retryPrompt: m.retryPrompt,
        onRetry: props.onRetry
      }
    ) : null
  ] });
}

// src/product/ChatCanvas.tsx
import { jsx as jsx88, jsxs as jsxs80 } from "react/jsx-runtime";
function ChatCanvas(props) {
  const C = useBrandColors();
  const { locale } = props;
  const bottomRef = useRef21(null);
  useEffect40(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [props.messages, props.busy]);
  const avatar = (size) => /* @__PURE__ */ jsx88(
    "div",
    {
      style: {
        width: size,
        height: size,
        borderRadius: "50%",
        background: C.chip,
        color: C.primary,
        display: "grid",
        placeItems: "center",
        fontSize: size * 0.46,
        fontWeight: 700,
        flexShrink: 0,
        fontFamily: '"Noto Sans Devanagari", "Mangal", "Kokila", sans-serif',
        overflow: "hidden",
        border: `1px solid ${C.border}`
      },
      children: props.avatarSrc ? /* @__PURE__ */ jsx88("img", { src: props.avatarSrc, alt: "", "aria-hidden": "true", style: { width: "100%", height: "100%", objectFit: "cover" } }) : "\u0938"
    }
  );
  return /* @__PURE__ */ jsxs80("div", { style: { position: "relative", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }, children: [
    props.ceoPrivileged === true ? /* @__PURE__ */ jsx88(
      "div",
      {
        "data-testid": "ceo-privileged-chip",
        style: {
          position: "absolute",
          top: 8,
          right: 12,
          zIndex: 2,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.02em",
          color: C.primary,
          background: C.chip,
          border: `1px solid ${C.border}`,
          borderRadius: 999,
          padding: "2px 10px",
          opacity: 0.85,
          pointerEvents: "none"
        },
        children: locale === "hi" ? "Shikka CEO \xB7 Privileged" : "Shikka CEO \xB7 Privileged"
      }
    ) : null,
    /* @__PURE__ */ jsx88(ChatCanvasBody, { ...props, avatar, bottomRef, C, locale })
  ] });
}
function ChatCanvasBody(props) {
  const { C, locale, avatar, bottomRef } = props;
  return /* @__PURE__ */ jsxs80(
    "div",
    {
      className: "ss-chat-canvas",
      style: { flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column", fontFamily: C.fontSans },
      children: [
        props.modeSurface,
        props.messages.length === 0 ? /* @__PURE__ */ jsxs80(
          "div",
          {
            style: {
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
              padding: "32px 24px"
            },
            children: [
              /* @__PURE__ */ jsx88(
                "div",
                {
                  style: {
                    width: 58,
                    height: 58,
                    borderRadius: "50%",
                    background: C.chip,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 22,
                    fontWeight: 700,
                    color: C.btn,
                    fontFamily: '"Noto Sans Devanagari", "Mangal", "Kokila", sans-serif',
                    overflow: "hidden",
                    border: `1px solid ${C.border}`
                  },
                  children: props.avatarSrc ? /* @__PURE__ */ jsx88("img", { src: props.avatarSrc, alt: "", "aria-hidden": "true", style: { width: "100%", height: "100%", objectFit: "cover" } }) : "\u0938"
                }
              ),
              /* @__PURE__ */ jsxs80("div", { style: { textAlign: "center" }, children: [
                /* @__PURE__ */ jsx88("h2", { style: { margin: "0 0 6px", fontSize: 26, fontWeight: 650, letterSpacing: "-0.025em", color: C.fg }, children: productText("welcomeTitle", locale) }),
                /* @__PURE__ */ jsx88("p", { style: { margin: 0, fontSize: 13, color: C.muted }, children: productText("welcomeSubtitle", locale) })
              ] }),
              props.composerSlot ? /* @__PURE__ */ jsx88("div", { style: { width: "100%", maxWidth: 720 }, children: props.composerSlot }) : null,
              props.suggestions && props.suggestions.length > 0 && /* @__PURE__ */ jsx88("div", { style: { display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 7, maxWidth: 720, width: "100%" }, children: props.suggestions.map((suggestion) => /* @__PURE__ */ jsx88(
                "button",
                {
                  type: "button",
                  onClick: () => props.onSuggestionClick?.(suggestion),
                  style: {
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 11px",
                    textAlign: "center",
                    borderRadius: 999,
                    border: `1px solid ${C.border}`,
                    background: C.card,
                    color: C.fg,
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: C.fontSans
                  },
                  children: suggestion
                },
                suggestion
              )) })
            ]
          }
        ) : /* @__PURE__ */ jsxs80("div", { style: { flex: 1, width: "100%", maxWidth: 820, margin: "0 auto", padding: "32px 24px 24px", display: "flex", flexDirection: "column", gap: 22 }, children: [
          props.messages.map((message) => /* @__PURE__ */ jsxs80(
            "div",
            {
              style: { display: "flex", justifyContent: message.role === "user" ? "flex-end" : "flex-start", gap: 10 },
              children: [
                message.role === "assistant" && avatar(26),
                /* @__PURE__ */ jsx88(
                  "div",
                  {
                    style: {
                      maxWidth: message.role === "user" ? "72%" : "calc(100% - 38px)",
                      padding: message.role === "user" ? "10px 14px" : "1px 0",
                      borderRadius: message.role === "user" ? "16px 16px 4px 16px" : 0,
                      background: message.role === "user" ? C.chip : "transparent",
                      color: C.fg,
                      fontSize: 14,
                      lineHeight: 1.65,
                      whiteSpace: message.role === "user" ? "pre-wrap" : void 0
                    },
                    children: message.role === "user" ? message.content : /* @__PURE__ */ jsx88(ChatMessageBody, { locale, message, onRetry: props.onRetryPrompt })
                  }
                )
              ]
            },
            message.id
          )),
          props.busy && /* @__PURE__ */ jsxs80("div", { style: { display: "flex", gap: 8, alignItems: "flex-end" }, children: [
            avatar(26),
            /* @__PURE__ */ jsx88(
              "div",
              {
                style: {
                  padding: "10px 14px",
                  background: C.assistantBg,
                  borderRadius: "16px 16px 16px 4px",
                  display: "flex",
                  gap: 4,
                  alignItems: "center"
                },
                children: [0, 1, 2].map((index) => /* @__PURE__ */ jsx88(
                  "span",
                  {
                    style: {
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: C.muted,
                      display: "inline-block",
                      opacity: 0.6 + index * 0.2
                    }
                  },
                  index
                ))
              }
            )
          ] }),
          /* @__PURE__ */ jsx88("div", { ref: bottomRef })
        ] })
      ]
    }
  );
}

// src/product/activityLedger.ts
function assistantTurnFromMessage(m) {
  const evidence = typeof m["turnEvidence"] === "object" && m["turnEvidence"] !== null ? m["turnEvidence"] : void 0;
  const str = (v) => typeof v === "string" ? v : void 0;
  const toolEvents = Array.isArray(m["toolEvents"]) ? m["toolEvents"] : [];
  return {
    messageId: str(m["messageId"]) ?? str(m["id"]) ?? "unknown",
    conversationId: str(m["conversationId"]) ?? "",
    contentPreview: str(m["content"])?.slice(0, 120) ?? "",
    createdAt: typeof m["createdAt"] === "number" ? m["createdAt"] : 0,
    generationState: str(m["generationState"]),
    errorCode: str(m["errorCode"]) ?? str(evidence?.["errorCode"]),
    modelProfile: str(evidence?.["logicalProfile"]),
    runId: str(evidence?.["runId"]),
    approvalId: str(evidence?.["approvalId"]),
    toolActions: toolEvents.map((e) => ({
      name: str(e["name"]) ?? str(e["toolName"]) ?? "tool",
      status: str(e["status"]) ?? "recorded"
    })),
    artifactIds: void 0
  };
}
function buildActivityLedger(input) {
  const entries = [];
  for (const t5 of input.assistantTurns ?? []) {
    const failed = t5.errorCode !== void 0 || t5.generationState === "error";
    entries.push({
      id: `assistant_turn:${t5.messageId}`,
      kind: failed ? "error" : "assistant_turn",
      at: t5.createdAt,
      label: t5.contentPreview || t5.messageId,
      status: t5.generationState,
      errorCode: t5.errorCode,
      modelProfile: t5.modelProfile,
      runId: t5.runId,
      approvalId: t5.approvalId,
      conversationId: t5.conversationId,
      source: "chat.messages"
    });
    for (const a of t5.toolActions ?? []) {
      entries.push({
        id: `tool_action:${t5.messageId}:${a.name}`,
        kind: "tool_action",
        at: t5.createdAt,
        label: a.name,
        status: a.status,
        runId: t5.runId,
        conversationId: t5.conversationId,
        source: "chat.messages"
      });
    }
    if (t5.approvalId) {
      entries.push({
        id: `approval:turn:${t5.approvalId}`,
        kind: "approval",
        at: t5.createdAt,
        label: t5.approvalId,
        status: "pending",
        approvalId: t5.approvalId,
        conversationId: t5.conversationId,
        source: "chat.messages"
      });
    }
  }
  for (const r of input.runs ?? []) {
    entries.push({
      id: `run:${r.runId}`,
      kind: "run",
      at: r.updatedAt ?? 0,
      label: r.title ?? r.runId,
      status: r.status,
      runId: r.runId,
      source: "cowork.runs"
    });
  }
  for (const a of input.approvals ?? []) {
    entries.push({
      id: `approval:${a.approvalId}`,
      kind: "approval",
      at: a.updatedAt ?? 0,
      label: a.actionLabel ?? a.approvalId,
      status: a.status,
      approvalId: a.approvalId,
      source: "cowork.approvals"
    });
  }
  for (const u of input.usage ?? []) {
    entries.push({
      id: `usage:${u.id}`,
      kind: "usage",
      at: u.at,
      label: u.label,
      status: u.status,
      source: "usage.ledger"
    });
  }
  return entries.sort((a, b) => b.at - a.at);
}

// src/product/activityLedgerView.tsx
import { jsx as jsx89, jsxs as jsxs81 } from "react/jsx-runtime";
var KIND_LABEL = {
  assistant_turn: "actAssistantTurn",
  tool_action: "actToolAction",
  approval: "actApproval",
  run: "actRun",
  artifact: "actArtifact",
  usage: "actUsage",
  error: "actError"
};
function formatTime3(at, locale) {
  if (!at) return "\u2014";
  try {
    return new Date(at).toLocaleString(locale === "hi" ? "hi-IN" : "en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "\u2014";
  }
}
function ActivityLedgerView(props) {
  const C = useBrandColors();
  const { locale } = props;
  if (props.loading) {
    return /* @__PURE__ */ jsx89("p", { style: { color: C.muted, fontSize: 13 }, children: productText("loading", locale) });
  }
  if (props.error) {
    return /* @__PURE__ */ jsx89("p", { style: { color: C.destructive, fontSize: 13 }, children: props.error });
  }
  if (props.entries.length === 0) {
    return /* @__PURE__ */ jsx89("p", { style: { color: C.muted, fontSize: 13 }, children: productText("emptyList", locale) });
  }
  return /* @__PURE__ */ jsx89("div", { className: "ss-activity-ledger", style: { display: "flex", flexDirection: "column", gap: 6 }, children: props.entries.map((e) => /* @__PURE__ */ jsxs81(
    "div",
    {
      style: {
        display: "grid",
        gridTemplateColumns: "110px 90px 1fr auto",
        gap: 10,
        alignItems: "baseline",
        padding: "7px 10px",
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        background: C.card,
        fontSize: 12
      },
      children: [
        /* @__PURE__ */ jsx89("span", { style: { color: C.muted }, children: formatTime3(e.at, locale) }),
        /* @__PURE__ */ jsx89(
          "span",
          {
            style: {
              ...{ padding: "1px 7px", borderRadius: 999, border: `1px solid ${C.border}`, fontSize: 10, justifySelf: "start" },
              color: e.kind === "error" ? C.destructive : C.fg
            },
            children: productText(KIND_LABEL[e.kind], locale)
          }
        ),
        /* @__PURE__ */ jsx89("span", { style: { color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: e.label }),
        /* @__PURE__ */ jsx89("span", { style: { color: e.errorCode ? C.destructive : C.muted, fontSize: 11 }, children: e.errorCode ?? e.status ?? "" })
      ]
    },
    e.id
  )) });
}

// src/product/ContextPane.tsx
import { useCallback as useCallback31, useEffect as useEffect41, useRef as useRef22, useState as useState47 } from "react";
import { jsx as jsx90, jsxs as jsxs82 } from "react/jsx-runtime";
var SURFACE_LABELS = {
  activity: "ctxActivity",
  controls: "ctxControls",
  captures: "ctxCaptures",
  agents: "ctxAgents",
  browser: "ctxBrowser",
  screenshot: "ctxScreenshot",
  video: "ctxVideo",
  workingTree: "ctxWorkingTree",
  files: "ctxFiles",
  diff: "ctxDiff",
  artifacts: "ctxArtifacts",
  approvals: "ctxApprovals",
  taskProgress: "ctxTaskProgress",
  terminal: "ctxTerminal"
};
var MIN_WIDTH = 260;
var MAX_WIDTH = 640;
var DEFAULT_WIDTH = 360;
function ContextPane(props) {
  const C = useBrandColors();
  const { locale } = props;
  const [width, setWidth] = useState47(() => {
    const fallback = props.initialWidth ?? DEFAULT_WIDTH;
    if (!props.storageKey || typeof window === "undefined") return fallback;
    try {
      const saved = Number(window.localStorage.getItem(props.storageKey));
      return Number.isFinite(saved) ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, saved)) : fallback;
    } catch {
      return fallback;
    }
  });
  const draggingRef = useRef22(false);
  const [dragging, setDragging] = useState47(false);
  const onPointerDown = useCallback31((event) => {
    event.preventDefault();
    draggingRef.current = true;
    setDragging(true);
  }, []);
  useEffect41(() => {
    const onMove = (event) => {
      if (!draggingRef.current) return;
      const next = window.innerWidth - event.clientX;
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, next)));
    };
    const onUp = () => {
      draggingRef.current = false;
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);
  useEffect41(() => {
    if (!props.storageKey) return;
    try {
      window.localStorage.setItem(props.storageKey, String(width));
    } catch {
    }
  }, [props.storageKey, width]);
  useEffect41(() => {
    if (!dragging) return;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [dragging]);
  const active = props.surfaces.find((surface) => surface.id === props.activeSurfaceId);
  return /* @__PURE__ */ jsxs82(
    "aside",
    {
      className: "ss-context-pane",
      style: {
        width,
        minWidth: MIN_WIDTH,
        display: "flex",
        height: "100%",
        borderLeft: `1px solid ${C.border}`,
        background: C.panel,
        fontFamily: C.fontSans,
        transition: dragging ? "none" : "width 160ms ease"
      },
      children: [
        /* @__PURE__ */ jsx90(
          "div",
          {
            className: "ss-context-pane-resizer",
            role: "separator",
            "aria-orientation": "vertical",
            "aria-label": locale === "hi" ? "\u0926\u093E\u092F\u093E\u0901 \u092A\u0948\u0928\u0932 \u091B\u094B\u091F\u093E-\u092C\u0921\u093C\u093E \u0915\u0930\u0947\u0902" : "Resize right panel",
            "aria-valuemin": MIN_WIDTH,
            "aria-valuemax": MAX_WIDTH,
            "aria-valuenow": width,
            tabIndex: 0,
            onMouseDown: onPointerDown,
            onDoubleClick: () => setWidth(props.initialWidth ?? DEFAULT_WIDTH),
            onKeyDown: (event) => {
              if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
              event.preventDefault();
              setWidth((current) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, current + (event.key === "ArrowLeft" ? 16 : -16))));
            },
            style: {
              width: 12,
              cursor: "col-resize",
              background: dragging ? C.chip : "transparent",
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              touchAction: "none"
            },
            children: /* @__PURE__ */ jsx90(
              "span",
              {
                "aria-hidden": "true",
                style: {
                  width: 3,
                  height: 34,
                  borderRadius: 999,
                  background: dragging ? C.primary : C.border
                }
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs82("div", { style: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }, children: [
          /* @__PURE__ */ jsxs82(
            "div",
            {
              style: {
                height: 40,
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "0 10px",
                borderBottom: `1px solid ${C.border}`,
                flexShrink: 0
              },
              children: [
                /* @__PURE__ */ jsx90("span", { style: { fontSize: 11, fontWeight: 600, color: C.fg }, children: productText("contextPane", locale) }),
                /* @__PURE__ */ jsx90("div", { style: { flex: 1 } }),
                /* @__PURE__ */ jsx90(
                  "button",
                  {
                    type: "button",
                    onClick: () => setWidth((current) => Math.max(MIN_WIDTH, current - 40)),
                    "aria-label": locale === "hi" ? "\u092A\u0948\u0928\u0932 \u091B\u094B\u091F\u093E \u0915\u0930\u0947\u0902" : "Narrow panel",
                    style: { border: "none", background: "transparent", color: C.muted, cursor: "pointer", fontSize: 15 },
                    children: "\u2212"
                  }
                ),
                /* @__PURE__ */ jsx90(
                  "button",
                  {
                    type: "button",
                    onClick: () => setWidth((current) => Math.min(MAX_WIDTH, current + 40)),
                    "aria-label": locale === "hi" ? "\u092A\u0948\u0928\u0932 \u092C\u0921\u093C\u093E \u0915\u0930\u0947\u0902" : "Widen panel",
                    style: { border: "none", background: "transparent", color: C.muted, cursor: "pointer", fontSize: 15 },
                    children: "+"
                  }
                ),
                /* @__PURE__ */ jsx90(
                  "button",
                  {
                    type: "button",
                    onClick: props.onClose,
                    "aria-label": productText("closePane", locale),
                    title: productText("closePane", locale),
                    style: {
                      width: 28,
                      height: 28,
                      display: "grid",
                      placeItems: "center",
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      background: C.chip,
                      color: C.fg,
                      cursor: "pointer",
                      fontSize: 18,
                      lineHeight: 1
                    },
                    children: "\xD7"
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx90("div", { style: { display: "flex", gap: 4, padding: "8px 10px 0", flexWrap: "wrap", flexShrink: 0 }, children: props.surfaces.map((surface) => {
            const isActive = surface.id === props.activeSurfaceId;
            return /* @__PURE__ */ jsx90(
              "button",
              {
                type: "button",
                onClick: () => props.onActiveSurfaceChange(surface.id),
                style: {
                  fontSize: 10,
                  padding: "3px 8px",
                  borderRadius: 999,
                  border: `1px solid ${isActive ? C.primary : C.border}`,
                  background: isActive ? C.chip : "transparent",
                  color: isActive ? C.fg : C.muted,
                  cursor: "pointer",
                  fontFamily: C.fontSans
                },
                children: productText(SURFACE_LABELS[surface.id], locale)
              },
              surface.id
            );
          }) }),
          /* @__PURE__ */ jsx90("div", { style: { flex: 1, overflow: "auto", padding: 12 }, children: active?.render ? active.render() : /* @__PURE__ */ jsx90("p", { style: { margin: 0, fontSize: 11.5, color: C.muted }, children: productText("contextEmpty", locale) }) })
        ] })
      ]
    }
  );
}

// src/product/DesignModeSurface.tsx
import { useEffect as useEffect42, useState as useState48 } from "react";
import { jsx as jsx91, jsxs as jsxs83 } from "react/jsx-runtime";
function useArtifactObjectUrl(artifact) {
  const [url, setUrl] = useState48(void 0);
  useEffect42(() => {
    if (!artifact) {
      setUrl(void 0);
      return;
    }
    const binary = atob(artifact.artifact);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const objectUrl = URL.createObjectURL(new Blob([bytes], { type: artifact.mimeType }));
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [artifact]);
  return url;
}
function DesignModeSurface(props) {
  const C = useBrandColors();
  const { locale, artifact } = props;
  const artifactUrl = useArtifactObjectUrl(artifact);
  return /* @__PURE__ */ jsxs83(
    "section",
    {
      style: {
        margin: "16px 24px 0",
        padding: 14,
        borderRadius: 14,
        border: `1px solid ${C.border}`,
        background: C.card,
        fontFamily: C.fontSans
      },
      children: [
        /* @__PURE__ */ jsxs83("div", { style: { display: "flex", alignItems: "center", gap: 8 }, children: [
          /* @__PURE__ */ jsx91("h3", { style: { margin: 0, fontSize: 13, fontWeight: 700, color: C.fg }, children: props.title }),
          /* @__PURE__ */ jsx91("span", { style: { fontSize: 10.5, color: C.muted }, children: props.dimensionLabel }),
          /* @__PURE__ */ jsxs83("span", { style: { fontSize: 10.5, color: C.muted }, children: [
            "\xB7 ",
            productText("designExportPngOnly", locale)
          ] }),
          /* @__PURE__ */ jsx91("div", { style: { flex: 1 } }),
          props.busy && /* @__PURE__ */ jsx91("span", { style: { fontSize: 10.5, color: C.muted }, children: "\u2026" })
        ] }),
        /* @__PURE__ */ jsx91("p", { style: { margin: "6px 0 0", fontSize: 10.5, lineHeight: 1.5, color: C.muted }, children: productText("designAiAssisted", locale) }),
        props.imageGenerationStatus === "not_configured" && /* @__PURE__ */ jsx91("p", { style: { margin: "6px 0 0", fontSize: 10.5, lineHeight: 1.5, color: C.muted }, children: productText("imageGenerationNotConfigured", locale) }),
        props.errorMessage && /* @__PURE__ */ jsxs83("p", { style: { margin: "10px 0 0", fontSize: 11.5, color: C.destructive }, children: [
          productText("designUnavailable", locale),
          " (",
          props.errorMessage,
          ")"
        ] }),
        artifact && artifactUrl && artifact.mimeType.startsWith("image/") && /* @__PURE__ */ jsxs83("figure", { style: { margin: "10px 0 0" }, children: [
          /* @__PURE__ */ jsx91(
            "img",
            {
              src: artifactUrl,
              alt: props.title,
              style: {
                display: "block",
                maxWidth: "100%",
                maxHeight: 260,
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                background: C.bg
              }
            }
          ),
          /* @__PURE__ */ jsxs83("figcaption", { style: { marginTop: 6, fontSize: 10.5, color: C.muted }, children: [
            artifact.format.toUpperCase(),
            " \xB7 ",
            Math.round(artifact.bytes / 1024),
            " KB"
          ] })
        ] }),
        artifact && artifactUrl && !artifact.mimeType.startsWith("image/") && /* @__PURE__ */ jsx91("p", { style: { margin: "10px 0 0", fontSize: 11.5, color: C.fg }, children: /* @__PURE__ */ jsxs83(
          "a",
          {
            href: artifactUrl,
            download: `${props.title}.${artifact.format}`,
            style: { color: C.primary },
            children: [
              productText("designExport", locale),
              " \xB7 ",
              artifact.format.toUpperCase(),
              " \xB7",
              " ",
              Math.round(artifact.bytes / 1024),
              " KB"
            ]
          }
        ) })
      ]
    }
  );
}

// src/product/ProductAccountMenu.tsx
import { useEffect as useEffect43, useRef as useRef23, useState as useState49 } from "react";
import { Fragment as Fragment12, jsx as jsx92, jsxs as jsxs84 } from "react/jsx-runtime";
var ACCOUNT_MENU_LABEL_KEYS = {
  settings: "settings",
  appearance: "appearance",
  language: "language",
  skills: "skills",
  connections: "connections",
  integrations: "integrations",
  permissionsTrust: "permissionsTrust",
  usage: "usage",
  support: "support",
  logout: "logout",
  ceoAssistant: "ceoAssistant",
  adminOrganization: "adminOrganization",
  systemIntegrations: "systemIntegrations",
  emergencyStop: "emergencyStop"
};
var NORMAL_ITEMS = [
  "settings",
  "appearance",
  "language",
  "skills",
  "connections",
  "integrations",
  "permissionsTrust",
  "usage",
  "support"
];
function privilegedAccountMenuItems(visibility) {
  const items = [];
  if (visibility.isCeoEligible || visibility.isSuperAdmin || visibility.isSuperSubAdmin) items.push("ceoAssistant");
  if (visibility.isSuperAdmin) items.push("adminOrganization", "systemIntegrations");
  if (visibility.isSuperAdmin || visibility.isSuperSubAdmin) items.push("emergencyStop");
  return items;
}
function ProductAccountMenu(props) {
  const C = useBrandColors();
  const { locale } = props;
  const [open, setOpen] = useState49(false);
  const rootRef = useRef23(null);
  useEffect43(() => {
    if (!open) return void 0;
    const onDocumentClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);
  const privileged = privilegedAccountMenuItems(props.visibility);
  const itemStyle = {
    display: "block",
    width: "100%",
    textAlign: "left",
    fontSize: 12,
    padding: "6px 9px",
    borderRadius: 7,
    border: "none",
    background: "transparent",
    color: C.fg,
    cursor: "pointer",
    fontFamily: C.fontSans
  };
  const renderItem = (id, danger = false) => {
    const unavailable = props.unavailableItems?.includes(id) === true;
    const style = unavailable ? { ...itemStyle, color: C.muted, cursor: "not-allowed" } : danger ? { ...itemStyle, color: C.destructive, fontWeight: 600 } : itemStyle;
    return /* @__PURE__ */ jsxs84(
      "button",
      {
        type: "button",
        disabled: unavailable,
        "aria-disabled": unavailable,
        onClick: () => {
          if (unavailable) return;
          setOpen(false);
          props.onSelect(id);
        },
        style,
        children: [
          productText(ACCOUNT_MENU_LABEL_KEYS[id], locale),
          unavailable ? /* @__PURE__ */ jsxs84("span", { style: { fontSize: 9.5, color: C.muted }, children: [
            " \xB7 ",
            productText("unavailable", locale)
          ] }) : null
        ]
      },
      id
    );
  };
  return /* @__PURE__ */ jsxs84("div", { ref: rootRef, style: { position: "relative", flexShrink: 0, fontFamily: C.fontSans }, children: [
    props.compact ? /* @__PURE__ */ jsx92(
      "button",
      {
        type: "button",
        "aria-expanded": open,
        "aria-label": productText("account", locale),
        title: props.displayName,
        onClick: () => setOpen((prev) => !prev),
        style: {
          width: 34,
          height: 34,
          borderRadius: "50%",
          border: `1px solid ${C.border}`,
          background: open ? C.accent : C.chip,
          color: open ? C.accentFg : C.primary,
          display: "grid",
          placeItems: "center",
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          padding: 0
        },
        children: props.displayName.charAt(0).toUpperCase()
      }
    ) : /* @__PURE__ */ jsxs84(
      "button",
      {
        type: "button",
        "aria-expanded": open,
        "aria-label": productText("account", locale),
        onClick: () => setOpen((prev) => !prev),
        style: {
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          padding: "8px 10px",
          border: "none",
          borderTop: `1px solid ${C.border}`,
          background: open ? C.chip : "transparent",
          color: C.fg,
          cursor: "pointer",
          textAlign: "left"
        },
        children: [
          /* @__PURE__ */ jsx92(
            "span",
            {
              style: {
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: C.chip,
                color: C.primary,
                display: "grid",
                placeItems: "center",
                fontSize: 10,
                fontWeight: 700,
                flexShrink: 0
              },
              children: props.displayName.charAt(0).toUpperCase()
            }
          ),
          /* @__PURE__ */ jsxs84("span", { style: { flex: 1, minWidth: 0 }, children: [
            /* @__PURE__ */ jsx92(
              "span",
              {
                style: {
                  display: "block",
                  fontSize: 10.5,
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                },
                children: props.displayName
              }
            ),
            props.subtitle && /* @__PURE__ */ jsx92("span", { style: { display: "block", fontSize: 9, color: C.muted }, children: props.subtitle })
          ] }),
          /* @__PURE__ */ jsx92("span", { style: { color: C.muted, fontSize: 10 }, children: open ? "\u25BE" : "\u25B8" })
        ]
      }
    ),
    open && /* @__PURE__ */ jsxs84(
      "div",
      {
        role: "menu",
        style: {
          position: "absolute",
          bottom: "calc(100% + 4px)",
          left: 8,
          width: 288,
          zIndex: 60,
          padding: 6,
          borderRadius: 12,
          border: `1px solid ${C.border}`,
          background: C.card,
          boxShadow: "0 16px 40px rgba(0,0,0,0.18)",
          maxHeight: "min(70vh, 520px)",
          overflowY: "auto"
        },
        children: [
          NORMAL_ITEMS.map((id) => renderItem(id)),
          privileged.length > 0 && /* @__PURE__ */ jsxs84(Fragment12, { children: [
            /* @__PURE__ */ jsx92(
              "div",
              {
                style: {
                  margin: "6px 4px 3px",
                  paddingTop: 6,
                  borderTop: `1px solid ${C.border}`,
                  fontSize: 9,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: C.muted
                },
                children: productText("privilegedSection", locale)
              }
            ),
            privileged.map((id) => renderItem(id, id === "emergencyStop"))
          ] }),
          /* @__PURE__ */ jsx92("div", { style: { margin: "6px 4px 3px", paddingTop: 6, borderTop: `1px solid ${C.border}` } }),
          renderItem("logout")
        ]
      }
    )
  ] });
}

// src/product/ProductPreferenceControls.tsx
import { jsx as jsx93, jsxs as jsxs85 } from "react/jsx-runtime";
function ProductPreferenceControls(props) {
  const C = useBrandColors();
  const buttonStyle = {
    minHeight: 30,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    padding: "0 9px",
    border: `1px solid ${C.border}`,
    borderRadius: 9,
    background: C.card,
    color: C.fg,
    cursor: "pointer",
    font: "inherit",
    fontSize: 11,
    whiteSpace: "nowrap"
  };
  return /* @__PURE__ */ jsxs85("div", { className: "ss-product-preferences", style: { display: "flex", alignItems: "center", gap: 5 }, children: [
    /* @__PURE__ */ jsxs85(
      "button",
      {
        type: "button",
        onClick: props.onToggleTheme,
        "aria-label": props.locale === "hi" ? "Light \u0914\u0930 Dark \u0925\u0940\u092E \u092C\u0926\u0932\u0947\u0902" : "Toggle Light and Dark theme",
        title: props.locale === "hi" ? "Light / Dark" : "Light / Dark",
        style: buttonStyle,
        children: [
          /* @__PURE__ */ jsx93("span", { "aria-hidden": "true", children: props.theme === "dark" ? "\u263E" : "\u2600" }),
          /* @__PURE__ */ jsx93("span", { children: props.theme === "dark" ? "Dark" : "Light" })
        ]
      }
    ),
    /* @__PURE__ */ jsxs85(
      "button",
      {
        type: "button",
        onClick: props.onToggleLocale,
        "aria-label": props.locale === "hi" ? "English \u092D\u093E\u0937\u093E \u091A\u0941\u0928\u0947\u0902" : "\u0939\u093F\u0902\u0926\u0940 \u092D\u093E\u0937\u093E \u091A\u0941\u0928\u0947\u0902",
        title: "\u0939\u093F\u0902\u0926\u0940 / English",
        style: buttonStyle,
        children: [
          /* @__PURE__ */ jsx93("span", { "aria-hidden": "true", children: "\u6587" }),
          /* @__PURE__ */ jsx93("span", { children: props.locale === "hi" ? "\u0939\u093F\u0902\u0926\u0940" : "English" })
        ]
      }
    )
  ] });
}

// src/product/ProductSidebar.tsx
import { jsx as jsx94, jsxs as jsxs86 } from "react/jsx-runtime";
var PRODUCT_NAV_ITEMS = [
  { id: "chat", icon: "\u{1F4AC}", labelKey: "navChat" },
  { id: "projects", icon: "\u{1F4C1}", labelKey: "navProjects" },
  { id: "files", icon: "\u{1F4C4}", labelKey: "navFiles" },
  { id: "automation", icon: "\u26A1", labelKey: "navAutomation" },
  { id: "apps", icon: "\u229E", labelKey: "navApps" },
  { id: "code", icon: "\u2318", labelKey: "navCode" },
  { id: "design", icon: "\u25C6", labelKey: "navDesign" },
  { id: "activity", icon: "\u2263", labelKey: "navActivity" },
  { id: "skills", icon: "\u2726", labelKey: "navSkills" }
];
function ProductSidebar(props) {
  const C = useBrandColors();
  const { locale } = props;
  return /* @__PURE__ */ jsxs86(
    "nav",
    {
      className: "ss-product-sidebar",
      style: {
        width: 224,
        minWidth: 224,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "visible",
        background: C.panel,
        borderRight: `1px solid ${C.border}`,
        fontFamily: C.fontSans,
        position: "relative",
        zIndex: 30
      },
      children: [
        /* @__PURE__ */ jsxs86("div", { style: { padding: "14px 10px 9px 14px", display: "flex", alignItems: "center", gap: 9 }, children: [
          /* @__PURE__ */ jsx94(
            "div",
            {
              style: {
                width: 34,
                height: 34,
                borderRadius: "50%",
                background: C.chip,
                display: "grid",
                placeItems: "center",
                fontSize: 15,
                fontWeight: 700,
                color: C.btn,
                flexShrink: 0,
                overflow: "hidden",
                border: `1px solid ${C.border}`,
                fontFamily: '"Noto Sans Devanagari", "Mangal", "Kokila", sans-serif'
              },
              children: props.logoSrc ? /* @__PURE__ */ jsx94("img", { src: props.logoSrc, alt: "", "aria-hidden": "true", style: { width: "100%", height: "100%", objectFit: "cover" } }) : "\u0938"
            }
          ),
          /* @__PURE__ */ jsxs86("div", { style: { minWidth: 0, flex: 1 }, children: [
            /* @__PURE__ */ jsx94("div", { style: { fontSize: 12, fontWeight: 700, color: C.fg, lineHeight: 1.3 }, children: "Smart Shikka" }),
            /* @__PURE__ */ jsx94("div", { style: { fontSize: 9.5, color: C.muted, lineHeight: 1.3 }, children: productText("tagline", locale) })
          ] }),
          props.onCollapse && /* @__PURE__ */ jsx94(
            "button",
            {
              type: "button",
              "aria-label": locale === "hi" ? "\u092C\u093E\u092F\u093E\u0901 \u092A\u0948\u0928\u0932 \u092C\u0902\u0926 \u0915\u0930\u0947\u0902" : "Close sidebar",
              title: locale === "hi" ? "\u092C\u093E\u092F\u093E\u0901 \u092A\u0948\u0928\u0932 \u092C\u0902\u0926 \u0915\u0930\u0947\u0902" : "Close sidebar",
              onClick: props.onCollapse,
              style: {
                width: 30,
                height: 30,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                background: C.chip,
                color: C.fg,
                cursor: "pointer",
                fontSize: 17
              },
              children: "\u2039"
            }
          )
        ] }),
        props.headerSlot,
        /* @__PURE__ */ jsx94("div", { style: { padding: "4px 10px 8px" }, children: /* @__PURE__ */ jsxs86(
          "button",
          {
            type: "button",
            onClick: props.onNewChat,
            style: {
              width: "100%",
              padding: "7px 10px",
              background: C.primary,
              color: C.btn,
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontFamily: C.fontSans
            },
            children: [
              /* @__PURE__ */ jsx94("span", { style: { fontSize: 13 }, children: "+" }),
              " ",
              productText("newChat", locale)
            ]
          }
        ) }),
        /* @__PURE__ */ jsx94("div", { style: { padding: "0 8px" }, children: PRODUCT_NAV_ITEMS.map((item) => {
          const active = props.activeRoute === item.id;
          const unavailable = props.unavailableRoutes?.includes(item.id) ?? false;
          return /* @__PURE__ */ jsxs86(
            "button",
            {
              type: "button",
              disabled: unavailable,
              title: unavailable ? locale === "hi" ? "\u0907\u0938 Desktop runtime \u092E\u0947\u0902 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902" : "Unavailable in this Desktop runtime" : void 0,
              onClick: () => props.onNavigate(item.id),
              style: {
                display: "flex",
                alignItems: "center",
                gap: 7,
                width: "100%",
                padding: "7px 9px",
                marginBottom: 2,
                borderRadius: 8,
                border: "none",
                borderLeft: active ? `3px solid ${C.primary}` : "3px solid transparent",
                background: active ? C.accent : "transparent",
                color: active ? C.accentFg : C.fg,
                fontSize: 11.5,
                fontWeight: active ? 600 : 400,
                cursor: unavailable ? "not-allowed" : "pointer",
                opacity: unavailable ? 0.45 : 1,
                textAlign: "left",
                fontFamily: C.fontSans
              },
              children: [
                /* @__PURE__ */ jsx94("span", { style: { fontSize: 12, width: 14, textAlign: "center" }, children: item.icon }),
                productText(item.labelKey, locale)
              ]
            },
            item.id
          );
        }) }),
        /* @__PURE__ */ jsxs86("div", { style: { flex: 1, minHeight: 0, overflow: "hidden", padding: "12px 8px 0" }, children: [
          /* @__PURE__ */ jsx94(
            "div",
            {
              style: {
                fontSize: 9,
                fontWeight: 600,
                color: C.muted,
                padding: "2px 8px 5px",
                textTransform: "uppercase",
                letterSpacing: "0.06em"
              },
              children: productText("recentChats", locale)
            }
          ),
          /* @__PURE__ */ jsx94("div", { style: { overflowY: "auto", height: "calc(100% - 24px)", paddingBottom: 8 }, children: props.sessions.slice(0, 16).map((session) => /* @__PURE__ */ jsx94(
            "button",
            {
              type: "button",
              onClick: () => props.onSelectSession(session.id),
              style: {
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "5px 8px",
                borderRadius: 6,
                background: session.id === props.activeSessionId ? C.chip : "transparent",
                border: "none",
                fontSize: 10,
                color: C.fg,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
                fontFamily: "inherit"
              },
              children: session.title
            },
            session.id
          )) })
        ] }),
        props.assistantSlot,
        props.accountSlot
      ]
    }
  );
}
function ProductSidebarRail(props) {
  const C = useBrandColors();
  return /* @__PURE__ */ jsxs86(
    "nav",
    {
      className: "ss-product-sidebar-rail",
      "aria-label": props.locale === "hi" ? "\u0938\u0902\u0915\u094D\u0937\u093F\u092A\u094D\u0924 \u0928\u0947\u0935\u093F\u0917\u0947\u0936\u0928" : "Compact navigation",
      style: {
        width: 52,
        minWidth: 52,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        padding: "12px 0",
        overflow: "hidden",
        background: C.panel,
        borderRight: `1px solid ${C.border}`,
        position: "relative",
        zIndex: 30,
        fontFamily: C.fontSans
      },
      children: [
        /* @__PURE__ */ jsx94(
          "button",
          {
            type: "button",
            "aria-label": props.locale === "hi" ? "\u092C\u093E\u092F\u093E\u0901 \u092A\u0948\u0928\u0932 \u0916\u094B\u0932\u0947\u0902" : "Open sidebar",
            title: props.locale === "hi" ? "\u092C\u093E\u092F\u093E\u0901 \u092A\u0948\u0928\u0932 \u0916\u094B\u0932\u0947\u0902" : "Open sidebar",
            onClick: props.onExpand,
            style: {
              width: 34,
              height: 34,
              borderRadius: "50%",
              border: `1px solid ${C.border}`,
              background: C.chip,
              color: C.fg,
              cursor: "pointer",
              fontSize: 15,
              fontWeight: 700,
              display: "grid",
              placeItems: "center",
              padding: 0,
              overflow: "hidden",
              marginBottom: 6,
              flexShrink: 0
            },
            children: props.logoSrc ? /* @__PURE__ */ jsx94("img", { src: props.logoSrc, alt: "", "aria-hidden": "true", style: { width: "100%", height: "100%", objectFit: "cover" } }) : "\u0938"
          }
        ),
        /* @__PURE__ */ jsx94(
          "button",
          {
            type: "button",
            "aria-label": productText("newChat", props.locale),
            title: productText("newChat", props.locale),
            onClick: props.onNewChat,
            style: {
              width: 34,
              height: 34,
              border: "none",
              borderRadius: 8,
              background: "transparent",
              color: C.primary,
              cursor: "pointer",
              fontSize: 20,
              fontWeight: 600,
              display: "grid",
              placeItems: "center",
              padding: 0,
              marginBottom: 4
            },
            children: "+"
          }
        ),
        PRODUCT_NAV_ITEMS.map((item) => {
          const active = props.activeRoute === item.id;
          const unavailable = props.unavailableRoutes?.includes(item.id) ?? false;
          return /* @__PURE__ */ jsx94(
            "button",
            {
              type: "button",
              "aria-label": productText(item.labelKey, props.locale),
              title: unavailable ? props.locale === "hi" ? "\u0907\u0938 Desktop runtime \u092E\u0947\u0902 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902" : "Unavailable in this Desktop runtime" : productText(item.labelKey, props.locale),
              disabled: unavailable,
              onClick: () => props.onNavigate(item.id),
              style: {
                width: 34,
                height: 34,
                display: "grid",
                placeItems: "center",
                border: "none",
                borderRadius: 8,
                background: active ? C.accent : "transparent",
                color: active ? C.accentFg : C.muted,
                cursor: unavailable ? "not-allowed" : "pointer",
                opacity: unavailable ? 0.45 : 1,
                fontSize: 15,
                padding: 0
              },
              children: item.icon
            },
            item.id
          );
        }),
        /* @__PURE__ */ jsx94("div", { style: { flex: 1 } }),
        props.assistantSlot,
        props.accountSlot
      ]
    }
  );
}

// src/product/ProductShell.tsx
import { jsx as jsx95, jsxs as jsxs87 } from "react/jsx-runtime";
function ProductShell(props) {
  const C = useBrandColors();
  return /* @__PURE__ */ jsxs87(
    "div",
    {
      className: "ss-product-shell",
      style: {
        display: "flex",
        flex: 1,
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        background: C.bg,
        color: C.fg,
        fontFamily: C.fontSans
      },
      children: [
        props.sidebar,
        /* @__PURE__ */ jsxs87(
          "div",
          {
            className: "ss-product-main",
            style: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, overflow: "hidden" },
            children: [
              props.topbar,
              /* @__PURE__ */ jsx95("div", { className: "ss-product-canvas", style: { flex: 1, display: "flex", minWidth: 0, minHeight: 0, overflow: "hidden" }, children: props.canvas }),
              props.composer && props.composerPlacement !== "welcome" ? /* @__PURE__ */ jsx95(
                "div",
                {
                  className: "ss-product-composer",
                  style: {
                    padding: "10px max(20px, calc((100% - 880px) / 2)) 14px",
                    borderTop: `1px solid ${C.border}`,
                    background: C.bg,
                    flexShrink: 0,
                    position: "relative",
                    zIndex: 20
                  },
                  children: /* @__PURE__ */ jsx95("div", { style: { width: "100%", maxWidth: 880, margin: "0 auto" }, children: props.composer })
                }
              ) : null
            ]
          }
        ),
        props.contextPane
      ]
    }
  );
}
function ProductTopbar(props) {
  const C = useBrandColors();
  return /* @__PURE__ */ jsxs87(
    "div",
    {
      style: {
        height: 44,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "0 20px",
        borderBottom: `1px solid ${C.border}`,
        flexShrink: 0
      },
      children: [
        /* @__PURE__ */ jsx95("span", { style: { width: 7, height: 7, borderRadius: "50%", background: props.statusDotColor } }),
        /* @__PURE__ */ jsx95("span", { style: { fontSize: 12, color: C.muted }, children: props.statusText }),
        /* @__PURE__ */ jsx95("div", { style: { flex: 1 } }),
        props.rightSlot
      ]
    }
  );
}

// src/product/ProductSettingsSurface.tsx
import { useEffect as useEffect44, useState as useState50 } from "react";
import { jsx as jsx96, jsxs as jsxs88 } from "react/jsx-runtime";
function text(locale, en, hi) {
  return locale === "hi" ? hi : en;
}
var ACCENT_LABELS = {
  saffron: { en: "Saffron", hi: "\u0915\u0947\u0938\u0930\u093F\u092F\u093E" },
  indigo: { en: "Indigo", hi: "\u0928\u0940\u0932" },
  emerald: { en: "Emerald", hi: "\u092A\u0928\u094D\u0928\u093E" },
  rose: { en: "Rose", hi: "\u0917\u0941\u0932\u093E\u092C\u0940" },
  violet: { en: "Violet", hi: "\u092C\u0948\u0902\u0917\u0928\u0940" },
  sky: { en: "Sky", hi: "\u0906\u0938\u092E\u093E\u0928\u0940" }
};
function ProductSettingsSurface(props) {
  const C = useBrandColors();
  const [section, setSection] = useState50(props.initialSection ?? "general");
  useEffect44(() => {
    if (props.initialSection) setSection(props.initialSection);
  }, [props.initialSection]);
  const [accent, setAccent] = useState50(() => getStoredBrandAccent());
  useEffect44(() => {
    applyBrandAccent(accent);
  }, [accent, props.theme]);
  const groups = [
    {
      id: "personal",
      label: text(props.locale, "Personal", "\u0928\u093F\u091C\u0940"),
      items: [
        { id: "general", label: text(props.locale, "General", "\u0938\u093E\u092E\u093E\u0928\u094D\u092F") },
        ...props.accountInfo ? [{ id: "account", label: text(props.locale, "Account", "\u0916\u093E\u0924\u093E") }] : [],
        { id: "appearance", label: text(props.locale, "Appearance", "\u0926\u093F\u0916\u093E\u0935\u091F") },
        { id: "shortcuts", label: text(props.locale, "Keyboard Shortcuts", "\u0915\u0940\u092C\u094B\u0930\u094D\u0921 \u0936\u0949\u0930\u094D\u091F\u0915\u091F") },
        ...props.voiceSlot ? [{ id: "voice", label: text(props.locale, "Voice", "\u0935\u0949\u0907\u0938") }] : [],
        ...props.usageSlot ? [{ id: "usage", label: text(props.locale, "Usage", "\u0909\u092A\u092F\u094B\u0917") }] : [],
        ...props.memorySlot ? [{ id: "memory", label: text(props.locale, "Memory", "\u0938\u094D\u092E\u0943\u0924\u093F") }] : [],
        ...props.chatHistorySlot ? [{ id: "chatHistory", label: text(props.locale, "Chat History", "\u091A\u0948\u091F \u0907\u0924\u093F\u0939\u093E\u0938") }] : []
      ]
    },
    {
      id: "integrations",
      label: text(props.locale, "Integrations", "\u090F\u0915\u0940\u0915\u0930\u0923"),
      items: [
        ...props.providersSlot ? [{ id: "providers", label: text(props.locale, "LLM Providers", "LLM \u092A\u094D\u0930\u0926\u093E\u0924\u093E") }] : [],
        ...props.connectionsSlot ? [{ id: "connections", label: text(props.locale, "Connections", "\u0915\u0928\u0947\u0915\u094D\u0936\u0928") }] : [],
        ...props.domainsSlot ? [{ id: "domains", label: text(props.locale, "Domains", "\u0921\u094B\u092E\u0947\u0928") }] : []
      ]
    },
    {
      id: "advanced",
      label: text(props.locale, "Advanced", "\u0909\u0928\u094D\u0928\u0924"),
      items: [
        { id: "agentPrompts", label: text(props.locale, "Agent Presets", "\u090F\u091C\u0947\u0902\u091F \u092A\u094D\u0930\u0940\u0938\u0947\u091F") },
        { id: "agentsControl", label: text(props.locale, "Agents & Control", "\u090F\u091C\u0947\u0902\u091F \u0935 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923") },
        ...props.skillsSlot ? [{ id: "skills", label: text(props.locale, "Skills", "\u0938\u094D\u0915\u093F\u0932\u094D\u0938") }] : [],
        ...props.knowledgeSlot ? [{ id: "knowledge", label: text(props.locale, "Knowledge", "\u0928\u0949\u0932\u0947\u091C") }] : [],
        ...props.rulesSlot ? [{ id: "rules", label: text(props.locale, "Global Rules", "\u0917\u094D\u0932\u094B\u092C\u0932 \u0928\u093F\u092F\u092E") }] : [],
        ...props.betaSlot ? [{ id: "beta", label: text(props.locale, "Beta Readiness", "\u092C\u0940\u091F\u093E \u0924\u0948\u092F\u093E\u0930\u0940") }] : []
      ]
    },
    {
      id: "admin",
      label: text(props.locale, "Administration", "\u092A\u094D\u0930\u0936\u093E\u0938\u0928"),
      items: [
        { id: "capabilities", label: text(props.locale, "Capabilities", "\u0915\u094D\u0937\u092E\u0924\u093E\u090F\u0901") },
        ...props.isSuperAdmin ? [{ id: "superAdmin", label: text(props.locale, "Super Admin", "\u0938\u0941\u092A\u0930 \u090F\u0921\u092E\u093F\u0928") }] : []
      ]
    }
  ].filter((group) => group.items.length > 0);
  const buttonStyle = (active) => ({
    width: "100%",
    padding: "9px 11px",
    border: 0,
    borderRadius: 8,
    background: active ? C.chip : "transparent",
    color: active ? C.primary : C.fg,
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    fontSize: 12,
    fontWeight: active ? 700 : 500
  });
  return /* @__PURE__ */ jsxs88(
    "section",
    {
      "data-testid": "product-settings-surface",
      className: "ss-product-settings",
      style: {
        display: "grid",
        gridTemplateColumns: "minmax(180px, 230px) minmax(0, 1fr)",
        width: "100%",
        minHeight: "100%",
        overflow: "hidden",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        background: C.card,
        color: C.fg,
        fontFamily: C.fontSans
      },
      children: [
        /* @__PURE__ */ jsxs88("nav", { style: { padding: 12, borderRight: `1px solid ${C.border}`, background: C.panel, overflowY: "auto" }, children: [
          /* @__PURE__ */ jsx96("h2", { style: { margin: "2px 8px 12px", fontSize: 17 }, children: text(props.locale, "Settings", "\u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938") }),
          /* @__PURE__ */ jsx96("div", { style: { display: "grid", gap: 14 }, children: groups.map((group) => /* @__PURE__ */ jsxs88("div", { children: [
            /* @__PURE__ */ jsx96(
              "div",
              {
                style: {
                  margin: "0 11px 5px",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: C.muted
                },
                children: group.label
              }
            ),
            /* @__PURE__ */ jsx96("div", { style: { display: "grid", gap: 3 }, children: group.items.map((item) => /* @__PURE__ */ jsx96(
              "button",
              {
                type: "button",
                style: buttonStyle(section === item.id),
                onClick: () => setSection(item.id),
                children: item.label
              },
              item.id
            )) })
          ] }, group.id)) })
        ] }),
        /* @__PURE__ */ jsxs88("div", { style: { minWidth: 0, overflow: "auto", padding: "clamp(16px, 3vw, 32px)" }, children: [
          section === "general" ? /* @__PURE__ */ jsxs88("div", { style: { display: "grid", gap: 20 }, children: [
            /* @__PURE__ */ jsxs88("header", { children: [
              /* @__PURE__ */ jsx96("h3", { style: { margin: "0 0 5px", fontSize: 19 }, children: text(props.locale, "General", "\u0938\u093E\u092E\u093E\u0928\u094D\u092F") }),
              /* @__PURE__ */ jsx96("p", { style: { margin: 0, color: C.muted, fontSize: 13 }, children: text(
                props.locale,
                "Language, version, and canonical runtime settings.",
                "\u092D\u093E\u0937\u093E, \u0938\u0902\u0938\u094D\u0915\u0930\u0923 \u0914\u0930 \u092E\u093E\u0928\u0915 \u0930\u0928\u091F\u093E\u0907\u092E \u0938\u0947\u091F\u093F\u0902\u0917\u094D\u0938\u0964"
              ) })
            ] }),
            /* @__PURE__ */ jsxs88(
              "label",
              {
                style: { display: "grid", gap: 7, maxWidth: 320, fontSize: 12, fontWeight: 700 },
                children: [
                  text(props.locale, "Language", "\u092D\u093E\u0937\u093E"),
                  /* @__PURE__ */ jsxs88(
                    "select",
                    {
                      value: props.locale,
                      disabled: !props.onLocaleChange,
                      onChange: (event) => props.onLocaleChange?.(event.target.value),
                      style: {
                        padding: "9px 10px",
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        background: C.inputBg,
                        color: C.fg
                      },
                      children: [
                        /* @__PURE__ */ jsx96("option", { value: "en", children: "English" }),
                        /* @__PURE__ */ jsx96("option", { value: "hi", children: "\u0939\u093F\u0902\u0926\u0940" })
                      ]
                    }
                  )
                ]
              }
            ),
            props.versionSlot,
            /* @__PURE__ */ jsx96(
              CanonicalSettingsControlPlane,
              {
                token: props.token,
                readOnly: props.readOnly ?? true,
                locale: props.locale,
                baseUrl: props.settingsBaseUrl,
                fetchImpl: props.settingsFetchImpl
              }
            )
          ] }) : null,
          section === "agentPrompts" ? /* @__PURE__ */ jsxs88("div", { style: { display: "grid", gap: 12, minWidth: 0 }, children: [
            /* @__PURE__ */ jsxs88("header", { children: [
              /* @__PURE__ */ jsx96("h3", { style: { margin: "0 0 5px", fontSize: 19 }, children: text(props.locale, "Agent Presets", "\u090F\u091C\u0947\u0902\u091F \u092A\u094D\u0930\u0940\u0938\u0947\u091F") }),
              /* @__PURE__ */ jsx96("p", { style: { margin: 0, color: C.muted, fontSize: 13 }, children: text(
                props.locale,
                "Shared behavioral selection for Web and Desktop.",
                "Web \u0914\u0930 Desktop \u0915\u0947 \u0932\u093F\u090F \u0938\u093E\u091D\u093E \u0935\u094D\u092F\u0935\u0939\u093E\u0930 \u091A\u092F\u0928\u0964"
              ) })
            ] }),
            /* @__PURE__ */ jsx96(
              AgentPromptSettings,
              {
                token: props.token,
                baseUrl: props.settingsBaseUrl,
                fetchImpl: props.settingsFetchImpl,
                readOnly: props.readOnly ?? true
              }
            )
          ] }) : null,
          section === "appearance" ? /* @__PURE__ */ jsxs88("div", { children: [
            /* @__PURE__ */ jsx96("h3", { style: { margin: "0 0 5px", fontSize: 19 }, children: text(props.locale, "Appearance", "\u0926\u093F\u0916\u093E\u0935\u091F") }),
            /* @__PURE__ */ jsx96("p", { style: { margin: "0 0 18px", color: C.muted, fontSize: 13 }, children: text(
              props.locale,
              "Use the same Smart Shikka theme on this surface.",
              "\u0907\u0938 \u0938\u0924\u0939 \u092A\u0930 \u0935\u0939\u0940 Smart Shikka \u0925\u0940\u092E \u0909\u092A\u092F\u094B\u0917 \u0915\u0930\u0947\u0902\u0964"
            ) }),
            /* @__PURE__ */ jsx96("div", { style: { display: "flex", flexWrap: "wrap", gap: 8 }, children: (props.themeModes ?? ["light", "dark", "system"]).map((value) => /* @__PURE__ */ jsx96(
              "button",
              {
                type: "button",
                disabled: !props.onThemeChange,
                "aria-pressed": props.theme === value,
                onClick: () => props.onThemeChange?.(value),
                style: {
                  minWidth: 92,
                  padding: "9px 12px",
                  border: `1px solid ${props.theme === value ? C.primary : C.border}`,
                  borderRadius: 9,
                  background: props.theme === value ? C.chip : C.inputBg,
                  color: props.theme === value ? C.primary : C.fg,
                  cursor: props.onThemeChange ? "pointer" : "not-allowed",
                  fontWeight: 700
                },
                children: value === "light" ? text(props.locale, "Light", "\u0932\u093E\u0907\u091F") : value === "dark" ? text(props.locale, "Dark", "\u0921\u093E\u0930\u094D\u0915") : text(props.locale, "System", "\u0938\u093F\u0938\u094D\u091F\u092E")
              },
              value
            )) }),
            /* @__PURE__ */ jsxs88("div", { style: { marginTop: 22 }, children: [
              /* @__PURE__ */ jsx96("h4", { style: { margin: "0 0 8px", fontSize: 13 }, children: text(props.locale, "Accent color", "\u090F\u0915\u094D\u0938\u0947\u0902\u091F \u0930\u0902\u0917") }),
              /* @__PURE__ */ jsx96(
                "div",
                {
                  role: "radiogroup",
                  "aria-label": text(props.locale, "Accent color", "\u090F\u0915\u094D\u0938\u0947\u0902\u091F \u0930\u0902\u0917"),
                  style: { display: "flex", flexWrap: "wrap", gap: 10 },
                  children: BRAND_ACCENTS.map((value) => /* @__PURE__ */ jsxs88(
                    "button",
                    {
                      type: "button",
                      role: "radio",
                      "aria-checked": accent === value,
                      title: value,
                      onClick: () => {
                        applyBrandAccent(value);
                        setAccent(value);
                      },
                      style: {
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "7px 12px 7px 8px",
                        border: `1px solid ${accent === value ? C.primary : C.border}`,
                        borderRadius: 999,
                        background: accent === value ? C.chip : C.inputBg,
                        color: C.fg,
                        cursor: "pointer",
                        fontWeight: accent === value ? 700 : 500,
                        fontSize: 12
                      },
                      children: [
                        /* @__PURE__ */ jsx96(
                          "span",
                          {
                            "data-accent-swatch": value,
                            style: {
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              background: `hsl(${BRAND_ACCENT_SWATCH[value]})`,
                              border: `1px solid ${C.border}`,
                              flexShrink: 0
                            }
                          }
                        ),
                        ACCENT_LABELS[value][props.locale === "hi" ? "hi" : "en"]
                      ]
                    },
                    value
                  ))
                }
              )
            ] })
          ] }) : null,
          section === "shortcuts" ? /* @__PURE__ */ jsxs88("div", { style: { display: "grid", gap: 12, maxWidth: 520 }, children: [
            /* @__PURE__ */ jsxs88("header", { children: [
              /* @__PURE__ */ jsx96("h3", { style: { margin: "0 0 5px", fontSize: 19 }, children: text(props.locale, "Keyboard Shortcuts", "\u0915\u0940\u092C\u094B\u0930\u094D\u0921 \u0936\u0949\u0930\u094D\u091F\u0915\u091F") }),
              /* @__PURE__ */ jsx96("p", { style: { margin: 0, color: C.muted, fontSize: 13 }, children: text(
                props.locale,
                "Real bindings active in the workspace. Remapping is not available yet.",
                "\u0935\u0930\u094D\u0915\u0938\u094D\u092A\u0947\u0938 \u092E\u0947\u0902 \u0938\u0915\u094D\u0930\u093F\u092F \u0905\u0938\u0932\u0940 \u092C\u093E\u0907\u0902\u0921\u093F\u0902\u0917\u0964 \u0930\u0940\u092E\u0948\u092A\u093F\u0902\u0917 \u0905\u092D\u0940 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902\u0964"
              ) })
            ] }),
            /* @__PURE__ */ jsx96("div", { style: { display: "grid", gap: 6 }, children: WORKSPACE_SHORTCUTS.map((sc) => /* @__PURE__ */ jsxs88(
              "div",
              {
                style: {
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  padding: "9px 12px",
                  border: `1px solid ${C.border}`,
                  borderRadius: 9,
                  background: C.inputBg,
                  fontSize: 13
                },
                children: [
                  /* @__PURE__ */ jsx96("span", { children: props.locale === "hi" ? sc.hi : sc.en }),
                  /* @__PURE__ */ jsx96(
                    "kbd",
                    {
                      style: {
                        padding: "3px 8px",
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        background: C.card,
                        fontFamily: "monospace",
                        fontSize: 11,
                        color: C.muted,
                        whiteSpace: "nowrap"
                      },
                      children: sc.keys
                    }
                  )
                ]
              },
              sc.id
            )) })
          ] }) : null,
          section === "account" && props.accountInfo ? /* @__PURE__ */ jsxs88("div", { style: { display: "grid", gap: 16, maxWidth: 460 }, children: [
            /* @__PURE__ */ jsxs88("header", { children: [
              /* @__PURE__ */ jsx96("h3", { style: { margin: "0 0 5px", fontSize: 19 }, children: text(props.locale, "Account", "\u0916\u093E\u0924\u093E") }),
              /* @__PURE__ */ jsx96("p", { style: { margin: 0, color: C.muted, fontSize: 13 }, children: text(
                props.locale,
                "Your signed-in identity on this Smart Shikka deployment.",
                "\u0907\u0938 Smart Shikka deployment \u092A\u0930 \u0906\u092A\u0915\u0940 \u0938\u093E\u0907\u0928-\u0907\u0928 \u092A\u0939\u091A\u093E\u0928\u0964"
              ) })
            ] }),
            /* @__PURE__ */ jsxs88(
              "div",
              {
                "data-testid": "settings-account-card",
                style: {
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: 16,
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  background: C.inputBg
                },
                children: [
                  /* @__PURE__ */ jsx96(
                    "span",
                    {
                      "aria-hidden": true,
                      style: {
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        background: C.chip,
                        color: C.primary,
                        fontWeight: 800,
                        fontSize: 18,
                        flexShrink: 0
                      },
                      children: props.accountInfo.email.trim().charAt(0).toUpperCase() || "?"
                    }
                  ),
                  /* @__PURE__ */ jsxs88("span", { style: { minWidth: 0 }, children: [
                    /* @__PURE__ */ jsx96("strong", { style: { display: "block", fontSize: 14, overflow: "hidden", textOverflow: "ellipsis" }, children: props.accountInfo.email }),
                    /* @__PURE__ */ jsxs88("small", { style: { color: C.muted }, children: [
                      text(props.locale, "Role", "\u092D\u0942\u092E\u093F\u0915\u093E"),
                      ": ",
                      props.accountInfo.role,
                      props.accountInfo.status ? ` \xB7 ${text(props.locale, "Status", "\u0938\u094D\u0925\u093F\u0924\u093F")}: ${props.accountInfo.status}` : ""
                    ] })
                  ] })
                ]
              }
            ),
            props.accountInfo.createdAt ? /* @__PURE__ */ jsxs88("p", { style: { margin: 0, color: C.muted, fontSize: 12 }, children: [
              text(props.locale, "Member since", "\u0938\u0926\u0938\u094D\u092F\u0924\u093E \u0924\u093F\u0925\u093F"),
              ": ",
              new Date(props.accountInfo.createdAt).toLocaleDateString(props.locale === "hi" ? "hi-IN" : "en-IN", { year: "numeric", month: "long", day: "numeric" })
            ] }) : null
          ] }) : null,
          section === "agentsControl" ? /* @__PURE__ */ jsxs88("div", { style: { display: "grid", gap: 12, maxWidth: 560 }, children: [
            /* @__PURE__ */ jsxs88("header", { children: [
              /* @__PURE__ */ jsx96("h3", { style: { margin: "0 0 5px", fontSize: 19 }, children: text(props.locale, "Agents & Control", "\u090F\u091C\u0947\u0902\u091F \u0935 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923") }),
              /* @__PURE__ */ jsx96("p", { style: { margin: 0, color: C.muted, fontSize: 13 }, children: text(
                props.locale,
                "Browser sessions, desktop control and live agent activity.",
                "\u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930 \u0938\u0947\u0936\u0928, \u0921\u0947\u0938\u094D\u0915\u091F\u0949\u092A \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923 \u0914\u0930 \u090F\u091C\u0947\u0902\u091F \u0915\u0940 \u0932\u093E\u0907\u0935 \u0917\u0924\u093F\u0935\u093F\u0927\u093F\u0964"
              ) })
            ] }),
            props.agentsControlSlot ? props.agentsControlSlot : /* @__PURE__ */ jsxs88("div", { style: { display: "grid", gap: 8 }, "data-testid": "agents-control-gated", children: [
              [
                {
                  title: text(props.locale, "Browser sessions", "\u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930 \u0938\u0947\u0936\u0928"),
                  desc: text(props.locale, "Brokered browser control with leases and approval pauses.", "\u0932\u0940\u091C\u093C \u0914\u0930 \u0905\u0928\u0941\u092E\u0924\u093F-\u0935\u093F\u0930\u093E\u092E \u0915\u0947 \u0938\u093E\u0925 \u092C\u094D\u0930\u094B\u0915\u0930-\u0906\u0927\u093E\u0930\u093F\u0924 \u092C\u094D\u0930\u093E\u0909\u091C\u093C\u0930 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923\u0964")
                },
                {
                  title: text(props.locale, "Desktop control", "\u0921\u0947\u0938\u094D\u0915\u091F\u0949\u092A \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923"),
                  desc: text(props.locale, "Owner-approved desktop actions with audit trail.", "\u0911\u0921\u093F\u091F \u091F\u094D\u0930\u0947\u0932 \u0915\u0947 \u0938\u093E\u0925 \u0913\u0928\u0930-\u0905\u0928\u0941\u092E\u0924 \u0921\u0947\u0938\u094D\u0915\u091F\u0949\u092A \u0915\u094D\u0930\u093F\u092F\u093E\u090F\u0901\u0964")
                },
                {
                  title: text(props.locale, "Live agent activity", "\u090F\u091C\u0947\u0902\u091F \u0915\u0940 \u0932\u093E\u0907\u0935 \u0917\u0924\u093F\u0935\u093F\u0927\u093F"),
                  desc: text(props.locale, "Real feed of every agent action \u2014 requested, approved, executed, blocked.", "\u0939\u0930 \u090F\u091C\u0947\u0902\u091F \u0915\u094D\u0930\u093F\u092F\u093E \u0915\u093E \u0935\u093E\u0938\u094D\u0924\u0935\u093F\u0915 \u092B\u093C\u0940\u0921 \u2014 \u0905\u0928\u0941\u0930\u094B\u0927\u093F\u0924, \u0905\u0928\u0941\u092E\u0924, \u0928\u093F\u0937\u094D\u092A\u093E\u0926\u093F\u0924, \u0905\u0935\u0930\u0941\u0926\u094D\u0927\u0964")
                }
              ].map((c) => /* @__PURE__ */ jsxs88(
                "div",
                {
                  style: {
                    padding: "12px 14px",
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    background: C.inputBg,
                    display: "grid",
                    gap: 4
                  },
                  children: [
                    /* @__PURE__ */ jsxs88("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }, children: [
                      /* @__PURE__ */ jsx96("span", { style: { fontSize: 14, fontWeight: 600 }, children: c.title }),
                      /* @__PURE__ */ jsx96(
                        "span",
                        {
                          style: {
                            fontSize: 11,
                            padding: "3px 8px",
                            borderRadius: 999,
                            border: `1px solid ${C.border}`,
                            color: C.muted,
                            whiteSpace: "nowrap"
                          },
                          children: text(props.locale, "Desktop app only", "\u0915\u0947\u0935\u0932 \u0921\u0947\u0938\u094D\u0915\u091F\u0949\u092A \u0910\u092A \u092E\u0947\u0902")
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsx96("span", { style: { fontSize: 12, color: C.muted }, children: c.desc })
                  ]
                },
                c.title
              )),
              /* @__PURE__ */ jsx96("p", { style: { margin: 0, color: C.muted, fontSize: 12 }, children: text(
                props.locale,
                "These controls run in the Smart Shikka desktop app. Install it to use them.",
                "\u092F\u0947 \u0928\u093F\u092F\u0902\u0924\u094D\u0930\u0923 Smart Shikka \u0921\u0947\u0938\u094D\u0915\u091F\u0949\u092A \u0910\u092A \u092E\u0947\u0902 \u091A\u0932\u0924\u0947 \u0939\u0948\u0902\u0964 \u0907\u0928\u094D\u0939\u0947\u0902 \u0909\u092A\u092F\u094B\u0917 \u0915\u0930\u0928\u0947 \u0915\u0947 \u0932\u093F\u090F \u0910\u092A \u0907\u0902\u0938\u094D\u091F\u0949\u0932 \u0915\u0930\u0947\u0902\u0964"
              ) })
            ] })
          ] }) : null,
          section === "usage" ? props.usageSlot : null,
          section === "voice" ? props.voiceSlot : null,
          section === "providers" ? props.providersSlot : null,
          section === "memory" ? props.memorySlot : null,
          section === "chatHistory" ? props.chatHistorySlot : null,
          section === "knowledge" ? props.knowledgeSlot : null,
          section === "skills" ? props.skillsSlot : null,
          section === "connections" ? props.connectionsSlot : null,
          section === "domains" ? props.domainsSlot : null,
          section === "beta" ? props.betaSlot : null,
          section === "rules" ? props.rulesSlot : null,
          section === "capabilities" || section === "superAdmin" ? /* @__PURE__ */ jsxs88("div", { children: [
            /* @__PURE__ */ jsx96("h3", { style: { margin: "0 0 5px", fontSize: 19 }, children: section === "superAdmin" ? text(props.locale, "Super Admin", "\u0938\u0941\u092A\u0930 \u090F\u0921\u092E\u093F\u0928") : text(props.locale, "Capabilities", "\u0915\u094D\u0937\u092E\u0924\u093E\u090F\u0901") }),
            /* @__PURE__ */ jsx96("p", { style: { margin: "0 0 18px", color: C.muted, fontSize: 13 }, children: text(
              props.locale,
              "Statuses reflect reachable surfaces and runtime truth.",
              "\u0938\u094D\u0925\u093F\u0924\u093F\u092F\u093E\u0901 \u0909\u092A\u0932\u092C\u094D\u0927 \u0938\u0924\u0939 \u0914\u0930 \u0930\u0928\u091F\u093E\u0907\u092E \u0938\u0924\u094D\u092F \u092A\u0930 \u0906\u0927\u093E\u0930\u093F\u0924 \u0939\u0948\u0902\u0964"
            ) }),
            props.truthSource === "local_offline" ? /* @__PURE__ */ jsx96(
              "p",
              {
                role: "note",
                "data-truth-source": "local_offline",
                style: { margin: "0 0 14px", color: C.muted, fontSize: 12 },
                children: text(
                  props.locale,
                  "Offline \u2014 server truth unreachable; states shown are this device's weaker local truth.",
                  "\u0911\u092B\u093C\u0932\u093E\u0907\u0928 \u2014 \u0938\u0930\u094D\u0935\u0930 \u0938\u0924\u094D\u092F \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902; \u0926\u093F\u0916\u093E\u0908 \u0917\u0908 \u0938\u094D\u0925\u093F\u0924\u093F\u092F\u093E\u0901 \u0907\u0938 \u0921\u093F\u0935\u093E\u0907\u0938 \u0915\u0940 \u0938\u094D\u0925\u093E\u0928\u0940\u092F (\u0915\u092E\u091C\u094B\u0930) \u0938\u0924\u094D\u092F \u0939\u0948\u0902\u0964"
                )
              }
            ) : null,
            /* @__PURE__ */ jsx96("div", { style: { display: "grid", gap: 8 }, children: (props.capabilities ?? []).filter(
              (capability) => section !== "superAdmin" || capability.id.startsWith("admin:")
            ).map((capability) => {
              const truthState = capability.truthId && props.truthEntries ? props.truthEntries.find(
                (e) => e.capabilityId === capability.truthId
              )?.state : void 0;
              const resolvedStatus = truthState ?? capability.status;
              return /* @__PURE__ */ jsxs88(
                "button",
                {
                  type: "button",
                  disabled: !capability.onOpen,
                  onClick: capability.onOpen,
                  style: {
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    gap: 12,
                    alignItems: "center",
                    padding: 12,
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    background: C.inputBg,
                    color: C.fg,
                    cursor: capability.onOpen ? "pointer" : "not-allowed",
                    textAlign: "left"
                  },
                  children: [
                    /* @__PURE__ */ jsxs88("span", { children: [
                      /* @__PURE__ */ jsx96("strong", { style: { display: "block", fontSize: 13 }, children: capability.label }),
                      /* @__PURE__ */ jsx96("small", { style: { color: C.muted }, children: capability.detail })
                    ] }),
                    /* @__PURE__ */ jsx96(
                      CapabilityStateBadge,
                      {
                        status: resolvedStatus,
                        locale: props.locale,
                        verbose: true
                      }
                    )
                  ]
                },
                capability.id
              );
            }) })
          ] }) : null
        ] })
      ]
    }
  );
}

// src/feedback/SafeErrorNotice.tsx
import { jsx as jsx97, jsxs as jsxs89 } from "react/jsx-runtime";
function SafeErrorNotice(props) {
  const { title, action } = safeErrorText(props.code, props.locale);
  return /* @__PURE__ */ jsxs89(
    "div",
    {
      role: "alert",
      className: "ss-safe-error",
      style: {
        border: "1px solid #eab308",
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 12,
        ...props.style
      },
      children: [
        /* @__PURE__ */ jsx97("strong", { children: title }),
        " ",
        /* @__PURE__ */ jsx97("code", { style: { fontSize: 11, opacity: 0.8 }, children: props.code }),
        /* @__PURE__ */ jsx97("div", { style: { marginTop: 2 }, children: action })
      ]
    }
  );
}

// src/api/client.ts
var CSRF_COOKIE_NAME3 = "smart_shikka_csrf";
var CSRF_HEADER_NAME3 = "x-smart-shikka-csrf";
function readCsrfCookie2() {
  if (typeof document === "undefined") return void 0;
  for (const part of document.cookie.split(";")) {
    const [name, ...rest] = part.trim().split("=");
    if (name === CSRF_COOKIE_NAME3) return decodeURIComponent(rest.join("="));
  }
  return void 0;
}
var ApiClient = class {
  constructor(config) {
    this.config = {
      timeout: 3e4,
      headers: {},
      ...config
    };
    this.defaultHeaders = {
      "Content-Type": "application/json",
      ...config.authToken ? { Authorization: `Bearer ${config.authToken}` } : {},
      ...config.headers
    };
  }
  createRequestHeaders(method, options, includeJsonContentType = true) {
    const headers2 = {
      ...this.defaultHeaders,
      ...options?.headers
    };
    if (!includeJsonContentType) {
      delete headers2["Content-Type"];
    }
    if (options?.requestId) {
      headers2["X-Request-Id"] = options.requestId;
    }
    if (method !== "GET") {
      const csrf = readCsrfCookie2();
      if (csrf) headers2[CSRF_HEADER_NAME3] = csrf;
    }
    return headers2;
  }
  async request(method, endpoint, body, options, serializeJson = true) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options?.timeout ?? this.config.timeout);
    const abortFromCaller = () => controller.abort();
    if (options?.signal) {
      if (options.signal.aborted) {
        controller.abort();
      } else {
        options.signal.addEventListener("abort", abortFromCaller, { once: true });
      }
    }
    try {
      const response = await fetch(`${this.config.baseUrl}${endpoint}`, {
        method,
        headers: this.createRequestHeaders(method, options, serializeJson),
        body: body ? serializeJson ? JSON.stringify(body) : body : null,
        credentials: "include",
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      options?.signal?.removeEventListener("abort", abortFromCaller);
      const data = await response.json();
      if (!response.ok) {
        const error = {
          code: data.code || data.errorCode || `HTTP_${response.status}`,
          message: data.message || data.error || "Unknown error",
          ...data.details ? { details: data.details } : {}
        };
        return {
          success: false,
          error
        };
      }
      return {
        success: true,
        data,
        ...data.message ? { message: data.message } : {}
      };
    } catch (error) {
      clearTimeout(timeoutId);
      options?.signal?.removeEventListener("abort", abortFromCaller);
      const networkError = error instanceof Error ? error : new Error("Network error");
      return {
        success: false,
        error: {
          code: "NETWORK_ERROR",
          message: networkError.name === "AbortError" ? "Request timeout" : networkError.message
        }
      };
    }
  }
  get(endpoint, options) {
    return this.request("GET", endpoint, void 0, options);
  }
  post(endpoint, body, options) {
    return this.request("POST", endpoint, body, options);
  }
  postFormData(endpoint, formData, options) {
    return this.request("POST", endpoint, formData, options, false);
  }
  put(endpoint, body, options) {
    return this.request("PUT", endpoint, body, options);
  }
  patch(endpoint, body, options) {
    return this.request("PATCH", endpoint, body, options);
  }
  delete(endpoint, options) {
    return this.request("DELETE", endpoint, void 0, options);
  }
};
var createApiClient = (config) => new ApiClient(config);

// src/api/chat.ts
var ChatService = class {
  constructor(client) {
    this.client = client;
  }
  async sendMessage(request) {
    return this.client.post("/api/chat", request);
  }
  async getMessages(conversationId, page = 1, pageSize = 50) {
    return this.client.get(`/api/chat/conversations/${conversationId}/messages?page=${page}&page_size=${pageSize}`);
  }
  async createConversation(title) {
    return this.client.post("/api/chat/conversations", { title });
  }
  async listConversations(page = 1, pageSize = 20) {
    return this.client.get(`/api/chat/conversations?page=${page}&page_size=${pageSize}`);
  }
  async deleteConversation(conversationId) {
    return this.client.delete(`/api/chat/conversations/${conversationId}`);
  }
};
var createChatService = (client) => new ChatService(client);

// src/api/workflow.ts
var WorkflowService = class {
  constructor(client) {
    this.client = client;
  }
  async listWorkflows(page = 1, pageSize = 20) {
    return this.client.get(`/api/workflows?page=${page}&page_size=${pageSize}`);
  }
  async getWorkflow(id) {
    return this.client.get(`/api/workflows/${id}`);
  }
  async createWorkflow(data) {
    return this.client.post("/api/workflows", data);
  }
  async updateWorkflow(id, data) {
    return this.client.put(`/api/workflows/${id}`, data);
  }
  async deleteWorkflow(id) {
    return this.client.delete(`/api/workflows/${id}`);
  }
  async dryRunWorkflow(id, inputs) {
    return this.client.post(`/api/workflows/${id}/dry-run`, { inputs });
  }
  async requestWorkflowRunApproval(id, inputs) {
    return this.client.post(`/api/workflows/${id}/run-approval`, { inputs });
  }
  async runWorkflow(id, inputs, options) {
    if (!options?.approvalToken && options?.executionAllowed !== true) {
      return {
        success: false,
        error: {
          code: "APPROVAL_REQUIRED",
          message: "Workflow execution requires an explicit approval token or executionAllowed=true."
        }
      };
    }
    return this.client.post(`/api/workflows/${id}/run`, {
      inputs,
      approval_token: options.approvalToken,
      execution_allowed: options.executionAllowed === true
    });
  }
  async exportWorkflow(id) {
    return this.client.get(`/api/workflows/${id}/export`);
  }
};
var createWorkflowService = (client) => new WorkflowService(client);

// src/api/voice.ts
async function blobToBase64(blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 32768) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 32768));
  }
  return btoa(binary);
}
var VoiceService = class {
  constructor(client) {
    this.client = client;
  }
  async grantConsent(sessionId) {
    return this.client.post("/api/voice/consent", { sessionId });
  }
  async transcribe(audioBlob, language = "hi-IN", sessionId, durationSeconds = 1) {
    if (!sessionId) {
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");
      formData.append("language", language);
      return this.client.postFormData("/api/voice/transcribe", formData);
    }
    return this.client.post("/api/voice/transcribe", {
      audioBase64: await blobToBase64(audioBlob),
      language,
      sessionId,
      durationSeconds
    });
  }
  async synthesize(text2, voiceId, sessionId = "voice-session", signal) {
    return this.client.post("/api/voice/synthesize", {
      text: text2,
      voiceProfile: voiceId,
      sessionId
    }, signal ? { signal } : void 0);
  }
  async turn(audioBlob, options) {
    return this.client.post("/api/voice/turn", {
      audioBase64: await blobToBase64(audioBlob),
      language: options.language,
      locale: options.language?.toLowerCase().startsWith("hi") ? "hi" : "en",
      sessionId: options.sessionId,
      durationSeconds: options.durationSeconds,
      ...options.conversationId !== void 0 ? { conversationId: options.conversationId } : {},
      ...options.idempotencyKey !== void 0 ? { idempotencyKey: options.idempotencyKey } : {}
    }, options.signal ? { signal: options.signal } : void 0);
  }
  async listVoices() {
    const response = await this.client.get("/api/voice/voices");
    if (!response.success || !response.data) {
      return response;
    }
    return { ...response, data: response.data.voices ?? [] };
  }
};
var createVoiceService = (client) => new VoiceService(client);

// src/logging/metrics.ts
var MetricsCollector = class _MetricsCollector {
  constructor() {
    this.metrics = /* @__PURE__ */ new Map();
    this.counters = {};
    this.gauges = {};
    this.timers = {};
  }
  static getInstance() {
    if (!_MetricsCollector.instance) {
      _MetricsCollector.instance = new _MetricsCollector();
    }
    return _MetricsCollector.instance;
  }
  recordCounter(name, value = 1, tags) {
    this.counters[name] = (this.counters[name] ?? 0) + value;
    this.metrics.set(name, this.createMetric(name, this.counters[name], "count", "counter", tags));
  }
  recordGauge(name, value, unit = "value", tags) {
    this.gauges[name] = value;
    this.metrics.set(name, this.createMetric(name, value, unit, "gauge", tags));
  }
  recordTimer(name, durationMs, tags) {
    this.timers[name] = [...this.timers[name] ?? [], durationMs];
    this.metrics.set(name, this.createMetric(name, durationMs, "ms", "timer", tags));
  }
  recordCacheStatus(status) {
    this.cacheStatus = status;
    this.recordCounter(`cache_${status}`, 1);
  }
  recordFallbackUsed() {
    this.recordCounter("fallbackUsed", 1);
  }
  recordProviderLatency(durationMs) {
    this.recordTimer("providerLatencyMs", durationMs);
  }
  recordVoiceRecordingError() {
    this.recordCounter("voiceRecordingErrorCount", 1);
  }
  recordRequest() {
    this.recordCounter("requestCount", 1);
  }
  recordError() {
    this.recordCounter("errorCount", 1);
  }
  getAllMetrics() {
    return Array.from(this.metrics.values());
  }
  getSnapshot() {
    return {
      counters: { ...this.counters },
      gauges: { ...this.gauges },
      timers: Object.fromEntries(Object.entries(this.timers).map(([key, value]) => [key, [...value]])),
      cacheStatus: this.cacheStatus,
      fallbackUsed: (this.counters.fallbackUsed ?? 0) > 0,
      providerLatencyMs: [...this.timers.providerLatencyMs ?? []],
      voiceRecordingErrorCount: this.counters.voiceRecordingErrorCount ?? 0,
      requestCount: this.counters.requestCount ?? 0,
      errorCount: this.counters.errorCount ?? 0
    };
  }
  reset() {
    this.metrics.clear();
    this.counters = {};
    this.gauges = {};
    this.timers = {};
    this.cacheStatus = void 0;
  }
  createMetric(name, value, unit, kind, tags) {
    return {
      name,
      value,
      unit,
      kind,
      timestamp: /* @__PURE__ */ new Date(),
      ...tags ? { tags } : {}
    };
  }
};
var metrics = MetricsCollector.getInstance();

// src/logging/audit.ts
var AuditService = class _AuditService {
  constructor() {
    this.entries = [];
    this.redactor = defaultRedactor;
  }
  static getInstance() {
    if (!_AuditService.instance) {
      _AuditService.instance = new _AuditService();
    }
    return _AuditService.instance;
  }
  configure(redactor) {
    this.redactor = redactor;
  }
  generateId() {
    return `audit_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }
  log(inputOrUserId, action, resource, resourceId, details) {
    const input = typeof inputOrUserId === "string" ? {
      userId: inputOrUserId,
      action: action ?? "READ",
      resource: resource ?? "unknown",
      ...resourceId ? { resourceId } : {},
      ...details ? { details } : {}
    } : inputOrUserId;
    const safeDetails = this.redactor.redactRecord(input.details);
    const entry = {
      id: this.generateId(),
      userId: input.userId,
      action: input.action,
      resource: input.resource,
      ...input.resourceId ? { resourceId: input.resourceId } : {},
      riskLevel: input.riskLevel ?? "low",
      approvalRequired: input.approvalRequired ?? false,
      consentState: input.consentState ?? "not_required",
      details: safeDetails.value,
      redactions: safeDetails.redactions,
      timestamp: /* @__PURE__ */ new Date()
    };
    this.entries.push(entry);
    return entry;
  }
  getEntries(filters) {
    return this.entries.filter((entry) => {
      if (filters?.userId && entry.userId !== filters.userId) return false;
      if (filters?.action && entry.action !== filters.action) return false;
      if (filters?.resource && entry.resource !== filters.resource) return false;
      return true;
    });
  }
  exportToJson() {
    return JSON.stringify(this.entries, null, 2);
  }
  clear() {
    this.entries = [];
  }
};
var audit = AuditService.getInstance();

// src/monitoring/dashboard.tsx
import { useEffect as useEffect45, useMemo as useMemo25, useState as useState51 } from "react";
import { jsx as jsx98, jsxs as jsxs90 } from "react/jsx-runtime";
var getLogLevelName = (level) => ["DEBUG", "INFO", "WARN", "ERROR", "FATAL"][level];
var getLogLevelColor = (level) => ["#64748b", "#60a5fa", "#fbbf24", "#f87171", "#dc2626"][level];
var summarizeLog = (log) => ({
  timestamp: log.timestamp,
  level: log.level,
  levelName: log.levelName,
  message: log.message,
  redactions: log.redactions,
  source: log.source
});
var MonitoringDashboard = ({
  adminOnly = false,
  refreshInterval = 5e3,
  searchQuery = ""
}) => {
  const [metricsData, setMetricsData] = useState51([]);
  const [logs, setLogs] = useState51([]);
  const [activeTab, setActiveTab] = useState51("metrics");
  useEffect45(() => {
    if (!adminOnly) return;
    const updateData = () => {
      setMetricsData(metrics.getAllMetrics());
      setLogs(logger.getLogs().map(summarizeLog));
    };
    updateData();
    const interval = setInterval(updateData, refreshInterval);
    return () => clearInterval(interval);
  }, [adminOnly, refreshInterval]);
  const filteredLogs = useMemo25(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return logs;
    return logs.filter(
      (log) => [log.levelName, log.message, log.source, log.redactions.join(" ")].filter(Boolean).some((field) => field?.toLowerCase().includes(query))
    );
  }, [logs, searchQuery]);
  if (!adminOnly) {
    return /* @__PURE__ */ jsxs90("div", { style: styles19.container, "aria-live": "polite", children: [
      /* @__PURE__ */ jsx98("h2", { style: styles19.title, children: "\u{1F4CA} Smart Shikka Monitor" }),
      /* @__PURE__ */ jsx98("p", { style: styles19.disabled, children: "Monitoring dashboard disabled. Admin access is required." })
    ] });
  }
  return /* @__PURE__ */ jsxs90("div", { style: styles19.container, children: [
    /* @__PURE__ */ jsxs90("div", { style: styles19.header, children: [
      /* @__PURE__ */ jsx98("h2", { style: styles19.title, children: "\u{1F4CA} Smart Shikka Monitor" }),
      /* @__PURE__ */ jsxs90("div", { style: styles19.tabs, children: [
        /* @__PURE__ */ jsx98("button", { type: "button", style: { ...styles19.tab, ...activeTab === "metrics" ? styles19.activeTab : {} }, onClick: () => setActiveTab("metrics"), children: "Metrics" }),
        /* @__PURE__ */ jsx98("button", { type: "button", style: { ...styles19.tab, ...activeTab === "logs" ? styles19.activeTab : {} }, onClick: () => setActiveTab("logs"), children: "Logs" })
      ] })
    ] }),
    activeTab === "metrics" && /* @__PURE__ */ jsx98("div", { style: styles19.content, children: metricsData.length === 0 ? /* @__PURE__ */ jsx98("p", { style: styles19.empty, children: "No metrics recorded yet" }) : /* @__PURE__ */ jsx98("div", { style: styles19.metricsGrid, children: metricsData.map((metric) => /* @__PURE__ */ jsxs90("div", { style: styles19.metricCard, children: [
      /* @__PURE__ */ jsx98("div", { style: styles19.metricName, children: metric.name }),
      /* @__PURE__ */ jsxs90("div", { style: styles19.metricValue, children: [
        metric.value,
        " ",
        /* @__PURE__ */ jsx98("span", { style: styles19.metricUnit, children: metric.unit })
      ] })
    ] }, `${metric.name}-${metric.kind}`)) }) }),
    activeTab === "logs" && /* @__PURE__ */ jsx98("div", { style: styles19.content, children: filteredLogs.length === 0 ? /* @__PURE__ */ jsx98("p", { style: styles19.empty, children: "No safe log summaries recorded yet" }) : /* @__PURE__ */ jsx98("div", { style: styles19.logsList, children: filteredLogs.slice(-50).reverse().map((log, index) => /* @__PURE__ */ jsxs90("div", { style: styles19.logEntry, children: [
      /* @__PURE__ */ jsxs90("span", { style: { ...styles19.logLevel, color: getLogLevelColor(log.level) }, children: [
        "[",
        getLogLevelName(log.level),
        "]"
      ] }),
      /* @__PURE__ */ jsx98("span", { style: styles19.logMessage, children: log.message }),
      log.redactions.length > 0 && /* @__PURE__ */ jsx98("span", { style: styles19.redactions, children: log.redactions.join(", ") })
    ] }, `${log.timestamp.toISOString()}-${index}`)) }) })
  ] });
};
var styles19 = {
  container: { backgroundColor: "#1e293b", borderRadius: "12px", padding: "20px", fontFamily: "'Segoe UI', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
  title: { margin: 0, fontSize: "18px", fontWeight: 600, color: "#f1f5f9" },
  disabled: { color: "#94a3b8", margin: "16px 0 0" },
  tabs: { display: "flex", gap: "8px" },
  tab: { padding: "8px 16px", backgroundColor: "transparent", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "6px", color: "#94a3b8", fontSize: "14px", cursor: "pointer" },
  activeTab: { backgroundColor: "#60a5fa", color: "#0f172a", borderColor: "#60a5fa" },
  content: { minHeight: "200px" },
  empty: { color: "#64748b", textAlign: "center", padding: "40px" },
  metricsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "12px" },
  metricCard: { backgroundColor: "#0f172a", borderRadius: "8px", padding: "16px" },
  metricName: { fontSize: "12px", color: "#94a3b8", marginBottom: "8px" },
  metricValue: { fontSize: "24px", fontWeight: 600, color: "#f1f5f9" },
  metricUnit: { fontSize: "12px", color: "#64748b" },
  logsList: { display: "flex", flexDirection: "column", gap: "4px", maxHeight: "400px", overflowY: "auto" },
  logEntry: { display: "flex", gap: "12px", padding: "8px", backgroundColor: "#0f172a", borderRadius: "4px", fontSize: "12px" },
  logLevel: { fontWeight: 600, minWidth: "60px" },
  logMessage: { color: "#f1f5f9", wordBreak: "break-word" },
  redactions: { color: "#94a3b8", marginLeft: "auto" }
};

// src/cost-usage/CostUsagePanel.tsx
import { useEffect as useEffect46, useMemo as useMemo26, useState as useState52 } from "react";
import { Fragment as Fragment13, jsx as jsx99, jsxs as jsxs91 } from "react/jsx-runtime";
var emptySummary = {
  totalTokens: 0,
  inputTokens: 0,
  outputTokens: 0,
  modelCalls: 0,
  toolCalls: 0,
  estimatedCostUsd: 0,
  retries: 0,
  fallbacks: 0,
  parentRuns: []
};
function rangeStart(preset) {
  if (preset === "custom") return void 0;
  const now = /* @__PURE__ */ new Date();
  if (preset === "today") {
    now.setHours(0, 0, 0, 0);
  } else {
    now.setDate(now.getDate() - (preset === "7d" ? 7 : 30));
  }
  return now.toISOString();
}
function buildUsageUrl(apiBaseUrl, filters) {
  const params = new URLSearchParams();
  const from = filters.datePreset === "custom" ? filters.customFrom : rangeStart(filters.datePreset);
  if (from) params.set("from", from);
  if (filters.datePreset === "custom" && filters.customTo) {
    params.set("to", filters.customTo);
  }
  for (const [key, value] of Object.entries(filters)) {
    if (key !== "datePreset" && key !== "customFrom" && key !== "customTo" && value) {
      params.set(key, value);
    }
  }
  return `${apiBaseUrl}/api/usage?${params.toString()}`;
}
var CostUsagePanel = ({
  locale = "en",
  apiBaseUrl = "",
  token,
  fetchImpl = globalThis.fetch,
  initialResponse
}) => {
  const [data, setData] = useState52(initialResponse);
  const [error, setError] = useState52();
  const [datePreset, setDatePreset] = useState52("7d");
  const [customFrom, setCustomFrom] = useState52("");
  const [customTo, setCustomTo] = useState52("");
  const [surface, setSurface] = useState52("");
  const [requestedMode, setRequestedMode] = useState52("");
  const [effectiveMode, setEffectiveMode] = useState52("");
  const [providerId, setProviderId] = useState52("");
  const [modelId, setModelId] = useState52("");
  const [projectId, setProjectId] = useState52("");
  const [agentRole, setAgentRole] = useState52("");
  const filters = useMemo26(() => ({
    datePreset,
    customFrom,
    customTo,
    surface,
    requestedMode,
    effectiveMode,
    providerId,
    modelId,
    projectId,
    agentRole
  }), [
    datePreset,
    customFrom,
    customTo,
    surface,
    requestedMode,
    effectiveMode,
    providerId,
    modelId,
    projectId,
    agentRole
  ]);
  useEffect46(() => {
    if (initialResponse || !fetchImpl) return;
    const controller = new AbortController();
    void fetchImpl(buildUsageUrl(apiBaseUrl, filters), {
      credentials: "include",
      headers: {
        accept: "application/json",
        ...token ? { authorization: `Bearer ${token}` } : {}
      },
      signal: controller.signal
    }).then(async (response) => {
      if (!response.ok) throw new Error(`usage_request_failed:${response.status}`);
      return response.json();
    }).then((payload) => {
      setData(payload);
      setError(void 0);
    }).catch((requestError) => {
      if (requestError instanceof Error && requestError.name === "AbortError") return;
      setError(locale === "hi" ? "\u0909\u092A\u092F\u094B\u0917 \u0921\u0947\u091F\u093E \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964" : "Usage data is unavailable.");
    });
    return () => controller.abort();
  }, [apiBaseUrl, fetchImpl, filters, initialResponse, locale, token]);
  const summary = data?.summary ?? emptySummary;
  const labels = locale === "hi" ? {
    title: "\u0932\u093E\u0917\u0924 \u0914\u0930 \u0909\u092A\u092F\u094B\u0917",
    total: "\u0915\u0941\u0932 \u091F\u094B\u0915\u0928",
    input: "\u0907\u0928\u092A\u0941\u091F \u091F\u094B\u0915\u0928",
    output: "\u0906\u0909\u091F\u092A\u0941\u091F \u091F\u094B\u0915\u0928",
    calls: "\u092E\u0949\u0921\u0932 \u0915\u0949\u0932",
    tools: "\u091F\u0942\u0932 \u0915\u0949\u0932",
    cost: "\u0905\u0928\u0941\u092E\u093E\u0928\u093F\u0924 \u0932\u093E\u0917\u0924",
    savings: "\u092E\u093E\u092A\u0940 \u0917\u0908 \u091F\u094B\u0915\u0928 \u092C\u091A\u0924",
    retries: "\u092A\u0941\u0928\u0903 \u092A\u094D\u0930\u092F\u093E\u0938",
    fallbacks: "\u092B\u0949\u0932\u092C\u0948\u0915",
    all: "\u0938\u092D\u0940",
    parentRuns: "Parent run \u0914\u0930 child stages",
    unavailable: "\u0930\u093F\u092A\u094B\u0930\u094D\u091F \u0928\u0939\u0940\u0902 \u0939\u0941\u0908"
  } : {
    title: "Cost & Usage",
    total: "Total tokens",
    input: "Input tokens",
    output: "Output tokens",
    calls: "Model calls",
    tools: "Tool calls",
    cost: "Estimated cost",
    savings: "Measured token savings",
    retries: "Retries",
    fallbacks: "Fallbacks",
    all: "All",
    parentRuns: "Parent runs and child stages",
    unavailable: "Not reported"
  };
  const select = (label, value, setter, values) => /* @__PURE__ */ jsxs91("label", { style: { display: "grid", gap: 4, fontSize: 12 }, children: [
    label,
    /* @__PURE__ */ jsxs91("select", { value, onChange: (event) => setter(event.target.value), children: [
      /* @__PURE__ */ jsx99("option", { value: "", children: labels.all }),
      [...new Set(values)].filter(Boolean).sort().map((item) => /* @__PURE__ */ jsx99("option", { value: item, children: item }, item))
    ] })
  ] });
  const cards = [
    [labels.total, summary.totalTokens],
    [labels.input, summary.inputTokens],
    [labels.output, summary.outputTokens],
    [labels.calls, summary.modelCalls],
    [labels.tools, summary.toolCalls],
    [labels.cost, `$${summary.estimatedCostUsd.toFixed(4)}`],
    [labels.retries, summary.retries],
    [labels.fallbacks, summary.fallbacks],
    [labels.savings, labels.unavailable]
  ];
  return /* @__PURE__ */ jsxs91("section", { style: { padding: 16, fontFamily: "sans-serif" }, children: [
    /* @__PURE__ */ jsx99("h2", { children: labels.title }),
    error ? /* @__PURE__ */ jsx99("div", { role: "alert", children: error }) : null,
    /* @__PURE__ */ jsx99("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }, children: cards.map(([label, value]) => /* @__PURE__ */ jsxs91("div", { style: { padding: 12, border: "1px solid #e2e8f0", borderRadius: 6 }, children: [
      /* @__PURE__ */ jsx99("div", { style: { fontSize: 12, color: "#64748b" }, children: label }),
      /* @__PURE__ */ jsx99("strong", { children: value })
    ] }, label)) }),
    /* @__PURE__ */ jsxs91("div", { style: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginTop: 16 }, children: [
      /* @__PURE__ */ jsxs91("label", { style: { display: "grid", gap: 4, fontSize: 12 }, children: [
        locale === "hi" ? "\u0905\u0935\u0927\u093F" : "Range",
        /* @__PURE__ */ jsxs91("select", { value: datePreset, onChange: (event) => setDatePreset(event.target.value), children: [
          /* @__PURE__ */ jsx99("option", { value: "today", children: locale === "hi" ? "\u0906\u091C" : "Today" }),
          /* @__PURE__ */ jsx99("option", { value: "7d", children: locale === "hi" ? "7 \u0926\u093F\u0928" : "7 days" }),
          /* @__PURE__ */ jsx99("option", { value: "30d", children: locale === "hi" ? "30 \u0926\u093F\u0928" : "30 days" }),
          /* @__PURE__ */ jsx99("option", { value: "custom", children: locale === "hi" ? "\u0915\u0938\u094D\u091F\u092E" : "Custom" })
        ] })
      ] }),
      datePreset === "custom" ? /* @__PURE__ */ jsxs91(Fragment13, { children: [
        /* @__PURE__ */ jsx99("input", { "aria-label": "from", type: "datetime-local", value: customFrom, onChange: (event) => setCustomFrom(event.target.value) }),
        /* @__PURE__ */ jsx99("input", { "aria-label": "to", type: "datetime-local", value: customTo, onChange: (event) => setCustomTo(event.target.value) })
      ] }) : null,
      select(locale === "hi" ? "\u0938\u0924\u0939" : "Surface", surface, setSurface, data?.events.map((item) => item.surface) ?? []),
      select("Requested mode", requestedMode, setRequestedMode, data?.events.map((item) => item.requestedMode) ?? []),
      select("Effective mode", effectiveMode, setEffectiveMode, data?.events.map((item) => item.effectiveMode) ?? []),
      select("Provider", providerId, setProviderId, data?.events.map((item) => item.providerId) ?? []),
      select("Model", modelId, setModelId, data?.events.map((item) => item.modelId) ?? []),
      select(locale === "hi" ? "\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F" : "Project", projectId, setProjectId, data?.events.map((item) => item.projectId ?? "") ?? []),
      select(locale === "hi" ? "\u090F\u091C\u0947\u0902\u091F \u092D\u0942\u092E\u093F\u0915\u093E" : "Agent role", agentRole, setAgentRole, data?.events.map((item) => item.agentRole ?? "") ?? [])
    ] }),
    /* @__PURE__ */ jsx99("h3", { children: labels.parentRuns }),
    summary.parentRuns.map((parent) => /* @__PURE__ */ jsxs91("details", { children: [
      /* @__PURE__ */ jsxs91("summary", { children: [
        parent.parentRunId,
        " \xB7 ",
        parent.totalTokens,
        " tokens \xB7 ",
        parent.modelCalls,
        " calls"
      ] }),
      /* @__PURE__ */ jsxs91("table", { style: { width: "100%", fontSize: 12 }, children: [
        /* @__PURE__ */ jsx99("thead", { children: /* @__PURE__ */ jsxs91("tr", { children: [
          /* @__PURE__ */ jsx99("th", { children: "Stage/role" }),
          /* @__PURE__ */ jsx99("th", { children: "Mode" }),
          /* @__PURE__ */ jsx99("th", { children: "Provider/model" }),
          /* @__PURE__ */ jsx99("th", { children: "Tokens" }),
          /* @__PURE__ */ jsx99("th", { children: "Retry/fallback" }),
          /* @__PURE__ */ jsx99("th", { children: "Measurement" })
        ] }) }),
        /* @__PURE__ */ jsx99("tbody", { children: parent.children.map((child) => /* @__PURE__ */ jsxs91("tr", { children: [
          /* @__PURE__ */ jsxs91("td", { children: [
            child.stageId ?? child.runId,
            " / ",
            child.agentRole ?? "\u2014"
          ] }),
          /* @__PURE__ */ jsxs91("td", { children: [
            child.requestedMode,
            " \u2192 ",
            child.effectiveMode
          ] }),
          /* @__PURE__ */ jsxs91("td", { children: [
            child.providerId,
            " / ",
            child.modelId
          ] }),
          /* @__PURE__ */ jsxs91("td", { children: [
            child.inputTokens,
            " + ",
            child.outputTokens,
            " = ",
            child.totalTokens
          ] }),
          /* @__PURE__ */ jsxs91("td", { children: [
            child.retryAttempt,
            " / ",
            child.fallbackUsed ? "yes" : "no"
          ] }),
          /* @__PURE__ */ jsx99("td", { children: child.usageMeasurement })
        ] }, child.usageEventId)) })
      ] })
    ] }, parent.parentRunId))
  ] });
};

// src/domains/domainsService.ts
var COOKIE_SESSION_TOKEN2 = "__smart_shikka_cookie_session__";
var CSRF_COOKIE_NAME4 = "smart_shikka_csrf";
var CSRF_HEADER_NAME4 = "x-smart-shikka-csrf";
function readCookie2(name) {
  if (typeof document === "undefined") return void 0;
  for (const part of document.cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return decodeURIComponent(rest.join("="));
  }
  return void 0;
}
var DomainsApiError = class extends Error {
  constructor(status, body) {
    super(typeof body.error === "string" ? body.error : `Domains request failed (${status}).`);
    this.status = status;
    this.errorCode = typeof body.errorCode === "string" ? body.errorCode : "domain_request_failed";
    this.domain = body.domain && typeof body.domain === "object" ? body.domain : void 0;
  }
};
function createDomainsService(options) {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = options.baseUrl ?? "";
  const cookieSession = options.token === COOKIE_SESSION_TOKEN2;
  const headers2 = (mutating = false) => {
    const next = { "Content-Type": "application/json" };
    if (!cookieSession) next.Authorization = `Bearer ${options.token}`;
    if (cookieSession && mutating) {
      const csrf = readCookie2(CSRF_COOKIE_NAME4);
      if (csrf) next[CSRF_HEADER_NAME4] = csrf;
    }
    return next;
  };
  const credentials = cookieSession ? "include" : "same-origin";
  async function call(method, path, body, signal) {
    const mutating = method !== "GET";
    const response = await fetchImpl(`${baseUrl}/api${path}`, {
      method,
      headers: headers2(mutating),
      credentials,
      ...body !== void 0 ? { body: JSON.stringify(body) } : {},
      ...signal ? { signal } : {}
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.success === false || payload.ok === false) {
      throw new DomainsApiError(response.status, payload);
    }
    return payload;
  }
  return {
    async listProjects(signal) {
      const body = await call(
        "GET",
        "/projects",
        void 0,
        signal
      );
      return (Array.isArray(body.projects) ? body.projects : []).filter((p) => typeof p.projectId === "string").map((p) => ({
        projectId: String(p.projectId),
        name: typeof p.name === "string" ? p.name : void 0
      }));
    },
    async list(projectId, signal) {
      const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
      const body = await call("GET", `/domains${query}`, void 0, signal);
      return Array.isArray(body.domains) ? body.domains : [];
    },
    async create(input) {
      const body = await call("POST", "/domains", input);
      return body.domain;
    },
    async verify(domainId) {
      const body = await call(
        "POST",
        `/domains/${encodeURIComponent(domainId)}/verify`,
        {}
      );
      return body.domain;
    },
    async activate(domainId) {
      const body = await call(
        "POST",
        `/domains/${encodeURIComponent(domainId)}/activate`,
        {}
      );
      return body.domain;
    },
    async setCanonical(domainId) {
      const body = await call(
        "POST",
        `/domains/${encodeURIComponent(domainId)}/canonical`,
        {}
      );
      return body.domain;
    },
    async setRedirectPolicy(domainId, redirectPolicy) {
      const body = await call(
        "PATCH",
        `/domains/${encodeURIComponent(domainId)}`,
        { redirectPolicy }
      );
      return body.domain;
    },
    async detach(domainId) {
      const body = await call(
        "POST",
        `/domains/${encodeURIComponent(domainId)}/detach`,
        {}
      );
      return { domain: body.domain, routingCleanup: body.routingCleanup };
    },
    async outputUrls(projectId, signal) {
      const body = await call(
        "GET",
        `/projects/${encodeURIComponent(projectId)}/output-urls`,
        void 0,
        signal
      );
      return body;
    }
  };
}

// src/domains/DomainsPanel.tsx
import { useCallback as useCallback32, useEffect as useEffect47, useMemo as useMemo27, useState as useState53 } from "react";
import { Fragment as Fragment14, jsx as jsx100, jsxs as jsxs92 } from "react/jsx-runtime";
var STATUS_LABEL_HI = {
  pending: "\u0938\u0924\u094D\u092F\u093E\u092A\u0928 \u092C\u093E\u0915\u0940",
  verified: "\u0938\u0924\u094D\u092F\u093E\u092A\u093F\u0924",
  tls_provisioning: "TLS \u0924\u0948\u092F\u093E\u0930 \u0939\u094B \u0930\u0939\u093E",
  active: "\u0938\u0915\u094D\u0930\u093F\u092F",
  failed: "\u0935\u093F\u092B\u0932",
  detached: "\u0921\u093F\u091F\u0948\u091A\u094D\u0921"
};
var STATUS_LABEL_EN = {
  pending: "Pending verification",
  verified: "Verified",
  tls_provisioning: "TLS provisioning",
  active: "Active",
  failed: "Failed",
  detached: "Detached"
};
var ERROR_LABEL_HI = {
  txt_mismatch: "DNS TXT record \u0905\u092D\u0940 match \u0928\u0939\u0940\u0902 \u0939\u0941\u0906 \u2014 record propagate \u0939\u094B\u0915\u0930 \u092B\u093F\u0930 verify \u0915\u0930\u0947\u0902\u0964",
  tls_unreachable: "HTTPS/TLS \u0905\u092D\u0940 reachable \u0928\u0939\u0940\u0902 \u2014 cert/CDN \u0915\u0947 \u092C\u093E\u0926 activate \u0915\u0930\u0947\u0902\u0964",
  tls_timeout: "TLS probe timeout \u2014 \u092C\u093E\u0926 \u092E\u0947\u0902 retry \u0915\u0930\u0947\u0902\u0964",
  dns_no_records: "Domain \u0915\u093E DNS \u0905\u092D\u0940 resolve \u0928\u0939\u0940\u0902 \u0939\u094B \u0930\u0939\u093E\u0964",
  dns_private_denied: "Domain internal/private address \u092A\u0930 resolve \u0939\u094B\u0924\u093E \u0939\u0948 \u2014 denied\u0964",
  domain_route_unproven: "Ownership verified \u0939\u0948, \u092A\u0930 Smart Shikka routing \u0905\u092D\u0940 \u0938\u093F\u0926\u094D\u0927 \u0928\u0939\u0940\u0902 \u2014 gateway binding configured/proven \u0928\u0939\u0940\u0902\u0964",
  route_unbound: "Smart Shikka route binding \u0938\u093F\u0926\u094D\u0927 \u0928\u0939\u0940\u0902\u0964",
  route_scope_mismatch: "Route binding \u0907\u0938 project/scope \u0938\u0947 match \u0928\u0939\u0940\u0902 \u0939\u0941\u0908\u0964",
  gateway_not_configured: "Smart Shikka domain gateway \u0905\u092D\u0940 configured \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
  domain_invalid: "\u092F\u0939 hostname \u092E\u093E\u0928\u094D\u092F \u0928\u0939\u0940\u0902 \u0939\u0948 \u2014 \u0938\u0939\u0940 domain \u0932\u093F\u0916\u0947\u0902 (\u091C\u0948\u0938\u0947 app.example.com)\u0964",
  domain_ip_denied: "IP address domain \u0915\u0947 \u0930\u0942\u092A \u092E\u0947\u0902 allowed \u0928\u0939\u0940\u0902 \u2014 DNS hostname \u0932\u093F\u0916\u0947\u0902\u0964",
  domain_localhost_denied: "localhost/local hostname custom domain \u0915\u0947 \u0930\u0942\u092A \u092E\u0947\u0902 allowed \u0928\u0939\u0940\u0902\u0964",
  domain_reserved: "\u092F\u0939 reserved/\u0909\u0924\u094D\u092A\u093E\u0926-\u0915\u094D\u0937\u0947\u0924\u094D\u0930 hostname \u0939\u0948 \u2014 \u0905\u092A\u0928\u093E domain \u0932\u093F\u0916\u0947\u0902\u0964",
  domain_wildcard_denied: "Wildcard (*) hostname allowed \u0928\u0939\u0940\u0902 \u2014 \u092A\u0942\u0930\u093E hostname \u0932\u093F\u0916\u0947\u0902\u0964",
  domain_claim_denied: "\u092F\u0939 domain \u092A\u0939\u0932\u0947 \u0938\u0947 \u0915\u093F\u0938\u0940 \u0905\u0928\u094D\u092F \u0926\u094D\u0935\u093E\u0930\u093E claim \u0939\u0948 \u2014 bind \u0928\u0939\u0940\u0902 \u0939\u094B \u0938\u0915\u0924\u093E\u0964",
  domain_not_found: "Domain record \u0928\u0939\u0940\u0902 \u092E\u093F\u0932\u093E \u2014 \u092A\u0939\u0932\u0947 domain \u091C\u094B\u0921\u093C\u0947\u0902\u0964",
  domain_not_verified: "DNS TXT verification \u0905\u092D\u0940 pending \u0939\u0948\u0964",
  domain_verification_failed: "DNS verification \u0935\u093F\u092B\u0932 \u2014 TXT record \u091C\u093E\u0901\u091A\u0915\u0930 \u092B\u093F\u0930 verify \u0915\u0930\u0947\u0902\u0964",
  domain_not_active: "Domain \u0905\u092D\u0940 active \u0928\u0939\u0940\u0902 \u2014 \u092A\u0939\u0932\u0947 verify + route proof \u092A\u0942\u0930\u093E \u0915\u0930\u0947\u0902\u0964",
  domain_tls_unreachable: "Domain \u092A\u0930 TLS \u0905\u092D\u0940 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902\u0964",
  domain_project_denied: "\u0907\u0938 project \u092A\u0930 domain action allowed \u0928\u0939\u0940\u0902\u0964",
  project_denied: "\u0907\u0938 project \u0924\u0915 \u092A\u0939\u0941\u0901\u091A \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
  project_id_required: "\u092A\u0939\u0932\u0947 project \u091A\u0941\u0928\u0947\u0902\u0964",
  redirect_policy_invalid: "Redirect policy \u092E\u093E\u0928\u094D\u092F \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
  domain_request_failed: "Domain request \u0935\u093F\u092B\u0932 \u2014 \u092A\u0941\u0928\u0903 \u092A\u094D\u0930\u092F\u093E\u0938 \u0915\u0930\u0947\u0902\u0964",
  domains_unavailable: "Domain \u0938\u0947\u0935\u093E \u0905\u092D\u0940 \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u0939\u0948\u0964"
};
var CLEANUP_LABEL_HI = {
  revoked: "Gateway routing binding revoked.",
  not_configured: "\u0915\u094B\u0908 Smart Shikka routing configured \u0928\u0939\u0940\u0902 \u0925\u093E \u2014 \u092C\u093E\u0939\u0930\u0940 site \u0905\u092A\u0930\u093F\u0935\u0930\u094D\u0924\u093F\u0924 \u0930\u0939\u0940\u0964",
  unavailable: "Routing cleanup \u0905\u092D\u0940 available \u0928\u0939\u0940\u0902 \u2014 manually verify \u0915\u0930\u0947\u0902\u0964"
};
function DomainsPanel(props) {
  const C = useBrandColors();
  const hi = props.locale !== "en";
  const t5 = (hiText, enText) => hi ? hiText : enText;
  const service = useMemo27(
    () => createDomainsService({
      token: props.token ?? "",
      baseUrl: props.apiBaseUrl ?? "",
      ...props.fetchImpl ? { fetchImpl: props.fetchImpl } : {}
    }),
    [props.token, props.apiBaseUrl, props.fetchImpl]
  );
  const [projects, setProjects] = useState53([]);
  const [projectsError, setProjectsError] = useState53(false);
  const [domains, setDomains] = useState53([]);
  const [outputUrls, setOutputUrls] = useState53(null);
  const [loadError, setLoadError] = useState53(null);
  const [hostname, setHostname] = useState53("");
  const [projectId, setProjectId] = useState53("");
  const [busy, setBusy] = useState53(null);
  const [actionError, setActionError] = useState53({});
  const [copied, setCopied] = useState53(null);
  const [detachNote, setDetachNote] = useState53(null);
  const refresh = useCallback32(
    async (signal) => {
      if (!props.token) return;
      try {
        const list = await service.list(void 0, signal);
        if (signal?.aborted) return;
        setDomains(list);
        setLoadError(null);
      } catch (err) {
        if (signal?.aborted) return;
        setLoadError(err instanceof DomainsApiError ? err.errorCode : "domains_unavailable");
      }
    },
    [service, props.token]
  );
  useEffect47(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, [refresh]);
  useEffect47(() => {
    if (!props.token) return;
    const controller = new AbortController();
    service.listProjects(controller.signal).then((list) => {
      setProjects(list);
      setProjectsError(false);
      setProjectId((prev) => prev || (list[0]?.projectId ?? ""));
    }).catch(() => setProjectsError(true));
    return () => controller.abort();
  }, [service, props.token]);
  useEffect47(() => {
    if (!projectId || !props.token) {
      setOutputUrls(null);
      return;
    }
    const controller = new AbortController();
    service.outputUrls(projectId, controller.signal).then((urls) => setOutputUrls(urls)).catch(() => setOutputUrls(null));
    return () => controller.abort();
  }, [service, projectId, props.token]);
  const runAction = useCallback32(
    async (key, fn) => {
      setBusy(key);
      setActionError((prev) => ({ ...prev, [key]: "" }));
      try {
        await fn();
        await refresh();
      } catch (err) {
        const code = err instanceof DomainsApiError ? err.errorCode : "request_failed";
        setActionError((prev) => ({ ...prev, [key]: code }));
      } finally {
        setBusy(null);
      }
    },
    [refresh]
  );
  const copy = useCallback32(async (text2, key) => {
    try {
      await navigator.clipboard.writeText(text2);
      setCopied(key);
      setTimeout(() => setCopied((prev) => prev === key ? null : prev), 2e3);
    } catch {
      setCopied(null);
    }
  }, []);
  const card = {
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: 12,
    background: C.card,
    display: "grid",
    gap: 8
  };
  const inputStyle = {
    padding: "8px 10px",
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    background: C.inputBg,
    color: C.fg,
    font: "inherit",
    fontSize: 13
  };
  const btnStyle = (primary = false) => ({
    padding: "6px 10px",
    borderRadius: 8,
    border: `1px solid ${primary ? C.primary : C.border}`,
    background: primary ? C.primary : "transparent",
    color: primary ? "#fff" : C.fg,
    cursor: "pointer",
    font: "inherit",
    fontSize: 12
  });
  const chip = (status) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    background: status === "active" ? "#1a7f37" : status === "verified" ? "#0a6ea8" : status === "failed" || status === "detached" ? "#b3261e" : "#8a6d00",
    color: "#fff"
  });
  const errorText = (code) => ERROR_LABEL_HI[code] ?? (hi ? `\u0915\u093E\u0930\u094D\u0930\u0935\u093E\u0908 \u0935\u093F\u092B\u0932: ${code}` : `Action failed: ${code}`);
  return /* @__PURE__ */ jsxs92("section", { "data-testid": "domains-panel", style: { display: "grid", gap: 16, minWidth: 0 }, children: [
    /* @__PURE__ */ jsxs92("header", { children: [
      /* @__PURE__ */ jsx100("h3", { style: { margin: "0 0 5px", fontSize: 19 }, children: t5("\u0921\u094B\u092E\u0947\u0928", "Domains") }),
      /* @__PURE__ */ jsx100("p", { style: { margin: 0, color: C.muted, fontSize: 13 }, children: t5(
        "Custom domain bind \u0915\u0930\u0947\u0902 \u2014 DNS TXT ownership + Smart Shikka route binding \u0938\u093F\u0926\u094D\u0927 \u0939\u094B\u0928\u0947 \u092A\u0930 \u0939\u0940 public URL \u0926\u093F\u0916\u0924\u093E \u0939\u0948\u0964",
        "Bind custom domains \u2014 a public URL only appears after DNS TXT ownership proof and verified Smart Shikka route binding."
      ) })
    ] }),
    /* @__PURE__ */ jsxs92("div", { style: card, "data-testid": "output-url-truth", children: [
      /* @__PURE__ */ jsx100("strong", { style: { fontSize: 13 }, children: t5("\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u0906\u0909\u091F\u092A\u0941\u091F URL", "Project output URLs") }),
      projects.length > 0 ? /* @__PURE__ */ jsxs92("label", { style: { display: "grid", gap: 4, fontSize: 12 }, children: [
        t5("\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F", "Project"),
        /* @__PURE__ */ jsx100(
          "select",
          {
            value: projectId,
            onChange: (e) => setProjectId(e.target.value),
            style: inputStyle,
            "data-testid": "output-urls-project",
            children: projects.map((p) => /* @__PURE__ */ jsx100("option", { value: p.projectId, children: p.name ?? p.projectId }, p.projectId))
          }
        )
      ] }) : /* @__PURE__ */ jsx100("span", { style: { fontSize: 12, color: C.muted }, "data-testid": "output-urls-no-project", children: projectsError ? t5("\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u0938\u0942\u091A\u0940 \u0932\u094B\u0921 \u0928\u0939\u0940\u0902 \u0939\u0941\u0908\u0964", "Could not load project list.") : t5("\u0915\u094B\u0908 \u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u0928\u0939\u0940\u0902\u0964", "No project.") }),
      outputUrls ? /* @__PURE__ */ jsxs92("div", { style: { display: "grid", gap: 6, fontSize: 12 }, "data-testid": "output-urls-rows", children: [
        /* @__PURE__ */ jsxs92("div", { "data-testid": "local-output-url", children: [
          /* @__PURE__ */ jsxs92("span", { style: { fontWeight: 700 }, children: [
            t5("\u0932\u094B\u0915\u0932", "Local"),
            ": "
          ] }),
          outputUrls.local.status === "available" ? /* @__PURE__ */ jsx100("code", { "data-testid": "local-output-url-value", children: outputUrls.local.url }) : /* @__PURE__ */ jsx100("span", { style: { color: C.muted }, children: outputUrls.local.reason === "no_listener" ? t5("\u0915\u094B\u0908 \u0932\u094B\u0915\u0932 listener \u0928\u0939\u0940\u0902 \u091A\u0932 \u0930\u0939\u093E", "No local listener running") : t5("configured \u0928\u0939\u0940\u0902", "Not configured") })
        ] }),
        /* @__PURE__ */ jsxs92("div", { "data-testid": "public-preview-url", children: [
          /* @__PURE__ */ jsxs92("span", { style: { fontWeight: 700 }, children: [
            t5("\u092A\u092C\u094D\u0932\u093F\u0915 \u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942", "Public preview"),
            ": "
          ] }),
          outputUrls.publicPreview.status === "available" ? /* @__PURE__ */ jsxs92("span", { children: [
            /* @__PURE__ */ jsx100("code", { "data-testid": "public-preview-url-value", children: outputUrls.publicPreview.url }),
            /* @__PURE__ */ jsx100("span", { style: { color: C.muted, marginLeft: 6 }, children: `SHA ${outputUrls.publicPreview.sourceSha.slice(0, 12)} \xB7 ${outputUrls.publicPreview.deployStatus}` })
          ] }) : /* @__PURE__ */ jsx100("span", { style: { color: C.muted }, children: t5("\u0915\u094B\u0908 \u092A\u094D\u0930\u0940\u0935\u094D\u092F\u0942 deploy \u0938\u093F\u0926\u094D\u0927 \u0928\u0939\u0940\u0902", "No verified preview deployment") })
        ] })
      ] }) : null
    ] }),
    /* @__PURE__ */ jsxs92("div", { style: card, "data-testid": "add-domain", children: [
      /* @__PURE__ */ jsx100("strong", { style: { fontSize: 13 }, children: t5("\u0921\u094B\u092E\u0947\u0928 \u091C\u094B\u0921\u093C\u0947\u0902", "Add domain") }),
      /* @__PURE__ */ jsxs92("div", { style: { display: "flex", gap: 8, flexWrap: "wrap" }, children: [
        /* @__PURE__ */ jsx100(
          "input",
          {
            type: "text",
            value: hostname,
            onChange: (e) => setHostname(e.target.value),
            placeholder: t5("app.example.com", "app.example.com"),
            style: { ...inputStyle, flex: 1, minWidth: 180 },
            "data-testid": "domain-hostname-input",
            "aria-label": t5("\u0921\u094B\u092E\u0947\u0928 hostname", "Domain hostname")
          }
        ),
        /* @__PURE__ */ jsx100(
          "select",
          {
            value: projectId,
            onChange: (e) => setProjectId(e.target.value),
            style: inputStyle,
            "data-testid": "domain-project-select",
            "aria-label": t5("\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F \u091A\u0941\u0928\u0947\u0902", "Select project"),
            children: projects.map((p) => /* @__PURE__ */ jsx100("option", { value: p.projectId, children: p.name ?? p.projectId }, p.projectId))
          }
        ),
        /* @__PURE__ */ jsx100(
          "button",
          {
            type: "button",
            style: btnStyle(true),
            disabled: busy === "add" || !hostname.trim() || !projectId,
            "data-testid": "domain-add-button",
            onClick: () => void runAction("add", async () => {
              await service.create({
                hostname: hostname.trim(),
                projectId
              });
              setHostname("");
            }),
            children: busy === "add" ? "\u2026" : t5("\u091C\u094B\u0921\u093C\u0947\u0902", "Add")
          }
        )
      ] }),
      actionError.add ? /* @__PURE__ */ jsx100("span", { style: { color: "#b3261e", fontSize: 12 }, "data-testid": "domain-add-error", children: errorText(actionError.add) }) : null
    ] }),
    loadError ? /* @__PURE__ */ jsx100("div", { style: { ...card, color: "#b3261e" }, "data-testid": "domains-load-error", children: errorText(loadError) }) : null,
    domains.length === 0 && !loadError ? /* @__PURE__ */ jsx100("span", { style: { color: C.muted, fontSize: 12 }, "data-testid": "domains-empty", children: t5("\u0915\u094B\u0908 domain bind \u0928\u0939\u0940\u0902 \u0939\u0948\u0964", "No domains bound yet.") }) : null,
    detachNote ? /* @__PURE__ */ jsx100("span", { style: { color: C.muted, fontSize: 12 }, "data-testid": "routing-cleanup-note", children: detachNote }) : null,
    domains.map((d) => /* @__PURE__ */ jsxs92("div", { style: card, "data-testid": `domain-row-${d.hostname}`, children: [
      /* @__PURE__ */ jsxs92("div", { style: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }, children: [
        /* @__PURE__ */ jsx100("code", { style: { fontSize: 13, fontWeight: 700 }, children: d.hostname }),
        /* @__PURE__ */ jsx100("span", { style: chip(d.status), "data-testid": `domain-status-${d.hostname}`, children: hi ? STATUS_LABEL_HI[d.status] : STATUS_LABEL_EN[d.status] }),
        d.isCanonical ? /* @__PURE__ */ jsx100("span", { style: { fontSize: 11, fontWeight: 700, color: C.primary }, children: t5("canonical", "canonical") }) : null
      ] }),
      /* @__PURE__ */ jsxs92("div", { style: { fontSize: 12, color: C.muted }, children: [
        t5("\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F", "Project"),
        ": ",
        /* @__PURE__ */ jsx100("code", { children: d.projectId })
      ] }),
      d.publicUrl ? /* @__PURE__ */ jsxs92("div", { style: { fontSize: 12 }, children: [
        t5("\u092A\u092C\u094D\u0932\u093F\u0915 URL", "Public URL"),
        ": ",
        /* @__PURE__ */ jsx100("code", { "data-testid": "domain-public-url", children: d.publicUrl })
      ] }) : null,
      d.status === "pending" || d.status === "failed" ? /* @__PURE__ */ jsxs92(
        "div",
        {
          style: {
            fontSize: 12,
            display: "grid",
            gap: 4,
            padding: 8,
            borderRadius: 8,
            background: C.panel
          },
          "data-testid": `dns-instructions-${d.hostname}`,
          children: [
            /* @__PURE__ */ jsx100("span", { style: { color: C.muted }, children: t5(
              '\u0905\u092A\u0928\u0947 DNS \u092E\u0947\u0902 \u092F\u0939 TXT record \u091C\u094B\u0921\u093C\u0947\u0902, \u092B\u093F\u0930 "Verify" \u0926\u092C\u093E\u090F\u0901:',
              'Add this TXT record in your DNS, then press "Verify":'
            ) }),
            /* @__PURE__ */ jsxs92("div", { style: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }, children: [
              /* @__PURE__ */ jsx100("code", { "data-testid": "dns-record-name", children: d.dnsInstruction.name }),
              /* @__PURE__ */ jsx100(
                "button",
                {
                  type: "button",
                  style: btnStyle(),
                  onClick: () => void copy(d.dnsInstruction.name, `name-${d.domainId}`),
                  "data-testid": "copy-dns-name",
                  children: copied === `name-${d.domainId}` ? t5("\u0915\u0949\u092A\u0940 \u0939\u0941\u0906", "Copied") : t5("\u0915\u0949\u092A\u0940", "Copy")
                }
              )
            ] }),
            /* @__PURE__ */ jsxs92("div", { style: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }, children: [
              /* @__PURE__ */ jsx100("code", { style: { wordBreak: "break-all" }, "data-testid": "dns-record-value", children: `${d.dnsInstruction.recordType} ${d.dnsInstruction.value}` }),
              /* @__PURE__ */ jsx100(
                "button",
                {
                  type: "button",
                  style: btnStyle(),
                  onClick: () => void copy(d.dnsInstruction.value, `value-${d.domainId}`),
                  "data-testid": "copy-dns-value",
                  children: copied === `value-${d.domainId}` ? t5("\u0915\u0949\u092A\u0940 \u0939\u0941\u0906", "Copied") : t5("\u0915\u0949\u092A\u0940", "Copy")
                }
              )
            ] }),
            d.dnsLastError ? /* @__PURE__ */ jsx100("span", { style: { color: "#b3261e" }, "data-testid": "dns-last-error", children: errorText(d.dnsLastError) }) : null
          ]
        }
      ) : null,
      d.status === "tls_provisioning" && d.dnsLastError ? /* @__PURE__ */ jsx100("span", { style: { color: "#8a6d00", fontSize: 12 }, children: errorText(d.dnsLastError) }) : null,
      d.status === "verified" ? /* @__PURE__ */ jsxs92("span", { style: { color: C.muted, fontSize: 12 }, "data-testid": `routing-unproven-${d.hostname}`, children: [
        t5(
          "Ownership verified \xB7 routing/TLS \u0905\u092D\u0940 \u0938\u093F\u0926\u094D\u0927 \u0928\u0939\u0940\u0902 \u2014 Activate route binding prove \u0939\u094B\u0928\u0947 \u092A\u0930 \u0939\u0940 active \u0939\u094B\u0917\u093E.",
          "Ownership verified \xB7 routing/TLS not proven yet \u2014 Activate activates only once the route binding is proven."
        ),
        d.dnsLastError ? ` (${errorText(d.dnsLastError)})` : ""
      ] }) : null,
      /* @__PURE__ */ jsxs92("div", { style: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }, children: [
        d.status === "pending" || d.status === "failed" ? /* @__PURE__ */ jsx100(
          "button",
          {
            type: "button",
            style: btnStyle(true),
            disabled: busy === `verify-${d.domainId}`,
            onClick: () => void runAction(`verify-${d.domainId}`, () => service.verify(d.domainId)),
            "data-testid": `verify-${d.hostname}`,
            children: t5("Verify", "Verify")
          }
        ) : null,
        d.status === "verified" || d.status === "tls_provisioning" ? /* @__PURE__ */ jsx100(
          "button",
          {
            type: "button",
            style: btnStyle(true),
            disabled: busy === `activate-${d.domainId}`,
            onClick: () => void runAction(`activate-${d.domainId}`, () => service.activate(d.domainId)),
            "data-testid": `activate-${d.hostname}`,
            children: t5("Activate", "Activate")
          }
        ) : null,
        d.status === "active" && !d.isCanonical ? /* @__PURE__ */ jsx100(
          "button",
          {
            type: "button",
            style: btnStyle(),
            disabled: busy === `canonical-${d.domainId}`,
            onClick: () => void runAction(`canonical-${d.domainId}`, () => service.setCanonical(d.domainId)),
            "data-testid": `canonical-${d.hostname}`,
            children: t5("Canonical \u092C\u0928\u093E\u090F\u0901", "Make canonical")
          }
        ) : null,
        d.status !== "detached" ? /* @__PURE__ */ jsxs92(Fragment14, { children: [
          /* @__PURE__ */ jsxs92("label", { style: { fontSize: 12, display: "flex", gap: 4, alignItems: "center" }, children: [
            t5("Redirect policy", "Redirect policy"),
            ":",
            /* @__PURE__ */ jsxs92(
              "select",
              {
                value: d.redirectPolicy,
                style: { ...inputStyle, padding: "4px 6px" },
                onChange: (e) => void runAction(
                  `policy-${d.domainId}`,
                  () => service.setRedirectPolicy(
                    d.domainId,
                    e.target.value
                  )
                ),
                "data-testid": `policy-${d.hostname}`,
                children: [
                  /* @__PURE__ */ jsx100("option", { value: "redirect", children: t5("canonical \u092A\u0930 redirect", "Redirect to canonical") }),
                  /* @__PURE__ */ jsx100("option", { value: "serve", children: t5("\u0938\u0940\u0927\u0947 serve", "Serve directly") })
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsx100(
            "button",
            {
              type: "button",
              style: { ...btnStyle(), color: "#b3261e", borderColor: "#b3261e" },
              disabled: busy === `detach-${d.domainId}`,
              onClick: () => void runAction(`detach-${d.domainId}`, async () => {
                const result = await service.detach(d.domainId);
                setDetachNote(
                  hi ? CLEANUP_LABEL_HI[result.routingCleanup] : result.routingCleanup
                );
              }),
              "data-testid": `detach-${d.hostname}`,
              children: t5("Detach", "Detach")
            }
          )
        ] }) : null
      ] }),
      actionError[`verify-${d.domainId}`] || actionError[`activate-${d.domainId}`] || actionError[`canonical-${d.domainId}`] || actionError[`detach-${d.domainId}`] || actionError[`policy-${d.domainId}`] ? /* @__PURE__ */ jsx100("span", { style: { color: "#b3261e", fontSize: 12 }, children: errorText(
        actionError[`verify-${d.domainId}`] ?? actionError[`activate-${d.domainId}`] ?? actionError[`canonical-${d.domainId}`] ?? actionError[`detach-${d.domainId}`] ?? actionError[`policy-${d.domainId}`] ?? ""
      ) }) : null
    ] }, d.domainId))
  ] });
}

// src/beta/betaService.ts
var COOKIE_SESSION_TOKEN3 = "__smart_shikka_cookie_session__";
var CSRF_COOKIE_NAME5 = "smart_shikka_csrf";
var CSRF_HEADER_NAME5 = "x-smart-shikka-csrf";
function readCookie3(name) {
  if (typeof document === "undefined") return void 0;
  for (const part of document.cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return decodeURIComponent(rest.join("="));
  }
  return void 0;
}
var BetaApiError = class extends Error {
  constructor(status, body) {
    super(typeof body.error === "string" ? body.error : `Beta request failed (${status}).`);
    this.status = status;
    this.errorCode = typeof body.errorCode === "string" ? body.errorCode : "beta_request_failed";
  }
};
function createBetaService(options) {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = options.baseUrl ?? "";
  const cookieSession = options.token === COOKIE_SESSION_TOKEN3;
  const headers2 = (mutating = false) => {
    const next = { "Content-Type": "application/json" };
    if (!cookieSession) next.Authorization = `Bearer ${options.token}`;
    if (cookieSession && mutating) {
      const csrf = readCookie3(CSRF_COOKIE_NAME5);
      if (csrf) next[CSRF_HEADER_NAME5] = csrf;
    }
    return next;
  };
  const credentials = cookieSession ? "include" : "same-origin";
  async function call(method, path, body, signal) {
    const mutating = method !== "GET";
    const response = await fetchImpl(`${baseUrl}/api${path}`, {
      method,
      headers: headers2(mutating),
      credentials,
      ...body !== void 0 ? { body: JSON.stringify(body) } : {},
      ...signal ? { signal } : {}
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.success === false || payload.ok === false) {
      throw new BetaApiError(response.status, payload);
    }
    return payload;
  }
  return {
    async listProjects(signal) {
      const body = await call(
        "GET",
        "/projects",
        void 0,
        signal
      );
      return (Array.isArray(body.projects) ? body.projects : []).filter((p) => typeof p.projectId === "string").map((p) => ({
        projectId: String(p.projectId),
        name: typeof p.name === "string" ? p.name : void 0
      }));
    },
    async run(projectId) {
      const body = await call(
        "POST",
        "/beta/acceptance/run",
        { projectId }
      );
      return body.run;
    },
    async latest(projectId, signal) {
      const body = await call(
        "GET",
        `/beta/acceptance/latest?projectId=${encodeURIComponent(projectId)}`,
        void 0,
        signal
      );
      return body.run ?? null;
    },
    async runs(projectId, signal) {
      const body = await call(
        "GET",
        `/beta/acceptance/runs?projectId=${encodeURIComponent(projectId)}`,
        void 0,
        signal
      );
      return Array.isArray(body.runs) ? body.runs : [];
    }
  };
}

// src/beta/BetaReadinessPanel.tsx
import { useCallback as useCallback33, useEffect as useEffect48, useMemo as useMemo28, useState as useState54 } from "react";
import { jsx as jsx101, jsxs as jsxs93 } from "react/jsx-runtime";
var STATUS_LABEL_HI2 = {
  pass: "\u092A\u093E\u0938",
  fail: "\u0935\u093F\u092B\u0932",
  blocked_env: "\u092A\u0930\u094D\u092F\u093E\u0935\u0930\u0923 \u092C\u0902\u0926",
  unavailable: "\u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902"
};
var STATUS_LABEL_EN2 = {
  pass: "Pass",
  fail: "Fail",
  blocked_env: "Blocked env",
  unavailable: "Unavailable"
};
var ERROR_LABEL_HI2 = {
  project_required: "Project \u091A\u0941\u0928\u0947\u0902\u0964",
  project_denied: "\u0907\u0938 project \u0915\u0947 \u0932\u093F\u090F owner \u0905\u0928\u0941\u092E\u0924\u093F \u0928\u0939\u0940\u0902 \u0939\u0948\u0964",
  beta_acceptance_unavailable: "Beta acceptance store \u0909\u092A\u0932\u092C\u094D\u0927 \u0928\u0939\u0940\u0902 \u2014 durable store \u091C\u093C\u0930\u0942\u0930\u0940\u0964",
  beta_acceptance_role_denied: "Owner access \u091C\u093C\u0930\u0942\u0930\u0940 \u0939\u0948\u0964",
  unauthenticated: "\u0938\u0924\u094D\u0930 \u0938\u092E\u093E\u092A\u094D\u0924 \u2014 \u092B\u093F\u0930 login \u0915\u0930\u0947\u0902\u0964",
  csrf_invalid: "CSRF token invalid \u2014 page refresh \u0915\u0930\u0947\u0902\u0964"
};
function BetaReadinessPanel(props) {
  const C = useBrandColors();
  const hi = props.locale !== "en";
  const t5 = (hiText, enText) => hi ? hiText : enText;
  const service = useMemo28(
    () => createBetaService({
      token: props.token ?? "",
      baseUrl: props.apiBaseUrl ?? "",
      ...props.fetchImpl ? { fetchImpl: props.fetchImpl } : {}
    }),
    [props.token, props.apiBaseUrl, props.fetchImpl]
  );
  const [projects, setProjects] = useState54([]);
  const [projectsError, setProjectsError] = useState54(false);
  const [projectId, setProjectId] = useState54("");
  const [report, setReport] = useState54(null);
  const [runs, setRuns] = useState54([]);
  const [loadError, setLoadError] = useState54(null);
  const [busy, setBusy] = useState54(false);
  const [actionError, setActionError] = useState54(null);
  useEffect48(() => {
    if (!props.token) return;
    const controller = new AbortController();
    service.listProjects(controller.signal).then((list) => {
      if (controller.signal.aborted) return;
      setProjects(list);
      setProjectsError(false);
      if (!projectId && list.length > 0) setProjectId(list[0].projectId);
    }).catch(() => {
      if (!controller.signal.aborted) setProjectsError(true);
    });
    return () => controller.abort();
  }, [service, props.token]);
  const refresh = useCallback33(
    async (pid, signal) => {
      if (!props.token || !pid) return;
      try {
        const [latest, history] = await Promise.all([
          service.latest(pid, signal),
          service.runs(pid, signal)
        ]);
        if (signal?.aborted) return;
        setReport(latest);
        setRuns(history);
        setLoadError(null);
      } catch (err) {
        if (!signal?.aborted) {
          setLoadError(err instanceof BetaApiError ? err.errorCode : "beta_request_failed");
        }
      }
    },
    [props.token, service]
  );
  useEffect48(() => {
    if (!projectId) return;
    const controller = new AbortController();
    void refresh(projectId, controller.signal);
    return () => controller.abort();
  }, [projectId, refresh]);
  const runNow = useCallback33(async () => {
    if (!projectId || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      const next = await service.run(projectId);
      setReport(next);
      setRuns((prev) => [next, ...prev].slice(0, 10));
    } catch (err) {
      setActionError(err instanceof BetaApiError ? err.errorCode : "beta_request_failed");
    } finally {
      setBusy(false);
    }
  }, [projectId, busy, service]);
  const verdict = report?.verdict;
  const card = {
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
    background: C.card
  };
  const statusColor2 = (s) => s === "pass" ? C.success : s === "fail" ? C.destructive : s === "blocked_env" ? C.warning : C.muted;
  return /* @__PURE__ */ jsxs93("section", { "data-testid": "beta-readiness-panel", "aria-label": t5("\u092C\u0940\u091F\u093E \u0924\u0948\u092F\u093E\u0930\u0940", "Beta readiness"), children: [
    /* @__PURE__ */ jsx101("h3", { style: { margin: "0 0 4px", fontSize: 15, color: C.fg }, children: t5("\u092C\u0940\u091F\u093E \u0924\u0948\u092F\u093E\u0930\u0940 \u0914\u0930 E2E \u0938\u094D\u0935\u0940\u0915\u0943\u0924\u093F", "Beta readiness & E2E acceptance") }),
    /* @__PURE__ */ jsx101("p", { style: { margin: "0 0 12px", fontSize: 12, color: C.muted }, children: t5(
      "Canonical run \u0905\u0938\u0932\u0940 \u0938\u0947\u0935\u093E checks \u091A\u0932\u093E\u0924\u093E \u0939\u0948 \u2014 pass \u0915\u0947\u0935\u0932 verified authority \u092A\u0930; blocked_env/unavailable \u0915\u094B\u0908 hidden pass \u0928\u0939\u0940\u0902\u0964",
      "The canonical run executes real service checks \u2014 pass only on a verified authority; blocked_env/unavailable never hidden."
    ) }),
    projectsError ? /* @__PURE__ */ jsx101("div", { role: "alert", style: { ...card, color: C.destructive }, children: t5("Projects load \u0928\u0939\u0940\u0902 \u0939\u0941\u090F \u2014 page refresh \u0915\u0930\u0947\u0902\u0964", "Projects failed to load \u2014 refresh the page.") }) : null,
    loadError ? /* @__PURE__ */ jsx101("div", { role: "alert", style: { ...card, color: C.destructive }, children: ERROR_LABEL_HI2[loadError] ?? t5("Beta report load \u0928\u0939\u0940\u0902 \u0939\u0941\u0906\u0964", "Beta report failed to load.") }) : null,
    actionError ? /* @__PURE__ */ jsx101("div", { role: "alert", style: { ...card, color: C.destructive }, children: ERROR_LABEL_HI2[actionError] ?? t5("Beta run \u0935\u093F\u092B\u0932\u0964", "Beta run failed.") }) : null,
    /* @__PURE__ */ jsxs93("div", { style: { ...card, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }, children: [
      /* @__PURE__ */ jsx101("label", { htmlFor: "beta-project", style: { fontSize: 13, color: C.fg }, children: t5("\u092A\u094D\u0930\u094B\u091C\u0947\u0915\u094D\u091F", "Project") }),
      /* @__PURE__ */ jsxs93(
        "select",
        {
          id: "beta-project",
          "data-testid": "beta-project-select",
          value: projectId,
          onChange: (e) => setProjectId(e.target.value),
          style: { padding: "6px 8px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.fg },
          children: [
            projects.length === 0 ? /* @__PURE__ */ jsx101("option", { value: "", children: t5("\u2014 \u0915\u094B\u0908 project \u0928\u0939\u0940\u0902 \u2014", "\u2014 no projects \u2014") }) : null,
            projects.map((p) => /* @__PURE__ */ jsx101("option", { value: p.projectId, children: p.name ? `${p.name} (${p.projectId})` : p.projectId }, p.projectId))
          ]
        }
      ),
      /* @__PURE__ */ jsx101(
        "button",
        {
          type: "button",
          "data-testid": "beta-run-button",
          disabled: !projectId || busy,
          onClick: () => void runNow(),
          style: {
            padding: "7px 14px",
            borderRadius: 8,
            border: 0,
            background: !projectId || busy ? C.chip : C.primary,
            color: !projectId || busy ? C.muted : C.primaryFg,
            cursor: !projectId || busy ? "not-allowed" : "pointer",
            fontWeight: 600
          },
          children: busy ? t5("\u091A\u0932 \u0930\u0939\u093E\u2026", "Running\u2026") : t5("\u092C\u0940\u091F\u093E \u0938\u094D\u0935\u0940\u0915\u0943\u0924\u093F \u091A\u0932\u093E\u090F\u0901", "Run beta acceptance")
        }
      )
    ] }),
    report ? /* @__PURE__ */ jsxs93("div", { "data-testid": "beta-report", style: card, children: [
      /* @__PURE__ */ jsxs93("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }, children: [
        /* @__PURE__ */ jsxs93("strong", { style: { fontSize: 14, color: C.fg }, children: [
          t5("\u0905\u0902\u0924\u093F\u092E run", "Latest run"),
          " ",
          /* @__PURE__ */ jsx101("code", { style: { fontSize: 11, color: C.muted }, children: report.runId })
        ] }),
        /* @__PURE__ */ jsx101(
          "span",
          {
            "data-testid": "beta-ready-verdict",
            style: {
              fontSize: 12,
              fontWeight: 700,
              color: verdict?.betaReady ? C.success : C.warning
            },
            children: verdict?.betaReady ? t5("\u092C\u0940\u091F\u093E READY", "BETA READY") : t5("\u092C\u0940\u091F\u093E READY \u0928\u0939\u0940\u0902", "NOT BETA READY")
          }
        )
      ] }),
      /* @__PURE__ */ jsxs93("div", { style: { fontSize: 12, color: C.muted, margin: "4px 0 10px" }, children: [
        new Date(report.createdAt).toLocaleString(),
        " \xB7",
        " ",
        t5(
          `\u092A\u093E\u0938 ${verdict?.passCount ?? 0} \xB7 \u0935\u093F\u092B\u0932 ${verdict?.failCount ?? 0} \xB7 \u092A\u0930\u094D\u092F\u093E\u0935\u0930\u0923 ${verdict?.blockedEnvCount ?? 0} \xB7 \u0905\u0928\u0941\u092A\u0932\u092C\u094D\u0927 ${verdict?.unavailableCount ?? 0}`,
          `pass ${verdict?.passCount ?? 0} \xB7 fail ${verdict?.failCount ?? 0} \xB7 env ${verdict?.blockedEnvCount ?? 0} \xB7 unavailable ${verdict?.unavailableCount ?? 0}`
        )
      ] }),
      /* @__PURE__ */ jsx101("ul", { "data-testid": "beta-check-list", style: { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }, children: report.checks.map((check) => /* @__PURE__ */ jsxs93(
        "li",
        {
          "data-testid": `beta-check-${check.key}`,
          style: {
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            padding: "7px 10px",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            fontSize: 13
          },
          children: [
            /* @__PURE__ */ jsxs93("span", { style: { color: C.fg }, children: [
              hi ? check.labelHi : check.key.replace(/_/g, " "),
              check.layer ? /* @__PURE__ */ jsx101("span", { style: { color: C.muted, fontSize: 10, marginLeft: 6 }, children: check.layer === "behavior" ? hi ? "\u0935\u094D\u092F\u0935\u0939\u093E\u0930" : "behavior" : hi ? "\u0938\u094D\u0930\u094B\u0924" : "source" }) : null,
              check.detail ? /* @__PURE__ */ jsxs93("span", { style: { color: C.muted, fontSize: 11 }, children: [
                " \u2014 ",
                check.detail
              ] }) : null
            ] }),
            /* @__PURE__ */ jsx101("span", { style: { color: statusColor2(check.status), fontWeight: 700, whiteSpace: "nowrap" }, children: hi ? STATUS_LABEL_HI2[check.status] : STATUS_LABEL_EN2[check.status] })
          ]
        },
        check.key
      )) })
    ] }) : /* @__PURE__ */ jsx101("div", { style: { ...card, color: C.muted, fontSize: 13 }, children: t5(
      "\u0907\u0938 project \u0915\u0947 \u0932\u093F\u090F \u0905\u092D\u0940 \u0915\u094B\u0908 beta acceptance run \u0928\u0939\u0940\u0902 \u2014 \u090A\u092A\u0930 \u0938\u0947 \u091A\u0932\u093E\u090F\u0901\u0964",
      "No beta acceptance run for this project yet \u2014 run one above."
    ) }),
    runs.length > 1 ? /* @__PURE__ */ jsxs93("details", { style: { ...card, fontSize: 12, color: C.muted }, children: [
      /* @__PURE__ */ jsx101("summary", { style: { cursor: "pointer", color: C.fg }, children: t5(`\u092A\u093F\u091B\u0932\u0947 runs (${runs.length})`, `Run history (${runs.length})`) }),
      /* @__PURE__ */ jsx101("ul", { style: { listStyle: "none", margin: "8px 0 0", padding: 0, display: "grid", gap: 4 }, children: runs.map((r) => /* @__PURE__ */ jsxs93("li", { style: { display: "flex", justifyContent: "space-between", gap: 8 }, children: [
        /* @__PURE__ */ jsx101("code", { style: { fontSize: 11 }, children: r.runId }),
        /* @__PURE__ */ jsxs93("span", { children: [
          new Date(r.createdAt).toLocaleString(),
          " \xB7",
          " ",
          r.verdict.betaReady ? t5("READY", "READY") : t5("READY \u0928\u0939\u0940\u0902", "NOT READY")
        ] })
      ] }, r.runId)) })
    ] }) : null
  ] });
}

// src/ceo/ShikkaCEO.ts
function isCeoEligible(role) {
  return role === "super-admin" || role === "super-sub-admin";
}
function classifyIntent(input) {
  const lowered = input.toLowerCase();
  if (lowered.includes("stop") || lowered.includes("cancel")) return "cancel";
  if (lowered.includes("deploy") || lowered.includes("release")) return "deploy";
  if (lowered.includes("pay") || lowered.includes("purchase")) return "pay";
  if (lowered.includes("write") || lowered.includes("edit") || lowered.includes("create")) return "write";
  if (lowered.includes("search") || lowered.includes("find")) return "search";
  return "chat";
}
function permissionLevelForIntent(intent) {
  if (["deploy", "pay"].includes(intent)) return "L3";
  if (["write", "cancel"].includes(intent)) return "L2";
  return "L1";
}
async function turn(req) {
  if (!isCeoEligible(req.role)) {
    return {
      success: true,
      ok: false,
      realExecutionPerformed: false,
      answer: "Smart Shikka CEO is only available to active super-admin or super-sub-admin.",
      activityState: "error",
      requiresApproval: false,
      errorCode: "ceo_role_denied"
    };
  }
  return req.turn({
    input: req.input,
    surface: req.surface,
    role: req.role ?? "unknown",
    locale: req.locale ?? "en",
    voice: req.source === "voice"
  });
}
async function routeIntent(req) {
  const intent = classifyIntent(req.input);
  const permissionLevel = permissionLevelForIntent(intent);
  const plan = [req.input, `Classified as ${intent}`, `Permission level ${permissionLevel}`];
  if (!isCeoEligible(req.role)) {
    return {
      intent,
      plan,
      permissionLevel,
      executor: "none",
      requiresApproval: permissionLevel !== "L1",
      allowed: false,
      denialCode: "ceo_role_denied",
      resultSummary: `Smart Shikka CEO is only available to active super-admin or super-sub-admin.`
    };
  }
  if (req.orchestrate) {
    return req.orchestrate({
      input: req.input,
      intent,
      permissionLevel,
      plan,
      surface: req.surface,
      role: req.role ?? "unknown",
      locale: req.locale ?? "en"
    });
  }
  return {
    intent,
    plan,
    permissionLevel,
    executor: "none",
    requiresApproval: permissionLevel !== "L1",
    allowed: false,
    denialCode: "ceo_orchestrator_not_configured",
    resultSummary: `Shikka CEO classified the request as ${intent} (${permissionLevel}). No canonical orchestrator is configured; action is blocked until one is wired.`
  };
}

// src/ceo/useShikkaCEO.ts
import { useCallback as useCallback34, useState as useState55 } from "react";
function useShikkaCEO(surface, role, locale = "en", orchestrate) {
  const [busy, setBusy] = useState55(false);
  const [lastResult, setLastResult] = useState55(void 0);
  const [error, setError] = useState55(void 0);
  const request = useCallback34(
    async (input, source = "text") => {
      setBusy(true);
      setError(void 0);
      try {
        const result = await routeIntent({ input, source, surface, locale, role, orchestrate });
        setLastResult(result);
        return result;
      } catch (e) {
        setError(errorToCode(e));
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [surface, locale, role, orchestrate]
  );
  return { request, busy, lastResult, error };
}

// src/ceo/DraggableShikkaOrb.tsx
import { useCallback as useCallback35, useEffect as useEffect49, useMemo as useMemo29, useRef as useRef24, useState as useState56 } from "react";
import { jsx as jsx102, jsxs as jsxs94 } from "react/jsx-runtime";
var VIEWPORT_MARGIN = 12;
var COMPACT_SIZE = 38;
var EXPANDED_WIDTH = 320;
var EXPANDED_HEIGHT = 138;
function clampPosition(position, expanded) {
  if (typeof window === "undefined") return position;
  const width = expanded ? Math.min(EXPANDED_WIDTH, Math.max(COMPACT_SIZE + 24, window.innerWidth - VIEWPORT_MARGIN * 2)) : COMPACT_SIZE + 12;
  const height = expanded ? EXPANDED_HEIGHT : COMPACT_SIZE + 12;
  return {
    right: Math.max(
      VIEWPORT_MARGIN,
      Math.min(window.innerWidth - width - VIEWPORT_MARGIN, position.right)
    ),
    bottom: Math.max(
      VIEWPORT_MARGIN,
      Math.min(window.innerHeight - height - VIEWPORT_MARGIN, position.bottom)
    )
  };
}
function DraggableShikkaOrb({
  state,
  locale,
  logo,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange
}) {
  const [position, setPosition] = useState56({ right: 20, bottom: 20 });
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState56(defaultExpanded);
  const dragStart = useRef24();
  const draggedRef = useRef24(false);
  const expanded = controlledExpanded ?? uncontrolledExpanded;
  const setExpanded = useCallback35(
    (nextExpanded) => {
      if (controlledExpanded === void 0) setUncontrolledExpanded(nextExpanded);
      onExpandedChange?.(nextExpanded);
      setPosition((current) => clampPosition(current, nextExpanded));
    },
    [controlledExpanded, onExpandedChange]
  );
  useEffect49(() => {
    setPosition((current) => clampPosition(current, expanded));
  }, [expanded]);
  useEffect49(() => {
    const handleResize = () => setPosition((current) => clampPosition(current, expanded));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [expanded]);
  const moveBy = useCallback35(
    (deltaRight, deltaBottom) => {
      setPosition(
        (current) => clampPosition(
          { right: current.right + deltaRight, bottom: current.bottom + deltaBottom },
          expanded
        )
      );
    },
    [expanded]
  );
  const onPointerDown = useCallback35((event) => {
    if (event.button > 0) return;
    draggedRef.current = false;
    dragStart.current = {
      x: event.clientX,
      y: event.clientY,
      right: position.right,
      bottom: position.bottom
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [position]);
  const onPointerMove = useCallback35(
    (event) => {
      const start = dragStart.current;
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) draggedRef.current = true;
      setPosition(() => clampPosition({ right: start.right - dx, bottom: start.bottom - dy }, expanded));
    },
    [expanded]
  );
  const onPointerEnd = useCallback35(() => {
    dragStart.current = void 0;
  }, []);
  const onClick = useCallback35(() => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    setExpanded(!expanded);
  }, [expanded, setExpanded]);
  const onKeyDown = useCallback35(
    (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setExpanded(!expanded);
        return;
      }
      if (event.key === "Escape" && expanded) {
        event.preventDefault();
        setExpanded(false);
        return;
      }
      const step = event.shiftKey ? 24 : 8;
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          moveBy(step, 0);
          break;
        case "ArrowRight":
          event.preventDefault();
          moveBy(-step, 0);
          break;
        case "ArrowUp":
          event.preventDefault();
          moveBy(0, step);
          break;
        case "ArrowDown":
          event.preventDefault();
          moveBy(0, -step);
          break;
        default:
          break;
      }
    },
    [expanded, moveBy, setExpanded]
  );
  const labels = useMemo29(
    () => locale === "hi" ? {
      group: "Smart Shikka CEO \u0938\u094D\u0925\u093F\u0924\u093F orb",
      hint: "Enter \u0938\u0947 \u0935\u093F\u0935\u0930\u0923 \u0916\u094B\u0932\u0947\u0902; arrow keys \u0938\u0947 viewport \u092E\u0947\u0902 \u0932\u0947 \u091C\u093E\u090F\u0901\u0964",
      details: "\u092F\u0939 \u0915\u0947\u0935\u0932 \u0938\u094D\u0925\u093F\u0924\u093F \u0926\u093F\u0916\u093E\u0924\u093E \u0939\u0948; \u0915\u094B\u0908 action \u092F\u093E recording \u0936\u0941\u0930\u0942 \u0928\u0939\u0940\u0902 \u0915\u0930\u0924\u093E\u0964"
    } : {
      group: "Smart Shikka CEO status orb",
      hint: "Press Enter for details; use arrow keys to move within the viewport.",
      details: "Status only. It does not start actions or recording."
    },
    [locale]
  );
  return /* @__PURE__ */ jsxs94(
    "div",
    {
      role: "button",
      tabIndex: 0,
      "aria-label": labels.group,
      "aria-expanded": expanded,
      "aria-keyshortcuts": "Enter Space ArrowUp ArrowDown ArrowLeft ArrowRight",
      onKeyDown,
      onClick,
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: onPointerEnd,
      style: {
        position: "fixed",
        right: position.right,
        bottom: position.bottom,
        zIndex: 50,
        cursor: "grab",
        touchAction: "none",
        borderRadius: 16,
        background: "hsl(var(--brand-card) / 0.96)",
        border: "1px solid var(--brand-border)",
        padding: expanded ? 10 : 6,
        boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
        maxWidth: "calc(100vw - 24px)",
        outline: "none"
      },
      children: [
        /* @__PURE__ */ jsx102(
          ShikkaPresence,
          {
            state,
            locale,
            size: expanded ? 56 : COMPACT_SIZE,
            logo,
            showLabel: expanded
          }
        ),
        expanded && /* @__PURE__ */ jsxs94(
          "div",
          {
            "aria-live": "polite",
            style: {
              color: "hsl(var(--brand-muted-foreground))",
              fontSize: 11,
              lineHeight: 1.35,
              marginTop: 6,
              textAlign: "center",
              maxWidth: EXPANDED_WIDTH - 20
            },
            children: [
              /* @__PURE__ */ jsx102("div", { children: labels.hint }),
              /* @__PURE__ */ jsx102("div", { children: labels.details })
            ]
          }
        )
      ]
    }
  );
}

// src/ceo/SidebarShikkaCeo.tsx
import { jsx as jsx103, jsxs as jsxs95 } from "react/jsx-runtime";
var LABELS = {
  idle: { en: "Ready", hi: "\u0924\u0948\u092F\u093E\u0930" },
  listening: { en: "Listening", hi: "\u0938\u0941\u0928 \u0930\u0939\u0940 \u0939\u0948" },
  transcribing: { en: "Transcribing", hi: "\u0932\u093F\u0916 \u0930\u0939\u0940 \u0939\u0948" },
  thinking: { en: "Thinking", hi: "\u0938\u094B\u091A \u0930\u0939\u0940 \u0939\u0948" },
  using_knowledge: { en: "Using knowledge", hi: "\u091C\u093E\u0928\u0915\u093E\u0930\u0940 \u0916\u094B\u091C \u0930\u0939\u0940 \u0939\u0948" },
  browsing: { en: "Browsing", hi: "\u092C\u094D\u0930\u093E\u0909\u091C\u093C \u0915\u0930 \u0930\u0939\u0940 \u0939\u0948" },
  coding: { en: "Coding", hi: "\u0915\u094B\u0921 \u0915\u0930 \u0930\u0939\u0940 \u0939\u0948" },
  acting: { en: "Working", hi: "\u0915\u093E\u092E \u0915\u0930 \u0930\u0939\u0940 \u0939\u0948" },
  waiting_for_approval: { en: "Needs approval", hi: "\u0905\u0928\u0941\u092E\u0924\u093F \u091A\u093E\u0939\u093F\u090F" },
  speaking: { en: "Speaking", hi: "\u092C\u094B\u0932 \u0930\u0939\u0940 \u0939\u0948" },
  error: { en: "Connection issue", hi: "\u0915\u0928\u0947\u0915\u094D\u0936\u0928 \u0938\u092E\u0938\u094D\u092F\u093E" }
};
function SidebarShikkaCeo(props) {
  const label = LABELS[props.state][props.locale];
  const minimal = isMinimalShikkaState(props.state);
  const full = Boolean(props.directEngagement || isFullShikkaState(props.state));
  const expanded = Boolean(props.expanded && !props.compact && full && !minimal);
  if (props.compact || minimal || !expanded) {
    return /* @__PURE__ */ jsx103(
      "button",
      {
        type: "button",
        className: "ss-sidebar-ceo-compact",
        "aria-label": `Smart Shikka: ${label}`,
        title: `Smart Shikka: ${label}`,
        onClick: () => props.onExpandedChange?.(!props.expanded),
        children: /* @__PURE__ */ jsx103(ShikkaPresence, { state: props.state, locale: props.locale, size: 34, logo: props.logo, showLabel: false })
      }
    );
  }
  return /* @__PURE__ */ jsxs95(
    "button",
    {
      type: "button",
      className: `ss-sidebar-ceo${expanded ? " is-expanded" : ""}`,
      "aria-label": `Smart Shikka: ${label}`,
      "aria-expanded": expanded,
      onClick: () => props.onExpandedChange?.(!props.expanded),
      children: [
        expanded ? /* @__PURE__ */ jsx103("span", { className: "ss-sidebar-ceo-stage", "aria-hidden": "true", children: /* @__PURE__ */ jsx103("img", { src: props.fullBodySrc ?? props.logo, alt: "" }) }) : /* @__PURE__ */ jsx103(ShikkaPresence, { state: props.state, locale: props.locale, size: 40, logo: props.logo, showLabel: false }),
        /* @__PURE__ */ jsxs95("span", { className: "ss-sidebar-ceo-copy", children: [
          /* @__PURE__ */ jsx103("strong", { children: "Smart Shikka" }),
          /* @__PURE__ */ jsx103("small", { children: label })
        ] })
      ]
    }
  );
}
function isFullShikkaState(state) {
  return state === "listening" || state === "transcribing" || state === "waiting_for_approval" || state === "speaking" || state === "error";
}
function isMinimalShikkaState(state) {
  return state === "thinking" || state === "using_knowledge" || state === "browsing" || state === "coding" || state === "acting";
}

// src/browser/BrowserControl.ts
async function performBrowserControl(req) {
  if (req.isEmergencyStopped) {
    return { ok: false, message: "Browser control blocked by Emergency Stop.", blockedByEmergencyStop: true };
  }
  if (!req.runtime) {
    return { ok: false, message: "Browser control runtime not configured.", blockedByEmergencyStop: false };
  }
  return req.runtime(req);
}

// src/browser/useBrowserControl.ts
import { useCallback as useCallback36, useState as useState57 } from "react";
function useBrowserControl(runtime) {
  const [busy, setBusy] = useState57(false);
  const [lastResult, setLastResult] = useState57(void 0);
  const execute = useCallback36(
    async (req) => {
      setBusy(true);
      try {
        const result = await performBrowserControl({ ...req, runtime });
        setLastResult(result);
        return result;
      } finally {
        setBusy(false);
      }
    },
    [runtime]
  );
  return { execute, busy, lastResult };
}

// src/desktop-control/DesktopControl.ts
async function performDesktopControl(req) {
  if (req.isEmergencyStopped) {
    return { ok: false, message: "Desktop control blocked by Emergency Stop.", blockedByEmergencyStop: true };
  }
  if (!req.runtime) {
    return { ok: false, message: "Desktop control runtime not configured.", blockedByEmergencyStop: false };
  }
  return req.runtime(req);
}

// src/desktop-control/useDesktopControl.ts
import { useCallback as useCallback37, useState as useState58 } from "react";
function useDesktopControl(runtime) {
  const [busy, setBusy] = useState58(false);
  const [lastResult, setLastResult] = useState58(void 0);
  const execute = useCallback37(async (req) => {
    setBusy(true);
    try {
      const result = await performDesktopControl({ ...req, runtime });
      setLastResult(result);
      return result;
    } finally {
      setBusy(false);
    }
  }, [runtime]);
  return { execute, busy, lastResult };
}
export {
  ActivityLedgerView,
  AgentOpsAgentsPanel,
  AgentOpsAuditTrailPanel,
  AgentOpsDashboard,
  AgentOpsEmptyState,
  AgentOpsInventoryPanel,
  AgentOpsSafetyNotice,
  AgentOpsStatusBadge,
  AgentOpsStorePanel,
  AgentPromptSettings,
  ApiClient,
  AppRegistryPanel,
  ApprovalCards,
  ApprovalQueuePanel,
  ArtifactCards,
  AuditService,
  BLOCKED_DESKTOP_ADMIN_ACTIONS,
  BRAND_ACCENTS,
  BRAND_ACCENT_SWATCH,
  Badge,
  BetaApiError,
  BetaReadinessPanel,
  BuilderPanel,
  BuilderServiceError,
  Button,
  ByokProvidersPanel,
  COOKIE_SESSION_TOKEN,
  COVER_PRESETS,
  CallSheetPanel,
  CanonicalSettingsControlPlane,
  CapabilityStateBadge,
  Card,
  CeoPlanPreviewPanel,
  CeoRoutingPreviewPanel,
  ChatCanvas,
  ChatComposer,
  ChatContainer,
  ChatHistoryPanel,
  ChatInput,
  ChatMessage,
  ChatMessageBody,
  ChatService,
  ChatSidebar,
  CitationChips,
  CommandPalette,
  CommsAttemptsPanel,
  ConnectionModeSelector,
  ConnectionsPanel,
  ConnectorRegistryPanel,
  ConnectorRuntimePreviewPanel,
  ConsoleLogSink,
  ContextPane,
  CostUsagePanel,
  DEFAULT_DESKTOP_CAPABILITIES,
  DesignModeSurface,
  DesktopBridgeEmptyState,
  DesktopCapabilityList,
  DesktopConnectionPanel,
  DesktopDiagnosticsPanel,
  DiffBlocks,
  DomainsApiError,
  DomainsPanel,
  DraggableShikkaOrb,
  ForegroundWakeControl,
  GRANT_REQUEST_OPTIONS,
  GlobalRulesPanel,
  HealthPreviewPanel,
  InMemoryLogSink,
  Input,
  IntegrationSettings,
  KnowledgeLibraryPanel,
  KnowledgeSourceSettings,
  LogLevel,
  Logger,
  MasterProviderSettingsPanel,
  McpToolsPanel,
  MemoryPanel,
  MessageErrorRow,
  MetricsCollector,
  Modal,
  ModeSelector,
  ModelPicker,
  ModelProviderSettings,
  ModelRoutingSettingsPanel,
  MonitoringDashboard,
  PERMISSION_CATEGORY_SCOPES,
  PRODUCT_NAV_ITEMS,
  PermissionStrip,
  ProductAccountMenu,
  ProductPreferenceControls,
  ProductSettingsSurface,
  ProductShell,
  ProductSidebar,
  ProductSidebarRail,
  ProductTopbar,
  RemoteBackendStatus,
  SAFE_ERROR_CODES,
  SETTINGS_NAMESPACES,
  SafeErrorNotice,
  SecretBoxPanel,
  SettingsEmptyState,
  SettingsPanel,
  SettingsStatusBadge,
  SettingsTabs,
  ShikkaPresence,
  SidebarShikkaCeo,
  SkillsSettings,
  SmartShikkaDock,
  SmartShikkaProvider,
  SpeechToText,
  SuperAdminPanel,
  SystemHealthPanel,
  SystemIntegrationsPanel,
  TaskQueuePreviewPanel,
  ToolActionCards,
  ToolMarketplace,
  TraceSummary,
  TypingIndicator,
  UNSUPPORTED_GRANT_REQUEST_OPTIONS,
  UnifiedWorkspace,
  VOICE_PREFERENCE_SETTING_KEY,
  VoiceAgent,
  VoiceButton,
  VoicePlayer,
  VoiceRecorder,
  VoiceService,
  VoiceSettingsPanel,
  VpsConnectionStatus,
  WORKSPACE_SHORTCUTS,
  WorkflowCanvas,
  WorkflowService,
  WorkflowSidebar,
  WorkflowToolbar,
  WorkspaceConnectorsPanel,
  WorkspaceProductSurface,
  WorkspaceProvider,
  WorkspaceShell,
  WorkspaceShellProvider,
  applyBrandAccent,
  applyBrandMode,
  assistantTurnFromMessage,
  audit,
  baseUiTokens,
  brandColors,
  buildActivityLedger,
  canSubmitComposer,
  classifyIntent,
  computeDryRunPreview,
  consumeOncePermissionCategories,
  createApiClient,
  createBetaService,
  createBuilderService,
  createCanonicalSettingsService,
  createChatService,
  createDefaultPermissionCategories,
  createDomainsService,
  createProductDesignService,
  createVoiceService,
  createWorkflowService,
  createWorkspaceAgentSkillsService,
  createWorkspaceBrowserSessionService,
  createWorkspaceCeoService,
  createWorkspaceChatService,
  createWorkspaceCodeIntelligenceService,
  createWorkspaceCodeService,
  createWorkspaceControlService,
  createWorkspaceCoworkService,
  createWorkspaceMemoryService,
  defaultRedactor,
  errorToCode,
  extractWakeCommand,
  focusModalBoundary,
  getDefaultLanguage,
  getFocusableModalElements,
  getInitialBrandMode,
  getStoredBrandAccent,
  grantRequestLabel,
  isCeoEligible,
  isFullShikkaState,
  isMinimalShikkaState,
  isSpeechRecognitionSupported,
  logger,
  metrics,
  normalizeHindiRoman,
  normalizeWakeTranscript,
  performBrowserControl,
  performDesktopControl,
  permissionCategoryLabel,
  permissionLevelForIntent,
  privilegedAccountMenuItems,
  productText,
  redactPII,
  redactRecord,
  redactText,
  redactUnknown,
  renderChatMarkdown,
  resetBrandAccent,
  resolveBrandMode,
  resolveProductModeRuntime,
  routeIntent,
  safeErrorCode,
  safeErrorCopy,
  safeErrorText,
  shouldSubmitComposerKey,
  turn,
  useBrandColors,
  useBrandTheme,
  useBrowserControl,
  useDesktopControl,
  useFormBridge,
  useOptionalWorkspaceShell,
  usePageCapability,
  useProjectRuntime,
  useSharedWorkspaceShellProps,
  useShikkaCEO,
  useSmartShikka,
  useSmartShikkaSettings,
  useWorkspace,
  useWorkspaceShell,
  useWorkspaceShortcuts,
  validateWorkflow,
  t4 as workspaceT
};
